/* === STRATEGY-CHAT EDGE FUNCTION CODE (STREAMING) — paste into Supabase dashboard manually ===

import { serve } from 'https://deno.land/std@0.168.0/http/server.ts'
import { createClient } from 'https://esm.sh/@supabase/supabase-js@2'

// IMPORTANT: Paste the EXACT system prompt from your currently deployed strategy-chat function here.
// The system prompt is the v2 prompt with 5-bucket comp logic, two-buyer framing, and 6-month recency guardrails.
// DO NOT modify the system prompt — only the streaming plumbing changes.
const SYSTEM_PROMPT = `[PASTE YOUR EXISTING SYSTEM PROMPT HERE — copy from the currently deployed edge function in Supabase dashboard]`

const corsHeaders = {
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Headers': 'authorization, content-type, x-client-info, apikey',
  'Access-Control-Allow-Methods': 'POST, OPTIONS',
}

serve(async (req) => {
  if (req.method === 'OPTIONS') {
    return new Response('ok', { headers: corsHeaders })
  }

  try {
    const { case_id, user_message } = await req.json()
    const authHeader = req.headers.get('Authorization')!
    const supabase = createClient(
      Deno.env.get('SUPABASE_URL')!,
      Deno.env.get('SUPABASE_ANON_KEY')!,
      { global: { headers: { Authorization: authHeader } } }
    )

    const supabaseAdmin = createClient(
      Deno.env.get('SUPABASE_URL')!,
      Deno.env.get('SUPABASE_SERVICE_ROLE_KEY')!
    )

    const { data: caseRow, error: caseErr } = await supabase
      .from('property_cases').select('*').eq('id', case_id).single()
    if (caseErr || !caseRow) throw new Error('Case not found or access denied')

    const { data: history } = await supabase
      .from('property_case_messages').select('role, content')
      .eq('case_id', case_id).order('created_at', { ascending: true })

    const { data: files } = await supabase
      .from('property_case_files').select('*').eq('case_id', case_id)
      .order('uploaded_at', { ascending: true })

    const messages: any[] = (history || []).map((m: any) => ({
      role: m.role,
      content: m.content,
    }))

    const userContent: any[] = []

    if (files && files.length > 0 && messages.length === 0) {
      for (const f of files) {
        if (!f.file_name?.toLowerCase().endsWith('.pdf')) continue
        const path = `${caseRow.agent_id}/${case_id}/${f.file_name}`
        const { data: blob, error: dlErr } = await supabaseAdmin.storage
          .from('property-docs').download(path)
        if (dlErr || !blob) continue
        const buf = await blob.arrayBuffer()
        const bytes = new Uint8Array(buf)
        let binary = ''
        const chunkSize = 0x8000
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)))
        }
        const base64 = btoa(binary)
        userContent.push({
          type: 'document',
          source: { type: 'base64', media_type: 'application/pdf', data: base64 },
        })
      }
      const fileLabels = files.map((f: any) => `- ${f.file_type}: ${f.file_name}`).join('\n')
      userContent.push({
        type: 'text',
        text: `The agent uploaded these files for this property:\n${fileLabels}\n\nAgent message:\n${user_message}`,
      })
    } else {
      userContent.push({ type: 'text', text: user_message })
    }

    messages.push({ role: 'user', content: userContent })

    await supabase.from('property_case_messages').insert({
      case_id,
      role: 'user',
      content: user_message,
    })

    // STREAMING: open SSE response back to client
    const encoder = new TextEncoder()
    let fullAssistantText = ''

    const stream = new ReadableStream({
      async start(controller) {
        try {
          const anthropicRes = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'x-api-key': Deno.env.get('ANTHROPIC_API_KEY')!,
              'anthropic-version': '2023-06-01',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              model: 'claude-opus-4-7',
              max_tokens: 8000,
              system: SYSTEM_PROMPT,
              messages,
              stream: true,
              tools: [{ type: 'web_search_20250305', name: 'web_search', max_uses: 8 }],
            }),
          })

          if (!anthropicRes.ok || !anthropicRes.body) {
            const errText = await anthropicRes.text()
            controller.enqueue(encoder.encode(`data: ${JSON.stringify({ error: errText })}\n\n`))
            controller.close()
            return
          }

          const reader = anthropicRes.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              const payload = line.slice(6).trim()
              if (!payload) continue
              try {
                const evt = JSON.parse(payload)
                if (evt.type === 'content_block_delta' && evt.delta?.type === 'text_delta') {
                  const textChunk = evt.delta.text || ''
                  fullAssistantText += textChunk
                  controller.enqueue(encoder.encode(`data: ${JSON.stringify({ text: textChunk })}\n\n`))
                }
              } catch (_e) {
                // ignore non-JSON or partial lines
              }
            }
          }

          // Save full assistant message at end of stream
          await supabase.from('property_case_messages').insert({
            case_id,
            role: 'assistant',
            content: fullAssistantText,
          })
          await supabase.from('property_cases')
            .update({ updated_at: new Date().toISOString() })
            .eq('id', case_id)

          controller.enqueue(encoder.encode(`data: ${JSON.stringify({ done: true })}\n\n`))
          controller.close()
        } catch (err) {
          controller.enqueue(encoder.encode(`data: ${JSON.stringify({ error: String(err) })}\n\n`))
          controller.close()
        }
      },
    })

    return new Response(stream, {
      headers: {
        ...corsHeaders,
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Connection': 'keep-alive',
      },
    })
  } catch (err) {
    return new Response(JSON.stringify({ error: String(err) }), {
      status: 500,
      headers: { ...corsHeaders, 'Content-Type': 'application/json' },
    })
  }
})

=== END EDGE FUNCTION CODE === */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle, Children, cloneElement, isValidElement, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer'
import { Loader } from '@googlemaps/js-api-loader'
import { supabase } from './supabase'
import OpenHouseLanding from './OpenHouseLanding'
import OpenHouseAdmin from './OpenHouseAdmin'
import LinksAdmin from './LinksAdmin'
import ListingsPage from './ListingsPage'
import FunnelLanding from './FunnelLanding'
import RecruitLanding from './RecruitLanding'
import FeedbackSurvey from './FeedbackSurvey'
import DemoLanding from './DemoLanding'
import ProtectLanding from './ProtectLanding'
import { PrivacyPage, TermsPage } from './LegalPages'
import { IdxListings } from './IdxListings'
import FunnelsAdmin from './FunnelsAdmin'
import TeamsAdmin from './TeamsAdmin'
import DealDeskReviewAdmin from './DealDeskReviewAdmin'
import RecruitingAdmin from './RecruitingAdmin'
import BillingAdmin from './BillingAdmin'
import { PAYPAL_CLIENT_ID, TECH_FEE_PLAN_ID } from './paypalConfig'
import SchoolDisclosure from './SchoolDisclosure'
import { HomeLanding } from './HomeLanding'
import { FUNNEL_RESERVED } from './funnelReserved'
import { isMlsLicensed, canUseLiveComps, canShowIdxListings } from './mlsLicense'
import {
  COMP_BUCKET_ORDER, compPullFingerprint, latestPullAnalysis, pullDiffersFromAnalysis,
  tierOverridesFromToggles, widenTogglesFromFingerprint, arizonaDateISO,
  strategyReportFileName, reportLanguageTag, reportLanguageOfFileName,
} from './pullConsistency'

import { S, FONT_HEAD, FONT_BODY } from './styles'

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch {
      document.body.removeChild(textarea);
      return false;
    }
  }
}

// ─── CONTENT CATEGORIES ──────────────────────────────────────────────────────
const TYPE_CFG = {
  crawford:          { label: 'Cromford Report',   color: '#147efb' },
  articles:          { label: 'Articles',          color: '#0c5fc4' },
  'real-estate-news':{ label: 'Real Estate News',  color: '#2d7a2d' },
  'mortgage-news':   { label: 'Mortgage News',     color: '#9b59b6' },
  reel:              { label: 'Reel Scripts',       color: '#fdbf00' },
  // legacy compat
  post:              { label: 'Post',              color: '#147efb' },
  image:             { label: 'Post',              color: '#147efb' },
  caption:           { label: 'Post',              color: '#147efb' },
}

// Card chip colours, one pair per category. Kept separate from TYPE_CFG.color,
// which is the older solid-badge colour still used by DetailModal and Admin.
// The three legacy types have no entry and fall back to neutral grey.
const CONTENT_CHIP = {
  crawford:           { bg:'#F7E3E3', fg:'#6B1414' },
  articles:           { bg:'#E4EDF7', fg:'#123763' },
  'real-estate-news': { bg:'#EFE6F7', fg:'#46186B' },
  'mortgage-news':    { bg:'#DDEFED', fg:'#0B3F3A' },
  reel:               { bg:'#FBE8DA', fg:'#6B3410' },
}
const CONTENT_CHIP_FALLBACK = { bg:'#f0f0ee', fg:'#555555' }
const CONTENT_NEW_CHIP = { bg:'#EAF3DE', fg:'#27500A' }

const CATEGORIES = [
  { key: 'crawford',           label: 'Cromford Report' },
  { key: 'articles',           label: 'Articles' },
  { key: 'real-estate-news',   label: 'Real Estate News' },
  { key: 'mortgage-news',      label: 'Mortgage News' },
  { key: 'reel',               label: 'Reel Scripts' },
]

const FILTERS = [
  { key: 'all', label: 'All Content' },
  ...CATEGORIES,
]

// ─── EVENT TYPES ──────────────────────────────────────────────────────────────
const EVENT_TYPES = {
  'ce-class':       { label: 'CE Class',        color: '#147efb' },
  'homepros-event': { label: 'HomePros Event',  color: '#fdbf00' },
  'industry-event': { label: 'Industry Event',  color: '#2d7a2d' },
}
// Soft-tint badge colours, same shape as CONTENT_CHIP. EVENT_TYPES.color is the
// older solid-badge colour; it has no readers left once these land, but it is
// kept rather than removed. This also ends CE Class sharing #147efb with every
// interactive affordance on the page.
const EVENT_CHIP = {
  'homepros-event': { bg:'#FAEEDA', fg:'#633806' },
  'industry-event': { bg:'#EAF3DE', fg:'#27500A' },
  'ce-class':       { bg:'#E4EDF7', fg:'#123763' },
}
const EVENT_CHIP_FALLBACK = { bg:'#f0f0ee', fg:'#555555' }

const EVENT_TYPE_OPTIONS = [
  { key: 'ce-class',       label: 'CE Class' },
  { key: 'homepros-event', label: 'HomePros Event' },
  { key: 'industry-event', label: 'Industry Event' },
]

// ─── VENDOR DIRECTORY ────────────────────────────────────────────────────────
const VENDOR_CATEGORIES = [
  'Inspector','Lender','Title','Contractor','Photographer','Videographer',
  'Stager','Cleaner','Locksmith','Moving Company','Insurance Agent','Landscaper',
  'Handyman','Surveyor','Home Warranty','1031 Exchange','Pool','CPA',
  'Credit Repair','Hard Money Lender'
]

const VENDOR_SUBTYPES = {
  'Inspector': ['Home','Termite/Pest','Pool','Sewer Scope','Septic','Well','Mold','Foundation/Structural','Roof','Stucco','HVAC'],
  'Lender': ['Conventional','FHA/VA','Jumbo','Investor/DSCR','Construction','Reverse Mortgage','ITIN'],
  'Contractor': ['Roofing','Plumbing','Electrical','HVAC','Painting (Interior)','Painting (Exterior)','Drywall/Stucco','Flooring','Tile/Countertop','Concrete/Driveway','Garage Door','Fence/Gate','Foundation','General/Multi-Trade'],
  'Insurance Agent': ['Homeowners','Auto','Umbrella','Landlord/Rental','Builders Risk'],
  'Cleaner': ['Move-in/Move-out','Recurring','Construction Cleanup','Window','Carpet','Pressure Washing'],
  'Pool': ['Service/Maintenance','Repair','Builder/Installation'],
  'Landscaper': ['Maintenance/Mowing','Design/Install','Tree Service/Arborist','Irrigation','Hardscape'],
  'CPA': ['Real Estate Focused','Tax Prep','Investor/Multi-Property'],
  'Photographer': ['Listing','Drone','Twilight'],
  'Videographer': ['Listing','Drone','Reel/Social'],
  'Moving Company': ['Local','Long-Distance','Storage']
}

const VENDOR_SUBTYPES_REQUIRED = ['Inspector','Contractor','Insurance Agent']

function hasImageUpload(type) {
  return type !== 'reel'
}

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── SUPABASE TABLE: content_downloads ──────────────────────────────────────
// CREATE TABLE content_downloads (
//   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
//   content_id UUID REFERENCES content(id) ON DELETE CASCADE,
//   created_at TIMESTAMPTZ DEFAULT now()
// );
// ALTER TABLE content_downloads ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "Users can insert own downloads" ON content_downloads FOR INSERT WITH CHECK (auth.uid() = user_id);
// CREATE POLICY "Admins can read all downloads" ON content_downloads FOR SELECT USING (true);

// ─── SUPABASE TABLE: notifications ──────────────────────────────────────────
// CREATE TABLE notifications (
//   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
//   message TEXT NOT NULL,
//   type TEXT DEFAULT 'content',
//   read BOOLEAN DEFAULT false,
//   created_at TIMESTAMPTZ DEFAULT now()
// );
// ALTER TABLE notifications ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "Users can read own notifications" ON notifications FOR SELECT USING (auth.uid() = user_id);
// CREATE POLICY "Users can update own notifications" ON notifications FOR UPDATE USING (auth.uid() = user_id);
// CREATE POLICY "Service role can insert" ON notifications FOR INSERT WITH CHECK (true);

// ─── SUPABASE TABLE: announcements ──────────────────────────────────────────
// CREATE TABLE announcements (
//   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
//   message TEXT,
//   active BOOLEAN DEFAULT false,
//   created_at TIMESTAMPTZ DEFAULT now()
// );
// INSERT INTO announcements (message, active) VALUES ('', false);

// ─── MAIN APP ───────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showWelcome, setShowWelcome] = useState(false)
  const [recoveryMode, setRecoveryMode] = useState(false)
  // The invite set-password gate (see the routing below) needs two facts the
  // profile alone cannot give. profileFailedFor: the user whose profile read
  // failed, so the gate falls through to the Dashboard instead of waiting on a
  // profile that is not coming. passwordGateDoneFor: the user who has just been
  // through the screen in this page load, so a marker write that failed does
  // not hold them there; the screen comes back on the next load instead.
  const [profileFailedFor, setProfileFailedFor] = useState(null)
  const [passwordGateDoneFor, setPasswordGateDoneFor] = useState(null)
  // Which user the current `profile` belongs to (or is being fetched for).
  // The auth listener below is registered once, so it cannot read `profile`
  // from its closure; this ref is how it tells an account switch (another tab
  // signing in shares this tab's session) from a token refresh.
  const profileUserRef = useRef(null)

  useEffect(() => {
    // Check for email confirmation redirect
    const hash = window.location.hash
    if (hash && hash.includes('type=signup')) {
      setShowWelcome(true)
      window.history.replaceState(null, '', window.location.pathname)
      setTimeout(() => setShowWelcome(false), 3000)
    }
    if (hash && hash.includes('type=recovery')) {
      setRecoveryMode(true)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      syncRealtimeToken(session)
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else setLoading(false)
    })
    supabase.auth.onAuthStateChange((event, session) => {
      syncRealtimeToken(session)
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true)
      }
      // A different account: drop the old profile BEFORE the new session
      // renders, so nothing shows or saves the previous user's data while the
      // new profile is fetched. A refresh of the same user keeps it.
      if (session && session.user.id !== profileUserRef.current) setProfile(null)
      setSession(session)
      if (session) fetchProfile(session.user.id)
      else { profileUserRef.current = null; setProfile(null); setLoading(false) }
    })
  }, [])

  // An expired or reused auth link redirects with its error in the hash, and
  // supabase-js leaves it there when it keeps a stored session (it clears the
  // hash only on success). Once the app has a session the error is stale, so
  // take it out of the address bar. Recovery and invite tokens are not
  // touched: they carry no error, and supabase-js clears those itself.
  useEffect(() => {
    if (!session) return
    const h = window.location.hash
    if (h && /(^#|&)(error|error_code|error_description)=/.test(h)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [session])

  // Records a password the user just set (SetPasswordScreen, either variant)
  // in the app's own copy of their profile, so the invite gate clears without
  // a reload. The row itself was already written by the screen.
  function notePasswordSet(userId, stamp) {
    setPasswordGateDoneFor(userId)
    if (stamp) setProfile(p => (p && p.id === userId ? { ...p, password_set_at: stamp } : p))
  }

  async function fetchProfile(userId) {
    profileUserRef.current = userId
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()
    // A slower fetch for an account that has since been switched away from
    // must not overwrite the newer one.
    if (profileUserRef.current !== userId) return
    // A failed read keeps the current profile rather than replacing it with
    // null: a null profile opens forms blank, and a blank form can be saved.
    if (error || !data) {
      console.error('[fetchProfile] read failed; keeping current profile:', error)
      setProfileFailedFor(userId)
      setLoading(false)
      return
    }
    setProfileFailedFor(null)
    setProfile(data)
    setLoading(false)
    // Update last_seen on login
    supabase.from('profiles').update({ last_seen: new Date().toISOString() }).eq('id', userId).then(() => {})
  }

  if (showWelcome) return (
    <div style={{...S.loading,flexDirection:'column',gap:'16px'}}>
      <div style={{fontSize:'48px'}}>✓</div>
      <div style={{fontSize:'22px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,textTransform:'uppercase',letterSpacing:'1px'}}>Email Confirmed!</div>
      <div style={{fontSize:'15px',color:'#555',fontFamily:FONT_BODY}}>Welcome to HomePros Hub.</div>
    </div>
  )
  // Public open house landing — no auth required. Render before loading/login gates.
  const publicPath = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase()
  if (publicPath === 'openhouse') return <OpenHouseLanding slug={null} />
  if (publicPath.startsWith('openhouse/')) {
    const ohSlug = publicPath.slice('openhouse/'.length).split('/')[0]
    return <OpenHouseLanding slug={ohSlug} />
  }
  if (publicPath.startsWith('links/')) {
    const linksSlug = publicPath.slice('links/'.length).split('/')[0]
    return <LinksPage slug={linksSlug} />
  }
  if (publicPath.startsWith('funnel/')) {
    const funnelSlug = publicPath.slice('funnel/'.length).split('/')[0]
    return <FunnelLanding slug={funnelSlug} />
  }
  if (publicPath === 'listings') return <ListingsPage />
  if (publicPath === 'join') return <RecruitLanding />
  if (publicPath === 'feedback') return <FeedbackSurvey />
  if (publicPath === 'demo') return <DemoLanding />
  if (publicPath === 'protect') return <ProtectLanding />
  if (publicPath === 'privacy') return <PrivacyPage />
  if (publicPath === 'terms') return <TermsPage />

  // Public catch-all single-segment slug. Excludes reserved keywords so the
  // logged-in SPA routes still win.
  // 2026-10-21: the ARMLS IDX agreement (HomePros ajaa01, Exhibit A) lists
  // agent pages as truetopproducer.com/<agent slug>, so /<slug> is the agent
  // page only (same page as /links/<slug>). Funnels live at /funnel/<slug>
  // (route above) so the two can never collide.
  const publicParts = publicPath.split('/').filter(Boolean)
  if (publicParts.length === 1 && !FUNNEL_RESERVED.includes(publicParts[0])) {
    return <LinksPage slug={publicParts[0]} />
  }
  // Password recovery: a reset link creates a session, so gate it BEFORE the
  // normal session→Dashboard routing, forcing the user to set a new password.
  if (recoveryMode) return <SetPasswordScreen onDone={(userId, stamp) => { setRecoveryMode(false); if (userId) notePasswordSet(userId, stamp); if (window.location.hash) window.history.replaceState(null, '', window.location.pathname) }} />
  if (loading) return <div style={S.loading}>Loading...</div>
  if (!session) return <HomeLanding />
  // Invited agents set a password before the Dashboard opens. Decided from the
  // user and their profile, not from the invite URL: the link works once, and a
  // second click, another tab, a reload or a visit tomorrow all arrive without
  // it. profiles.password_set_at is a setup marker for this screen only (see
  // supabase/schema/2026-10-07_profiles_password_set_at.sql); it is never an
  // access control. Accounts that were not invited (invited_at null) skip all
  // of this. Recovery above takes priority.
  if (session.user.invited_at && passwordGateDoneFor !== session.user.id) {
    const uid = session.user.id
    const own = profile && profile.id === uid ? profile : null
    // Still loading this user's profile: wait, rather than flash the Dashboard.
    // A failed read falls through to the Dashboard — never trap anyone here on
    // a read error.
    if (!own && profileFailedFor !== uid) return <div style={S.loading}>Loading...</div>
    if (own && !own.password_set_at) return <SetPasswordScreen variant="invite" onDone={(userId, stamp) => { notePasswordSet(userId || uid, stamp); if (window.location.hash) window.history.replaceState(null, '', window.location.pathname) }} />
  }
  // Keyed on the user: a session that changes to another account (another tab
  // signing in shares this one's session) remounts the whole workspace, so no
  // page keeps form state copied from the previous account.
  return <Dashboard key={session?.user?.id} session={session} profile={profile} setProfile={setProfile} />
}

// ─── LINKS (public agent page) ───────────────────────────────────────────────
// Public agent "link in bio" page at /links/{slug}. Fetches via the anon-callable
// get_agent_page RPC (returns null when the agent isn't published) and renders a
// mobile-first card layout. Sections with no data are omitted entirely.
const LINKS_NAVY = '#1d2e42'
const LINKS_BLUE = '#147efb'
const LINKS_GOLD = '#fdbf00'
const LINKS_MUTED = '#7a869a'
const LINKS_BORDER = 'rgba(29,46,66,0.10)'
const LINKS_LINE = '#dce3eb'
const LINKS_INK = '#102234'

function linksInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
}
function linksFirstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || ''
}
function linksPrice(n) {
  if (n == null || n === '' || isNaN(Number(n))) return ''
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n))
}
function linksDate(s) {
  if (!s) return ''
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function LinksSocialIcon({ type }) {
  const p = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'currentColor' }
  if (type === 'instagram') return (
    <svg {...p}><path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 3.68A6.16 6.16 0 1018.16 12 6.16 6.16 0 0012 5.84zm0 10.16A4 4 0 1116 12a4 4 0 01-4 4zm6.4-10.4a1.44 1.44 0 11-1.44-1.44 1.44 1.44 0 011.44 1.44z"/></svg>
  )
  if (type === 'facebook') return (
    <svg {...p}><path d="M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z"/></svg>
  )
  return (
    <svg {...p}><path d="M4.98 3.5A2.5 2.5 0 002.5 6a2.5 2.5 0 002.48 2.5A2.5 2.5 0 007.5 6a2.5 2.5 0 00-2.52-2.5zM3 9h4v12H3zM9 9h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05 4.02 0 4.76 2.65 4.76 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.07 1.4-2.07 2.85V21H9z"/></svg>
  )
}

function LinksWinRow({ win, t }) {
  const status = String(win.event_type || '').toLowerCase()
  const color = status === 'active' ? '#22a05b' : status === 'pending' ? LINKS_GOLD : '#e5484d'
  const tint = status === 'active' ? 'rgba(34,160,91,0.12)' : status === 'pending' ? 'rgba(253,191,0,0.14)' : 'rgba(229,72,77,0.12)'
  const label = status === 'active' ? t.active : status === 'pending' ? t.pending : t.sold
  const price = status === 'active' ? '' : linksPrice(win.close_price)
  const date = status === 'active' ? '' : linksDate(win.close_date)
  const secondLine = [price, date].filter(Boolean).join(' · ')
  return (
    <div style={{ background: 'white', border: `1px solid ${LINKS_LINE}`, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', marginBottom: '10px' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: tint, color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5L12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9 21v-6h6v6"/></svg>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: '15px', color: LINKS_NAVY, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{win.address}</div>
        {secondLine && <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px' }}>{secondLine}</div>}
      </div>
      <div style={{ background: tint, color, padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>{label}</div>
    </div>
  )
}

const LINKS_CARD = { background: 'white', borderRadius: '18px', border: `1px solid ${LINKS_LINE}` }
// Resource-grid tile (video / calculator / prequalify): icon square on top,
// bold title, muted subtitle. Works on <div>, <button> and <a> alike.
const LINKS_TILE = { ...LINKS_CARD, padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', width: '100%', boxSizing: 'border-box', textAlign: 'left', textDecoration: 'none', cursor: 'pointer', fontFamily: FONT_BODY }

function LinksTileIcon({ children }) {
  return (
    <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(20,126,251,0.10)', color: LINKS_BLUE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  )
}

// UI-string translations for the public link page. Agent-authored content (block
// titles/subtitles, addresses, names, brokerage) is NEVER translated — only the
// fixed chrome below. {name}/{agent} tokens are filled at the call site.
const LINKS_T = {
  en: {
    realtor: 'REALTOR®', salesperson: 'Real estate salesperson',
    call: 'Call', email: 'Email', saveContact: 'Save my contact',
    connectZillow: 'Connect on Zillow',
    worth: "What's your home worth?",
    aiAnalysis: 'AI analysis', agentReviewed: 'Reviewed by a licensed agent',
    reportPreparedBy: 'A personalized report, prepared by {name}',
    addressPlaceholder: 'Start typing your address', getReport: 'Get my report',
    fullName: 'Full name', emailLabel: 'Email', phone: 'Phone',
    sendReport: 'Send my report', back: 'Back', sending: 'Sending…',
    thanks: 'Thanks, {name}', reportShortly: '{agent} will get your report over shortly.',
    errAddress: 'Please enter your address', errName: 'Please enter your name',
    errContact: 'Enter an email or phone so we can reach you',
    errGeneric: 'Something went wrong — please try again.',
    meet: 'Meet {name}', videoIntro: '60 second intro · tap to play', close: 'Close',
    moreFrom: 'MORE FROM {name}', myWins: 'MY WINS',
    zillowProfile: 'Zillow profile', zillowSub: 'Listings and client reviews',
    open: 'Open', active: 'Active', pending: 'Pending', sold: 'Sold',
    getPrequalified: 'Get prequalified', prequalSub: 'Know your budget before you shop',
    estimatePayment: 'Estimate a payment',
    adjustNote: 'Adjust anything below. Starting values are editable estimates.',
    homePrice: 'Home price', downPayment: 'Down payment', interestRate: 'Interest rate',
    loanTerm: 'Loan term', years: 'years',
    propTaxYear: 'Property tax (per year)', insYearLabel: "Homeowner's insurance (per year)",
    hoaMonthLabel: 'HOA dues (per month)', estTotalMonthly: 'Estimated total monthly payment',
    principalInterest: 'Principal & interest', propertyTax: 'Property tax',
    insurance: 'Insurance', mortgageInsurance: 'Mortgage insurance', hoa: 'HOA',
    disclaimerPrequal: 'Estimate only. Mortgage insurance is estimated and varies by lender and credit profile. Not a loan offer or commitment to lend. Click "Get prequalified" for a true prequalification from one of our lenders.',
    disclaimerNoPrequal: 'Estimate only. Mortgage insurance is estimated and varies by lender and credit profile. Not a loan offer or commitment to lend. Consult a licensed lender for actual terms.',
    equalHousing: 'Equal Housing Opportunity', pageNotFound: 'Page not found',
  },
  es: {
    realtor: 'REALTOR®', salesperson: 'Agente de bienes raíces',
    call: 'Llamar', email: 'Correo', saveContact: 'Guardar mi contacto',
    connectZillow: 'Conéctese en Zillow',
    worth: '¿Cuánto vale su casa?',
    aiAnalysis: 'Análisis con IA', agentReviewed: 'Revisado por un agente con licencia',
    reportPreparedBy: 'Un informe personalizado, preparado por {name}',
    addressPlaceholder: 'Escriba su dirección', getReport: 'Ver mi informe',
    fullName: 'Nombre completo', emailLabel: 'Correo electrónico', phone: 'Teléfono',
    sendReport: 'Enviar mi informe', back: 'Atrás', sending: 'Enviando…',
    thanks: 'Gracias, {name}', reportShortly: '{agent} le enviará su informe en breve.',
    errAddress: 'Por favor escriba su dirección', errName: 'Por favor escriba su nombre',
    errContact: 'Escriba un correo o teléfono para poder contactarle',
    errGeneric: 'Algo salió mal — por favor intente de nuevo.',
    meet: 'Conozca a {name}', videoIntro: 'Presentación de 60 segundos · toque para ver', close: 'Cerrar',
    moreFrom: 'MÁS DE {name}', myWins: 'MIS RESULTADOS',
    zillowProfile: 'Perfil de Zillow', zillowSub: 'Propiedades y reseñas de clientes',
    open: 'Abrir', active: 'Activa', pending: 'En proceso', sold: 'Vendida',
    getPrequalified: 'Obtenga su precalificación', prequalSub: 'Conozca su presupuesto antes de buscar',
    estimatePayment: 'Calcule su pago',
    adjustNote: 'Ajuste cualquier valor. Las cifras iniciales son estimados editables.',
    homePrice: 'Precio de la casa', downPayment: 'Enganche', interestRate: 'Tasa de interés',
    loanTerm: 'Plazo del préstamo', years: 'años',
    propTaxYear: 'Impuesto predial (al año)', insYearLabel: 'Seguro de vivienda (al año)',
    hoaMonthLabel: 'Cuota de HOA (al mes)', estTotalMonthly: 'Pago mensual total estimado',
    principalInterest: 'Capital e intereses', propertyTax: 'Impuesto predial',
    insurance: 'Seguro', mortgageInsurance: 'Seguro hipotecario', hoa: 'HOA',
    disclaimerPrequal: 'Solo un estimado. El seguro hipotecario es un estimado y varía según el prestamista y el perfil crediticio. No constituye una oferta de préstamo ni un compromiso de otorgar crédito. Haga clic en "Obtenga su precalificación" para una precalificación real con uno de nuestros prestamistas.',
    disclaimerNoPrequal: 'Solo un estimado. El seguro hipotecario es un estimado y varía según el prestamista y el perfil crediticio. No constituye una oferta de préstamo ni un compromiso de otorgar crédito. Consulte con un prestamista con licencia para conocer los términos reales.',
    equalHousing: 'Igualdad de Oportunidad de Vivienda', pageNotFound: 'Página no encontrada',
  },
}

function LinksLinkIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
    </svg>
  )
}

// featured — full-width card. With image_url, a full-width 1:1 image tops the
// card and title/subtitle/Open sit below; when image_only, the whole card is
// just the tappable image. Without an image it's a plain padded card.
function LinksFeaturedBlock({ block, t }) {
  const hasImg = block.image_url && String(block.image_url).trim()
  const imageOnly = hasImg && block.image_only
  return (
    <a href={block.url} target="_blank" rel="noopener noreferrer" style={{ ...LINKS_CARD, display: 'block', marginBottom: '10px', overflow: hasImg ? 'hidden' : undefined, textDecoration: 'none' }}>
      {hasImg && (
        <img src={block.image_url} alt={block.title || ''} style={{ width: '100%', aspectRatio: '8 / 3', objectFit: 'cover', display: 'block' }} />
      )}
      {!imageOnly && (
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: '15px', fontWeight: 500, color: LINKS_NAVY }}>{block.title}</div>
          {block.subtitle && <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px' }}>{block.subtitle}</div>}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: LINKS_BLUE, fontSize: '13px', fontWeight: 600, marginTop: '10px' }}>
            {t.open}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>
          </div>
        </div>
      )}
    </a>
  )
}

// link_tile — two-per-row grid card. With image_url, a 76px image band tops the
// card; without one, a blue link icon fills that space.
function LinksTileBlock({ block }) {
  const hasImg = block.image_url && String(block.image_url).trim()
  return (
    <a href={block.url} target="_blank" rel="noopener noreferrer" style={{ ...LINKS_CARD, display: 'block', overflow: 'hidden', textDecoration: 'none' }}>
      {hasImg
        ? <img src={block.image_url} alt={block.title || ''} style={{ width: '100%', height: '76px', objectFit: 'cover', display: 'block' }} />
        : <div style={{ height: '76px', background: 'rgba(20,126,251,0.10)', color: LINKS_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LinksLinkIcon size={24} /></div>}
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 500, color: LINKS_NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.title}</div>
        {block.subtitle && <div style={{ fontSize: '12px', color: LINKS_MUTED, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.subtitle}</div>}
      </div>
    </a>
  )
}

// link_row — full-width row: tinted blue link icon, title/subtitle, chevron.
function LinksRowBlock({ block }) {
  return (
    <a href={block.url} target="_blank" rel="noopener noreferrer" style={{ ...LINKS_CARD, display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', marginBottom: '10px', textDecoration: 'none' }}>
      <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'rgba(20,126,251,0.10)', color: LINKS_BLUE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LinksLinkIcon size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '15px', fontWeight: 500, color: LINKS_NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.title}</div>
        {block.subtitle && <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{block.subtitle}</div>}
      </div>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={LINKS_MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M9 6l6 6-6 6"/></svg>
    </a>
  )
}

// Home value capture — two-step lead form. Step 1 collects the address, step 2
// the contact info, then submits via the submit_link_page_lead RPC and swaps in
// a confirmation. Keeps the blue header band across all states.
function LinksHomeValueCard({ slug, agentFirst, t }) {
  const [step, setStep] = useState(1)
  const [address, setAddress] = useState('')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  // Structured address parts captured from a Places selection; null/'' when the
  // user typed a free-text address without selecting a suggestion.
  const [city, setCity] = useState('')
  const [stateShort, setStateShort] = useState('')
  const [postal, setPostal] = useState('')
  const [lat, setLat] = useState(null)
  const [lng, setLng] = useState(null)
  const [placesReady, setPlacesReady] = useState(false)
  const addressElRef = useRef(null)      // container the Places element mounts into
  const autocompleteRef = useRef(null)   // the PlaceAutocompleteElement instance

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '10px', border: `1px solid ${LINKS_LINE}`, background: 'white', color: LINKS_NAVY, fontSize: '16px', fontFamily: FONT_BODY, outline: 'none' }
  const submitBtn = { width: '100%', textAlign: 'center', border: 'none', background: LINKS_GOLD, color: LINKS_INK, borderRadius: '10px', padding: '13px 0', fontSize: '14px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }
  // White chip behind the red so the error stays legible on the blue card.
  const errStyle = { display: 'inline-block', color: '#C8102E', background: 'white', borderRadius: '8px', padding: '5px 10px', fontSize: '13px', marginTop: '8px', fontFamily: FONT_BODY }

  // Google Places autocomplete via the modern PlaceAutocompleteElement. Fully
  // self-contained — independent of the legacy Autocomplete in StrategyCaseView.
  // If the key is missing or the element fails to load, placesReady stays false
  // and the plain text input is used, so the form still works.
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY
    if (!key) { console.warn('[Places/link] REACT_APP_GOOGLE_PLACES_KEY not set; using plain text input'); return }
    let cancelled = false
    const loader = new Loader({ apiKey: key, version: 'weekly', libraries: ['places'] })
    loader.load()
      .then((google) => google.maps.importLibrary('places'))
      .then((placesLib) => {
        if (cancelled) return
        const { PlaceAutocompleteElement } = placesLib
        if (!PlaceAutocompleteElement) { console.warn('[Places/link] PlaceAutocompleteElement unavailable'); return }
        const pae = new PlaceAutocompleteElement({
          includedPrimaryTypes: ['street_address'],
          includedRegionCodes: ['us'],
          // Hard-restrict to the Arizona bounds so out-of-state (e.g. Nevada)
          // results are excluded from suggestions, not just deprioritized.
          locationRestriction: { north: 37.0, south: 31.3, east: -109.0, west: -114.8 },
        })
        pae.placeholder = t.addressPlaceholder
        Object.assign(pae.style, {
          width: '100%', boxSizing: 'border-box', padding: '12px 14px',
          borderRadius: '10px', border: `1px solid ${LINKS_LINE}`,
          background: 'white', color: LINKS_NAVY, fontSize: '16px', fontFamily: FONT_BODY,
        })

        // Typed text without a selection: read the real input via composedPath so
        // it works through the element's shadow DOM. A manual edit invalidates any
        // previously selected structured fields (they no longer match the text).
        pae.addEventListener('input', (e) => {
          const node = (e.composedPath && e.composedPath()[0]) || e.target
          const v = node && typeof node.value === 'string' ? node.value : ''
          setAddress(v)
          setCity(''); setStateShort(''); setPostal(''); setLat(null); setLng(null)
        })

        // Selection: keep the formatted address as the shown value; parse parts.
        pae.addEventListener('gmp-select', async (event) => {
          const prediction = event.placePrediction || (event.detail && event.detail.placePrediction)
          if (!prediction) return
          try {
            const place = prediction.toPlace()
            await place.fetchFields({ fields: ['formattedAddress', 'addressComponents', 'location'] })
            if (cancelled) return
            const comps = place.addressComponents || []
            const pick = (type, short) => {
              const c = comps.find((x) => (x.types || []).includes(type))
              return c ? ((short ? c.shortText : c.longText) || '') : ''
            }
            const streetLine = [pick('street_number'), pick('route')].filter(Boolean).join(' ')
            const loc = place.location
            const latVal = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null
            const lngVal = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null
            setAddress(place.formattedAddress || streetLine || '')
            setCity(pick('locality') || pick('postal_town') || pick('sublocality') || '')
            setStateShort(pick('administrative_area_level_1', true))
            setPostal(pick('postal_code'))
            setLat(typeof latVal === 'number' ? latVal : null)
            setLng(typeof lngVal === 'number' ? lngVal : null)
          } catch (err) {
            console.error('[Places/link] fetchFields failed:', err)
          }
        })

        autocompleteRef.current = pae
        setPlacesReady(true)
      })
      .catch((err) => { console.error('[Places/link] loader failed:', err) })
    return () => { cancelled = true }
  }, [])

  // Mount the element into its container whenever step 1 is showing. appendChild
  // re-attaches it if it was detached (e.g. after visiting step 2 and back).
  useEffect(() => {
    if (placesReady && step === 1 && !done && addressElRef.current && autocompleteRef.current) {
      addressElRef.current.appendChild(autocompleteRef.current)
    }
  }, [placesReady, step, done])

  // Keep the Places element's placeholder in sync with the active language.
  useEffect(() => {
    if (autocompleteRef.current) autocompleteRef.current.placeholder = t.addressPlaceholder
  }, [t, placesReady])

  function goStep2() {
    if (!address.trim()) { setError(t.errAddress); return }
    setError('')
    setStep(2)
  }
  function back() {
    setError('')
    setStep(1)
  }
  async function submit() {
    if (!fullName.trim()) { setError(t.errName); return }
    if (!email.trim() && !phone.trim()) { setError(t.errContact); return }
    setError('')
    setSubmitting(true)
    const { data: leadId, error: rpcErr } = await supabase.rpc('submit_link_page_lead', {
      p_slug: slug,
      p_full_name: fullName.trim(),
      p_email: email.trim() || null,
      p_phone: phone.trim() || null,
      p_address: address.trim(),
      p_city: city || null,
      p_state: stateShort || null,
      p_postal_code: postal || null,
      p_lat: lat,
      p_lng: lng,
    })
    if (rpcErr) {
      console.error('[submit_link_page_lead] failed:', rpcErr)
      setError(t.errGeneric)
      setSubmitting(false)
      return
    }
    // Fire-and-forget FUB push. A lead that saved but failed to reach FUB must
    // NOT look like a failure to the person filling out the form — the RPC
    // already persisted it, so we confirm regardless and only log invoke errors.
    if (leadId) {
      supabase.functions
        .invoke('send-link-page-lead-to-fub', { body: { lead_id: leadId } })
        .then(({ error: fnErr }) => { if (fnErr) console.error('[send-link-page-lead-to-fub] invoke failed:', fnErr) })
        .catch((e) => console.error('[send-link-page-lead-to-fub] threw:', e))
    }
    setDone(true)
  }

  const leadFirst = linksFirstName(fullName)

  return (
    <div style={{ marginTop: '16px', borderRadius: '22px', overflow: 'hidden', background: `linear-gradient(115deg, rgba(9,100,210,0.65), rgba(20,126,251,0.97)), url(/valuation-bg.jpg) center / cover no-repeat, ${LINKS_BLUE}` }}>
      <div style={{ color: 'white', padding: '20px 20px 16px', position: 'relative', overflow: 'hidden' }}>
        <div className="links-sweep" style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '30%', background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0) 100%)', animation: 'linksSweep 5s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em' }}>{t.worth}</div>
          <div style={{ fontSize: '13px', opacity: 0.92, marginTop: '2px' }}>{t.reportPreparedBy.replace('{name}', agentFirst)}</div>
        </div>
      </div>
      <div style={{ background: '#1d2e42', padding: '9px 18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <svg className="links-pulse" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdbf00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, animation: 'linksPulse 2.6s ease-in-out infinite' }}>
          <path d="M6 12h5"/><path d="M11 12l6-6"/><path d="M11 12l6 6"/>
          <circle cx="5" cy="12" r="1.8"/><circle cx="18" cy="6" r="1.8"/><circle cx="18" cy="18" r="1.8"/>
        </svg>
        <span style={{ fontSize: '10px', color: 'white' }}>{t.aiAnalysis}</span>
        <span style={{ color: '#6b8199' }}>·</span>
        <span style={{ fontSize: '10px', color: '#a8bdd4' }}>{t.agentReviewed}</span>
      </div>
      <div style={{ padding: '18px 20px 20px' }}>
        {done ? (
          // White panel so the confirmation keeps its existing colors on the blue card.
          <div style={{ textAlign: 'center', padding: '20px 16px', background: 'white', borderRadius: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(34,160,91,0.12)', color: '#22a05b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{ fontSize: '17px', fontWeight: 600, color: LINKS_NAVY, marginTop: '12px' }}>{t.thanks.replace('{name}', leadFirst)}</div>
            <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '4px' }}>{t.reportShortly.replace('{agent}', agentFirst)}</div>
          </div>
        ) : step === 1 ? (
          <>
            {placesReady
              ? <div ref={addressElRef} className="links-addr" style={{ width: '100%', borderRadius: '10px' }} />
              : <input className="links-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t.addressPlaceholder} style={inputStyle} />}
            {error && <div style={errStyle}>{error}</div>}
            <button type="button" onClick={goStep2} style={{ ...submitBtn, marginTop: '10px' }}>{t.getReport}</button>
          </>
        ) : (
          <>
            <input className="links-input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t.fullName} style={inputStyle} />
            <input className="links-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder={t.emailLabel} style={{ ...inputStyle, marginTop: '10px' }} />
            <input className="links-input" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder={t.phone} style={{ ...inputStyle, marginTop: '10px' }} />
            {error && <div style={errStyle}>{error}</div>}
            <button type="button" onClick={submit} disabled={submitting} style={{ ...submitBtn, marginTop: '10px', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'wait' : 'pointer' }}>{submitting ? t.sending : t.sendReport}</button>
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <button type="button" onClick={back} disabled={submitting} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.88)', fontSize: '13px', fontFamily: FONT_BODY, cursor: submitting ? 'default' : 'pointer', padding: 0, textDecoration: 'underline' }}>{t.back}</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Parse a YouTube video ID (11 chars) from the common URL shapes. Channel URLs
// (/@handle, /channel/UC…, /c/name) contain no video ID and return null.
function linksYouTubeId(url) {
  if (!url || typeof url !== 'string') return null
  const s = url.trim()
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,        // youtube.com/watch?v=ID
    /youtu\.be\/([A-Za-z0-9_-]{11})/,   // youtu.be/ID
    /\/embed\/([A-Za-z0-9_-]{11})/,     // youtube.com/embed/ID
    /\/shorts\/([A-Za-z0-9_-]{11})/,    // youtube.com/shorts/ID
  ]
  for (const re of patterns) {
    const m = s.match(re)
    if (m && m[1]) return m[1]
  }
  return null
}

// Lite YouTube embed. Renders nothing when no video ID can be parsed. Collapsed:
// a resource-grid tile topped by the YouTube thumbnail — only that image loads
// on page load. The iframe is injected ONLY after the user clicks, and the
// open card spans the full grid row (fullRow does the same when the video is
// the grid's only tile).
function LinksVideoCard({ url, agentFirst, t, fullRow }) {
  const [open, setOpen] = useState(false)
  const id = linksYouTubeId(url)
  if (!id) return null
  const meetLabel = t.meet.replace('{name}', agentFirst)

  if (!open) {
    return (
      <div onClick={() => setOpen(true)} style={{ ...LINKS_TILE, gridColumn: fullRow ? '1 / -1' : undefined }}>
        <div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: '12px', overflow: 'hidden', position: 'relative', background: LINKS_NAVY }}>
          <img src={`https://img.youtube.com/vi/${id}/mqdefault.jpg`} alt={meetLabel} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: LINKS_NAVY }}>{meetLabel}</div>
          <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px' }}>{t.videoIntro}</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ ...LINKS_CARD, overflow: 'hidden', gridColumn: '1 / -1' }}>
      <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#000' }}>
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={meetLabel}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '15px', fontWeight: 500, color: LINKS_NAVY }}>{meetLabel}</div>
        <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: LINKS_BLUE, fontSize: '13px', fontWeight: 600, fontFamily: FONT_BODY, cursor: 'pointer', padding: 0 }}>{t.close}</button>
      </div>
    </div>
  )
}

// Payment calculator — standard amortization, all local state, no persistence.
// Rendered only when the RPC returns show_calculator true.
function LinksCalculatorCard({ prequalifyUrl, t }) {
  const [homePrice, setHomePrice] = useState(450000)
  const [downPct, setDownPct] = useState(20)
  const [rate, setRate] = useState(6.5)
  const [termYears, setTermYears] = useState(30)
  const [taxYear, setTaxYear] = useState(2475)
  const [insYear, setInsYear] = useState(1500)
  const [hoaMonth, setHoaMonth] = useState(0)

  const downAmount = homePrice * (downPct / 100)
  const loan = homePrice - downAmount
  const i = rate / 100 / 12
  const n = termYears * 12
  // Standard amortization; guard the zero-rate case so we don't divide by zero.
  const pi = n > 0 ? (i === 0 ? loan / n : loan * i / (1 - Math.pow(1 + i, -n))) : 0
  const taxMonthly = taxYear / 12
  const insMonthly = insYear / 12
  const hoaMonthly = hoaMonth
  // PMI applies under 20% down; annual rate (% of loan) steps by down bracket.
  const pmiRate = downPct < 5 ? 0.85 : downPct < 10 ? 0.62 : downPct < 15 ? 0.38 : downPct < 20 ? 0.25 : 0
  const pmiMonthly = loan * pmiRate / 100 / 12
  const total = pi + taxMonthly + insMonthly + hoaMonthly + pmiMonthly

  const numFromInput = (v) => (v === '' || isNaN(Number(v)) ? 0 : Number(v))

  const labelRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }
  const labelText = { fontSize: '13px', color: LINKS_NAVY }
  const valText = { fontSize: '13px', fontWeight: 600, color: LINKS_NAVY }
  const slider = { width: '100%', accentColor: LINKS_BLUE, display: 'block' }
  const numLabel = { fontSize: '12px', color: LINKS_MUTED, marginBottom: '4px' }
  const numInput = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${LINKS_BORDER}`, background: '#f2f4f7', color: LINKS_NAVY, fontSize: '14px', fontFamily: FONT_BODY, outline: 'none' }
  const breakdownRow = { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.85)', marginTop: '6px' }

  return (
    <div style={{ ...LINKS_CARD, marginTop: '16px', padding: '20px' }}>
      <div style={{ fontSize: '15px', fontWeight: 500, color: LINKS_NAVY }}>{t.estimatePayment}</div>
      <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px' }}>{t.adjustNote}</div>

      <div style={{ marginTop: '18px' }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={labelRow}><span style={labelText}>{t.homePrice}</span><span style={valText}>{linksPrice(homePrice)}</span></div>
          <input type="range" min={100000} max={1500000} step={5000} value={homePrice} onChange={(e) => setHomePrice(Number(e.target.value))} style={slider} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={labelRow}><span style={labelText}>{t.downPayment}</span><span style={valText}>{downPct}% · {linksPrice(downAmount)}</span></div>
          <input type="range" min={0} max={50} step={1} value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} style={slider} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={labelRow}><span style={labelText}>{t.interestRate}</span><span style={valText}>{rate}%</span></div>
          <input type="range" min={2} max={12} step={0.1} value={rate} onChange={(e) => setRate(Number(e.target.value))} style={slider} />
        </div>
        <div>
          <div style={labelRow}><span style={labelText}>{t.loanTerm}</span><span style={valText}>{termYears} {t.years}</span></div>
          <input type="range" min={10} max={30} step={5} value={termYears} onChange={(e) => setTermYears(Number(e.target.value))} style={slider} />
        </div>
      </div>

      <div style={{ height: '1px', background: LINKS_BORDER, margin: '18px 0' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <div style={numLabel}>{t.propTaxYear}</div>
          <input type="number" min={0} value={taxYear} onChange={(e) => setTaxYear(numFromInput(e.target.value))} style={numInput} />
        </div>
        <div>
          <div style={numLabel}>{t.insYearLabel}</div>
          <input type="number" min={0} value={insYear} onChange={(e) => setInsYear(numFromInput(e.target.value))} style={numInput} />
        </div>
        <div>
          <div style={numLabel}>{t.hoaMonthLabel}</div>
          <input type="number" min={0} value={hoaMonth} onChange={(e) => setHoaMonth(numFromInput(e.target.value))} style={numInput} />
        </div>
      </div>

      <div style={{ background: LINKS_NAVY, borderRadius: '12px', padding: '18px', marginTop: '18px', color: 'white' }}>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{t.estTotalMonthly}</div>
        <div style={{ fontSize: '28px', fontWeight: 600, marginTop: '2px' }}>{linksPrice(total)}</div>
        <div style={{ marginTop: '10px' }}>
          <div style={breakdownRow}><span>{t.principalInterest}</span><span>{linksPrice(pi)}</span></div>
          <div style={breakdownRow}><span>{t.propertyTax}</span><span>{linksPrice(taxMonthly)}</span></div>
          <div style={breakdownRow}><span>{t.insurance}</span><span>{linksPrice(insMonthly)}</span></div>
          {pmiMonthly > 0 && <div style={breakdownRow}><span>{t.mortgageInsurance}</span><span>{linksPrice(pmiMonthly)}</span></div>}
          {hoaMonthly > 0 && <div style={breakdownRow}><span>{t.hoa}</span><span>{linksPrice(hoaMonthly)}</span></div>}
        </div>
      </div>

      <div style={{ fontSize: '11px', color: LINKS_MUTED, marginTop: '12px', lineHeight: 1.5 }}>{prequalifyUrl && String(prequalifyUrl).trim() ? t.disclaimerPrequal : t.disclaimerNoPrequal}</div>

      {prequalifyUrl && String(prequalifyUrl).trim() && (
        <a href={prequalifyUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', background: LINKS_BLUE, color: 'white', borderRadius: '10px', padding: '12px 0', fontSize: '13px', fontWeight: 600, fontFamily: FONT_BODY, marginTop: '14px' }}>{t.getPrequalified}</a>
      )}
    </div>
  )
}

// fallback: rendered instead of the not-found state when the slug has no
// published agent page (used by the /<slug> catch-all to fall back to funnels).
function LinksPage({ slug, fallback }) {
  const [data, setData] = useState(undefined) // undefined = loading
  const [lang, setLang] = useState('en')      // 'en' | 'es'; seeded from RPC lang_default

  useEffect(() => {
    let cancelled = false
    if (!slug) { setData(null); return }
    supabase
      .rpc('get_agent_page', { p_slug: slug })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) { console.error('[get_agent_page] failed:', error); setData(null); return }
        setData(data ?? null)
        if (data && data.lang_default === 'es') setLang('es')
      })
    return () => { cancelled = true }
  }, [slug])

  const wrap = { minHeight: '100vh', background: '#f1f4f8', fontFamily: FONT_BODY, display: 'flex', justifyContent: 'center' }
  const t = LINKS_T[lang] || LINKS_T.en

  if (data === undefined) return <div style={{ ...wrap, alignItems: 'center', color: LINKS_MUTED }}>Loading...</div>
  if (data === null) return fallback || <div style={{ ...wrap, alignItems: 'center', color: LINKS_MUTED }}>{t.pageNotFound}</div>

  const d = data
  const first = linksFirstName(d.full_name)
  const socials = [
    { type: 'instagram', url: d.instagram },
    { type: 'facebook', url: d.facebook },
    { type: 'linkedin', url: d.linkedin },
  ].filter((s) => s.url && String(s.url).trim())
  const wins = Array.isArray(d.wins) ? d.wins : []
  const blocks = Array.isArray(d.blocks) ? d.blocks : []
  const featured = blocks.filter((b) => b.block_type === 'featured')
  const tiles = blocks.filter((b) => b.block_type === 'link_tile')
  const linkRows = blocks.filter((b) => b.block_type === 'link_row')

  // Secondary hero action: compact translucent pill under the EN/ES toggle.
  const zillowBtn = { display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', whiteSpace: 'nowrap', background: 'rgba(16,34,52,0.42)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '999px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, fontFamily: FONT_BODY }
  // Hero CTAs: side by side, wrapping to stacked only when too narrow to fit.
  const heroBtn = { flex: '1 1 140px', boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none', borderRadius: '12px', padding: '13px 0', fontSize: '14px', fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer' }
  const langBtn = (on) => ({ background: 'none', border: 'none', borderBottom: `3px solid ${on ? LINKS_GOLD : 'transparent'}`, color: on ? 'white' : '#b9c9da', padding: '4px 1px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', fontFamily: FONT_BODY, cursor: 'pointer' })

  // Name splits onto two lines at the first space.
  const fullName = String(d.full_name || '').trim()
  const nameBreak = fullName.indexOf(' ')
  const nameLines = nameBreak > 0 ? [fullName.slice(0, nameBreak), fullName.slice(nameBreak + 1)] : [fullName]
  const license = d.license_number && String(d.license_number).trim()

  // Resource grid: video on the left, and — only when the calculator is off —
  // the standalone prequalify tile on the right (the calculator's own button
  // covers prequalify, so never both). A lone tile spans the full row; with
  // neither, the grid doesn't render.
  const hasVideo = !!linksYouTubeId(d.youtube_url)
  const showCalc = d.show_calculator === true
  const showPrequal = !showCalc && !!(d.prequalify_url && String(d.prequalify_url).trim())
  const resourceCount = (hasVideo ? 1 : 0) + (showPrequal ? 1 : 0)
  const rightSpan = resourceCount === 1 ? '1 / -1' : undefined

  // Build a vCard 3.0 from the RPC data and trigger a .vcf download. Missing
  // fields are skipped entirely. No avatar embed — it bloats the file and
  // client support is inconsistent.
  function handleSaveContact() {
    const esc = (s) => String(s).replace(/([\\,;])/g, '\\$1').replace(/\n/g, '\\n')
    const name = String(d.full_name || '').trim()
    const lines = ['BEGIN:VCARD', 'VERSION:3.0']
    if (name) {
      const parts = name.split(/\s+/)
      const fn = parts[0]
      const ln = parts.slice(1).join(' ')
      lines.push(`N:${esc(ln)};${esc(fn)};;;`)
      lines.push(`FN:${esc(name)}`)
    }
    if (d.brokerage && String(d.brokerage).trim()) lines.push(`ORG:${esc(String(d.brokerage).trim())}`)
    const roleTitle = d.is_realtor ? t.realtor : t.salesperson
    if (roleTitle) lines.push(`TITLE:${esc(roleTitle)}`)
    if (d.phone && String(d.phone).trim()) lines.push(`TEL;TYPE=CELL:${String(d.phone).trim()}`)
    if (d.email && String(d.email).trim()) lines.push(`EMAIL:${String(d.email).trim()}`)
    if (slug) lines.push(`URL:https://truetopproducer.com/links/${slug}`)
    lines.push('END:VCARD')

    const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const fileSlug = (name || 'contact').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'contact'
    a.download = `${fileSlug}.vcf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={wrap}>
      {/* Ambient light-sweep keyframe (defined once, reused) + reduced-motion opt-out. */}
      <style>{`@keyframes linksSweep{from{transform:translateX(-60%) skewX(var(--sweep-skew,-12deg))}to{transform:translateX(220%) skewX(var(--sweep-skew,-12deg))}}@keyframes linksPulse{0%,100%{opacity:1}50%{opacity:0.45}}@media (prefers-reduced-motion: reduce){.links-sweep,.links-pulse{animation:none!important}}.links-res-grid{grid-template-columns:1fr 1fr}@media (max-width:359px){.links-res-grid{grid-template-columns:1fr}}.links-input:focus,.links-addr:focus-within{box-shadow:0 0 0 3px ${LINKS_GOLD}}`}</style>
      <div style={{ width: '100%', maxWidth: '760px', boxSizing: 'border-box', padding: '16px' }}>

        {/* 1. Hero — brokerage, language, agent identity and primary CTAs */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '28px', background: `linear-gradient(115deg, transparent 57%, rgba(20,126,251,0.13) 57%, rgba(20,126,251,0.03) 83%, transparent 83%), linear-gradient(100deg, #102234 15%, rgba(28,48,68,0.8) 65%, rgba(20,126,251,0.21)), url(/hero-bg.jpg) center / cover no-repeat, #1c3044`, padding: 'clamp(20px, 5vw, 32px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: LINKS_GOLD, color: LINKS_INK, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 800, fontFamily: FONT_HEAD, flexShrink: 0 }}>H</div>
              <div style={{ color: 'white', fontSize: '15px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.brokerage}</div>
            </div>
            <div style={{ display: 'flex', gap: '14px', flexShrink: 0 }}>
              <button type="button" onClick={() => setLang('en')} style={langBtn(lang === 'en')}>EN</button>
              <button type="button" onClick={() => setLang('es')} style={langBtn(lang === 'es')}>ES</button>
            </div>
          </div>

          {/* Links to zillow_url exactly as stored — no suffix, no trimming of
              the path. Whatever the agent pasted is what the visitor opens.
              Right-aligned directly under the EN/ES row. */}
          {d.zillow_url && String(d.zillow_url).trim() && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <a href={d.zillow_url} target="_blank" rel="noopener noreferrer" style={zillowBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                  <path d="M3 10.5 12 3l9 7.5"/>
                  <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5"/>
                  <path d="M12 18s-2.9-1.9-2.9-3.8a1.75 1.75 0 0 1 2.9-1.2 1.75 1.75 0 0 1 2.9 1.2c0 1.9-2.9 3.8-2.9 3.8z"/>
                </svg>
                <span>{t.connectZillow}</span>
              </a>
            </div>
          )}

          {/* The pill row takes space from the gap above the name, not adds to it. */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginTop: d.zillow_url && String(d.zillow_url).trim() ? '24px' : '40px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'clamp(2.2rem, 7vw, 3.2rem)', fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 1.02, color: 'white', fontFamily: FONT_HEAD, overflowWrap: 'anywhere' }}>
                {nameLines.map((line, i) => <div key={i}>{line}</div>)}
              </div>
              <div style={{ width: '44px', height: '4px', borderRadius: '2px', background: LINKS_GOLD, marginTop: '16px' }} />
              <div style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: LINKS_GOLD, marginTop: '14px' }}>{d.is_realtor ? t.realtor : t.salesperson}</div>
              <div style={{ fontSize: '14px', color: '#e2ebf5', marginTop: '6px' }}>{[d.brokerage, 'Phoenix, AZ'].filter(Boolean).join(' · ')}</div>
              {license && <div style={{ fontSize: '12px', color: '#a6b9cb', marginTop: '3px' }}>{license}</div>}
            </div>
            {d.avatar_url
              ? <img src={d.avatar_url} alt={d.full_name} style={{ width: '104px', height: '104px', borderRadius: '50%', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.22)', boxSizing: 'border-box', flexShrink: 0 }} />
              : <div style={{ width: '104px', height: '104px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.22)', boxSizing: 'border-box', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '60px', fontWeight: 800, letterSpacing: '-0.04em', color: 'rgba(255,255,255,0.14)', fontFamily: FONT_HEAD }}>{linksInitials(d.full_name)}</div>}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '26px' }}>
            <a href={`tel:${d.phone || ''}`} style={{ ...heroBtn, background: LINKS_GOLD, color: LINKS_INK, border: `1px solid ${LINKS_GOLD}` }}>{t.call}</a>
            <button type="button" onClick={handleSaveContact} style={{ ...heroBtn, position: 'relative', overflow: 'hidden', background: 'rgba(16,34,52,0.42)', color: 'white', border: '1px solid #a6b9cb' }}>
            <span className="links-sweep" style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '28%', background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%)', animation: 'linksSweep 4.5s ease-in-out infinite', pointerEvents: 'none', '--sweep-skew': '-18deg' }} />
            <span style={{ position: 'relative' }}>{t.saveContact}</span>
            </button>
          </div>
        </div>

        {/* 2. Socials, below the hero */}
        {socials.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
            {socials.map((s) => (
              <a key={s.type} href={s.url} target="_blank" rel="noopener noreferrer" style={{ width: '38px', height: '38px', borderRadius: '10px', border: `1px solid ${LINKS_LINE}`, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: LINKS_NAVY }}>
                <LinksSocialIcon type={s.type} />
              </a>
            ))}
          </div>
        )}

        {/* 3. Home value capture */}
        <LinksHomeValueCard slug={slug} agentFirst={first} t={t} />

        {/* 4. Resources — video (expands in place) + prequalify */}
        {resourceCount > 0 && (
          <div className="links-res-grid" style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
            <LinksVideoCard url={d.youtube_url} agentFirst={first} t={t} fullRow={resourceCount === 1} />
            {/* Standalone prequalify — only when the calculator isn't shown (its
                button covers this) and a prequalify URL exists. Never both. */}
            {showPrequal && (
              <a href={d.prequalify_url} target="_blank" rel="noopener noreferrer" style={{ ...LINKS_TILE, gridColumn: rightSpan }}>
                <LinksTileIcon><LinksLinkIcon size={22} /></LinksTileIcon>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: LINKS_NAVY }}>{t.getPrequalified}</div>
                  <div style={{ fontSize: '13px', color: LINKS_MUTED, marginTop: '2px' }}>{t.prequalSub}</div>
                </div>
              </a>
            )}
          </div>
        )}

        {/* 4a. Payment calculator */}
        {showCalc && <LinksCalculatorCard prequalifyUrl={d.prequalify_url} t={t} />}

        {/* 4b. More from — agent blocks */}
        {blocks.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: LINKS_MUTED, marginBottom: '12px', fontFamily: FONT_HEAD, textTransform: 'uppercase' }}>{t.moreFrom.replace('{name}', first)}</div>
            {featured.map((b) => <LinksFeaturedBlock key={b.id} block={b} t={t} />)}
            {tiles.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                {tiles.map((b) => <LinksTileBlock key={b.id} block={b} />)}
              </div>
            )}
            {linkRows.map((b) => <LinksRowBlock key={b.id} block={b} />)}
          </div>
        )}

        {canShowIdxListings(d) && <IdxListings page={`agent:${slug}`} source={`linktree-${slug}`} lang={lang} variant="agent" agentFirst={first} frame={(content) => <div style={{ marginTop: '24px' }}>{content}</div>} />}

        {/* 6. My Wins */}
        {wins.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '2px', color: LINKS_MUTED, marginBottom: '10px', fontFamily: FONT_HEAD }}>{t.myWins}</div>
            {wins.map((w, i) => <LinksWinRow key={i} win={w} t={t} />)}
          </div>
        )}

        {/* 7. Footer */}
        <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: `1px solid ${LINKS_LINE}`, textAlign: 'center', color: LINKS_MUTED, fontSize: '12px', lineHeight: 1.7, paddingBottom: '24px' }}>
          {[d.brokerage, d.phone, t.equalHousing, d.full_name].filter(Boolean).join(' · ')}
        </div>

      </div>
    </div>
  )
}

// ─── SET PASSWORD (recovery and invite) ──────────────────────────────────────
// Standalone screen shown when a user arrives via a password-reset link
// (PASSWORD_RECOVERY), or, as variant "invite", to an invited agent whose
// profile has no password_set_at yet. Reuses the working updateUser({ password })
// pattern from the Profile change-password block. showToast is optional
// (App-level has none).
//
// onDone(userId, stamp): stamp is the password_set_at that was written, or
// null when that write failed. The password itself is saved either way.
function SetPasswordScreen({ onDone, showToast, variant }) {
  const isInvite = variant === 'invite'
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // Set when the password saved but the marker did not. The screen then says so
  // and offers Continue instead of SET PASSWORD; the agent is not held here.
  const [markerFailedFor, setMarkerFailedFor] = useState(null)

  async function handleSet() {
    if (markerFailedFor) return  // password already saved; only Continue is left
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true); setError('')
    const { data: updated, error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) { setError(error.message); setLoading(false); return }
    // The password is saved. Now the setup marker on the user's own profile row.
    // Zero rows back is a failure too: an RLS refusal is not an error.
    const userId = updated?.user?.id || null
    const stamp = new Date().toISOString()
    const { data: marked, error: markErr } = userId
      ? await supabase.from('profiles').update({ password_set_at: stamp }).eq('id', userId).select('id')
      : { data: null, error: { message: 'no signed-in user' } }
    setLoading(false)
    if (markErr || !marked || marked.length === 0) {
      console.error('[SetPasswordScreen] password saved; marker write failed:', markErr)
      setError('Your password is saved, but we could not record it (' + (markErr?.message || 'no row updated') + '). You may see this screen again next time.')
      setMarkerFailedFor(userId || 'unknown')
      return
    }
    if (showToast) showToast('Password set!')
    onDone(userId, stamp)
  }

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#1d2e42',fontFamily:FONT_BODY,padding:'24px'}}>
      <div style={{background:'white',borderRadius:'12px',padding:'40px 32px',maxWidth:'380px',width:'100%',boxShadow:'0 12px 40px rgba(0,0,0,0.3)',borderTop:'3px solid #147efb'}}>
        {!isInvite && <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'3px',color:'#fdbf00',marginBottom:'8px',fontFamily:FONT_HEAD}}>PASSWORD RESET</div>}
        <div style={{fontSize:'26px',fontWeight:900,color:'#1d2e42',marginBottom:'6px',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>{isInvite ? 'Welcome to HomePros Hub' : 'SET YOUR PASSWORD'}</div>
        <p style={{fontSize:'13px',color:'#888',marginBottom:'24px',fontFamily:FONT_BODY}}>{isInvite ? 'Set a password to finish setting up your account.' : 'Choose a new password to finish signing in.'}</p>
        <div style={S.field}>
          <label style={S.label}>NEW PASSWORD</label>
          <input className="login-input" style={{...S.input,border:'1px solid #ddd',background:'white'}} type="password" placeholder="Minimum 8 characters" value={newPassword} onChange={e=>{setNewPassword(e.target.value);setError('')}} />
        </div>
        <div style={S.field}>
          <label style={S.label}>CONFIRM PASSWORD</label>
          <input className="login-input" style={{...S.input,border:'1px solid #ddd',background:'white'}} type="password" placeholder="Re-enter new password" value={confirmPassword} onChange={e=>{setConfirmPassword(e.target.value);setError('')}} onKeyDown={e=>e.key==='Enter'&&handleSet()} />
        </div>
        {error && <div style={S.errorBox}>{error}</div>}
        {markerFailedFor ? (
          <button onClick={()=>onDone(markerFailedFor === 'unknown' ? null : markerFailedFor, null)}
            style={{...S.btnPrimary,width:'100%',padding:'14px',marginTop:'8px'}}>
            CONTINUE
          </button>
        ) : (
        <button onClick={handleSet} disabled={!newPassword||!confirmPassword||loading}
          style={{...S.btnPrimary,width:'100%',opacity:(!newPassword||!confirmPassword||loading)?0.5:1,cursor:(!newPassword||!confirmPassword||loading)?'not-allowed':'pointer',padding:'14px',marginTop:'8px'}}>
          {loading ? 'SETTING...' : 'SET PASSWORD'}
        </button>
        )}
      </div>
    </div>
  )
}


// ─── DASHBOARD ───────────────────────────────────────────────────────────────
function Dashboard({ session, profile, setProfile }) {
  const [toast, setToast] = useState('')
  function parseCurrentPath() {
    // Backwards compat: ?page=events query string from old email links
    const params = new URLSearchParams(window.location.search)
    const qPage = params.get('page')
    if (qPage && ['events','strategy','binsr','dealdesk','pipeline','profile','admin','assistant','ada','library','vendors','broker','market','weekly-emails'].includes(qPage)) {
      return { page: qPage, caseId: null }
    }
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '')
    const parts = path.split('/').filter(Boolean)
    // Root is the Assistant workspace — the default landing after login. The
    // content feed is at /library; there is no /content path, and the Hub page
    // it used to belong to was deleted.
    if (parts.length === 0) return { page: 'assistant', caseId: null }
    const [first, second] = parts
    if (first === 'assistant') return { page: 'assistant', caseId: null }
    if (first === 'ada') return { page: 'ada', caseId: second || null }
    if (first === 'library') return { page: 'library', caseId: null }
    if (first === 'vendors') return { page: 'vendors', caseId: null }
    if (first === 'broker') return { page: 'broker', caseId: null }
    if (first === 'weekly-emails') return { page: 'weekly-emails', caseId: null }
    if (first === 'strategy') return { page: 'strategy', caseId: second || null }
    if (first === 'market') return { page: 'market', caseId: second || null }
    // A saved check is addressed by id at /casita/{id}; bare /casita is the
    // list. Same shape as market and strategy above.
    if (first === 'casita') return { page: 'casita', caseId: second || null }
    // /marketplace is the list; /marketplace/{mls} one listing's fill sheet.
    if (first === 'marketplace') return { page: 'marketplace', caseId: second || null }
    if (first === 'binsr') return { page: 'binsr', caseId: second || null }
    if (first === 'dealdesk') return { page: 'dealdesk', caseId: second || null }
    if (first === 'pipeline') return { page: 'pipeline', caseId: second || null }
    if (first === 'messages') return { page: 'messages', caseId: null }
    if (first === 'admin' && second === 'openhouse') return { page: 'openhouse-admin', caseId: null }
    if (first === 'admin' && second === 'links') return { page: 'links-admin', caseId: null }
    if (first === 'admin' && second === 'funnels') return { page: 'funnels-admin', caseId: null }
    if (first === 'admin' && second === 'teams') return { page: 'teams-admin', caseId: null }
    if (first === 'admin' && second === 'review') return { page: 'dealdesk-review', caseId: null }
    if (first === 'admin' && second === 'recruiting') return { page: 'recruiting-admin', caseId: null }
    if (first === 'admin' && second === 'billing') return { page: 'billing-admin', caseId: null }
    if (first === 'tech-fee') return { page: 'tech-fee', caseId: null }
    if (first === 'mywins') return { page: 'mywins', caseId: null }
    if (['events', 'profile', 'admin'].includes(first)) return { page: first, caseId: null }
    return { page: 'invalid', caseId: null }
  }

  const initPath = parseCurrentPath()
  const [page, setPage] = useState(initPath.page === 'invalid' ? 'assistant' : initPath.page)
  const [activeCaseId, setActiveCaseId] = useState(initPath.caseId)

  // Text handed from the Assistant composer to BinsrPage's create form. BINSR
  // needs a representation choice before a row can be inserted, so the address
  // rides along instead of being written straight to the table.
  const [binsrPrefill, setBinsrPrefill] = useState('')
  // Same idea for ADA, but the row already exists by the time we navigate — the
  // text rides along so AdaCaseView can send it as the opening question.
  const [adaPrefill, setAdaPrefill] = useState('')
  const [pipelinePrefill, setPipelinePrefill] = useState('')

  const navigateTo = useCallback((newPage, newCaseId = null, opts = {}) => {
    setPage(newPage)
    setActiveCaseId(newCaseId)
    setBinsrPrefill(opts.binsrPrefill || '')
    setAdaPrefill(opts.adaPrefill || '')
    setPipelinePrefill(opts.pipelinePrefill || '')
    let path = '/' + newPage
    if (newPage === 'strategy' && newCaseId) path = '/strategy/' + newCaseId
    if (newPage === 'market' && newCaseId) path = '/market/' + newCaseId
    if (newPage === 'casita' && newCaseId) path = '/casita/' + newCaseId
    if (newPage === 'marketplace' && newCaseId) path = '/marketplace/' + newCaseId
    if (newPage === 'binsr' && newCaseId) path = '/binsr/' + newCaseId
    if (newPage === 'ada' && newCaseId) path = '/ada/' + newCaseId
    if (newPage === 'dealdesk' && newCaseId) path = '/dealdesk/' + newCaseId
    if (newPage === 'pipeline' && newCaseId) path = '/pipeline/' + newCaseId
    if (newPage === 'openhouse-admin') path = '/admin/openhouse'
    if (newPage === 'links-admin') path = '/admin/links'
    if (newPage === 'funnels-admin') path = '/admin/funnels'
    if (newPage === 'teams-admin') path = '/admin/teams'
    if (newPage === 'dealdesk-review') path = '/admin/review'
    if (newPage === 'recruiting-admin') path = '/admin/recruiting'
    if (newPage === 'billing-admin') path = '/admin/billing'
    if (newPage === 'mywins') path = '/mywins'
    if (window.location.pathname !== path) {
      window.history.pushState({ page: newPage, caseId: newCaseId }, '', path)
    }
  }, [])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  // Deal Desk session attestation. Deliberately in-memory and held here rather
  // than in the case view: Dashboard survives navigation between cases, so one
  // attestation covers the session, while a reload remounts it and requires a
  // fresh one. Persisting it — localStorage, a profiles column — would make the
  // gate a one-time click for the life of the account, which is not what
  // "the attestation applies to this session only" means.
  const [dealdeskAttested, setDealdeskAttested] = useState(false)
  // The name typed at the session gate, kept for copy attestations. Same
  // lifetime as the flag above: in memory, gone on reload.
  const [dealdeskAttestedName, setDealdeskAttestedName] = useState('')
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [showLicenseModal, setShowLicenseModal] = useState(false)
  const [licenseModalValue, setLicenseModalValue] = useState('')
  const [licenseSaving, setLicenseSaving] = useState(false)
  const [licenseError, setLicenseError] = useState('')

  // Invalid path redirect on mount
  useEffect(() => {
    if (initPath.page === 'invalid') {
      showToast('Page not found — taking you to your assistant.')
      window.history.replaceState({ page: 'assistant', caseId: null }, '', '/')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const { page: newPage, caseId: newCaseId } = parseCurrentPath()
      if (newPage === 'invalid') {
        setPage('assistant'); setActiveCaseId(null)
        showToast('Page not found — taking you to your assistant.')
        window.history.replaceState({ page: 'assistant', caseId: null }, '', '/')
      } else {
        setPage(newPage); setActiveCaseId(newCaseId)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profile && !profile.welcome_seen && profile.brokerage && profile.role !== 'admin') {
      const t = setTimeout(() => setShowWelcomeModal(true), 500)
      return () => clearTimeout(t)
    }
  }, [profile])

  async function dismissWelcome() {
    setShowWelcomeModal(false)
    await supabase.from('profiles').update({ welcome_seen: true }).eq('id', session.user.id)
    setProfile(prev => ({ ...prev, welcome_seen: true }))
  }

  // Brokerage and MLS agent ID are set by an admin now (Admin panel, or the
  // invite). The first-login popups that asked the agent for them are gone: an
  // agent could not complete them once the database stops accepting those
  // writes, and a popup with no way out blocks the whole workspace. A profile
  // with no brokerage gets the non-blocking notice at the top of the main column.

  // License number modal — fires once a brokerage is set, if license_number is
  // missing (agents AND admins). No longer waits on armls_agent_id, which the
  // agent can no longer enter.
  useEffect(() => {
    if (profile && profile.brokerage && !profile.license_number) {
      const t = setTimeout(() => setShowLicenseModal(true), 300)
      return () => clearTimeout(t)
    }
  }, [profile])

  async function saveLicenseNumber() {
    const val = licenseModalValue.trim()
    if (!val) { setLicenseError('Please enter your license number'); return }
    if (licenseSaving) return
    setLicenseSaving(true); setLicenseError('')
    const { error } = await supabase.from('profiles').update({ license_number: val }).eq('id', session.user.id)
    if (error) { setLicenseError('Error: ' + error.message); setLicenseSaving(false); return }
    setProfile(prev => ({ ...prev, license_number: val }))
    setShowLicenseModal(false); setLicenseSaving(false)
  }

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const isAdmin = profile?.role === 'admin'

  // Messages before launch (MESSAGES_LAUNCHED): anyone who cannot see it is
  // sent home once their profile is known. replaceState, so Back does not
  // land on /messages and bounce again.
  const profileReady = !!profile && profile.id === session.user.id
  const messagesBlocked = page === 'messages' && profileReady && !canSeeMessages(isAdmin)
  // One live Messages channel for the whole session, here because Dashboard
  // survives navigation and the phone rail is only mounted while its drawer is open.
  useMessagesLive(session.user.id, profileReady && canSeeMessages(isAdmin), page === 'messages')
  useEffect(() => {
    if (!messagesBlocked) return
    setPage('assistant'); setActiveCaseId(null)
    window.history.replaceState({ page: 'assistant', caseId: null }, '', '/')
  }, [messagesBlocked])

  // Bounce non-admins off admin-only routes once profile is known
  useEffect(() => {
    if (page === 'funnels-admin' && profile && !isAdmin) navigateTo('library')
    if (page === 'teams-admin' && profile && !isAdmin) navigateTo('library')
    if (page === 'dealdesk-review' && profile && !isAdmin) navigateTo('library')
    if (page === 'recruiting-admin' && profile && !isAdmin) navigateTo('library')
  }, [page, profile, isAdmin, navigateTo])

  // The agent's own tech fee row (agent_billing RLS: an agent reads only their
  // own). undefined while loading, null when there is none — and no row means
  // Exempt. Read again on the way into /tech-fee so the page is current, and
  // by /tech-fee after a PayPal approval (it resolves to the row it read).
  // Dashboard is keyed on the user, so a late read never lands on another account.
  const [ownBilling, setOwnBilling] = useState(undefined)
  const onTechFeePage = page === 'tech-fee'
  const loadOwnBilling = useCallback(async () => {
    const { data, error } = await supabase.from('agent_billing')
      .select('tech_fee, status, started_at, last_payment_at')
      .eq('user_id', session.user.id)
      .maybeSingle()
    if (error) console.error('[agent_billing] own row:', error)
    const row = error ? null : (data || null)
    setOwnBilling(row)
    return row
  }, [session.user.id])
  useEffect(() => { loadOwnBilling() }, [loadOwnBilling, onTechFeePage])
  const techFeeNotice = ownBilling && ownBilling.tech_fee === 'on' && ownBilling.status !== 'active'
    ? (TECH_FEE_NOTICE[ownBilling.status] || TECH_FEE_NOTICE.not_set_up)
    : null

  // ADA has no list page — a conversation is always addressed by id. A bare
  // /ada has nothing to render, so send it back to the workspace, which is
  // where a new conversation gets started.
  useEffect(() => {
    if (page === 'ada' && !activeCaseId) navigateTo('assistant')
  }, [page, activeCaseId, navigateTo])
  // isError extends the dwell time. 2500ms is fine for "Saved" — it is not
  // enough for a sentence an agent has to read and then act on, and error
  // messages are the only ones that ask for an action. Optional and defaulted,
  // so every existing single-argument caller is unaffected.
  //
  // durationMs is a third optional argument for the case neither default fits:
  // a success message that also reports something the agent has to notice. It
  // overrides isError's dwell time and changes nothing else — there is no error
  // STYLING in this toast, only a longer timeout, so a long non-error message
  // no longer has to pretend to be an error to earn the time. Every existing
  // one- and two-argument caller is untouched.
  function showToast(msg, isError = false, durationMs) {
    setToast(msg)
    setTimeout(() => setToast(''), durationMs ?? (isError ? 6000 : 2500))
  }
  async function signOut() { await supabase.auth.signOut() }

  // One rail means one query. Every view used to call this for itself, purely to
  // hand `recent` down to its own copy of the rail.
  const { recent, loadingRecent } = useRecentCases(session.user.id)

  // Same rule, same reason. The BROKERAGE rows' live text and the newsletter
  // badge all read from this one hook rather than querying per row.
  const { railStatus, loadingRailStatus, hasNewIssue } = useRailStatus(session, profile)

  return (
    // Every route is chrome-free now, so there is no flag left to branch on.
    // S.page is minHeight:100vh — a floor, not a ceiling — which would let the
    // page grow with its content and make the window the scroller. The shell
    // needs the opposite: a fixed viewport box whose inner panes scroll.
    //
    // The height itself comes from the .chromeless-vh class rather than this
    // object. It needs a 100vh → 100dvh fallback pair, and a JS object cannot
    // hold one: a duplicate key replaces rather than cascades, so the fallback
    // would be dropped before React saw it. Inline styles also beat classes, so
    // height must not appear here at all.
    <div className="chromeless-vh" style={{...S.page, minHeight:0, overflow:'hidden'}}>
      <style>{`@keyframes pulse{0%,100%{opacity:0.3}50%{opacity:0.8}}@keyframes streamCursor{0%,50%{opacity:1}51%,100%{opacity:0}}@keyframes bar{0%,100%{transform:scaleY(0.35)}50%{transform:scaleY(1)}}@keyframes stratPulse{0%,100%{box-shadow:0 0 0 1px rgba(20,126,251,0.35),0 0 14px rgba(20,126,251,0.12)}50%{box-shadow:0 0 0 1px rgba(20,126,251,0.75),0 0 26px rgba(20,126,251,0.3)}}@keyframes railDotPulse{0%,100%{opacity:1}50%{opacity:0.35}}@media (prefers-reduced-motion: reduce){.rail-dot-pulse{animation:none!important}}.chromeless-vh{height:100vh;height:100dvh;}`}</style>

      {/* The rail lives here, above the page switch, so navigation never
          unmounts it. It used to be rendered by CaseShell and by
          AssistantWorkspace, which meant every route tore down one copy and
          built another — a visible flash, and the reason scroll position had to
          be persisted by hand. overflow:hidden bounds this row to the height
          flex allocated it; without it the rail's intrinsic height sets the flex
          line's cross size and the window becomes the scroller. */}
      <div style={{flex:1,display:'flex',flexDirection:'row',alignItems:'stretch',background:'#ffffff',minHeight:0,overflow:'hidden'}}>

        {!isMobile && (
          <RailColumn
            session={session} profile={profile} navigateTo={navigateTo} showToast={showToast}
            isAdmin={isAdmin} signOut={signOut}
            recent={recent} loadingRecent={loadingRecent} isMobile={isMobile} page={page}
            railStatus={railStatus} loadingRailStatus={loadingRailStatus} hasNewIssue={hasNewIssue}
          />
        )}

        {/* MAIN COLUMN — every page renders into this. minHeight:0 so a page's
            own scroll pane is bounded by the row rather than by its content. */}
        <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',background:'#ffffff',minHeight:0,overflow:'hidden'}}>

          {/* Drawer handle first, so it sits above whatever page renders. */}
          {isMobile && (
            <RailDrawer
              session={session} profile={profile} navigateTo={navigateTo} showToast={showToast}
              isAdmin={isAdmin} signOut={signOut}
              recent={recent} loadingRecent={loadingRecent} page={page}
              railStatus={railStatus} loadingRailStatus={loadingRailStatus} hasNewIssue={hasNewIssue}
            />
          )}

          {/* NO BROKERAGE YET. Replaces the old blocking brokerage popup: an
              admin assigns brokerage now, so the agent is told, not asked.
              Non-blocking, above whatever page renders. */}
          {profile && !profile.brokerage && (
            <div style={{padding:'10px 20px 0',flexShrink:0}}>
              <div style={{...reportNoticeStrip, marginTop:0}}>
                {reportNoticeIcon}
                <span><span style={{ fontWeight: 500 }}>Your brokerage hasn't been assigned yet.</span> Your broker will set it. Some tools are limited until then.</span>
              </div>
            </div>
          )}

          {/* TECH FEE OWED. The same gold strip, shown only when the agent's
              agent_billing row has tech_fee 'on' and is not active. No row or
              Exempt: nothing. Hidden on /tech-fee, which says the same thing. */}
          {techFeeNotice && page !== 'tech-fee' && (
            <div style={{padding:'10px 20px 0',flexShrink:0}}>
              <div style={{...reportNoticeStrip, marginTop:0}}>
                {reportNoticeIcon}
                <span style={{flex:1,minWidth:0}}><span style={{ fontWeight: 500 }}>{techFeeNotice}</span></span>
                <button type="button" onClick={()=>navigateTo('tech-fee')}
                  style={{flexShrink:0,background:'none',border:'none',padding:0,cursor:'pointer',color:'#147efb',fontSize:'12.5px',fontWeight:500,fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>
                  Set it up →
                </button>
              </div>
            </div>
          )}

      {/* EVENTS PAGE */}
      {page === 'events' && <EventsPage session={session} profile={profile} isAdmin={isAdmin} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} signOut={signOut} />}

      {/* The form copies the profile once, at mount. So it mounts only once
          THIS user's profile has loaded, keyed on its id so an account change
          rebuilds it. Until then, the app's loading state — never a blank
          form that could be saved. */}
      {page === 'profile' && (profile && profile.id === session.user.id
        ? <ProfilePage key={profile.id} session={session} profile={profile} setProfile={setProfile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />
        : <div style={{...S.loading, minHeight:0, flex:1}}>Loading...</div>)}

      {/* Same gate as /admin/billing: a non-admin gets the plain notice, and
          AdminPage never mounts for them, so none of its reads fire. */}
      {page === 'admin' && (!profileReady
        ? <div style={{...S.loading, minHeight:0, flex:1}}>Loading...</div>
        : isAdmin
          ? <AdminPage session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />
          : <AdminsOnlyNotice />)}
      {page === 'openhouse-admin' && <OpenHouseView session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'links-admin' && <LinkPageView session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'mywins' && <MyWins session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'funnels-admin' && isAdmin && <FunnelsView session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'teams-admin' && isAdmin && <TeamsView session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'dealdesk-review' && isAdmin && <DealDeskReviewView session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />}
      {/* Gated here rather than bounced: a non-admin gets a plain notice. The
          table's RLS refuses them anyway. */}
      {page === 'billing-admin' && (!profileReady
        ? <div style={{...S.loading, minHeight:0, flex:1}}>Loading...</div>
        : isAdmin
          ? <BillingView session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />
          : <AdminsOnlyNotice />)}
      {page === 'tech-fee' && <TechFeePage billing={ownBilling} isMobile={isMobile} userId={session.user.id} onLinked={loadOwnBilling} />}
      {page === 'recruiting-admin' && isAdmin && <RecruitingView session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'assistant' && <AssistantWorkspace session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'library' && <ContentLibraryView session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'vendors' && <VendorsView session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'broker' && <BrokerView session={session} profile={profile} setProfile={setProfile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'weekly-emails' && <WeeklyEmailsView session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'ada' && activeCaseId && <AdaCaseView key={activeCaseId} session={session} profile={profile} caseId={activeCaseId} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} initialMessage={adaPrefill} />}

      {page === 'strategy' && !activeCaseId && <StrategyPage session={session} showToast={showToast} isMobile={isMobile} onOpenCase={(id)=>navigateTo('strategy',id)} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'strategy' && activeCaseId && <StrategyCaseView key={activeCaseId} session={session} caseId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('strategy')} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'market' && !activeCaseId && <MarketReportPage session={session} showToast={showToast} isMobile={isMobile} onOpenCase={(id)=>navigateTo('market',id)} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'market' && activeCaseId && <MarketReportView key={activeCaseId} session={session} requestId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('market')} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'casita' && !activeCaseId && <CasitaChecksPage session={session} profile={profile} showToast={showToast} isMobile={isMobile} onOpenCheck={(id)=>navigateTo('casita',id)} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'casita' && activeCaseId && <CasitaCheckView key={activeCaseId} session={session} profile={profile} checkId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('casita')} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'marketplace' && !activeCaseId && <MarketplacePage showToast={showToast} isMobile={isMobile} onOpenListing={(id)=>navigateTo('marketplace',id)} />}
      {page === 'marketplace' && activeCaseId && <MarketplaceListingView key={activeCaseId} session={session} listingId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('marketplace')} />}

      {page === 'binsr' && !activeCaseId && <BinsrPage session={session} showToast={showToast} isMobile={isMobile} onOpenCase={(id)=>navigateTo('binsr',id)} initialAddress={binsrPrefill} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'binsr' && activeCaseId && <BinsrCaseView key={activeCaseId} session={session} caseId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('binsr')} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}

      {page === 'dealdesk' && !activeCaseId && <DealDeskPage session={session} showToast={showToast} isMobile={isMobile} onOpenCase={(id)=>navigateTo('dealdesk',id)} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} attested={dealdeskAttested} />}
      {page === 'dealdesk' && activeCaseId && <DealDeskCaseView key={activeCaseId} session={session} caseId={activeCaseId} showToast={showToast} isMobile={isMobile} onBack={()=>navigateTo('dealdesk')} profile={profile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} attested={dealdeskAttested} attestedName={dealdeskAttestedName} />}

      {page === 'pipeline' && !activeCaseId && <PipelinePage session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} />}
      {page === 'messages' && (profileReady
        ? canSeeMessages(isAdmin) && <MessagesPage session={session} profile={profile} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} />
        : <div style={{...S.loading, minHeight:0, flex:1}}>Loading...</div>)}

      {page === 'pipeline' && activeCaseId && <PipelineView key={activeCaseId} session={session} profile={profile} conversationId={activeCaseId} showToast={showToast} isMobile={isMobile} navigateTo={navigateTo} isAdmin={isAdmin} signOut={signOut} initialMessage={pipelinePrefill} />}

        </div>
      </div>

      <div style={{...S.toast,opacity:toast?1:0,transform:toast?'translateY(0)':'translateY(10px)'}}>
        {toast}
      </div>

      {/* DEAL DESK ATTESTATION — covers the whole route, list and case view
          alike. Gating only the case view would let an agent pick a form set
          and a representation on a live transaction first and meet the gate
          afterwards, which reads as an interruption rather than a condition of
          entry. Sits outside the row with the other modals. */}
      {page === 'dealdesk' && !dealdeskAttested && (
        <DealDeskAttestationGate
          session={session}
          caseId={activeCaseId}
          isMobile={isMobile}
          onAttested={(typedName)=>{ setDealdeskAttestedName(typedName); setDealdeskAttested(true) }}
        />
      )}

      {showLicenseModal && (
        <div style={S.overlay}>
          <div style={{
            background:'white', borderRadius:'16px', width:'min(480px, 92vw)', maxWidth:'92vw',
            padding:'40px 32px', textAlign:'center', maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
          }}>
            <div style={{
              fontSize:'24px', fontWeight:900, color:'#1d2e42',
              marginBottom:'12px', fontFamily:FONT_HEAD, letterSpacing:'0.5px', lineHeight:1.2,
            }}>
              Your license number
            </div>
            <div style={{ fontSize:'14px', color:'#555', lineHeight:1.6, marginBottom:'24px' }}>
              Enter your real estate license number. It appears on your public link page and is required before you can publish it.
            </div>
            {licenseError && (
              <div style={{
                padding:'10px 14px', marginBottom:'16px',
                background:'rgba(255,60,60,0.08)', border:'1px solid rgba(255,60,60,0.2)',
                color:'#cc3333', fontSize:'13px', borderRadius:'8px',
              }}>{licenseError}</div>
            )}
            <input
              style={{...S.input, textAlign:'center'}}
              value={licenseModalValue}
              onChange={e=>setLicenseModalValue(e.target.value)}
              placeholder="SA123456000"
            />
            <div style={{ fontSize:'12px', color:'#888', marginTop:'6px', marginBottom:'20px', fontFamily:FONT_BODY, lineHeight:1.4 }}>
              As shown on your Arizona real estate license.
            </div>
            <button
              onClick={saveLicenseNumber}
              disabled={licenseSaving}
              style={{
                ...S.btnPrimary,
                width:'100%', padding:'14px 16px', fontSize:'14px', borderRadius:'10px',
                opacity: licenseSaving ? 0.6 : 1,
                cursor: licenseSaving ? 'not-allowed' : 'pointer',
              }}
            >
              {licenseSaving ? 'Saving…' : 'Save & Continue'}
            </button>
          </div>
        </div>
      )}

      {/* WELCOME MODAL */}
      {showWelcomeModal && (
        <div style={S.overlay}>
          <div style={{background:'white',borderRadius:'16px',width:'min(480px, 92vw)',maxWidth:'92vw',padding:'40px 32px',textAlign:'center',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.15)',fontFamily:FONT_BODY}}>
            <div style={{fontSize:'28px',fontWeight:900,color:'#1d2e42',marginBottom:'8px',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>
              Welcome to HomePros Hub, {(profile?.full_name || '').split(' ')[0] || 'there'}! 👋
            </div>
            <div style={{fontSize:'14px',color:'#555',marginBottom:'28px'}}>Here's how it works in 3 simple steps</div>
            <div style={{textAlign:'left',display:'flex',flexDirection:'column',gap:'16px',marginBottom:'32px'}}>
              {[
                { icon: '📲', title: 'Browse Content', desc: 'Find posts, reports and reel scripts uploaded by your broker' },
                { icon: '⬇️', title: 'Download & Copy', desc: 'Save the image and copy the caption in one tap' },
                { icon: '🚀', title: 'Post Everywhere', desc: 'Share to Instagram, Facebook, LinkedIn and more' },
                { icon: '🤖', title: 'AI Tools', desc: 'Try Strategy Assistant for pricing analysis and BINSR Assistant for inspection repair language' },
              ].map((step, i) => (
                <div key={i} style={{display:'flex',gap:'14px',alignItems:'flex-start'}}>
                  <div style={{fontSize:'24px',flexShrink:0,marginTop:'2px'}}>{step.icon}</div>
                  <div>
                    <div style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'2px'}}>{step.title}</div>
                    <div style={{fontSize:'13px',color:'#555',lineHeight:1.5}}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{fontSize:'13px',color:'#6b7280',fontStyle:'italic',marginBottom:'20px',fontFamily:FONT_BODY}}>
              Note: Your broker can review Strategy and BINSR cases for quality and coaching.
            </div>
            <button onClick={dismissWelcome} style={{...S.btnPrimary,width:'100%',padding:'16px',fontSize:'15px',borderRadius:'10px'}}>
              Let's Go! 🚀
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── CONTENT CARD ─────────────────────────────────────────────────────────────
function ContentCard({ item, isNew, onClick, onToast, userId, isMobile }) {
  const cfg = TYPE_CFG[item.type] || TYPE_CFG.crawford
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [dlFeedback, setDlFeedback] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)

  async function handleCopy(e) {
    e.stopPropagation()
    const ok = await copyToClipboard(item.content || '')
    if (ok) { setCopyFeedback(true); setTimeout(() => setCopyFeedback(false), 2000); onToast('Copied to clipboard!') }
  }

  async function handleDownload(e) {
    e.stopPropagation()
    if (!item.image_url) return
    try {
      const res = await fetch(item.image_url)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.title.replace(/[^a-zA-Z0-9]/g, '_') + '.' + (blob.type.split('/')[1] || 'jpg')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setDlFeedback(true); setTimeout(() => setDlFeedback(false), 2000)
      onToast('Image downloaded!')
      if (userId) supabase.from('content_downloads').insert({ user_id: userId, content_id: item.id }).then(() => {})
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  // Same shell CompCard uses, so the two card families read as one system.
  const chipBase = { position:'absolute', top:'8px', fontSize:'10px', padding:'3px 8px', borderRadius:'12px', fontFamily:FONT_BODY, fontWeight:600, whiteSpace:'nowrap' }
  const linkBtn = { background:'none', border:'none', padding:0, fontSize:'11.5px', fontFamily:FONT_BODY, cursor:'pointer', fontWeight:600 }
  const chip = CONTENT_CHIP[item.type] || CONTENT_CHIP_FALLBACK

  return (
    <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden',cursor:'pointer'}} onClick={onClick}>
      <div style={{position:'relative'}}>
        {/* objectPosition:'top' because these are report graphics and flyers,
            where the headline sits at the top — a centered crop cuts it off and
            leaves the middle band. CompCard keeps the default centered crop,
            which suits property photos.
            //
            A RATIO, not a fixed height. At 110px against a fluid column this
            box was roughly 3:1, so a portrait Cromford graphic lost about 70%
            of itself and the surviving slice cut through the headline. It also
            got worse the wider the window went — the height was px while the
            width was not, so a bigger column meant a bigger scale factor
            against the same 110px. A ratio scales with the column, so the crop
            fraction is now identical at every width.
            //
            ContentThumbFallback carries the SAME ratio. The two must move
            together or no-image cards sit at a different height. */}
        {item.image_url && !imgFailed ? (
          <img
            src={item.image_url}
            alt={item.title}
            onError={()=>setImgFailed(true)}
            style={{width:'100%',aspectRatio:'4 / 3',objectFit:'cover',objectPosition:'top',display:'block'}}
          />
        ) : (
          <ContentThumbFallback />
        )}
        <div style={{...chipBase,left:'8px',background:chip.bg,color:chip.fg}}>{cfg.label}</div>
        {isNew && (
          <div style={{...chipBase,right:'8px',background:CONTENT_NEW_CHIP.bg,color:CONTENT_NEW_CHIP.fg}}>New</div>
        )}
      </div>

      <div style={{padding:'10px 12px 12px'}}>
        <div style={{fontSize:'13px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {item.title}
        </div>
        <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,marginTop:'3px'}}>
          {new Date(item.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
          {item.tag ? ` · ${item.tag}` : ''}
        </div>
        {item.content && (
          <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,lineHeight:1.45,marginTop:'6px',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
            {item.content}
          </div>
        )}
        {(item.image_url || item.content) && (
          <div style={{display:'flex',gap:'14px',marginTop:'10px'}}>
            {item.image_url && (
              <button style={{...linkBtn,color:dlFeedback?'#2d7a2d':'#147efb'}} onClick={handleDownload}>{dlFeedback ? 'Saved' : 'Download'}</button>
            )}
            {item.content && (
              <button style={{...linkBtn,color:copyFeedback?'#2d7a2d':'#147efb'}} onClick={handleCopy}>{copyFeedback ? 'Copied' : 'Copy caption'}</button>
            )}
          </div>
        )}
        {isMobile && (item.image_url || item.content) && (
          <div style={{fontSize:'11px',color:'#bbb',marginTop:'8px',fontFamily:FONT_BODY,lineHeight:1.4}}>Save image → open your app → upload → paste caption</div>
        )}
      </div>
    </div>
  )
}

// Mirrors CompPhotoFallback, at this card's own 4:3 box. The ratio is the same
// literal the image above carries — a card with no image has to occupy exactly
// the height one with an image would, or the grid row staggers.
function ContentThumbFallback() {
  return (
    <div style={{width:'100%',aspectRatio:'4 / 3',background:'#f4f4f2',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#c4c4c0" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>
      </svg>
    </div>
  )
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ onClose, onSuccess, isMobile }) {
  const [type, setType] = useState('crawford')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [tag, setTag] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function handleImageChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setImageFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setImagePreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  async function handleSubmit() {
    if (!title.trim()) { setError('Title is required'); return }
    if (type === 'reel' && !content.trim()) { setError('Script content is required'); return }
    if (type !== 'reel' && !content.trim() && !imageFile) { setError('Add a caption or image (or both)'); return }
    if (imageFile && imageFile.size > 5 * 1024 * 1024) { setError('Image must be under 5MB. Please compress your image and try again.'); return }
    setLoading(true); setError('')

    let imageUrl = null

    if (imageFile) {
      const ext = imageFile.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('content-media')
        .upload(fileName, imageFile, { contentType: imageFile.type })
      if (uploadError) { setError('Image upload failed: ' + uploadError.message); setLoading(false); return }
      const { data: urlData } = supabase.storage.from('content-media').getPublicUrl(fileName)
      imageUrl = urlData.publicUrl
    }

    const { data: inserted, error: insertError } = await supabase.from('content').insert({
      type, title: title.trim(), content: content.trim() || null, tag: tag.trim() || null, image_url: imageUrl
    }).select().single()
    if (insertError) { setError(insertError.message); setLoading(false); return }

    // Email notification via Edge Function (non-blocking)
    try {
      await supabase.functions.invoke('notify-new-content', {
        body: { title: inserted.title, caption: inserted.content, type: inserted.type }
      })
    } catch (e) {
      console.warn('Email notification failed (non-blocking):', e)
    }

    // In-app notification (non-blocking)
    const categoryLabel = TYPE_CFG[inserted.type]?.label || inserted.type
    try {
      const { data: { session: sess } } = await supabase.auth.getSession()
      await supabase.functions.invoke('create-notifications', {
        body: { message: `New ${categoryLabel}: ${inserted.title}`, type: 'content', exclude_user_id: sess?.user?.id }
      })
    } catch (e) {
      console.warn('In-app notification failed (non-blocking):', e)
    }

    setLoading(false); onSuccess()
  }

  // Portalled to document.body. This modal is opened from the account menu,
  // which on mobile lives inside RailDrawer's fixed, scrollable 300px panel.
  // Rendering a full-viewport overlay from inside that subtree is fragile
  // however the CSS happens to resolve; at body level there is no ancestor to
  // clip it, no stacking context to escape, and no flex parent to answer to.
  return createPortal(
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={isMobile ? {...S.modal,width:'100%',height:'100%',maxWidth:'100%',maxHeight:'100%',borderRadius:0} : S.modal}>
        <div style={S.modalHeader}>
          <div style={S.modalTitle}>UPLOAD CONTENT</div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>CATEGORY</label>
          <div style={S.typeGrid}>
            {CATEGORIES.map(t => (
              <div key={t.key}
                style={{...S.typeOpt,...(type===t.key?S.typeOptActive:{})}}
                onClick={()=>{setType(t.key);if(t.key==='reel'){setImageFile(null);setImagePreview(null)}}}>
                <div style={{fontSize:'11px',fontWeight:700,color:type===t.key?'#147efb':'#555',letterSpacing:'0.5px',fontFamily:FONT_HEAD,textTransform:'uppercase'}}>
                  {t.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>TITLE</label>
          <input style={S.input} placeholder="e.g. Spring Market Update — Buyer Tips"
            value={title} onChange={e=>setTitle(e.target.value)} />
        </div>
        {hasImageUpload(type) && (
          <div style={S.fieldGroup}>
            <label style={S.label}>IMAGE</label>
            <div style={S.fileDropZone}>
              {imagePreview ? (
                <div style={{position:'relative',display:'inline-block'}}>
                  <img src={imagePreview} alt="Preview" style={{maxWidth:'100%',maxHeight:'200px',borderRadius:'6px'}} />
                  <button style={S.removeImgBtn} onClick={()=>{setImageFile(null);setImagePreview(null)}}>✕</button>
                </div>
              ) : (
                <label style={S.fileLabel}>
                  <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>Click to upload image</div>
                  <input type="file" accept="image/*" onChange={handleImageChange} style={{display:'none'}} />
                </label>
              )}
            </div>
          </div>
        )}
        <div style={S.fieldGroup}>
          <label style={S.label}>{type === 'reel' ? 'SCRIPT' : 'CAPTION'}</label>
          <textarea style={S.textarea}
            placeholder={type === 'reel' ? 'Paste your Reel script here...' : 'Write a caption...'}
            value={content} onChange={e=>setContent(e.target.value)} />
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>TAG (OPTIONAL)</label>
          <input style={S.input} placeholder="e.g. Market Update, Buyer Tips"
            value={tag} onChange={e=>setTag(e.target.value)} />
        </div>
        {error && <div style={S.errorBox}>{error}</div>}
        <div style={S.modalFooter}>
          <button style={S.btnOutline} onClick={onClose}>Cancel</button>
          <button style={{...S.btnPrimary,flex:2,opacity:loading?0.6:1}} onClick={handleSubmit} disabled={loading}>
            {loading ? (imageFile ? 'Uploading...' : 'Publishing...') : 'PUBLISH TO LIBRARY'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── DETAIL MODAL ─────────────────────────────────────────────────────────────
function DetailModal({ item, isAdmin, onClose, onDelete, onToast, isMobile, userId }) {
  const cfg = TYPE_CFG[item.type] || TYPE_CFG.crawford
  const [copyFeedback, setCopyFeedback] = useState(false)
  const [dlFeedback, setDlFeedback] = useState(false)

  async function handleCopy() {
    const ok = await copyToClipboard(item.content || '')
    if (ok) { setCopyFeedback(true); setTimeout(() => setCopyFeedback(false), 2000); onToast('Copied to clipboard!') }
  }

  async function handleDownload() {
    if (!item.image_url) return
    try {
      const res = await fetch(item.image_url)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = item.title.replace(/[^a-zA-Z0-9]/g, '_') + '.' + (blob.type.split('/')[1] || 'jpg')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setDlFeedback(true); setTimeout(() => setDlFeedback(false), 2000)
      onToast('Image downloaded!')
      if (userId) supabase.from('content_downloads').insert({ user_id: userId, content_id: item.id }).then(() => {})
    } catch (e) {
      console.error('Download failed:', e)
    }
  }

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...(isMobile ? {...S.detailModal,width:'100%',height:'100%',maxWidth:'100%',maxHeight:'100%',borderRadius:0} : S.detailModal),position:'relative'}}>
        <button onClick={onClose} style={{position:'absolute',top:'12px',right:'12px',width:'32px',height:'32px',borderRadius:'50%',background:'rgba(0,0,0,0.5)',border:'none',color:'white',fontSize:'16px',fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10}}>✕</button>
        {item.image_url && (
          <div style={S.detailMedia}>
            <img src={item.image_url} alt={item.title} style={{width:'100%',height:'100%',objectFit:'contain',background:'#f5f5f5'}} />
          </div>
        )}
        <div style={S.detailBody}>
          <div style={{display:'inline-block',background:cfg.color,color:'white',padding:'4px 12px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',textTransform:'uppercase',marginBottom:'12px',fontFamily:FONT_HEAD}}>
            {cfg.label}
          </div>
          <div style={S.detailTitle}>{item.title}</div>
          <div style={S.detailMeta}>
            Added {new Date(item.created_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}
            {item.tag && ` · ${item.tag}`}
          </div>
          {item.content && (
            <div style={S.detailContent}>
              {item.content.split('\n').map((line,i) => <p key={i} style={{margin:'0 0 6px'}}>{line||<br/>}</p>)}
            </div>
          )}
          <div style={{...S.detailActions,...(isMobile?{flexDirection:'column'}:{})}}>
            {item.image_url && <button style={{...S.btnPrimary,...(isMobile?{padding:'14px'}:{}),background:dlFeedback?'#2d7a2d':'#147efb'}} onClick={handleDownload}>{dlFeedback ? '✅ Saved!' : 'Download Image'}</button>}
            {item.content && <button style={{...(item.image_url ? S.btnOutline : S.btnPrimary),...(isMobile?{padding:'14px'}:{}),
              ...(copyFeedback?{background:'#2d7a2d',color:'white',borderColor:'#2d7a2d'}:{})}} onClick={handleCopy}>{copyFeedback ? '✅ Copied!' : 'Copy Caption'}</button>}
            <button style={{...S.btnOutline,...(isMobile?{padding:'14px'}:{})}} onClick={onClose}>Close</button>
            {isAdmin && <button style={{...S.btnDanger,...(isMobile?{padding:'14px'}:{})}} onClick={()=>onDelete(item.id)}>Delete</button>}
          </div>
          {isMobile && (item.image_url || item.content) && (
            <div style={{fontSize:'11px',color:'#999',textAlign:'center',marginTop:'12px',fontFamily:FONT_BODY,lineHeight:1.4}}>Save image → open your app → upload → paste caption</div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── AVANCE FEATURED CARD ─────────────────────────────────────────────────────
// Was a full-height dark grid tile with a typewriter animation; now a quiet
// sidebar tile. The phrase list, the three state vars, the timing effect and the
// inline `blink` keyframe are all gone — nothing else in the file used any of
// them. It is still a whole-tile link to avanceschool.com.
function AvanceCard() {
  return (
    <a href="https://avanceschool.com" target="_blank" rel="noopener noreferrer"
      style={{display:'block',textDecoration:'none',background:'white',border:'0.5px solid #e8e8e8',borderRadius:'10px',padding:'12px 14px',marginTop:'16px'}}>
      <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'6px'}}>AVANCE SCHOOL</div>
      <div style={{fontSize:'12px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.45,marginBottom:'8px'}}>
        CE courses and Arizona real estate education.
      </div>
      <div style={{fontSize:'11.5px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>Start your CE →</div>
    </a>
  )
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
function Logo() {
  return (
    <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
      <div style={{color:'#fdbf00',fontSize:'22px',lineHeight:1}}>&#9654;</div>
      <div>
        <div style={{fontSize:'16px',fontWeight:900,letterSpacing:'2px',color:'white',lineHeight:1.2,fontFamily:FONT_HEAD}}>
          HOMEPROS <span style={{color:'#147efb'}}>HUB</span>
        </div>
        <div style={{fontSize:'9px',letterSpacing:'2px',color:'#666',fontFamily:FONT_HEAD}}>POWERED BY HOMEPROS</div>
      </div>
    </div>
  )
}

// ─── EVENTS PAGE ─────────────────────────────────────────────────────────────
const EVENTS_CONTENT_WIDTH = '1100px'

function EventsPage({ session, profile, isAdmin, showToast, isMobile, navigateTo, signOut }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('list')
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [showEventModal, setShowEventModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [filterType, setFilterType] = useState('all')
  // Same single-id shape the case lists use for their inline confirm.
  const [confirmDelete, setConfirmDelete] = useState(null)

  const fetchEvents = useCallback(async () => {
    const { data } = await supabase.from('events').select('*').order('event_date', { ascending: true })
    setEvents(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchEvents() }, [fetchEvents])

  useEffect(() => {
    const channel = supabase.channel('events-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => fetchEvents())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchEvents])

  async function handleDelete(id) {
    await supabase.from('events').delete().eq('id', id)
    setConfirmDelete(null)
    setSelectedEvent(null)
    showToast('Event deleted')
  }

  const now = new Date()
  const filtered = events.filter(e => {
    if (filterType !== 'all' && e.event_type !== filterType) return false
    return true
  })
  const upcomingEvents = filtered.filter(e => new Date(e.event_date) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))

  // Calendar helpers
  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDay = new Date(year, month, 1).getDay()
  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1))
  const eventsForDay = (day) => filtered.filter(e => {
    const d = new Date(e.event_date + 'T00:00:00')
    return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
  })

  // All three control groups ride in the title row now — toggle, type filter,
  // and the admin's add button. Measured at ~645px against ~1065px of available
  // summary width on a 1440px laptop, and the row already scrolls on mobile.
  const filterPill = sel => ({
    border:`0.5px solid ${sel ? '#147efb' : '#e8e8e8'}`,borderRadius:'18px',padding:'5px 12px',
    fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,whiteSpace:'nowrap',flexShrink:0,
    background:sel ? '#f4f4f2' : 'transparent',color:sel ? '#1d2e42' : '#666',
    cursor:'pointer',transition:'all .15s',
  })

  const summaryNode = (
    <div style={{display:'flex',alignItems:'center',gap:'8px',justifyContent:'flex-end',minWidth:0,overflowX:'auto'}}>

      {/* One segmented pill rather than two separate chips — these are two
          states of one control, not two independent filters. */}
      <div style={{display:'inline-flex',border:'0.5px solid #e8e8e8',borderRadius:'16px',overflow:'hidden',flexShrink:0}}>
        {[['list','List'],['calendar','Calendar']].map(([k,label]) => (
          <button key={k} onClick={()=>setViewMode(k)}
            style={{border:'none',padding:'5px 12px',fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,
              whiteSpace:'nowrap',cursor:'pointer',transition:'all .15s',
              background:viewMode===k ? '#f4f4f2' : 'transparent',
              color:viewMode===k ? '#1d2e42' : '#666'}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{width:'1px',height:'18px',background:'#e8e8e8',flexShrink:0}} />

      <button style={filterPill(filterType==='all')} onClick={()=>setFilterType('all')}>All</button>
      {EVENT_TYPE_OPTIONS.map(t => (
        <button key={t.key} style={filterPill(filterType===t.key)} onClick={()=>setFilterType(t.key)}>{t.label}</button>
      ))}

      {isAdmin && (
        <button
          onClick={()=>{setEditingEvent(null);setShowEventModal(true)}}
          style={{flexShrink:0,background:'#fdbf00',color:'#412402',border:'none',borderRadius:'8px',
            padding:'6px 14px',fontSize:'12px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',
            whiteSpace:'nowrap',cursor:'pointer'}}>
          Add event
        </button>
      )}
    </div>
  )

  // Same chip shape ContentCard uses.
  const eventChipStyle = (t) => {
    const c = EVENT_CHIP[t] || EVENT_CHIP_FALLBACK
    return { display:'inline-block',padding:'3px 8px',borderRadius:'12px',fontSize:'10px',
      fontWeight:600,fontFamily:FONT_BODY,whiteSpace:'nowrap',background:c.bg,color:c.fg }
  }

  const listNode = (
    <div>
        {viewMode === 'list' ? (
          /* LIST VIEW */
          <div style={{paddingBottom:'60px'}}>
            {upcomingEvents.length === 0 ? (
              <div style={S.emptyState}>
                <h3 style={{fontSize:'20px',color:'#999',fontFamily:FONT_HEAD,textTransform:'uppercase',letterSpacing:'1px'}}>
                  {isAdmin ? 'Add your first event' : 'No upcoming events — check back soon'}
                </h3>
              </div>
            ) : upcomingEvents.map(evt => {
              const d = new Date(evt.event_date + 'T00:00:00')
              const cfg = EVENT_TYPES[evt.event_type] || EVENT_TYPES['homepros-event']
              return (
                <div key={evt.id} onClick={()=>setSelectedEvent(evt)}
                  style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 15px',
                    marginBottom:'10px',display:'flex',gap:'14px',alignItems:'flex-start',cursor:'pointer'}}>
                  <div style={{minWidth:'46px',width:'46px',textAlign:'center',background:'#f4f4f2',borderRadius:'8px',padding:'8px 4px',flexShrink:0}}>
                    <div style={{fontSize:'9.5px',fontWeight:600,color:'#999',letterSpacing:'1px',fontFamily:FONT_BODY}}>{d.toLocaleString('en-US',{month:'short'}).toUpperCase()}</div>
                    <div style={{fontSize:'19px',fontWeight:400,color:'#1d2e42',fontFamily:FONT_BODY,lineHeight:1.25}}>{d.getDate()}</div>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'5px',flexWrap:'wrap'}}>
                      <span style={eventChipStyle(evt.event_type)}>{cfg.label}</span>
                      {evt.event_time && <span style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>{formatTime(evt.event_time)}</span>}
                    </div>
                    <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'3px'}}>{evt.title}</div>
                    {evt.location && (isURL(evt.location) ? <a href={evt.location} target="_blank" rel="noopener noreferrer" style={{fontSize:'11.5px',color:'#147efb',fontFamily:FONT_BODY,textDecoration:'none'}}>📍 Join online</a> : <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>📍 {evt.location}</div>)}
                    {evt.description && <div style={{fontSize:'11.5px',color:'#999',marginTop:'5px',lineHeight:1.45,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{evt.description}</div>}
                  </div>
                  {/* objectPosition:'top' for the same reason ContentCard uses it —
                      flyers put their title at the top, and a centered cover crop
                      shows the middle band instead. The detail modal's flyer is
                      large enough to need no crop hint. */}
                  {evt.flyer_url && <img src={evt.flyer_url} alt="" style={{width:'96px',height:'96px',objectFit:'cover',objectPosition:'top',borderRadius:'8px',flexShrink:0}} />}
                </div>
              )
            })}
          </div>
        ) : (
          /* CALENDAR VIEW */
          <div style={{paddingBottom:'60px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'16px',marginBottom:'20px'}}>
              <button onClick={prevMonth} style={S.monthNavBtn}>◀</button>
              <span style={{fontSize:'18px',fontWeight:800,fontFamily:FONT_HEAD,letterSpacing:'2px',textTransform:'uppercase',minWidth:'200px',textAlign:'center'}}>
                {currentMonth.toLocaleString('en-US',{month:'long',year:'numeric'})}
              </span>
              <button onClick={nextMonth} style={S.monthNavBtn}>▶</button>
            </div>
            {/* Seven day columns at ~50px each are unreadable on a phone. The
                grid scrolls rather than dropping to list view: the List/Calendar
                toggle is an explicit choice the agent made in the title row, and
                silently overriding it would ignore that. */}
            <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch',maxWidth:'100%'}}>
            <div style={{...S.calGrid,minWidth:'700px'}}>
              {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(d => (
                <div key={d} style={{...S.calHeader,background:'#f4f4f2',color:'#999',fontSize:'10px',fontWeight:600,letterSpacing:'1px',fontFamily:FONT_BODY,padding:'9px 8px'}}>{d}</div>
              ))}
              {Array.from({length:firstDay}).map((_,i) => (
                <div key={'e'+i} style={{...S.calDay,minHeight:'62px',...S.calDayOther}} />
              ))}
              {Array.from({length:daysInMonth}).map((_,i) => {
                const day = i + 1
                const dayEvents = eventsForDay(day)
                const isToday = day===now.getDate() && month===now.getMonth() && year===now.getFullYear()
                return (
                  <div key={day} style={{...S.calDay,minHeight:'62px',...(isToday?{background:'rgba(20,126,251,0.04)'}:{})}}>
                    <div style={{...S.calDayNum,fontWeight:500,fontFamily:FONT_BODY,...(isToday?{color:'#147efb'}:{})}}>{day}</div>
                    {dayEvents.slice(0,3).map(evt => {
                      const c = EVENT_CHIP[evt.event_type] || EVENT_CHIP_FALLBACK
                      return <div key={evt.id} style={{...S.calEvent,background:c.bg,color:c.fg,fontFamily:FONT_BODY,fontWeight:600,letterSpacing:0}} onClick={()=>setSelectedEvent(evt)}>{evt.title}</div>
                    })}
                    {dayEvents.length > 3 && <div style={{fontSize:'10px',color:'#999',fontFamily:FONT_HEAD,paddingLeft:'6px'}}>+{dayEvents.length-3} more</div>}
                  </div>
                )
              })}
            </div>
            </div>
          </div>
        )}
    </div>
  )

  return (
    <>
      <CaseShell
        isMobile={isMobile}
        title="Events"
        summary={summaryNode}
        setupOpen={false}
        onToggleSetup={null}
        setup={null}
        loading={loading}
        messages={[]}
        renderMessage={()=>null}
        emptyState={listNode}
        contentWidth={EVENTS_CONTENT_WIDTH}
        controls={null}
        showComposer={false}
        composerValue=""
        onComposerChange={()=>{}}
        onSubmit={()=>{}}
        composerPlaceholder=""
        sendDisabled
        footer={null}
      />

      {/* EVENT FORM MODAL */}
      {showEventModal && (
        <EventFormModal
          isMobile={isMobile}
          event={editingEvent}
          onClose={()=>{setShowEventModal(false);setEditingEvent(null)}}
          onSuccess={()=>{setShowEventModal(false);setEditingEvent(null);fetchEvents();showToast(editingEvent?'Event updated!':'Event published!')}} />
      )}

      {/* EVENT DETAIL MODAL */}
      {selectedEvent && (
        <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget){setConfirmDelete(null);setSelectedEvent(null)}}}>
          <div style={isMobile ? {...S.detailModal,width:'100%',height:'100%',maxWidth:'100%',maxHeight:'100%',borderRadius:0} : S.detailModal}>
            {selectedEvent.flyer_url && (
              <div style={S.detailMedia}>
                <img src={selectedEvent.flyer_url} alt={selectedEvent.title} style={{width:'100%',height:'100%',objectFit:'cover'}} />
              </div>
            )}
            <div style={S.detailBody}>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'12px'}}>
                <span style={eventChipStyle(selectedEvent.event_type)}>
                  {(EVENT_TYPES[selectedEvent.event_type]||EVENT_TYPES['homepros-event']).label}
                </span>
              </div>
              <div style={S.detailTitle}>{selectedEvent.title}</div>
              <div style={S.detailMeta}>
                {new Date(selectedEvent.event_date+'T00:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
                {selectedEvent.event_time && ` · ${formatTime(selectedEvent.event_time)}`}
                {selectedEvent.location && (isURL(selectedEvent.location) ? <> · <a href={selectedEvent.location} target="_blank" rel="noopener noreferrer" style={{color:'#147efb',textDecoration:'none'}}>📍 Join Online</a></> : ` · 📍 ${selectedEvent.location}`)}
              </div>
              {selectedEvent.description && <div style={S.detailContent}>{selectedEvent.description}</div>}
              {/* Delete used to fire on the first click. Same two-step the case
                  lists use, so destructive actions behave alike everywhere. */}
              <div style={S.detailActions}>
                <button style={S.btnOutline} onClick={()=>{setConfirmDelete(null);setSelectedEvent(null)}}>Close</button>
                {isAdmin && <button style={S.btnPrimary} onClick={()=>{setConfirmDelete(null);setEditingEvent(selectedEvent);setSelectedEvent(null);setShowEventModal(true)}}>Edit</button>}
                {isAdmin && (confirmDelete === selectedEvent.id ? (
                  <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
                    <span style={{fontSize:'12px',color:'#C8102E',fontFamily:FONT_BODY}}>Delete?</span>
                    <button onClick={()=>handleDelete(selectedEvent.id)} style={{...S.btnDanger,padding:'4px 10px',fontSize:'11px',flex:'none'}}>Yes</button>
                    <button onClick={()=>setConfirmDelete(null)} style={{background:'none',border:'1px solid #e8e8e8',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',cursor:'pointer',color:'#555',fontFamily:FONT_HEAD}}>No</button>
                  </div>
                ) : (
                  <button style={S.btnDanger} onClick={()=>setConfirmDelete(selectedEvent.id)}>Delete</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function isURL(str) { return str && (str.startsWith('http://') || str.startsWith('https://')) }

function formatTime(t) {
  if (!t) return ''
  const [h,m] = t.split(':')
  const hr = parseInt(h)
  const ampm = hr >= 12 ? 'PM' : 'AM'
  const h12 = hr % 12 || 12
  return `${h12}:${m} ${ampm}`
}

// ─── EVENT FORM MODAL ────────────────────────────────────────────────────────
function EventFormModal({ event, onClose, onSuccess, isMobile }) {
  const [title, setTitle] = useState(event?.title || '')
  const [description, setDescription] = useState(event?.description || '')
  const [eventDate, setEventDate] = useState(event?.event_date || '')
  const [eventTime, setEventTime] = useState(event?.event_time?.slice(0,5) || '')
  const [location, setLocation] = useState(event?.location || '')
  const [eventType, setEventType] = useState(event?.event_type || 'homepros-event')
  const [flyerFile, setFlyerFile] = useState(null)
  const [flyerPreview, setFlyerPreview] = useState(event?.flyer_url || null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function handleImageChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB'); return }
    setFlyerFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setFlyerPreview(ev.target.result)
    reader.readAsDataURL(file)
  }

  async function handleSubmit() {
    if (!title.trim()) { setError('Title is required'); return }
    if (!eventDate) { setError('Date is required'); return }
    setLoading(true); setError('')

    let flyerUrl = event?.flyer_url || null
    if (flyerFile) {
      const ext = flyerFile.name.split('.').pop()
      const fileName = `events/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('content-media').upload(fileName, flyerFile, { contentType: flyerFile.type })
      if (uploadError) { setError('Flyer upload failed: ' + uploadError.message); setLoading(false); return }
      const { data: urlData } = supabase.storage.from('content-media').getPublicUrl(fileName)
      flyerUrl = urlData.publicUrl
    }

    const payload = {
      title: title.trim(), description: description.trim() || null,
      event_date: eventDate, event_time: eventTime || null,
      location: location.trim() || null, event_type: eventType,
      flyer_url: flyerUrl,
    }

    if (event) {
      const { error: updateError } = await supabase.from('events').update(payload).eq('id', event.id)
      if (updateError) { setError(updateError.message); setLoading(false); return }
    } else {
      const { data: inserted, error: insertError } = await supabase.from('events').insert(payload).select().single()
      if (insertError) { setError(insertError.message); setLoading(false); return }
      try {
        const notifyBody = { title: inserted.title, caption: `${inserted.event_date}${inserted.event_time ? ' at ' + formatTime(inserted.event_time) : ''}${inserted.location ? ' — ' + inserted.location : ''}`, type: 'event', imageUrl: inserted.flyer_url || null }
        console.log('[EventNotify] Invoking notify-new-content with:', notifyBody)
        const { data: { session } } = await supabase.auth.getSession()
        const notifyRes = await supabase.functions.invoke('notify-new-content', {
          body: notifyBody,
          headers: { Authorization: `Bearer ${session.access_token}` }
        })
        console.log('[EventNotify] Response:', notifyRes)
      } catch (e) { console.warn('[EventNotify] Failed (non-blocking):', e) }

      // In-app notification (non-blocking)
      try {
        const eventDateStr = new Date(inserted.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const { data: { session: sess } } = await supabase.auth.getSession()
        await supabase.functions.invoke('create-notifications', {
          body: { message: `New Event: ${inserted.title} on ${eventDateStr}`, type: 'event', exclude_user_id: sess?.user?.id }
        })
      } catch (e) { console.warn('In-app notification failed (non-blocking):', e) }
    }
    setLoading(false); onSuccess()
  }

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={isMobile ? {...S.modal,width:'100%',height:'100%',maxWidth:'100%',maxHeight:'100%',borderRadius:0} : S.modal}>
        <div style={S.modalHeader}>
          <div style={S.modalTitle}>{event ? 'EDIT EVENT' : 'ADD EVENT'}</div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>EVENT TYPE</label>
          <div style={{...S.typeGrid,gridTemplateColumns:'repeat(3,1fr)'}}>
            {EVENT_TYPE_OPTIONS.map(t => (
              <div key={t.key} style={{...S.typeOpt,...(eventType===t.key?S.typeOptActive:{})}} onClick={()=>setEventType(t.key)}>
                <div style={{fontSize:'11px',fontWeight:700,color:eventType===t.key?'#147efb':'#555',letterSpacing:'0.5px',fontFamily:FONT_HEAD,textTransform:'uppercase'}}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>TITLE</label>
          <input style={S.input} placeholder="e.g. Monthly Team Meeting" value={title} onChange={e=>setTitle(e.target.value)} />
        </div>
        <div style={{display:'flex',gap:'12px'}}>
          <div style={{...S.fieldGroup,flex:1}}>
            <label style={S.label}>DATE</label>
            <input style={S.input} type="date" value={eventDate} onChange={e=>setEventDate(e.target.value)} />
          </div>
          <div style={{...S.fieldGroup,flex:1}}>
            <label style={S.label}>TIME (OPTIONAL)</label>
            <input style={S.input} type="time" value={eventTime} onChange={e=>setEventTime(e.target.value)} />
          </div>
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>LOCATION (OPTIONAL)</label>
          <input style={S.input} placeholder="e.g. HomePros Office, Phoenix AZ" value={location} onChange={e=>setLocation(e.target.value)} />
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>DESCRIPTION (OPTIONAL)</label>
          <textarea style={S.textarea} placeholder="Event details, agenda, what to bring..." value={description} onChange={e=>setDescription(e.target.value)} />
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>FLYER IMAGE (OPTIONAL)</label>
          <div style={S.fileDropZone}>
            {flyerPreview ? (
              <div style={{position:'relative',display:'inline-block'}}>
                <img src={flyerPreview} alt="Preview" style={{maxWidth:'100%',maxHeight:'200px',borderRadius:'6px'}} />
                <button style={S.removeImgBtn} onClick={()=>{setFlyerFile(null);setFlyerPreview(null)}}>✕</button>
              </div>
            ) : (
              <label style={S.fileLabel}>
                <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>Click to upload flyer</div>
                <input type="file" accept="image/*" onChange={handleImageChange} style={{display:'none'}} />
              </label>
            )}
          </div>
        </div>
        {error && <div style={S.errorBox}>{error}</div>}
        <div style={S.modalFooter}>
          <button style={S.btnOutline} onClick={onClose}>Cancel</button>
          <button style={{...S.btnPrimary,flex:2,opacity:loading?0.6:1}} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : event ? 'UPDATE EVENT' : 'PUBLISH EVENT'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── PROFILE PAGE ───────────────────────────────────────────────────────────
const PROFILE_CONTENT_WIDTH = '640px'

function ProfilePage({ session, profile, setProfile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [fullName, setFullName] = useState(profile?.full_name || '')
  const [licenseNumber, setLicenseNumber] = useState(profile?.license_number ?? '')
  const [langDefault, setLangDefault] = useState(profile?.lang_default === 'es' ? 'es' : 'en')
  const [phone, setPhone] = useState(profile?.phone || '')
  const [bio, setBio] = useState(profile?.bio || '')
  const [instagram, setInstagram] = useState(profile?.instagram || '')
  const [facebook, setFacebook] = useState(profile?.facebook || '')
  const [linkedin, setLinkedin] = useState(profile?.linkedin || '')
  const [zillowUrl, setZillowUrl] = useState(profile?.zillow_url || '')
  const [youtubeUrl, setYoutubeUrl] = useState(profile?.youtube_url || '')
  const [prequalifyUrl, setPrequalifyUrl] = useState(profile?.prequalify_url || '')
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [pwError, setPwError] = useState('')

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `avatars/${session.user.id}.${ext}`
    const { error } = await supabase.storage.from('content-media').upload(path, file, { upsert: true })
    if (error) { showToast('Upload failed: ' + error.message); setUploading(false); return }
    const { data } = supabase.storage.from('content-media').getPublicUrl(path)
    const url = data.publicUrl + '?t=' + Date.now()
    setAvatarUrl(url)
    await supabase.from('profiles').update({ avatar_url: url }).eq('id', session.user.id)
    setProfile(prev => ({ ...prev, avatar_url: url }))
    setUploading(false)
    showToast('Photo updated')
  }

  async function handleSave() {
    // Never save a form that was not built from this user's loaded profile.
    if (profile?.id !== session.user.id) { showToast('Your profile is still loading. Try again in a moment.'); return }
    setSaving(true)
    // Brokerage and armls_agent_id are admin-set: not required here and never
    // sent, so a profile save cannot change or clear them.
    const updates = { full_name: fullName.trim(), license_number: licenseNumber.trim() || null, phone: phone.trim(), bio: bio.trim(), instagram: instagram.trim(), facebook: facebook.trim(), linkedin: linkedin.trim(), zillow_url: zillowUrl.trim(), youtube_url: youtubeUrl.trim(), prequalify_url: prequalifyUrl.trim(), lang_default: langDefault }
    const { error } = await supabase.from('profiles').update(updates).eq('id', session.user.id)
    if (error) { showToast('Error: ' + error.message); setSaving(false); return }
    setProfile(prev => ({ ...prev, ...updates }))
    showToast('Profile saved')
    setSaving(false)
  }

  const initials = (fullName || session.user.email || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  const bodyNode = (
    <div>
        <div style={{background:'white',borderRadius:'10px',padding:'32px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',marginBottom:'24px'}}>
          {/* Avatar */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:'32px'}}>
            <div style={{position:'relative',width:'100px',height:'100px',marginBottom:'12px'}}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" style={{width:'100px',height:'100px',borderRadius:'50%',objectFit:'cover',border:'3px solid #147efb'}} />
              ) : (
                <div style={{width:'100px',height:'100px',borderRadius:'50%',background:'#1d2e42',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'32px',fontWeight:900,color:'#fdbf00',fontFamily:FONT_HEAD,border:'3px solid #147efb'}}>{initials}</div>
              )}
            </div>
            <label style={{cursor:'pointer',fontSize:'12px',fontWeight:700,color:'#147efb',letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase'}}>
              {uploading ? 'UPLOADING...' : 'CHANGE PHOTO'}
              <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{display:'none'}} />
            </label>
          </div>

          {/* Fields */}
          <div style={S.fieldGroup}>
            <label style={S.label}>FULL NAME</label>
            <input style={S.input} value={fullName} onChange={e=>setFullName(e.target.value)} />
          </div>
          {/* Admin-set. Same read-only treatment as EMAIL below; the note says
              who changes it. Read from the profile, not local state, because
              nothing on this page can edit it. */}
          <div style={S.fieldGroup}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:'8px'}}>
              <label style={S.label}>MLS AGENT ID</label>
              <span style={{fontSize:'12px',color:'#888',fontFamily:FONT_BODY}}>Set by your broker</span>
            </div>
            <input style={{...S.input,background:'#eee',color:'#999',cursor:'not-allowed'}} value={profile?.armls_agent_id || 'Not set'} readOnly />
          </div>
          <div style={S.fieldGroup}>
            <label style={S.label}>LICENSE NUMBER</label>
            <input style={S.input} value={licenseNumber} onChange={e=>setLicenseNumber(e.target.value)} placeholder="SA123456000" />
          </div>
          <div style={S.fieldGroup}>
            <label style={S.label}>EMAIL</label>
            <input style={{...S.input,background:'#eee',color:'#999',cursor:'not-allowed'}} value={session.user.email} readOnly />
          </div>
          <div style={S.fieldGroup}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:'8px'}}>
              <label style={S.label}>BROKERAGE</label>
              <span style={{fontSize:'12px',color:'#888',fontFamily:FONT_BODY}}>Set by your broker</span>
            </div>
            <input style={{...S.input,background:'#eee',color:'#999',cursor:'not-allowed'}} value={profile?.brokerage || 'Not set'} readOnly />
          </div>
          <div style={S.fieldGroup}>
            <label style={S.label}>PHONE</label>
            <input style={S.input} value={phone} onChange={e=>setPhone(e.target.value)} placeholder="480-555-1234" />
          </div>
          <div style={S.fieldGroup}>
            <label style={S.label}>BIO / ABOUT ME</label>
            <textarea style={S.textarea} value={bio} onChange={e=>setBio(e.target.value)} placeholder="Tell us about yourself..." />
          </div>

          <div style={{borderTop:'1px solid #e8e8e8',paddingTop:'20px',marginTop:'8px',marginBottom:'20px'}}>
            <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'2px',color:'#999',marginBottom:'16px',fontFamily:FONT_HEAD}}>LINKS</div>
            <div style={S.fieldGroup}>
              <label style={S.label}>INSTAGRAM</label>
              <input style={S.input} value={instagram} onChange={e=>setInstagram(e.target.value)} placeholder="https://instagram.com/yourhandle" />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>FACEBOOK</label>
              <input style={S.input} value={facebook} onChange={e=>setFacebook(e.target.value)} placeholder="https://facebook.com/yourpage" />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>LINKEDIN</label>
              <input style={S.input} value={linkedin} onChange={e=>setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/yourprofile" />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>ZILLOW CONNECT LINK</label>
              <input style={S.input} value={zillowUrl} onChange={e=>setZillowUrl(e.target.value)} placeholder="https://www.zillow.com/agent/connect-with-me/yourname" />
              <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,marginTop:'6px',lineHeight:1.5}}>Paste your Zillow connect link exactly as it appears — this is what visitors tap to connect with you on Zillow.</div>
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>YOUTUBE</label>
              <input style={S.input} value={youtubeUrl} onChange={e=>setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/@yourchannel" />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>PREQUALIFY</label>
              <input style={S.input} value={prequalifyUrl} onChange={e=>setPrequalifyUrl(e.target.value)} placeholder="https://yourlender.com/prequalify" />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>DEFAULT LANGUAGE</label>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                {[['en','English'],['es','Español']].map(([code,label]) => (
                  <button key={code} type="button" onClick={()=>setLangDefault(code)} style={{
                    padding:'10px 18px',
                    background: langDefault===code ? '#147efb' : 'white',
                    color: langDefault===code ? 'white' : '#1d2e42',
                    border: langDefault===code ? '1px solid #147efb' : '1px solid #ddd',
                    borderRadius:'6px',
                    cursor:'pointer',
                    fontSize:'13px',
                    fontWeight:600,
                    fontFamily:FONT_BODY,
                    transition:'all 0.15s',
                  }}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          <button onClick={handleSave} disabled={saving} style={{...S.btnPrimary,width:'100%',opacity:saving?0.6:1,cursor:saving?'not-allowed':'pointer',padding:'14px'}}>
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
        </div>

        {/* CHANGE PASSWORD */}
        <div style={{background:'white',borderRadius:'10px',padding:'32px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',marginBottom:'24px'}}>
          <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'2px',color:'#999',marginBottom:'20px',fontFamily:FONT_HEAD}}>CHANGE PASSWORD</div>
          <div style={S.fieldGroup}>
            <label style={S.label}>NEW PASSWORD</label>
            <input style={S.input} type="password" placeholder="Minimum 8 characters" value={newPassword} onChange={e=>{setNewPassword(e.target.value);setPwError('')}} />
          </div>
          <div style={S.fieldGroup}>
            <label style={S.label}>CONFIRM PASSWORD</label>
            <input style={S.input} type="password" placeholder="Re-enter new password" value={confirmPassword} onChange={e=>{setConfirmPassword(e.target.value);setPwError('')}} />
          </div>
          {pwError && <div style={{...S.errorBox,...(pwError.startsWith('✅')?{color:'#2d7a2d',borderColor:'rgba(45,122,45,0.3)',background:'rgba(45,122,45,0.06)'}:{})}}>{pwError}</div>}
          <button
            disabled={!newPassword || !confirmPassword || pwLoading}
            onClick={async()=>{
              if (newPassword !== confirmPassword) { setPwError('Passwords do not match'); return }
              if (newPassword.length < 8) { setPwError('Password must be at least 8 characters'); return }
              setPwLoading(true); setPwError('')
              const { error } = await supabase.auth.updateUser({ password: newPassword })
              if (error) { setPwError(error.message); setPwLoading(false); return }
              setNewPassword(''); setConfirmPassword('')
              // Setup marker for the invite screen (never an access control).
              // The password is already saved, so a failed marker write is
              // reported but does not undo the success.
              const stamp = new Date().toISOString()
              const { data: marked, error: markErr } = await supabase.from('profiles')
                .update({ password_set_at: stamp }).eq('id', session.user.id).select('id')
              setPwLoading(false)
              if (markErr || !marked || marked.length === 0) {
                console.error('[ProfilePage] password saved; marker write failed:', markErr)
                showToast('✅ Password updated, but it could not be recorded: ' + (markErr?.message || 'no row updated'))
                return
              }
              setProfile(p => (p && p.id === session.user.id ? { ...p, password_set_at: stamp } : p))
              showToast('✅ Password updated!')
            }}
            style={{...S.btnPrimary,width:'100%',opacity:(!newPassword||!confirmPassword||pwLoading)?0.5:1,cursor:(!newPassword||!confirmPassword||pwLoading)?'not-allowed':'pointer',padding:'14px'}}>
            {pwLoading ? 'UPDATING...' : 'UPDATE PASSWORD'}
          </button>
        </div>
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Profile"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={PROFILE_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── ADMIN PAGE ─────────────────────────────────────────────────────────────
const ADMIN_CONTENT_WIDTH = '1100px'

function AdminPage({ session, profile, showToast, navigateTo, isMobile, isAdmin, signOut }) {
  const [tab, setTab] = useState('agents')
  const [agents, setAgents] = useState([])
  const [contentItems, setContentItems] = useState([])
  const [downloads, setDownloads] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [resendingId, setResendingId] = useState(null)
  const [setPwAgent, setSetPwAgent] = useState(null)   // the agent whose password we're setting
  const [setPwValue, setSetPwValue] = useState('')
  const [setPwConfirm, setSetPwConfirm] = useState('')
  const [setPwError, setSetPwError] = useState('')
  const [setPwLoading, setSetPwLoading] = useState(false)
  const [allCases, setAllCases] = useState([])
  const [casesLoading, setCasesLoading] = useState(false)
  const [caseToolFilter, setCaseToolFilter] = useState('all')
  const [caseDateFilter, setCaseDateFilter] = useState('all')
  // The case row itself, not its id — the confirm modal names the agent and the
  // address, and they are already on the row from loadCases.
  const [caseConfirmDelete, setCaseConfirmDelete] = useState(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingCase, setDeletingCase] = useState(false)
  const [adminVendors, setAdminVendors] = useState([])
  const [showVendorForm, setShowVendorForm] = useState(false)
  const [editingVendor, setEditingVendor] = useState(null)
  const [vfName, setVfName] = useState('')
  const [vfCompany, setVfCompany] = useState('')
  const [vfCategory, setVfCategory] = useState('')
  const [vfSubtitle, setVfSubtitle] = useState('')
  const [vfPhone, setVfPhone] = useState('')
  const [vfEmail, setVfEmail] = useState('')
  const [vfNotes, setVfNotes] = useState('')
  const [vendorConfirmDelete, setVendorConfirmDelete] = useState(null)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  // Brokerage (required) and MLS agent ID (optional) are set by the admin at
  // invite time now; the agent can no longer choose them.
  const [inviteBrokerage, setInviteBrokerage] = useState('')
  const [inviteArmlsId, setInviteArmlsId] = useState('')
  // Tech fee: 'on' (default) gives the new agent an agent_billing row after the
  // invite succeeds; 'exempt' writes nothing, since no row means Exempt.
  const [inviteTechFee, setInviteTechFee] = useState('on')
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [armlsAgent, setArmlsAgent] = useState(null)   // agent whose ARMLS ID we're editing
  const [armlsValue, setArmlsValue] = useState('')
  const [armlsError, setArmlsError] = useState('')
  const [armlsLoading, setArmlsLoading] = useState(false)
  const [editProfileAgent, setEditProfileAgent] = useState(null)  // agent whose profile we're editing
  const [epFullName, setEpFullName] = useState('')
  const [epPhone, setEpPhone] = useState('')
  const [epLicense, setEpLicense] = useState('')
  const [epBrokerage, setEpBrokerage] = useState('')
  const [epBio, setEpBio] = useState('')
  const [epSlug, setEpSlug] = useState('')
  const [epInstagram, setEpInstagram] = useState('')
  const [epFacebook, setEpFacebook] = useState('')
  const [epLinkedin, setEpLinkedin] = useState('')
  const [epZillow, setEpZillow] = useState('')
  const [epYoutube, setEpYoutube] = useState('')
  const [epPrequalify, setEpPrequalify] = useState('')
  const [epLangDefault, setEpLangDefault] = useState('en')
  const [epError, setEpError] = useState('')
  const [epLoading, setEpLoading] = useState(false)
  const [epAvatarUrl, setEpAvatarUrl] = useState('')
  const [epAvatarUploading, setEpAvatarUploading] = useState(false)
  const [epAvatarError, setEpAvatarError] = useState('')
  const epAvatarInputRef = useRef(null)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    try {
      const [profilesRes, contentRes, downloadsRes] = await Promise.all([
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        supabase.from('content').select('*').order('created_at', { ascending: false }),
        supabase.from('content_downloads').select('*')
      ])
      // Fetch auth users separately so failure doesn't block the panel
      let authUsers = []
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession()
        const authRes = await supabase.functions.invoke('bright-api-to-invite-agent', {
          body: { action: 'list' },
          headers: { Authorization: `Bearer ${authSession.access_token}` }
        })
        authUsers = authRes?.data?.users || []
      } catch (e) {
        console.warn('Failed to fetch auth users (non-blocking):', e)
      }
      const authMap = {}
      authUsers.forEach(u => { authMap[u.id] = u })
      const enriched = (profilesRes.data || []).map(p => ({
        ...p,
        last_sign_in_at: authMap[p.id]?.last_sign_in_at || null
      }))
      setAgents(enriched)
      setContentItems(contentRes.data || [])
      setDownloads(downloadsRes?.data || [])
    } catch (e) {
      console.error('Admin loadData error:', e)
    }
    setLoading(false)
  }

  async function loadCases() {
    setCasesLoading(true)
    try {
      const [stratRes, binsrRes, profilesRes] = await Promise.all([
        supabase.from('property_cases').select('*, property_case_files(count), property_case_messages(count)').order('created_at', { ascending: false }),
        supabase.from('binsr_cases').select('*, binsr_case_files(count), binsr_case_messages(count)').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, full_name, email')
      ])
      const profileMap = {}
      ;(profilesRes.data || []).forEach(p => { profileMap[p.id] = p })
      const stratCases = (stratRes.data || []).map(c => ({
        ...c, tool: 'strategy', address: c.subject_address || 'Untitled',
        agentName: profileMap[c.agent_id]?.full_name || '—',
        agentEmail: profileMap[c.agent_id]?.email || '—',
        fileCount: c.property_case_files?.[0]?.count || 0,
        msgCount: c.property_case_messages?.[0]?.count || 0,
      }))
      const binsrCases = (binsrRes.data || []).map(c => ({
        ...c, tool: 'binsr', address: c.property_address || 'Untitled',
        agentName: profileMap[c.user_id]?.full_name || '—',
        agentEmail: profileMap[c.user_id]?.email || '—',
        fileCount: c.binsr_case_files?.[0]?.count || 0,
        msgCount: c.binsr_case_messages?.[0]?.count || 0,
      }))
      const combined = [...stratCases, ...binsrCases].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      setAllCases(combined)
    } catch (e) { console.error('loadCases error:', e) }
    setCasesLoading(false)
  }

  useEffect(() => { if (tab === 'cases') loadCases() }, [tab])

  function closeCaseDelete() {
    setCaseConfirmDelete(null)
    setDeleteConfirmText('')
  }

  // Both tools go through here. The table names differ; the sequence does not.
  //
  // FILE NAMES ARE READ FIRST, before anything is deleted. The previous version
  // deleted the file rows and THEN selected file_name from the same table for
  // the storage paths, which always came back empty — so no admin case delete
  // has ever removed a storage object. Orphaned PDFs from every prior delete are
  // still sitting in both buckets and will need a separate sweep.
  //
  // THE CASE DELETE RUNS BEFORE THE CHILD DELETES, which is a change from how
  // this read before. All four foreign keys are ON DELETE CASCADE, so the case
  // delete removes the children by itself; running it first means a delete that
  // is REFUSED destroys nothing. The old order deleted messages and files up
  // front, so a refusal — the exact case this function now detects — would have
  // emptied the case and left it standing. The explicit child deletes stay
  // afterwards: no-ops under cascade, and the safety net if cascade is ever
  // dropped.
  //
  // EVERY DELETE USES .select(), because a row refused by RLS is NOT an error.
  // PostgREST applies the policy as a filter, so a refused delete matches zero
  // rows and comes back { error: null } — indistinguishable from success unless
  // you count the rows it returned. That is why this looked like it worked for
  // as long as it did.
  async function deleteAdminCase(c) {
    if (deletingCase) return
    const isStrategy = c.tool === 'strategy'
    const caseTable = isStrategy ? 'property_cases' : 'binsr_cases'
    const msgsTable = isStrategy ? 'property_case_messages' : 'binsr_case_messages'
    const filesTable = isStrategy ? 'property_case_files' : 'binsr_case_files'
    const bucket = isStrategy ? 'property-docs' : 'binsr-docs'
    const ownerId = isStrategy ? c.agent_id : c.user_id

    setDeletingCase(true)
    try {
      // 1. Names, while the rows still exist.
      const { data: caseFiles, error: listErr } = await supabase
        .from(filesTable).select('file_name, storage_path').eq('case_id', c.id)
      if (listErr) console.error('admin delete: could not list files:', listErr.message)

      // 2. The decision. Nothing has been destroyed at this point.
      const { data: deleted, error: caseErr } = await supabase
        .from(caseTable).delete().eq('id', c.id).select()
      if (caseErr) { showToast('Delete failed: ' + caseErr.message); closeCaseDelete(); return }
      if (!deleted || deleted.length === 0) {
        showToast('Delete was refused — the case is still there. You may not have permission to delete it.')
        closeCaseDelete()
        return
      }

      // 3. Cleanup. The case is gone; none of this can change that, so errors
      //    are logged rather than reported as a failed delete.
      const { error: msgErr } = await supabase.from(msgsTable).delete().eq('case_id', c.id).select()
      if (msgErr) console.error('admin delete: messages:', msgErr.message)
      const { error: fileErr } = await supabase.from(filesTable).delete().eq('case_id', c.id).select()
      if (fileErr) console.error('admin delete: file rows:', fileErr.message)

      // The stored path is exact, including a file a shared user uploaded under
      // their own folder; the rebuilt owner path covers rows without one.
      const paths = (caseFiles || []).map(f => f.storage_path || `${ownerId}/${c.id}/${f.file_name}`)
      if (paths.length) {
        const { error: stErr } = await supabase.storage.from(bucket).remove(paths)
        if (stErr) console.error('admin delete: storage remove failed:', stErr.message)
      }

      setAllCases(prev => prev.filter(x => x.id !== c.id))
      closeCaseDelete()
      showToast('Case deleted')
    } catch (e) {
      showToast('Delete failed: ' + e.message)
    } finally {
      setDeletingCase(false)
    }
  }

  async function loadAdminVendors() {
    const { data } = await supabase.from('vendors').select('*').order('name')
    setAdminVendors(data || [])
  }

  useEffect(() => { if (tab === 'vendors') loadAdminVendors() }, [tab])

  function openVendorForm(vendor) {
    setEditingVendor(vendor || null)
    setVfName(vendor?.name || ''); setVfCompany(vendor?.company || ''); setVfCategory(vendor?.category || '')
    setVfSubtitle(vendor?.subtitle || '')
    setVfPhone(vendor?.phone || ''); setVfEmail(vendor?.email || ''); setVfNotes(vendor?.notes || '')
    setShowVendorForm(true)
  }

  async function saveVendor() {
    if (!vfName.trim() || !vfCategory) { showToast('Name and category required'); return }
    if (VENDOR_SUBTYPES_REQUIRED.includes(vfCategory) && !vfSubtitle.trim()) { showToast(`Subtype is required for ${vfCategory}`); return }
    const payload = { name: vfName.trim(), company: vfCompany.trim() || null, category: vfCategory, subtitle: vfSubtitle.trim() || null, phone: vfPhone.trim() || null, email: vfEmail.trim() || null, notes: vfNotes.trim() || null }
    if (editingVendor) {
      await supabase.from('vendors').update(payload).eq('id', editingVendor.id)
    } else {
      payload.added_by = session.user.id
      await supabase.from('vendors').insert(payload)
    }
    setShowVendorForm(false)
    loadAdminVendors()
    showToast(editingVendor ? 'Vendor updated' : 'Vendor added')
  }

  async function deleteVendor(id) {
    await supabase.from('vendors').delete().eq('id', id)
    setAdminVendors(prev => prev.filter(v => v.id !== id))
    setVendorConfirmDelete(null)
    showToast('Vendor deleted')
  }

  async function removeAgent(agent) {
    if (agent.id === session.user.id) { showToast('Cannot remove yourself'); return }
    const { data: { session: authSession } } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke('bright-api-to-invite-agent', {
      body: { action: 'delete', userId: agent.id },
      headers: { Authorization: `Bearer ${authSession.access_token}` }
    })
    if (error || data?.error) { showToast('Error: ' + (data?.error || error?.message)); return }
    setAgents(prev => prev.filter(a => a.id !== agent.id))
    setConfirmDelete(null)
    showToast('Agent removed')
  }

  async function handleResendInvite(agent) {
    if (resendingId) return
    setResendingId(agent.id)
    const { data: { session: authSession } } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke('bright-api-to-invite-agent', {
      body: { email: agent.email, full_name: agent.full_name },
      headers: { Authorization: `Bearer ${authSession.access_token}` }
    })
    setResendingId(null)
    if (error || data?.error) { showToast('Could not resend: ' + (data?.error || error?.message || 'unknown')); return }
    showToast('Invite resent to ' + agent.email)
  }

  async function handleSetPassword() {
    if (setPwValue !== setPwConfirm) { setSetPwError('Passwords do not match'); return }
    if (setPwValue.length < 8) { setSetPwError('Password must be at least 8 characters'); return }
    setSetPwLoading(true); setSetPwError('')
    // Pass the admin's token explicitly so the function can verify caller role.
    const { data: { session } } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke('admin-set-password', {
      body: { userId: setPwAgent.id, newPassword: setPwValue },
      headers: { Authorization: `Bearer ${session.access_token}` }
    })
    setSetPwLoading(false)
    if (error || data?.error) {
      // Prefer data.error; else pull the real message from the function's response
      // body. invoke() collapses a non-2xx (e.g. the 403 owner-protection) into a
      // generic "non-2xx" message — the actual { error } is on error.context (the Response).
      let msg = data?.error
      if (!msg && error) {
        try {
          const body = await error.context?.json?.()
          msg = body?.error
        } catch (_) {}
        msg = msg || error.message || 'Failed to set password'
      }
      setSetPwError(msg || 'Failed to set password')
      return
    }
    showToast('Password set for ' + setPwAgent.email)
    setSetPwAgent(null); setSetPwValue(''); setSetPwConfirm('')
  }

  async function handleSetRole(agent, newRole) {
    const who = agent.full_name || agent.email
    const prompt = newRole === 'admin' ? `Make ${who} an admin?` : `Remove admin access from ${who}?`
    if (!window.confirm(prompt)) return
    const { data: { session: authSession } } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke('admin-set-role', {
      body: { userId: agent.id, newRole },
      headers: { Authorization: `Bearer ${authSession.access_token}` }
    })
    if (error || data?.error) {
      // Surface the real function message (owner-protection etc.), not the generic non-2xx.
      let msg = data?.error
      if (!msg && error) {
        try { const body = await error.context?.json?.(); msg = body?.error } catch (_) {}
        msg = msg || error.message || 'Failed to change role'
      }
      showToast(msg || 'Failed to change role')
      return
    }
    showToast(newRole === 'admin' ? `${who} is now an admin` : `${who} is now an agent`)
    loadData()
  }

  async function handleSaveArmls() {
    const val = armlsValue.trim().toLowerCase()   // ARMLS IDs are lowercase (e.g. ab283)
    setArmlsLoading(true); setArmlsError('')
    // Goes through the admin edge function (service role) — a direct profiles
    // update is blocked by RLS (agents can only update their own row).
    const { data: { session } } = await supabase.auth.getSession()
    const { data, error } = await supabase.functions.invoke('admin-set-armls', {
      body: { userId: armlsAgent.id, armlsId: val },
      headers: { Authorization: `Bearer ${session.access_token}` }
    })
    setArmlsLoading(false)
    if (error || data?.error) {
      // surface the real message like handleSetPassword does
      let msg = data?.error
      if (!msg && error) { try { const b = await error.context?.json?.(); msg = b?.error } catch(_){} msg = msg || error.message }
      setArmlsError(msg || 'Could not save'); return
    }
    showToast('ARMLS ID saved for ' + (armlsAgent.full_name || armlsAgent.email))
    setArmlsAgent(null)
    loadData()
  }

  function openEditProfile(agent) {
    setEditProfileAgent(agent)
    setEpFullName(agent.full_name || '')
    setEpPhone(agent.phone || '')
    setEpLicense(agent.license_number || '')
    setEpBrokerage(agent.brokerage || '')
    setEpBio(agent.bio || '')
    setEpSlug(agent.slug || '')
    setEpInstagram(agent.instagram || '')
    setEpFacebook(agent.facebook || '')
    setEpLinkedin(agent.linkedin || '')
    setEpZillow(agent.zillow_url || '')
    setEpYoutube(agent.youtube_url || '')
    setEpPrequalify(agent.prequalify_url || '')
    setEpLangDefault(agent.lang_default === 'es' ? 'es' : 'en')
    setEpAvatarUrl(agent.avatar_url || '')
    setEpAvatarError('')
    setEpError('')
  }

  async function handleSaveProfile() {
    if (!editProfileAgent) return
    setEpLoading(true); setEpError('')
    const slug = epSlug.trim()
    // The agent page also answers at /<slug>, so a slug that is one of the
    // site's own paths (/join, /strategy, ...) would never reach it.
    if (slug && FUNNEL_RESERVED.includes(slug.toLowerCase())) {
      setEpError('That slug is a reserved site address. Choose a different one.'); setEpLoading(false); return
    }
    // Enforce slug uniqueness in app code so we return a clean error rather than
    // letting the DB unique constraint throw.
    if (slug) {
      const { data: dupe, error: dupeErr } = await supabase
        .from('profiles').select('id').eq('slug', slug).neq('id', editProfileAgent.id).limit(1)
      if (dupeErr) { setEpError('Could not verify slug: ' + dupeErr.message); setEpLoading(false); return }
      if (dupe && dupe.length) { setEpError('That slug is already used by another agent. Choose a different one.'); setEpLoading(false); return }
    }
    // Admins can write another agent's row directly (RLS admin UPDATE policy).
    const updates = {
      full_name: epFullName.trim(),
      phone: epPhone.trim(),
      license_number: epLicense.trim() || null,
      // null, not '': the profiles_brokerage_valid CHECK rejects an empty
      // string, which used to fail the whole save for an unassigned agent.
      brokerage: epBrokerage || null,
      bio: epBio.trim(),
      slug: slug || null,
      instagram: epInstagram.trim(),
      facebook: epFacebook.trim(),
      linkedin: epLinkedin.trim(),
      zillow_url: epZillow.trim(),
      youtube_url: epYoutube.trim(),
      prequalify_url: epPrequalify.trim(),
      lang_default: epLangDefault,
    }
    const { error } = await supabase.from('profiles').update(updates).eq('id', editProfileAgent.id)
    setEpLoading(false)
    if (error) { setEpError('Save failed: ' + error.message); return }
    showToast('Profile saved for ' + (epFullName.trim() || editProfileAgent.email))
    setEditProfileAgent(null)
    loadData()
  }

  // Avatar upload for the agent being edited — same bucket/path/cache-buster as
  // ProfilePage.handleAvatarUpload, but keyed on editProfileAgent.id. Writes
  // avatar_url immediately (independent of the Save Profile button).
  async function handleEpAvatarUpload(e) {
    const file = e.target.files?.[0]
    if (!file || !editProfileAgent) return
    const ext = (file.name.split('.').pop() || '').toLowerCase()
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) { setEpAvatarError('Image must be jpg, png, or webp'); return }
    if (file.size > 5 * 1024 * 1024) { setEpAvatarError('Image exceeds 5MB'); return }
    setEpAvatarUploading(true); setEpAvatarError('')
    const path = `avatars/${editProfileAgent.id}.${ext}`
    const { error: upErr } = await supabase.storage.from('content-media').upload(path, file, { upsert: true })
    if (upErr) { setEpAvatarError('Upload failed: ' + upErr.message); setEpAvatarUploading(false); return }
    const { data } = supabase.storage.from('content-media').getPublicUrl(path)
    const url = data.publicUrl + '?t=' + Date.now()
    const { error: dbErr } = await supabase.from('profiles').update({ avatar_url: url }).eq('id', editProfileAgent.id)
    if (dbErr) { setEpAvatarError('Save failed: ' + dbErr.message); setEpAvatarUploading(false); return }
    setEpAvatarUrl(url)
    setEpAvatarUploading(false)
    if (epAvatarInputRef.current) epAvatarInputRef.current.value = ''
    showToast('Photo updated')
  }

  async function deleteContent(item) {
    if (item.image_url) {
      const path = item.image_url.split('/storage/v1/object/public/')[1]
      if (path) await supabase.storage.from(path.split('/')[0]).remove([path.split('/').slice(1).join('/')])
    }
    await supabase.from('content').delete().eq('id', item.id)
    setContentItems(prev => prev.filter(c => c.id !== item.id))
    showToast('Content deleted')
  }

  async function sendInvite() {
    if (!inviteName.trim() || !inviteEmail.trim()) { setInviteError('Name and email are required'); return }
    if (!inviteBrokerage) { setInviteError('Brokerage is required'); return }
    setInviteLoading(true)
    setInviteError('')
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      // armls_agent_id only when entered; the function normalizes it and adds
      // each field to the profile only when provided.
      const armlsId = inviteArmlsId.trim()
      const { data, error } = await supabase.functions.invoke('bright-api-to-invite-agent', {
        body: { email: inviteEmail.trim(), full_name: inviteName.trim(), brokerage: inviteBrokerage, ...(armlsId ? { armls_agent_id: armlsId } : {}) },
        headers: { Authorization: `Bearer ${authSession.access_token}` }
      })
      if (error) { setInviteError(error.message || 'Failed to send invite'); setInviteLoading(false); return }
      if (data?.error) { setInviteError(data.error); setInviteLoading(false); return }
      // Tech fee row, written from the admin's session (agent_billing RLS lets
      // admins insert). The invite has already gone out, so a failure here is
      // reported and left for /admin/billing rather than undoing anything.
      let billingNote = ''
      if (inviteTechFee === 'on') {
        if (!data?.userId) {
          billingNote = ' Tech fee not set: no user id came back. Turn it on in Billing.'
        } else {
          const { error: billErr } = await supabase.from('agent_billing').insert({ user_id: data.userId, tech_fee: 'on' })
          if (billErr && billErr.code !== '23505') {
            console.error('[sendInvite] agent_billing insert failed:', billErr)
            billingNote = ' Tech fee not set (' + billErr.message + '). Turn it on in Billing.'
          }
        }
      }
      // Send welcome email
      try {
        await supabase.functions.invoke('notify-new-content', { body: { type: 'welcome', name: inviteName.trim(), email: inviteEmail.trim() } })
      } catch (e) { console.error('Welcome email error:', e) }
      showToast('Invite sent to ' + inviteEmail.trim() + '.' + billingNote, !!billingNote)
      setShowInvite(false)
      setInviteName('')
      setInviteEmail('')
      setInviteBrokerage('')
      setInviteArmlsId('')
      setInviteTechFee('on')
      loadData()
    } catch (e) {
      setInviteError(e.message || 'Failed to send invite')
    }
    setInviteLoading(false)
  }

  const summaryNode = (
    // Same compact segmented pill Events and Open House use. The old shared tab
    // tokens were a full-width control with a solid blue active segment — this
    // was the last consumer, so those tokens are gone from styles.js.
    <div style={{display:'flex',justifyContent:'flex-end',overflowX:'auto',minWidth:0}}>
      <div style={{display:'inline-flex',border:'0.5px solid #e8e8e8',borderRadius:'16px',overflow:'hidden',flexShrink:0}}>
        {[['agents','Agents'],['cases','All cases'],['vendors','Vendors'],['content-admin','Content'],['stats','Stats']].map(([id,label]) => (
          <button key={id} onClick={()=>setTab(id)}
            style={{border:'none',padding:'5px 12px',fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,
              whiteSpace:'nowrap',flexShrink:0,cursor:'pointer',transition:'all .15s',
              background: tab===id ? '#f4f4f2' : 'transparent',
              color: tab===id ? '#1d2e42' : '#666'}}>
            {label}
          </button>
        ))}
      </div>
    </div>
  )

  const bodyNode = (
    <div>

        {tab === 'agents' && (
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
              <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>{agents.length} total agents</div>
              <button onClick={()=>{setShowInvite(true);setInviteError('')}} style={{background:'#147efb',color:'white',border:'none',borderRadius:'6px',padding:'10px 18px',fontSize:'12px',fontWeight:700,cursor:'pointer',letterSpacing:'1px',fontFamily:FONT_HEAD}}>+ ADD AGENT</button>
            </div>
            <div style={{background:'white',borderRadius:'10px',overflowX:'auto',overflowY:'hidden',WebkitOverflowScrolling:'touch',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'900px'}}>
                <thead>
                  <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                    {['FULL NAME','EMAIL','ROLE','STATUS','JOINED','LAST SEEN',''].map(h => (
                      <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {agents.map(agent => (
                    <tr key={agent.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                      <td style={{padding:'12px 16px',fontWeight:600,color:'#222'}}>{agent.full_name || '—'}</td>
                      <td style={{padding:'12px 16px',color:'#555'}}>
                        {agent.email || '—'}
                        <div style={{fontSize:'11px',marginTop:'3px',fontFamily:'monospace',color: agent.armls_agent_id ? '#888' : '#c0392b'}}>
                          {agent.armls_agent_id ? ('ARMLS: ' + agent.armls_agent_id) : 'no ARMLS ID'}
                        </div>
                      </td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',
                          background: agent.role === 'admin' ? 'rgba(20,126,251,0.1)' : 'rgba(0,0,0,0.04)',
                          color: agent.role === 'admin' ? '#147efb' : '#999',
                          border: agent.role === 'admin' ? '1px solid rgba(20,126,251,0.3)' : '1px solid #e8e8e8'
                        }}>{agent.role || 'agent'}</span>
                      </td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',
                          background: agent.last_sign_in_at ? 'rgba(45,122,45,0.1)' : 'rgba(253,191,0,0.15)',
                          color: agent.last_sign_in_at ? '#2d7a2d' : '#b8860b',
                          border: agent.last_sign_in_at ? '1px solid rgba(45,122,45,0.3)' : '1px solid rgba(253,191,0,0.4)'
                        }}>{agent.last_sign_in_at ? 'ACTIVE' : 'INVITED'}</span>
                      </td>
                      <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{agent.created_at ? new Date(agent.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                      <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{agent.last_seen ? new Date(agent.last_seen).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                      <td style={{padding:'12px 16px',textAlign:'right'}}>
                        <button onClick={()=>handleResendInvite(agent)} disabled={resendingId===agent.id} style={{
                          marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',
                          cursor: resendingId===agent.id ? 'wait' : 'pointer',
                          opacity: resendingId===agent.id ? 0.6 : 1,
                          // Emphasize (filled) for agents who haven't logged in yet (INVITED); outline for ACTIVE.
                          background: agent.last_sign_in_at ? 'transparent' : '#147efb',
                          color: agent.last_sign_in_at ? '#147efb' : 'white',
                          border: '1px solid ' + (agent.last_sign_in_at ? 'rgba(20,126,251,0.4)' : '#147efb'),
                        }}>{resendingId===agent.id ? 'SENDING...' : 'RESEND INVITE'}</button>
                        <button onClick={()=>{setSetPwAgent(agent);setSetPwValue('');setSetPwConfirm('');setSetPwError('')}} style={{
                          marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',cursor:'pointer',
                          background:'transparent',color:'#1d2e42',border:'1px solid rgba(29,46,66,0.35)',
                        }}>SET PASSWORD</button>
                        <button onClick={()=>{setArmlsAgent(agent);setArmlsValue(agent.armls_agent_id || '');setArmlsError('')}} style={{
                          marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',cursor:'pointer',
                          background:'transparent',color:'#6d28d9',border:'1px solid rgba(109,40,217,0.4)',
                        }}>ARMLS ID</button>
                        <button onClick={()=>openEditProfile(agent)} style={{
                          marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',cursor:'pointer',
                          background:'transparent',color:'#0f766e',border:'1px solid rgba(15,118,110,0.4)',
                        }}>EDIT PROFILE</button>
                        {agent.is_owner ? (
                          <span style={{display:'inline-block',marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',color:'#999',background:'rgba(0,0,0,0.04)',border:'1px solid #e8e8e8'}}>OWNER</span>
                        ) : agent.role === 'admin' ? (
                          <button onClick={()=>handleSetRole(agent,'agent')} style={{
                            marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',cursor:'pointer',
                            background:'transparent',color:'#b8860b',border:'1px solid rgba(184,134,11,0.4)',
                          }}>REMOVE ADMIN</button>
                        ) : (
                          <button onClick={()=>handleSetRole(agent,'admin')} style={{
                            marginRight:'8px',marginBottom:'4px',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px',cursor:'pointer',
                            background:'transparent',color:'#2d7a2d',border:'1px solid rgba(45,122,45,0.4)',
                          }}>MAKE ADMIN</button>
                        )}
                        {agent.id !== session.user.id && (
                          <button onClick={()=>setConfirmDelete(agent)} style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'4px'}}>REMOVE</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'cases' && (
          <div>
            {/* Filters */}
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginBottom:'16px',alignItems:'center'}}>
              {[['all','All'],['strategy','Strategy'],['binsr','BINSR']].map(([k,l]) => (
                <button key={k} onClick={()=>setCaseToolFilter(k)} style={{...S.chip,...(caseToolFilter===k?S.chipActive:{})}}>{l}</button>
              ))}
              <div style={{width:'1px',height:'20px',background:'#e8e8e8',margin:'0 4px'}} />
              {[['all','All Time'],['7','Last 7 Days'],['30','Last 30 Days']].map(([k,l]) => (
                <button key={k} onClick={()=>setCaseDateFilter(k)} style={{...S.chip,...(caseDateFilter===k?S.chipActive:{})}}>{l}</button>
              ))}
            </div>

            {casesLoading ? (
              <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading cases...</div>
            ) : (() => {
              let filtered = allCases
              if (caseToolFilter !== 'all') filtered = filtered.filter(c => c.tool === caseToolFilter)
              if (caseDateFilter !== 'all') {
                const daysAgo = new Date(Date.now() - parseInt(caseDateFilter) * 24 * 60 * 60 * 1000)
                filtered = filtered.filter(c => new Date(c.created_at) > daysAgo)
              }
              if (filtered.length === 0) return (
                <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>
                  {allCases.length === 0 ? 'No agents have created cases yet.' : 'No cases found for this filter.'}
                </div>
              )
              return (
                <div style={{background:'white',borderRadius:'10px',overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'700px'}}>
                      <thead>
                        <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                          {['TOOL','AGENT','ADDRESS','CREATED','FILES','MSGS',''].map(h => (
                            <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(c => (
                          <tr key={c.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                            <td style={{padding:'12px 16px'}}>
                              <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',
                                background:c.tool==='strategy'?'rgba(253,191,0,0.15)':'rgba(20,126,251,0.1)',
                                color:c.tool==='strategy'?'#b8860b':'#147efb',
                                border:c.tool==='strategy'?'1px solid rgba(253,191,0,0.4)':'1px solid rgba(20,126,251,0.3)'
                              }}>{c.tool==='strategy'?'STRATEGY':'BINSR'}</span>
                            </td>
                            <td style={{padding:'12px 16px'}}>
                              <div style={{fontWeight:600,color:'#222'}}>{c.agentName}</div>
                              <div style={{fontSize:'11px',color:'#999'}}>{c.agentEmail}</div>
                            </td>
                            <td style={{padding:'12px 16px',fontWeight:600,color:'#222',maxWidth:'250px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.address}</td>
                            <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{timeAgo(c.created_at)}</td>
                            <td style={{padding:'12px 16px',color:'#555'}}>{c.fileCount}</td>
                            <td style={{padding:'12px 16px',color:'#555'}}>{c.msgCount}</td>
                            <td style={{padding:'12px 16px',textAlign:'right',whiteSpace:'nowrap'}}>
                              <button onClick={()=>navigateTo(c.tool, c.id)} style={{background:'#147efb',color:'white',border:'none',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginRight:'6px'}}>VIEW</button>
                              {/* Opens the confirm modal. The inline Yes/No that
                                  used to sit here was one click from destroying
                                  another agent's case and its files. */}
                              <button onClick={()=>{setDeleteConfirmText('');setCaseConfirmDelete(c)}} style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>DELETE</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })()}
          </div>
        )}

        {tab === 'vendors' && (
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
              <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>{adminVendors.length} vendors</div>
              <button onClick={()=>openVendorForm(null)} style={{background:'#147efb',color:'white',border:'none',borderRadius:'6px',padding:'10px 18px',fontSize:'12px',fontWeight:700,cursor:'pointer',letterSpacing:'1px',fontFamily:FONT_HEAD}}>+ ADD VENDOR</button>
            </div>
            <div style={{background:'white',borderRadius:'10px',overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'600px'}}>
                  <thead>
                    <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                      {['NAME','COMPANY','CATEGORY','SUBTYPE','PHONE','EMAIL',''].map(h => (
                        <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminVendors.map(v => (
                      <tr key={v.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                        <td style={{padding:'12px 16px',fontWeight:600,color:'#222'}}>{v.name}</td>
                        <td style={{padding:'12px 16px',color:'#555'}}>{v.company || '—'}</td>
                        <td style={{padding:'12px 16px'}}><span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,background:'rgba(20,126,251,0.1)',color:'#147efb',border:'1px solid rgba(20,126,251,0.3)'}}>{v.category}</span></td>
                        <td style={{padding:'12px 16px',color:'#555'}}>{v.subtitle || '—'}</td>
                        <td style={{padding:'12px 16px',color:'#555'}}>{v.phone || '—'}</td>
                        <td style={{padding:'12px 16px',color:'#555'}}>{v.email || '—'}</td>
                        <td style={{padding:'12px 16px',textAlign:'right',whiteSpace:'nowrap'}}>
                          <button onClick={()=>openVendorForm(v)} style={{background:'#147efb',color:'white',border:'none',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginRight:'6px'}}>EDIT</button>
                          {vendorConfirmDelete === v.id ? (
                            <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}>
                              <button onClick={()=>deleteVendor(v.id)} style={{...S.btnDanger,padding:'4px 10px',fontSize:'11px',flex:'none'}}>Yes</button>
                              <button onClick={()=>setVendorConfirmDelete(null)} style={{background:'none',border:'1px solid #e8e8e8',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',cursor:'pointer',color:'#555',fontFamily:FONT_HEAD}}>No</button>
                            </span>
                          ) : (
                            <button onClick={()=>setVendorConfirmDelete(v.id)} style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>DELETE</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {adminVendors.length === 0 && <tr><td colSpan={7} style={{padding:'40px 20px',textAlign:'center',color:'#999',fontFamily:FONT_BODY}}>No vendors yet. Click + Add Vendor to start building the directory.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {showVendorForm && (
              <div style={S.overlay} onClick={()=>setShowVendorForm(false)}>
                <div style={{...S.modal,maxWidth:'480px'}} onClick={e=>e.stopPropagation()}>
                  <div style={S.modalHeader}>
                    <div style={S.modalTitle}>{editingVendor ? 'EDIT VENDOR' : 'ADD VENDOR'}</div>
                    <button style={S.closeBtn} onClick={()=>setShowVendorForm(false)}>×</button>
                  </div>
                  <div style={S.field}><div style={S.label}>NAME *</div><input value={vfName} onChange={e=>setVfName(e.target.value)} style={S.input} placeholder="John Smith" /></div>
                  <div style={S.field}><div style={S.label}>COMPANY</div><input value={vfCompany} onChange={e=>setVfCompany(e.target.value)} style={S.input} placeholder="ABC Inspections LLC" /></div>
                  <div style={S.field}><div style={S.label}>CATEGORY *</div>
                    <select value={vfCategory} onChange={e=>{setVfCategory(e.target.value); setVfSubtitle('')}} style={{...S.input,cursor:'pointer'}}>
                      <option value="">Select category...</option>
                      {VENDOR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {Array.isArray(VENDOR_SUBTYPES[vfCategory]) && VENDOR_SUBTYPES[vfCategory].length > 0 && (
                    <div style={S.field}>
                      <div style={S.label}>
                        SUBTYPE{VENDOR_SUBTYPES_REQUIRED.includes(vfCategory) ? <span style={{color:'#C8102E'}}> *</span> : ' (optional)'}
                      </div>
                      <select value={vfSubtitle} onChange={e=>setVfSubtitle(e.target.value)} style={{...S.input,cursor:'pointer'}}>
                        <option value="">-- Select --</option>
                        {VENDOR_SUBTYPES[vfCategory].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={S.field}><div style={S.label}>PHONE</div><input value={vfPhone} onChange={e=>setVfPhone(e.target.value)} style={S.input} placeholder="480-555-1234" /></div>
                  <div style={S.field}><div style={S.label}>EMAIL</div><input value={vfEmail} onChange={e=>setVfEmail(e.target.value)} style={S.input} placeholder="john@abcinspections.com" /></div>
                  <div style={S.field}><div style={S.label}>NOTES</div><textarea value={vfNotes} onChange={e=>setVfNotes(e.target.value)} style={S.textarea} placeholder="Any notes about this vendor..." /></div>
                  <div style={S.modalFooter}>
                    <button onClick={()=>setShowVendorForm(false)} style={S.btnOutline}>Cancel</button>
                    <button onClick={saveVendor} style={S.btnPrimary}>{editingVendor ? 'Save Changes' : 'Add Vendor'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'content-admin' && (
          <div>
            <div style={{fontSize:'13px',color:'#999',marginBottom:'16px',fontFamily:FONT_BODY}}>{contentItems.length} total items</div>
            <div style={{background:'white',borderRadius:'10px',overflowX:'auto',overflowY:'hidden',WebkitOverflowScrolling:'touch',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'560px'}}>
                <thead>
                  <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                    {['TITLE','CATEGORY','UPLOADED',''].map(h => (
                      <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contentItems.map(item => (
                    <tr key={item.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                      <td style={{padding:'12px 16px',fontWeight:600,color:'#222',maxWidth:'300px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.title}</td>
                      <td style={{padding:'12px 16px'}}>
                        <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',background:'rgba(0,0,0,0.04)',color:'#555',border:'1px solid #e8e8e8'}}>{item.type}</span>
                      </td>
                      <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                      <td style={{padding:'12px 16px',textAlign:'right'}}>
                        <button onClick={()=>deleteContent(item)} style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>DELETE</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'stats' && (() => {
          const sevenAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          const activeThisWeek = agents.filter(a => a.last_seen && new Date(a.last_seen) > sevenAgo).length
          // Download counts per content
          const dlByContent = {}
          downloads.forEach(d => { dlByContent[d.content_id] = (dlByContent[d.content_id] || 0) + 1 })
          // Download counts per user
          const dlByUser = {}
          downloads.forEach(d => { dlByUser[d.user_id] = (dlByUser[d.user_id] || 0) + 1 })
          // Most downloaded item
          const topContentId = Object.keys(dlByContent).sort((a,b) => dlByContent[b] - dlByContent[a])[0]
          const topContentItem = contentItems.find(c => c.id === topContentId)
          // Top 10 content by downloads
          const topContent = contentItems.map(c => ({ ...c, dlCount: dlByContent[c.id] || 0 })).sort((a,b) => b.dlCount - a.dlCount).slice(0, 10)
          // Agents sorted by last_seen
          const sortedAgents = [...agents].sort((a,b) => {
            const da = a.last_seen ? new Date(a.last_seen) : new Date(0)
            const db = b.last_seen ? new Date(b.last_seen) : new Date(0)
            return db - da
          })
          return (
            <div style={{paddingBottom:'60px'}}>
              {/* OVERVIEW CARDS */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:'16px',marginBottom:'32px'}}>
                {[
                  { label: 'Total Agents', value: agents.length, color: '#147efb' },
                  { label: 'Active This Week', value: activeThisWeek, color: '#2d7a2d' },
                  { label: 'Total Downloads', value: downloads.length, color: '#fdbf00' },
                  { label: 'Most Downloaded', value: topContentItem ? topContentItem.title.slice(0, 20) + (topContentItem.title.length > 20 ? '...' : '') : '—', sub: topContentItem ? `${dlByContent[topContentId]} downloads` : '', color: '#9b59b6' },
                ].map((stat, i) => (
                  <div key={i} style={{background:'white',borderRadius:'10px',padding:'24px 20px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',borderTop:`3px solid ${stat.color}`}}>
                    <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'2px',color:'#999',marginBottom:'8px',fontFamily:FONT_HEAD,textTransform:'uppercase'}}>{stat.label}</div>
                    <div style={{fontSize:typeof stat.value === 'number'?'36px':'18px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD}}>{stat.value}</div>
                    {stat.sub && <div style={{fontSize:'12px',color:'#999',marginTop:'4px',fontFamily:FONT_BODY}}>{stat.sub}</div>}
                  </div>
                ))}
              </div>

              {/* AGENT ACTIVITY TABLE */}
              <div style={{marginBottom:'32px'}}>
                <div style={{fontSize:'13px',fontWeight:900,letterSpacing:'1px',color:'#222',marginBottom:'12px',fontFamily:FONT_HEAD}}>AGENT ACTIVITY</div>
                <div style={{background:'white',borderRadius:'10px',overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'600px'}}>
                      <thead>
                        <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                          {['NAME','EMAIL','STATUS','LAST SEEN','DOWNLOADS'].map(h => (
                            <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAgents.map(agent => (
                          <tr key={agent.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                            <td style={{padding:'12px 16px',fontWeight:600,color:'#222'}}>{agent.full_name || '—'}</td>
                            <td style={{padding:'12px 16px',color:'#555'}}>{agent.email || '—'}</td>
                            <td style={{padding:'12px 16px'}}>
                              <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',
                                background: agent.last_sign_in_at ? 'rgba(45,122,45,0.1)' : 'rgba(253,191,0,0.15)',
                                color: agent.last_sign_in_at ? '#2d7a2d' : '#b8860b',
                                border: agent.last_sign_in_at ? '1px solid rgba(45,122,45,0.3)' : '1px solid rgba(253,191,0,0.4)'
                              }}>{agent.last_sign_in_at ? 'ACTIVE' : 'INVITED'}</span>
                            </td>
                            <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{agent.last_seen ? new Date(agent.last_seen).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                            <td style={{padding:'12px 16px',fontWeight:700,color:'#222'}}>{dlByUser[agent.id] || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* TOP CONTENT TABLE */}
              <div>
                <div style={{fontSize:'13px',fontWeight:900,letterSpacing:'1px',color:'#222',marginBottom:'12px',fontFamily:FONT_HEAD}}>TOP CONTENT</div>
                <div style={{background:'white',borderRadius:'10px',overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'500px'}}>
                      <thead>
                        <tr style={{background:'#1d2e42',color:'white',textAlign:'left'}}>
                          {['TITLE','CATEGORY','DOWNLOADS','UPLOADED'].map(h => (
                            <th key={h} style={{padding:'12px 16px',fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'1px',fontSize:'11px'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {topContent.map(item => {
                          const cfg = TYPE_CFG[item.type] || TYPE_CFG.crawford
                          return (
                            <tr key={item.id} style={{borderBottom:'1px solid #f0f0f0'}}>
                              <td style={{padding:'12px 16px',fontWeight:600,color:'#222',maxWidth:'250px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.title}</td>
                              <td style={{padding:'12px 16px'}}>
                                <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,textTransform:'uppercase',background:`${cfg.color}15`,color:cfg.color,border:`1px solid ${cfg.color}40`}}>{cfg.label}</span>
                              </td>
                              <td style={{padding:'12px 16px',fontWeight:700,color:'#222'}}>{item.dlCount}</td>
                              <td style={{padding:'12px 16px',color:'#999',fontSize:'12px'}}>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</td>
                            </tr>
                          )
                        })}
                        {topContent.length === 0 && (
                          <tr><td colSpan={4} style={{padding:'40px 20px',textAlign:'center',color:'#999',fontFamily:FONT_BODY}}>No downloads yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )
        })()}

        {showInvite && (
          <div style={S.overlay} onClick={()=>setShowInvite(false)}>
            <div style={{...S.modal,maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
              <div style={S.modalHeader}>
                <div style={S.modalTitle}>ADD AGENT</div>
                <button style={S.closeBtn} onClick={()=>setShowInvite(false)}>×</button>
              </div>
              {inviteError && <div style={S.errorBox}>{inviteError}</div>}
              <div style={S.fieldGroup}>
                <label style={S.label}>FULL NAME</label>
                <input style={S.input} value={inviteName} onChange={e=>setInviteName(e.target.value)} placeholder="Agent's full name" />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>EMAIL</label>
                <input style={S.input} type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="agent@example.com" onKeyDown={e=>e.key==='Enter'&&sendInvite()} />
              </div>
              {/* Same brokerage control as Edit Profile. Required. */}
              <div style={S.fieldGroup}>
                <label style={S.label}>BROKERAGE <span style={{color:'#e74c3c'}}>*</span></label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                  {['HomePros','HomePros Advantage','HomePros Management'].map(opt => (
                    <button key={opt} type="button" onClick={()=>setInviteBrokerage(opt)} style={{
                      padding:'10px 18px',
                      background: inviteBrokerage===opt ? '#147efb' : 'white',
                      color: inviteBrokerage===opt ? 'white' : '#1d2e42',
                      border: inviteBrokerage===opt ? '1px solid #147efb' : '1px solid #ddd',
                      borderRadius:'6px',cursor:'pointer',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,transition:'all 0.15s',
                    }}>{opt}</button>
                  ))}
                </div>
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>MLS AGENT ID</label>
                <input style={S.input} value={inviteArmlsId} onChange={e=>setInviteArmlsId(e.target.value)} placeholder="ab283 (optional)" />
              </div>
              {/* Same button pair as BROKERAGE above. On is the default. */}
              <div style={S.fieldGroup}>
                <label style={S.label}>TECH FEE</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                  {[['on','On'],['exempt','Exempt']].map(([val,label]) => (
                    <button key={val} type="button" onClick={()=>setInviteTechFee(val)} style={{
                      padding:'10px 18px',
                      background: inviteTechFee===val ? '#147efb' : 'white',
                      color: inviteTechFee===val ? 'white' : '#1d2e42',
                      border: inviteTechFee===val ? '1px solid #147efb' : '1px solid #ddd',
                      borderRadius:'6px',cursor:'pointer',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,transition:'all 0.15s',
                    }}>{label}</button>
                  ))}
                </div>
              </div>
              <div style={{fontSize:'12px',color:'#999',marginBottom:'20px',lineHeight:1.5,fontFamily:FONT_BODY}}>They'll receive an email with a link to set their password and sign in.</div>
              <div style={S.modalFooter}>
                <button onClick={()=>setShowInvite(false)} style={S.btnOutline}>Cancel</button>
                <button onClick={sendInvite} disabled={inviteLoading} style={{...S.btnPrimary,opacity:inviteLoading?0.6:1,cursor:inviteLoading?'not-allowed':'pointer'}}>{inviteLoading ? 'SENDING...' : 'SEND INVITE'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Type-to-confirm, because this deletes another person's work. The
            phrase is the property address as it appears in the table, which is
            'Untitled' when the case has none — weak as a phrase, but it matches
            what the admin is looking at, and predictability matters more here
            than entropy. Trimmed and case-insensitive; nothing else. */}
        {caseConfirmDelete && (() => {
          const phrase = (caseConfirmDelete.address || '').trim()
          const typed = deleteConfirmText.trim()
          const matches = typed.length > 0 && typed.toLowerCase() === phrase.toLowerCase()
          return (
          <div style={S.overlay} onClick={()=>{ if (!deletingCase) closeCaseDelete() }}>
            <div style={{...S.modal,maxWidth:'480px'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:'20px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,letterSpacing:'1px',marginBottom:'14px'}}>Delete this case?</div>
              <div style={{background:'#faf9f7',border:`0.5px solid ${RAIL_BORDER}`,borderRadius:'8px',padding:'12px 14px',marginBottom:'14px',fontSize:'13px',fontFamily:FONT_BODY,lineHeight:1.6}}>
                <div style={{color:'#999',fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',fontFamily:FONT_HEAD,marginBottom:'4px'}}>{caseConfirmDelete.tool === 'strategy' ? 'STRATEGY' : 'BINSR'}</div>
                <div style={{fontWeight:700,color:'#1d2e42'}}>{caseConfirmDelete.address}</div>
                <div style={{color:'#555',marginTop:'2px'}}>{caseConfirmDelete.agentName}</div>
                <div style={{color:'#999',fontSize:'12px'}}>{caseConfirmDelete.agentEmail}</div>
              </div>
              <p style={{fontSize:'13.5px',color:'#555',lineHeight:1.6,marginBottom:'14px',fontFamily:FONT_BODY}}>
                This permanently deletes the case, its <strong>{caseConfirmDelete.msgCount}</strong> message{caseConfirmDelete.msgCount === 1 ? '' : 's'} and its <strong>{caseConfirmDelete.fileCount}</strong> uploaded file{caseConfirmDelete.fileCount === 1 ? '' : 's'}, including the documents in storage. It belongs to another agent and cannot be recovered.
              </p>
              <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY,marginBottom:'6px'}}>
                Type <strong style={{color:'#1d2e42'}}>{phrase}</strong> to confirm:
              </div>
              <input
                value={deleteConfirmText}
                onChange={e=>setDeleteConfirmText(e.target.value)}
                onKeyDown={e=>{ if (e.key === 'Enter' && matches && !deletingCase) deleteAdminCase(caseConfirmDelete) }}
                autoFocus
                style={{...S.input, marginBottom:'18px', width:'100%', boxSizing:'border-box'}}
              />
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={closeCaseDelete} disabled={deletingCase} style={S.btnOutline}>Cancel</button>
                <button
                  onClick={()=>deleteAdminCase(caseConfirmDelete)}
                  disabled={!matches || deletingCase}
                  style={{...S.btnDanger, ...((!matches || deletingCase) ? {opacity:0.4, cursor:'not-allowed'} : {})}}>
                  {deletingCase ? 'Deleting…' : 'Delete case'}
                </button>
              </div>
            </div>
          </div>
          )
        })()}

        {confirmDelete && (
          <div style={S.overlay} onClick={()=>setConfirmDelete(null)}>
            <div style={{...S.modal,maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:'20px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,letterSpacing:'1px',marginBottom:'16px'}}>Remove Agent?</div>
              <p style={{fontSize:'14px',color:'#555',lineHeight:1.6,marginBottom:'24px',fontFamily:FONT_BODY}}>
                Are you sure you want to remove <strong>{confirmDelete.full_name || confirmDelete.email}</strong>? This action cannot be undone.
              </p>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={()=>setConfirmDelete(null)} style={S.btnOutline}>Cancel</button>
                <button onClick={()=>removeAgent(confirmDelete)} style={S.btnDanger}>Remove Agent</button>
              </div>
            </div>
          </div>
        )}

        {setPwAgent && (
          <div style={S.overlay} onClick={()=>setSetPwAgent(null)}>
            <div style={{...S.modal,maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:'20px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,letterSpacing:'1px',marginBottom:'6px'}}>Set Password</div>
              <p style={{fontSize:'13px',color:'#888',marginBottom:'20px',fontFamily:FONT_BODY}}>
                For <strong>{setPwAgent.full_name || setPwAgent.email}</strong>{setPwAgent.full_name && setPwAgent.email ? ` (${setPwAgent.email})` : ''}
              </p>
              <div style={S.field}>
                <label style={S.label}>NEW PASSWORD</label>
                <input style={S.input} type="password" placeholder="Minimum 8 characters" value={setPwValue} onChange={e=>{setSetPwValue(e.target.value);setSetPwError('')}} />
              </div>
              <div style={S.field}>
                <label style={S.label}>CONFIRM PASSWORD</label>
                <input style={S.input} type="password" placeholder="Re-enter new password" value={setPwConfirm} onChange={e=>{setSetPwConfirm(e.target.value);setSetPwError('')}} onKeyDown={e=>e.key==='Enter'&&handleSetPassword()} />
              </div>
              {setPwError && <div style={S.errorBox}>{setPwError}</div>}
              <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                <button onClick={()=>setSetPwAgent(null)} style={S.btnOutline}>Cancel</button>
                <button onClick={handleSetPassword} disabled={setPwLoading} style={{...S.btnPrimary,opacity:setPwLoading?0.6:1,cursor:setPwLoading?'wait':'pointer'}}>{setPwLoading ? 'SETTING...' : 'SET PASSWORD'}</button>
              </div>
            </div>
          </div>
        )}

        {armlsAgent && (
          <div style={S.overlay} onClick={()=>setArmlsAgent(null)}>
            <div style={{...S.modal,maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:'20px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,letterSpacing:'1px',marginBottom:'6px'}}>Edit ARMLS ID</div>
              <p style={{fontSize:'13px',color:'#888',marginBottom:'16px',fontFamily:FONT_BODY}}>
                For <strong>{armlsAgent.full_name || armlsAgent.email}</strong>{armlsAgent.full_name && armlsAgent.email ? ` (${armlsAgent.email})` : ''}
              </p>
              <div style={S.field}>
                <label style={S.label}>ARMLS AGENT ID</label>
                <input style={S.input} value={armlsValue} onChange={e=>{setArmlsValue(e.target.value);setArmlsError('')}} placeholder="e.g. ab283" onKeyDown={e=>e.key==='Enter'&&handleSaveArmls()} />
                <div style={{fontSize:'12px',color:'#888',marginTop:'6px',fontFamily:FONT_BODY,lineHeight:1.4}}>The agent's ARMLS/MLS agent ID (e.g. ab283). This connects their closings to My Wins.</div>
              </div>
              {armlsError && <div style={S.errorBox}>{armlsError}</div>}
              <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                <button onClick={()=>setArmlsAgent(null)} style={S.btnOutline}>Cancel</button>
                <button onClick={handleSaveArmls} disabled={armlsLoading} style={{...S.btnPrimary,opacity:armlsLoading?0.6:1,cursor:armlsLoading?'wait':'pointer'}}>{armlsLoading ? 'SAVING...' : 'SAVE'}</button>
              </div>
            </div>
          </div>
        )}

        {editProfileAgent && (
          <div style={S.overlay} onClick={()=>setEditProfileAgent(null)}>
            <div style={{...S.modal,maxWidth:'560px'}} onClick={e=>e.stopPropagation()}>
              <div style={{fontSize:'20px',fontWeight:900,color:'#222',fontFamily:FONT_HEAD,letterSpacing:'1px',marginBottom:'6px'}}>Edit Profile</div>
              <p style={{fontSize:'13px',color:'#888',marginBottom:'20px',fontFamily:FONT_BODY}}>
                For <strong>{editProfileAgent.full_name || editProfileAgent.email}</strong>{editProfileAgent.full_name && editProfileAgent.email ? ` (${editProfileAgent.email})` : ''}
              </p>

              <div style={S.field}>
                <label style={S.label}>PHOTO</label>
                <div style={{display:'flex',alignItems:'center',gap:'14px'}}>
                  {epAvatarUrl
                    ? <img src={epAvatarUrl} alt="" style={{width:'72px',height:'72px',borderRadius:'12px',objectFit:'cover',border:'1px solid #e8e8e8',flexShrink:0}} />
                    : <div style={{width:'72px',height:'72px',borderRadius:'12px',background:'#f5f5f5',border:'1px solid #e8e8e8',display:'flex',alignItems:'center',justifyContent:'center',color:'#bbb',fontSize:'26px',flexShrink:0}}>👤</div>}
                  <div>
                    <input ref={epAvatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{display:'none'}} onChange={handleEpAvatarUpload} />
                    <button type="button" onClick={()=>!epAvatarUploading && epAvatarInputRef.current?.click()} disabled={epAvatarUploading} style={{...S.btnOutline,cursor:epAvatarUploading?'wait':'pointer',opacity:epAvatarUploading?0.6:1}}>{epAvatarUploading ? 'UPLOADING…' : 'UPLOAD PHOTO'}</button>
                    {epAvatarError && <div style={{...S.errorBox,marginTop:'8px',marginBottom:0}}>{epAvatarError}</div>}
                  </div>
                </div>
              </div>
              <div style={S.field}><label style={S.label}>FULL NAME</label>
                <input style={S.input} value={epFullName} onChange={e=>{setEpFullName(e.target.value);setEpError('')}} placeholder="Jane Agent" /></div>
              <div style={S.field}><label style={S.label}>PHONE</label>
                <input style={S.input} value={epPhone} onChange={e=>setEpPhone(e.target.value)} placeholder="480-555-1234" /></div>
              <div style={S.field}><label style={S.label}>LICENSE NUMBER</label>
                <input style={S.input} value={epLicense} onChange={e=>setEpLicense(e.target.value)} placeholder="SA123456000" /></div>

              <div style={S.field}>
                <label style={S.label}>BROKERAGE</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                  {['HomePros','HomePros Advantage','HomePros Management'].map(opt => (
                    <button key={opt} type="button" onClick={()=>setEpBrokerage(opt)} style={{
                      padding:'10px 18px',
                      background: epBrokerage===opt ? '#147efb' : 'white',
                      color: epBrokerage===opt ? 'white' : '#1d2e42',
                      border: epBrokerage===opt ? '1px solid #147efb' : '1px solid #ddd',
                      borderRadius:'6px',cursor:'pointer',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,transition:'all 0.15s',
                    }}>{opt}</button>
                  ))}
                </div>
              </div>

              <div style={S.field}><label style={S.label}>BIO / ABOUT</label>
                <textarea style={S.textarea} value={epBio} onChange={e=>setEpBio(e.target.value)} placeholder="Short bio..." /></div>

              <div style={S.field}><label style={S.label}>SLUG</label>
                <input style={S.input} value={epSlug} onChange={e=>{setEpSlug(e.target.value);setEpError('')}} placeholder="jane-agent" />
                <div style={{fontSize:'12px',color:'#888',marginTop:'6px',fontFamily:FONT_BODY,lineHeight:1.4}}>Public link page address: /&lt;slug&gt; (also /links/&lt;slug&gt;). Must be unique across all agents. Agent addresses are listed with ARMLS: a new or changed slug must be reported to them.</div></div>

              <div style={S.field}><label style={S.label}>INSTAGRAM</label>
                <input style={S.input} value={epInstagram} onChange={e=>setEpInstagram(e.target.value)} placeholder="https://instagram.com/yourhandle" /></div>
              <div style={S.field}><label style={S.label}>FACEBOOK</label>
                <input style={S.input} value={epFacebook} onChange={e=>setEpFacebook(e.target.value)} placeholder="https://facebook.com/yourpage" /></div>
              <div style={S.field}><label style={S.label}>LINKEDIN</label>
                <input style={S.input} value={epLinkedin} onChange={e=>setEpLinkedin(e.target.value)} placeholder="https://linkedin.com/in/yourprofile" /></div>
              <div style={S.field}><label style={S.label}>ZILLOW CONNECT LINK</label>
                <input style={S.input} value={epZillow} onChange={e=>setEpZillow(e.target.value)} placeholder="https://www.zillow.com/agent/connect-with-me/yourname" />
                <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,marginTop:'6px',lineHeight:1.5}}>Paste your Zillow connect link exactly as it appears — this is what visitors tap to connect with you on Zillow.</div></div>
              <div style={S.field}><label style={S.label}>YOUTUBE</label>
                <input style={S.input} value={epYoutube} onChange={e=>setEpYoutube(e.target.value)} placeholder="https://youtube.com/@yourchannel" /></div>
              <div style={S.field}><label style={S.label}>PREQUALIFY</label>
                <input style={S.input} value={epPrequalify} onChange={e=>setEpPrequalify(e.target.value)} placeholder="https://yourlender.com/prequalify" /></div>

              <div style={S.field}>
                <label style={S.label}>DEFAULT LANGUAGE</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'4px'}}>
                  {[['en','English'],['es','Español']].map(([code,label]) => (
                    <button key={code} type="button" onClick={()=>setEpLangDefault(code)} style={{
                      padding:'10px 18px',
                      background: epLangDefault===code ? '#147efb' : 'white',
                      color: epLangDefault===code ? 'white' : '#1d2e42',
                      border: epLangDefault===code ? '1px solid #147efb' : '1px solid #ddd',
                      borderRadius:'6px',cursor:'pointer',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,transition:'all 0.15s',
                    }}>{label}</button>
                  ))}
                </div>
              </div>

              {epError && <div style={S.errorBox}>{epError}</div>}
              <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                <button onClick={()=>setEditProfileAgent(null)} style={S.btnOutline}>Cancel</button>
                <button onClick={handleSaveProfile} disabled={epLoading} style={{...S.btnPrimary,opacity:epLoading?0.6:1,cursor:epLoading?'wait':'pointer'}}>{epLoading ? 'SAVING...' : 'SAVE PROFILE'}</button>
              </div>
            </div>
          </div>
        )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Admin"
      summary={summaryNode}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={ADMIN_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── ASSISTANT WORKSPACE ─────────────────────────────────────────────────────
// Default landing at "/" (also reachable at /assistant). The composer belongs to
// ADA: anything typed here opens a law conversation. Strategy and BINSR are
// reached by clicking them, not by guessing at what the text meant.

const RAIL_BG = '#f4f4f2'
const RAIL_BORDER = '#d8d8d4'

// Size and color are props so the card can render this larger and in brand blue
// without a second copy of the path data. Defaults keep the rail unchanged.
function StrategistIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/>
    </svg>
  )
}

// size/color are props for the same reason StrategistIcon's are — the rail now
// renders these inside tinted tiles at 14px. Defaults keep every other caller
// unchanged.
function InspectionIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1z"/>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <polyline points="9 14 11 16 15 12"/>
    </svg>
  )
}

function AdaIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l9 4.5-9 4.5-9-4.5L12 3z"/>
      <path d="M5 10v4.5c0 2.5 3.1 4.5 7 4.5s7-2 7-4.5V10"/>
      <line x1="21" y1="7.5" x2="21" y2="13"/>
    </svg>
  )
}

// Scales, not another document. The other three rail icons are already a chart,
// a clipboard and a mortarboard; a form icon here would read as the inspection
// desk at 16px.
// size/color are props for the same reason StrategistIcon's are: the setup card
// renders this larger than the rail does. Defaults keep the rail unchanged.
function DealDeskIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="21"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
      <line x1="4" y1="7" x2="20" y2="7"/>
      <path d="M4 7L1.5 13h5z"/>
      <path d="M20 7L17.5 13h5z"/>
    </svg>
  )
}

function MessagesIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4V5z"/>
    </svg>
  )
}

function PipelineIcon({ size = 16, color = '#1d2e42' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 4h18l-7 8v7l-4 2v-9L3 4z"/>
    </svg>
  )
}

// Recent cases across all five tools: owned rows only, normalized into one
// shape and interleaved by recency. Shared by the workspace and the ADA view so
// the rail reads the same in both. Deliberately excludes shared_with cases.
function useRecentCases(userId) {
  const [recent, setRecent] = useState([])
  const [loadingRecent, setLoadingRecent] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      supabase.from('property_cases').select('id, subject_address, updated_at')
        .eq('agent_id', userId).order('updated_at', { ascending: false }).limit(8),
      supabase.from('binsr_cases').select('id, property_address, updated_at')
        .eq('user_id', userId).order('updated_at', { ascending: false }).limit(8),
      supabase.from('ada_cases').select('id, title, updated_at')
        .eq('user_id', userId).order('updated_at', { ascending: false }).limit(8),
      supabase.from('dealdesk_cases').select('id, title, updated_at')
        .eq('user_id', userId).order('updated_at', { ascending: false }).limit(8),
      supabase.from('pipeline_conversations').select('id, title, updated_at')
        .eq('agent_id', userId).order('updated_at', { ascending: false }).limit(8),
      supabase.from('casita_checks').select('id, address, updated_at')
        .eq('user_id', userId).order('updated_at', { ascending: false }).limit(8),
    ]).then(([stratRes, binsrRes, adaRes, deskRes, pipeRes, casitaRes]) => {
      if (cancelled) return
      if (stratRes.error) console.error('assistant strategy recents:', stratRes.error)
      if (binsrRes.error) console.error('assistant binsr recents:', binsrRes.error)
      if (adaRes.error) console.error('assistant ada recents:', adaRes.error)
      if (deskRes.error) console.error('assistant dealdesk recents:', deskRes.error)
      if (pipeRes.error) console.error('assistant pipeline recents:', pipeRes.error)
      if (casitaRes.error) console.error('assistant casita recents:', casitaRes.error)
      const merged = [
        ...(stratRes.data || []).map(c => ({ id: c.id, label: c.subject_address, tool: 'strategy', updated_at: c.updated_at })),
        ...(binsrRes.data || []).map(c => ({ id: c.id, label: c.property_address, tool: 'binsr', updated_at: c.updated_at })),
        ...(adaRes.data || []).map(c => ({ id: c.id, label: c.title, tool: 'ada', updated_at: c.updated_at })),
        ...(deskRes.data || []).map(c => ({ id: c.id, label: c.title, tool: 'dealdesk', updated_at: c.updated_at })),
        ...(pipeRes.data || []).map(c => ({ id: c.id, label: c.title, tool: 'pipeline', updated_at: c.updated_at })),
        // `tool` is also the navigateTo target and the page key — the rail
        // calls navigateTo(r.tool, r.id), so 'casita' here is what makes a
        // recent row open at /casita/{id}.
        ...(casitaRes.data || []).map(c => ({ id: c.id, label: c.address, tool: 'casita', updated_at: c.updated_at })),
      ]
      merged.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      setRecent(merged.slice(0, 8))
      setLoadingRecent(false)
    })
    return () => { cancelled = true }
  }, [userId])

  return { recent, loadingRecent }
}

// ─── RAIL STATUS ─────────────────────────────────────────────────────────────
// One rail means one set of queries — the same rule useRecentCases follows
// above. Every BROKERAGE row that carries live text reads from this hook rather
// than fetching for itself, so the rail costs four requests per session no
// matter how many rows end up showing something.
//
// Dates here are bare 'YYYY-MM-DD' DATE columns (events.event_date and the
// date field inside open_house.schedule), so "today" is built from LOCAL parts.
// The UTC form used at OpenHouseAdmin.jsx:340 rolls over at 5pm Arizona time,
// which would drop today's event off the rail for the whole evening.
function railTodayYMD() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// 'YYYY-MM-DD' → 'Tue' inside a week, 'Sep 14' beyond it. The 'T00:00:00'
// suffix is the same local-midnight parse the event notification already uses
// at App.js:3119; without it a bare date string parses as UTC and can render
// the day before.
function railFormatDay(ymd) {
  if (typeof ymd !== 'string' || !ymd) return undefined
  const d = new Date(ymd + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return undefined
  const today = new Date(railTodayYMD() + 'T00:00:00')
  const days = Math.round((d - today) / 86400000)
  // < 7 rather than <= 7: at exactly seven days out the weekday name repeats
  // today's, and "Tue" would be read as this Tuesday.
  return days < 7
    ? d.toLocaleDateString('en-US', { weekday: 'short' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// '13:00' → '1', '14:30' → '2:30'. Deliberately not formatTime (App.js:3047),
// which returns '1:00 PM' — a range of those is three times the width the rail
// has for it.
function railHour(hhmm) {
  const [h, m] = String(hhmm || '').split(':')
  const hr = parseInt(h, 10)
  if (!Number.isFinite(hr)) return ''
  const h12 = hr % 12 || 12
  return m && m !== '00' ? `${h12}:${m}` : String(h12)
}

// Signature takes the objects, but every dependency is a primitive read off
// them — `profile` gets a new identity whenever setProfile runs (ProfilePage
// does, and so does BrokerView after it stamps last_broker_issue), and an
// object dep would refetch the whole rail on any unrelated profile edit.
function useRailStatus(session, profile) {
  const userId = session?.user?.id
  const lastSeen = profile?.last_seen
  const lastBrokerIssue = profile?.last_broker_issue

  const [railStatus, setRailStatus] = useState({})
  const [loadingRailStatus, setLoadingRailStatus] = useState(true)
  const [hasNewIssue, setHasNewIssue] = useState(false)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    const today = railTodayYMD()

    Promise.all([
      // Next brokerage-wide event. `events` has no agent column at all, so this
      // is the same row for every agent.
      supabase.from('events').select('event_date')
        .gte('event_date', today).order('event_date', { ascending: true }).limit(1),
      // Newest content row only. The badge rule below is monotonic in
      // created_at — both halves are lower bounds — so if the newest row fails
      // it, no older row can pass.
      supabase.from('content').select('created_at')
        .order('created_at', { ascending: false }).limit(1),
      // This agent's PUBLISHED open house. OpenHouseAdmin's loader
      // (OpenHouseAdmin.jsx:193-198) deliberately omits is_active so drafts stay
      // editable there; this is agent-facing status text, so it must not.
      supabase.from('open_house').select('schedule, event_date, start_time, end_time, updated_at')
        .eq('agent_id', userId).eq('is_active', true)
        .order('updated_at', { ascending: false }).limit(1),
      // Newsletter feed. Unlike a Supabase builder, a bare fetch REJECTS on a
      // network fault, which would reject the whole Promise.all and blank every
      // other row — so it resolves to null here rather than propagating.
      fetch(BROKER_FEED_URL)
        .then(res => (res.ok ? res.json() : null))
        .catch(() => null),
    ]).then(([evRes, contentRes, ohRes, feed]) => {
      if (cancelled) return
      if (evRes.error) console.error('rail status events:', evRes.error)
      if (contentRes.error) console.error('rail status content:', contentRes.error)
      if (ohRes.error) console.error('rail status open house:', ohRes.error)

      const next = {}

      // EVENTS
      const nextEvent = (evRes.data || [])[0]
      if (nextEvent) {
        const label = railFormatDay(nextEvent.event_date)
        if (label) next.events = label
      }

      // CONTENT — the exact rule ContentLibraryView applies at App.js:7569-7570
      // and 7678, including its null-last_seen guard: an agent with no last_seen
      // gets nothing, rather than every row counting as new.
      const newestContent = (contentRes.data || [])[0]
      const lastSeenDate = lastSeen ? new Date(lastSeen) : null
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      if (newestContent && lastSeenDate) {
        const created = new Date(newestContent.created_at)
        if (created > lastSeenDate && created > sevenDaysAgo) next.content = 'New'
      }

      // OPEN HOUSE — prefer the schedule JSONB, fall back to the legacy day-1
      // scalars. Same precedence and the same slice(0,5) normalisation as
      // OpenHouseAdmin.jsx:217-225 and its normalizeScheduleDay at line 140.
      const oh = (ohRes.data || [])[0]
      if (oh) {
        let days = Array.isArray(oh.schedule)
          ? oh.schedule
              .filter(d => d && typeof d === 'object' && typeof d.date === 'string' && d.date)
              .map(d => ({
                date: d.date,
                start_time: typeof d.start_time === 'string' ? d.start_time.slice(0, 5) : '',
                end_time: typeof d.end_time === 'string' ? d.end_time.slice(0, 5) : '',
              }))
          : []
        if (days.length === 0 && oh.event_date) {
          days = [{
            date: oh.event_date,
            start_time: (oh.start_time || '').slice(0, 5),
            end_time: (oh.end_time || '').slice(0, 5),
          }]
        }
        // Schedule days are not stored in order, and nothing prunes elapsed
        // ones, so sort the future ones and take the first.
        const upcoming = days
          .filter(d => d.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date))[0]
        if (upcoming) {
          const day = railFormatDay(upcoming.date)
          const from = railHour(upcoming.start_time)
          const to = railHour(upcoming.end_time)
          if (day) next.openHouse = from && to ? `${day} ${from}-${to}` : day
        }
      }

      setRailStatus(next)

      // NEW ISSUE — the feed is published newest-first, so entry 0 is current.
      // A failed fetch left `feed` null, and null must read as "no badge"
      // rather than as "unknown slug, therefore new".
      const newestSlug = Array.isArray(feed) && feed[0] && typeof feed[0].slug === 'string'
        ? feed[0].slug
        : null
      setHasNewIssue(!!newestSlug && newestSlug !== lastBrokerIssue)

      setLoadingRailStatus(false)
    }).catch(err => {
      // useRecentCases has no terminal catch, but it also renders a "Loading..."
      // line off its flag. Nothing renders off this one, and a permanently-true
      // loading flag would be invisible — so fail closed instead.
      if (cancelled) return
      console.error('rail status:', err)
      setLoadingRailStatus(false)
    })

    return () => { cancelled = true }
  }, [userId, lastSeen, lastBrokerIssue])

  return { railStatus, loadingRailStatus, hasNewIssue }
}

// Brokerage row icons, same inline-SVG shape the context cards use.
const BROKERAGE_ICON_PATHS = {
  content:   '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>',
  events:    '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 11h18"/>',
  mywins:    '<path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3"/><path d="M7 5H4v2a3 3 0 0 0 3 3"/>',
  links:     '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19"/>',
  openhouse: '<path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M10 20v-5h4v5"/>',
  vendors:   '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M3 12h18"/>',
  broker:    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  // A send glyph rather than a second envelope: `broker` above is already an
  // envelope, and two envelopes one row apart read as the same destination.
  weekly:    '<path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/>',
}

function RailIcon({ name }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true"
      dangerouslySetInnerHTML={{__html: BROKERAGE_ICON_PATHS[name] || ''}} />
  )
}

// Brokerage destinations. Every target is the exact string the top nav already
// passes to navigateTo, so both entry points stay in step. Content and Vendors
// now have their own shell routes; the Hub page still carries duplicate copies
// of both sections until stage 4 removes them.
const BROKERAGE_ROWS = [
  // `status` names the useRailStatus field this row reads; a row without one
  // shows no status text at all. `badge` follows the same data-driven shape
  // TEAM_TOOLS uses for its 'armls' chip.
  { icon: 'broker',    label: 'From the broker', target: 'broker',          badge:  'new' },
  { icon: 'content',   label: 'Content',    target: 'library',         status: 'content' },
  { icon: 'weekly',    label: 'Weekly emails', target: 'weekly-emails' },
  { icon: 'events',    label: 'Events',     target: 'events',          status: 'events' },
  { icon: 'mywins',    label: 'My wins',    target: 'mywins' },
  { icon: 'links',     label: 'Link page',  target: 'links-admin' },
  { icon: 'openhouse', label: 'Open house', target: 'openhouse-admin', status: 'openHouse' },
  { icon: 'vendors',   label: 'Vendors',    target: 'vendors' },
]

// First use of localStorage in this codebase — everything else persists user
// preference to a profiles column. sessionStorage would not survive a new tab,
// which the collapse state needs to. Guarded because Safari private mode throws
// on access.
// Off-site destinations. Came from the Hub page's "YOUR BROKER ECOSYSTEM" card
// grid; the podcast room came from the content sidebar's button stack.
const ECOSYSTEM_LINKS = [
  { label: 'Avance School',  url: 'https://avanceschool.com' },
  { label: 'Follow Up Boss', url: 'https://app.followupboss.com/login' },
  { label: 'Brokermint',     url: 'https://my.brokermint.com/#/dashboard' },
  { label: 'Podcast room',   url: 'https://tidycal.com/joshmarquez/podcast-room' },
]

const MY_CASES_KEY = 'hub.rail.myCasesOpen'
function readMyCasesOpen() {
  try { return window.localStorage.getItem(MY_CASES_KEY) === '1' } catch { return false }
}

// The five tool rows, as data. `key` is BOTH the navigateTo target and the page
// value the active state compares against — they are the same string today and
// a table keeps them from drifting apart.
//
// Ask ADA goes to the composer, not to a new case. Creating the row on click
// left an empty "New conversation" behind every time an agent opened ADA and
// changed their mind; submit() creates it instead, titled from the question, so
// a row never exists without a message in it.
// Bar-chart glyph for the Market Snapshot rail row. Same size/color contract
// as the other inline icons in this file.
function MarketIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="12" width="4" height="9" rx="1" />
      <rect x="10" y="7" width="4" height="14" rx="1" />
      <rect x="17" y="3" width="4" height="18" rx="1" />
    </svg>
  )
}

// House outline for the Casita Check rail row. The three paths are the ones
// TILE_ICONS['Single Family'] and BROKERAGE_ICON_PATHS.openhouse already carry —
// written as a component rather than a path string because TEAM_TOOLS takes an
// Icon, and with the same size/color contract as every other icon in this file.
function CasitaIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>
    </svg>
  )
}

// Storefront for the Marketplace rail row. Same size/color contract as
// CasitaIcon above.
function MarketplaceIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l1.5-5h15L21 9"/><path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/>
    </svg>
  )
}

const TEAM_TOOLS = [
  { key:'strategy',  label:'The Strategist',  Icon:StrategistIcon, tile:'#E4EDF7', ink:'#123763', badge:'armls' },
  // The tile pair is CONTENT_CHIP.reel's (#FBE8DA/#6B3410) rather than a new
  // colour: every other soft tint in this palette is already taken by another
  // row in this list, and the two teals would have sat one apart and read as a mistake.
  { key:'casita',    label:'Casita Check',   Icon:CasitaIcon,     tile:'#FBE8DA', ink:'#6B3410', badge:'new' },
  { key:'market',    label:'Market Snapshot', Icon:MarketIcon,     tile:'#FFF4D6', ink:'#6B4B00', badge:'armls' },
  { key:'marketplace', label:'Marketplace',  Icon:MarketplaceIcon, tile:'#E4EDF7', ink:'#123763', badge:'new' },
  { key:'dealdesk',  label:'Deal Desk',       Icon:DealDeskIcon,   tile:'#EFE6F7', ink:'#46186B' },
  { key:'binsr',     label:'Inspection desk', Icon:InspectionIcon, tile:'#FAEEDA', ink:'#633806' },
  { key:'pipeline',  label:'Pipeline',        Icon:PipelineIcon,   tile:'#DCEEEC', ink:'#0B4A44' },
  // badge 'unread': the live "N new" count from meta-inbox, not a static chip.
  // Pipeline's tile pair, since Messages feeds the same Follow Up Boss.
  { key:'messages',  label:'Messages',        Icon:MessagesIcon,   tile:'#DCEEEC', ink:'#0B4A44', badge:'unread' },
  { key:'assistant', label:'Ask Ada',         Icon:AdaIcon,        tile:'#EAF3DE', ink:'#27500A' },
]

// The workspace rail, shared by AssistantWorkspace and AdaCaseView. Same rows
// and same hover fill as when it lived inline in the workspace; the account menu
// floats above the profile row rather than sitting in flow.
function WorkspaceRail({ session, profile, navigateTo, showToast, isAdmin, signOut, recent, loadingRecent, railStatus = {}, loadingRailStatus, hasNewIssue, isMobile, page }) {
  const [accountOpen, setAccountOpen] = useState(false)
  const [myCasesOpen, setMyCasesOpen] = useState(readMyCasesOpen)
  const messagesVisible = canSeeMessages(isAdmin)
  const messagesUnread = useMessagesUnread(page, messagesVisible)

  // Everything below moved here from Dashboard when the top nav was deleted.
  // The rail is now the only component mounted on every route, and exactly one
  // instance of it is mounted at a time — CaseShell's desktop rail and its
  // mobile drawer are mutually exclusive — so this state has one owner. The
  // alternative was threading a dozen props and handlers through every view.
  const [announcement, setAnnouncement] = useState(null)
  const [annDismissed, setAnnDismissed] = useState(false)
  const [showAnnEdit, setShowAnnEdit] = useState(false)
  const [annEditMsg, setAnnEditMsg] = useState('')
  const [annEditActive, setAnnEditActive] = useState(false)
  const [annSaving, setAnnSaving] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [showNotifPanel, setShowNotifPanel] = useState(false)
  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    try { window.localStorage.setItem(MY_CASES_KEY, myCasesOpen ? '1' : '0') } catch { /* private mode */ }
  }, [myCasesOpen])

  useEffect(() => {
    supabase.from('announcements').select('*').limit(1).single().then(({ data }) => {
      if (data) setAnnouncement(data)
    })
  }, [])

  // Notifications: fetch + real-time. New rows are added, and rows marked read
  // (here or in another tab) are updated in place. Until the channel is
  // SUBSCRIBED, and whenever it errors, times out or closes, the list is
  // re-read every 30 seconds; after a drop it is re-read once on reconnect.
  useEffect(() => {
    const uid = session.user.id
    let cancelled = false
    const fetchNotifications = () => supabase.from('notifications').select('*').eq('user_id', uid)
      .order('created_at', { ascending: false }).limit(20)
      .then(({ data }) => { if (data && !cancelled) setNotifications(data) })
    fetchNotifications()

    let pollTimer = null
    let dropped = false
    const startPoll = () => { if (!pollTimer) pollTimer = setInterval(fetchNotifications, MESSAGES_POLL_MS) }
    const stopPoll = () => { clearInterval(pollTimer); pollTimer = null }
    startPoll()
    const channel = supabase.channel(liveChannelName('notif-changes'))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}` }, payload => {
        setNotifications(prev => [payload.new, ...prev.filter(n => n.id !== payload.new.id)].slice(0, 20))
        if (payload.new?.type === 'message') window.dispatchEvent(new Event(MESSAGES_UNREAD_EVENT))
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}` }, payload => {
        setNotifications(prev => prev.map(n => n.id === payload.new.id ? { ...n, ...payload.new } : n))
      })
      .subscribe(status => {
        if (cancelled) return  // our own cleanup closes the channel
        if (status === 'SUBSCRIBED') {
          stopPoll()
          if (dropped) { dropped = false; fetchNotifications() }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          dropped = true
          startPoll()
        }
      })
    return () => { cancelled = true; stopPoll(); supabase.removeChannel(channel) }
  }, [session.user.id])

  const unreadCount = notifications.filter(n => !n.read).length

  async function markAllRead() {
    await supabase.from('notifications').update({ read: true }).eq('user_id', session.user.id).eq('read', false)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  async function markOneRead(id) {
    await supabase.from('notifications').update({ read: true }).eq('id', id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setShowNotifPanel(false)
  }

  async function saveAnnouncement() {
    setAnnSaving(true)
    const payload = { message: annEditMsg.trim(), active: annEditActive }
    if (announcement?.id) {
      await supabase.from('announcements').update(payload).eq('id', announcement.id)
      setAnnouncement({ ...announcement, ...payload })
    } else {
      const { data } = await supabase.from('announcements').insert(payload).select().single()
      if (data) setAnnouncement(data)
    }
    setAnnSaving(false)
    setShowAnnEdit(false)
  }

  const initials = (profile?.full_name || session.user.email || '?')
    .split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()

  const railLabel = { fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'8px',padding:'0 8px' }
  // Plain rows: no card, no border. Hover is the only affordance, so it fills —
  // one step DARKER than the rail (RAIL_BG #f4f4f2), not white. White was the
  // fill until the BROKERAGE rows gained white icon tiles, which it dissolved;
  // filling with RAIL_BG itself would match the rail and erase the hover
  // outright. #ebebe8 sits between the two, so both stay legible.
  const railRow = { width:'100%',textAlign:'left',background:'transparent',border:'none',borderRadius:'8px',padding:'8px',cursor:'pointer',fontFamily:FONT_BODY,display:'flex',alignItems:'center',gap:'10px',transition:'background .15s' }
  // White tile behind every BROKERAGE icon. RailIcon already renders at 16px
  // with a #1d2e42 stroke, so the tile is pure chrome — no icon change.
  const railTile = { flexShrink:0,width:'30px',height:'30px',borderRadius:'8px',background:'#ffffff',border:'0.5px solid #e8e8e8',display:'flex',alignItems:'center',justifyContent:'center' }
  const railStatusText = { flexShrink:0,fontSize:'11px',fontWeight:400,color:'#999',whiteSpace:'nowrap' }
  const railNewBadge = { flexShrink:0,background:'#fdbf00',color:'#412402',fontSize:'10px',fontWeight:500,padding:'3px 8px',borderRadius:'4px',whiteSpace:'nowrap' }
  const fillRow = e => { e.currentTarget.style.background = '#ebebe8' }
  const clearRow = e => { e.currentTarget.style.background = 'transparent' }

  // Account menu rows. Targets are the same strings the top nav passes to
  // navigateTo, so both entry points stay in step.
  const menuItem = { width:'100%',textAlign:'left',background:'transparent',border:'none',borderRadius:'6px',padding:'6px 9px',fontSize:'12.5px',cursor:'pointer',fontFamily:FONT_BODY,color:'#1d2e42',transition:'background .15s' }
  const fillMenu = e => { e.currentTarget.style.background = RAIL_BG }
  const clearMenu = e => { e.currentTarget.style.background = 'transparent' }
  const divider = { height:'1px',background:RAIL_BORDER,margin:'5px 0' }
  const go = target => { setAccountOpen(false); navigateTo(target) }

  const showAnnouncement = announcement?.active && announcement?.message && !annDismissed

  return (
    // minHeight:100% makes the rail fill a column-direction parent, which is
    // what AdaCaseView's scroll wrapper is — without it the rail is only as tall
    // as its content and marginTop:auto on the account block has no free space
    // to push into. flex:1 can't be used instead: in AssistantWorkspace the rail
    // is a direct child of a ROW, where flex governs width and would collapse
    // the 268px. Off on mobile, where the parent is a column and 100% would give
    // the rail the whole viewport.
    <div style={{width:isMobile?'100%':'268px',flexShrink:0,minHeight:isMobile?0:'100%',background:RAIL_BG,borderRight:isMobile?'none':`1px solid ${RAIL_BORDER}`,borderBottom:isMobile?`1px solid ${RAIL_BORDER}`:'none',padding:'20px 12px',display:'flex',flexDirection:'column',gap:'20px',boxSizing:'border-box'}}>

      <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'0 8px',position:'relative'}}>
        {/* The title is the way home. With the top nav and Back to Hub both
            gone, nothing else routes to 'assistant' — the landing would only be
            reachable by typing the URL. */}
        <button onClick={()=>navigateTo('assistant')}
          style={{flex:1,minWidth:0,display:'flex',alignItems:'center',gap:'10px',background:'transparent',border:'none',padding:0,cursor:'pointer',textAlign:'left',fontFamily:FONT_HEAD}}>
          <span style={{color:'#fdbf00',fontSize:'18px',lineHeight:1,flexShrink:0}}>&#9654;</span>
          <span style={{flex:1,minWidth:0,fontSize:'15px',fontWeight:900,letterSpacing:'2px',color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>HOMEPROS HUB</span>
        </button>

        {/* The bell lived in the top nav. The nav is gone, and the rail is the
            only frame left that every route renders, so it lives here now. */}
        <button
          onClick={()=>setShowNotifPanel(o=>!o)}
          aria-label="Notifications"
          aria-expanded={showNotifPanel}
          style={{flexShrink:0,background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:'2px',position:'relative',lineHeight:1}}>
          🔔
          {unreadCount > 0 && <span style={{position:'absolute',top:'-4px',right:'-4px',background:'#fdbf00',color:'#1d2e42',fontSize:'9px',fontWeight:900,borderRadius:'50%',width:'15px',height:'15px',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT_HEAD}}>{unreadCount}</span>}
        </button>

        {showNotifPanel && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:'8px',background:'white',borderRadius:'10px',border:`1px solid ${RAIL_BORDER}`,boxShadow:'0 8px 24px rgba(0,0,0,0.14)',zIndex:30,overflow:'hidden'}}>
            <div style={{padding:'10px 12px',borderBottom:'1px solid #f0f0f0',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px'}}>
              <span style={{fontSize:'11px',fontWeight:900,letterSpacing:'1px',color:'#222',fontFamily:FONT_HEAD}}>NOTIFICATIONS</span>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                {unreadCount > 0 && <button onClick={markAllRead} style={{background:'none',border:'none',color:'#147efb',fontSize:'10px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>MARK ALL READ</button>}
                <button onClick={()=>setShowNotifPanel(false)} aria-label="Close notifications" style={{background:'none',border:'none',color:'#999',fontSize:'13px',cursor:'pointer',fontWeight:700,lineHeight:1}}>✕</button>
              </div>
            </div>
            <div style={{maxHeight:'300px',overflowY:'auto'}}>
              {notifications.length === 0 ? (
                <div style={{padding:'28px 16px',textAlign:'center',color:'#999',fontSize:'12.5px',fontFamily:FONT_BODY}}>You're all caught up! 🎉</div>
              ) : notifications.map(n => (
                <div key={n.id} onClick={()=>{ markOneRead(n.id); if (n.type === 'message' && messagesVisible) navigateTo('messages') }} style={{padding:'10px 12px',borderBottom:'1px solid #f5f5f5',cursor:'pointer',background:n.read?'white':'rgba(20,126,251,0.04)',display:'flex',gap:'8px',alignItems:'flex-start'}}>
                  <span style={{fontSize:'14px',flexShrink:0,marginTop:'1px'}}>{n.type === 'event' ? '📅' : n.type === 'message' ? '💬' : '📄'}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'12.5px',color:'#222',fontWeight:n.read?400:600,fontFamily:FONT_BODY,lineHeight:1.4}}>{n.message}</div>
                    <div style={{fontSize:'10.5px',color:'#999',marginTop:'3px',fontFamily:FONT_BODY}}>{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span style={{width:'7px',height:'7px',borderRadius:'50%',background:'#147efb',flexShrink:0,marginTop:'5px'}} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Same condition as the top banner, which is hidden on this route. */}
      {showAnnouncement && (
        <div style={{display:'flex',alignItems:'flex-start',gap:'8px',background:'#fdf6e3',color:'#1d2e42',
          // Shorthand first, then the two left overrides — a `border` written
          // after `borderLeft` would reset the accent it was meant to keep.
          border:'1px solid #ecdca6',borderLeftWidth:'3px',borderLeftColor:'#fdbf00',
          borderRadius:'10px',padding:'9px 10px'}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b0894a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:'1px'}} aria-hidden="true">
            <path d="M3 10v4a1 1 0 0 0 1 1h3l6 4V5L7 9H4a1 1 0 0 0-1 1z"/>
            <path d="M17 9a3.5 3.5 0 0 1 0 6"/>
          </svg>
          <div style={{fontSize:'11.5px',lineHeight:1.45,fontWeight:500,fontFamily:FONT_BODY,minWidth:0,overflowWrap:'anywhere'}}>
            {announcement.message}
          </div>
          {/* The dismiss ✕ moved down with the banner — its only caller used to
              be the top band, so without this annDismissed could never flip. */}
          <button onClick={()=>setAnnDismissed(true)} aria-label="Dismiss announcement"
            style={{flexShrink:0,background:'none',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:700,color:'#b0a68a',padding:0,lineHeight:1}}>✕</button>
        </div>
      )}

      {/* YOUR TEAM — the four tool rows, grouped into a card. The label stays
          outside it. These rows do NOT use the shared railRow/fillRow/clearRow
          that My cases, Brokerage, Ecosystem and the account menu use: they
          carry a tinted icon tile, an active state and a trailing badge, and
          folding that into the shared style would drag every other row with it.

          ACTIVE STATE IS NEW. Before this the rail marked nothing — all four
          rows rendered identically on every route, so the rail never showed
          where you were. `page` is threaded from Dashboard through RailColumn
          and RailDrawer to get here; it was not previously a prop.

          Every row carries a 3px transparent left border so the active one's
          red border does not shift its content sideways relative to the others. */}
      <div>
        <div style={railLabel}>YOUR TEAM</div>
        <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'7px 6px'}}>
          {TEAM_TOOLS.map(tool => {
            // Messages is admin-only until MESSAGES_LAUNCHED.
            if (tool.key === 'messages' && !messagesVisible) return null
            // Marketplace reads live MLS data with the brokerage's own key, so
            // it shows only where live comps do. The route explains itself to
            // anyone else (get-marketplace-listings refuses them).
            if (tool.key === 'marketplace' && !canUseLiveComps(profile)) return null
            // A locked row is visible to everyone and openable by nobody but an
            // admin. Every difference below hangs off this one value.
            const unlocked = !tool.locked || isAdmin
            const active = unlocked && page === tool.key
            const Icon = tool.Icon
            return (
              <button
                key={tool.key}
                onClick={unlocked ? ()=>navigateTo(tool.key) : undefined}
                aria-current={active ? 'page' : undefined}
                aria-disabled={unlocked ? undefined : true}
                style={{
                  width:'100%',textAlign:'left',border:'none',fontFamily:FONT_BODY,
                  cursor: unlocked ? 'pointer' : 'default',
                  display:'flex',alignItems:'center',gap:'9px',padding:'7px 9px',
                  borderRadius:'8px',transition:'background .15s',
                  borderLeft:`3px solid ${active ? '#e23744' : 'transparent'}`,
                  background: active ? '#f4f4f2' : 'transparent',
                }}
                onMouseEnter={e=>{ if (unlocked && !active) e.currentTarget.style.background = '#f4f4f2' }}
                onMouseLeave={e=>{ if (unlocked && !active) e.currentTarget.style.background = 'transparent' }}>
                <span style={{
                  width:'26px',height:'26px',borderRadius:'7px',flexShrink:0,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  background:tool.tile, opacity: unlocked ? 1 : 0.6,
                }}>
                  <Icon size={14} color={tool.ink} />
                </span>
                <span style={{
                  flex:1,minWidth:0,fontSize:'13px',
                  fontWeight: active ? 500 : 600,
                  color: unlocked ? '#1d2e42' : '#999',
                  whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',
                }}>{tool.label}</span>
                {/* The live chip only where live data actually flows: the
                    Strategist for a live-comps brokerage, Market Snapshot only
                    for a HomePros admin while it is paused for everyone else. */}
                {tool.badge === 'armls' && (tool.key === 'market' ? canUseMarketSnapshot(profile) : canUseLiveComps(profile)) && (
                  <span style={{
                    flexShrink:0,display:'inline-flex',alignItems:'center',gap:'4px',
                    background:'#FCEBEB',borderRadius:'10px',padding:'2px 7px',
                  }}>
                    <span className="rail-dot-pulse" style={{
                      width:'5px',height:'5px',borderRadius:'50%',background:'#e23744',
                      animation:'railDotPulse 1.4s ease-in-out infinite',
                    }} />
                    <span style={{fontSize:'8px',letterSpacing:'0.6px',fontWeight:700,color:'#791F1F',fontFamily:FONT_HEAD}}>ARMLS LIVE</span>
                  </span>
                )}
                {/* railNewBadge, the same gold chip the BROKERAGE rows use for
                    a new newsletter issue — reused rather than restyled, so the
                    rail has one NEW badge rather than two that nearly match.
                    Unlike that one this is not driven by unread state: it marks
                    a new TOOL, and it comes off the row when it stops being
                    new. */}
                {tool.badge === 'new' && (
                  <span style={railNewBadge}>NEW</span>
                )}
                {tool.badge === 'unread' && messagesUnread > 0 && (
                  <span style={railStatusText}>{messagesUnread} new</span>
                )}
                {/* Market Snapshot paused. A small rail chip in the grey pair
                    CASE_CHIP_TONES uses for neutral chips. Static, and the row
                    stays clickable so the page can explain. */}
                {tool.key === 'market' && !canUseMarketSnapshot(profile) && (
                  <span style={{
                    flexShrink:0,...CASE_CHIP_TONES.grey,
                    fontSize:'8.5px',padding:'2px 7px',borderRadius:'10px',
                    fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.4px',
                  }}>Paused</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* MY CASES — the old always-visible Recent list, now behind one row. */}
      <div style={{minWidth:0}}>
        <button
          onClick={()=>setMyCasesOpen(o=>!o)}
          aria-expanded={myCasesOpen}
          style={railRow} onMouseEnter={fillRow} onMouseLeave={clearRow}>
          <span style={{flex:1,minWidth:0,fontSize:'13px',fontWeight:600,color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            My cases{recent.length > 0 ? ` · ${recent.length}` : ''}
          </span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
            {myCasesOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
        </button>

        {myCasesOpen && (
          <div style={{maxHeight:'240px',overflowY:'auto',minWidth:0}}>
            {loadingRecent ? (
              <div style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,padding:'4px 8px'}}>Loading...</div>
            ) : recent.length === 0 ? (
              <div style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,lineHeight:1.5,padding:'4px 8px'}}>No cases yet.</div>
            ) : recent.map(r => (
              <button key={`${r.tool}-${r.id}`} onClick={()=>navigateTo(r.tool, r.id)}
                style={{...railRow,display:'block',fontSize:'13px',color:'#444',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                onMouseEnter={fillRow} onMouseLeave={clearRow}>
                {r.label || 'Untitled Property'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={railLabel}>BROKERAGE</div>
        {BROKERAGE_ROWS.map(row => {
          // My Wins is built from MLS data; shown to the licensed brokerages
          // (HomePros, HomePros Advantage), each seeing only its own office's
          // wins. The route itself shows a notice to anyone else.
          if (row.target === 'mywins' && !isMlsLicensed(profile)) return null
          const status = row.status ? railStatus[row.status] : undefined
          const showBadge = row.badge === 'new' && hasNewIssue
          return (
            <button key={row.label} onClick={()=>navigateTo(row.target)} style={railRow} onMouseEnter={fillRow} onMouseLeave={clearRow}>
              <span style={railTile}><RailIcon name={row.icon} /></span>
              {/* flex:1 so the label absorbs the slack and whatever follows sits
                  hard right; ellipsis so a long label clips instead of pushing
                  the status out of the rail. */}
              <span style={{flex:1,minWidth:0,fontSize:'13px',fontWeight:600,color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{row.label}</span>
              {showBadge && <span style={railNewBadge}>NEW</span>}
              {status && <span style={railStatusText}>{status}</span>}
            </button>
          )
        })}
      </div>

      {/* ECOSYSTEM — the old "YOUR BROKER ECOSYSTEM" card grid from the Hub
          page, plus the podcast room link from the content sidebar. Plain rows,
          every one of them off-site. */}
      <div>
        <div style={railLabel}>ECOSYSTEM</div>
        {ECOSYSTEM_LINKS.map(link => (
          <a key={link.label} href={link.url} target="_blank" rel="noopener noreferrer"
            style={{...railRow,textDecoration:'none',boxSizing:'border-box'}} onMouseEnter={fillRow} onMouseLeave={clearRow}>
            <span style={{flex:1,minWidth:0,fontSize:'13px',fontWeight:600,color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{link.label}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
              <path d="M7 17 17 7"/><polyline points="9 7 17 7 17 15"/>
            </svg>
          </a>
        ))}
      </div>

      {/* ACCOUNT — the menu floats above the profile row instead of sitting in
          flow. In flow it pushed the rail taller, which was fine when the page
          could grow but now overflows the bounded rail off the bottom. */}
      <div style={{marginTop:'auto',minWidth:0,position:'relative'}}>
        {accountOpen && (
          <div style={{position:'absolute',bottom:'100%',left:0,right:0,marginBottom:'8px',zIndex:20,background:'#ffffff',border:`1px solid ${RAIL_BORDER}`,borderRadius:'12px',padding:'6px',boxShadow:'0 8px 24px rgba(0,0,0,0.14)'}}>
            <button onClick={()=>go('profile')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Profile</button>

            <div style={divider} />

            {isAdmin && (
              <>
                {/* The modal moved here with the nav's upload button, so this
                    opens it directly. It used to only navigate to /content,
                    where the real button lived — that page is gone. */}
                <button onClick={()=>{setAccountOpen(false); setShowUpload(true)}} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Upload content</button>
                <button onClick={()=>{setAccountOpen(false); setAnnEditMsg(announcement?.message||''); setAnnEditActive(announcement?.active||false); setShowAnnEdit(true)}} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Manage announcement</button>
                <button onClick={()=>go('funnels-admin')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Funnels</button>
                <button onClick={()=>go('teams-admin')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Teams</button>
                <button onClick={()=>go('dealdesk-review')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Review</button>
                <button onClick={()=>go('recruiting-admin')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Recruiting</button>
                <button onClick={()=>go('billing-admin')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Billing</button>
                <button onClick={()=>go('admin')} style={menuItem} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Admin</button>
                <div style={divider} />
              </>
            )}

            <button onClick={()=>{setAccountOpen(false); signOut()}} style={{...menuItem,color:'#888'}} onMouseEnter={fillMenu} onMouseLeave={clearMenu}>Sign out</button>
          </div>
        )}

        <button
          onClick={()=>setAccountOpen(o=>!o)}
          aria-expanded={accountOpen}
          style={{width:'100%',display:'flex',alignItems:'center',gap:'10px',padding:'12px 8px 0',borderTop:`1px solid ${RAIL_BORDER}`,background:'transparent',border:'none',borderTopStyle:'solid',cursor:'pointer',minWidth:0,textAlign:'left',fontFamily:FONT_BODY}}>
          <div style={{width:'30px',height:'30px',borderRadius:'50%',background:'#1d2e42',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:700,fontFamily:FONT_HEAD,flexShrink:0}}>
            {initials}
          </div>
          <div style={{flex:1,minWidth:0,fontSize:'13px',color:'#555',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {profile?.full_name || session.user.email}
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
            {accountOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
        </button>
      </div>

      {/* Both modals moved down from Dashboard with the nav that used to open
          them. They are position:fixed overlays, so rendering them from inside
          the rail — or from inside the mobile drawer — puts them on top of the
          whole viewport all the same. */}
      {showUpload && (
        <UploadModal isMobile={isMobile} onClose={()=>setShowUpload(false)}
          onSuccess={()=>{setShowUpload(false);showToast('Content published!')}} />
      )}

      {showAnnEdit && createPortal(
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setShowAnnEdit(false)}>
          <div style={{background:'white',borderRadius:'12px',width:'min(440px, 95vw)',maxWidth:'95vw',padding:'28px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.15)',fontFamily:FONT_BODY}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
              <div style={{fontSize:'18px',fontWeight:900,letterSpacing:'1px',color:'#222',fontFamily:FONT_HEAD}}>EDIT ANNOUNCEMENT</div>
              <button style={S.closeBtn} onClick={()=>setShowAnnEdit(false)}>✕</button>
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>MESSAGE</label>
              <textarea style={{...S.textarea,minHeight:'80px'}} value={annEditMsg} onChange={e=>setAnnEditMsg(e.target.value)} placeholder="Type your announcement..." />
            </div>
            <div style={{marginBottom:'20px',display:'flex',alignItems:'center',gap:'10px'}}>
              <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'13px',fontWeight:600,color:'#222',fontFamily:FONT_BODY}}>
                <input type="checkbox" checked={annEditActive} onChange={e=>setAnnEditActive(e.target.checked)}
                  style={{width:'18px',height:'18px',accentColor:'#147efb'}} />
                {annEditActive ? <span style={{color:'#2d7a2d',fontWeight:700}}>✅ Banner is LIVE — agents can see this</span> : 'Turn ON to show banner to agents'}
              </label>
            </div>
            {annEditActive && annEditMsg.trim() && (
              <div style={{background:'#fdbf00',padding:'10px 16px',borderRadius:'6px',marginBottom:'20px',display:'flex',alignItems:'center',gap:'8px'}}>
                <span>📣</span>
                <span style={{fontSize:'13px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD}}>{annEditMsg.trim()}</span>
              </div>
            )}
            <div style={{display:'flex',gap:'10px'}}>
              <button style={S.btnOutline} onClick={()=>setShowAnnEdit(false)}>Cancel</button>
              <button style={{...S.btnPrimary,flex:2,opacity:annSaving?0.6:1}} onClick={saveAnnouncement} disabled={annSaving}>
                {annSaving ? 'SAVING...' : 'SAVE ANNOUNCEMENT'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ─── RAIL COLUMN ─────────────────────────────────────────────────────────────
// The desktop home for WorkspaceRail. Dashboard renders it once, above the page
// switch, so navigation never unmounts it. It used to persist scrollTop by hand
// into a module variable — unnecessary now that the node itself survives.
function RailColumn({ session, profile, navigateTo, showToast, isAdmin, signOut, recent, loadingRecent, railStatus, loadingRailStatus, hasNewIssue, isMobile, page }) {
  return (
    <div
      style={{flexShrink:0,display:'flex',flexDirection:'column',minHeight:0,background:RAIL_BG,overflowY:'auto'}}>
      <WorkspaceRail
        session={session} profile={profile} navigateTo={navigateTo} showToast={showToast}
        isAdmin={isAdmin} signOut={signOut}
        recent={recent} loadingRecent={loadingRecent} isMobile={isMobile} page={page}
        railStatus={railStatus} loadingRailStatus={loadingRailStatus} hasNewIssue={hasNewIssue}
      />
    </div>
  )
}

// ─── RAIL DRAWER ─────────────────────────────────────────────────────────────
// The mobile home for WorkspaceRail. Dashboard renders it once at the top of the
// main column, so like RailColumn it survives navigation.
//
// It owns drawerOpen and railNavigate outright, because nothing outside the
// drawer ever read either one. The scrim and panel are position:fixed, so their
// place in the DOM does not matter and the surrounding layout stays untouched.
function RailDrawer({ session, profile, navigateTo, showToast, isAdmin, signOut, recent, loadingRecent, railStatus, loadingRailStatus, hasNewIssue, page }) {
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Every rail destination — team rows, recents, and the account menu — goes
  // through the navigateTo prop, so wrapping it is enough to close the drawer on
  // navigation without touching WorkspaceRail.
  const railNavigate = (...args) => { setDrawerOpen(false); navigateTo(...args) }

  return (
    <>
      <div style={{flexShrink:0,display:'flex',alignItems:'center',gap:'8px',paddingLeft:'14px'}}>
        <button
          onClick={()=>setDrawerOpen(true)}
          aria-label="Open menu"
          style={{flexShrink:0,marginTop:'16px',background:'transparent',border:`1px solid ${RAIL_BORDER}`,borderRadius:'8px',width:'34px',height:'30px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      </div>

      {drawerOpen && (
        <>
          <div
            onClick={()=>setDrawerOpen(false)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:400}}
          />
          <div style={{position:'fixed',top:0,left:0,bottom:0,width:'82%',maxWidth:'300px',background:RAIL_BG,zIndex:401,overflowY:'auto',WebkitOverflowScrolling:'touch'}}>
            <button
              onClick={()=>setDrawerOpen(false)}
              aria-label="Close menu"
              style={{position:'absolute',top:'12px',right:'12px',zIndex:1,background:'transparent',border:'none',cursor:'pointer',padding:'4px',lineHeight:1,color:'#888',fontSize:'18px'}}>
              ✕
            </button>
            <WorkspaceRail
              session={session} profile={profile} navigateTo={railNavigate} showToast={showToast}
              isAdmin={isAdmin} signOut={signOut}
              recent={recent} loadingRecent={loadingRecent} isMobile page={page}
              railStatus={railStatus} loadingRailStatus={loadingRailStatus} hasNewIssue={hasNewIssue}
            />
          </div>
        </>
      )}
    </>
  )
}

// ─── CASE CARD ───────────────────────────────────────────────────────────────
// One case, as a card, for every tool's case list. Presentation only: no state,
// no fetching, no knowledge of which table the row came from. The caller builds
// `chips` and joins `meta`, so the three lists can differ in content while the
// geometry stays identical — the thing three hand-copied row blocks could not
// hold on to.
//
// Every delete control stops propagation. The whole card is the open target, so
// without that a click on "No" would also open the case it just declined to
// delete.
const CASE_CHIP = {
  padding: '3px 8px', borderRadius: '12px', fontSize: '10px',
  fontWeight: 600, letterSpacing: '0.3px', display: 'inline-flex',
  fontFamily: FONT_HEAD, whiteSpace: 'nowrap',
}
// bg / text pairs. Rep colours carry meaning across tools: gold is the seller
// side, blue the buyer side, and they must match wherever a rep is shown.
const CASE_CHIP_TONES = {
  gold:  { background: 'rgba(253,191,0,0.20)',  color: '#854f0b' },
  blue:  { background: 'rgba(20,126,251,0.12)', color: '#185fa5' },
  grey:  { background: 'rgba(136,135,128,0.16)', color: '#5f5e5a' },
  green: { background: 'rgba(99,153,34,0.14)',  color: '#3b6d11' },
}
const caseChip = (tone, label) => (
  <span key={label} style={{ ...CASE_CHIP, ...(CASE_CHIP_TONES[tone] || CASE_CHIP_TONES.grey) }}>{label}</span>
)

// ─── STEP MARKER ─────────────────────────────────────────────────────────────
// Numbered badge for the Strategy case view's five steps. A new shape, not a
// variant of an existing one: the chip vocabulary above is a 12px-radius pill,
// and nothing in this file was round or numbered.
//
// THE NUMBER STAYS VISIBLE WHEN DONE. A checkmark would say the step is
// finished and take away the thing that says where it sits in the sequence,
// which is the entire reason the markers are numbered rather than ticked.
//
// Exactly one is gold at a time — see stepState in StrategyCaseView. Two golds
// would be two next actions, which is the problem this replaces rather than a
// version of it.
const STEP_MARKER_TONES = {
  done:  { background: '#f4f4f2', color: '#999999' },
  next:  { background: '#fdbf00', color: '#412402' },
  later: { background: '#1d2e42', color: '#ffffff' },
}
// Only 'next' inverts. done is grey and later is navy, and both read fine on
// gold — it is the gold-on-gold pair that disappears, and it disappears in the
// one state the marker exists to signal.
const STEP_MARKER_NEXT_ON_GOLD = { background: '#412402', color: '#fdbf00' }

// onGold is a PROP, not a detected background. The marker has no business
// knowing where it was placed, and sniffing the parent would make every future
// caller's colour choice a silent input to this component.
function StepMarker({ n, state, onGold = false }) {
  const tone = (state === 'next' && onGold)
    ? STEP_MARKER_NEXT_ON_GOLD
    : (STEP_MARKER_TONES[state] || STEP_MARKER_TONES.later)
  return (
    <span style={{
      width: '19px', height: '19px', borderRadius: '50%',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '10.5px', fontWeight: 500, flexShrink: 0, fontFamily: FONT_BODY,
      ...tone,
    }}>{n}</span>
  )
}

// ─── DOCUMENT TILE ───────────────────────────────────────────────────────────
// One appearance for documents across the Strategist, the inspection desk and
// Deal Desk. PRESENTATION ONLY: every tool keeps its own uploadFile,
// removeFile, hidden input and file_type strings, and this component knows
// none of them. It is handed a label, a sub-line and — for an empty or add
// tile — the tool's own <input>, which it renders as a child of its <label>
// root so the whole tile is the click target.
//
// The three tools are three different systems. Strategy's removeFile takes
// (id, name), BINSR's takes (id, name) against a different bucket, Deal Desk's
// takes the whole row and reads storage_path. That stays true; only the pixels
// are shared.
//
// THE ICONS LIVE HERE. The upload glyph was inlined six times across the three
// tools before this — once in Strategy, twice in BINSR, three times in Deal
// Desk — and the filled state was the same drawing recoloured. A filled tile
// now gets a real document mark instead.
function DocTileIcon({ mode }) {
  const color = mode === 'filled' ? '#147efb' : '#999'
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
  if (mode === 'filled') {
    // A page with a folded corner. New: before this, a filled row showed the
    // upload arrow in blue, which said "put something here" about a slot that
    // already had something in it.
    return (
      <span style={{ flexShrink: 0, display: 'flex', color }}>
        <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      </span>
    )
  }
  if (mode === 'add') {
    return (
      <span style={{ flexShrink: 0, display: 'flex', color }}>
        <svg {...common}><path d="M12 5v14M5 12h14"/></svg>
      </span>
    )
  }
  return (
    <span style={{ flexShrink: 0, display: 'flex', color }}>
      <svg {...common}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
    </span>
  )
}

function DocTile({ mode, label, sub, chip, onRemove, input, title }) {
  const [hover, setHover] = useState(false)
  const isFilled = mode === 'filled'
  // A <label> for the two states that open a picker, so a click anywhere on
  // the tile reaches the hidden input inside it. A filled tile has no input
  // and must not behave like a button, so it is a plain div.
  const Root = isFilled ? 'div' : 'label'
  return (
    <Root
      title={title}
      onMouseEnter={()=>setHover(true)}
      onMouseLeave={()=>setHover(false)}
      style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px',
        borderRadius: '9px', padding: '9px 11px', fontFamily: FONT_BODY,
        border: isFilled ? '0.5px solid #e8e8e8' : `0.5px dashed ${hover ? '#147efb' : '#d8d8d4'}`,
        background: isFilled ? '#f4f4f2' : '#ffffff',
        cursor: isFilled ? 'default' : 'pointer',
        transition: 'border-color .15s',
      }}>
      <DocTileIcon mode={mode} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span style={{ fontSize: '12.5px', color: '#1d2e42' }}>{label}</span>
          {chip && (
            <span style={{ flexShrink: 0, fontSize: '9.5px', fontWeight: 500, padding: '1px 6px', borderRadius: '10px', background: '#FAEEDA', color: '#633806', whiteSpace: 'nowrap' }}>{chip}</span>
          )}
        </span>
        {/* nowrap + ellipsis so a long filename truncates instead of growing
            the tile and breaking the row's even split. */}
        <span style={{ display: 'block', fontSize: '11px', color: isFilled ? '#666' : '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>
      </span>
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '14px', lineHeight: 1, padding: '2px 4px', transition: 'color .15s' }}
          onMouseEnter={e=>e.currentTarget.style.color='#C8102E'}
          onMouseLeave={e=>e.currentTarget.style.color='#999'}
        >✕</button>
      )}
      {input}
    </Root>
  )
}

// The row. Tiles carry flex:1 and minWidth:0, so across they share the width
// evenly and stacked they each fill it.
function DocTileRow({ children, isMobile }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px', flexDirection: isMobile ? 'column' : undefined }}>
      {children}
    </div>
  )
}

// ─── RECENCY GROUPING ────────────────────────────────────────────────────────
// Day boundaries are PHOENIX days, not UTC ones. A case touched at 6pm Phoenix
// is already "tomorrow" in UTC, so a UTC boundary would file this evening's work
// under yesterday for the whole evening — the exact hours an agent is most
// likely to be looking at the list. Arizona does not observe DST, so the offset
// is a fixed -07:00 and never shifts mid-year; the Intl call is still the right
// way to ask, because it stays correct if that ever changes.
const PHOENIX_TZ = 'America/Phoenix'
// en-CA formats as YYYY-MM-DD, which parses back to calendar parts cleanly.
const phoenixDay = (d) => {
  const [y, m, day] = new Intl.DateTimeFormat('en-CA', {
    timeZone: PHOENIX_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d).split('-').map(Number)
  return { y, m, d: day }
}
// Whole days between two Phoenix calendar dates. Rebuilding them through
// Date.UTC makes this pure date arithmetic with no clock time in it, so no
// partial-day or offset error can creep in.
const phoenixDayDiff = (then, now) =>
  Math.round((Date.UTC(now.y, now.m - 1, now.d) - Date.UTC(then.y, then.m - 1, then.d)) / 86400000)

// Order is the render order. A group with no cases is dropped at render.
const RECENCY_GROUPS = ['Today', 'This week', 'Earlier this month', 'Older']
const recencyGroupOf = (updatedAt, now) => {
  const t = phoenixDay(new Date(updatedAt))
  const n = phoenixDay(now)
  const days = phoenixDayDiff(t, n)
  // <= 0 rather than === 0: a row whose updated_at is slightly ahead of the
  // client clock is today, not a negative-day group that does not exist.
  if (days <= 0) return 'Today'
  // 1–6. "Last 7 days excluding today" — today is day 0 and owns its own group.
  if (days < 7) return 'This week'
  if (t.y === n.y && t.m === n.m) return 'Earlier this month'
  return 'Older'
}

const GROUP_EYEBROW = {
  fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase',
  color: '#999', marginBottom: '8px', fontFamily: FONT_HEAD,
}

// `stage` is optional: { text, color }. Every consumer of this card passes chips
// and meta, but only the Strategy list has a pipeline worth naming, so the row
// is omitted entirely rather than reserved as blank space for the others.
function CaseCard({ chips, address, meta, stage, onOpen, deletable, confirming, onAskDelete, onConfirmDelete, onCancelDelete }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn() }
  return (
    <div
      onClick={onOpen}
      style={{
        border: '0.5px solid #e8e8e8', borderRadius: '11px',
        background: '#ffffff',
        padding: '11px 13px', position: 'relative', cursor: 'pointer',
      }}>

      {chips != null && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>{chips}</div>
      )}

      {/* paddingRight keeps a long address clear of the delete affordance, and it
          is measured against the card's side padding rather than guessed. The X
          is 22px wide at right:12px, so its left edge sits 34px in from the card
          edge; the title's own box already stops at the 13px padding, so 27px
          leaves ~6px of clearance — the same gap the old 22px bought against the
          old 18px padding.

          The address WRAPS. It is the one thing on this card an agent needs in
          full, so no ellipsis and no nowrap: normal wrapping handles the spaces
          in "15919 W CENTRAL ST, Surprise, AZ 85374", and overflowWrap catches
          the token that has none. Cards in a row size to the tallest, which is
          the correct trade for never hiding half a street name. */}
      <div style={{ fontWeight: 500, fontSize: '13.5px', color: '#1d2e42', fontFamily: FONT_BODY, paddingRight: '27px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
        {address}
      </div>
      <div style={{ fontSize: '11.5px', color: '#888', fontFamily: FONT_BODY, marginTop: '2px' }}>{meta}</div>
      {stage && (
        <div style={{ fontSize: '10.5px', color: stage.color, fontFamily: FONT_BODY, marginTop: '2px' }}>{stage.text}</div>
      )}

      {deletable && (
        <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
          {confirming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
              <span style={{ fontSize: '12px', color: '#C8102E', fontFamily: FONT_BODY }}>Delete?</span>
              <button onClick={stop(onConfirmDelete)} style={{ ...S.btnDanger, padding: '4px 10px', fontSize: '11px', flex: 'none' }}>Yes</button>
              <button onClick={stop(onCancelDelete)} style={{ background: 'none', border: '1px solid #e8e8e8', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: '#555', fontFamily: FONT_HEAD }}>No</button>
            </div>
          ) : (
            <button
              onClick={stop(onAskDelete)}
              style={{ width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '15px', color: '#999', borderRadius: '6px', padding: 0, transition: 'color .15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#C8102E'}
              onMouseLeave={e => e.currentTarget.style.color = '#999'}
            >✕</button>
          )}
        </div>
      )}
    </div>
  )
}

// Three up on a real screen, one on a phone. minmax(0,1fr) rather than 1fr so a
// long unbroken address cannot force a column wider than its third.
//
// Three at a 10px gap gives 280px a card inside the 860px shell column, which is
// what the tightened padding and 13.5px title were sized against.
const caseGrid = (isMobile) => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0,1fr))',
  gap: '10px',
})

// Strategy's own grid. caseGrid above is shared with BINSR and Deal Desk, so
// widening it in place would reflow two lists that were never designed for four
// columns and are not part of this pass.
//
// auto-fill rather than a hard repeat(4): four columns at the shell's full
// width, then three, then two as the window narrows, with no media query and no
// breakpoint to maintain. 240px is the floor at which the 13.5px address still
// wraps to a readable two lines instead of four.
//
// Same minmax(0,...) as caseGrid, so a long unbroken token still cannot force a
// column wider than its share. Grid items stretch by default, so cards continue
// to size to the tallest in their row, and CaseCard owns the address wrapping —
// neither behaviour changes here.
const strategyGrid = (isMobile) => ({
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: '10px',
})

// ─── ICON TILE SELECTOR ──────────────────────────────────────────────────────
// The setup selector every tool should share. Today's pills are one primitive
// (DealDeskPillButton) wrapped three different ways, with icons supplied as raw
// path strings keyed by DISPLAY LABEL — rename an option and its icon silently
// vanishes. This keys on value and takes a React node, so neither can drift.
//
// No tooltips, deliberately. The pill carries real tooltip machinery — layout
// measurement, clip-ancestor detection, mobile tap-to-reveal — and a tile that
// shows its icon and label at size does not need a hover to explain itself. The
// pill stays where per-option explanation genuinely earns its keep.
// Keyed by option VALUE. The three maps these came from were keyed by label.
const TILE_ICONS = {
  // Property type — the four already in use.
  'Single Family': '<path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>',
  'Condo / Townhouse': '<rect x="4" y="3" width="7" height="18"/><rect x="13" y="8" width="7" height="13"/><path d="M7 7h1M7 11h1M7 15h1M16 12h1M16 16h1"/>',
  'Manufactured / Mobile': '<path d="M2 17h20"/><path d="M4 17v-5l6-4h8v9"/><circle cx="8" cy="19" r="1.4"/><circle cx="17" cy="19" r="1.4"/>',
  'Land': '<path d="M4 20h16"/><path d="M12 20V9"/><path d="M12 9 8 5M12 9l4-4"/><path d="M12 14l-3-2M12 14l3-2"/>',
  // Client type — these already existed in ICON_PATHS and were never rendered,
  // because the client-type row passed showIcon:false.
  'Seller': '<path d="M3 3h7l10 10-7 7L3 10V3z"/><circle cx="7" cy="7" r="1.4"/>',
  'Buyer': '<circle cx="8" cy="8" r="4"/><path d="M11 11l9 9"/><path d="M17 17l2-2M19.5 14.5l1.5-1.5"/>',
  'First-time buyer': '<path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-.5z"/>',
  'Investor': '<path d="M3 17l6-6 4 4 7-7"/><path d="M17 5h4v4"/>',
  'Listing agent': '<rect x="3" y="7" width="18" height="13" rx="1.5"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  // Condition — new. Same 1.7 weight and 24-box as the rest.
  'Distressed': '<path d="M12 3 2.5 20h19z"/><path d="M12 9v5"/><path d="M12 17h.01"/>',
  'Original': '<path d="M4 10.5 12 4l8 6.5"/><path d="M6 10v10h12V10"/><path d="M10 20v-5h4v5"/>',
  'Partially updated': '<path d="M15.5 3.5a4.5 4.5 0 0 0-5.9 5.9L3 16l5 5 6.6-6.6a4.5 4.5 0 0 0 5.9-5.9l-3 3-2.9-2.9z"/>',
  'Fully remodeled': '<path d="M12 3l1.9 4.6L18.5 9l-4.6 1.4L12 15l-1.9-4.6L5.5 9l4.6-1.4z"/><path d="M18 16l.7 1.8L20.5 18.5l-1.8.7L18 21l-.7-1.8L15.5 18.5l1.8-.7z"/>',
  // Deal Desk form sets. A sheet with a folded corner and a second sheet behind
  // it — a SET of forms, not one document. Both values share the glyph: the
  // choice is WHICH forms, and both options are equally a form set.
  'HomePros': '<path d="M9 2h5l4 4v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/><path d="M14 2v4h4"/><path d="M11 11h5M11 14h4"/><path d="M6 6v13a2 2 0 0 0 2 2h8"/>',
  'HomePros Advantage': '<path d="M9 2h5l4 4v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"/><path d="M14 2v4h4"/><path d="M11 11h5M11 14h4"/><path d="M6 6v13a2 2 0 0 0 2 2h8"/>',
}

// The create-card wrapper, shared. The same white/RAIL_BORDER string was copied
// byte-identical into all three create forms; this is that one style, restyled
// to the CaseCard family so a case list and the card that makes a case read as
// the same object. Each screen keeps its own marginBottom.
const SETUP_CARD_BLUE = {
  border: '1.5px solid rgba(20,126,251,0.55)',
  borderRadius: '16px',
  background: 'linear-gradient(150deg, rgba(20,126,251,0.12), rgba(20,126,251,0.01) 60%)',
  padding: '20px',
}

// Field + button on one line, stacked on a phone where a button beside a text
// input is too cramped to hit. Shared so the three create cards cannot drift.
const createRow = (isMobile) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  alignItems: 'stretch',
  gap: '10px',
})

// 13px, the size a slim pill wants. Reads TILE_ICONS, which Deal Desk's create
// card still uses.
const pillIcon = (paths) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
       style={{flexShrink:0}} aria-hidden="true"
       dangerouslySetInnerHTML={{ __html: paths }} />
)

// The setup eyebrow, in one place. It was three byte-identical copies —
// IconTileSelector's label, Strategy's DOCUMENTS header and BINSR's groupLabel
// — each adding its own margin. Only the five shared properties live here; the
// margins stay at the call sites, because they differ and are layout rather
// than type. A deduplication, not a restyle: no value changed.
const SETUP_EYEBROW = { fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#999', fontFamily:FONT_HEAD }

// The shared cap for all three tool setups. They had three different widths
// for the same block — Strategy uncapped, BINSR uncapped after its 560px came
// off, Deal Desk at 520 — so the pill rows, the hints and the document lists
// all ended on a different edge in each tool. One value here means they end on
// the same one.
const SETUP_CONTENT_WIDTH = '720px'

// The one selector all three setups use. It replaced an icon-tile grid, which
// had been the wrong shape for these rows: a two-option Buyer/Seller does not
// need artwork to be legible, and tiles at that count read as a large empty
// grid. Options are [{ value, label }]; any `icon` on an option is ignored.
function PillSelector({ label, options, selected, onSelect, disabledFor, allowDeselect = true, hint, chip }) {
  // One key, not a boolean per pill — only one pill can be hovered at a time.
  // Same approach the widen pills and the panel header button use.
  const [hoverKey, setHoverKey] = useState(null)
  return (
    <div>
      <div style={{display:'flex',alignItems:'baseline',gap:'16px',marginBottom:'13px'}}>
        {/* Without a chip this is the same single div it has always been. The
            104px is load-bearing — the hint below offsets by 120px, which is
            this width plus the row's 16px gap — so the wrapper keeps it and
            lets the chip sit outside, where it costs the label nothing.
            baseline, matching the row, so a 9.5px chip sits on the same line
            as a 10px uppercase eyebrow rather than floating above it. */}
        {chip ? (
          <div style={{display:'flex',alignItems:'baseline',gap:'6px',flexShrink:0}}>
            <div style={{...SETUP_EYEBROW,width:'104px',flexShrink:0}}>{label}</div>
            <span style={{fontSize:'9.5px',fontWeight:500,padding:'1px 6px',borderRadius:'10px',background:'#FAEEDA',color:'#633806',whiteSpace:'nowrap',flexShrink:0,fontFamily:FONT_BODY}}>{chip}</span>
          </div>
        ) : (
          <div style={{...SETUP_EYEBROW,width:'104px',flexShrink:0}}>{label}</div>
        )}
        <div style={{display:'flex',flexWrap:'wrap',gap:'7px'}}>
          {options.map(o => {
            const isSelected = selected === o.value
            const isDisabled = !!(disabledFor && disabledFor(o.value))
            const isHovered = hoverKey === o.value && !isDisabled && !isSelected
            return (
              <button
                key={o.value}
                disabled={isDisabled}
                onMouseEnter={()=>setHoverKey(o.value)}
                onMouseLeave={()=>setHoverKey(null)}
                onClick={()=>{
                  if (isDisabled) return
                  // Deselect-to-clear: clicking the lit pill clears it,
                  // carried over from the tile grid this replaced.
                  onSelect(isSelected && allowDeselect ? null : o.value)
                }}
                style={{
                  fontSize:'12.5px', fontFamily:FONT_BODY, padding:'5px 13px', borderRadius:'18px',
                  cursor: isDisabled ? 'default' : 'pointer',
                  border: isSelected ? '0.5px solid #147efb' : `0.5px solid ${isHovered ? '#d8d8d4' : '#e8e8e8'}`,
                  background: isSelected ? '#f4f4f2' : '#ffffff',
                  color: isSelected ? '#1d2e42' : '#666',
                  fontWeight: isSelected ? 500 : 400,
                  transition:'all .15s',
                  ...(isDisabled ? { opacity: 0.4 } : {}),
                }}>
                {o.label}
              </button>
            )
          })}
        </div>
      </div>
      {/* 120px = the label's 104px plus the row's 16px gap, so the hint starts
          under the pills rather than under the label. */}
      {hint && (
        <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginLeft:'120px'}}>{hint}</div>
      )}
    </div>
  )
}


// One reading column for every conversational tool. Strategy, BINSR and Deal
// Desk each carried their own 900px constant and ADA sat on the shell's 640px
// default, so four tools read at three widths. The per-tool constants remain as
// names because the notice bars, error boxes and controls card reference them.
const SHARED_CONTENT_WIDTH = '860px'

// ─── CASE SHELL ──────────────────────────────────────────────────────────────
// The one two-column shell every case view runs on, so Strategy, BINSR and ADA
// cannot drift apart. It owns the frame — rail, stage, transcript, composer —
// and nothing about any individual tool. Everything tool-specific arrives as a
// node: `summary`, `setup`, `controls`, `footer`, and `renderMessage`.
//
// Scrolling is the shell's job because it has the container. Consumers reach it
// through a forwarded ref:
//
//   const shell = useRef(null)
//   <CaseShell ref={shell} … />
//   shell.current?.scrollToBottom()
//
// renderMessage is called as renderMessage(message, streamingRef). Attaching
// that ref to whichever message should anchor pinAssistantToTop is the
// consumer's choice — Strategy and BINSR pin the question today, ADA pins the
// answer, and the shell stays out of that argument.
const CaseShell = forwardRef(function CaseShell({
  isMobile,
  title, summary, setupOpen, onToggleSetup, setup,
  // Optional. Absent → the bare 28px circle, unchanged. Present → a labelled
  // pill, because a naked chevron does not say what it opens.
  toggleLabel, toggleLabelAccent,
  loading, messages, renderMessage, emptyState, onTranscriptScroll,
  contentWidth = SHARED_CONTENT_WIDTH,
  controls,
  composerValue, onComposerChange, onSubmit, composerPlaceholder, sendDisabled,
  composerMultiline, composerDisabled, showComposer = true,
  // Optional node rendered immediately before the send button, inside the
  // composer's flex row. Strategy passes its step marker; every other caller
  // passes nothing and the composer is byte-for-byte what it was. Same shape
  // as panelStep — a node, so the shell stays ignorant of what a step is.
  composerBadge,
  footer,
  // Optional second column beside the transcript. Absent → the shell is the
  // single scrolling column it has always been, and every existing call site
  // is unaffected because the row collapses to just the scroller.
  panel,
  // Names the panel in its header and, rotated, on the collapsed strip. A
  // consumer that passes a panel without one still works — a strip with no
  // words on it would be an unlabelled sliver nobody could identify.
  panelLabel = 'Panel',
  // Optional node rendered immediately before panelLabel in the panel header.
  // Strategy passes its step marker; every other caller passes nothing and the
  // header is byte-for-byte what it was. A node rather than a number, so the
  // shell stays ignorant of what a step is.
  panelStep,
  // Panel takes the whole case area and the scroller hides. Deliberately NOT
  // an overlay: every overlay in this file shares zIndex 500, CompDetailModal's
  // Escape handler is unconditional and it locks body scroll, so a second
  // overlay would fight all three. Widening the panel leaves CompDetailModal
  // the only overlay, exactly as it already works.
  panelWide = false,
  // Whether the panel offers its collapse chevron. Defaults true, so every
  // existing caller keeps the control it had. A consumer that reserves the
  // column before it has anything to put in it passes false, since collapsing
  // an empty panel to a strip labelled after nothing is a control that only
  // makes the screen worse.
  panelCollapsible = true,
  // Optional text link in the panel header, between the label and the chevron.
  // Both are required for it to render — a link with no handler is furniture.
  panelAction,
  onPanelAction,
}, ref) {
  const transcriptRef = useRef(null)
  const streamingMessageRef = useRef(null)
  // The shell owns the row, so it owns whether the second column is wide or
  // thin. Deliberately NOT a prop: a consumer supplies the panel's contents and
  // its name, and has no business knowing how much width the row gave it.
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  // Which of the two columns is showing below 768px, where they cannot sit side
  // by side. Internal for the same reason panelCollapsed is: the row is the
  // shell's, and a consumer has no business knowing how it was divided.
  // Ignored entirely above the breakpoint.
  const [narrowView, setNarrowView] = useState('conversation')
  // Which header control the cursor is over, or null. A key rather than a
  // boolean per control — the same approach the widen pills use, and it stays
  // correct if the header ever carries a second one.
  const [hoverKey, setHoverKey] = useState(null)

  // Scrolls the transcript container itself, never the page — setting scrollTop
  // on the ref rather than calling scrollIntoView, which would move the window.
  function scrollToBottom() {
    const container = transcriptRef.current
    if (!container) return
    requestAnimationFrame(() => { container.scrollTop = container.scrollHeight })
  }

  // Pins the streaming answer's top edge to the top of the visible area, so a
  // long response reads from its first line instead of chasing the bottom.
  function pinAssistantToTop() {
    const container = transcriptRef.current
    if (!container) return
    requestAnimationFrame(() => {
      const el = streamingMessageRef.current
      if (!el || !container) return
      const containerTop = container.getBoundingClientRect().top
      const elTop = el.getBoundingClientRect().top
      container.scrollTop += (elTop - containerTop)
    })
  }

  useImperativeHandle(ref, () => ({ scrollToBottom, pinAssistantToTop }))

  return (
    // The stage only. The rail and the mobile drawer moved up to Dashboard so
    // they render once and survive navigation; this is now purely the right-hand
    // column, and Dashboard's main column is what bounds its height.
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',background:'#ffffff',minHeight:0,overflow:'hidden'}}>

        {/* Title row stays out of the scroll area so the summary strip and the
            chevron remain reachable however far down the transcript runs. */}
        <div style={{flexShrink:0,display:'flex',alignItems:'center',gap:'12px',padding:'8px 20px 12px',minWidth:0}}>
          <div style={{fontSize:'16px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',flexShrink:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'50%'}}>
            {title}
          </div>
          {/* overflow:hidden so a summary that cannot shrink — an unbreakable
              pill, a long label — clips instead of painting over the chevron. */}
          {summary && <div style={{flex:1,minWidth:0,overflow:'hidden'}}>{summary}</div>}
          {onToggleSetup && (toggleLabel ? (
            // No aria-label: the visible text is the accessible name, which is
            // what WCAG 2.5.3 asks for. An aria-label of "Show setup" over a
            // button reading "Documents" would leave voice control unable to
            // address it by the words on screen.
            <button
              onClick={onToggleSetup}
              aria-expanded={!!setupOpen}
              style={{marginLeft:'auto',flexShrink:0,display:'inline-flex',alignItems:'center',gap:'5px',borderRadius:'16px',padding:'5px 11px',fontSize:'11.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,whiteSpace:'nowrap',cursor:'pointer',transition:'background .15s',
                      background:toggleLabelAccent ? '#fffdf5' : 'transparent',
                      border:toggleLabelAccent ? '1px solid #fdbf00' : `0.5px solid ${RAIL_BORDER}`}}
              onMouseEnter={e=>e.currentTarget.style.background = toggleLabelAccent ? '#fff8e6' : RAIL_BG}
              onMouseLeave={e=>e.currentTarget.style.background = toggleLabelAccent ? '#fffdf5' : 'transparent'}>
              {toggleLabel}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={toggleLabelAccent ? '#8a6800' : '#888'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {setupOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
          ) : (
            <button
              onClick={onToggleSetup}
              aria-expanded={!!setupOpen}
              aria-label={setupOpen ? 'Hide setup' : 'Show setup'}
              style={{marginLeft:'auto',flexShrink:0,background:'transparent',border:`1px solid ${RAIL_BORDER}`,borderRadius:'999px',width:'28px',height:'28px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'background .15s'}}
              onMouseEnter={e=>e.currentTarget.style.background=RAIL_BG}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {setupOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
          ))}
        </div>

        {/* Below 768px the two columns cannot sit side by side, so one shows at
            a time and this chooses which. Only when there IS a panel — with one
            column there is nothing to switch between.
            Same segmented pill the Events page uses, so the app has one of
            these patterns rather than two. Full width here rather than that
            one's inline-flex, because it is the only control on its row. */}
        {isMobile && panel && (
          <div style={{flexShrink:0,padding:'10px 16px',borderBottom:'0.5px solid #e8e8e8'}}>
            <div style={{display:'flex',border:'0.5px solid #e8e8e8',borderRadius:'16px',overflow:'hidden'}}>
              {[['conversation','Conversation'],['panel',panelLabel]].map(([k,label]) => (
                <button key={k} onClick={()=>setNarrowView(k)}
                  style={{flex:1,border:'none',padding:'7px 12px',fontSize:'12px',fontFamily:FONT_BODY,fontWeight:500,
                    whiteSpace:'nowrap',cursor:'pointer',transition:'all .15s',
                    background:narrowView===k ? '#f4f4f2' : 'transparent',
                    color:narrowView===k ? '#1d2e42' : '#666'}}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Scroller + optional panel on one row. The ROW now carries the
            vertical bound the scroller used to take directly from the outer
            column — flex:1 to claim the space between the title row and the
            controls, minHeight:0 so it can actually shrink and let its children
            scroll rather than growing to fit them.
            The scroller's own style is untouched. Its flex:1 now governs its
            horizontal share of this row instead of its vertical share of the
            column, which is what makes it yield exactly the panel's width, and
            it still fills the row's height through the default align stretch.
            With no panel the row has one child at flex:1 — identical layout to
            before the row existed. */}
        <div style={{display:'flex',flex:1,minHeight:0}}>

          {/* LEFT COLUMN — the conversation side. Holds the scroller, the
              controls slot, the composer and the footer, so the PANEL beside it
              runs the full height of the row instead of stopping above three
              full-width siblings.
              Hidden outright when the comps take the whole screen — the See all
              grid on desktop, the Comps tab on a phone. There is no message box
              while comps are full-screen; Back to conversation, or the
              Conversation tab, brings the whole column back.
              minHeight:0 is load-bearing. Without it the scroller's flex:1 has
              nothing to shrink against and the column grows past the row. */}
          <div style={{flex:1,minWidth:0,minHeight:0,display:((isMobile && narrowView === 'panel') || panelWide) ? 'none' : 'flex',flexDirection:'column'}}>
          {/* overflowX is pinned to hidden on purpose. Leaving it unset makes CSS
              compute it to `auto` (one axis non-visible forces the other), which
              turns this into a horizontal scroller — and setup grids built on
              `1fr` carry a min-content floor they cannot shrink past, so they
              overflow and take the centered column sideways with them. */}
          {/* HIDDEN, NEVER UNMOUNTED. display:'none' rather than a conditional:
              unmounting this element destroys scrollTop, nulls transcriptRef so
              scrollToBottom and pinAssistantToTop silently no-op, and strands
              shouldAutoScroll in the consumer describing an element that no
              longer exists — with no error and no log. The dispatcher effect
              will not re-fire to repair it, because no message id changed. */}
          <div ref={transcriptRef} onScroll={onTranscriptScroll} style={{flex:1,minWidth:0,minHeight:0,overflowY:'auto',overflowX:'hidden',padding:'4px 20px 20px'}}>
            {loading ? (
              // Nothing else renders while loading — not setup, not the empty
              // state. A consumer whose setup is expensive shouldn't paint it
              // against a transcript that hasn't arrived yet.
              <div style={{width:'100%',maxWidth:contentWidth,margin:'0 auto'}}>
                <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY,paddingTop:'40px'}}>Loading...</div>
              </div>
            ) : (
              <>
                {/* Setup is full-bleed inside the scroll area — it is the wide,
                    two-or-three-column part of a case and must not be squeezed
                    into the transcript's reading column. */}
                {setupOpen && setup && (
                  <div style={{width:'100%',minWidth:0,marginBottom:'24px'}}>{setup}</div>
                )}

                {/* One centered column via margin:auto. Centering lives here alone
                    — the rows below no longer each re-declare width and max-width. */}
                <div style={{width:'100%',maxWidth:contentWidth,margin:'0 auto',display:'flex',flexDirection:'column',gap:'18px'}}>
                  {messages.length === 0
                    ? emptyState
                    : messages.map(m => renderMessage(m, streamingMessageRef))}
                </div>
              </>
            )}
          </div>

            {/* The report bar — Generate Report, the notes and language toggles,
                the headshots. It belongs to the conversation, not to the comps,
                which is why it sits in this column.
                //
                Measured on an iPhone: the shell gets 557px and the fixed-height
                siblings take 48 title + 50 segmented control + 153 controls + 79
                composer, leaving the flex row 227. The panel's own fixed top
                block needs more than that, so overflow:hidden clipped the comp
                cards out of sight. Those numbers still hold; what reclaims the
                space has changed. This slot no longer hides itself — the COLUMN
                hides, taking the controls, the composer and the footer with it,
                so the comps get the whole row rather than the 153 alone.
                //
                Still display and not a conditional, up on the column: the report
                toggles hold state, and unmounting would reset an agent's language
                and notes choice every time they looked at a comp. */}
            {controls && <div style={{flexShrink:0}}>{controls}</div>}

        {/* COMPOSER — pinned to the bottom of the column. Off for the case
            lists, which are not conversations; on everywhere else. */}
        {showComposer && (
        <div style={{flexShrink:0,padding:isMobile?'0 20px 20px':'0 20px 28px',display:'flex',justifyContent:'center'}}>
          {/* The input keeps flex:1, so it absorbs the space the paperclip used
              to occupy and the send button stays hard right on its own. */}
          <div style={{width:'100%',maxWidth:contentWidth,display:'flex',alignItems:composerMultiline?'flex-end':'center',gap:'10px',background:'white',border:'1px solid #d8d8d4',borderRadius:'14px',padding:'8px 8px 8px 14px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
            {composerMultiline ? (
              // Shift+Enter newlines, Enter sends — the tools that draft long
              // passages need the break; ADA's single-line ask does not.
              <textarea
                value={composerValue}
                onChange={onComposerChange}
                onKeyDown={e=>{ if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); onSubmit() } }}
                placeholder={composerPlaceholder}
                disabled={composerDisabled}
                autoFocus={!isMobile}
                // rows={1} is the whole fix. A textarea with no rows attribute
                // defaults to TWO, so it rested ~62px tall while ADA's input sat
                // at 41px — and minHeight is a floor, which is why setting it to
                // 40 last pass changed nothing. Growth still comes from
                // maxHeight:120px.
                rows={1}
                style={{...S.input,background:'transparent',border:'none',padding:'10px 0',flex:1,minWidth:0,maxHeight:'120px',resize:'none',lineHeight:1.5,fontFamily:FONT_BODY}}
              />
            ) : (
              <input
                value={composerValue}
                onChange={onComposerChange}
                onKeyDown={e=>{ if (e.key==='Enter') onSubmit() }}
                placeholder={composerPlaceholder}
                disabled={composerDisabled}
                autoFocus={!isMobile}
                // No minHeight: with rows={1} on the textarea both variants now
                // rest at their natural single-line height. Still an <input> —
                // a textarea here would change what Enter means.
                style={{...S.input,background:'transparent',border:'none',padding:'10px 0',flex:1,minWidth:0}}
              />
            )}
            {/* A SIBLING of the send button, not a child. That button is a 30px
                circle sized to its 14px icon; anything inside it would either
                displace the arrow or burst the circle. Sits in the same flex
                row and takes the row's 10px gap. */}
            {composerBadge}
            <button
              onClick={()=>onSubmit()}
              disabled={sendDisabled}
              aria-label="Send"
              style={{flexShrink:0,width:'30px',height:'30px',borderRadius:'50%',background:'#1d2e42',border:'none',cursor:sendDisabled?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                      opacity:sendDisabled ? 0.45 : 1}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
              </svg>
            </button>
          </div>
        </div>
        )}

        {footer && <div style={{flexShrink:0}}>{footer}</div>}
          </div>

          {/* A frame, not a scroller. overflow:hidden plus a flex column lets
              the PANEL decide what scrolls inside it — a panel that wants a
              fixed header over a scrolling body cannot get one if this
              container is the thing that scrolls. Whatever it scrolls is still
              independent of the transcript: reading a comp must not move the
              answer. */}
          {/* MOUNTED WHENEVER THERE IS A PANEL, on every screen size. Both ways
              of putting it away — the narrow-screen segmented control and the
              desktop collapse chevron — are display:'none' on the container
              below, never a mount gate.
              This comment used to claim exactly that and was only half true:
              the display expression handled the narrow case while
              `(isMobile || !panelCollapsed)` sat on the gate here, so a desktop
              collapse unmounted the whole subtree. The panel scrolls internally
              — a flex:1 minHeight:0 overflowY:auto region around the comp cards
              — that scrollTop lives only in the DOM node, nothing holds a ref to
              it and nothing restores it, so unmounting silently sent an agent
              back to the top of the list every time they collapsed and reopened.
              Same reasoning as the scroller above, now applied to both branches
              rather than one.
              A collapsed case that is then narrowed is still safe: the strip is
              desktop-only, but the display expression only hides on desktop when
              panelCollapsed, so below the breakpoint the segmented control alone
              decides, exactly as before. */}
          {/* flex on mobile only. flexShrink:0 with no flex-grow means this
              never asks the row for vertical space — on desktop it borrows a
              height from align-items:stretch and that is enough. Below the
              breakpoint the sibling scroller is display:none, so it is not a
              flex item at all, and stretch yielded nothing for this to
              inherit: the panel rendered with no height, no cards and nothing
              to scroll. Flex here makes it claim the row's height directly
              rather than depending on a sibling being present.
              The basis is 0, not auto. With auto the panel's height starts
              from its content — a fixed top block over a scrolling body — so
              it sized to the top block and the body was left with nothing,
              which is the same empty panel by a different route. A basis of 0
              takes the row's height first and hands the remainder to the body.
              Desktop stays undefined, which React omits — the style object
              above the breakpoint is byte-for-byte what it was. */}
          {panel && (
            <div style={{width:(isMobile || panelWide) ? '100%' : '400px',flex:isMobile ? '1 1 0' : undefined,flexShrink:0,minHeight:0,overflow:'hidden',display:((isMobile && narrowView !== 'panel') || (!isMobile && panelCollapsed)) ? 'none' : 'flex',flexDirection:'column',borderLeft:(isMobile || panelWide) ? 'none' : '0.5px solid #e8e8e8'}}>
              {/* Two-column furniture. There is nothing to collapse toward when
                  only one column shows, and the segmented control above already
                  carries the label. */}
              {!isMobile && (
                <div style={{flexShrink:0,height:'44px',display:'flex',alignItems:'center',gap:'8px',padding:'0 8px 0 16px',borderBottom:'0.5px solid #e8e8e8'}}>
                  {/* FIRST, and with no margin of its own. The link below pins
                      itself right with marginLeft:auto, and two auto margins in
                      one flex row split the free space between them — a marker
                      carrying its own would strand that link mid-header. The row
                      is a fixed 44px and centres its children, so 19px fits. */}
                  {panelStep}
                  <span style={{fontSize:'12.5px',color:'#1d2e42',fontWeight:500,fontFamily:FONT_BODY}}>{panelLabel}</span>
                  {/* Both props required — a link with no handler is furniture.
                      marginLeft:auto here rather than on the chevron, so the
                      link sits hard right when the chevron is hidden. */}
                  {panelAction && onPanelAction && (
                    <button
                      onClick={onPanelAction}
                      onMouseEnter={()=>setHoverKey('panelAction')}
                      onMouseLeave={()=>setHoverKey(null)}
                      style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:'6px',
                        border:'0.5px solid #147efb',
                        background:hoverKey === 'panelAction' ? '#f4f4f2' : '#fff',
                        color:'#147efb',fontSize:'12px',fontWeight:500,fontFamily:'inherit',
                        padding:'4px 11px',borderRadius:'18px',cursor:'pointer',transition:'background .15s'}}>
                      {/* currentColor, so the glyph follows the button's text
                          colour rather than restating the hex. */}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="3" width="7" height="7"/>
                        <rect x="14" y="3" width="7" height="7"/>
                        <rect x="3" y="14" width="7" height="7"/>
                        <rect x="14" y="14" width="7" height="7"/>
                      </svg>
                      {panelAction}
                    </button>
                  )}
                  {/* No collapse control while wide — there is nothing to
                      collapse toward, and the strip it collapses to is a
                      two-column affordance. Nor when the consumer says the
                      panel is not collapsible, which is how a reserved-but-
                      empty column avoids offering to fold itself away. */}
                  {!panelWide && panelCollapsible && (
                  <button
                    onClick={()=>setPanelCollapsed(true)}
                    aria-label="Collapse comps"
                    // marginLeft:auto only when the link is absent. Two auto
                    // margins in one flex row split the free space between
                    // them, which would strand the link mid-header.
                    style={{marginLeft:(panelAction && onPanelAction) ? 0 : 'auto',width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',background:'none',border:'none',padding:0,cursor:'pointer',color:'#666'}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                  )}
                </div>
              )}
              {/* The panel keeps its own flex:1 / minHeight:0, so whatever it
                  scrolls internally still scrolls under this header. */}
              {panel}
            </div>
          )}

          {/* Collapsed: a strip, not a hidden panel. The comps are still there
              and still one click away, and the report gets the width back. The
              whole strip is the target rather than a 28px icon inside it.
              Desktop only — a 40px sliver taking width from a phone screen buys
              nothing the segmented control does not already give. */}
          {!isMobile && panel && panelCollapsed && (
            <div
              onClick={()=>setPanelCollapsed(false)}
              role="button"
              tabIndex={0}
              onKeyDown={e=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPanelCollapsed(false) } }}
              aria-label="Expand comps"
              style={{width:'40px',flexShrink:0,borderLeft:'0.5px solid #e8e8e8',background:'#f4f4f2',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',padding:'14px 0'}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              <span style={{writingMode:'vertical-rl',fontSize:'12px',color:'#1d2e42',fontFamily:FONT_BODY,marginTop:'12px'}}>{panelLabel}</span>
            </div>
          )}

        </div>

    </div>
  )
})

// ─── TRANSCRIPT MARKDOWN ────────────────────────────────────────────────────
// Hoisted from BinsrCaseView. StrategyCaseView carries a second copy that
// differs only in local variable names, statement formatting, and the operand
// order of one `&&` — the two were diffed and produce identical output. Both
// still call their own private copy; switching them over is a separate change
// so this pass cannot regress two working views.
function renderCaseInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>
    return part
  })
}

// Walks a list run, stepping OVER blank lines between items instead of stopping
// at the first one.
//
// This is the numbering defect, and it is a document defect rather than a
// cosmetic one. Stopping at a blank line closed the list and opened a new one
// for the next item, and every <ol> restarts its counter at 1 — so a drafted
// addendum, whose paragraphs are always blank-line separated, rendered as
// paragraph 1, paragraph 1, paragraph 1, and an agent copying it into a form
// carried that into escrow. It fired regardless of what the model emitted: even
// correctly numbered 1, 2, 3 input rendered 1, 1, 1.
//
// Blanks are only consumed when the list actually resumes after them, so a list
// followed by a paragraph still ends where it should. This matches CommonMark,
// where a blank line makes a list "loose" but does not terminate it.
//
// `loose` reports whether any blank line was stepped over, so the caller can
// give the items paragraph spacing rather than list spacing. Spacing is
// cosmetic; sequence is not.
function collectListItems(lines, start, matches, strip) {
  const values = []
  let idx = start
  let loose = false
  while (idx < lines.length) {
    const t = lines[idx].trim()
    if (matches(t)) { values.push(strip(t)); idx++; continue }
    if (t === '') {
      let peek = idx
      while (peek < lines.length && lines[peek].trim() === '') peek++
      if (peek < lines.length && matches(lines[peek].trim())) { loose = true; idx = peek; continue }
    }
    break
  }
  return { values, next: idx, loose }
}

function renderCaseMarkdown(text) {
  if (!text) return null
  const lines = text.split('\n')
  const elements = []
  let idx = 0
  while (idx < lines.length) {
    const trimmed = lines[idx].trim()
    if (trimmed === '') { idx++; continue }
    if (/^-{3,}$/.test(trimmed)) { elements.push(<hr key={idx} style={{border:'none',borderTop:'1px solid #e8e8e8',margin:'16px 0'}} />); idx++; continue }
    if (trimmed.startsWith('### ')) { elements.push(<div key={idx} style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',margin:'12px 0 6px',fontFamily:FONT_HEAD}}>{renderCaseInline(trimmed.slice(4))}</div>); idx++; continue }
    if (trimmed.startsWith('## ')) { elements.push(<div key={idx} style={{fontSize:'18px',fontWeight:700,color:'#1d2e42',margin:'14px 0 8px',fontFamily:FONT_HEAD}}>{renderCaseInline(trimmed.slice(3))}</div>); idx++; continue }
    if (trimmed.startsWith('# ')) { elements.push(<div key={idx} style={{fontSize:'24px',fontWeight:700,color:'#1d2e42',margin:'16px 0 10px',fontFamily:FONT_HEAD}}>{renderCaseInline(trimmed.slice(2))}</div>); idx++; continue }
    if (trimmed.startsWith('|') && trimmed.includes('|')) {
      const tableRows = []
      while (idx < lines.length && lines[idx].trim().startsWith('|')) {
        const row = lines[idx].trim()
        if (!/^[\s|:-]+$/.test(row)) { tableRows.push(row.split('|').filter(c => c.trim() !== '').map(c => c.trim())) }
        idx++
      }
      if (tableRows.length > 0) {
        elements.push(<div key={'tbl'+idx} style={{overflowX:'auto',WebkitOverflowScrolling:'touch',marginTop:'8px',marginBottom:'12px',maxWidth:'100%'}}><table style={{borderCollapse:'collapse',minWidth:'100%',fontSize:'13px',fontFamily:FONT_BODY}}><thead><tr>{tableRows[0].map((cell,ci) => <th key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px',background:'#f5f5f5',fontWeight:700,textAlign:'left',whiteSpace:'nowrap'}}>{renderCaseInline(cell)}</th>)}</tr></thead><tbody>{tableRows.slice(1).map((row,ri) => <tr key={ri}>{row.map((cell,ci) => <td key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px'}}>{renderCaseInline(cell)}</td>)}</tr>)}</tbody></table></div>)
      }
      continue
    }
    if (trimmed.startsWith('- ')) {
      const items = collectListItems(lines, idx, l => l.startsWith('- '), l => l.slice(2))
      idx = items.next
      elements.push(<ul key={'ul'+idx} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderCaseInline(item)}</li>)}</ul>)
      continue
    }
    if (/^\d+\.\s/.test(trimmed)) {
      const items = collectListItems(lines, idx, l => /^\d+\.\s/.test(l), l => l.replace(/^\d+\.\s/, ''))
      idx = items.next
      elements.push(<ol key={'ol'+idx} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderCaseInline(item)}</li>)}</ol>)
      continue
    }
    let para = trimmed; idx++
    while (idx < lines.length && lines[idx].trim() !== '' && !lines[idx].trim().startsWith('#') && !lines[idx].trim().startsWith('- ') && !/^\d+\.\s/.test(lines[idx].trim()) && !lines[idx].trim().startsWith('|') && !/^-{3,}$/.test(lines[idx].trim())) { para += ' ' + lines[idx].trim(); idx++ }
    elements.push(<p key={'p'+idx} style={{margin:'8px 0',lineHeight:1.65}}>{renderCaseInline(para)}</p>)
  }
  return elements
}

// Hoisted verbatim from BinsrCaseView so Deal Desk's copy path and BINSR's
// Export button share one definition. Nothing about BINSR's behaviour changes:
// its call site still reads stripMarkdown(raw) and now resolves here.
//
// Note the HR rule below is looser than renderCaseMarkdown's — it accepts
// `* * *` and `_ _ _` as well as `---`. That is right for stripping, but wrong
// for deciding where a draft ends, which is why the separator scan uses the
// renderer's stricter rule instead of this one.
// singleNumberedList is BINSR-only and opt-in. It means exactly what it says:
// the numbered run NEVER resets, so the whole text is one sequence no matter
// what sits between items. On a BINSR the category labels group ONE list of
// items — a seller answering "Seller will correct items 1, 3 and 7" has to land
// on exactly one item, and two item 1s make that ambiguous.
//
// This started as a heading-only exemption, which was wrong in practice. The
// exemption keyed off the "## " marker, and the model writes those labels
// however it likes — bold, plain, all-caps with a colon. Four of the five
// plausible formats never engaged it, so the numbering restarted anyway. The
// prompt now asks for headings AND this no longer depends on getting them.
//
// Never-reset is safe HERE and would not be elsewhere: BINSR's SECTION 1 is a
// single item list with no prose between items — an intro line, then labels and
// items, then the credit line at the end. Deal Desk and Strategy pass nothing
// and keep restarting, which is right for output that genuinely has separate
// lists. Do not switch them on to this without re-reading that sentence.
function stripMarkdown(text, { singleNumberedList = false } = {}) {
  if (!text) return ''
  let out = text
  // Decode common HTML entities
  out = out.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ')
  // Process line-by-line for line-level constructs
  const lines = out.split('\n')
  const result = []
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i]
    // Horizontal rules — drop the line
    if (/^\s*([-*_]\s*){3,}\s*$/.test(line)) continue
    // Table divider rows like |---|---|
    if (/^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line)) continue
    // Table rows — convert pipes to " — "
    if (/^\s*\|.+\|\s*$/.test(line)) {
      line = line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim()).filter(Boolean).join(' — ')
    }
    // A line of nothing but hashes — drop it. The rule below deliberately
    // requires whitespace after the hashes, because "#Heading" with no space is
    // not a heading and should pass through as written. That leaves a bare "##"
    // matching nothing at all: not this rule, not the horizontal rule above
    // (which is [-*_] only), not a table row. It reached the clipboard intact.
    // Handled separately rather than by loosening the rule below, which would
    // start eating the "#" off "#Heading".
    if (/^\s*#{1,6}\s*$/.test(line)) continue
    // Headings — strip leading hashes
    line = line.replace(/^\s*#{1,6}\s+/, '')
    // Blockquotes — strip leading > markers
    line = line.replace(/^\s*>+\s?/, '')
    result.push(line)
  }
  // Renumber ordered lists, same rule collectListItems applies on screen:
  // discard whatever the model emitted and re-derive sequentially, treating a
  // run of blank lines as continuing the list.
  //
  // This path never touches the renderer — the button copies m.content, the
  // raw model text — so fixing the display alone left the worse of the two
  // states: the screen showing 1, 2, 3 while the clipboard pasted 1, 1, 1,
  // with nothing on screen to warn anyone. This is one click from a form.
  //
  // Runs after the line pass above, not inside it, because stripping a
  // heading or a blockquote marker can reveal a numbered item that was not
  // visible as one before.
  const renumbered = []
  let counter = 0
  let heldBlanks = 0
  for (const line of result) {
    const m = line.match(/^(\s*)\d+\.(\s.*)$/)
    if (m) {
      counter += 1
      while (heldBlanks > 0) { renumbered.push(''); heldBlanks-- }
      renumbered.push(`${m[1]}${counter}.${m[2]}`)
      continue
    }
    // Blanks are held rather than emitted, so they can either sit inside the
    // list or close it depending on what turns up next. Either way they are
    // replayed verbatim — spacing in copied contract text is not ours to edit.
    if (line.trim() === '') { heldBlanks++; continue }
    while (heldBlanks > 0) { renumbered.push(''); heldBlanks-- }
    // Under singleNumberedList nothing ends the run — not a label, not prose.
    if (!singleNumberedList) counter = 0
    renumbered.push(line)
  }
  while (heldBlanks > 0) { renumbered.push(''); heldBlanks-- }
  out = renumbered.join('\n')
  // Inline emphasis — strip ** __ * _ markers but keep content
  out = out.replace(/\*\*(.+?)\*\*/g, '$1')
  out = out.replace(/__(.+?)__/g, '$1')
  out = out.replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1$2')
  out = out.replace(/(^|[^_])_(?!\s)([^_\n]+?)_(?!_)/g, '$1$2')
  // Inline code backticks
  out = out.replace(/`([^`]+)`/g, '$1')
  // Collapse 3+ newlines to 2
  out = out.replace(/\n{3,}/g, '\n\n')
  return out.trim()
}

// ─── SETUP PANEL CARDS ──────────────────────────────────────────────────────
// The card vocabulary for a case's setup panel. Shared rather than inlined so
// the views that adopt it cannot drift on padding, radius, or tint. Icons are
// passed in and inherit `color` from the tinted square, so an icon drawn with
// stroke="currentColor" needs no colour prop of its own.
// titleIsControl drops the nowrap/ellipsis from the title slot. Right for a
// filename, wrong for a form control — an <input> in that slot gets clipped.
// Default is false, so Deal Desk's cards are unchanged.
// variant 'document' tints the icon square blue, so a
// filled slot in Strategy and a file row in Deal Desk read as the same object.
// Strategy needs it because its three zones are fixed slots that must render
// when empty.
// `gold` is the frame only, and it is deliberately separate from `variant`.
// variant is one string, so 'accent' and 'document' are mutually exclusive — a
// filled document slot could not be both blue-iconed and gold-framed. gold
// carries the bg and border; accent/document_ keep owning the icon square and
// the subtitle colour, so turning it on moves nothing else.
function SetupCard({ icon, title, subtitle, action, variant = 'default', titleIsControl = false, gold = false, blue = false }) {
  const accent = variant === 'accent'
  const document_ = variant === 'document'
  const goldFrame = accent || gold
  // Colours only, from SETUP_CARD_BLUE. Its padding (20px) and radius (16px)
  // belong to a create card and would make this one visibly larger than the
  // cards stacked either side of it, so the frame is borrowed and the geometry
  // is not. gold wins if both are somehow passed — no caller does.
  return (
    <div style={{
      display:'flex',alignItems:'center',gap:'11px',
      background:goldFrame ? '#fffdf5' : blue ? SETUP_CARD_BLUE.background : '#ffffff',
      border:goldFrame ? '1px solid #fdbf00' : blue ? SETUP_CARD_BLUE.border : `0.5px solid ${RAIL_BORDER}`,
      borderRadius:'12px',padding:'11px 13px',minWidth:0,
    }}>
      <div style={{
        width:'32px',height:'32px',borderRadius:'9px',flexShrink:0,
        display:'flex',alignItems:'center',justifyContent:'center',
        background:accent ? 'rgba(253,191,0,0.18)' : document_ ? '#eaf4ff' : '#f4f4f2',
        color:accent ? '#8a6800' : document_ ? '#147efb' : '#1d2e42',
      }}>
        {icon}
      </div>
      <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:'1px'}}>
        <div style={titleIsControl
          ? {fontSize:'13px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,minWidth:0}
          : {fontSize:'13px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{title}</div>
        {subtitle && (
          <div style={{fontSize:'11.5px',color:accent ? '#8a7340' : '#999',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{subtitle}</div>
        )}
      </div>
      {action && <div style={{flexShrink:0}}>{action}</div>}
    </div>
  )
}

// Same shell as SetupCard's default variant, with the icon and the remove
// affordance fixed — a document row is always a document row.

// Rail-style inline SVG — 24 viewBox, 2px stroke, currentColor so the card's
// tinted square owns the colour.

function MapPinIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}



function AssistantWorkspace({ session, profile, showToast, isMobile, navigateTo }) {
  const [input, setInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const composerRef = useRef(null)

  // The Strategist card reads Strategy cases only, so it queries separately
  // rather than filtering the merged rail list — which is capped at 8 across all
  // three tools and could hold no Strategy rows at all. count:'exact' rides
  // along on the same request so "All N cases" reports the real total, not the
  // six that came back.
  const [stratCases, setStratCases] = useState([])
  const [stratTotal, setStratTotal] = useState(0)
  const [loadingStrat, setLoadingStrat] = useState(true)

  useEffect(() => {
    let cancelled = false
    supabase.from('property_cases')
      .select('id, subject_address, updated_at', { count: 'exact' })
      .eq('agent_id', session.user.id)
      .order('updated_at', { ascending: false })
      .limit(6)
      .then(({ data, count, error }) => {
        if (cancelled) return
        if (error) console.error('assistant strategy card:', error)
        setStratCases(data || [])
        setStratTotal(count ?? (data || []).length)
        setLoadingStrat(false)
      })
    return () => { cancelled = true }
  }, [session.user.id])

  // The composer is ADA's. Every submission opens a law conversation — no
  // guessing at intent from the wording. The row is created here so the view it
  // lands on already has an id, then the text rides along as the first question.
  async function submit() {
    const text = input.trim()
    if (text.length < 5 || submitting) return
    setSubmitting(true)
    const { data, error } = await supabase.from('ada_cases')
      .insert({ user_id: session.user.id, title: text.slice(0, 80) }).select().single()
    if (error) { showToast('Could not start a conversation'); setSubmitting(false); return }
    setSubmitting(false)
    setInput('')
    navigateTo('ada', data.id, { adaPrefill: text })
  }

  // Chips are sample questions, not launchers — they fill the composer so the
  // agent can edit before sending.
  function fillComposer(text) {
    setInput(text)
    if (composerRef.current) composerRef.current.focus()
  }

  const disabled = submitting || input.trim().length < 5

  // Card typography matches the rail's section label, minus its horizontal
  // padding — the card supplies its own.
  const cardLabel = { fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD }
  const goldBtn = { background:'#fdbf00',color:'#1d2e42',border:'none',borderRadius:'8px',padding:'9px 16px',fontSize:'12px',fontWeight:700,letterSpacing:'0.5px',fontFamily:FONT_HEAD,cursor:'pointer',flexShrink:0 }
  const cardShell = { width:'100%',maxWidth:'480px',margin:'0 auto',background:'#ffffff',border:'none',borderRadius:'12px',padding:'16px 18px',animation:'stratPulse 2.4s ease-in-out infinite' }
  const linkBtn = { background:'none',border:'none',padding:0,cursor:'pointer',color:'#147efb',fontSize:'12px',fontFamily:FONT_BODY }

  const newestCase = stratCases[0]

  const cardHeader = (
    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
      <StrategistIcon size={15} color="#147efb" />
      <div style={cardLabel}>THE STRATEGIST</div>
    </div>
  )

  return (
    // The landing screen only. The rail and the drawer moved up to Dashboard,
    // which also supplies the column this sits in. minHeight:0 so the scroll
    // pane below is bounded by that column rather than by its own content.
    <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',background:'#ffffff',minHeight:0}}>

        {/* Centred by the child's auto margins, NOT by justify-content. The two
            are not interchangeable on a scroll container.
            //
            justify-content:center splits overflow across both ends, and only the
            end edge is scrollable — the browser never creates scroll range before
            the start edge. So once the content outgrew a short window, the top of
            "Ask ADA" was both clipped and unreachable: scrolling up did nothing.
            //
            Auto margins behave differently by spec. With positive free space they
            absorb it and centre the child; with negative free space they resolve
            to zero, the child falls to flex-start, and the whole of it is inside
            the scroll range. Centred when it fits, top-aligned and reachable when
            it does not.
            //
            justify-content had to come OFF rather than stay alongside them: auto
            margins only override it while free space is positive, so leaving
            center would have re-centred the overflow and kept the bug on exactly
            the short window this fixes.
            //
            The desktop paddingTop is a buffer so the heading's ascenders never
            sit flush against the edge. Mobile keeps 24px and its own top
            alignment, and its margin stays 0 so it does not start centring. */}
        <div style={{flex:1,minHeight:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',padding:isMobile?'24px 20px 48px':'40px 20px 80px',overflowY:'auto'}}>
          <div style={{width:'100%',maxWidth:'640px',margin:isMobile?0:'auto 0'}}>
            <div style={{fontSize:isMobile?'26px':'34px',fontWeight:900,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'1px',lineHeight:1.1,marginBottom:'8px'}}>
              Ask Ada
            </div>
            <div style={{fontSize:'15px',color:'#555',fontFamily:FONT_BODY,marginBottom:'6px'}}>
              Arizona real estate law, ADRE rules, and fair housing.
            </div>
            <div style={{fontSize:'14px',color:'#555',fontFamily:FONT_BODY,marginBottom:'22px'}}>
              Not knowing the rule has never been a defense. It's a liability.
            </div>

            {/* COMPOSER — the dominant element on this screen. The input sits on
                its own line above the send row so it reads as a place to write,
                not a single-line field squeezed against a button. */}
            <div style={{width:'100%',display:'flex',flexDirection:'column',background:'white',border:`1px solid ${RAIL_BORDER}`,borderRadius:'14px',padding:'10px 10px 8px 14px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
              <input
                ref={composerRef}
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{ if (e.key==='Enter') submit() }}
                placeholder="Ask anything about Arizona real estate law"
                autoFocus={!isMobile}
                style={{...S.input,background:'transparent',border:'none',borderRadius:0,padding:0,height:'44px',fontSize:'15px',width:'100%'}}
              />
              {/* flex-end, not space-between: the send button is the only child
                  now, and space-between would leave it hanging on the left. */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end'}}>
                <button
                  onClick={submit}
                  disabled={disabled}
                  aria-label="Send"
                  style={{flexShrink:0,width:'38px',height:'38px',borderRadius:'50%',background:'#1d2e42',border:'none',cursor:disabled?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',
                          opacity:disabled ? 0.45 : 1}}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* SAMPLE QUESTIONS — fill the composer, never submit on their own */}
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap',marginTop:'14px'}}>
              <button onClick={()=>fillComposer('Can I pay a referral fee to an unlicensed person?')}
                style={{...S.chip,cursor:'pointer',boxShadow:'none',fontSize:'12px',padding:'7px 13px',borderColor:RAIL_BORDER}}>
                Can I pay a referral fee to an unlicensed person?
              </button>
              <button onClick={()=>fillComposer("Must my broker's name appear in my social posts?")}
                style={{...S.chip,cursor:'pointer',boxShadow:'none',fontSize:'12px',padding:'7px 13px',borderColor:RAIL_BORDER}}>
                Must my broker's name appear in my social posts?
              </button>
            </div>

            {/* THE STRATEGIST — three states over the agent's own Strategy cases.
                Nothing renders until the query settles, so an agent with cases
                never sees the empty state flash first. */}
            {!loadingStrat && (
              <div style={{marginTop:'28px'}}>
                {stratCases.length === 0 ? (
                  <div style={cardShell}>
                    {cardHeader}
                    <div style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px',marginBottom:'4px'}}>
                      Price your first listing
                    </div>
                    <div style={{fontSize:'13px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.5,marginBottom:'14px'}}>
                      Live MLS comps, a pricing strategy, and a branded seller report in English or Spanish.
                    </div>
                    <button onClick={()=>navigateTo('strategy')} style={goldBtn}>Start a case</button>
                  </div>
                ) : (
                  <div style={cardShell}>
                    {cardHeader}
                    <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:'14px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {newestCase.subject_address || 'Untitled Property'}
                        </div>
                        <div style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,marginTop:'3px'}}>
                          Last worked {timeAgo(newestCase.updated_at)}
                        </div>
                      </div>
                      <button onClick={()=>navigateTo('strategy', newestCase.id)} style={goldBtn}>Continue</button>
                    </div>

                    {stratTotal > 1 && (
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'14px',paddingTop:'12px',borderTop:'1px solid #f0f0f0'}}>
                        <button onClick={()=>navigateTo('strategy')} style={linkBtn}>All {stratTotal} cases</button>
                        <button onClick={()=>navigateTo('strategy')} style={linkBtn}>New case</button>
                      </div>
                    )}
                  </div>
                )}

                {/* BINSR — a line, not a card. Sits outside so the pulse stays
                    on the Strategist alone. */}
                <div
                  onClick={()=>navigateTo('binsr')}
                  style={{maxWidth:'480px',margin:'12px auto 0',fontSize:'12px',fontFamily:FONT_BODY,cursor:'pointer',textAlign:'center'}}
                >
                  <span style={{color:'#147efb'}}>Inspection desk</span>
                  <span style={{color:'#999'}}> — draft BINSR responses that hold up.</span>
                </div>

                {/* The two hero cards from the deleted Hub page, at roughly half
                    their old height. heroPulse is gone rather than the inline
                    shadows: the colors are what distinguish these two cards, and
                    a third pulsing element under the Strategist above reads as
                    noise. The LIVE dot's `pulse` keyframe was only ever defined
                    inside loginCSS, so it never animated here — it is in the
                    Dashboard style block now. */}
                <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'12px',marginTop:'18px'}}>
                  <div onClick={()=>navigateTo('strategy')} style={{
                    background:'linear-gradient(135deg, #1d2e42 0%, #2a4060 100%)',border:'1px solid #fdbf00',
                    borderRadius:'12px',padding:'14px 16px',cursor:'pointer',position:'relative',
                    boxShadow:'0 8px 32px rgba(226,55,68,0.28), 0 0 60px rgba(226,55,68,0.12)',overflow:'hidden'
                  }}>
                    {!isMobile && (
                      <div style={{position:'absolute', top:0, right:0, bottom:0, width:'50%', pointerEvents:'none', overflow:'hidden'}}>
                        <div style={{
                          position:'absolute', top:'42%', right:'-50px', transform:'translateY(-50%)',
                          width:'190px', height:'190px', borderRadius:'50%',
                          background:'radial-gradient(circle, rgba(226,55,68,0.30) 0%, rgba(226,55,68,0) 70%)'
                        }}></div>
                        <div style={{position:'absolute', top:'12px', right:'14px', display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                          <div style={{display:'flex', alignItems:'flex-end', gap:'2px', height:'14px', marginBottom:'4px'}}>
                            {[ {h:'55%',d:'0s'},{h:'90%',d:'.13s'},{h:'42%',d:'.26s'},{h:'100%',d:'.39s'},{h:'68%',d:'.52s'},{h:'88%',d:'.65s'},{h:'50%',d:'.78s'} ].map((b,i)=>(
                              <span key={i} style={{
                                width:'3px', height:b.h, borderRadius:'2px',
                                background:'linear-gradient(#ff5c69, #e23744)',
                                transformOrigin:'bottom', display:'inline-block',
                                animation:`bar 1.1s ease-in-out infinite`, animationDelay:b.d
                              }}></span>
                            ))}
                          </div>
                          <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                            <span style={{
                              width:'8px', height:'8px', borderRadius:'50%', background:'#ff3b3b',
                              boxShadow:'0 0 9px #ff3b3b', animation:'pulse 1.3s ease-in-out infinite', display:'inline-block'
                            }}></span>
                            <span style={{
                              fontSize:'26px', fontWeight:700, lineHeight:'0.82', color:'#ff4d4d',
                              letterSpacing:'0.5px', fontFamily:FONT_HEAD, textShadow:'0 0 14px rgba(255,59,59,0.55)'
                            }}>LIVE</span>
                          </div>
                          <span style={{fontSize:'8px', fontWeight:700, letterSpacing:'2.5px', color:'#ff8a90', fontFamily:FONT_HEAD, marginTop:'1px'}}>MLS DATA</span>
                        </div>
                      </div>
                    )}
                    <div style={{fontSize:'9px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',marginBottom:'5px',fontFamily:FONT_HEAD}}>AI POWERED</div>
                    <div style={{fontSize:isMobile?'16px':'18px',fontWeight:700,lineHeight:1.1,color:'white',marginBottom:'5px',fontFamily:FONT_HEAD}}>Strategy Assistant</div>
                    <div style={{fontSize:'11px',lineHeight:1.4,color:'#cbd5e1',marginBottom:'10px',fontFamily:FONT_BODY}}>
                      Comp analysis, pricing, and offer strategy.
                    </div>
                    <button onClick={e=>{e.stopPropagation();navigateTo('strategy')}} style={{background:'#fdbf00',color:'#1d2e42',fontFamily:FONT_HEAD,padding:'7px 14px',fontSize:'11px',fontWeight:700,letterSpacing:'0.5px',borderRadius:'7px',border:'none',cursor:'pointer'}}>Open →</button>
                  </div>
                  <div onClick={()=>navigateTo('binsr')} style={{
                    background:'linear-gradient(135deg, #1d2e42 0%, #2a4060 100%)',border:'1px solid #fdbf00',
                    borderRadius:'12px',padding:'14px 16px',cursor:'pointer',
                    boxShadow:'0 8px 24px rgba(20,126,251,0.15)',overflow:'hidden'
                  }}>
                    <div style={{fontSize:'9px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',marginBottom:'5px',fontFamily:FONT_HEAD}}>AI POWERED</div>
                    <div style={{fontSize:isMobile?'16px':'18px',fontWeight:700,lineHeight:1.1,color:'white',marginBottom:'5px',fontFamily:FONT_HEAD}}>BINSR Drafting</div>
                    <div style={{fontSize:'11px',lineHeight:1.4,color:'#cbd5e1',marginBottom:'10px',fontFamily:FONT_BODY}}>
                      Inspection findings → broker-defensible language.
                    </div>
                    <button onClick={e=>{e.stopPropagation();navigateTo('binsr')}} style={{background:'#fdbf00',color:'#1d2e42',fontFamily:FONT_HEAD,padding:'7px 14px',fontSize:'11px',fontWeight:700,letterSpacing:'0.5px',borderRadius:'7px',border:'none',cursor:'pointer'}}>Open →</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
    </div>
  )
}

// ─── THINKING MARK ───────────────────────────────────────────────────────────
// The Avance ring at transcript scale, ported from AvanceBrandMoment.jsx in the
// avance-ai repo — same two counter-rotating conic gradients in blue and gold.
// Agents who take CE at Avance already know this mark; seeing it think inside
// the Hub connects the two products without a word.
//
// Three deliberate changes from the original, each forced by the context:
//
// The rings are a gradient BORDER over a solid center. The original fills that
// center with #060d1a because it sits on a dark brand screen; here the
// transcript is white, so the fill is white or the ring reads as a dark disc.
//
// The original spins at 24s and 36s, right for something glanced at while
// reading a page. Waiting for an answer, that reads as motionless. These run at
// 3s and 4.5s — still counter-rotating, still slow enough not to nag.
//
// The two sonar rings are NEW, not from Avance. Rotation alone reads as
// breathing rather than working; a pulse travelling outward reads as searching,
// which is literally what the tool loop is doing while it queries FUB. Offset
// by half the cycle so one is always mid-flight.
//
// Sized in px rather than em so it cannot inherit a surprising scale from
// whatever line it sits on.
function ThinkingMark({ size = 24 }) {
  const inner = Math.round(size * 0.6)
  const dot = Math.max(4, Math.round(size * 0.17))
  const halo = Math.round(size * 1.7)

  const ring = {
    position:'absolute', top:'50%', left:'50%',
    transform:'translate(-50%,-50%)',
    width:size, height:size, borderRadius:'50%',
  }

  return (
    <span style={{display:'inline-block',width:size,height:size,position:'relative',verticalAlign:'middle',flexShrink:0}}>
      <style>{`
        @keyframes hpMarkRipple { 0% { transform: translate(-50%,-50%) scale(0.5); opacity: 0.55 } 100% { transform: translate(-50%,-50%) scale(1.9); opacity: 0 } }
        @keyframes hpMarkHalo { 0%,100% { transform: translate(-50%,-50%) scale(0.85); opacity: 0.5 } 50% { transform: translate(-50%,-50%) scale(1.15); opacity: 0.95 } }
        @keyframes hpMarkCw  { 0% { transform: translate(-50%,-50%) rotate(0deg) } 100% { transform: translate(-50%,-50%) rotate(360deg) } }
        @keyframes hpMarkCcw { 0% { transform: translate(-50%,-50%) rotate(0deg) } 100% { transform: translate(-50%,-50%) rotate(-360deg) } }
        @keyframes hpMarkBeat { 0%,100% { transform: translate(-50%,-50%) scale(0.7); opacity: 0.6 } 50% { transform: translate(-50%,-50%) scale(1.25); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) {
          .hp-mark-layer { animation: none !important }
        }
      `}</style>

      {/* Sonar — blue leads, gold follows half a cycle behind */}
      <span className="hp-mark-layer" style={{...ring,border:'1px solid rgba(20,126,251,0.5)',animation:'hpMarkRipple 2.4s ease-out infinite',zIndex:0}} />
      <span className="hp-mark-layer" style={{...ring,border:'1px solid rgba(253,191,0,0.5)',animation:'hpMarkRipple 2.4s ease-out infinite',animationDelay:'1.2s',zIndex:0}} />

      {/* Halo */}
      <span className="hp-mark-layer" style={{
        position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:halo,height:halo,borderRadius:'50%',
        background:'radial-gradient(circle, rgba(20,126,251,0.20) 0%, rgba(253,191,0,0.10) 45%, transparent 70%)',
        filter:'blur(5px)',
        animation:'hpMarkHalo 2.4s ease-in-out infinite',
        zIndex:1,
      }} />

      {/* Outer ring — clockwise */}
      <span className="hp-mark-layer" style={{
        ...ring,
        border:'1.5px solid transparent',
        background:'linear-gradient(#ffffff,#ffffff) padding-box, conic-gradient(from 0deg, #147efb, #fdbf00, #147efb, #fdbf00, #147efb) border-box',
        animation:'hpMarkCw 3s linear infinite',
        zIndex:2,
      }} />

      {/* Inner ring — counter-clockwise */}
      <span className="hp-mark-layer" style={{
        position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:inner,height:inner,borderRadius:'50%',
        border:'1.5px solid transparent',
        background:'linear-gradient(#ffffff,#ffffff) padding-box, conic-gradient(from 180deg, #fdbf00, #147efb, #fdbf00, #147efb, #fdbf00) border-box',
        animation:'hpMarkCcw 4.5s linear infinite',
        zIndex:3,
      }} />

      {/* Center light */}
      <span className="hp-mark-layer" style={{
        position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:dot,height:dot,borderRadius:'50%',
        background:'radial-gradient(circle, #ffffff 0%, #147efb 65%, transparent 100%)',
        boxShadow:'0 0 9px rgba(20,126,251,0.75)',
        animation:'hpMarkBeat 1.6s ease-in-out infinite',
        zIndex:4,
      }} />
    </span>
  )
}

// The full thinking row: mark, then the label. `label` differs per tool — BINSR
// drafts, Strategy analyzes, Pipeline looks things up — so it is a prop rather
// than a constant here.
function ThinkingRow({ label = 'Thinking…' }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:'10px'}}>
      <ThinkingMark />
      <span style={{fontSize:'14px',color:'#999',fontFamily:FONT_BODY}}>{label}</span>
    </span>
  )
}

// ─── PIPELINE CARDS ──────────────────────────────────────────────────────────
// pipeline-chat emits a fenced block after its prose describing what shape the
// answer is; the edge function parses it into a `meta` object on the message row
// and strips it from the text. These render from that.
//
// FILTERING HAPPENS HERE, NOT IN THE PROMPT. The model was asked to skip import
// artifacts and redundant stages and did not reliably do it — an instruction is
// a request, a filter is a guarantee. The model can emit whatever it likes; the
// card decides what earns space.
//
// Every field is optional. A meta with only a name still renders; a meta with
// nothing useful renders nothing at all and the prose stands alone, which is
// what happens today.

// Tags that record HOW a contact arrived rather than anything about them. These
// came in with the LionDesk-era migration and appear on tens of thousands of
// rows, so they would otherwise be on almost every card and mean nothing.
const PIPELINE_TAG_NOISE = /^(import\b|imported\b|source:|migrated\b)/i

function pipelineTags(raw, subtitle) {
  if (!raw) return []
  const sub = String(subtitle || '').toLowerCase()
  const list = Array.isArray(raw) ? raw : String(raw).split(',')
  return list
    .map(t => String(t).trim())
    .filter(Boolean)
    .filter(t => !PIPELINE_TAG_NOISE.test(t))
    .filter(t => !(sub && sub.includes(t.toLowerCase())))
    .slice(0, 4)
}

// Chip colours borrow the rail's tool tiles, so a tag on a card and a tool in
// the sidebar read as the same system. Assignment is by hash rather than a
// lookup table: the tag vocabulary is the agent's, not ours, and a table would
// leave every unrecognised tag grey.
const PIPELINE_CHIP_TONES = [
  { bg:'#E4EDF7', fg:'#123763' },
  { bg:'#EFE6F7', fg:'#46186B' },
  { bg:'#FAEEDA', fg:'#633806' },
  { bg:'#DCEEEC', fg:'#0B4A44' },
  { bg:'#EAF3DE', fg:'#27500A' },
]
function pipelineChipTone(label) {
  let h = 0
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0
  return PIPELINE_CHIP_TONES[h % PIPELINE_CHIP_TONES.length]
}

function pipelineInitials(name) {
  return String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?'
}

// A stage that merely repeats the subtitle is noise. "Agent at West USA Realty"
// with a stage of "Agent" beside it says one thing twice, and the second time
// costs a line.
function pipelineStageWorthShowing(stage, subtitle) {
  if (!stage) return false
  const s = String(stage).trim().toLowerCase()
  if (!s) return false
  return !String(subtitle || '').toLowerCase().includes(s)
}

// Emails and phone numbers arrive inside the rendered markdown, so they cannot
// be matched before rendering — this walks the finished tree instead. A single
// capture group means split() alternates text, match, text, match, so the odd
// indices are the hits and no second regex test is needed.
//
// Colour only, not anchors: an agent reads these on a phone with the contact
// already open in Follow Up Boss, and a mailto: that hijacks the tap is worse
// than plain text they can select.
const PIPELINE_LINK_RE = /([\w.+-]+@[\w-]+\.[\w.-]+|(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/

function pipelineLinkify(node) {
  if (typeof node === 'string') {
    const parts = node.split(PIPELINE_LINK_RE)
    if (parts.length === 1) return node
    return parts.map((part, i) =>
      i % 2 === 1
        ? <span key={i} style={{color:'#147efb'}}>{part}</span>
        : part
    )
  }
  if (Array.isArray(node)) return Children.map(node, child => pipelineLinkify(child))
  if (isValidElement(node)) {
    if (node.props && node.props.children) {
      return cloneElement(node, { children: pipelineLinkify(node.props.children) })
    }
    return node
  }
  return node
}

function PipelineContactCard({ meta, children }) {
  const name = meta.name ? String(meta.name) : null
  const subtitle = meta.subtitle ? String(meta.subtitle) : null
  const assigned = meta.assigned ? String(meta.assigned) : null
  const signal = meta.signal ? String(meta.signal) : null
  const tags = pipelineTags(meta.tags, subtitle)
  const stage = pipelineStageWorthShowing(meta.stage, subtitle) ? String(meta.stage) : null

  // Nothing to frame — render the prose bare rather than an empty box.
  if (!name) return <>{children}</>

  const metaLine = [subtitle, stage, assigned ? `Assigned to ${assigned}` : null]
    .filter(Boolean).join(' · ')

  return (
    <div style={{background:'#fbfbfa',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'16px 18px'}}>

      <div style={{display:'flex',alignItems:'center',gap:'11px',marginBottom:'14px',minWidth:0}}>
        <div style={{width:'34px',height:'34px',borderRadius:'9px',background:'#DCEEEC',color:'#0B4A44',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:500,fontFamily:FONT_HEAD,letterSpacing:'0.3px',flexShrink:0}}>
          {pipelineInitials(name)}
        </div>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:'15px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,lineHeight:1.3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</div>
          {metaLine && (
            <div style={{fontSize:'12.5px',color:'#888',fontFamily:FONT_BODY,lineHeight:1.4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{metaLine}</div>
          )}
        </div>
        {tags.length > 0 && (
          <div style={{display:'flex',gap:'6px',flexShrink:0,flexWrap:'wrap',justifyContent:'flex-end'}}>
            {tags.map(t => {
              const tone = pipelineChipTone(t)
              return (
                <span key={t} style={{background:tone.bg,color:tone.fg,fontSize:'11px',padding:'3px 9px',borderRadius:'11px',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>{t}</span>
              )
            })}
          </div>
        )}
      </div>

      <div style={{fontSize:'14px',lineHeight:1.7,color:'#1d2e42',fontFamily:FONT_BODY}}>
        {pipelineLinkify(children)}
      </div>

      {/* Green, and only for engagement. A block on every contact would stop
          meaning anything — the point is that it marks the ones worth acting on. */}
      {signal && (
        <div style={{marginTop:'14px',background:'#EAF3DE',borderRadius:'10px',padding:'11px 13px',display:'flex',gap:'10px',alignItems:'flex-start'}}>
          <span style={{color:'#3B6D11',fontSize:'15px',lineHeight:1.3,flexShrink:0}} aria-hidden="true">◆</span>
          <div style={{fontSize:'13px',lineHeight:1.6,color:'#27500A',fontFamily:FONT_BODY}}>{signal}</div>
        </div>
      )}

    </div>
  )
}

// Rows, not cards. A list of twenty people as twenty cards is a scroll; as rows
// it is something an agent can scan down.
// timeAgo was written for things that already happened. A list row can carry a
// DUE date instead, and "just now" on a task due this afternoon is wrong — so
// only the past goes through timeAgo. Today is named, the future is dated, and
// anything that is not a date at all is left exactly as the model wrote it.
function pipelineRowDate(value) {
  // A bare YYYY-MM-DD is parsed as UTC midnight, which in Phoenix reads back as
  // 5pm the previous day — every due date would display one day early. Adding
  // the time with no zone suffix parses it as local midnight instead. A full
  // ISO timestamp carries its own zone and is left alone.
  const raw = String(value).trim()
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const then = startOfDay(parsed)
  const today = startOfDay(new Date())

  if (then === today) return 'Today'
  if (then > today) return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return timeAgo(parsed)
}

function PipelineListCard({ meta, children }) {
  const rows = meta.row ? (Array.isArray(meta.row) ? meta.row : [meta.row]) : []
  const total = meta.total ? String(meta.total) : null
  if (rows.length === 0) return <>{children}</>

  // "Everyone in Nurture" returns a column of identical Nurture chips, which
  // says nothing the question did not already say. Computed once rather than
  // per row. A single row is not a column, so its chip stays.
  const rowStages = rows.map(r => (String(r).split('|')[1] || '').trim())
  const stageIsUniform = rows.length > 1 && rowStages.every(st => st && st === rowStages[0])

  return (
    <div>
      <div style={{fontSize:'14px',lineHeight:1.7,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'12px'}}>
        {children}
      </div>
      <div style={{background:'#fbfbfa',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden'}}>
        {rows.map((r, i) => {
          // name | stage | last activity | assigned
          const parts = String(r).split('|').map(p => p.trim())
          const [rName, rStage, rActivity, rAssigned] = parts
          const tone = rStage ? pipelineChipTone(rStage) : null
          return (
            <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',borderTop:i === 0 ? 'none' : '0.5px solid #f0f0ee',minWidth:0}}>
              <div style={{fontSize:'13.5px',color:'#1d2e42',fontFamily:FONT_BODY,fontWeight:500,minWidth:0,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{rName}</div>
              {rStage && !stageIsUniform && (
                <span style={{background:tone.bg,color:tone.fg,fontSize:'11px',padding:'3px 9px',borderRadius:'11px',fontFamily:FONT_BODY,whiteSpace:'nowrap',flexShrink:0}}>{rStage}</span>
              )}
              {rActivity && (
                <span style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,whiteSpace:'nowrap',flexShrink:0}}>
                  {pipelineRowDate(rActivity)}
                </span>
              )}
              {rAssigned && (
                <span style={{fontSize:'12px',color:'#bbb',fontFamily:FONT_BODY,whiteSpace:'nowrap',flexShrink:0,maxWidth:'120px',overflow:'hidden',textOverflow:'ellipsis'}}>{rAssigned}</span>
              )}
            </div>
          )
        })}
      </div>
      {total && (
        <div style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,marginTop:'8px'}}>
          Showing {rows.length} of {total}
        </div>
      )}
    </div>
  )
}

// The one entry point. Unknown or absent type falls through to bare prose, which
// is what every answer looks like today — so a model that stops emitting blocks,
// or emits a shape we have not built, degrades to working rather than broken.
function PipelineAnswer({ meta, children, conversationId, showToast }) {
  const type = meta && typeof meta === 'object' ? String(meta.type || '').toLowerCase() : ''
  if (type === 'contact') return <PipelineContactCard meta={meta}>{children}</PipelineContactCard>
  if (type === 'list') return <PipelineListCard meta={meta}>{children}</PipelineListCard>
  // The proposal card takes no children — the prose is one line introducing the
  // draft, so it sits above the card rather than inside it.
  if (type === 'proposal') {
    return (
      <>
        {children}
        <div style={{marginTop:'12px'}}>
          <PipelineProposalCard meta={meta} conversationId={conversationId} showToast={showToast} />
        </div>
      </>
    )
  }
  return <>{children}</>
}

// ─── PIPELINE: PROPOSAL CARD ─────────────────────────────────────────────────
// The approval step. Pipeline never writes into Follow Up Boss on its own — the
// model calls propose_note or propose_task, which writes nothing, and this card
// is the only path from that proposal to an actual write.
//
// The agent reads the exact text before approving, not a summary of it. A note
// is a permanent client record and the model wrote the words, so the words are
// what gets shown.
//
// The person id comes from the tool input, captured server-side. If a search
// returned two people with the same name, the model is required to ask which
// one before proposing — so by the time a card renders, the target is settled.
// This card cannot change it.
// "2026-08-28" plus "17:00" reads as "Aug 28 at 5:00 PM". The date is parsed at
// local midnight rather than UTC, so the day does not slip back one in Phoenix,
// and anything unparseable is shown exactly as it arrived.
function pipelineDueLabel(dateStr, timeStr) {
  const parsed = new Date(`${dateStr}T${timeStr || '00:00'}:00`)
  if (Number.isNaN(parsed.getTime())) return timeStr ? `${dateStr} at ${timeStr}` : dateStr
  const day = parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (!timeStr) return day
  return `${day} at ${parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

function PipelineProposalCard({ meta, conversationId, showToast }) {
  const [writing, setWriting] = useState(false)
  const [written, setWritten] = useState(null)
  const [error, setError] = useState('')

  const kind = String(meta.kind || 'note')
  const name = meta.fub_person_name ? String(meta.fub_person_name) : 'this contact'
  const body = meta.body ? String(meta.body) : ''
  const dueDate = meta.due_date ? String(meta.due_date) : null
  const dueTime = meta.due_time ? String(meta.due_time) : null
  const personId = Number(meta.fub_person_id)

  // Whether this proposal was already approved is a fact in the database, not
  // in this component. The transcript refetches on every turn and this card
  // remounts with it, so without this the approve button returns on a note that
  // is already in Follow Up Boss — and tomorrow the 60-second dedupe in
  // pipeline-write has long expired, so the second click would write again.
  //
  // agent_id is filtered explicitly rather than left to RLS. The owner policy
  // would scope it, but admins can read every row, and during the pilot every
  // Pipeline user is an admin — without the filter one agent's identical note
  // would mark another's proposal as already written.
  //
  // How the match is made depends on the kind — see the comparison below.
  useEffect(() => {
    // Cleared first. Nothing below ever sets it false, so without this a match
    // found for an earlier proposal survives a dependency change and leaves the
    // card claiming a write that has not happened.
    setWritten(null)
    if (!personId || !body) return
    let cancelled = false
    ;(async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const uid = session?.user?.id
      if (!uid || cancelled) return
      const { data } = await supabase.from('pipeline_writes')
        .select('body, due_date, written_at')
        .eq('agent_id', uid)
        .eq('fub_person_id', personId)
        .eq('kind', kind)
        .order('written_at', { ascending: false })
        .limit(20)
      if (cancelled || !data) return
      // A note is stored with the "via HomePros Hub" stamp appended, so the
      // stored body is longer than the proposal and only a prefix can match. A
      // task carries no stamp, so exact equality is the right test — a prefix
      // match there marks a genuinely new task as already written whenever its
      // name is the same as, or shorter than, an earlier one.
      const hit = data.find(r => {
        const stored = String(r.body || '')
        if (kind !== 'task') return stored.startsWith(body)
        // "Follow up" is a task an agent sets on the same contact again and
        // again, so the text alone cannot identify one write. The due date is
        // what separates this week's from next week's.
        return stored === body && String(r.due_date || '') === String(dueDate || '')
      })
      if (hit) setWritten(hit.written_at)
    })()
    return () => { cancelled = true }
  }, [personId, kind, body, dueDate])

  async function approve() {
    if (writing || written) return
    setWriting(true); setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pipeline-write`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          kind,
          fub_person_id: personId,
          fub_person_name: name,
          body,
          due_date: dueDate,
          due_time: dueTime,
          conversation_id: conversationId,
        }),
      })
      const data = await res.json()
      if (data?.needs_connection) { setError('Your Follow Up Boss key is missing or invalid.'); setWriting(false); return }
      if (!res.ok || data?.error) throw new Error(data?.error || `Write failed: ${res.status}`)
      setWritten(data.written_at || new Date().toISOString())
      showToast(data.duplicate ? 'Already written' : (kind === 'note' ? 'Note added' : 'Task created'))
    } catch (e) {
      setError(e.message)
    }
    setWriting(false)
  }

  // Once written, the card becomes a record rather than a control. No second
  // write is possible from it — the button is gone, not disabled, because a
  // greyed button invites a second click on something already done.
  if (written) {
    return (
      <div style={{background:'#fbfbfa',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'14px 16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
          <span style={{color:'#3B6D11',fontSize:'13px'}} aria-hidden="true">✓</span>
          <span style={{fontSize:'12.5px',color:'#27500A',fontFamily:FONT_BODY}}>
            {kind === 'note' ? 'Note added to' : 'Task created for'} {name} · {timeAgo(written)}
          </span>
        </div>
        <div style={{fontSize:'13.5px',lineHeight:1.6,color:'#666',fontFamily:FONT_BODY,whiteSpace:'pre-wrap'}}>{body}</div>
      </div>
    )
  }

  return (
    <div style={{background:'white',border:`1px solid ${RAIL_BORDER}`,borderRadius:'12px',padding:'16px 18px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'9px',marginBottom:'12px',flexWrap:'wrap'}}>
        <span style={{background:'#FAEEDA',color:'#633806',fontSize:'11px',padding:'3px 10px',borderRadius:'11px',fontFamily:FONT_BODY}}>
          {kind === 'note' ? 'Note' : 'Task'}
        </span>
        <span style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY}}>
          for <span style={{color:'#1d2e42',fontWeight:500}}>{name}</span>
        </span>
        {dueDate && (
          <span style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY}}>· due {pipelineDueLabel(dueDate, dueTime)}</span>
        )}
      </div>

      {/* The text itself, in a box, unedited. What is shown here is exactly what
          Follow Up Boss will receive — plus the HomePros Hub stamp the function
          appends to notes, which is noted below rather than shown inline so the
          agent reads their own words first. */}
      <div style={{background:'#f4f4f2',borderRadius:'10px',padding:'12px 14px',fontSize:'14px',lineHeight:1.65,color:'#1d2e42',fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
        {body}
      </div>

      {kind === 'note' && (
        <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginTop:'8px'}}>
          Signed “via HomePros Hub” in the timeline.
        </div>
      )}

      {error && (
        <div style={{fontSize:'12.5px',color:'#C8102E',fontFamily:FONT_BODY,marginTop:'10px'}}>{error}</div>
      )}

      <div style={{display:'flex',alignItems:'center',gap:'12px',marginTop:'14px'}}>
        <button onClick={approve} disabled={writing || !personId || !body}
          style={{...S.btnPrimary,padding:'9px 20px',fontSize:'12px',opacity:(writing || !personId || !body) ? 0.5 : 1}}>
          {writing ? 'Writing…' : (kind === 'note' ? 'Add to Follow Up Boss' : 'Create task')}
        </button>
        <span style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>
          Nothing is written until you click.
        </span>
      </div>
    </div>
  )
}

// ─── PIPELINE ACTIONS ────────────────────────────────────────────────────────
// Five buttons on the landing page. They replace the three suggestion pills,
// which were written before we knew what the tools could actually do — one of
// them ("everyone in Nurture") is a bad default for an agent with forty
// contacts, and none of them pointed at the work that matters.
//
// Each button is a question, not a code path. Pressing one sends text through
// the same composer an agent could have typed into, so the language matching,
// the drafting, and the card rendering all work exactly as they do otherwise.
// If a button ever proves too slow or too vague, THAT one gets a dedicated
// path — but building five special cases before knowing which needs it would
// be five features to maintain instead of five sentences.
//
// Three send immediately. Two prime the composer instead, because they need
// the agent to say who or what — a button cannot know which client they just
// got off the phone with.
//
// The tinted tiles borrow the rail's tool colours, so a button here and a tool
// in the sidebar read as the same system.
const PIPELINE_ACTIONS = [
  {
    key: 'reconnect',
    label: 'Who should I reconnect with?',
    sub: 'Past clients, quietest first',
    tile: '#FAEEDA', ink: '#633806',
    send: 'Who are my past clients I have not talked to in a while? Show me the quietest first.',
    icon: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></>,
  },
  {
    key: 'attention',
    label: "Who's paying attention?",
    sub: 'Opening your emails, never called',
    tile: '#EAF3DE', ink: '#3B6D11',
    send: 'Who is opening my emails but has never been called or texted?',
    icon: <><path d="M2 6h20v12H2z"/><path d="m2 7 10 6 10-6"/></>,
  },
  {
    key: 'today',
    label: 'Today',
    sub: "What's due, and who it's for",
    tile: '#E4EDF7', ink: '#123763',
    send: 'What tasks do I have open? Tell me what is due today and what is overdue, and who each one is for.',
    icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
  },
  {
    key: 'logcall',
    label: 'Log a call',
    sub: 'Say what happened, it writes the note',
    tile: '#EFE6F7', ink: '#46186B',
    // Primes rather than sends — only the agent knows who they spoke to.
    prime: 'I just talked to ',
    icon: <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3"/></>,
  },
  {
    key: 'brief',
    label: 'Before you call',
    sub: 'Everything on file, in one read',
    tile: '#DCEEEC', ink: '#0B4A44',
    prime: 'Tell me everything about ',
    icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  },
]

// The starter grid every tool uses. Was PipelineActions, which read the module
// constant directly — so a second tool could not use it without editing the
// component. `actions` is a prop now and nothing else changed.
//
// Each action is { key, label, sub, tile, ink, icon, send | prime }. `send`
// fires immediately; `prime` only fills the composer, which is the right shape
// for anything needing a name, an item or an amount that only the agent knows.
// An action may also carry its own `disabled` — Strategy's "Analyze these comps"
// has nothing to analyze until a pull returns. It ORs with the grid-level flag,
// so an actions array without the key behaves exactly as before.
function StarterActions({ actions, onSend, onPrime, disabled }) {
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))',gap:'10px',marginTop:'16px'}}>
      {actions.map(a => {
        const isDisabled = !!disabled || !!a.disabled
        return (
        <button
          key={a.key}
          disabled={isDisabled}
          onClick={() => a.send ? onSend(a.send) : onPrime(a.prime)}
          style={{
            background:'white',
            border:'0.5px solid #e8e8e8',
            borderRadius:'12px',
            padding:'14px 15px',
            display:'flex',
            alignItems:'flex-start',
            gap:'11px',
            textAlign:'left',
            cursor: isDisabled ? 'default' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
            font:'inherit',
            width:'100%',
          }}
        >
          <span style={{width:'32px',height:'32px',borderRadius:'8px',background:a.tile,color:a.ink,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {a.icon}
            </svg>
          </span>
          <span style={{minWidth:0}}>
            <span style={{display:'block',fontSize:'13.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,lineHeight:1.35}}>{a.label}</span>
            <span style={{display:'block',fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,lineHeight:1.45,marginTop:'2px'}}>{a.sub}</span>
          </span>
        </button>
        )
      })}
    </div>
  )
}

// ─── TOOL INTRO BAND ─────────────────────────────────────────────────────────
// The navy hero that opens a tool's landing page. Lifted verbatim out of
// PipelinePage, which had it inline with literal values — every measurement,
// colour and the radial-gradient overlay are unchanged, so Pipeline renders the
// same DOM through this as it did without it.
//
// `badge` is a node rather than props, so Pipeline keeps its own connected /
// disconnected pill and its pulse keyframes exactly as they were and no other
// tool has to model a concept it does not have.
//
// `stats` is an array of NODES, gated by the caller. Falsy entries are dropped
// here so a caller can inline `cond && <>…</>` in the array literal — the same
// per-stat gating Pipeline already did, where a slow config query costs one stat
// rather than the strip.
function ToolIntroBand({ title, description, badge, stats }) {
  const items = (stats || []).filter(Boolean)
  return (
    <div style={{background:'#1d2e42',borderRadius:'14px',overflow:'hidden',marginBottom:'24px'}}>

      <div style={{padding:'26px 28px',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',top:0,right:0,width:'50%',height:'100%',background:'radial-gradient(circle at 75% 30%, rgba(20,126,251,0.30) 0%, transparent 60%)',pointerEvents:'none'}} />

        <div style={{position:'relative',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'20px',flexWrap:'wrap'}}>
          <div>
            <div style={{fontSize:'34px',color:'#fff',fontFamily:FONT_HEAD,lineHeight:1,letterSpacing:'-0.5px'}}>
              {title}
            </div>
            <div style={{fontSize:'15px',color:'#B5D4F4',marginTop:'10px',maxWidth:'440px',lineHeight:1.6,fontFamily:FONT_BODY}}>
              {description}
            </div>
          </div>

          {badge}
        </div>
      </div>

      {/* No strip at all rather than an empty bar, for a tool with no stats. */}
      {items.length > 0 && (
        <div style={{background:'rgba(255,255,255,0.05)',padding:'11px 28px',display:'flex',gap:'22px',flexWrap:'wrap',borderTop:'1px solid rgba(255,255,255,0.08)'}}>
          {items.map((s, i) => (
            <span key={i} style={{fontSize:'12px',color:'#8fa8c4',fontFamily:FONT_BODY}}>{s}</span>
          ))}
        </div>
      )}

    </div>
  )
}

// ─── PIPELINE ────────────────────────────────────────────────────────────────
// Follow Up Boss contacts, by asking. Read-only in this version.
//
// Contacts are looked up LIVE on every turn by pipeline-chat, using the agent's
// OWN FUB key — so FUB decides what they can see and no permission logic lives
// here. What persists is the transcript, which does contain client details the
// model quoted back.
//
// TWO VIEWS, not one. PipelinePage is the landing at /pipeline: it explains the
// tool and takes the first question. PipelineView is one conversation at
// /pipeline/{id}. The single-thread version this replaces reopened the same
// growing transcript on every visit — so a question about one contact carried
// every previous contact with it into the model's context, and the landing
// page's explanation became unreachable after the first question.
//
// A conversation is created on SUBMIT, not on click, so an agent who opens
// Pipeline and changes their mind leaves no empty row behind. Same rule the
// Ask ADA composer follows.
const PIPELINE_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

// Shared by both views, so the connect card cannot drift between them.
function usePipelineConnection(userId) {
  const [connected, setConnected] = useState(null)
  useEffect(() => {
    let cancelled = false
    supabase.from('agent_fub_keys').select('fub_user_name')
      .eq('user_id', userId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setConnected(!!data) })
    return () => { cancelled = true }
  }, [userId])
  return [connected, setConnected]
}

// The key is generated inside Follow Up Boss, so it cannot be set for the agent
// from here — the three steps are the whole instruction.
function PipelineConnectCard({ userId, fullName, isMobile, showToast, onConnected }) {
  const [keyInput, setKeyInput] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [connectError, setConnectError] = useState('')

  // Verified before it is stored. A key that does not work is worth catching at
  // paste time, not on the agent's first question — and the identity that comes
  // back is Follow Up Boss's own, so pasting a colleague's key shows their name
  // rather than silently reading their book under this agent's account.
  async function connectKey() {
    const key = keyInput.trim()
    if (!key || connecting) return
    setConnecting(true); setConnectError('')

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pipeline-write`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ verify: true, fub_api_key: key }),
      })
      const data = await res.json()

      if (!res.ok || data?.ok !== true) {
        setConnectError(data?.error || `Could not verify that key: ${res.status}`)
        setConnecting(false)
        return
      }

      const { error } = await supabase.from('agent_fub_keys').upsert({
        user_id: userId,
        fub_api_key: key,
        fub_user_id: data.fub_user_id ?? null,
        fub_user_name: data.fub_user_name ?? null,
        fub_role: data.fub_role ?? null,
      }, { onConflict: 'user_id' })
      setConnecting(false)
      if (error) { setConnectError(error.message || 'Could not save that key.'); return }

      setKeyInput('')
      onConnected()
      showToast(data.fub_user_name ? `Connected as ${data.fub_user_name}` : 'Follow Up Boss connected')
    } catch (e) {
      setConnecting(false)
      setConnectError(e.message || 'Could not reach Follow Up Boss.')
    }
  }

  return (
    <div style={{...SETUP_CARD_BLUE,width:'100%',boxSizing:'border-box'}}>
      <div style={{fontSize:'13px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'8px'}}>
        Connect your Follow Up Boss account
      </div>
      <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.6,marginBottom:'12px'}}>
        Pipeline uses your own Follow Up Boss key, so you only ever see your own contacts.
        <div style={{marginTop:'8px'}}>1. In Follow Up Boss, open Admin, then API.</div>
        <div>2. Create a new key and name it HomePros Hub.</div>
        <div>3. Copy it and paste it below. Follow Up Boss shows it once.</div>
      </div>
      <div style={createRow(isMobile)}>
        <input
          value={keyInput}
          onChange={e=>{ setKeyInput(e.target.value); setConnectError('') }}
          onKeyDown={e=>{ if (e.key==='Enter') connectKey() }}
          placeholder="Paste your Follow Up Boss API key"
          style={{...S.input, flex:1, minWidth:0, marginBottom:0}}
        />
        <button onClick={connectKey} disabled={connecting || !keyInput.trim()}
          style={{...S.btnPrimary,flex:'none',flexShrink:0,padding:'9px 18px',fontSize:'12px',opacity:(connecting||!keyInput.trim())?0.5:1}}>
          {connecting ? 'Connecting…' : 'Connect'}
        </button>
      </div>
      {connectError && (
        <div style={{fontSize:'12px',color:'#C8102E',fontFamily:FONT_BODY,marginTop:'8px'}}>{connectError}</div>
      )}
    </div>
  )
}

const pipelineDisclaimer = (
  <div style={{background:'#f4f4f2',borderTop:'0.5px solid #e8e8e8',color:'#666',padding:'11px 16px',display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_BODY}}>
    <div style={{fontSize:'13px',color:'#791F1F',flexShrink:0}}>⚠</div>
    <div style={{fontSize:'10.5px',lineHeight:1.4}}><span style={{fontWeight:700,color:'#791F1F',letterSpacing:'0.3px',fontFamily:FONT_HEAD}}>YOU APPROVE&nbsp;&nbsp;</span>Pipeline can draft a note or a task, but nothing is written to Follow Up Boss until you click. Stages, tags, and deals are still done in Follow Up Boss.</div>
  </div>
)

// ─── MESSAGES ────────────────────────────────────────────────────────────────
// Messenger and Instagram DMs to the agent's own Facebook Page, at /messages.
// Built from the approved mockup (desktop and phone).
//
// Everything goes through edge functions; the browser never sees a Page token:
//   meta-inbox          list / read / reply / unread
//   meta-connect-start  the Facebook login URL (meta-connect-callback brings
//                       the agent back here with ?meta=... result codes)
//   meta-disconnect     remove a Page
// Who may connect, the 24-hour window and Follow Up Boss are all decided
// server side. This page only shows what the functions say.
//
// NOT SET UP: while the functions are not deployed, every call fails (404, or
// a network error because the 404 carries no CORS headers). The page shows one
// calm "not set up yet" card instead of an error, and the rail shows no count.
//
// PHONE (under 960px, MESSAGES_NARROW_PX): the list and a conversation are two
// screens. A conversation opens full screen over everything, with a back arrow,
// sized to window.visualViewport so the reply box stays above the keyboard.
// Opening one pushes a history step (same /messages URL, state.msgThread), so
// the phone's own back button or swipe closes it; back from the list leaves
// the page as normal. Dashboard's popstate handler sees /messages either way.

// LAUNCH SWITCH. false: Messages (the rail entry, the /messages route and the
// message notification click-through) is visible to admins only; everyone
// else sees no entry and /messages sends them home. Set to true at launch to
// open it to all agents. The server-side brokerage check applies either way.
const MESSAGES_LAUNCHED = true

function canSeeMessages(isAdmin) {
  return MESSAGES_LAUNCHED || isAdmin
}

const MESSAGES_UNREAD_EVENT = 'messages-unread-changed'

// Realtime gets its token from the session App already holds (initial load and
// every auth event: INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED).
// A token given to setAuth() marks it manual in realtime-js, so connect, the
// channel join and the heartbeat no longer call supabase.auth.getSession(),
// which waits on the cross-tab auth lock. On SIGNED_OUT supabase-js itself
// switches realtime back to its own callback.
function syncRealtimeToken(session) {
  if (session?.access_token) supabase.realtime.setAuth(session.access_token)
}

// A fresh topic per mount. supabase.channel(name) hands back an existing
// channel with that name even while it is still closing, and subscribe() on a
// closing channel does nothing, so a quick unmount/remount could lose its join.
let liveChannelSeq = 0
function liveChannelName(base) { return `${base}-${++liveChannelSeq}-${Math.random().toString(36).slice(2, 8)}` }
// Fired by useMessagesLive: detail is { table, eventType, record } for a row
// change, or { poll: true } when the page should resync (fallback poll, or the
// channel came back after a drop and may have missed rows).
const MESSAGES_CHANGED_EVENT = 'messages-changed'
const MESSAGES_POLL_MS = 30 * 1000
const MESSAGES_POLL_FAST_MS = 10 * 1000
const MESSAGES_NARROW_PX = 960
const MSG_GREEN = '#1e6b41'
const MSG_FAINT = '#999'

// The one way this page talks to the meta-* functions. notSetUp covers both a
// missing function and a network failure: from the browser they look the same.
async function callMetaFunction(name, body) {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(body || {}),
    })
    if (res.status === 404) return { notSetUp: true }
    const data = await res.json().catch(() => null)
    if (!data) return { failed: true }
    return { data }
  } catch {
    return { notSetUp: true }
  }
}

// The rail's "N new". Refreshed on every navigation, when /messages opens a
// thread, and (debounced) on every live change or fallback poll from
// useMessagesLive. null hides it. Disabled (no calls at all) for anyone who
// cannot see Messages.
function useMessagesUnread(page, enabled) {
  const [count, setCount] = useState(null)
  const refresh = useCallback(async () => {
    if (!enabled) { setCount(null); return }
    const r = await callMetaFunction('meta-inbox', { action: 'unread' })
    setCount(r.data?.ok ? r.data.unread_total : null)
  }, [enabled])
  useEffect(() => { refresh() }, [refresh, page])
  useEffect(() => {
    window.addEventListener(MESSAGES_UNREAD_EVENT, refresh)
    return () => window.removeEventListener(MESSAGES_UNREAD_EVENT, refresh)
  }, [refresh])
  // One inbound message is a message insert plus two thread updates within
  // milliseconds; one refresh covers them.
  useEffect(() => {
    let timer = null
    const on = () => { clearTimeout(timer); timer = setTimeout(refresh, 500) }
    window.addEventListener(MESSAGES_CHANGED_EVENT, on)
    return () => { clearTimeout(timer); window.removeEventListener(MESSAGES_CHANGED_EVENT, on) }
  }, [refresh])
  return count
}

// Supabase Realtime for Messages. RLS on meta_threads and meta_messages
// (own rows only) decides what this session receives; the user_id filter only
// saves the server work. Each change is re-broadcast as MESSAGES_CHANGED_EVENT
// for the rail count and /messages, which re-read through meta-inbox rather
// than trusting the payload.
//
// Fallback: until the channel is SUBSCRIBED, and whenever it errors, times out
// or closes, a { poll: true } event goes out every 10 seconds while /messages
// is open and visible, every 30 seconds otherwise. When it comes back after a
// drop, one { poll: true } resyncs whatever was missed.
function useMessagesLive(userId, enabled, onMessagesPage) {
  const onPageRef = useRef(onMessagesPage)
  onPageRef.current = onMessagesPage
  useEffect(() => {
    if (!enabled || !userId) return
    const fire = detail => window.dispatchEvent(new CustomEvent(MESSAGES_CHANGED_EVENT, { detail }))
    let cancelled = false
    let pollTimer = null
    let lastPoll = Date.now()
    let dropped = false
    const tick = () => {
      const due = onPageRef.current && !document.hidden ? MESSAGES_POLL_FAST_MS : MESSAGES_POLL_MS
      if (Date.now() - lastPoll < due - 500) return
      lastPoll = Date.now()
      fire({ poll: true })
    }
    const startPoll = () => { if (!pollTimer) { lastPoll = Date.now(); pollTimer = setInterval(tick, MESSAGES_POLL_FAST_MS) } }
    const stopPoll = () => { clearInterval(pollTimer); pollTimer = null }
    const changed = table => payload => fire({ table, eventType: payload.eventType, record: payload.new })

    startPoll()
    const channel = supabase.channel(liveChannelName(`messages-live-${userId}`))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'meta_messages', filter: `user_id=eq.${userId}` }, changed('meta_messages'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meta_threads', filter: `user_id=eq.${userId}` }, changed('meta_threads'))
      .subscribe(status => {
        if (cancelled) return  // our own cleanup closes the channel
        if (status === 'SUBSCRIBED') {
          stopPoll()
          if (dropped) { dropped = false; fire({ poll: true }) }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          dropped = true
          startPoll()
        }
      })
    return () => { cancelled = true; stopPoll(); supabase.removeChannel(channel) }
  }, [userId, enabled])
}

function useNarrowWidth(px) {
  const [narrow, setNarrow] = useState(() => window.innerWidth < px)
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < px)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [px])
  return narrow
}

// The visible part of the screen while a phone keyboard is up. null when not
// active or not supported; the full-screen thread then falls back to 100%.
function useVisualViewportBox(active) {
  const [box, setBox] = useState(null)
  useEffect(() => {
    const vv = window.visualViewport
    if (!active || !vv) { setBox(null); return }
    const on = () => setBox({ top: vv.offsetTop, height: vv.height })
    on()
    vv.addEventListener('resize', on)
    vv.addEventListener('scroll', on)
    return () => { vv.removeEventListener('resize', on); vv.removeEventListener('scroll', on) }
  }, [active])
  return box
}

// meta-connect-callback sends the agent back with short codes only, never
// text, so nobody can craft a /messages link that says words of their own.
const META_CONNECT_ERRORS = {
  expired: 'That took too long, or the link was already used. Click Connect to try again.',
  cancelled: 'Facebook connect was cancelled. Nothing was connected.',
  not_eligible: 'Messenger connect is available to HomePros and HomePros Advantage agents.',
  needs_fub: 'Connect your Follow Up Boss account first. Messages are saved to your own Follow Up Boss, so it has to be connected before Messenger and Instagram.',
  no_pages: 'No Page was chosen. Click Connect and pick your business Page.',
  not_setup: 'Messenger connect is not set up yet.',
  meta_failed: 'Facebook did not finish the connection. Try again in a minute.',
}

function plural(n, one, many) { return `${n} ${n === 1 ? one : many}` }

function connectNotices(params) {
  const meta = params.get('meta')
  if (meta === 'error') {
    const text = META_CONNECT_ERRORS[params.get('e')] || META_CONNECT_ERRORS.meta_failed
    return [{ tone: 'bad', title: 'NOT CONNECTED.', text }]
  }
  if (meta !== 'done') return []
  const n = k => { const v = parseInt(params.get(k) || '0', 10); return Number.isFinite(v) && v > 0 ? v : 0 }
  const notices = []
  const connected = n('connected') + n('no_ig')
  if (connected > 0) {
    const dms = n('connected') > 0 ? 'Messenger and Instagram DMs' : 'Messenger DMs'
    notices.push({ tone: 'good', title: 'CONNECTED.', text: `${connected === 1 ? 'Your Page now sends its' : `${connected} Pages now send their`} ${dms} here. New conversations are added to your Follow Up Boss.` })
  }
  if (n('no_ig') > 0) {
    notices.push({ tone: 'mixed', title: 'INSTAGRAM NOT LINKED.', text: `${plural(n('no_ig'), 'Page has', 'Pages have')} no Instagram Professional account linked, so only Messenger is connected. Link one to the Page in Facebook, then click Connect again.` })
  }
  if (n('homepros') > 0) {
    notices.push({ tone: 'mixed', title: `${plural(n('homepros'), 'PAGE WAS', 'PAGES WERE')} NOT CONNECTED.`, text: n('homepros') === 1 ? 'This Page is already connected through HomePros.' : 'These Pages are already connected through HomePros.' })
  }
  if (n('other_agent') > 0) {
    notices.push({ tone: 'mixed', title: `${plural(n('other_agent'), 'PAGE WAS', 'PAGES WERE')} NOT CONNECTED.`, text: n('other_agent') === 1 ? 'Another agent has already connected this Page or its Instagram account.' : 'Another agent has already connected these Pages or their Instagram accounts.' })
  }
  if (n('failed') > 0) {
    notices.push({ tone: 'bad', title: `${plural(n('failed'), 'PAGE WAS', 'PAGES WERE')} NOT CONNECTED.`, text: 'Facebook did not accept the connection. Click Connect to try again.' })
  }
  return notices
}

const NOTICE_TONES = {
  good:  { background:'rgba(34,160,91,0.08)', border:'1px solid rgba(34,160,91,0.3)', color:MSG_GREEN },
  mixed: { background:'rgba(253,191,0,0.10)', border:'1px solid rgba(253,191,0,0.45)', color:'#5c4400' },
  bad:   { background:'rgba(200,16,46,0.06)', border:'1px solid rgba(200,16,46,0.2)', color:'#C8102E' },
}

// The Page picker after Facebook login (meta-connect-callback stores the
// granted Pages; meta-connect-pick lists and connects them). Nothing is ticked
// to start with. Greyed rows cannot be ticked.
const MSG_PICK_LABEL = {
  yours: 'Already connected',
  other_agent: 'Connected by another agent',
  homepros: 'Connected through HomePros',
  reconnect: 'Needs reconnecting',
}

function MsgPagePicker({ picker, setPicker, onConnect, onCancel }) {
  const { status, pages, selected, connecting, error } = picker
  const toggle = id => setPicker(p => {
    const next = new Set(p.selected)
    if (next.has(id)) next.delete(id); else next.add(id)
    return { ...p, selected: next, error: '' }
  })
  return (
    <div style={{...SETUP_CARD_BLUE, maxWidth:'640px', boxSizing:'border-box'}}>
      <div style={{fontSize:'13px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'8px'}}>Choose the Pages to connect</div>
      <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.6,marginBottom:'12px'}}>
        Pick only Pages whose messages should come to you. Pages you manage for other people should be connected by them.
      </div>
      {status === 'loading' && <div style={{fontSize:'12.5px',color:MSG_FAINT,fontFamily:FONT_BODY}}>Loading your Pages…</div>}
      {status === 'expired' && <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY}}>That list expired. Click Connect to start again.</div>}
      {status === 'ready' && (
        <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
          {pages.map(pg => {
            const pickable = pg.status === 'available' || pg.status === 'reconnect'
            const on = selected.has(pg.page_id)
            return (
              <label key={pg.page_id} style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:'6px 12px',background:'#ffffff',border:`1px solid ${on ? '#147efb' : '#e8e8e8'}`,borderRadius:'10px',padding:'10px 12px',cursor:pickable ? 'pointer' : 'default',opacity:pickable ? 1 : 0.55}}>
                <input type="checkbox" checked={on} disabled={!pickable || connecting} onChange={()=>toggle(pg.page_id)} style={{width:'16px',height:'16px',flexShrink:0}} />
                <MsgAvatar name={pg.page_name} size={30} />
                {/* Names wrap; on a narrow screen the status label drops below. */}
                <div style={{minWidth:0,flex:'1 1 150px'}}>
                  <div style={{fontWeight:700,fontSize:'13.5px',color:'#1d2e42',fontFamily:FONT_BODY,overflowWrap:'anywhere'}}>{pg.page_name || `Page ${pg.page_id}`}</div>
                  <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>{pg.ig_username ? `Instagram @${pg.ig_username}` : 'No Instagram linked'}</div>
                </div>
                {MSG_PICK_LABEL[pg.status] && <span style={{fontSize:'11.5px',color:pg.status === 'reconnect' ? '#8a6100' : '#666',fontFamily:FONT_BODY,marginLeft:'auto',whiteSpace:'nowrap'}}>{MSG_PICK_LABEL[pg.status]}</span>}
              </label>
            )
          })}
        </div>
      )}
      {error && <div style={{...S.errorBox,marginTop:'12px',marginBottom:0}}>{error}</div>}
      <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap',marginTop:'14px'}}>
        {status === 'ready' && (
          <button onClick={onConnect} disabled={connecting || selected.size === 0}
            style={{...S.btnPrimary,flex:'none',padding:'10px 18px',fontSize:'12px',opacity:connecting || selected.size === 0 ? 0.5 : 1,cursor:connecting || selected.size === 0 ? 'default' : 'pointer'}}>
            {connecting ? 'Connecting…' : selected.size > 1 ? `Connect ${selected.size} Pages` : 'Connect Page'}
          </button>
        )}
        <button onClick={onCancel} disabled={connecting} style={{...S.btnOutline,flex:'none',padding:'10px 16px',fontSize:'12.5px'}}>{status === 'expired' ? 'Close' : 'Cancel'}</button>
      </div>
    </div>
  )
}

function MessagesNotice({ tone, title, text, action }) {
  return (
    <div style={{...NOTICE_TONES[tone],borderRadius:'10px',padding:'12px 16px',fontSize:'13px',lineHeight:1.5,display:'flex',gap:'12px',alignItems:'center',flexWrap:'wrap',fontFamily:FONT_BODY}}>
      <div style={{flex:1,minWidth:'220px'}}>
        {title && <span style={{fontFamily:FONT_HEAD,fontWeight:700,letterSpacing:'0.5px'}}>{title} </span>}
        {text}
      </div>
      {action}
    </div>
  )
}

function msgInitials(name) {
  const parts = String(name || '').replace(/^@/, '').split(/[\s._]+/).filter(Boolean)
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase()
}

function MsgAvatar({ name, size = 36 }) {
  return (
    <span aria-hidden="true" style={{width:`${size}px`,height:`${size}px`,borderRadius:'50%',background:'#1d2e42',color:'#fdbf00',fontFamily:FONT_HEAD,fontWeight:900,fontSize:`${Math.round(size * 0.36)}px`,letterSpacing:'0.5px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
      {msgInitials(name)}
    </span>
  )
}

function MsgPlatformTag({ platform }) {
  const ig = platform === 'instagram'
  return (
    <span style={{fontFamily:FONT_HEAD,fontWeight:700,fontSize:'10px',letterSpacing:'1px',padding:'3px 7px',borderRadius:'4px',flexShrink:0,
      background: ig ? 'rgba(193,53,132,0.10)' : 'rgba(20,126,251,0.10)', color: ig ? '#a12a6c' : '#147efb'}}>
      {ig ? 'INSTAGRAM' : 'MESSENGER'}
    </span>
  )
}

function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate() }

function msgClock(d) { return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) }

// List timestamps: "9:42 AM" today, "Yesterday", a weekday this week, else "Oct 18".
function msgListTime(iso) {
  if (!iso) return ''
  const d = new Date(iso), now = new Date()
  if (sameDay(d, now)) return msgClock(d)
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (sameDay(d, y)) return 'Yesterday'
  if (now - d < 6 * 86400000) return d.toLocaleDateString('en-US', { weekday: 'short' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function msgDayLabel(iso) {
  const d = new Date(iso), now = new Date()
  if (sameDay(d, now)) return 'TODAY'
  const y = new Date(now); y.setDate(now.getDate() - 1)
  if (sameDay(d, y)) return 'YESTERDAY'
  if (now - d < 6 * 86400000) return d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
}

function msgWindowLabel(t) {
  if (!t.window_open || !t.window_closes_at) return 'Window closed'
  const hours = Math.floor((new Date(t.window_closes_at) - Date.now()) / 3600000)
  return `Reply open · ${hours < 1 ? 'under 1h' : `${hours}h`} left`
}

function msgReplyUntil(iso) {
  const d = new Date(iso), now = new Date()
  const t = new Date(now); t.setDate(now.getDate() + 1)
  const day = sameDay(d, now) ? 'today' : sameDay(d, t) ? 'tomorrow' : d.toLocaleDateString('en-US', { weekday: 'long' })
  return `${msgClock(d)} ${day}`
}

const MSG_WINDOW_CLOSED = 'The 24-hour reply window has closed. You can reply after this person messages again.'
const MSG_RECONNECT = 'Your Facebook Page needs to be reconnected. Reconnect it above, then reply.'
const MSG_NOT_ELIGIBLE = 'Messenger connect is available to HomePros and HomePros Advantage agents.'

function MsgBlockedBox({ icon, children }) {
  return (
    <div style={{background:'#f4f4f2',border:'1px solid #e8e8e8',borderRadius:'8px',padding:'14px 16px',fontSize:'13px',color:'#666',lineHeight:1.5,display:'flex',gap:'10px',alignItems:'flex-start',fontFamily:FONT_BODY}}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MSG_FAINT} strokeWidth="2" strokeLinecap="round" aria-hidden="true" style={{flexShrink:0}}>
        <circle cx="12" cy="12" r="9"/>{icon === 'clock' ? <path d="M12 7v5l3 2"/> : <path d="M12 8v5M12 16.5v.01"/>}
      </svg>
      <div>{children}</div>
    </div>
  )
}

// Attachments are stored as "[ig_reel attachment]" or "[image, video
// attachment]" (meta-agent-webhook messageText). Display only: the stored
// text and the FUB notes keep the raw wording.
const MSG_ATTACHMENT_LABELS = {
  ig_reel: 'Reel shared', reel: 'Reel shared',
  ig_post: 'Post shared',
  image: 'Photo', video: 'Video', audio: 'Voice message', file: 'File',
  story_mention: 'Story mention', story_reply: 'Story reply', ig_story: 'Story reply', story: 'Story reply',
  share: 'Link', fallback: 'Link', template: 'Link',
}
const MSG_OPEN_IN = {
  instagram: { label: 'Open in Instagram', href: 'https://www.instagram.com/direct/inbox/' },
  messenger: { label: 'Open in Messenger', href: 'https://business.facebook.com/latest/inbox' },
}

// "Reel shared", "Photo, Video", ... or null when the text is not an attachment.
function msgAttachmentLabel(text) {
  const m = /^\[([a-z_]+(?:, [a-z_]+)*) attachment\]$/.exec(text || '')
  if (!m) return null
  const labels = [...new Set(m[1].split(', ').map(t => MSG_ATTACHMENT_LABELS[t] || 'Attachment'))]
  return labels.join(', ')
}

// List preview, which may carry a "You: " prefix.
function msgPreviewText(preview) {
  const p = preview || ''
  const you = p.startsWith('You: ')
  const label = msgAttachmentLabel(you ? p.slice(5) : p)
  return label ? `${you ? 'You: ' : ''}${label}` : p
}

// Instagram @handle beside the name, so two accounts with the same display
// name can be told apart. Nothing when there is no username, or when the
// name already is the username (the profile had no display name).
function MsgHandle({ t }) {
  if (!t.username || t.username === t.name) return null
  return <span style={{fontWeight:400,color:MSG_FAINT}}> · @{t.username}</span>
}

function MsgThreadRow({ t, selected, onOpen }) {
  return (
    <button onClick={()=>onOpen(t.id)} aria-current={selected ? 'true' : undefined}
      style={{width:'100%',textAlign:'left',border:'none',borderBottom:'1px solid #f3f3f1',cursor:'pointer',fontFamily:FONT_BODY,display:'flex',gap:'10px',padding:'12px 16px',alignItems:'flex-start',
        background: selected ? 'rgba(20,126,251,0.05)' : '#ffffff', boxShadow: selected ? 'inset 3px 0 0 #147efb' : 'none'}}>
      <MsgAvatar name={t.name} />
      <span style={{flex:1,minWidth:0,display:'block'}}>
        <span style={{display:'flex',alignItems:'center',gap:'6px'}}>
          <span style={{flex:1,minWidth:0,fontSize:'13.5px',fontWeight:700,color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            {t.name}<MsgHandle t={t} />
            {t.unread && <span aria-label="unread" style={{display:'inline-block',width:'7px',height:'7px',borderRadius:'50%',background:'#147efb',marginLeft:'6px',verticalAlign:'middle'}} />}
          </span>
          <span style={{fontSize:'11px',color:MSG_FAINT,flexShrink:0,fontVariantNumeric:'tabular-nums'}}>{msgListTime(t.last_message_at)}</span>
        </span>
        <span style={{display:'block',fontSize:'12.5px',marginTop:'3px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:t.unread ? '#1d2e42' : '#666',fontWeight:t.unread ? 600 : 400}}>
          {msgPreviewText(t.last_preview)}
        </span>
        <span style={{display:'flex',gap:'6px',alignItems:'center',marginTop:'6px'}}>
          <MsgPlatformTag platform={t.platform} />
          <span style={{fontSize:'10.5px',color:t.window_open ? MSG_GREEN : MSG_FAINT}}>{msgWindowLabel(t)}</span>
        </span>
      </span>
    </button>
  )
}

function MsgThreadPane({ narrow, summary, detail, loading, blocked, onBack, draft, setDraft, sending, sendError, onSend }) {
  const scrollRef = useRef(null)
  const messages = detail?.messages || []
  useEffect(() => {
    const el = scrollRef.current
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight })
  }, [messages.length, detail?.thread?.id])

  if (!summary) {
    return <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:MSG_FAINT,fontSize:'13px',fontFamily:FONT_BODY,padding:'24px'}}>Pick a conversation.</div>
  }

  const thread = detail?.thread?.id === summary.id ? detail.thread : summary
  let lastDay = null

  return (
    <div style={{flex:1,minHeight:0,minWidth:0,display:'flex',flexDirection:'column',background:'#ffffff'}}>
      <div style={{flexShrink:0,display:'flex',alignItems:'center',gap:'12px',padding:narrow?'8px 12px':'14px 20px',borderBottom:'1px solid #e8e8e8'}}>
        {narrow && (
          <button onClick={onBack} aria-label="Back to conversations"
            style={{background:'none',border:'none',color:'#147efb',fontSize:'28px',lineHeight:1,cursor:'pointer',padding:'0 6px 2px 0'}}>&#8249;</button>
        )}
        <MsgAvatar name={thread.name} size={narrow ? 32 : 36} />
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontFamily:FONT_HEAD,fontWeight:800,fontSize:narrow?'17px':'20px',letterSpacing:'0.5px',color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{thread.name}<MsgHandle t={thread} /></div>
          <div style={{display:'flex',gap:'8px',alignItems:'center',marginTop:'3px',fontSize:'12px',color:'#666',flexWrap:'wrap'}}>
            <MsgPlatformTag platform={thread.platform} />
            {detail?.thread?.id === summary.id && <span>{detail.thread.fub_person_id ? 'In Follow Up Boss' : 'Added to Follow Up Boss on their first message'}</span>}
          </div>
        </div>
      </div>

      <div ref={scrollRef} style={{flex:1,minHeight:0,overflowY:'auto',padding:'20px',display:'flex',flexDirection:'column',gap:'10px',background:'#fafaf8'}}>
        {loading && messages.length === 0 && <div style={{color:MSG_FAINT,fontSize:'13px',fontFamily:FONT_BODY}}>Loading...</div>}
        {messages.map(m => {
          const day = msgDayLabel(m.sent_at)
          const showDay = day !== lastDay
          lastDay = day
          const out = m.dir === 'out'
          const attachment = msgAttachmentLabel(m.text)
          const openIn = MSG_OPEN_IN[thread.platform]
          return (
            <Fragment key={m.id || m.sent_at}>
              {showDay && <span style={{alignSelf:'center',fontFamily:FONT_HEAD,fontWeight:700,fontSize:'10px',letterSpacing:'1.5px',color:MSG_FAINT,margin:'6px 0'}}>{day}</span>}
              <div style={{maxWidth:narrow?'85%':'72%',alignSelf:out?'flex-end':'flex-start',padding:'10px 14px',borderRadius:'14px',fontSize:'14px',lineHeight:1.45,fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere',
                ...(out ? { background:'#147efb', color:'#ffffff', borderBottomRightRadius:'4px' } : { background:'#ffffff', color:'#1d2e42', border:'1px solid #e8e8e8', borderBottomLeftRadius:'4px' })}}>
                {attachment ? (
                  <>
                    <span style={{fontStyle:'italic'}}>{attachment}</span>
                    {openIn && (
                      <a href={openIn.href} target="_blank" rel="noopener noreferrer"
                        style={{display:'block',marginTop:'4px',fontSize:'12px',fontWeight:600,fontStyle:'normal',textDecoration:'none',color:out ? '#ffffff' : '#147efb'}}>
                        {openIn.label} &#8599;
                      </a>
                    )}
                  </>
                ) : m.text}
              </div>
              <span style={{alignSelf:out?'flex-end':'flex-start',fontSize:'10.5px',color:MSG_FAINT,marginTop:'-4px',fontVariantNumeric:'tabular-nums',fontFamily:FONT_BODY}}>{msgClock(new Date(m.sent_at))}</span>
            </Fragment>
          )
        })}
      </div>

      <div style={{flexShrink:0,borderTop:'1px solid #e8e8e8',padding:narrow?'8px 10px calc(10px + env(safe-area-inset-bottom, 0px))':'12px 16px 14px',background:'#ffffff'}}>
        {blocked ? blocked : (
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            <div style={{display:'flex',gap:'10px',alignItems:'flex-end'}}>
              <label htmlFor="messages-reply" style={{position:'absolute',left:'-9999px'}}>Reply</label>
              <textarea id="messages-reply" value={draft} onChange={e=>setDraft(e.target.value)}
                onKeyDown={e=>{ if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend() }}
                placeholder={`Reply to ${thread.name}`} rows={narrow ? 1 : 2}
                style={{...S.textarea,minHeight:0,height:narrow?'40px':'64px',resize:'none',padding:narrow?'9px 12px':'10px 12px',borderRadius:narrow?'18px':'6px',flex:1,minWidth:0}} />
              {narrow ? (
                <button onClick={onSend} disabled={sending || !draft.trim()} aria-label="Send"
                  style={{width:'40px',height:'40px',borderRadius:'50%',background:'#1d2e42',border:'none',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',opacity:(sending || !draft.trim()) ? 0.45 : 1}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </button>
              ) : (
                <button onClick={onSend} disabled={sending || !draft.trim()}
                  style={{...S.btnPrimary,flex:'none',background:'#1d2e42',padding:'10px 18px',fontSize:'12px',opacity:(sending || !draft.trim()) ? 0.5 : 1}}>
                  {sending ? 'Sending…' : 'Send'}
                </button>
              )}
            </div>
            {sendError && <div style={{fontSize:'12px',color:'#C8102E',fontFamily:FONT_BODY}}>{sendError}</div>}
            <div style={{display:'flex',justifyContent:'space-between',gap:'10px',flexWrap:'wrap',fontSize:narrow?'10.5px':'11.5px',color:MSG_FAINT,fontFamily:FONT_BODY,padding:narrow?'0 4px':0}}>
              <span>{thread.window_closes_at ? `You can reply until ${msgReplyUntil(thread.window_closes_at)}.` : ''}</span>
              <span>Saved to Follow Up Boss when sent</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function MessagesPage({ session, profile, showToast, isMobile, navigateTo }) {
  const narrow = useNarrowWidth(MESSAGES_NARROW_PX)
  const [status, setStatus] = useState('loading')  // loading | not_set_up | error | ready
  const [inbox, setInbox] = useState(null)
  // Read once, then the query string is cleared so a reload does not repeat it.
  const [notices, setNotices] = useState(() => connectNotices(new URLSearchParams(window.location.search)))
  // Green notices ("CONNECTED.") hide themselves after 10 seconds; Dismiss still works.
  useEffect(() => {
    const good = notices.filter(n => n.tone === 'good')
    if (good.length === 0) return
    const t = setTimeout(() => setNotices(prev => prev.filter(n => !good.includes(n))), 10000)
    return () => clearTimeout(t)
  }, [notices])
  // Connected Pages: one compact line, expanded on "Manage".
  const [pagesOpen, setPagesOpen] = useState(false)
  // ?meta=pick: back from Facebook with granted Pages waiting to be picked.
  const [picker, setPicker] = useState(() => new URLSearchParams(window.location.search).get('meta') === 'pick'
    ? { status: 'loading', pages: [], selected: new Set(), connecting: false, error: '' } : null)
  const [filter, setFilter] = useState('all')
  const [selectedId, setSelectedId] = useState(null)
  const [narrowOpen, setNarrowOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(null)
  const [disconnecting, setDisconnecting] = useState(false)
  const vvBox = useVisualViewportBox(narrow && narrowOpen)

  useEffect(() => {
    if (window.location.search) window.history.replaceState(window.history.state, '', '/messages')
  }, [])

  const pickerLoading = picker?.status === 'loading'
  useEffect(() => {
    if (!pickerLoading) return
    let gone = false
    callMetaFunction('meta-connect-pick', { action: 'list' }).then(r => {
      if (gone) return
      if (!r.data?.ok) { setPicker(null); setNotices([{ tone: 'bad', title: 'NOT CONNECTED.', text: r.data?.error || META_CONNECT_ERRORS.meta_failed }]); return }
      const pages = r.data.pages || []
      setPicker(p => p && { ...p, status: pages.length ? 'ready' : 'expired', pages })
    })
    return () => { gone = true }
  }, [pickerLoading])

  async function connectPicked() {
    if (!picker || picker.connecting || picker.selected.size === 0) return
    setPicker(p => ({ ...p, connecting: true, error: '' }))
    const r = await callMetaFunction('meta-connect-pick', { action: 'connect', page_ids: [...picker.selected] })
    if (!r.data?.ok) {
      if (r.data?.expired) { setPicker(p => ({ ...p, status: 'expired', connecting: false })); return }
      setPicker(p => ({ ...p, connecting: false, error: r.data?.error || 'Could not connect. Try again.' }))
      return
    }
    const params = new URLSearchParams({ meta: 'done' })
    for (const [k, v] of Object.entries(r.data.counts || {})) if (v > 0) params.set(k, String(v))
    setNotices(connectNotices(params))
    setPicker(null)
    load()
    window.dispatchEvent(new Event(MESSAGES_UNREAD_EVENT))
  }

  async function cancelPicker() {
    setPicker(null)
    await callMetaFunction('meta-connect-pick', { action: 'cancel' })
  }

  const load = useCallback(async () => {
    const r = await callMetaFunction('meta-inbox', { action: 'list' })
    if (r.notSetUp) { setStatus('not_set_up'); return null }
    if (!r.data?.ok) { setStatus('error'); return null }
    setInbox(r.data)
    setStatus('ready')
    return r.data
  }, [])
  useEffect(() => { load() }, [load])

  // Re-reads the open conversation without touching the draft. 'read' marks it
  // read, so this only runs while the agent can actually see it.
  const refreshOpenThread = useCallback(async (id) => {
    const r = await callMetaFunction('meta-inbox', { action: 'read', thread_id: id })
    if (!r.data?.ok) return
    setDetail(prev => (prev && prev.thread?.id !== id) ? prev : r.data)
    setInbox(prev => prev && { ...prev, threads: prev.threads.map(t => t.id === id ? { ...t, unread: false } : t) })
    window.dispatchEvent(new Event(MESSAGES_UNREAD_EVENT))
  }, [])

  const openThread = useCallback(async (id) => {
    setSelectedId(id)
    if (narrow) {
      const st = { page: 'messages', caseId: null, msgThread: id }
      if (window.history.state?.msgThread) window.history.replaceState(st, '', '/messages')
      else window.history.pushState(st, '', '/messages')
      setNarrowOpen(true)
    }
    setSendError('')
    setDraft('')
    setDetailLoading(true)
    const r = await callMetaFunction('meta-inbox', { action: 'read', thread_id: id })
    setDetailLoading(false)
    if (!r.data?.ok) { showToast(r.data?.error || 'Could not open that conversation'); return }
    setDetail(r.data)
    setInbox(prev => prev && { ...prev, threads: prev.threads.map(t => t.id === id ? { ...t, unread: false } : t) })
    window.dispatchEvent(new Event(MESSAGES_UNREAD_EVENT))
  }, [showToast, narrow])

  // Back button / swipe on a phone: the history step is gone, so close.
  useEffect(() => {
    const onPop = () => { if (!window.history.state?.msgThread) setNarrowOpen(false) }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // The on-screen back arrow goes through history too, so the step is used up
  // and the phone's back button then leaves the page as expected.
  const closeNarrowThread = useCallback(() => {
    if (window.history.state?.msgThread) window.history.back()
    else setNarrowOpen(false)
  }, [])

  const threads = inbox?.threads || []
  const connections = inbox?.connections || []
  const visible = threads.filter(t => filter === 'all' || t.platform === filter)
  const unreadTotal = threads.filter(t => t.unread).length
  const summary = threads.find(t => t.id === selectedId) || null
  const needsReconnect = connections.some(c => c.status === 'needs_reconnect')
  const reconnectCount = connections.filter(c => c.status === 'needs_reconnect').length
  // A Page that needs reconnecting opens the list, so its Reconnect path is in view.
  useEffect(() => { if (needsReconnect) setPagesOpen(true) }, [needsReconnect])

  // Live updates (useMessagesLive). Refs, so the listener is attached once and
  // still sees the current selection.
  const liveRef = useRef({})
  liveRef.current = { threads, selectedId, narrow, narrowOpen }
  useEffect(() => {
    let timer = null
    let wantDetail = false   // a live insert into the open conversation
    let checkOpen = false    // a poll or tab return: re-open only if it moved
    const viewingId = () => {
      const { selectedId: id, narrow: n, narrowOpen: open } = liveRef.current
      return id && !document.hidden && (!n || open) ? id : null
    }
    const same = (a, b) => (a ? Date.parse(a) : null) === (b ? Date.parse(b) : null)
    const run = async () => {
      timer = null
      const id = viewingId()
      const known = id ? liveRef.current.threads.find(t => t.id === id) : null
      if (wantDetail && id) await refreshOpenThread(id)
      const fresh = await load()
      if (checkOpen && !wantDetail && id && fresh) {
        const now = (fresh.threads || []).find(t => t.id === id)
        if (now && known && !same(now.last_message_at, known.last_message_at)) await refreshOpenThread(id)
      }
      wantDetail = false
      checkOpen = false
    }
    const schedule = () => { clearTimeout(timer); timer = setTimeout(run, 500) }
    const on = e => {
      const d = e.detail || {}
      if (d.poll) { checkOpen = true; schedule(); return }
      const rec = d.record || {}
      if (d.table === 'meta_threads' && d.eventType === 'UPDATE') {
        // Opening a thread writes last_read_at; that alone is not news, and
        // reacting to it would loop (read -> update -> read).
        const t = liveRef.current.threads.find(x => x.id === rec.id)
        if (t && same(t.last_message_at, rec.last_message_at) && same(t.last_inbound_at, rec.last_inbound_at)) return
      }
      if (d.table === 'meta_messages' && rec.thread_id && rec.thread_id === liveRef.current.selectedId) wantDetail = true
      schedule()
    }
    // Back on the tab: catch up; the open conversation is re-opened (and so
    // marked read) only if it has a newer message.
    const onVisible = () => { if (!document.hidden) { checkOpen = true; schedule() } }
    window.addEventListener(MESSAGES_CHANGED_EVENT, on)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(timer)
      window.removeEventListener(MESSAGES_CHANGED_EVENT, on)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load, refreshOpenThread])

  // Desktop opens the newest conversation; a phone starts on the list.
  const firstThreadId = threads[0]?.id || null
  useEffect(() => {
    if (status === 'ready' && !narrow && !selectedId && firstThreadId) openThread(firstThreadId)
  }, [status, narrow, selectedId, firstThreadId, openThread])

  async function connect() {
    if (connecting) return
    setConnecting(true)
    const r = await callMetaFunction('meta-connect-start', {})
    if (r.data?.ok && r.data.url) { window.location.assign(r.data.url); return }
    setConnecting(false)
    if (r.notSetUp) { setNotices([{ tone: 'bad', title: 'NOT CONNECTED.', text: META_CONNECT_ERRORS.not_setup }]); return }
    setNotices([{ tone: 'bad', title: 'NOT CONNECTED.', text: r.data?.error || META_CONNECT_ERRORS.meta_failed }])
    if (r.data?.not_eligible || r.data?.needs_fub) load()
  }

  async function disconnect(bindingId) {
    if (disconnecting) return
    setDisconnecting(true)
    const r = await callMetaFunction('meta-disconnect', { binding_id: bindingId })
    setDisconnecting(false)
    setConfirmDisconnect(null)
    if (!r.data?.ok) { showToast(r.data?.error || 'Could not disconnect the Page. Try again.'); return }
    showToast('Page disconnected')
    setSelectedId(null); setDetail(null); closeNarrowThread()
    load()
    window.dispatchEvent(new Event(MESSAGES_UNREAD_EVENT))
  }

  async function send() {
    const text = draft.trim()
    if (!text || sending || !summary) return
    setSending(true); setSendError('')
    const r = await callMetaFunction('meta-inbox', { action: 'reply', thread_id: summary.id, text })
    setSending(false)
    if (r.notSetUp) { setSendError('Messages is not set up yet.'); return }
    if (!r.data?.ok) {
      setSendError(r.data?.error || 'Your message was not sent. Try again.')
      if (r.data?.window_closed) setInbox(prev => prev && { ...prev, threads: prev.threads.map(t => t.id === summary.id ? { ...t, window_open: false } : t) })
      if (r.data?.needs_reconnect || r.data?.not_eligible) load()
      return
    }
    setDraft('')
    const msg = r.data.message
    if (!r.data.duplicate && msg) {
      setDetail(prev => prev && prev.thread?.id === summary.id ? { ...prev, messages: [...prev.messages, msg] } : prev)
      setInbox(prev => prev && { ...prev, threads: prev.threads.map(t => t.id === summary.id ? { ...t, last_preview: `You: ${text}`.slice(0, 120), last_message_at: msg.sent_at } : t) })
    }
  }

  // What replaces the reply box, if anything. Order matters: brokerage first,
  // then the Page's connection, then the 24-hour window.
  let blocked = null
  if (summary) {
    const binding = connections.find(c => c.id === summary.binding_id)
    if (inbox && !inbox.eligible) blocked = <MsgBlockedBox>{MSG_NOT_ELIGIBLE}</MsgBlockedBox>
    else if (binding?.status === 'needs_reconnect') blocked = <MsgBlockedBox>{MSG_RECONNECT}</MsgBlockedBox>
    else if (!summary.window_open) blocked = (
      <MsgBlockedBox icon="clock">
        {MSG_WINDOW_CLOSED}
        <div style={{fontSize:'12px',color:MSG_FAINT,marginTop:'2px'}}>Reach {summary.name} another way from Follow Up Boss.</div>
      </MsgBlockedBox>
    )
  }

  const pill = (tone, label) => (
    <div style={{display:'flex',alignItems:'center',gap:'9px',flexShrink:0,borderRadius:'999px',padding:'7px 14px',
      ...(tone === 'ok' ? { background:'rgba(34,160,91,0.14)', border:'1px solid rgba(34,160,91,0.45)' } : { background:'rgba(253,191,0,0.14)', border:'1px solid rgba(253,191,0,0.4)' })}}>
      <span style={{width:'7px',height:'7px',borderRadius:'50%',flexShrink:0,background:tone === 'ok' ? '#22a05b' : '#fdbf00'}} />
      <span style={{fontSize:'11px',letterSpacing:'0.5px',color:tone === 'ok' ? '#7fd4a3' : '#e8d9a8',fontFamily:FONT_BODY}}>{label}</span>
    </div>
  )

  const ready = status === 'ready'
  const pagesLabel = n => n === 1 ? '1 Page' : `${n} Pages`
  const badge = !ready || !inbox.eligible ? null
    : connections.length === 0 ? pill('warn', 'Connect your Page')
    : needsReconnect ? pill('warn', `${pagesLabel(reconnectCount)} need${reconnectCount === 1 ? 's' : ''} reconnecting`)
    : pill('ok', `${pagesLabel(connections.length)} connected`)

  // Counts only; the names are in the Pages line below (Manage).
  const igCount = connections.filter(c => c.ig_username).length
  const stats = ready && inbox.eligible ? [
    ...(connections.length > 0 ? [(<><span style={{color:'#fdbf00'}}>{connections.length}</span> Facebook {connections.length === 1 ? 'Page' : 'Pages'}</>)] : []),
    ...(igCount > 0 ? [(<><span style={{color:'#fdbf00'}}>{igCount}</span> Instagram {igCount === 1 ? 'account' : 'accounts'}</>)] : []),
    (<>Reply within <span style={{color:'#fdbf00'}}>24 hours</span> of their last message</>),
  ] : []

  const card = (children, blue) => (
    <div style={{...(blue ? SETUP_CARD_BLUE : { border:`1px solid ${RAIL_BORDER}`, borderRadius:'16px', background:'#ffffff', padding:'20px' }), maxWidth:'640px', boxSizing:'border-box'}}>{children}</div>
  )
  const cardTitle = t => <div style={{fontSize:'13px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',marginBottom:'8px'}}>{t}</div>
  const cardText = { fontSize:'12.5px', color:'#555', fontFamily:FONT_BODY, lineHeight:1.6 }

  let body = null
  if (status === 'loading') {
    body = <div style={{color:MSG_FAINT,fontSize:'13px',fontFamily:FONT_BODY}}>Loading...</div>
  } else if (status === 'not_set_up') {
    body = card(<>
      {cardTitle('Messages is not set up yet')}
      <div style={cardText}>Facebook Messenger and Instagram DMs will show here once Messages is switched on. There is nothing to do on your side yet.</div>
    </>)
  } else if (status === 'error') {
    body = (
      <MessagesNotice tone="bad" text="Could not load your messages."
        action={<button onClick={()=>{ setStatus('loading'); load() }} style={{...S.btnOutline,flex:'none',padding:'8px 14px',fontSize:'12.5px'}}>Try again</button>} />
    )
  } else if (!inbox.eligible && connections.length === 0) {
    body = card(<>
      {cardTitle('Messages')}
      <div style={cardText}>{MSG_NOT_ELIGIBLE}</div>
    </>)
  } else if (connections.length === 0 && !inbox.fub_connected) {
    body = card(<>
      {cardTitle('Connect Follow Up Boss first')}
      <div style={cardText}>Messages are saved to your own Follow Up Boss, so it has to be connected before Messenger and Instagram.</div>
      <div style={{marginTop:'14px'}}>
        <button onClick={()=>navigateTo('pipeline')} style={{...S.btnPrimary,flex:'none',padding:'10px 18px',fontSize:'12px'}}>Go to Pipeline to connect</button>
      </div>
    </>, true)
  } else if (connections.length === 0) {
    body = card(<>
      {cardTitle('Connect your Facebook Page')}
      <div style={cardText}>
        Bring Messenger and Instagram DMs from your own business Page into TrueTopProducer. Each new conversation becomes a lead in your Follow Up Boss, assigned to you.
        <div style={{marginTop:'8px'}}>1. Click Connect. Facebook opens and asks you to log in.</div>
        <div>2. Pick your business Page. Pick its Instagram account too, if it has one.</div>
        <div>3. Allow the permissions. You come back here when it's done.</div>
      </div>
      <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap',marginTop:'14px'}}>
        <button onClick={connect} disabled={connecting} style={{...S.btnPrimary,flex:'none',padding:'10px 18px',fontSize:'12px',opacity:connecting?0.6:1}}>{connecting ? 'Opening Facebook…' : 'Connect Facebook Page'}</button>
        <span style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>Takes about a minute</span>
      </div>
      <div style={{fontSize:'11.5px',color:MSG_FAINT,fontFamily:FONT_BODY,marginTop:'10px',lineHeight:1.5}}>Instagram needs a Professional account linked to the Page. A personal Facebook profile can't be connected, only a Page.</div>
    </>, true)
  } else {
    const filterChip = (key, label) => (
      <button key={key} onClick={()=>setFilter(key)} aria-pressed={filter === key}
        style={{border:`0.5px solid ${filter === key ? '#147efb' : '#e8e8e8'}`,borderRadius:'18px',padding:'4px 10px',fontSize:'11.5px',cursor:'pointer',fontFamily:FONT_BODY,background:'#ffffff',color:filter === key ? '#147efb' : '#666'}}>
        {label}
      </button>
    )
    const list = (
      <div style={{display:'flex',flexDirection:'column',minWidth:0,minHeight:0,borderRight:narrow ? 'none' : '1px solid #e8e8e8'}}>
        <div style={{flexShrink:0,padding:'12px 16px',borderBottom:'1px solid #e8e8e8',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px',flexWrap:'wrap'}}>
          {!narrow && <span style={{fontFamily:FONT_HEAD,fontWeight:900,fontSize:'13px',letterSpacing:'1px',color:'#1d2e42'}}>CONVERSATIONS</span>}
          <div style={{display:'flex',gap:'6px'}}>
            {filterChip('all', unreadTotal > 0 ? `All · ${unreadTotal} new` : 'All')}
            {filterChip('messenger', 'Messenger')}
            {filterChip('instagram', 'Instagram')}
          </div>
        </div>
        <div style={{flex:1,minHeight:0,overflowY:narrow ? 'visible' : 'auto'}}>
          {visible.length === 0 ? (
            <div style={{padding:'28px 16px',textAlign:'center',color:MSG_FAINT,fontSize:'12.5px',fontFamily:FONT_BODY,lineHeight:1.6}}>
              {threads.length === 0 ? 'No messages yet. New Messenger and Instagram DMs to your Page will show here.' : 'Nothing here for this filter.'}
            </div>
          ) : visible.map(t => (
            <MsgThreadRow key={t.id} t={t} selected={!narrow && t.id === selectedId} onOpen={openThread} />
          ))}
        </div>
      </div>
    )

    const pane = (
      <MsgThreadPane narrow={narrow} summary={summary} detail={detail} loading={detailLoading} blocked={blocked}
        onBack={closeNarrowThread} draft={draft} setDraft={setDraft} sending={sending} sendError={sendError} onSend={send} />
    )

    const pagesSummary = (
      <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap'}}>
        <span style={{width:'7px',height:'7px',borderRadius:'50%',flexShrink:0,background:needsReconnect ? '#fdbf00' : '#22a05b'}} />
        <span style={{fontSize:'13px',color:'#1d2e42',fontFamily:FONT_BODY,fontWeight:600}}>
          {connections.length === 1 ? '1 Page connected' : `${connections.length} Pages connected`}
        </span>
        {needsReconnect && (
          <span style={{fontSize:'12.5px',color:'#8a6100',fontFamily:FONT_BODY}}>
            · {reconnectCount === 1 ? '1 Page needs reconnecting' : `${reconnectCount} Pages need reconnecting`}
          </span>
        )}
        <button onClick={()=>{ setPagesOpen(o => !o); setConfirmDisconnect(null) }} aria-expanded={pagesOpen}
          style={{marginLeft:'auto',background:'none',border:'none',padding:'4px 2px',cursor:'pointer',color:'#147efb',fontSize:'12.5px',fontWeight:600,fontFamily:FONT_BODY}}>
          {pagesOpen ? 'Hide' : 'Manage'}
        </button>
      </div>
    )

    const pagesRow = (
      <div style={{background:'#ffffff',border:`1px solid ${RAIL_BORDER}`,borderRadius:'14px',padding:pagesOpen ? '12px 14px' : '9px 14px',display:'flex',flexDirection:'column',gap:'10px'}}>
        {pagesSummary}
        {pagesOpen && <div style={{height:'1px',background:'#e8e8e8'}} />}
        {pagesOpen && connections.map(c => (
          <div key={c.id} style={{display:'flex',gap:'14px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontFamily:FONT_HEAD,fontWeight:700,fontSize:'10px',letterSpacing:'1.5px',color:MSG_FAINT}}>YOUR PAGE</span>
            <div style={{display:'flex',alignItems:'center',gap:'10px',flex:1,minWidth:'220px'}}>
              <MsgAvatar name={c.page_name} />
              <div style={{minWidth:0}}>
                <div style={{fontWeight:700,fontSize:'14px',color:'#1d2e42',fontFamily:FONT_BODY}}>{c.page_name || 'Your Page'}</div>
                <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>Facebook Page · {c.ig_username ? `Instagram @${c.ig_username}` : 'No Instagram linked'}</div>
              </div>
            </div>
            <span style={{fontSize:'11px',display:'flex',alignItems:'center',gap:'6px',color:'#666',fontFamily:FONT_BODY}}>
              <span style={{width:'7px',height:'7px',borderRadius:'50%',background:c.status === 'active' ? '#22a05b' : '#fdbf00'}} />
              {c.status === 'active' ? 'Receiving messages' : 'Needs reconnecting'}
            </span>
            <button onClick={()=>setConfirmDisconnect(c.id)} style={{...S.btnDanger,flex:'none',padding:'8px 14px',fontSize:'12.5px'}}>Disconnect</button>
            {confirmDisconnect === c.id && (
              <div style={{flexBasis:'100%',background:'#f4f4f2',border:'1px solid #e8e8e8',borderRadius:'10px',padding:'12px 14px',fontSize:'13px',color:'#1d2e42',lineHeight:1.5,display:'flex',gap:'12px',alignItems:'center',flexWrap:'wrap',fontFamily:FONT_BODY}}>
                <div style={{flex:1,minWidth:'220px'}}><strong>Disconnect {c.page_name || 'this Page'}?</strong> New DMs stop coming in, and these conversations are removed from TrueTopProducer. Notes already in your Follow Up Boss stay there.</div>
                <button onClick={()=>setConfirmDisconnect(null)} style={{...S.btnOutline,flex:'none',padding:'8px 14px',fontSize:'12.5px'}}>Cancel</button>
                <button onClick={()=>disconnect(c.id)} disabled={disconnecting} style={{...S.btnDanger,flex:'none',padding:'8px 14px',fontSize:'12.5px'}}>{disconnecting ? 'Disconnecting…' : 'Disconnect'}</button>
              </div>
            )}
          </div>
        ))}
        {pagesOpen && inbox.eligible && (
          <div>
            <button onClick={connect} disabled={connecting} style={{...S.btnOutline,flex:'none',padding:'8px 14px',fontSize:'12.5px'}}>{connecting ? 'Opening Facebook…' : 'Add another Page'}</button>
          </div>
        )}
      </div>
    )

    body = (
      <>
        {!inbox.eligible && <MessagesNotice tone="bad" title="MESSAGES STOPPED." text={MSG_NOT_ELIGIBLE} />}
        {inbox.eligible && needsReconnect && (
          <MessagesNotice tone="mixed" title="RECONNECT YOUR PAGE."
            text="Facebook stopped accepting the connection, so new DMs are not coming in. Reconnecting takes a minute and keeps your conversations."
            action={<button onClick={connect} disabled={connecting} style={{...S.btnPrimary,flex:'none',padding:'9px 16px',fontSize:'12px'}}>{connecting ? 'Opening…' : 'Reconnect'}</button>} />
        )}

        {/* One compact line above the inbox, on desktop and phone. */}
        {pagesRow}

        {narrow ? (
          <div style={{background:'#ffffff',border:`1px solid ${RAIL_BORDER}`,borderRadius:'14px',overflow:'hidden'}}>{list}</div>
        ) : (
          <div style={{background:'#ffffff',border:`1px solid ${RAIL_BORDER}`,borderRadius:'14px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)',display:'grid',gridTemplateColumns:'320px minmax(0,1fr)',height:'max(460px, calc(100vh - 400px))',overflow:'hidden'}}>
            {list}
            {pane}
          </div>
        )}

        {narrow && narrowOpen && summary && (
          <div style={{position:'fixed',left:0,right:0,top:vvBox ? `${vvBox.top}px` : 0,height:vvBox ? `${vvBox.height}px` : '100%',zIndex:400,background:'#ffffff',display:'flex',flexDirection:'column',paddingTop:'env(safe-area-inset-top, 0px)',boxSizing:'border-box'}}>
            {pane}
          </div>
        )}
      </>
    )
  }

  return (
    <div style={{flex:1,minWidth:0,minHeight:0,display:'flex',flexDirection:'column',background:'#ffffff',overflow:'hidden'}}>
      <div style={{flex:1,minHeight:0,overflowY:'auto'}}>
        <div style={{maxWidth:'1120px',margin:'0 auto',padding:isMobile ? '8px 16px 32px' : '24px 28px 40px',display:'flex',flexDirection:'column',gap:'16px',boxSizing:'border-box'}}>
          {narrow ? (
            // Phone: the compact band from the approved phone mockup, so the
            // conversations start on the first screen.
            <div style={{background:'#1d2e42',borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}>
              <span style={{fontSize:'26px',lineHeight:1,color:'#ffffff',fontFamily:FONT_HEAD}}>Messages</span>
              {badge}
            </div>
          ) : (
            <ToolIntroBand
              title="Messages"
              description="Facebook and Instagram DMs to your business Page, in one inbox. Every message and every reply is saved to your Follow Up Boss."
              badge={badge}
              stats={stats}
            />
          )}
          {notices.map((n, i) => (
            <MessagesNotice key={i} {...n}
              action={<button onClick={()=>setNotices(prev => prev.filter((_, j) => j !== i))} style={{...S.btnOutline,flex:'none',padding:'7px 12px',fontSize:'12px'}}>Dismiss</button>} />
          ))}
          {picker && <MsgPagePicker picker={picker} setPicker={setPicker} onConnect={connectPicked} onCancel={cancelPicker} />}
          {body}
          <div style={{display:'flex',gap:'18px',fontSize:'12px',fontFamily:FONT_BODY}}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:'#666',textDecoration:'none'}}>Privacy Policy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color:'#666',textDecoration:'none'}}>Terms of Use</a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── PIPELINE: LANDING ───────────────────────────────────────────────────────
// Always the landing. Every visit to /pipeline lands here, whatever was asked
// last time — the explanation is the point of this screen and has to stay
// reachable. Past conversations are in the rail's My cases list, not on the
// page: an agent asking a quick question should not scroll past a list first.
function PipelinePage({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [input, setInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [connected, setConnected] = usePipelineConnection(session.user.id)

  // Two numbers for the hero strip, from the nightly account config. Both start
  // null and the strip omits whichever is missing, so a slow or failed query
  // costs a stat rather than the screen.
  const [stats, setStats] = useState({ stageCount: null, contactTotal: null })

  useEffect(() => {
    let cancelled = false
    supabase.from('fub_account_config').select('config_type, payload')
      .in('config_type', ['stages', 'contact_total'])
      .then(({ data }) => {
        if (cancelled || !data) return
        const stages = data.find(r => r.config_type === 'stages')?.payload
        const total = data.find(r => r.config_type === 'contact_total')?.payload?.total
        setStats({
          stageCount: Array.isArray(stages) ? stages.length : null,
          contactTotal: typeof total === 'number' ? total : null,
        })
      })
    return () => { cancelled = true }
  }, [])

  // The row is created here so the view it lands on already has an id, then the
  // text rides along as the opening question. Titled from the question, so the
  // My cases entry reads "Zuleika Barraza" rather than "New conversation."
  async function submit(textArg) {
    const text = (typeof textArg === 'string' ? textArg : input).trim()
    if (text.length < 3 || submitting) return
    setSubmitting(true)
    const { data, error } = await supabase.from('pipeline_conversations')
      .insert({ agent_id: session.user.id, title: text.slice(0, 60) }).select().single()
    if (error) { showToast('Could not start a conversation'); setSubmitting(false); return }
    setSubmitting(false)
    setInput('')
    navigateTo('pipeline', data.id, { pipelinePrefill: text })
  }

  const disabled = submitting || input.trim().length < 3

  const bodyNode = (
    <div style={{paddingTop:isMobile?'8px':'24px'}}>
      <style>{`
        @keyframes pipelinePulse { 0%,100% { opacity: 1; transform: scale(1) } 50% { opacity: 0.4; transform: scale(0.8) } }
        @media (prefers-reduced-motion: reduce) {
          .pipeline-pulse { animation: none !important }
        }
      `}</style>

      <ToolIntroBand
        title="Pipeline"
        description="Ask about anyone in your database. In English or Spanish — it reads notes in both."
        badge={(
          <>
            {connected === true && (
              <div style={{display:'flex',alignItems:'center',gap:'9px',flexShrink:0,background:'rgba(34,160,91,0.14)',border:'1px solid rgba(34,160,91,0.45)',borderRadius:'999px',padding:'7px 14px'}}>
                <span className="pipeline-pulse" style={{width:'7px',height:'7px',borderRadius:'50%',background:'#22a05b',flexShrink:0,animation:'pipelinePulse 2s ease-in-out infinite'}} />
                <span style={{fontSize:'11px',letterSpacing:'0.5px',color:'#7fd4a3',fontFamily:FONT_BODY}}>Follow Up Boss connected</span>
              </div>
            )}

            {/* Amber and still, not green and pulsing — the pill states what is
                true right now, and nothing is reading yet. connected === null is
                the unresolved case and shows neither, so the pill never has to
                be corrected a moment after it appears. */}
            {connected === false && (
              <div style={{display:'flex',alignItems:'center',gap:'9px',flexShrink:0,background:'rgba(253,191,0,0.14)',border:'1px solid rgba(253,191,0,0.4)',borderRadius:'999px',padding:'7px 14px'}}>
                <span style={{width:'7px',height:'7px',borderRadius:'50%',background:'#fdbf00',flexShrink:0}} />
                <span style={{fontSize:'11px',letterSpacing:'0.5px',color:'#e8d9a8',fontFamily:FONT_BODY}}>Connect your account</span>
              </div>
            )}
          </>
        )}
        // Same per-stat gating as before: a null from the nightly config drops
        // its own stat and nothing else.
        stats={[
          stats.contactTotal !== null && (<><span style={{color:'#fdbf00'}}>{stats.contactTotal.toLocaleString()}</span> in the brokerage</>),
          stats.stageCount !== null && (<><span style={{color:'#fdbf00'}}>{stats.stageCount}</span> stages</>),
          (<><span style={{color:'#fdbf00'}}>Español</span> · English</>),
          'Nothing is written without your approval',
        ]}
      />

      {connected === false ? (
        <PipelineConnectCard
          userId={session.user.id}
          fullName={profile?.full_name}
          isMobile={isMobile}
          showToast={showToast}
          onConnected={()=>setConnected(true)}
        />
      ) : connected === true ? (
        <>
          <div style={{width:'100%',display:'flex',flexDirection:'column',background:'white',border:`1px solid ${RAIL_BORDER}`,borderRadius:'14px',padding:'10px 10px 8px 14px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
            <input
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{ if (e.key==='Enter') submit() }}
              placeholder="Ask about a contact, a stage, or who's engaging"
              autoFocus={!isMobile}
              style={{...S.input,background:'transparent',border:'none',borderRadius:0,padding:0,height:'44px',fontSize:'15px',width:'100%'}}
            />
            <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end'}}>
              <button
                onClick={submit}
                disabled={disabled}
                aria-label="Send"
                style={{flexShrink:0,width:'38px',height:'38px',borderRadius:'50%',background:'#1d2e42',border:'none',cursor:disabled?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',opacity:disabled ? 0.45 : 1}}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
                </svg>
              </button>
            </div>
          </div>

          <StarterActions
            actions={PIPELINE_ACTIONS}
            onSend={submit}
            onPrime={(text)=>setInput(text)}
            disabled={connected !== true}
          />
        </>
      ) : null}

      {/* WHAT IT DOES. Two columns, because the boundary is the thing agents get
          wrong — RealTalk's own capability list claims it manages deals, which
          is true of the API and false for this brokerage. Saying what it cannot
          do is as useful as saying what it can. */}
      <div style={{marginTop:'28px',display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'12px'}}>
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'14px 16px'}}>
          <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'8px'}}>WHAT IT CAN DO</div>
          <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.7}}>
            <div>Pull up a contact and everything on file — notes, texts, calls, tasks, and which of your emails they opened.</div>
            <div style={{marginTop:'6px'}}>Find people by stage, tag, or how recently they were active.</div>
            <div style={{marginTop:'6px'}}>Flag a signal you'd otherwise miss, like someone opening every send and never being called.</div>
            <div style={{marginTop:'6px'}}>Draft a note or a task for a contact — you read it and approve it before anything is written.</div>
          </div>
        </div>
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'14px 16px'}}>
          <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'8px'}}>WHAT IT CANNOT DO</div>
          <div style={{fontSize:'12.5px',color:'#555',fontFamily:FONT_BODY,lineHeight:1.7}}>
            <div>Write anything on its own. It can draft a note or a task, but you approve every one before it reaches Follow Up Boss.</div>
            <div style={{marginTop:'6px'}}>Send an email or a text. Follow Up Boss does not allow it through the API, so nothing leaves from here.</div>
            <div style={{marginTop:'6px'}}>Touch a transaction. Deals come from Brokermint and are read-only.</div>
          </div>
        </div>
      </div>

      <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginTop:'14px',lineHeight:1.6}}>
        Each question starts its own conversation. Past ones are under My cases in the sidebar.
      </div>
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Pipeline"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={PIPELINE_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={pipelineDisclaimer}
    />
  )
}

// ─── PIPELINE: CONVERSATION VIEW ─────────────────────────────────────────────
// One conversation, addressed by id at /pipeline/{id}. Reached from the landing
// composer or from My cases in the rail.
function PipelineView({ session, profile, conversationId, showToast, isMobile, navigateTo, isAdmin, signOut, initialMessage = '' }) {
  const shellRef = useRef(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [connected, setConnected] = usePipelineConnection(session.user.id)
  const autoSentRef = useRef(false)
  const sendMessageRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    supabase.from('pipeline_conversations').select('title').eq('id', conversationId).single()
      .then(({ data }) => { if (!cancelled && data) setTitle(data.title || '') })
    return () => { cancelled = true }
  }, [conversationId])

  useEffect(() => {
    let cancelled = false
    supabase.from('pipeline_messages').select('*')
      .eq('conversation_id', conversationId).order('created_at')
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) showToast('Could not load this conversation')
        setMessages(data || [])
        setLoading(false)
        // Arrived from the landing composer with the question already typed.
        // Fired once, after the existing transcript lands so nothing is lost.
        if (initialMessage && !autoSentRef.current) {
          autoSentRef.current = true
          sendMessageRef.current?.(initialMessage)
        }
      })
    return () => { cancelled = true }
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Non-streaming: pipeline-chat runs a tool loop, so there is nothing to stream
  // until the loop settles. A Pipeline answer is a few sentences, not a report.
  async function sendMessage(textArg) {
    const userMsg = (typeof textArg === 'string' ? textArg : input).trim()
    if (!userMsg || sending) return
    if (typeof textArg !== 'string') setInput('')

    const tempUserId = 'temp-user-' + Date.now()
    const tempAssistantId = 'temp-assistant-' + Date.now()
    setMessages(prev => [...prev,
      { id: tempUserId, role: 'user', content: userMsg, created_at: new Date().toISOString() },
      { id: tempAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true },
    ])
    setSending(true)
    shellRef.current?.scrollToBottom()

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pipeline-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ conversation_id: conversationId, user_message: userMsg }),
      })
      const data = await res.json()

      // The key was cleared, or was never valid. Drop the optimistic pair and
      // show the connect card rather than leaving a dead turn in the transcript.
      if (data?.needs_connection) {
        setMessages(prev => prev.filter(m => m.id !== tempUserId && m.id !== tempAssistantId))
        setConnected(false)
        setInput(userMsg)
        setSending(false)
        return
      }
      if (!res.ok || data?.error) throw new Error(data?.error || `Request failed: ${res.status}`)

      // The function writes both rows itself, so refetch rather than patching
      // the optimistic pair — the stored transcript is the source of truth.
      const { data: fresh } = await supabase.from('pipeline_messages')
        .select('*').eq('conversation_id', conversationId).order('created_at')
      if (fresh) setMessages(fresh)
    } catch (e) {
      setMessages(prev => prev.map(m =>
        m.id === tempAssistantId ? { ...m, content: e.message, error: true, streaming: false } : m
      ))
    }
    setSending(false)
  }

  sendMessageRef.current = sendMessage

  const renderMessage = (m, streamingRef) => (
    <div key={m.id} ref={typeof m.id === 'string' && m.id.startsWith('temp-assistant-') ? streamingRef : null}>
      <div style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
        {m.role === 'user' ? (
          <div style={{maxWidth:'80%',background:'#f4f4f2',color:'#1d2e42',borderRadius:'14px',padding:'12px 16px',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
            {m.content}
          </div>
        ) : (
          <div style={{width:'100%',color:m.error?'#C8102E':'#1d2e42',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,overflowWrap:'anywhere'}}>
            {m.error ? m.content : <PipelineAnswer meta={m.meta} conversationId={conversationId} showToast={showToast}>{renderCaseMarkdown(m.content)}</PipelineAnswer>}
            {m.streaming && !m.content && (
              <ThinkingRow label="Looking that up…" />
            )}
            {/* Only on a settled answer that actually carries a draft. The
                separator is the prompt's own signal that everything above it is
                meant to be pasted somewhere, so a reply without one has nothing
                to copy and shows no link. */}
            {!m.error && !m.streaming && m.content &&
              m.content.split('\n').some(l => /^-{3,}$/.test(l.trim())) && (
              <div style={{marginTop:'10px'}}>
                <button
                  onClick={async ()=>{
                    try {
                      await navigator.clipboard.writeText(dealdeskCopyText(m.content))
                      showToast('Copied to clipboard')
                    } catch { showToast('Copy failed — try selecting manually') }
                  }}
                  style={{background:'none',border:'none',padding:0,color:'#147efb',fontSize:'12px',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>
                  Copy to clipboard
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {m.role === 'assistant' && messages[messages.length - 1]?.id !== m.id && (
        <div style={{height:'0.5px',background:'#e8e8e8',marginTop:'18px'}} />
      )}
    </div>
  )

  const emptyState = (
    <div style={{paddingTop:'40px',color:'#999',fontSize:'14px',fontFamily:FONT_BODY}}>
      Ask about a contact, a stage, or who's engaging.
    </div>
  )

  const connectFooter = connected === false ? (
    <div style={{padding:isMobile?'0 20px 20px':'0 20px 28px',display:'flex',justifyContent:'center'}}>
      <div style={{width:'100%',maxWidth:PIPELINE_CONTENT_WIDTH}}>
        <PipelineConnectCard
          userId={session.user.id}
          fullName={profile?.full_name}
          isMobile={isMobile}
          showToast={showToast}
          onConnected={()=>setConnected(true)}
        />
      </div>
    </div>
  ) : null

  return (
    <CaseShell
      ref={shellRef}
      isMobile={isMobile}
      title={title || 'Pipeline'}
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={messages}
      renderMessage={renderMessage}
      emptyState={emptyState}
      contentWidth={PIPELINE_CONTENT_WIDTH}
      controls={null}
      showComposer={connected === true}
      composerValue={input}
      onComposerChange={e=>setInput(e.target.value)}
      onSubmit={()=>sendMessage()}
      composerPlaceholder="Ask about a contact, a stage, or who's engaging"
      sendDisabled={sending || !input.trim()}
      composerDisabled={sending}
      footer={<>{connectFooter}{pipelineDisclaimer}</>}
    />
  )
}

// ─── ADA: CONVERSATION VIEW ──────────────────────────────────────────────────
// ADA has no list page — a conversation is always addressed by id at /ada/{id}.
// The shell is the same rail the workspace uses; only the main column differs,
// which is a transcript rather than a landing screen.
function AdaCaseView({ session, profile, caseId, showToast, isMobile, navigateTo, isAdmin, signOut, initialMessage = '' }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')

  const shellRef = useRef(null)
  const autoSentRef = useRef(false)
  const sendMessageRef = useRef(null)

  // The case row, for its title. AdaCaseView did not read this row before the
  // shell existed — the transcript was the whole view — so this query is new.
  useEffect(() => {
    let cancelled = false
    supabase.from('ada_cases').select('title').eq('id', caseId).single()
      .then(({ data }) => { if (!cancelled && data) setTitle(data.title || '') })
    return () => { cancelled = true }
  }, [caseId])

  useEffect(() => {
    let cancelled = false
    supabase.from('ada_case_messages').select('*').eq('case_id', caseId).order('created_at')
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) showToast('Could not load this conversation')
        setMessages(data || [])
        setLoading(false)
        // A conversation opened from the workspace composer arrives with its
        // first question already typed. Fire it once, after the existing
        // transcript has landed so the optimistic rows are not overwritten.
        if (initialMessage && !autoSentRef.current) {
          autoSentRef.current = true
          sendMessageRef.current?.(initialMessage)
        }
      })
    return () => { cancelled = true }
  }, [caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scrolling belongs to the shell, which owns the transcript container.
  const scrollToBottom = () => shellRef.current?.scrollToBottom()
  const pinAssistantToTop = () => shellRef.current?.pinAssistantToTop()

  async function sendMessage(textArg) {
    const userMsg = (typeof textArg === 'string' ? textArg : input).trim()
    if (!userMsg || sending) return
    if (typeof textArg !== 'string') setInput('')

    const tempUserId = 'temp-user-' + Date.now()
    const tempAssistantId = 'temp-assistant-' + Date.now()
    const tempUserMsg = { id: tempUserId, role: 'user', content: userMsg, created_at: new Date().toISOString() }
    const tempAssistantMsg = { id: tempAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg])
    setSending(true)
    scrollToBottom()

    let pinnedOnFirstToken = false

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ada-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ case_id: caseId, user_message: userMsg }),
      })

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulatedText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (!payload) continue
          try {
            const evt = JSON.parse(payload)
            if (evt.text) {
              accumulatedText += evt.text
              const updatedText = accumulatedText
              setMessages(prev => prev.map(m =>
                m.id === tempAssistantId ? { ...m, content: updatedText } : m
              ))
              if (!pinnedOnFirstToken) { pinnedOnFirstToken = true; pinAssistantToTop() }
            }
            if (evt.error) throw new Error(evt.error)
            if (evt.done) {
              const { data: fresh } = await supabase.from('ada_case_messages').select('*').eq('case_id', caseId).order('created_at')
              if (fresh) setMessages(fresh)
            }
          } catch (parseErr) {
            if (String(parseErr).includes('Error')) throw parseErr
          }
        }
      }
    } catch (e) {
      // Surface the failure in the assistant bubble rather than dropping it, so
      // the question stays on screen next to what went wrong.
      setMessages(prev => prev.map(m =>
        m.id === tempAssistantId ? { ...m, content: e.message, error: true, streaming: false } : m
      ))
    }
    setSending(false)
  }

  sendMessageRef.current = sendMessage

  const disabled = sending || !input.trim()

  // Unchanged from the pre-shell version — the shell hands in the ref so ADA can
  // keep anchoring on the assistant bubble rather than the question.
  const renderMessage = (m, streamingRef) => (
    <div key={m.id} ref={typeof m.id === 'string' && m.id.startsWith('temp-assistant-') ? streamingRef : null}>
      <div style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
        {m.role === 'user' ? (
          <div style={{maxWidth:'80%',background:'#f4f4f2',color:'#1d2e42',borderRadius:'14px',padding:'12px 16px',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
            {m.content}
          </div>
        ) : (
          <div style={{width:'100%',color:m.error?'#C8102E':'#1d2e42',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
            {m.content}
            {m.streaming && !m.content && (
              <ThinkingRow label="Thinking…" />
            )}
          </div>
        )}
      </div>
      {m.role === 'assistant' && messages[messages.length - 1]?.id !== m.id && (
        <div style={{height:'0.5px',background:'#e8e8e8',marginTop:'18px'}} />
      )}
    </div>
  )

  const emptyState = (
    <div style={{paddingTop:'40px'}}>
      <div style={{fontSize:isMobile?'24px':'30px',fontWeight:900,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'1px',lineHeight:1.1,marginBottom:'8px'}}>
        Ask Ada
      </div>
      <div style={{fontSize:'15px',color:'#555',fontFamily:FONT_BODY}}>
        Arizona real estate law, ADRE rules, and fair housing.
      </div>
    </div>
  )

  return (
    <CaseShell
      ref={shellRef}
      isMobile={isMobile}
      title={title}
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={messages}
      renderMessage={renderMessage}
      emptyState={emptyState}
      controls={null}
      composerValue={input}
      onComposerChange={e=>setInput(e.target.value)}
      onSubmit={()=>sendMessage()}
      contentWidth={SHARED_CONTENT_WIDTH}
      composerPlaceholder="Ask anything about Arizona real estate law"
      sendDisabled={disabled}
      footer={null}
    />
  )
}

// The Hub page still inlines this same list; stage 4 removes that copy and this
// stays the single source.
const CANVA_TEMPLATES = [
  {label:'Logos',url:'https://www.canva.com/design/DAGwisFnujQ/m2zy7PjjFNoH3jGvfnxh1Q/view?utm_content=DAGwisFnujQ&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Business Cards',url:'https://www.canva.com/design/DAGwKOOXgOc/CB3ZjGd0hKGLTzgx6CW0bQ/view?utm_content=DAGwKOOXgOc&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Facebook Covers',url:'https://www.canva.com/design/DAGwdrXnpBw/LbwSgu87Vj8JuTVvpB0Stw/view?utm_content=DAGwdrXnpBw&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Social Media Content',url:'https://www.canva.com/design/DAGwYHdDwsY/DMhmdiqlGzZUPJdky6GumQ/view?utm_content=DAGwYHdDwsY&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Flyers',url:'https://www.canva.com/design/DAGwYI8jz44/m4ij329TiImWqGxeEzN4Dg/view?utm_content=DAGwYI8jz44&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Landscape For Sale Sign',url:'https://www.canva.com/design/DAGwh5NBwas/KGX-FA4XK3fEg9VwDhryNw/view?utm_content=DAGwh5NBwas&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Portrait For Sale Sign',url:'https://www.canva.com/design/DAGwhyaRThM/r2GOdMm9BYHZMMk4Dq1Nsg/view?utm_content=DAGwhyaRThM&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Email Signatures',url:'https://www.canva.com/design/DAGwYOP8ZWI/Lf9C0s-b9C6d9x532slcOg/view?utm_content=DAGwYOP8ZWI&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
  {label:'Property Advertisement',url:'https://www.canva.com/design/DAHBIy7xIew/AGQAzhqvY0Y8HdJa-Wq3Ew/view?utm_content=DAHBIy7xIew&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview'},
]

// Shared by every quiet sidebar group below.
const sidebarLabel = { fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'6px' }
const sidebarRow = { display:'block',padding:'6px 0',textDecoration:'none',color:'#1d2e42',fontSize:'12.5px',fontFamily:FONT_BODY }

// Four rows, then the rest behind a link. Purely presentational — CANVA_TEMPLATES
// is unchanged and every entry is still reachable.
function CanvaTemplatesBlock() {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? CANVA_TEMPLATES : CANVA_TEMPLATES.slice(0, 4)
  return (
    <div style={{marginTop:'16px'}}>
      <div style={sidebarLabel}>TEMPLATES</div>
      {shown.map((t,i) => (
        <a key={i} href={t.url} target="_blank" rel="noopener noreferrer" style={sidebarRow}>{t.label}</a>
      ))}
      {!expanded && CANVA_TEMPLATES.length > 4 && (
        <button onClick={()=>setExpanded(true)}
          style={{background:'none',border:'none',padding:'6px 0',fontSize:'11.5px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY,cursor:'pointer'}}>
          All {CANVA_TEMPLATES.length} templates
        </button>
      )}
    </div>
  )
}

// ─── CONTENT LIBRARY (own route) ─────────────────────────────────────────────
// Owns its content fetch and filter state outright. Dashboard used to run a
// second copy for the Hub page and the nav's new-content badge; both went with
// the Hub page and the nav, so this is the only reader of the content table now.
const LIBRARY_CONTENT_WIDTH = '1100px'

function ContentLibraryView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [visibleCount, setVisibleCount] = useState(12)

  const fetchContent = useCallback(async () => {
    const { data } = await supabase.from('content').select('*').order('created_at', { ascending: false })
    setItems(data || [])
    setLoading(false)
  }, [])
  useEffect(() => { fetchContent() }, [fetchContent])

  const lastSeen = profile?.last_seen ? new Date(profile.last_seen) : null
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

  const filtered = items.filter(item => {
    const matchType = filter === 'all' || item.type === filter
    const s = search.toLowerCase()
    const matchSearch = !search ||
      item.title.toLowerCase().includes(s) ||
      (item.content||'').toLowerCase().includes(s) ||
      (item.tag||'').toLowerCase().includes(s) ||
      (TYPE_CFG[item.type]?.label||'').toLowerCase().includes(s)
    return matchType && matchSearch
  })

  async function handleDelete(id) {
    await supabase.from('content').delete().eq('id', id)
    setSelected(null); showToast('Content deleted')
    fetchContent()
  }

  const summaryNode = (
    <div style={{display:'flex',justifyContent:'flex-end',minWidth:0}}>
      <div style={{...S.searchBox,position:'relative',maxWidth:'260px',width:'100%'}}>
        <input style={{...S.searchInput,width:'100%',paddingRight:search?'24px':undefined}} placeholder="Search content..."
          value={search} onChange={e=>setSearch(e.target.value)} />
        {search && (
          <button onClick={()=>setSearch('')} style={{position:'absolute',right:'8px',top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'#999',fontSize:'14px',cursor:'pointer',padding:'0 2px',lineHeight:1}}>✕</button>
        )}
      </div>
    </div>
  )

  const sidebarNode = (
    <div style={{width:isMobile?'100%':'260px',flexShrink:0}}>
      {/* Onboarding: a light card with a gold rule rather than a solid gold block. */}
      <a href="https://docs.google.com/forms/d/e/1FAIpQLSf1A5cEOHrQxxLxNfsDmXwyeB4ikybCNI9nKinTVcBQZllaVA/viewform?usp=header"
        target="_blank" rel="noopener noreferrer"
        style={{display:'block',textDecoration:'none',background:'white',border:'0.5px solid #e8e8e8',borderLeft:'2px solid #fdbf00',borderRadius:'10px',padding:'12px 14px'}}>
        <div style={{fontSize:'12.5px',fontWeight:600,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'5px'}}>New agent onboarding form</div>
        <div style={{fontSize:'11.5px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>Start here →</div>
      </a>

      <CanvaTemplatesBlock />

      <div style={{marginTop:'16px'}}>
        <div style={sidebarLabel}>FORMS &amp; BOOKING</div>
        <a href="https://docs.google.com/forms/d/e/1FAIpQLScydf3NbnVkprUm_4gHKFyHgbVA2vAUvwcWNBZ3SLP6ZsKZpg/viewform"
          target="_blank" rel="noopener noreferrer" style={sidebarRow}>Commission disbursement</a>
        <a href="https://tidycal.com/joshmarquez/podcast-room"
          target="_blank" rel="noopener noreferrer" style={sidebarRow}>Reserve podcast room</a>
        {/* Still a link out to the custom GPT. It moves when the real tool exists. */}
        <a href="https://chatgpt.com/g/g-684a1fb2871481918f4e0d3b882baa03-homepros-dealdesk-a-i"
          target="_blank" rel="noopener noreferrer" style={sidebarRow}>Deal Desk AI</a>
      </div>

      <AvanceCard />
    </div>
  )

  const listNode = (
    <div>
      <div style={{display:'flex',gap:'8px',flexWrap:isMobile?'nowrap':'wrap',overflowX:isMobile?'auto':'visible',paddingBottom:isMobile?'8px':0,marginBottom:'16px'}}>
        {FILTERS.map(f => {
          const sel = filter === f.key
          return (
            <button key={f.key}
              onClick={()=>{setFilter(f.key);setVisibleCount(12)}}
              style={{border:`0.5px solid ${sel ? '#147efb' : '#e8e8e8'}`,borderRadius:'18px',padding:'5px 12px',
                fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,whiteSpace:'nowrap',flexShrink:0,
                background:sel ? '#f4f4f2' : 'transparent',color:sel ? '#1d2e42' : '#666',
                cursor:'pointer',transition:'all .15s'}}>
              {f.label}
            </button>
          )
        })}
      </div>

      {search && !loading && filtered.length > 0 && (
        <div style={{fontSize:'13px',color:'#999',marginBottom:'12px',fontFamily:FONT_BODY}}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "<strong style={{color:'#555'}}>{search}</strong>"
        </div>
      )}

      <div style={{display:'flex',gap:'24px',alignItems:'flex-start',flexDirection:isMobile?'column':'row'}}>
        <div style={{flex:1,minWidth:0,width:isMobile?'100%':undefined}}>
          {/* 190px floor puts four cards across. The grid gets 816px — the
              1100px shell column less the 260px sidebar and the 24px row gap —
              so four columns need a floor at or under 192px and five need 150px
              or less. 190 sits inside that band rather than on its edge, where
              subpixel rounding decides. 200px would drop it back to three. */}
          <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill,minmax(190px,1fr))',gap:'16px'}}>
            {loading ? (
              <div style={S.emptyState}><p style={{color:'#999'}}>Loading content...</p></div>
            ) : filtered.length === 0 ? (
              <>
                <div style={S.emptyState}>
                  {search ? (
                    <>
                      <h3 style={{fontSize:'20px',color:'#999',fontFamily:FONT_HEAD,textTransform:'uppercase',letterSpacing:'1px',marginBottom:'12px'}}>
                        No content found for "{search}"
                      </h3>
                      <button onClick={()=>setSearch('')} style={{...S.btnPrimary,width:'auto',padding:'10px 24px'}}>CLEAR SEARCH</button>
                    </>
                  ) : (
                    <h3 style={{fontSize:'20px',color:'#999',fontFamily:FONT_HEAD,textTransform:'uppercase',letterSpacing:'1px'}}>
                      {isAdmin ? 'Upload your first piece of content' : 'No content yet — check back soon'}
                    </h3>
                  )}
                </div>
              </>
            ) : (
              <>
                {filtered.slice(0, visibleCount).map(item => (
                  <ContentCard key={item.id} item={item} userId={session.user.id} isMobile={isMobile}
                    isNew={lastSeen ? (new Date(item.created_at) > lastSeen && new Date(item.created_at) > sevenDaysAgo) : false}
                    onClick={()=>setSelected(item)}
                    onToast={showToast} />
                ))}
              </>
            )}
          </div>
          {filtered.length > visibleCount && (
            <div style={{textAlign:'center',padding:'24px 0'}}>
              <button style={{...S.btnPrimary,width:'auto',padding:'12px 32px'}} onClick={()=>setVisibleCount(c=>c+12)}>
                LOAD MORE ({filtered.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
        {sidebarNode}
      </div>
    </div>
  )

  return (
    <>
      <CaseShell
        isMobile={isMobile}
        title="Content"
        summary={summaryNode}
        setupOpen={false}
        onToggleSetup={null}
        setup={null}
        loading={loading}
        messages={[]}
        renderMessage={()=>null}
        emptyState={listNode}
        contentWidth={LIBRARY_CONTENT_WIDTH}
        controls={null}
        showComposer={false}
        composerValue=""
        onComposerChange={()=>{}}
        onSubmit={()=>{}}
        composerPlaceholder=""
        sendDisabled
        footer={null}
      />
      {selected && (
        <DetailModal item={selected} isAdmin={isAdmin} isMobile={isMobile} userId={session.user.id}
          onClose={()=>setSelected(null)} onDelete={handleDelete}
          onToast={showToast} />
      )}
    </>
  )
}

// ─── VENDORS (own route) ─────────────────────────────────────────────────────
const VENDORS_CONTENT_WIDTH = '1100px'

function VendorsView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [vendors, setVendors] = useState([])
  const [vendorReviews, setVendorReviews] = useState({})
  const [vendorSearch, setVendorSearch] = useState('')
  const [vendorCategory, setVendorCategory] = useState('all')
  const [expandedVendor, setExpandedVendor] = useState(null)
  const [reviewDrafts, setReviewDrafts] = useState({})
  const [loading, setLoading] = useState(true)

  const loadVendors = useCallback(async () => {
    const [vRes, rRes] = await Promise.all([
      supabase.from('vendors').select('*').order('name'),
      supabase.from('vendor_reviews').select('*').order('created_at', { ascending: false })
    ])
    setVendors(vRes.data || [])
    const byVendor = {}
    ;(rRes.data || []).forEach(r => { if (!byVendor[r.vendor_id]) byVendor[r.vendor_id] = []; byVendor[r.vendor_id].push(r) })
    setVendorReviews(byVendor)
    setLoading(false)
  }, [])
  useEffect(() => { loadVendors() }, [loadVendors])

  function avgRating(vendorId) {
    const reviews = vendorReviews[vendorId] || []
    if (reviews.length === 0) return null
    return (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1)
  }

  const summaryNode = (
    <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',minWidth:0}}>
      <input value={vendorSearch} onChange={e=>setVendorSearch(e.target.value)} placeholder="Search vendors..." style={{flex:1,minWidth:0,maxWidth:'220px',padding:'8px 12px',border:`0.5px solid ${RAIL_BORDER}`,borderRadius:'8px',fontFamily:FONT_BODY,fontSize:'13px'}} />
      <select value={vendorCategory} onChange={e=>setVendorCategory(e.target.value)} style={{flexShrink:0,padding:'8px 12px',border:`0.5px solid ${RAIL_BORDER}`,borderRadius:'8px',fontFamily:FONT_BODY,fontSize:'13px',background:'white',cursor:'pointer'}}>
        <option value="all">All Categories</option>
        {VENDOR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  )

  let vf = vendors
  if (vendorCategory !== 'all') vf = vf.filter(v => v.category === vendorCategory)
  if (vendorSearch.trim()) { const q = vendorSearch.toLowerCase(); vf = vf.filter(v => v.name.toLowerCase().includes(q) || (v.company||'').toLowerCase().includes(q) || v.category.toLowerCase().includes(q) || (v.subtitle||'').toLowerCase().includes(q)) }

  const listNode = (
    <div>
      <div style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY,marginBottom:'16px'}}>HomePros-trusted vendors. Rate after every use.</div>
      {vf.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px',color:'#999',fontFamily:FONT_BODY}}>{vendors.length === 0 ? 'No vendors added yet. Your broker is curating this list.' : 'No vendors match your search.'}</div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'repeat(auto-fill,minmax(300px,1fr))',gap:'16px'}}>
          {vf.map(v => {
            const reviews = vendorReviews[v.id] || []
            const rating = avgRating(v.id)
            const isExp = expandedVendor === v.id
            const latest = reviews[0]
            return (
              <div key={v.id} style={{background:'white',borderRadius:'12px',padding:'18px',border:'1px solid #e8e8e8',fontFamily:FONT_BODY,display:'flex',flexDirection:'column',gap:'10px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'8px'}}>
                  <span style={{padding:'3px 10px',borderRadius:'4px',fontSize:'10px',fontWeight:700,letterSpacing:'1px',fontFamily:FONT_HEAD,background:'rgba(20,126,251,0.1)',color:'#147efb',border:'1px solid rgba(20,126,251,0.3)'}}>{v.category.toUpperCase()}</span>
                  {rating && <span style={{fontSize:'12px',color:'#666'}}>{'★'.repeat(Math.round(parseFloat(rating)))}{'☆'.repeat(5-Math.round(parseFloat(rating)))} <span style={{color:'#999'}}>({reviews.length})</span></span>}
                </div>
                <div>
                  <div style={{fontSize:'15px',fontWeight:700,color:'#222'}}>{v.name}</div>
                  {v.subtitle && <div style={{fontSize:'12px',color:'#888',fontFamily:FONT_BODY,marginTop:'2px'}}>{v.subtitle}</div>}
                  {v.company && <div style={{fontSize:'12px',color:'#666',marginTop:'2px'}}>{v.company}</div>}
                </div>
                {latest && !isExp && latest.review_text && <div style={{fontSize:'12px',color:'#555',fontStyle:'italic',borderLeft:'2px solid #fdbf00',paddingLeft:'10px'}}>"{latest.review_text}"</div>}
                <div style={{display:'flex',gap:'8px',marginTop:'4px'}}>
                  {v.phone && <a href={`tel:${v.phone}`} style={{fontSize:'11px',color:'#147efb',textDecoration:'none',fontWeight:600}}>📞 {v.phone}</a>}
                  {v.email && <a href={`mailto:${v.email}`} style={{fontSize:'11px',color:'#147efb',textDecoration:'none',fontWeight:600}}>✉️ Email</a>}
                </div>
                <button onClick={()=>setExpandedVendor(isExp?null:v.id)} style={{background:'transparent',border:'1px solid #e8e8e8',borderRadius:'6px',padding:'8px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px',color:'#666'}}>{isExp?'COLLAPSE':'VIEW REVIEWS & RATE'}</button>
                {isExp && (
                  <div style={{borderTop:'1px solid #f0f0f0',paddingTop:'12px',display:'flex',flexDirection:'column',gap:'12px'}}>
                    <div style={{background:'#f9fafb',borderRadius:'8px',padding:'12px'}}>
                      <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'1px',color:'#666',marginBottom:'8px',fontFamily:FONT_HEAD}}>RATE THIS VENDOR</div>
                      <div style={{display:'flex',gap:'4px',marginBottom:'8px'}}>
                        {[1,2,3,4,5].map(n => <button key={n} onClick={()=>setReviewDrafts({...reviewDrafts,[v.id]:{...(reviewDrafts[v.id]||{}),rating:n}})} style={{background:'none',border:'none',cursor:'pointer',fontSize:'20px',color:(reviewDrafts[v.id]?.rating||0)>=n?'#fdbf00':'#ddd'}}>★</button>)}
                      </div>
                      <textarea value={reviewDrafts[v.id]?.text||''} onChange={e=>setReviewDrafts({...reviewDrafts,[v.id]:{...(reviewDrafts[v.id]||{}),text:e.target.value}})} placeholder="Optional notes..." style={{width:'100%',minHeight:'60px',padding:'8px',border:'1px solid #e8e8e8',borderRadius:'6px',fontFamily:FONT_BODY,fontSize:'12px',resize:'vertical',boxSizing:'border-box'}} />
                      <button onClick={async()=>{
                        const d = reviewDrafts[v.id]; if (!d?.rating) { showToast('Pick a star rating first'); return }
                        const { error: rErr } = await supabase.from('vendor_reviews').insert({vendor_id:v.id,agent_id:session.user.id,rating:d.rating,review_text:d.text||null})
                        if (rErr) { showToast('Failed: '+rErr.message); return }
                        setReviewDrafts({...reviewDrafts,[v.id]:{}})
                        loadVendors()
                        showToast('Review submitted')
                      }} style={{...S.btnPrimary,marginTop:'8px',fontSize:'12px',padding:'8px 16px'}}>Submit Review</button>
                    </div>
                    {reviews.length > 0 && (
                      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                        <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'1px',color:'#666',fontFamily:FONT_HEAD}}>ALL REVIEWS ({reviews.length})</div>
                        {reviews.map(r => (
                          <div key={r.id} style={{background:'#f9fafb',borderRadius:'8px',padding:'10px'}}>
                            <div style={{fontSize:'12px',color:'#fdbf00',marginBottom:'4px'}}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div>
                            {r.review_text && <div style={{fontSize:'12px',color:'#444',lineHeight:1.5}}>{r.review_text}</div>}
                            <div style={{fontSize:'10px',color:'#999',marginTop:'4px'}}>{timeAgo(r.created_at)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Vendors"
      summary={summaryNode}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={VENDORS_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── FROM THE BROKER (own route) ─────────────────────────────────────────────
// The newsletter itself is read-only: it is published as a static public JSON
// feed on the Avance site, and no part of it lives in Supabase. The one write
// here is unrelated to the content — openIssue stamps profiles.last_broker_issue
// so the rail's NEW badge can clear. Both views — the issue list and the reading
// view — are this one component switched by `openSlug`, so there is no
// /broker/:slug route in parseCurrentPath.
//
// Field notes, from reading the feed rather than the spec:
//   number  is a STRING ('010'), never a number — no arithmetic on it.
//   lead    is a BOOLEAN flag (true on the opening section of every issue) or
//           null. It is NOT lead text. A string is still handled below in case
//           the publication starts sending one.
//   sources entries are { label, url }.
// Any block whose `type` — or body item whose `kind` — is unrecognized is
// skipped, so a new shape added upstream degrades to nothing rather than throws.
const BROKER_CONTENT_WIDTH = '860px'
const BROKER_ORIGIN = 'https://avanceschool.com'
const BROKER_FEED_URL = `${BROKER_ORIGIN}/publication/issues.json`

// The html fields are trusted hand-authored markup, but they are authored for
// the Avance site: 12 of the feed's 18 hrefs are root-relative (/meet-ada,
// /about/josh-marquez). Left alone on the Hub domain, a single-segment one falls
// through App's funnel catch-all (App.js:433) and a two-segment one lands on
// 'invalid' and bounces to the workspace. Rewriting the origin keeps them
// pointing at the publication; target=_blank keeps the Hub SPA mounted.
function brokerFixHtml(html) {
  if (typeof html !== 'string') return ''
  return html.replace(/<a\s+([^>]*?)>/gi, (_tag, attrs) => {
    let out = attrs.replace(/href="(\/[^"]*)"/i, (_m, path) => `href="${BROKER_ORIGIN}${path}"`)
    if (!/\btarget=/i.test(out)) out += ' target="_blank" rel="noopener noreferrer"'
    return `<a ${out}>`
  })
}

// Injected markup can't be reached by inline styles, so the prose typography
// lives in one scoped style block — the same approach Dashboard already uses for
// its keyframes at App.js:2132. Only the tags the feed actually emits are
// covered: p, strong, em, a, br, ul, li.
const BROKER_PROSE_CSS = `
.bkr p{margin:0 0 12px;font-size:15px;line-height:1.75;color:#3d4a5c;font-family:${FONT_BODY};}
.bkr p:last-child{margin-bottom:0;}
.bkr a{color:#1d2e42;text-decoration:underline;text-underline-offset:2px;}
.bkr strong{color:#1d2e42;font-weight:700;}
.bkr em{font-style:italic;}
.bkr ul{margin:0 0 12px;padding-left:20px;}
.bkr li{font-size:15px;line-height:1.75;color:#3d4a5c;margin-bottom:6px;font-family:${FONT_BODY};}
.bkr-dark p,.bkr-dark li{color:rgba(255,255,255,0.88);}
.bkr-dark strong{color:#ffffff;}
.bkr-dark a{color:#fdbf00;}
`

const brokerEyebrow = { fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD }
const brokerCard = { background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px' }

function BrokerView({ session, profile, setProfile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [issues, setIssues] = useState([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [openSlug, setOpenSlug] = useState(null)

  // One fetch on mount. Public feed, CORS-open (access-control-allow-origin: *),
  // so a bare fetch with no headers is all this needs. The cancelled flag keeps
  // a late response from setting state on an unmounted view.
  useEffect(() => {
    let cancelled = false
    fetch(BROKER_FEED_URL)
      .then(res => { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json() })
      .then(data => { if (!cancelled) { setIssues(Array.isArray(data) ? data : []); setLoading(false) } })
      .catch(() => { if (!cancelled) { setFailed(true); setLoading(false) } })
    return () => { cancelled = true }
  }, [])

  // A slug that no longer resolves falls back to the list rather than rendering
  // an empty reading view.
  const open = openSlug ? issues.find(i => i && i.slug === openSlug) : null

  // Opening the NEWEST issue is what clears the rail's NEW badge. Opening an
  // older one deliberately does not — the newest is still unread, and writing
  // an older slug would mark it read.
  //
  // The update is awaited because a Supabase builder is a lazy thenable: left
  // un-awaited it issues no request at all and fails silently, which is what
  // scripts/check-supabase-awaits.js exists to block. Reading is never gated on
  // it — the view switches first, and a failure only logs.
  //
  // On success the in-memory profile is patched rather than refetched. The rail
  // badge is derived from profile.last_broker_issue, so without this the badge
  // would stay lit until the next page load; a refetch would cost a round trip
  // to learn a value we just wrote.
  async function openIssue(slug) {
    setOpenSlug(slug)
    const newest = issues[0] && issues[0].slug
    if (!newest || slug !== newest || profile?.last_broker_issue === slug) return
    try {
      const { error } = await supabase.from('profiles')
        .update({ last_broker_issue: slug })
        .eq('id', session.user.id)
      if (error) { console.warn('[broker last_issue]', error.message); return }
      setProfile(p => ({ ...p, last_broker_issue: slug }))
    } catch (e) {
      console.warn('[broker last_issue]', e)
    }
  }

  function renderBodyItem(item, key) {
    if (!item || typeof item.html !== 'string') return null
    if (item.kind === 'prose') {
      return <div key={key} className="bkr" dangerouslySetInnerHTML={{__html: brokerFixHtml(item.html)}} />
    }
    if (item.kind === 'callout') {
      // Only 'cream' and 'dark' exist in the feed today. An unknown variant
      // still renders — as cream — because dropping authored copy is worse
      // than showing it in the default treatment.
      const dark = item.variant === 'dark'
      return (
        <div key={key} className={dark ? 'bkr bkr-dark' : 'bkr'}
          style={{borderRadius:'12px',padding:'18px 20px',
            background: dark ? '#1d2e42' : '#fffdf5',
            border: dark ? '1px solid #1d2e42' : '1px solid #fdbf00'}}
          dangerouslySetInnerHTML={{__html: brokerFixHtml(item.html)}} />
      )
    }
    return null
  }

  function renderBlock(block, key) {
    if (!block) return null

    if (block.type === 'intro') {
      if (typeof block.html !== 'string') return null
      // Gold left rule on a white card — the same standfirst treatment the
      // content sidebar's onboarding card uses at App.js:7601.
      return (
        <div key={key} className="bkr"
          style={{...brokerCard,borderLeft:'2px solid #fdbf00',padding:'18px 20px'}}
          dangerouslySetInnerHTML={{__html: brokerFixHtml(block.html)}} />
      )
    }

    if (block.type === 'section') {
      const body = Array.isArray(block.body) ? block.body : []
      // lead arrives as true/null, not as copy. Treat true as the flag it is;
      // render a string as a standfirst only if one ever shows up.
      const leadText = typeof block.lead === 'string' && block.lead.trim() ? block.lead.trim() : null
      return (
        <div key={key} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {block.lead === true && <div style={{...brokerEyebrow,color:'#fdbf00'}}>Lead story</div>}
          {typeof block.headline === 'string' && block.headline && (
            <h3 style={{margin:0,fontSize:'20px',fontWeight:700,lineHeight:1.25,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>
              {block.headline}
            </h3>
          )}
          {leadText && (
            <div style={{fontSize:'14.5px',lineHeight:1.6,color:'#6b7280',fontFamily:FONT_BODY}}>{leadText}</div>
          )}
          {body.map((item, n) => renderBodyItem(item, n))}
        </div>
      )
    }

    if (block.type === 'signoff') {
      const sig = block.signature && typeof block.signature === 'object' ? block.signature : null
      const lines = sig && Array.isArray(sig.lines) ? sig.lines.filter(l => typeof l === 'string') : []
      return (
        <div key={key} style={{...brokerCard,padding:'20px',display:'flex',flexDirection:'column',gap:'12px'}}>
          {typeof block.headline === 'string' && block.headline && (
            <h3 style={{margin:0,fontSize:'18px',fontWeight:700,lineHeight:1.3,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>
              {block.headline}
            </h3>
          )}
          {typeof block.html === 'string' && (
            <div className="bkr" dangerouslySetInnerHTML={{__html: brokerFixHtml(block.html)}} />
          )}
          {sig && (lines.length > 0 || typeof sig.nameHtml === 'string') && (
            <div style={{borderTop:'0.5px solid #e8e8e8',paddingTop:'12px'}}>
              {typeof sig.nameHtml === 'string' && (
                <div className="bkr" style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'4px'}}
                  dangerouslySetInnerHTML={{__html: brokerFixHtml(sig.nameHtml)}} />
              )}
              {lines.map((line, n) => (
                <div key={n} style={{fontSize:'13px',lineHeight:1.6,color:'#6b7280',fontFamily:FONT_BODY}}>{line}</div>
              ))}
            </div>
          )}
        </div>
      )
    }

    return null // unrecognized block type
  }

  // ── LIST VIEW ──
  const listNode = (
    <div>
      <div style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY,marginBottom:'16px'}}>
        My weekly note on the tools, the rules, and what actually changed this week.
      </div>
      {issues.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px',color:'#999',fontFamily:FONT_BODY}}>
          No issues published yet.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {issues.map(issue => {
            if (!issue || !issue.slug) return null
            return (
              <button key={issue.slug} onClick={()=>openIssue(issue.slug)}
                style={{...brokerCard,padding:'18px 20px',textAlign:'left',cursor:'pointer',width:'100%',
                  display:'flex',flexDirection:'column',gap:'6px',fontFamily:FONT_BODY,transition:'background .15s'}}
                onMouseEnter={e=>e.currentTarget.style.background=RAIL_BG}
                onMouseLeave={e=>e.currentTarget.style.background='white'}>
                <div style={brokerEyebrow}>
                  Issue {issue.number}{issue.dateLabel ? ` · ${issue.dateLabel}` : ''}
                </div>
                <div style={{fontSize:'17px',fontWeight:700,lineHeight:1.3,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>
                  {issue.title}
                </div>
                {issue.dek && (
                  <div style={{fontSize:'14px',lineHeight:1.6,color:'#6b7280'}}>{issue.dek}</div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )

  // ── READING VIEW ──
  const sources = open && Array.isArray(open.sources)
    ? open.sources.filter(s => s && typeof s.url === 'string')
    : []

  const readingNode = open && (
    <div>
      <button onClick={()=>setOpenSlug(null)}
        style={{background:'none',border:'none',padding:'0 0 16px',cursor:'pointer',
          fontSize:'13px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>
        ← Back to all issues
      </button>

      <div style={{marginBottom:'24px'}}>
        <div style={{...brokerEyebrow,marginBottom:'8px'}}>
          Issue {open.number}{open.dateLabel ? ` · ${open.dateLabel}` : ''}
        </div>
        <h2 style={{margin:'0 0 10px',fontSize:'27px',fontWeight:700,lineHeight:1.2,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>
          {open.title}
        </h2>
        {open.dek && (
          <div style={{fontSize:'15.5px',lineHeight:1.65,color:'#6b7280',fontFamily:FONT_BODY}}>{open.dek}</div>
        )}
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:'28px'}}>
        {(Array.isArray(open.blocks) ? open.blocks : []).map((b, n) => renderBlock(b, n))}
      </div>

      {sources.length > 0 && (
        <div style={{marginTop:'32px',paddingTop:'20px',borderTop:'0.5px solid #e8e8e8'}}>
          <div style={{...brokerEyebrow,marginBottom:'10px'}}>Sources</div>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            {sources.map((s, n) => (
              <a key={n} href={s.url} target="_blank" rel="noopener noreferrer"
                style={{fontSize:'13px',lineHeight:1.6,color:'#147efb',fontFamily:FONT_BODY,textDecoration:'none'}}>
                {s.label || s.url}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // CaseShell paints nothing but "Loading..." while `loading` is true
  // (App.js:5516), so the failure message belongs here, after it clears.
  const failedNode = (
    <div style={{textAlign:'center',padding:'40px',color:'#999',fontFamily:FONT_BODY,fontSize:'14px'}}>
      The newsletter couldn't load right now. Try again in a moment.
    </div>
  )

  const bodyNode = (
    <div>
      <style>{BROKER_PROSE_CSS}</style>
      {failed ? failedNode : open ? readingNode : listNode}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="From the broker"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={BROKER_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── WEEKLY EMAILS (list only) ───────────────────────────────────────────────
// The list of newsletter issues an agent can send to their own database. Unlike
// BrokerView above — whose issues come from a public JSON feed on the Avance
// site — these live in Supabase, in public.weekly_emails.
//
// NO published FILTER IN THE QUERY, deliberately. RLS already hides unpublished
// rows from an agent, so adding .eq('published', true) here would be a second
// copy of the same rule in a place that cannot enforce it — and it would hide
// an admin's own drafts, which they are meant to see. The Draft chip below is
// what tells an admin which rows an agent cannot see yet.
//
// TWO VIEWS, ONE COMPONENT, switched by `openId` — the same shape BrokerView
// uses for openSlug, and for the same reason: there is no /weekly-emails/:id
// route and parseCurrentPath knows nothing about an issue id.
const WEEKLY_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

// The preview is an iframe, never dangerouslySetInnerHTML. These rows hold a
// FULL EMAIL DOCUMENT — its own <body> background, its own table widths, often
// its own <style> — and injected into the page it would fight the app's layout
// and leak its CSS into every screen after it. An iframe gives it its own
// document, and sandbox="" with no allow tokens gives it no script, no forms,
// no navigation, and a null origin.
const WEEKLY_PREVIEW_HEIGHT = '620px'

// MONTH AND YEAR ONLY, never a day. Agents grab an issue and send it whenever
// their week allows, so a specific day is wrong the moment it is published —
// and it is the recipient who would see the discrepancy, not the sender.
// issue_date keeps its full date in the column; this is display only.
//
// The 'T00:00:00' append still matters even without a day on screen. A DATE
// column arrives as 'YYYY-MM-DD' and parses bare as UTC midnight, which is the
// previous day everywhere west of Greenwich — so an issue dated the 1st would
// render as the PREVIOUS MONTH. The suffix forces local parsing, the same idiom
// linksDate (App.js:474) and the events views already use.
function weeklyDateLabel(s) {
  if (!s) return ''
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00')
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

// PASTE CHECK. These emails are pasted out of a mail designer, and the failure
// that actually happens is a truncated paste — the clipboard cut mid-document
// and the closing tags never arrived. It renders fine in the preview and breaks
// in the recipient's inbox, so it is caught here rather than after sending.
//
// Only the five tags that carry layout: an unclosed <td> collapses a table, an
// unclosed <font> bleeds a colour through the rest of the message. A count, not
// a parse — the browser's parser silently repairs what it reads, which is
// exactly the repair a mail client will not do.
const WEEKLY_BALANCE_TAGS = ['font', 'span', 'table', 'tr', 'td']
const WEEKLY_MERGE_FIELD = '%contact_first_name%'

function weeklyTagBalance(html) {
  const s = String(html || '')
  return WEEKLY_BALANCE_TAGS.map(tag => {
    // '<td' matches '<td ' and '<td>' and nothing else in this set — no tag here
    // is a prefix of another. Case-insensitive because designers emit both.
    const opens = (s.match(new RegExp('<' + tag + '\\b', 'gi')) || []).length
    const closes = (s.match(new RegExp('</' + tag + '\\b', 'gi')) || []).length
    return { tag, opens, closes, ok: opens === closes }
  })
}

function WeeklyEmailsView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)
  const [lang, setLang] = useState('en')
  const [copying, setCopying] = useState(false)

  // ── Admin editor state. Every field is held for BOTH languages at once, so
  //    switching the pill never costs somebody their typing.
  const [formId, setFormId] = useState(null)   // null = new issue, else editing
  const [fLang, setFLang] = useState('en')
  const [fIssueNumber, setFIssueNumber] = useState('')
  const [fIssueDate, setFIssueDate] = useState('')
  const [fSubjectEn, setFSubjectEn] = useState('')
  const [fPreviewEn, setFPreviewEn] = useState('')
  const [fHtmlEn, setFHtmlEn] = useState('')
  const [fSubjectEs, setFSubjectEs] = useState('')
  const [fPreviewEs, setFPreviewEs] = useState('')
  const [fHtmlEs, setFHtmlEs] = useState('')
  const [saving, setSaving] = useState(false)

  // MarketReportPage's read shape (App.js:9149-9156): destructure { data, error },
  // log rather than toast on a read failure, coalesce to [] so a refusal renders
  // as an empty list rather than a crash, and clear loading either way.
  const fetchRows = useCallback(async () => {
    const { data, error } = await supabase.from('weekly_emails')
      .select('*')
      .order('issue_date', { ascending: false })
      .order('issue_number', { ascending: false })
    if (error) console.error('weekly emails query error:', error)
    setRows(data || [])
    setLoading(false)
  }, [])
  useEffect(() => { fetchRows() }, [fetchRows])

  // A row that no longer resolves falls back to the list rather than rendering
  // an empty reading view — the same guard BrokerView applies to openSlug.
  const open = openId ? rows.find(r => r && r.id === openId) : null

  // Language always resets to English on open. Carrying the previous row's
  // choice forward would land somebody on a Spanish pill for a row that has no
  // Spanish, and the pill that would let them back out is not rendered.
  function openIssue(id) {
    setOpenId(id)
    setLang('en')
  }

  // 'es' is only reachable while html_es exists — the pill is not rendered
  // otherwise — but the guard is here as well so a stale value can never select
  // a body that is null.
  const showEs = lang === 'es' && !!open?.html_es
  const openSubject = open ? (showEs ? (open.subject_es || open.subject_en) : open.subject_en) : ''
  const openHtml = open ? (showEs ? open.html_es : open.html_en) : ''

  // Subject only, and deliberately NOT logged. The log records that an agent
  // took the email; copying the subject line to retype it somewhere is not that.
  async function copySubject() {
    const ok = await copyToClipboard(openSubject || '')
    if (!ok) { showToast('Copy failed — try selecting manually', true); return }
    showToast('Copied')
  }

  // confirmCopy's ordering (App.js:16277-16309): write to the clipboard first,
  // log only once that succeeded. A row written before the write would claim the
  // agent took something they did not get. A log that fails after a successful
  // copy is the lesser error, so it is reported rather than swallowed.
  async function copyHtml() {
    if (!open || copying) return
    setCopying(true)
    const ok = await copyToClipboard(openHtml || '')
    if (!ok) {
      showToast('Copy failed — try selecting manually', true)
      setCopying(false)
      return
    }
    const { error: logErr } = await supabase.from('weekly_email_copies').insert({
      user_id: session.user.id,
      weekly_email_id: open.id,
      lang: showEs ? 'es' : 'en',
    })
    if (logErr) {
      console.error('weekly email copy log failed:', logErr.message)
      showToast('Copied — but the record failed to save')
    } else {
      showToast('Copied')
    }
    setCopying(false)
  }

  // ── ADMIN EDITOR ──────────────────────────────────────────────────────────
  const nextIssueNumber = rows.length
    ? Math.max(...rows.map(r => Number(r.issue_number) || 0)) + 1
    : 1

  // Prefilled once the first load lands, and only while the field is still
  // untouched — retyping over a prefill is fine, having it overwritten is not.
  useEffect(() => {
    if (!loading && formId === null && fIssueNumber === '') setFIssueNumber(String(nextIssueNumber))
  }, [loading, formId, fIssueNumber, nextIssueNumber])

  function resetForm(nextNum) {
    setFormId(null)
    setFLang('en')
    setFIssueNumber(String(nextNum))
    setFIssueDate('')
    setFSubjectEn(''); setFPreviewEn(''); setFHtmlEn('')
    setFSubjectEs(''); setFPreviewEs(''); setFHtmlEs('')
  }

  function editIssue(r) {
    setFormId(r.id)
    setFLang('en')
    setFIssueNumber(String(r.issue_number ?? ''))
    setFIssueDate(r.issue_date ? String(r.issue_date).slice(0, 10) : '')
    setFSubjectEn(r.subject_en || ''); setFPreviewEn(r.preview_en || ''); setFHtmlEn(r.html_en || '')
    setFSubjectEs(r.subject_es || ''); setFPreviewEs(r.preview_es || ''); setFHtmlEs(r.html_es || '')
  }

  const adminEs = fLang === 'es'
  const adminHtml = adminEs ? fHtmlEs : fHtmlEn
  const balance = weeklyTagBalance(adminHtml)
  const badTags = balance.filter(b => !b.ok).map(b => b.tag)
  const balanced = badTags.length === 0
  const hasMergeField = String(adminHtml || '').includes(WEEKLY_MERGE_FIELD)

  // Publish is gated on balance; Save draft never is. A draft is the place to
  // park a paste that is still wrong.
  const canPublish = balanced && !saving

  async function saveIssue(publish) {
    if (saving) return
    const num = parseInt(fIssueNumber, 10)
    if (!Number.isFinite(num)) { showToast('Issue number is required', true); return }
    if (!fIssueDate) { showToast('Issue date is required', true); return }
    if (!fSubjectEn.trim() || !fHtmlEn.trim()) { showToast('English needs a subject and HTML', true); return }

    // The table's check constraint requires subject_es and html_es to be both
    // null or both present. Refused here with a message rather than silently
    // nulling the half that was filled in — that would throw away typing and
    // report success.
    const esSubject = fSubjectEs.trim()
    const esHtml = fHtmlEs.trim()
    if (!!esSubject !== !!esHtml) {
      showToast('Spanish needs both a subject and HTML, or neither', true)
      return
    }
    const hasEs = !!esSubject && !!esHtml

    setSaving(true)
    const payload = {
      issue_number: num,
      issue_date: fIssueDate,
      subject_en: fSubjectEn.trim(),
      preview_en: fPreviewEn.trim() || null,
      html_en: fHtmlEn,
      subject_es: hasEs ? esSubject : null,
      preview_es: hasEs ? (fPreviewEs.trim() || null) : null,
      html_es: hasEs ? fHtmlEs : null,
      published: publish,
    }

    // THE PRIOR VALUE, READ FROM THE ROW — not inferred from form state. The
    // form cannot tell you what published was before this save: an admin who
    // opens a live issue and clicks Publish again has published=true in state
    // both before and after, and firing on that would re-ring every agent for a
    // typo fix. `rows` is not good enough either — it is whatever the last fetch
    // saw, and someone else may have published in between.
    let wasPublished = false
    if (formId) {
      const { data: prior, error: priorErr } = await supabase.from('weekly_emails')
        .select('published').eq('id', formId).single()
      // Bailing rather than guessing. Not knowing the prior value means not
      // knowing whether to notify, and the likely causes — row deleted, RLS
      // refusal — would fail the update on the next line anyway.
      if (priorErr) { showToast('Could not save the email: ' + priorErr.message, true); setSaving(false); return }
      wasPublished = prior?.published === true
    }

    // Writes toast and bail on error, unlike the read above which only logs —
    // MarketReportPage's createRequest (App.js:9411-9422) is the shape.
    if (formId) {
      // .select() for the same reason deleteRequest uses it: an RLS refusal on
      // an update returns zero rows and NO error, so the row count is the only
      // honest signal.
      const { data: updated, error } = await supabase.from('weekly_emails')
        .update(payload).eq('id', formId).select()
      if (error) { showToast('Could not save the email: ' + error.message, true); setSaving(false); return }
      if (!updated || updated.length === 0) {
        showToast('Save was refused — the email is unchanged.', true)
        setSaving(false)
        return
      }
    } else {
      const { error } = await supabase.from('weekly_emails')
        .insert({ ...payload, created_by: session.user.id }).select().single()
      if (error) { showToast('Could not save the email: ' + error.message, true); setSaving(false); return }
    }

    // ONE RING PER ISSUE. Only the unpublished → published transition: a new row
    // saved as published, or an existing draft flipped live. A draft save never
    // notifies, and re-saving a live issue never notifies again.
    //
    // create-notifications only, never notify-new-content — that function's
    // WELCOME_TOOLS/TYPE_LABELS copy is hardcoded to content categories and
    // would send an email describing the wrong thing entirely.
    //
    // Non-blocking and after the write, matching UploadModal (App.js:2615-2623):
    // the issue is already saved, and a notification that fails is a follow-up
    // problem rather than a reason to tell an admin the save did not happen.
    const becamePublished = publish && !wasPublished
    if (becamePublished) {
      try {
        const { data: { session: sess } } = await supabase.auth.getSession()
        await supabase.functions.invoke('create-notifications', {
          body: {
            message: `Weekly email ${String(num).padStart(3, '0')}: ${payload.subject_en}`,
            type: 'weekly-email',
            exclude_user_id: sess?.user?.id,
          }
        })
      } catch (e) {
        console.warn('Weekly email notification failed (non-blocking):', e)
      }
    }

    setSaving(false)
    showToast(publish ? 'Published' : 'Draft saved')
    resetForm(formId ? nextIssueNumber : num + 1)
    fetchRows()
  }

  const weeklyChip = (bg, fg, label) => (
    <span key={label} style={{ ...CASE_CHIP, background: bg, color: fg }}>{label}</span>
  )

  const adminNode = !isAdmin ? null : (
    <div style={{marginTop:'28px',borderTop:'0.5px solid #e8e8e8',paddingTop:'22px'}}>
      <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:'#999',fontFamily:FONT_HEAD,marginBottom:'12px'}}>
        Admin · issues
      </div>

      {rows.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'22px'}}>
          {rows.map(r => (
            <div key={r.id}
              style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'8px',
                padding:'9px 12px',display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_BODY}}>
              <span style={{flexShrink:0,color:'#999',fontSize:'11.5px',fontFamily:'monospace'}}>
                {String(r.issue_number).padStart(3, '0')}
              </span>
              <span style={{flex:1,minWidth:0,fontSize:'12.5px',color:'#1d2e42',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {r.subject_en}
              </span>
              <span style={{flexShrink:0}}>
                {r.published ? caseChip('green', 'Published') : caseChip('gold', 'Draft')}
              </span>
              <button onClick={()=>editIssue(r)}
                style={{background:'none',border:'none',padding:0,flexShrink:0,cursor:'pointer',
                  fontSize:'12px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'16px 18px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px'}}>
          <span style={{fontSize:'12.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>
            {formId ? 'Editing issue ' + String(fIssueNumber).padStart(3, '0') : 'New issue'}
          </span>
          {formId && (
            <button onClick={()=>resetForm(nextIssueNumber)}
              style={{background:'none',border:'none',padding:0,cursor:'pointer',
                fontSize:'12px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>
              Cancel edit
            </button>
          )}
        </div>

        <div style={{display:'flex',gap:'10px',marginBottom:'12px',flexWrap:'wrap'}}>
          <div style={{flex:'0 0 110px'}}>
            <label style={S.label}>ISSUE NUMBER</label>
            <input style={S.input} inputMode="numeric" value={fIssueNumber}
              onChange={e=>setFIssueNumber(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div style={{flex:'1 1 180px',minWidth:0}}>
            <label style={S.label}>ISSUE DATE</label>
            <input style={S.input} type="date" value={fIssueDate}
              onChange={e=>setFIssueDate(e.target.value)} />
          </div>
        </div>

        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center',marginBottom:'12px'}}>
          {/* Both languages stay in state, so this only changes which three
              fields are on screen. Nothing is discarded by switching. */}
          <span onClick={()=>setFLang('en')} style={marketPillStyle(!adminEs)}>English</span>
          <span onClick={()=>setFLang('es')} style={marketPillStyle(adminEs)}>Español</span>
        </div>

        <div style={{marginBottom:'12px'}}>
          <label style={S.label}>SUBJECT {adminEs ? '(ES)' : '(EN)'}</label>
          <input style={S.input}
            value={adminEs ? fSubjectEs : fSubjectEn}
            onChange={e=>(adminEs ? setFSubjectEs : setFSubjectEn)(e.target.value)} />
        </div>

        <div style={{marginBottom:'12px'}}>
          <label style={S.label}>PREVIEW {adminEs ? '(ES)' : '(EN)'}</label>
          <input style={S.input}
            value={adminEs ? fPreviewEs : fPreviewEn}
            onChange={e=>(adminEs ? setFPreviewEs : setFPreviewEn)(e.target.value)} />
        </div>

        <div style={{marginBottom:'10px'}}>
          <label style={S.label}>HTML {adminEs ? '(ES)' : '(EN)'}</label>
          <textarea style={{...S.textarea,minHeight:'180px',fontFamily:'monospace',fontSize:'11.5px'}}
            placeholder="Paste the full email HTML"
            value={adminHtml}
            onChange={e=>(adminEs ? setFHtmlEs : setFHtmlEn)(e.target.value)} />
        </div>

        {/* Recomputed on every keystroke from the textarea value — no button, no
            stale result to trust. */}
        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'14px'}}>
          {weeklyChip('rgba(136,135,128,0.16)', '#5f5e5a', `${adminHtml.length.toLocaleString('en-US')} characters`)}
          {balanced
            ? weeklyChip('rgba(99,153,34,0.14)', '#3b6d11', 'Tags balanced')
            : weeklyChip('rgba(200,16,46,0.10)', '#C8102E', 'Unbalanced: ' + badTags.join(', '))}
          {hasMergeField
            ? weeklyChip('rgba(99,153,34,0.14)', '#3b6d11', WEEKLY_MERGE_FIELD)
            : weeklyChip('rgba(136,135,128,0.16)', '#5f5e5a', 'No ' + WEEKLY_MERGE_FIELD)}
        </div>

        <div style={{display:'flex',gap:'8px',flexWrap:'wrap',alignItems:'center'}}>
          <button onClick={()=>saveIssue(false)} disabled={saving} style={marketSecondaryStyle(!saving)}>
            {saving ? 'Saving...' : 'Save draft'}
          </button>
          <button onClick={()=>saveIssue(true)} disabled={!canPublish} style={marketPrimaryStyle(canPublish)}>
            {saving ? 'Saving...' : 'Publish'}
          </button>
          {!balanced && (
            <span style={{fontSize:'11.5px',color:'#C8102E',fontFamily:FONT_BODY}}>
              Fix the unbalanced tags before publishing.
            </span>
          )}
        </div>
      </div>
    </div>
  )

  const listNode = (
    <div>
      <div style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY,marginBottom:'16px'}}>
        Ready-to-send emails for your database. One a week, written for you.
      </div>
      {rows.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px',color:'#999',fontFamily:FONT_BODY}}>
          No emails published yet.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {rows.map(r => (
            <button key={r.id} onClick={()=>openIssue(r.id)}
              style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',
                padding:'16px 18px',display:'flex',flexDirection:'column',gap:'6px',fontFamily:FONT_BODY,
                textAlign:'left',cursor:'pointer',width:'100%',transition:'background .15s'}}
              onMouseEnter={e=>e.currentTarget.style.background=RAIL_BG}
              onMouseLeave={e=>e.currentTarget.style.background='white'}>
              <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:'#999',fontFamily:FONT_HEAD}}>
                Issue {String(r.issue_number).padStart(3, '0')}
                {r.issue_date ? ` · ${weeklyDateLabel(r.issue_date)}` : ''}
              </div>
              <div style={{fontSize:'13.5px',fontWeight:500,color:'#1d2e42'}}>
                {r.subject_en}
              </div>
              {r.preview_en && (
                <div style={{fontSize:'12px',color:'#666',lineHeight:1.6}}>{r.preview_en}</div>
              )}
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginTop:'4px'}}>
                {caseChip('blue', 'English')}
                {/* Nothing at all when the Spanish version is missing. A chip
                    naming an absence is still a chip, and it read as a fault
                    rather than as work in progress. */}
                {r.html_es && caseChip('green', 'Español')}
                {/* Admins only: an agent never sees an unpublished row at all,
                    so a Draft chip on their screen would be unreachable. */}
                {isAdmin && r.published === false && caseChip('gold', 'Draft')}
              </div>
            </button>
          ))}
        </div>
      )}
      {adminNode}
    </div>
  )

  const readingNode = open && (
    <div>
      <button onClick={()=>setOpenId(null)}
        style={{background:'none',border:'none',padding:'0 0 16px',cursor:'pointer',
          fontSize:'13px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>
        ← Back to all emails
      </button>

      {/* Same Draft chip the list card carries, on the same condition. Without
          it an admin can open an unpublished issue, read it through, and have
          nothing on screen say no agent can see it. */}
      <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap',marginBottom:'12px'}}>
        <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:'#999',fontFamily:FONT_HEAD}}>
          Issue {String(open.issue_number).padStart(3, '0')}
          {open.issue_date ? ` · ${weeklyDateLabel(open.issue_date)}` : ''}
        </span>
        {isAdmin && open.published === false && caseChip('gold', 'Draft')}
      </div>

      {/* The subject is its own field in the sending tool, not part of the HTML,
          so it gets its own control rather than being something to select out
          of the heading. */}
      <div style={{background:'#f4f4f2',border:'1px solid #d8d8d4',borderRadius:'8px',
        padding:'12px 14px',display:'flex',alignItems:'flex-start',gap:'12px',marginBottom:'16px'}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',textTransform:'uppercase',color:'#999',fontFamily:FONT_HEAD,marginBottom:'4px'}}>
            Subject line
          </div>
          <div style={{fontSize:'14px',color:'#1d2e42',fontFamily:FONT_BODY,lineHeight:1.5,overflowWrap:'anywhere'}}>
            {openSubject}
          </div>
        </div>
        <button onClick={copySubject}
          style={{background:'none',border:'none',padding:0,flexShrink:0,cursor:'pointer',
            fontSize:'12px',fontWeight:600,color:'#147efb',fontFamily:FONT_BODY}}>
          Copy
        </button>
      </div>

      <div style={{display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center',marginBottom:'14px'}}>
        <span onClick={()=>setLang('en')} style={marketPillStyle(!showEs)}>English</span>
        {/* Rendered only when there is a Spanish body to switch to. A pill that
            selects nothing is worse than no pill. */}
        {open.html_es && (
          <span onClick={()=>setLang('es')} style={marketPillStyle(showEs)}>Español</span>
        )}
      </div>

      <div style={{marginBottom:'8px'}}>
        <button onClick={copyHtml} disabled={copying} style={marketPrimaryStyle(!copying)}>
          {copying ? 'Copying...' : 'Copy HTML'}
        </button>
      </div>

      <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginBottom:'18px'}}>
        In Follow Up Boss, open the email composer and click the &lt;&gt; button, then paste.
      </div>

      {/* sandbox="" — no allow tokens at all. See the note above the constant. */}
      <iframe
        srcDoc={openHtml || ''}
        sandbox=""
        title={`Email preview — issue ${open.issue_number}`}
        style={{width:'100%',height:WEEKLY_PREVIEW_HEIGHT,border:'0.5px solid #e8e8e8',
          borderRadius:'8px',background:'#ffffff',display:'block'}}
      />
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Weekly emails"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={open ? readingNode : listNode}
      contentWidth={WEEKLY_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── LINK PAGE / OPEN HOUSE (shell wrappers) ─────────────────────────────────
// Both admin pages live in their own modules and can't reach CaseShell without
// a circular import, so the shell wraps them from this side. Each has three
// top-level branches — loading guard, admin roster, editor — and the chrome was
// stripped from all three, so whichever renders drops straight into the stage.
// Neither has a search or filter control, so summary is null; Open house's only
// title-row candidate is its Edit/Leads tab pair, and that tab state is local to
// OpenHouseAdmin.jsx.
const LINKS_ADMIN_CONTENT_WIDTH = '900px'
const OPENHOUSE_ADMIN_CONTENT_WIDTH = '1100px'

function LinkPageView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Link page"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<LinksAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} />}
      contentWidth={LINKS_ADMIN_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

function OpenHouseView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Open house"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<OpenHouseAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} isMobile={isMobile} />}
      contentWidth={OPENHOUSE_ADMIN_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── FUNNELS / TEAMS (shell wrappers) ────────────────────────────────────────
// Same reason LinkPageView and OpenHouseView exist: both pages live in their own
// modules and cannot import CaseShell without a circular edge. Neither has a
// filter or tab row, so summary is null.
const FUNNELS_CONTENT_WIDTH = '1000px'
const TEAMS_CONTENT_WIDTH = '1000px'

function FunnelsView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Funnels"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<FunnelsAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} />}
      contentWidth={FUNNELS_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

function TeamsView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Teams"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<TeamsAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} />}
      contentWidth={TEAMS_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

const DEALDESK_REVIEW_CONTENT_WIDTH = '1000px'

// Same wrapper TeamsView is: the shell, with the whole screen as emptyState and
// no composer. renderCaseMarkdown is handed down as a prop because it lives in
// this file and is not exported — importing it into the screen would close a
// cycle.
function DealDeskReviewView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Deal Desk review"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<DealDeskReviewAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} renderMd={renderCaseMarkdown} />}
      contentWidth={DEALDESK_REVIEW_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

const RECRUITING_CONTENT_WIDTH = '1000px'

// Same wrapper FunnelsView is: the shell, with the whole screen as emptyState
// and no composer. The shell owns the "Recruiting" heading, so the screen does
// not repeat it — its own header row carries the tabs and the refresh only.
function RecruitingView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Recruiting"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<RecruitingAdmin session={session} profile={profile} showToast={showToast} navigateTo={navigateTo} />}
      contentWidth={RECRUITING_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

const BILLING_CONTENT_WIDTH = '1000px'

// Same wrapper RecruitingView is: the shell, with the whole screen as
// emptyState and no composer. Only rendered for admins (Dashboard).
function BillingView({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  return (
    <CaseShell
      isMobile={isMobile}
      title="Billing"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={false}
      messages={[]}
      renderMessage={()=>null}
      emptyState={<BillingAdmin showToast={showToast} />}
      contentWidth={BILLING_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// What a non-admin sees on an admin route that is gated with a notice rather
// than a bounce (/admin, /admin/billing).
function AdminsOnlyNotice() {
  return (
    <div style={{flex:1,minHeight:0,display:'flex',alignItems:'center',justifyContent:'center',padding:'24px',fontFamily:FONT_BODY}}>
      <div style={{fontSize:'14px',color:'#555'}}>Admins only.</div>
    </div>
  )
}

// ─── TECH FEE ────────────────────────────────────────────────────────────────
// The agent's $99/month tech fee. The strip text (Dashboard) and the /tech-fee
// card both read the agent's own agent_billing row; no row means Exempt.
const TECH_FEE_NOTICE = {
  not_set_up: "Your $99/month tech fee isn't set up yet.",
  past_due: "Your last tech fee payment didn't go through. Update it in PayPal.",
  cancelled: 'Your tech fee was cancelled. Set it up again.',
}
const TECH_FEE_PAYPAL_ERROR = "PayPal couldn't start the payment. Try again or call the broker."

// The PayPal JS SDK, loaded once per page load and only when /tech-fee needs a
// button. A failed load is forgotten, so the next visit tries again.
let paypalSdkPromise = null
function loadPayPalSdk() {
  if (window.paypal && window.paypal.Buttons) return Promise.resolve(window.paypal)
  if (!paypalSdkPromise) {
    paypalSdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&vault=true&intent=subscription`
      s.async = true
      s.onload = () => (window.paypal && window.paypal.Buttons ? resolve(window.paypal) : reject(new Error('PayPal SDK has no Buttons')))
      s.onerror = () => { s.remove(); reject(new Error('PayPal SDK failed to load')) }
      document.head.appendChild(s)
    }).catch(e => { paypalSdkPromise = null; throw e })
  }
  return paypalSdkPromise
}

function techFeeDay(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', month: 'long', day: 'numeric', year: 'numeric' })
}

// The Smart Button tags the subscription with the agent's user id (custom_id),
// which is how tech-fee-link and paypal-webhook know whose it is. onApprove
// records it through tech-fee-link, then re-reads the row so the card turns
// Active without a reload.
function TechFeePage({ billing, isMobile, userId, onLinked }) {
  const buttonRef = useRef(null)
  const [ppError, setPpError] = useState('')
  const [linking, setLinking] = useState(false)
  const [linkNote, setLinkNote] = useState('')

  async function linkSubscription(subscriptionId) {
    setLinking(true); setPpError(''); setLinkNote('')
    let msg = ''
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession()
      const { data, error } = await supabase.functions.invoke('tech-fee-link', {
        body: { subscription_id: subscriptionId },
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      })
      msg = data?.error || ''
      if (!msg && error) { try { const b = await error.context?.json?.(); msg = b?.error } catch (_) {} msg = msg || error.message }
    } catch (e) {
      msg = e.message || 'unknown error'
    }
    if (msg) {
      console.error('[tech-fee] link failed:', msg)
      setPpError(`PayPal approved it, but the Hub couldn't record it (${msg}). Call the broker.`)
    }
    const row = await onLinked()
    if (!msg && row && row.status !== 'active') setLinkNote('Approved in PayPal. It can take a minute to show as Active.')
    setLinking(false)
  }
  // The button's callbacks are bound once, at render; the ref keeps them
  // calling the current linkSubscription.
  const linkRef = useRef(linkSubscription)
  linkRef.current = linkSubscription

  const card = { width:'100%',maxWidth:'480px',margin:'0 auto',background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'22px 22px 24px',fontFamily:FONT_BODY,color:'#1d2e42' }
  const eyebrow = { fontSize:'11px',fontWeight:500,letterSpacing:'1.5px',color:'#888',marginBottom:'8px' }
  const line = { fontSize:'13px',color:'#555',lineHeight:1.5 }
  const owes = billing && billing.tech_fee === 'on'
  const active = owes && billing.status === 'active'
  const showButton = !!owes && !active

  useEffect(() => {
    if (!showButton) return
    let cancelled = false
    let buttons = null
    loadPayPalSdk().then(paypal => {
      if (cancelled || !buttonRef.current) return
      buttons = paypal.Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'subscribe' },
        createSubscription: (data, actions) => actions.subscription.create({ plan_id: TECH_FEE_PLAN_ID, custom_id: userId }),
        onApprove: (data) => linkRef.current(data.subscriptionID),
        onError: (err) => { console.error('[tech-fee] PayPal error:', err); setPpError(TECH_FEE_PAYPAL_ERROR) },
      })
      return buttons.render(buttonRef.current)
    }).catch(e => {
      console.error('[tech-fee] PayPal button:', e)
      if (!cancelled) setPpError(TECH_FEE_PAYPAL_ERROR)
    })
    return () => {
      cancelled = true
      if (buttons && buttons.close) Promise.resolve(buttons.close()).catch(() => {})
    }
  }, [showButton, userId])

  let body
  if (billing === undefined) {
    body = <div style={line}>Loading…</div>
  } else if (!owes) {
    body = <div style={line}>No tech fee on your account.</div>
  } else {
    body = (
      <>
        <div style={{display:'flex',alignItems:'center',gap:'10px',flexWrap:'wrap',marginBottom:'8px'}}>
          <div style={{fontSize:'24px',fontWeight:500,color:'#1d2e42'}}>$99 per month</div>
          {active && caseChip('green', 'Active')}
        </div>
        {active ? (
          <>
            <div style={line}>Started {techFeeDay(billing.started_at)} · last payment {techFeeDay(billing.last_payment_at)}</div>
            <div style={{...line,marginTop:'6px'}}>Manage or cancel in your PayPal account.</div>
          </>
        ) : (
          <>
            {billing.status !== 'not_set_up' && TECH_FEE_NOTICE[billing.status] && (
              <div style={{...line,color:'#1d2e42',fontWeight:500,marginBottom:'6px'}}>{TECH_FEE_NOTICE[billing.status]}</div>
            )}
            <div style={line}>Paid monthly through PayPal. Your card details stay with PayPal, never in the Hub.</div>
            <div ref={buttonRef} style={{marginTop:'18px',maxWidth:'320px',opacity:linking?0.5:1,pointerEvents:linking?'none':'auto'}} />
            {linking && <div style={{...line,marginTop:'8px'}}>Recording your subscription…</div>}
            {linkNote && <div style={{...line,marginTop:'8px'}}>{linkNote}</div>}
            {ppError && <div style={{...line,marginTop:'8px',color:'#c0392b'}}>{ppError}</div>}
          </>
        )}
      </>
    )
  }

  return (
    <div style={{flex:1,minHeight:0,overflowY:'auto',padding:isMobile?'24px 16px 48px':'40px 20px 80px',background:'#ffffff'}}>
      <div style={card}>
        <div style={eyebrow}>TECH FEE</div>
        {body}
      </div>
    </div>
  )
}

// ─── STRATEGY: CASES LIST ───────────────────────────────────────────────────
// ─── MARKET SNAPSHOT: ZIP micro-market report ─────────────────────────────────
// Saved report REQUESTS (client, ZIP, audience, language, teammate). Never the
// numbers. Opening a request pulls fresh data; the PDF is saved to storage so a
// phone can share a real https link.
const MARKET_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

function marketSlug(s) {
  const out = String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
  return out || 'client'
}

// Outlined pill per the design system: 18px radius, selected = blue border on
// the inset fill. Blue marks the selected state; it is never a fill.
function marketPillStyle(selected) {
  return {
    padding: '5px 12px', borderRadius: '18px', fontSize: '11.5px', fontFamily: FONT_BODY,
    fontWeight: 500, cursor: 'pointer', userSelect: 'none', color: '#1d2e42',
    background: selected ? '#f4f4f2' : '#ffffff',
    border: selected ? '0.5px solid #147efb' : '0.5px solid #e8e8e8',
  }
}

// Gold primary, overridden inline on S.btnPrimary for the same reason Strategy
// does it: the shared style is blue at 35 call sites.
function marketPrimaryStyle(enabled) {
  return {
    ...S.btnPrimary, background: '#fdbf00', color: '#412402', fontWeight: 500, letterSpacing: 'normal',
    textTransform: 'none', borderRadius: '8px', flex: 'none', flexShrink: 0, padding: '8px 16px',
    fontSize: '11.5px', opacity: enabled ? 1 : 0.5,
  }
}
function marketSecondaryStyle(enabled) {
  return {
    background: '#ffffff', color: '#1d2e42', border: '0.5px solid #e8e8e8', borderRadius: '8px',
    padding: '8px 14px', fontSize: '11.5px', fontFamily: FONT_BODY, fontWeight: 500,
    cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.5,
  }
}

function MarketReportPage({ session, showToast, isMobile, onOpenCase, profile, navigateTo, isAdmin, signOut }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [clientName, setClientName] = useState('')
  const [zip, setZip] = useState('')
  const [audience, setAudience] = useState('seller')
  const [language, setLanguage] = useState('en')
  const [nameFocused, setNameFocused] = useState(false)
  const [zipFocused, setZipFocused] = useState(false)

  const fetchRows = useCallback(async () => {
    const { data, error } = await supabase.from('market_report_requests')
      .select('*').eq('user_id', session.user.id).order('updated_at', { ascending: false })
    if (error) console.error('market requests query error:', error)
    setRows(data || [])
    setLoading(false)
  }, [session.user.id])
  useEffect(() => { fetchRows() }, [fetchRows])

  const zipOk = /^\d{5}$/.test(zip.trim())
  const canCreate = clientName.trim().length >= 2 && zipOk && !creating
  // Paused for everyone but a HomePros admin while its MLS licensing is
  // confirmed. armls-market-snapshot refuses on the server either way.
  const snapshotOpen = canUseMarketSnapshot(profile)

  async function createRequest() {
    if (!snapshotOpen || !canCreate) return
    setCreating(true)
    const { data, error } = await supabase.from('market_report_requests')
      .insert({ user_id: session.user.id, client_name: clientName.trim(), zip: zip.trim(), audience, language })
      .select().single()
    if (error) { showToast('Could not save the report: ' + error.message, true); setCreating(false); return }
    setCreating(false)
    setClientName('')
    setZip('')
    onOpenCase(data.id)
  }

  // Same RLS-refusal guard as Strategy: a refused delete returns zero rows and
  // no error, so count the rows rather than trusting the absence of an error.
  async function deleteRequest(id) {
    const { data: deleted, error } = await supabase.from('market_report_requests').delete().eq('id', id).select()
    if (error) { showToast('Delete failed: ' + error.message, true); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the report is still there.', true)
      setConfirmDelete(null)
      return
    }
    // The stored PDF goes with its request, ROW FIRST: only once the row is
    // gone, so a refused delete never orphans the row's file. The path is
    // built from client, ZIP, audience and month, so two requests can share
    // one file; it is removed only when no other request still points at it.
    // A failed removal is logged, not reported — the request is already gone.
    const pdfPath = deleted[0]?.last_pdf_path
    if (pdfPath) {
      const { data: stillUsed, error: useErr } = await supabase.from('market_report_requests')
        .select('id').eq('last_pdf_path', pdfPath).limit(1)
      if (useErr) {
        console.error('[market report delete] could not check other requests; PDF kept:', useErr.message)
      } else if (!stillUsed || stillUsed.length === 0) {
        const { error: rmErr } = await supabase.storage.from('property-docs').remove([pdfPath])
        if (rmErr) console.error('[market report delete] stored PDF not removed:', rmErr.message)
      }
    }
    setRows(prev => prev.filter(r => r.id !== id))
    setConfirmDelete(null)
    showToast('Report deleted')
  }

  const renderCard = (r) => {
    const chips = (
      <>
        <span style={{...CASE_CHIP, background:'#E4EDF7', color:'#123763'}}>{r.audience === 'buyer' ? 'Buyer' : 'Seller'}</span>
        <span style={{...CASE_CHIP, background:'#F4F4F2', color:'#666'}}>{r.language === 'es' ? 'Español' : 'English'}</span>
      </>
    )
    const meta = [`ZIP ${r.zip}`, r.last_generated_at ? `PDF ${timeAgo(r.last_generated_at)}` : null].filter(Boolean).join(' · ')
    const stage = r.last_generated_at ? null : { text: 'No PDF yet', color: '#999' }
    return (
      <CaseCard
        key={r.id}
        chips={chips}
        address={r.client_name || 'Untitled'}
        meta={meta}
        stage={stage}
        onOpen={()=>onOpenCase(r.id)}
        deletable
        confirming={confirmDelete === r.id}
        onAskDelete={()=>setConfirmDelete(r.id)}
        onConfirmDelete={()=>deleteRequest(r.id)}
        onCancelDelete={()=>setConfirmDelete(null)}
      />
    )
  }

  const q = search.trim().toLowerCase()
  const filtered = q
    ? rows.filter(r => (r.client_name || '').toLowerCase().includes(q) || (r.zip || '').includes(q))
    : rows

  const inputStyle = (focused) => ({
    flex:1, minWidth:0, boxSizing:'border-box', textAlign:'left', background:'#f4f4f2', borderRadius:'8px',
    border: focused ? '0.5px solid #147efb' : '0.5px solid #e8e8e8', padding:'8px 11px', fontSize:'11.5px',
    fontFamily:FONT_BODY, color:'#1d2e42', outline:'none',
  })

  // Shared by the create card and the empty state so the two cannot drift.
  const createFields = (
    <div>
      <div style={{display:'flex', gap:'7px', marginBottom:'8px'}}>
        <input
          value={clientName} onChange={e=>setClientName(e.target.value)}
          onFocus={()=>setNameFocused(true)} onBlur={()=>setNameFocused(false)}
          placeholder="Client name — e.g. Maria Gonzalez"
          style={{...inputStyle(nameFocused), flex:2}}
        />
        <input
          value={zip} onChange={e=>setZip(e.target.value.replace(/\D/g,'').slice(0,5))}
          onKeyDown={e=>{ if (e.key==='Enter') createRequest() }}
          onFocus={()=>setZipFocused(true)} onBlur={()=>setZipFocused(false)}
          placeholder="ZIP" inputMode="numeric"
          style={{...inputStyle(zipFocused), flex:'0 0 84px'}}
        />
      </div>
      <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center'}}>
        <span onClick={()=>setAudience('seller')} style={marketPillStyle(audience==='seller')}>For a seller</span>
        <span onClick={()=>setAudience('buyer')} style={marketPillStyle(audience==='buyer')}>For a buyer</span>
        <span style={{width:'6px'}} />
        <span onClick={()=>setLanguage('en')} style={marketPillStyle(language==='en')}>English</span>
        <span onClick={()=>setLanguage('es')} style={marketPillStyle(language==='es')}>Español</span>
        <span style={{flex:1}} />
        <button onClick={createRequest} disabled={!canCreate} style={marketPrimaryStyle(canCreate)}>
          {creating ? 'Saving...' : 'Create report'}
        </button>
      </div>
    </div>
  )

  const listNode = (
    <div>
      {/* "Live MLS data", never the feed's name — marketing compliance rule. */}
      <ToolIntroBand
        title="Market Snapshot"
        description="A three-month picture of any ZIP code, in plain English. Name the client, enter the ZIP, and hand them a report."
        stats={[
          (<><span style={{color:'#fdbf00'}}>Live MLS data</span></>),
          'One ZIP at a time',
          'Client-ready PDF',
        ]}
      />
      {rows.length === 0 ? (
        <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'22px 20px',textAlign:'center'}}>
          <div style={{width:'38px',height:'38px',borderRadius:'10px',background:'#FFF4D6',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
            <MarketIcon size={19} color="#6B4B00" />
          </div>
          <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'5px'}}>
            Start your first report
          </div>
          <div style={{fontSize:'11.5px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.6,maxWidth:'360px',margin:'0 auto 14px'}}>
            Who is it for, and which ZIP code. The numbers are pulled fresh every time you open it.
          </div>
          <div style={{maxWidth:'440px',margin:'0 auto',textAlign:'left'}}>
            {snapshotOpen ? createFields : marketPausedNotice}
          </div>
        </div>
      ) : (
      <>
      <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 14px',marginBottom:'14px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'9px'}}>
          <span style={{color:'#147efb',fontSize:'14px',lineHeight:1,fontFamily:FONT_BODY}}>+</span>
          <span style={{fontSize:'12.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>New report</span>
        </div>
        {snapshotOpen ? createFields : marketPausedNotice}
      </div>

      <input
        value={search}
        onChange={e=>setSearch(e.target.value)}
        onFocus={()=>setSearchFocused(true)}
        onBlur={()=>setSearchFocused(false)}
        placeholder="Search by client or ZIP"
        style={{
          width:'100%', boxSizing:'border-box', marginBottom:'16px',
          background:'#ffffff', borderRadius:'18px',
          border: searchFocused ? '0.5px solid #147efb' : '0.5px solid #e8e8e8',
          padding:'5px 12px', fontSize:'11px', fontFamily:FONT_BODY,
          color:'#1d2e42', outline:'none',
        }}
      />

      {filtered.length === 0 ? (
        <div style={{padding:'24px 0',color:'#999',fontSize:'13px',fontFamily:FONT_BODY}}>
          No reports match that search.
        </div>
      ) : (
        <div style={strategyGrid(isMobile)}>
          {filtered.map(renderCard)}
        </div>
      )}
      </>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Market Snapshot"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={MARKET_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

function MarketReportView({ session, requestId, showToast, isMobile, onBack, profile, navigateTo, isAdmin, signOut }) {
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [snapshot, setSnapshot] = useState(null)
  const [snapError, setSnapError] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [openingPdf, setOpeningPdf] = useState(false)
  const [reportPreview, setReportPreview] = useState(null)
  const [teammates, setTeammates] = useState([])
  const [selectedMateId, setSelectedMateId] = useState(null)
  const selectedMate = teammates.find(t => t.armls_agent_id === selectedMateId) || null
  // Paused for everyone but a HomePros admin. serverPaused covers a response
  // with code market_snapshot_paused (a stale profile in the browser): it
  // renders the same notice, never the red error.
  const [serverPaused, setServerPaused] = useState(false)
  const snapshotPaused = !canUseMarketSnapshot(profile) || serverPaused

  // Load the request row. A missing or refused row sends the agent back to the
  // list with a toast rather than rendering an empty report.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.from('market_report_requests').select('*').eq('id', requestId).single()
      if (cancelled) return
      if (error || !data) { showToast('Could not open that report.', true); onBack(); return }
      setRequest(data)
      setSelectedMateId(data.teammate_armls_id || null)
      setLoading(false)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId])

  // Team lookup, copied from Strategy. Silent by design: a team-lookup failure
  // must never stop a report being generated. Every early return leaves
  // `teammates` empty, which is what hides the feature.
  const myArmlsId = profile?.armls_agent_id
  const myBrokerage = profile?.brokerage
  useEffect(() => {
    if (!myArmlsId || !myBrokerage) return
    let cancelled = false
    ;(async () => {
      try {
        const { data: mine, error: mineErr } = await supabase
          .from('team_members').select('team_id').eq('armls_agent_id', myArmlsId)
        if (mineErr || cancelled) return
        const teamId = mine?.[0]?.team_id
        if (!teamId) return

        const { data: mates, error: matesErr } = await supabase
          .from('team_members').select('armls_agent_id').eq('team_id', teamId)
        if (matesErr || cancelled) return
        const mateIds = (mates || [])
          .map(m => m.armls_agent_id)
          .filter(id => id && id !== myArmlsId)
        if (mateIds.length === 0) return

        // Same brokerage only: a report carries the signed-in agent's
        // brokerage name, so a teammate from another brokerage is never
        // offered for its footer.
        const { data: profs, error: profErr } = await supabase
          .from('profiles')
          .select('full_name, phone, email, armls_agent_id, avatar_url')
          .in('armls_agent_id', mateIds)
          .eq('brokerage', myBrokerage)
        if (profErr || cancelled) return
        setTeammates((profs || []).filter(p => p.full_name && p.full_name.trim()))
      } catch (_e) {
        // Silent by design. See the note above.
      }
    })()
    return () => { cancelled = true }
  }, [myArmlsId, myBrokerage])

  // Live pull. Read the body as text first, then parse — a gateway error page
  // is not JSON, and a truncated body must never be parsed.
  const fetchSnapshot = useCallback(async (zip) => {
    setFetching(true)
    setSnapError(null)
    setSnapshot(null)
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token
      if (!token) throw new Error('Your session has expired. Please refresh the page and sign in again.')
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/armls-market-snapshot`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ zip }),
      })
      const raw = await res.text()
      let body = null
      try { body = JSON.parse(raw) } catch (_) { body = null }
      if (body?.code === 'market_snapshot_paused') { setServerPaused(true); return }
      if (!res.ok || !body?.ok) throw new Error(body?.error || `Market data unavailable (${res.status})`)
      setSnapshot(body)
    } catch (e) {
      console.error('[armls-market-snapshot]', e)
      setSnapError(e.message)
    } finally {
      setFetching(false)
    }
  }, [])
  // No auto-fetch while paused: the server would refuse, and every refused
  // open writes an audit row.
  useEffect(() => { if (request?.zip && !snapshotPaused) fetchSnapshot(request.zip) }, [request?.zip, fetchSnapshot, snapshotPaused])

  // Toggles persist to the request row so the next PDF matches what is on screen.
  async function updateRequest(patch) {
    setRequest(r => ({ ...r, ...patch }))
    const { error } = await supabase.from('market_report_requests')
      .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', request.id)
    if (error) showToast('Could not save that change: ' + error.message, true)
  }

  function triggerBlobDownload(blob, fileName) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Preview overlay, copied from Strategy. The object URL is revoked on close
  // and on unmount so a PDF is never leaked for the life of the tab.
  function closeReportPreview() {
    setReportPreview(prev => {
      if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl)
      return null
    })
  }
  useEffect(() => {
    return () => {
      setReportPreview(prev => {
        if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl)
        return null
      })
    }
  }, [])

  // Web Share with the FILE as the payload. No blob: URL ever leaves this
  // function — it is meaningless on the receiving device.
  async function shareReport() {
    if (!reportPreview?.blob) return
    const file = new File([reportPreview.blob], reportPreview.fileName, { type: 'application/pdf' })
    const shareTitle = `Market snapshot — ${reportPreview.label || 'HomePros'}`
    const payload = { files: [file], title: shareTitle, text: `${shareTitle} · HomePros` }
    if (navigator.canShare && navigator.canShare(payload)) {
      try {
        await navigator.share(payload)
      } catch (e) {
        if (e.name !== 'AbortError') showToast('Could not open share')
      }
      return
    }
    showToast("This browser can't share files. Use Download, then attach the file.")
  }

  async function generatePdf() {
    if (!profile?.brokerage) { showToast('Please complete your brokerage setup first.'); return }
    if (!snapshot?.ok || generating) return
    setGenerating(true)
    try {
      let blob
      try {
        blob = await pdf(<MarketReportPDF data={snapshot} request={request} profile={profile} email={session.user.email} language={request.language} teammate={selectedMate} />).toBlob()
      } catch (pdfErr) {
        throw new Error(`Could not build PDF: ${pdfErr.message}`)
      }
      // Month in the name, not the day: same month = same numbers, so a
      // regeneration overwrites; a new month is a new file.
      const ym = new Date().toISOString().slice(0, 7)
      const fileName = `${marketSlug(request.client_name)}_${request.zip}_${request.audience}_${ym}.pdf`
      const path = `${session.user.id}/market-reports/${fileName}`
      const { error: upErr } = await supabase.storage.from('property-docs')
        .upload(path, blob, { upsert: true, contentType: 'application/pdf' })
      if (upErr) throw new Error(`Could not save PDF: ${upErr.message}`)
      const nowIso = new Date().toISOString()
      const { error: rowErr } = await supabase.from('market_report_requests')
        .update({ last_generated_at: nowIso, last_pdf_path: path, updated_at: nowIso }).eq('id', request.id)
      if (rowErr) console.error('market request update error:', rowErr)
      setRequest(r => ({ ...r, last_generated_at: nowIso, last_pdf_path: path }))

      // Desktop only. iOS ignores <a download> and NAVIGATES to the blob: URL,
      // which would take the tab before the overlay could paint. On a phone
      // the overlay opens and its Share button, a fresh tap, hands over the
      // PDF file itself. No link is created.
      if (!isMobile) triggerBlobDownload(blob, fileName)
      // A SECOND object URL, deliberately: triggerBlobDownload revokes its own.
      const previewUrl = URL.createObjectURL(blob)
      setReportPreview({ blob, blobUrl: previewUrl, fileName, language: request.language, label: `${request.client_name} · ${request.zip}` })
      showToast('Report PDF generated and saved.')
    } catch (e) {
      console.error('[market-report-pdf]', e)
      showToast(`PDF failed: ${e.message}`, true)
    } finally {
      setGenerating(false)
    }
  }

  // The stored PDF, opened in the preview overlay where Download and Share
  // work on the file itself. Replaces "Copy share link", which handed out a
  // one-year URL. Downloaded under the owner's own storage read; no link is
  // created. Not offered while Market Snapshot is paused, as the link was not.
  async function openLastPdf() {
    if (snapshotPaused || !request?.last_pdf_path || openingPdf) return
    setOpeningPdf(true)
    try {
      const { data, error } = await supabase.storage.from('property-docs').download(request.last_pdf_path)
      if (error || !data) throw new Error(error?.message || 'the file was not found')
      const blob = data.type === 'application/pdf' ? data : new Blob([data], { type: 'application/pdf' })
      const fileName = request.last_pdf_path.split('/').pop()
      setReportPreview(prev => {
        if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl)
        return { blob, blobUrl: URL.createObjectURL(blob), fileName, language: request.language, label: `${request.client_name} · ${request.zip}` }
      })
    } catch (e) {
      console.error('[market-report-open]', e)
      showToast(`Could not open the last PDF: ${e.message}`, true)
    } finally {
      setOpeningPdf(false)
    }
  }

  const lang = request?.language === 'es' ? 'es' : 'en'
  const sentences = snapshot ? buildMarketSentences(snapshot, request?.audience || 'seller', lang) : []
  const last = snapshot?.last_month || {}
  const thin = !!last.low_confidence || !(last.sold > 0)
  const through = snapshot ? marketMonthLabel(snapshot.as_of?.through_month, lang) : ''

  const card = { background:'#ffffff', border:'0.5px solid #e8e8e8', borderRadius:'12px', padding:'14px 16px', marginBottom:'14px' }
  const eyebrow = { fontSize:'10px', letterSpacing:'1px', textTransform:'uppercase', color:'#999', fontFamily:FONT_BODY, marginBottom:'6px' }

  const bodyNode = request ? (
    <div>
      <div onClick={onBack} style={{display:'inline-block', color:'#147efb', cursor:'pointer', fontSize:'11.5px', fontFamily:FONT_BODY, marginBottom:'12px'}}>← All reports</div>

      <div style={card}>
        <div style={eyebrow}>Report settings</div>
        <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center', marginBottom: teammates.length ? '8px' : 0}}>
          <span onClick={()=>updateRequest({ audience:'seller' })} style={marketPillStyle(request.audience==='seller')}>For a seller</span>
          <span onClick={()=>updateRequest({ audience:'buyer' })} style={marketPillStyle(request.audience==='buyer')}>For a buyer</span>
          <span style={{width:'6px'}} />
          <span onClick={()=>updateRequest({ language:'en' })} style={marketPillStyle(request.language==='en')}>English</span>
          <span onClick={()=>updateRequest({ language:'es' })} style={marketPillStyle(request.language==='es')}>Español</span>
        </div>
        {teammates.length > 0 && (
          <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center'}}>
            <span style={{fontSize:'11px', color:'#666', fontFamily:FONT_BODY, marginRight:'4px'}}>On the report:</span>
            <span onClick={()=>{ setSelectedMateId(null); updateRequest({ teammate_armls_id: null }) }} style={marketPillStyle(!selectedMateId)}>Just me</span>
            {teammates.map(t => (
              <span key={t.armls_agent_id} onClick={()=>{ setSelectedMateId(t.armls_agent_id); updateRequest({ teammate_armls_id: t.armls_agent_id }) }} style={marketPillStyle(selectedMateId === t.armls_agent_id)}>
                {t.full_name}
              </span>
            ))}
          </div>
        )}
      </div>

      {snapshotPaused && marketPausedNotice}
      {!snapshotPaused && fetching && (
        <div style={{...card, color:'#666', fontSize:'12px', fontFamily:FONT_BODY}}>Pulling live MLS data for {request.zip}…</div>
      )}
      {!snapshotPaused && !fetching && snapError && (
        <div style={{...card, borderColor:'#C8102E'}}>
          <div style={{fontSize:'12.5px', color:'#C8102E', fontFamily:FONT_BODY, marginBottom:'8px'}}>{snapError}</div>
          <button onClick={()=>fetchSnapshot(request.zip)} style={marketSecondaryStyle(true)}>Try again</button>
        </div>
      )}
      {!snapshotPaused && !fetching && snapshot && (
        <>
          <div style={card}>
            <div style={eyebrow}>{snapshot.zip}{snapshot.city ? ` · ${snapshot.city}` : ''} · MLS data through {through}</div>
            {sentences.map(s => (
              <div key={s.key} style={{marginBottom:'12px'}}>
                <div style={{fontSize:'14px', color:'#1d2e42', fontFamily:FONT_BODY, lineHeight:1.45}}>{s.text}</div>
                {s.sub ? <div style={{fontSize:'11px', color:'#999', fontFamily:FONT_BODY, marginTop:'2px'}}>{s.sub}</div> : null}
              </div>
            ))}
            <div style={{background:'#f4f4f2', borderRadius:'8px', padding:'10px 12px', marginTop:'4px'}}>
              {thin ? (
                <div style={{fontSize:'12px', color:'#666', fontFamily:FONT_BODY}}>
                  Not enough homes sold in {marketMonthLabel(last.label, lang)} to show a typical price ({last.sold ?? 0} sales).
                </div>
              ) : (
                <>
                  <div style={eyebrow}>Typical sold price · {marketMonthLabel(last.label, lang)}</div>
                  <div style={{fontSize:'20px', fontWeight:500, color:'#1d2e42', fontFamily:FONT_BODY}}>{marketMoney(last.median_sold_price)}</div>
                  <div style={{fontSize:'11px', color:'#666', fontFamily:FONT_BODY, marginTop:'2px'}}>Half sold for more, half for less.</div>
                </>
              )}
            </div>
          </div>

          <div style={card}>
            <div style={eyebrow}>By the numbers</div>
            <table style={{width:'100%', borderCollapse:'collapse', fontFamily:FONT_BODY, fontSize:'12px', color:'#1d2e42'}}>
              <thead>
                <tr style={{color:'#999', fontSize:'10.5px'}}>
                  <th style={{textAlign:'left', fontWeight:500, padding:'4px 0'}}>Month</th>
                  <th style={{textAlign:'right', fontWeight:500, padding:'4px 0'}}>For sale</th>
                  <th style={{textAlign:'right', fontWeight:500, padding:'4px 0'}}>New</th>
                  <th style={{textAlign:'right', fontWeight:500, padding:'4px 0'}}>Under contract</th>
                  <th style={{textAlign:'right', fontWeight:500, padding:'4px 0'}}>Sold</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.months.map((m, i) => (
                  <tr key={i} style={{borderTop:'0.5px solid #e8e8e8'}}>
                    <td style={{padding:'6px 0'}}>{marketMonthLabel(m.label, lang)}</td>
                    <td style={{textAlign:'right', padding:'6px 0'}}>{m.active ?? '—'}</td>
                    <td style={{textAlign:'right', padding:'6px 0'}}>{m.new_listings ?? '—'}</td>
                    <td style={{textAlign:'right', padding:'6px 0'}}>{m.pended ?? '—'}</td>
                    <td style={{textAlign:'right', padding:'6px 0'}}>{m.sold ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{...card, display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center'}}>
            <button onClick={generatePdf} disabled={generating} style={marketPrimaryStyle(!generating)}>
              {generating ? 'Building PDF…' : 'Generate PDF'}
            </button>
            {!snapshotPaused && request.last_pdf_path && (
              <button onClick={openLastPdf} disabled={openingPdf} style={marketSecondaryStyle(!openingPdf)}>
                {openingPdf ? 'Opening…' : 'Open last PDF'}
              </button>
            )}
            <span style={{fontSize:'11px', color:'#999', fontFamily:FONT_BODY}}>
              {request.last_generated_at ? `Last PDF ${timeAgo(request.last_generated_at)}` : 'No PDF yet'}
            </span>
          </div>
        </>
      )}
    </div>
  ) : null

  return (
    <>
    <CaseShell
      isMobile={isMobile}
      title={request?.client_name || 'Market Snapshot'}
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={MARKET_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
    {reportPreview && (
      <div
        style={isMobile ? {...S.overlay, alignItems:'flex-start'} : S.overlay}
        onClick={e=>e.target===e.currentTarget&&closeReportPreview()}>
        <div
          onClick={e=>e.stopPropagation()}
          className={isMobile ? 'chromeless-vh' : undefined}
          style={isMobile
            ? {...S.modal, width:'100vw', maxWidth:'100vw', maxHeight:'100%',
               boxSizing:'border-box', borderRadius:0, padding:'16px',
               display:'flex', flexDirection:'column', overflow:'hidden', overflowY:'hidden'}
            : {...S.modal, maxWidth:'720px', maxHeight:'90vh',
               display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <div style={{...S.modalHeader, flexShrink:0, marginBottom: isMobile ? '10px' : '16px', gap:'12px'}}>
            <div style={{minWidth:0}}>
              <div style={{display:'flex', alignItems:'center', gap: isMobile ? '8px' : '10px', flexWrap: isMobile ? 'nowrap' : 'wrap', minWidth:0}}>
                <div style={isMobile
                  ? {...S.modalTitle, fontSize:'15px', letterSpacing:'0.5px', whiteSpace:'nowrap'}
                  : S.modalTitle}>REPORT READY</div>
                <span style={{...CASE_CHIP, ...CASE_CHIP_TONES.grey, flexShrink:0}}>
                  {reportPreview.language === 'es' ? 'ESPAÑOL' : 'ENGLISH'}
                </span>
              </div>
              <div style={{fontSize: isMobile ? '11.5px' : '12px', color:'#999', marginTop: isMobile ? '3px' : '6px', fontFamily:FONT_BODY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                {reportPreview.label}
              </div>
            </div>
            <button style={{...S.closeBtn, flexShrink:0}} onClick={closeReportPreview}>×</button>
          </div>
          <div style={{display:'flex', gap:'10px', flexWrap:'wrap', flexShrink:0, marginBottom:'14px'}}>
            <button
              onClick={()=>triggerBlobDownload(reportPreview.blob, reportPreview.fileName)}
              style={{...S.btnPrimary, flex:'none', padding:'9px 18px', fontSize:'12px', background:'#fdbf00', color:'#1d2e42'}}>
              Download
            </button>
            <button
              onClick={shareReport}
              style={{...S.btnPrimary, flex:'none', padding:'9px 18px', fontSize:'12px', background:'#147efb', color:'#ffffff',
                      display:'inline-flex', alignItems:'center', gap:'7px'}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
                <path d="M12 16V4"/><polyline points="8 8 12 4 16 8"/><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/>
              </svg>
              Share
            </button>
          </div>
          <iframe
            src={reportPreview.blobUrl}
            title={`Market snapshot preview — ${reportPreview.fileName}`}
            style={{width:'100%', flex: isMobile ? 1 : 'none', height: isMobile ? 'auto' : '60vh',
                    border:`1px solid ${RAIL_BORDER}`, borderRadius:'8px', background:'#f4f4f2', minHeight:0}}
          />
        </div>
      </div>
    )}
    </>
  )
}

// ─── REPORT DELIVERY (shared) ───────────────────────────────────────────────
// Everything two tools need to hand a generated PDF to an agent. All of it was
// local to StrategyCaseView until Casita Check needed the same overlay; it is
// lifted rather than copied so the two cannot drift.
//
// MarketReportView keeps its own copies, from when its overlay carried a third
// button (Copy share link, removed 2026-10-10). Folding it into these is now
// possible but a separate change.

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function addressSlug(addr) {
  if (!addr) return 'property'
  return addr.replace(/,/g, '').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').slice(0, 60)
}

// Hands the ACTUAL PDF to the device share sheet. Two things matter here.
//
// NOTHING IS AWAITED BEFORE navigator.share. iOS treats the share sheet as a
// user-gesture action, and an await before the call (rebuilding the PDF,
// fetching a URL) spends the gesture and the sheet silently never opens. The
// blob is already in state, so the File is constructed synchronously and the
// first await in this function IS the share call. Calling through this shared
// function adds a synchronous frame and no await, so that still holds.
//
// NO blob: URL EVER LEAVES THIS FUNCTION. A blob URL is meaningless on the
// receiving device — it resolves only inside the tab that minted it — so it
// is never put in `text`, `url`, or anywhere a recipient could see it. The
// file itself is the payload; the caption is our own prose.
async function shareReportFile(preview, shareTitle, showToast, unsupportedMsg) {
  if (!preview?.blob) return
  // Web Share requires File, not Blob. Synchronous — see above.
  const file = new File([preview.blob], preview.fileName, { type: 'application/pdf' })
  const payload = { files: [file], title: shareTitle, text: `${shareTitle} · HomePros` }
  // Feature-test with the FILE payload specifically: a browser can support
  // navigator.share for links and still refuse files, and canShare() with no
  // argument would answer the wrong question.
  if (navigator.canShare && navigator.canShare(payload)) {
    try {
      await navigator.share(payload)
    } catch (e) {
      // Dismissing the sheet rejects with AbortError. That is the user saying
      // no, not a failure, and toasting at them for it would be noise.
      if (e.name !== 'AbortError') showToast('Could not open share')
    }
    return
  }
  showToast(unsupportedMsg)
}

// The preview's state and the two things that must happen to its object URL:
// revoke THEN clear on close, because the iframe is unmounted by the same
// render that clears the state and the handle is only dead once nothing is
// reading it — and revoke on unmount, because leaving a view with the overlay
// open would otherwise leak the handle for the life of the tab. Both read the
// latest value through the setter, so neither needs a dependency on it.
function useReportPreview() {
  const [preview, setPreview] = useState(null)
  const close = useCallback(() => {
    setPreview(prev => {
      if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl)
      return null
    })
  }, [])
  useEffect(() => () => {
    setPreview(prev => {
      if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl)
      return null
    })
  }, [])
  return [preview, setPreview, close]
}

// The generated-report overlay, shared by the Strategist and Casita Check.
// Lifted verbatim out of StrategyCaseView when the second tool needed it —
// every style, comment and breakpoint below is the one that shipped there, so
// the Strategy report's overlay is unchanged by the extraction.
//
// `preview` is { blob, blobUrl, fileName, language, addressLabel }. This
// component neither creates nor revokes the object URL: useReportPreview owns
// that lifecycle, because the handle has to be revoked on unmount even when
// the overlay is closed.
//
// Download is wired internally — every caller does the same two things with
// the same two fields. Share is a prop, because the share sheet's caption
// names the document.
/* REPORT PREVIEW — opens after a generate, alongside the download that
    already fired. Closing it revokes the object URL; nothing revokes it
    while it is open, which would blank the iframe mid-read. */
/* The scrim carries a LOCAL override, not an edit to S.overlay — that
    constant is shared by the share modal and every other dialog. On iOS
    the scrim is position:fixed;inset:0, which sizes to the LARGE viewport
    (URL bar hidden), while the panel is dvh-tall (bar shown). Centring a
    shorter panel inside a taller scrim splits the difference above and
    below, and the half above goes under the address bar — taking the
    header and the buttons with it. Top-aligned, any overflow falls off
    the BOTTOM, where the iframe is and where losing height is harmless. */
function ReportPreviewOverlay({ preview, isMobile, onClose, onShare, docLabel = 'Report' }) {
  if (!preview) return null
  return (
    <div
      style={isMobile ? {...S.overlay, alignItems:'flex-start'} : S.overlay}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      {/* Desktop mirrors the share modal. MOBILE DELIBERATELY DOES NOT:
          S.modal is a centred min(560px,95vw) card, which on a phone leaves
          roughly 290px for a US-Letter page — about 4pt type, unreadable.
          Every other modal in this file is a centred card at every
          breakpoint; this one goes full-bleed because the content is a
          document rather than a form. */}
      {/* Height comes from .chromeless-vh (defined in the always-mounted
          style block near the app root), NOT from an inline key. That class
          carries the cascading `height:100vh; height:100dvh;` pair, and the
          note there spells out why: a JS style object cannot hold a
          fallback pair, because a duplicate key replaces rather than
          cascades. Inline styles also beat classes, so `height` must not
          appear in the style object at all.
          Gated: the desktop branch has no inline height, so an
          unconditional class would make the centred card 100vh tall. */}
      <div
        onClick={e=>e.stopPropagation()}
        className={isMobile ? 'chromeless-vh' : undefined}
        style={isMobile
          // boxSizing is the whole bug: S.modal never sets it, so under the
          // default content-box a 100dvh panel with 16px padding rendered
          // 100dvh + 32px. The scrim centres its child, so that excess was
          // split above AND below the viewport — which is where the button
          // row went. border-box makes 100dvh mean 100dvh.
          //
          // overflowY as well as overflow: S.modal carries overflowY:'auto',
          // and these are separate keys in a style object, so `overflow`
          // alone never cleared it. The flex column owns the layout; the
          // panel itself must never scroll.
          // maxHeight:'100%' rather than 'none': a ceiling against the scrim
          // itself, so however dvh resolves the panel can never be taller
          // than the box it sits in.
          ? {...S.modal, width:'100vw', maxWidth:'100vw', maxHeight:'100%',
             boxSizing:'border-box', borderRadius:0, padding:'16px',
             display:'flex', flexDirection:'column', overflow:'hidden', overflowY:'hidden'}
          : {...S.modal, maxWidth:'720px', maxHeight:'90vh',
             display:'flex', flexDirection:'column', overflow:'hidden'}}>

        {/* S.modalHeader was authored for a form dialog. At 375px the 20px/900
            title plus the language chip wrapped to two lines and the address
            added a third, running the header past 120px — which, with
            flexShrink:0 here and on the button row, is what shoved the
            buttons off the top. Mobile gets a compact variant; desktop is
            exactly as it was. */}
        <div style={{...S.modalHeader, flexShrink:0, marginBottom: isMobile ? '10px' : '16px', gap:'12px'}}>
          <div style={{minWidth:0}}>
            {/* nowrap on mobile: the title and chip share one line or the
                chip is pushed, never wrapped onto a second row. */}
            <div style={{display:'flex', alignItems:'center', gap: isMobile ? '8px' : '10px', flexWrap: isMobile ? 'nowrap' : 'wrap', minWidth:0}}>
              <div style={isMobile
                ? {...S.modalTitle, fontSize:'15px', letterSpacing:'0.5px', whiteSpace:'nowrap'}
                : S.modalTitle}>REPORT READY</div>
              <span style={{...CASE_CHIP, ...CASE_CHIP_TONES.grey, flexShrink:0}}>
                {preview.language === 'es' ? 'ESPAÑOL' : 'ENGLISH'}
              </span>
            </div>
            {preview.addressLabel && (
              <div style={{fontSize: isMobile ? '11.5px' : '12px', color:'#999', marginTop: isMobile ? '3px' : '6px', fontFamily:FONT_BODY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                {preview.addressLabel}
              </div>
            )}
          </div>
          <button style={{...S.closeBtn, flexShrink:0}} onClick={onClose}>×</button>
        </div>

        {/* Download, Share, then Email in Piece 3. Wrapping row, so the
            third button needs no restructuring. */}
        <div style={{display:'flex', gap:'10px', flexWrap:'wrap', flexShrink:0, marginBottom:'14px'}}>
          <button
            onClick={()=>triggerBlobDownload(preview.blob, preview.fileName)}
            style={{...S.btnPrimary, flex:'none', padding:'9px 18px', fontSize:'12px', background:'#fdbf00', color:'#1d2e42'}}>
            Download
          </button>
          {/* Inline SVG, not an icon font — this codebase has no icon-class
              convention, every glyph is inline. */}
          <button
            onClick={onShare}
            style={{...S.btnPrimary, flex:'none', padding:'9px 18px', fontSize:'12px', background:'#147efb', color:'#ffffff',
                    display:'inline-flex', alignItems:'center', gap:'7px'}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
              <path d="M12 16V4"/><polyline points="8 8 12 4 16 8"/><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/>
            </svg>
            Share
          </button>
        </div>

        {/* The real file, in the browser's own PDF viewer. */}
        <iframe
          src={preview.blobUrl}
          title={`${docLabel} preview — ${preview.fileName}`}
          style={{width:'100%', flex: isMobile ? 1 : 'none', height: isMobile ? 'auto' : '60vh',
                  border:`1px solid ${RAIL_BORDER}`, borderRadius:'8px', background:'#f4f4f2', minHeight:0}}
        />
      </div>
    </div>
  )
}

// ─── CASITA CHECK ────────────────────────────────────────────────────────────
// What a lot allows under Arizona's casita law and the city's own code. The
// whole calculation lives in the casita-check edge function — this screen sends
// a Google-picked point and renders whichever of its eight statuses comes back.
//
// NO ARITHMETIC HERE, deliberately. Every square-foot figure on this page is a
// number the function computed and a `basis` sentence it wrote to explain that
// number. A second implementation in the browser would be a second answer to
// the same question, and an agent is going to repeat what this says to a
// client.
const CASITA_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

const casitaCard = { background:'#ffffff', border:'0.5px solid #e8e8e8', borderRadius:'12px', padding:'14px 16px' }
// Same amber frame Deal Desk's blocked notice uses (App.js:16863) — both say
// "there is a gap here you have to close yourself", so they look alike.
const casitaAmberCard = { background:'#fffdf5', border:'1px solid #fdbf00', borderRadius:'10px', padding:'12px 14px' }
const casitaNavyCard = { background:'#1d2e42', borderRadius:'14px', padding:'20px 22px', color:'#ffffff' }

// '—' rather than 0 or a blank: the county genuinely has no figure for some
// parcels, and an em dash says that where an empty cell reads as a bug.
function casitaNum(n) {
  return (typeof n === 'number' && Number.isFinite(n)) ? n.toLocaleString('en-US') : '—'
}

// "QUEEN CREEK" -> "Queen Creek". The county stores jurisdiction in caps, which
// is right for a chip and wrong inside a sentence.
function casitaTitleCase(s) {
  return String(s || '').toLowerCase().replace(/(^|[\s'’-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase())
}

// The literal casita-check returns as `source`. Repeated here because a row
// read back out of casita_checks has no response envelope to carry it, and the
// county card prints it on both paths. If the function's wording ever changes,
// this is the second place to change.
const CASITA_SOURCE_NOTE = 'Maricopa County Assessor public records. Not guaranteed; not a survey.'

// The three statuses that are a QUESTION rather than an answer: the agent has
// not settled which parcel they mean yet. They are handled inline on the list
// page and never saved — a row per "is this the right property?" would fill
// the list with decisions nobody made.
//
// confirm_parcel is in this set even though it carries a parcel, because the
// parcel it carries is the one we are asking the agent to confirm.
const CASITA_INTERSTITIAL = new Set(['not_found', 'multiple_parcels', 'confirm_parcel'])

// Status → the list card's chip. Tones come from CASE_CHIP_TONES via caseChip,
// so a Casita card and a Strategy card read as the same object.
const CASITA_STATUS_CHIP = {
  allowed:           { tone: 'green', label: 'Allowed' },
  state_minimum:     { tone: 'blue',  label: 'State minimum' },
  check_with_city:   { tone: 'gold',  label: 'Check with city' },
  luke_apz:          { tone: 'gold',  label: 'Luke AFB zone' },
  // Reads as a verdict, not a place: the card that carries this chip has no
  // number on it, and "Luke AFB territory" alone would leave an agent
  // scanning the list unsure which way it went.
  luke_city_prohibited: { tone: 'gold', label: 'No casita · Luke AFB' },
  not_single_family: { tone: 'grey',  label: 'Not single-family' },
  stacked_parcel:    { tone: 'grey',  label: 'Stacked parcel' },
  missing_data:      { tone: 'grey',  label: 'Missing county data' },
}

// ONE caller into the edge function, shared by the list page's Check lot and
// the detail view's Re-check. Same auth and error handling as pullComps
// (App.js:13288): the session guard is there because getSession() already
// refreshes an expired token, so a null session means the refresh itself
// failed and `Bearer undefined` would otherwise go out and come back looking
// like a server fault.
//
// Throws on every failure. Each caller owns its own busy flag and its own
// place to put the message, which is why neither is in here.
async function runCasitaCheck(body) {
  const { data: { session: currentSession } } = await supabase.auth.getSession()
  const token = currentSession?.access_token
  if (!token) throw new Error('Your session has expired. Please refresh the page and sign in again.')

  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/casita-check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  })
  // The function reports the real reason in `error`; the status alone discards
  // it. Guarded parse, because an upstream gateway failure is not JSON and
  // letting that throw would replace a real error with a parser message.
  if (!res.ok) {
    let serverMsg = ''
    try { const errBody = await res.json(); serverMsg = errBody?.error || '' } catch (_) { serverMsg = '' }
    throw new Error(serverMsg || `Server error (${res.status})`)
  }
  const data = await res.json()
  if (!data.ok) throw new Error(data.error || 'The lot check could not be completed.')
  return data
}

// Save, or overwrite the save for this parcel. ON CONFLICT (user_id, apn), so
// re-checking a lot updates the one row rather than accumulating snapshots.
//
// .select() and a ROW COUNT, not .single(): a row refused by RLS is not an
// error — PostgREST applies the policy as a filter, so a refused upsert comes
// back { error: null } with zero rows, and .single() would turn that into a
// confusing PGRST116 rather than the plain fact that nothing was written.
//
// `address` is passed in rather than read off the payload: the Google address
// the agent picked is what they will recognise in the list, and a Re-check
// sends only an APN so it has no Google address of its own to offer.
async function upsertCasitaCheck({ userId, data, address }) {
  const parcel = data?.parcel
  if (!parcel?.apn) return { row: null, error: new Error('No parcel to save') }
  const rule = data.result || null
  const nowIso = new Date().toISOString()

  // WHERE THE NOTES GO. casita_checks has three jsonb columns — parcel,
  // result, luke — and none of them is "note". The notes ride in `result`
  // rather than in a new column because the two are mutually exclusive by
  // construction: luke_city_prohibited and check_with_city are exactly the
  // statuses that produce no rule, so `result` is free on precisely the rows
  // that carry a note. casitaRowToResponse below splits them back apart, and
  // max_sqft / rule_verified_on still read `rule`, so they stay null here.
  const notes = {}
  if (data.note) { notes.note = data.note; notes.note_es = data.note_es || null }
  if (data.city_note) { notes.city_note = data.city_note; notes.city_note_es = data.city_note_es || null }
  const storedResult = rule || (Object.keys(notes).length > 0 ? notes : null)

  const payload = {
    user_id: userId,
    apn: parcel.apn,
    address: address || parcel.county_address || parcel.apn,
    county_address: parcel.county_address || null,
    jurisdiction: parcel.jurisdiction || null,
    status: data.status,
    // Denormalised so the list card needs no jsonb unpacking.
    max_sqft: rule?.max_sqft ?? null,
    parcel,
    result: storedResult,
    luke: data.luke || null,
    rule_verified_on: rule?.verified_on ?? null,
    checked_at: nowIso,
    updated_at: nowIso,
  }

  const { data: rows, error } = await supabase
    .from('casita_checks').upsert(payload, { onConflict: 'user_id,apn' }).select()
  if (error) return { row: null, error }
  if (!rows || rows.length === 0) return { row: null, refused: true }
  return { row: rows[0] }
}

// A stored row, back in the shape the renderer already speaks. The detail view
// draws a saved check with exactly the code that draws a live one, so the two
// cannot drift — the only thing this function does is put the envelope back
// around the three jsonb columns.
function casitaRowToResponse(row) {
  if (!row) return null
  const stored = row.result || null
  // A notes-only `result` (see upsertCasitaCheck) is NOT a rule and must not
  // reach the renderer as one — `rule &&` guards a dozen branches that would
  // otherwise draw a source link and a verified date for an object that has
  // neither. Split on the keys, which is safe because a rule never carries
  // them and a note object never carries a max_sqft.
  const hasNote = !!(stored && (stored.note || stored.city_note))
  return {
    ok: true,
    status: row.status,
    parcel: row.parcel || null,
    result: hasNote ? null : stored,
    note: stored?.note ?? null,
    note_es: stored?.note_es ?? null,
    city_note: stored?.city_note ?? null,
    city_note_es: stored?.city_note_es ?? null,
    luke: row.luke || null,
    candidates: [],
    source: CASITA_SOURCE_NOTE,
  }
}

// EVERY status's screen, in one place, because two views render them: the list
// page shows the three interstitial ones inline, and the detail view shows the
// seven that get saved.
//
// Presentation only — it fetches nothing and saves nothing. `onUseApn` and
// `onClear` are the two interactive affordances the interstitial statuses
// need; the detail view passes neither because it never reaches those
// branches. `reportBar` is a node for the same reason in reverse.
function CasitaResultView({ data, isMobile, busy = false, onUseApn, onClear, reportBar = null }) {
  const parcel = data?.parcel || null
  const rule = data?.result || null
  const status = data?.status || null
  const candidates = Array.isArray(data?.candidates) ? data.candidates : []
  // { checked, territory, apz } | null. Null on a response from a deployment
  // older than the zone check, and on the statuses that carry no parcel — both
  // read as "Not checked", which is what they are.
  const luke = data?.luke || null
  // ENGLISH ON SCREEN, both statuses. The Spanish counterparts travel on the
  // response and are saved, but nothing renders them here — the page is the
  // agent's, and the client-facing document is where language is a choice.
  const note = data?.note || null
  const cityNote = data?.city_note || null
  // Order matters: a zone beats the territory, the territory beats being
  // outside it, and "we never got an answer" is its own state rather than a
  // quiet "Outside".
  const lukeRowValue = luke
    ? (luke.apz || (luke.territory ? 'Inside territory' : (luke.checked ? 'Outside' : 'Not checked')))
    : 'Not checked'

  const eyebrow = { ...SETUP_EYEBROW, marginBottom:'10px' }
  const factRow = { display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:'10px', padding:'5px 0', fontFamily:FONT_BODY }
  const factLabel = { fontSize:'11.5px', color:'#999', flexShrink:0 }
  const factValue = { fontSize:'12.5px', color:'#1d2e42', textAlign:'right', minWidth:0, overflowWrap:'anywhere' }

  // FROM COUNTY RECORDS. Rendered for every status that resolved to one parcel,
  // including missing_data — a null lot size reads as '—' and that absence is
  // exactly what that status is about.
  const countyNode = parcel && (
    <div style={{ ...casitaCard, minWidth:0 }}>
      <div style={eyebrow}>FROM COUNTY RECORDS</div>
      {[
        ['Lot size', parcel.lot_sqft != null ? `${casitaNum(parcel.lot_sqft)} sq ft` : '—'],
        ['Living area', parcel.living_sqft != null ? `${casitaNum(parcel.living_sqft)} sq ft` : '—'],
        ['Year built', parcel.year_built != null ? String(parcel.year_built) : '—'],
        // parcel.jurisdiction, not parcel.city — the jurisdiction is the body
        // whose code applies, and the two differ on parcels the county lists
        // under one city's mailing address and another's jurisdiction.
        //
        // "NO CITY/TOWN" is the county's own wording for unincorporated land.
        // Printed raw it reads as a missing value rather than as the fact it
        // is. The mapping stays here rather than reading the rule's
        // jurisdiction_label, because this row is a county RECORD and should
        // read in sentence case beside the other facts, not in the label's
        // all-caps — and it has to work on the statuses that carry a parcel
        // but no rule at all.
        ['City', parcel.jurisdiction === 'NO CITY/TOWN' ? 'Unincorporated Maricopa County' : (parcel.jurisdiction || '—')],
        ['Subdivision', parcel.subdivision || '—'],
        // A county record in the sense that matters: it is a fact about this
        // parcel's location, and it is the one row an agent near the West
        // Valley has to read before saying anything.
        ['Luke AFB', lukeRowValue],
      ].map(([k, v]) => (
        <div key={k} style={factRow}>
          <span style={factLabel}>{k}</span>
          <span style={factValue}>{v}</span>
        </div>
      ))}
      {parcel.apn && (
        <div style={factRow}>
          <span style={factLabel}>Parcel</span>
          <span style={{ ...factValue, fontFamily:'monospace', fontSize:'11.5px' }}>{parcel.apn}</span>
        </div>
      )}
      {data?.source && (
        <div style={{ fontSize:'10.5px', color:'#999', fontFamily:FONT_BODY, lineHeight:1.5, marginTop:'10px', paddingTop:'10px', borderTop:'0.5px solid #e8e8e8' }}>
          {data.source}
        </div>
      )}
      {/* The verification date belongs HERE, beside the rule it describes,
          rather than in the intro band. In the band it was a fixed literal
          that would go stale the first time one city's entry was re-read and
          another's was not; read off rule.verified_on it is a fact about the
          answer on screen. */}
      {rule && (
        <div style={{ display:'flex', alignItems:'baseline', gap:'10px', flexWrap:'wrap', marginTop:'8px' }}>
          {rule.source_url && (
            <a href={rule.source_url} target="_blank" rel="noopener noreferrer"
              style={{ fontSize:'12px', color:'#147efb', fontFamily:FONT_BODY, textDecoration:'none' }}>
              Rule source
            </a>
          )}
          {rule.verified_on && (
            <span style={{ fontSize:'11px', color:'#999', fontFamily:FONT_BODY }}>Verified {rule.verified_on}</span>
          )}
        </div>
      )}
    </div>
  )

  // The answer. Two statuses share it, and the only differences are the label
  // and whether the number is a ceiling or a floor.
  const ruleNode = rule && (
    <div style={{ display:'flex', flexDirection:'column', gap:'14px', minWidth:0 }}>

      <div style={casitaNavyCard}>
        {/* The function names the authority now, because only the rules table
            knows whether a city, the county or state law produced the number —
            "NO CITY/TOWN" is the county's storage value and is not a thing to
            print at a client. The fallback is the behaviour this had before
            those fields existed, for a response from an older deployment. */}
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#fdbf00', fontFamily:FONT_HEAD }}>
          {(rule.jurisdiction_label || parcel?.jurisdiction || 'THIS CITY')} · {rule.authority_label || (status === 'state_minimum' ? 'STATE CASITA LAW APPLIES' : 'CITY ORDINANCE APPLIES')}
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:'9px', flexWrap:'wrap', marginTop:'10px' }}>
          <span style={{ fontSize:'38px', fontWeight:700, lineHeight:1, fontFamily:FONT_HEAD, letterSpacing:'0.5px' }}>
            {casitaNum(rule.max_sqft)}
          </span>
          <span style={{ fontSize:'13px', color:'#B5D4F4', fontFamily:FONT_BODY }}>
            {status === 'state_minimum' ? 'sq ft at least' : 'sq ft max'}
          </span>
        </div>
        <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.88)', fontFamily:FONT_BODY, marginTop:'8px' }}>
          Up to two casitas: one attached and one detached. The size limit applies to each.
        </div>
        {rule.basis && (
          <div style={{ fontSize:'12.5px', color:'#B5D4F4', fontFamily:FONT_BODY, lineHeight:1.6, marginTop:'10px' }}>
            {rule.basis}
          </div>
        )}
        {(rule.max_sqft_with_permit != null || rule.second_casita_sqft != null) && (
          <div style={{ marginTop:'12px', paddingTop:'10px', borderTop:'1px solid rgba(255,255,255,0.14)', display:'flex', flexDirection:'column', gap:'4px' }}>
            {rule.max_sqft_with_permit != null && (
              <div style={{ fontSize:'12.5px', color:'#ffffff', fontFamily:FONT_BODY }}>
                Up to {casitaNum(rule.max_sqft_with_permit)} sq ft with an Administrative Use Permit
              </div>
            )}
            {rule.second_casita_sqft != null && (
              <div style={{ fontSize:'12.5px', color:'#ffffff', fontFamily:FONT_BODY }}>
                Second casita: up to {casitaNum(rule.second_casita_sqft)} sq ft
              </div>
            )}
          </div>
        )}
      </div>

      {/* The three fixed rows below Maximum size are the same for every city in
          the table, so they are literals rather than fields the function
          returns. If a city ever differs on one of them it belongs in the rule
          object, not here. */}
      <div style={casitaCard}>
        <div style={eyebrow}>WHAT THE RULE ALLOWS</div>
        {[
          ['Casitas allowed', rule.units || '—'],
          ['Maximum size', `${status === 'state_minimum' ? 'At least ' : ''}${casitaNum(rule.max_sqft)} sq ft`],
          ['Height', 'Zoning district maximum'],
          ['Side and rear setback', '5 ft minimum'],
          ['Extra parking', 'Not required'],
        ].map(([k, v]) => (
          <div key={k} style={factRow}>
            <span style={factLabel}>{k}</span>
            <span style={factValue}>{v}</span>
          </div>
        ))}
      </div>

      {Array.isArray(rule.flags) && rule.flags.length > 0 && (
        <div style={casitaAmberCard}>
          <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#7a5c00', fontFamily:FONT_HEAD, marginBottom:'8px' }}>
            CHECK BEFORE PROMISING
          </div>
          <ul style={{ margin:0, paddingLeft:'18px', display:'flex', flexDirection:'column', gap:'6px' }}>
            {rule.flags.map((f, i) => (
              <li key={i} style={{ fontSize:'12.5px', color:'#7a5c00', fontFamily:FONT_BODY, lineHeight:1.6 }}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {reportBar}

    </div>
  )

  // A plain sentence in a card, for the statuses that are an explanation rather
  // than a number. tone 'amber' for check_with_city, which is the one the
  // mockup frames; the rest are neutral because nothing is wrong with them.
  const noticeNode = (title, body, tone) => (
    <div style={tone === 'amber' ? casitaAmberCard : casitaCard}>
      {title && (
        <div style={{ fontSize:'14px', fontWeight:500, color: tone === 'amber' ? '#7a5c00' : '#1d2e42', fontFamily:FONT_BODY, marginBottom:'6px' }}>
          {title}
        </div>
      )}
      <div style={{ fontSize:'12.5px', color: tone === 'amber' ? '#7a5c00' : '#666', fontFamily:FONT_BODY, lineHeight:1.65 }}>
        {body}
      </div>
    </div>
  )

  // Left column per status. Right column is countyNode wherever a parcel came
  // back, so the layout below composes the two rather than each status
  // rebuilding the grid.
  let leftNode = null
  if (status === 'allowed' || status === 'state_minimum') {
    leftNode = ruleNode
  } else if (status === 'check_with_city') {
    leftNode = (
      <div style={casitaAmberCard}>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#7a5c00', fontFamily:FONT_HEAD, marginBottom:'8px' }}>
          {parcel?.jurisdiction || 'UNINCORPORATED'}
        </div>
        <div style={{ fontSize:'14px', fontWeight:500, color:'#7a5c00', fontFamily:FONT_BODY, marginBottom:'6px' }}>
          Check with the city
        </div>
        <div style={{ fontSize:'12.5px', color:'#7a5c00', fontFamily:FONT_BODY, lineHeight:1.65 }}>
          Arizona's casita law covers cities of 75,000 or more. {casitaTitleCase(parcel?.jurisdiction) || 'This jurisdiction'} writes its own rules, so there is no size we can promise from state law.
        </div>
        {/* What this city's code says, where it has been read. Below the
            sentence above and separated by a rule, because it narrows that
            sentence rather than replacing it: the answer is still "check with
            the city", and this is what to ask them. Absent for almost every
            jurisdiction, and the card is unchanged when it is. */}
        {cityNote && (
          <div style={{ fontSize:'12.5px', color:'#7a5c00', fontFamily:FONT_BODY, lineHeight:1.65, marginTop:'10px', paddingTop:'10px', borderTop:'1px solid rgba(122,92,0,0.22)' }}>
            {cityNote}
          </div>
        )}
      </div>
    )
  } else if (status === 'confirm_parcel') {
    leftNode = (
      <div style={casitaCard}>
        <div style={eyebrow}>COUNTY RECORD FOUND</div>
        <div style={{ fontSize:'14px', fontWeight:500, color:'#1d2e42', fontFamily:FONT_BODY, overflowWrap:'anywhere' }}>
          {parcel?.county_address || '—'}
        </div>
        <div style={{ fontSize:'12.5px', color:'#666', fontFamily:FONT_BODY, lineHeight:1.65, marginTop:'8px' }}>
          Is this the right property? The county's house number does not match the address you picked.
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap', marginTop:'12px' }}>
          <button
            onClick={()=>onUseApn && onUseApn(parcel.apn)}
            disabled={busy || !parcel?.apn}
            style={marketPrimaryStyle(!busy && !!parcel?.apn)}>
            {busy ? 'Checking…' : 'Yes, use this parcel'}
          </button>
          <button
            onClick={onClear}
            disabled={busy}
            style={{ background:'none', border:'none', padding:0, color:'#147efb', fontSize:'12px', fontFamily:FONT_BODY, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
            No, try another address
          </button>
        </div>
      </div>
    )
  } else if (status === 'multiple_parcels') {
    leftNode = (
      <div style={casitaCard}>
        <div style={eyebrow}>MORE THAN ONE PARCEL</div>
        <div style={{ fontSize:'12.5px', color:'#666', fontFamily:FONT_BODY, lineHeight:1.65, marginBottom:'12px' }}>
          The county returned more than one record for this address. Pick the one you mean.
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {candidates.map((c, i) => (
            <button
              key={c.apn || i}
              onClick={()=>onUseApn && onUseApn(c.apn)}
              disabled={busy || !c.apn}
              style={{
                width:'100%', textAlign:'left', background:'#ffffff', border:'0.5px solid #e8e8e8',
                borderRadius:'8px', padding:'10px 12px', fontFamily:FONT_BODY,
                cursor: (busy || !c.apn) ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
              }}>
              <span style={{ display:'block', fontSize:'12.5px', color:'#1d2e42', overflowWrap:'anywhere' }}>
                {c.county_address || '(no address on record)'}
              </span>
              {c.apn && (
                <span style={{ display:'block', fontSize:'11px', color:'#999', fontFamily:'monospace', marginTop:'2px' }}>{c.apn}</span>
              )}
            </button>
          ))}
        </div>
        <div style={{ marginTop:'12px' }}>
          <button
            onClick={onClear}
            disabled={busy}
            style={{ background:'none', border:'none', padding:0, color:'#147efb', fontSize:'12px', fontFamily:FONT_BODY, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
            Try another address
          </button>
        </div>
      </div>
    )
  } else if (status === 'luke_apz') {
    leftNode = (
      <div style={casitaAmberCard}>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#7a5c00', fontFamily:FONT_HEAD, marginBottom:'8px' }}>
          LUKE AFB · {luke?.apz}
        </div>
        <div style={{ fontSize:'14px', fontWeight:500, color:'#7a5c00', fontFamily:FONT_BODY, marginBottom:'6px' }}>
          Check with the city or county
        </div>
        <div style={{ fontSize:'12.5px', color:'#7a5c00', fontFamily:FONT_BODY, lineHeight:1.65 }}>
          This parcel appears to be inside Luke Air Force Base's {luke?.apz}. Maricopa County's Airport Districts I–III prohibit new residential buildings, including casitas, and cities apply their own airport overlays here. Nothing can be promised without the city's or county's answer.
        </div>
      </div>
    )
  } else if (status === 'luke_city_prohibited') {
    // Amber, like luke_apz and check_with_city — the three statuses that end
    // in "you cannot promise anything here". Deliberately NOT the navy card:
    // navy is the card with a number on it, and there is no number here.
    leftNode = (
      <div style={casitaAmberCard}>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'#7a5c00', fontFamily:FONT_HEAD, marginBottom:'8px' }}>
          {(parcel?.jurisdiction || 'THIS CITY')} · LUKE AFB TERRITORY
        </div>
        <div style={{ fontSize:'14px', fontWeight:500, color:'#7a5c00', fontFamily:FONT_BODY, marginBottom:'6px' }}>
          No casita allowed here
        </div>
        <div style={{ fontSize:'12.5px', color:'#7a5c00', fontFamily:FONT_BODY, lineHeight:1.65 }}>
          {/* The function's own words. The fallback covers a row saved before
              the note existed — the verdict is in the status either way. */}
          {note || "This city does not allow accessory dwelling units on single-family lots inside the territory in the vicinity of Luke AFB. Confirm with the city's planning department."}
        </div>
      </div>
    )
  } else if (status === 'not_single_family') {
    // The county's use code, quoted back. A parcel with NO code reaches this
    // status too — the function's gate is a whitelist — and there the sentence
    // has to say that rather than print "County use code null".
    leftNode = noticeNode(
      'Not a single-family lot',
      parcel?.puc
        ? `County use code ${parcel.puc} is not a single-family residence. Casitas apply to single-family lots.`
        : 'The county has no use code on this parcel, so it cannot be confirmed as a single-family residence. Casitas apply to single-family lots.',
    )
  } else if (status === 'stacked_parcel') {
    leftNode = noticeNode(
      'Not a single-family lot',
      'This looks like a condo or stacked unit. Casitas apply to single-family lots.',
    )
  } else if (status === 'missing_data') {
    leftNode = noticeNode(
      'No calculation possible',
      'The county record is missing lot size or living area for this parcel, so no calculation is possible.',
    )
  } else if (status === 'not_found') {
    leftNode = noticeNode(
      'Nothing on record',
      'No county parcel found at this address.',
    )
  }
  if (!leftNode) return null

  // One grid for every status. Two columns only when there is a county card to
  // put in the second one — not_found has no parcel, and a 280px empty column
  // beside one sentence would read as something that failed to load.
  return (
    <div style={{
      display:'grid',
      gridTemplateColumns: (isMobile || !countyNode) ? '1fr' : 'minmax(0,1fr) 280px',
      gap:'14px', alignItems:'start',
    }}>
      {leftNode}
      {countyNode}
    </div>
  )
}

// ─── CASITA CHECK: THE LIST ─────────────────────────────────────────────────
// The landing page at /casita. Modelled on MarketReportPage (App.js:9128): a
// create control at the top, the agent's saved rows below, and a click into a
// detail view at /casita/{id}.
//
// The lookup lives HERE rather than in the detail view because a check starts
// with an address and ends with a row — there is nothing to open until the
// county has answered. A successful check saves and navigates; the three
// interstitial statuses stay on this page because they are questions, not
// results.
function CasitaChecksPage({ session, profile, showToast, isMobile, onOpenCheck, navigateTo, isAdmin, signOut }) {
  const [address, setAddress] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [lat, setLat] = useState(null)
  const [lng, setLng] = useState(null)
  const [placesReady, setPlacesReady] = useState(false)
  // Distinct from !placesReady, which is also true for the second or two the
  // loader is in flight. Only this says the lookup is never coming, and only
  // it earns the "reload the page" hint.
  const [placesFailed, setPlacesFailed] = useState(false)
  const autocompleteRef = useRef(null)   // the PlaceAutocompleteElement instance

  // A CALLBACK REF, not an effect — the one place this page departs from
  // LinksHomeValueCard (App.js:757). Links mounts its element from an effect
  // keyed on [placesReady, step, done]: every flag that gates its container,
  // all three its own local state. Here the container lives inside `listNode`,
  // which is handed to CaseShell as `emptyState`, and CaseShell renders
  // NOTHING but "Loading..." until its own `loading` prop clears
  // (App.js:5528). So the container's presence is gated by ANOTHER
  // component's internals, which no dependency array in this function can
  // track. A callback ref fires exactly when the node attaches, whatever put
  // it there, and again if it is ever remounted.
  const mountAddressEl = useCallback((node) => {
    if (node && autocompleteRef.current) node.appendChild(autocompleteRef.current)
  }, [])

  const [checking, setChecking] = useState(false)
  const [interstitial, setInterstitial] = useState(null)   // a question, not a result
  const [error, setError] = useState(null)
  // The body of the last request, so Retry repeats exactly what failed rather
  // than rebuilding one from state the agent may have edited since.
  const lastBodyRef = useRef(null)
  // The Google address that produced the last request, so an APN follow-up
  // still saves under the address the agent typed rather than the county's.
  const lastAddressRef = useRef('')

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Collapsed on arrival. Local state, not persisted: there is no
  // casita_help_seen column and this change adds none.
  const [helpOpen, setHelpOpen] = useState(false)

  // MarketReportPage's read shape: destructure { data, error }, log rather
  // than toast on a read failure, and coalesce to [] so an RLS refusal renders
  // as an empty list rather than a crash.
  const fetchRows = useCallback(async () => {
    const { data, error: readErr } = await supabase.from('casita_checks')
      .select('*').eq('user_id', session.user.id).order('updated_at', { ascending: false })
    if (readErr) console.error('casita checks query error:', readErr)
    setRows(data || [])
    setLoading(false)
  }, [session.user.id])
  useEffect(() => { fetchRows() }, [fetchRows])

  // Google Places via the modern PlaceAutocompleteElement, the same approach
  // LinksHomeValueCard uses (App.js:682) — NOT the Strategist's legacy
  // Autocomplete, which takes its input node in the constructor. Built detached
  // once and appended to its container, so nothing depends on mount order.
  //
  // street_number is the field that matters most here and is the reason this
  // fetches addressComponents at all: casita-check compares it against the
  // county's own house number and asks the agent to confirm when they differ.
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY
    if (!key) { console.warn('[Places/casita] REACT_APP_GOOGLE_PLACES_KEY not set; using plain text input'); setPlacesFailed(true); return }
    let cancelled = false
    const loader = new Loader({ apiKey: key, version: 'weekly', libraries: ['places'] })
    loader.load()
      .then((google) => google.maps.importLibrary('places'))
      .then((placesLib) => {
        if (cancelled) return
        const { PlaceAutocompleteElement } = placesLib
        if (!PlaceAutocompleteElement) { console.warn('[Places/casita] PlaceAutocompleteElement unavailable'); setPlacesFailed(true); return }
        const pae = new PlaceAutocompleteElement({
          includedPrimaryTypes: ['street_address'],
          includedRegionCodes: ['us'],
          // Hard-restricted to Arizona. The only parcel source behind this page
          // is the Maricopa County Assessor, so an out-of-state suggestion
          // could only ever resolve to no parcel at all.
          locationRestriction: { north: 37.0, south: 31.3, east: -109.0, west: -114.8 },
        })
        pae.placeholder = 'Start typing the property address'
        Object.assign(pae.style, {
          width: '100%', boxSizing: 'border-box', padding: '10px 12px',
          borderRadius: '8px', border: `0.5px solid ${RAIL_BORDER}`,
          background: '#ffffff', color: '#1d2e42', fontSize: '13px', fontFamily: FONT_BODY,
        })

        // Typed text with no selection: read the real input through the shadow
        // DOM via composedPath. A manual edit invalidates the point and the
        // house number, which described the suggestion rather than this text.
        pae.addEventListener('input', (e) => {
          const node = (e.composedPath && e.composedPath()[0]) || e.target
          const v = node && typeof node.value === 'string' ? node.value : ''
          setAddress(v)
          setStreetNumber(''); setLat(null); setLng(null)
        })

        pae.addEventListener('gmp-select', async (event) => {
          const prediction = event.placePrediction || (event.detail && event.detail.placePrediction)
          if (!prediction) return
          try {
            const place = prediction.toPlace()
            await place.fetchFields({ fields: ['formattedAddress', 'addressComponents', 'location'] })
            if (cancelled) return
            const comps = place.addressComponents || []
            const pick = (type, short) => {
              const c = comps.find((x) => (x.types || []).includes(type))
              return c ? ((short ? c.shortText : c.longText) || '') : ''
            }
            const streetLine = [pick('street_number'), pick('route')].filter(Boolean).join(' ')
            const loc = place.location
            const latVal = loc ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null
            const lngVal = loc ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null
            setAddress(place.formattedAddress || streetLine || '')
            setStreetNumber(pick('street_number'))
            setLat(typeof latVal === 'number' ? latVal : null)
            setLng(typeof lngVal === 'number' ? lngVal : null)
            // A new address makes the question on screen about a different
            // property, so it goes rather than sitting under the new text.
            setInterstitial(null)
            setError(null)
          } catch (err) {
            console.error('[Places/casita] fetchFields failed:', err)
          }
        })

        autocompleteRef.current = pae
        setPlacesReady(true)
      })
      .catch((err) => { console.error('[Places/casita] loader failed:', err); if (!cancelled) setPlacesFailed(true) })
    return () => { cancelled = true }
  }, [])

  // Check, then SAVE AND LEAVE. A response that settled on one parcel is a
  // result and belongs at its own URL; the three interstitial statuses are
  // questions and stay here until the agent answers one.
  async function runAndSave(body, addressForRow) {
    if (checking) return
    setChecking(true)
    setError(null)
    lastBodyRef.current = body
    if (typeof addressForRow === 'string') lastAddressRef.current = addressForRow
    try {
      const data = await runCasitaCheck(body)

      if (!data.parcel || CASITA_INTERSTITIAL.has(data.status)) {
        setInterstitial(data)
        return
      }

      const { row, error: saveErr, refused } = await upsertCasitaCheck({
        userId: session.user.id,
        data,
        address: lastAddressRef.current,
      })
      // A refused write is not an error — see the note on upsertCasitaCheck.
      // Either way the agent is told and stays put, because navigating to a
      // row that was never written would land on a detail view with nothing
      // to load.
      if (saveErr) { setError(`Checked, but could not save: ${saveErr.message}`); return }
      if (refused || !row) { setError('Checked, but the save was refused. Nothing was kept.'); return }

      setInterstitial(null)
      onOpenCheck(row.id)
    } catch (e) {
      console.error('[casita-check]', e)
      setInterstitial(null)
      setError(e.message)
    } finally {
      setChecking(false)
    }
  }

  const hasPoint = typeof lat === 'number' && typeof lng === 'number'
  const canCheck = hasPoint && !checking

  function checkLot() {
    if (!canCheck) return
    runAndSave({ lat, lng, street_number: streetNumber, address }, address)
  }

  // Back to a live field. Clears the question but keeps the address, so "No,
  // try another address" leaves the agent looking at what they typed rather
  // than an empty box they have to remember.
  function clearInterstitial() {
    setInterstitial(null)
    setError(null)
  }

  // .select() and a row count, the same guard every other list in this file
  // uses: a delete refused by RLS matches zero rows and returns no error.
  async function deleteCheck(id) {
    const { data: deleted, error: delErr } = await supabase
      .from('casita_checks').delete().eq('id', id).select()
    if (delErr) { showToast('Delete failed: ' + delErr.message, true); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the check is still there.', true)
      setConfirmDelete(null)
      return
    }
    setRows(prev => prev.filter(r => r.id !== id))
    setConfirmDelete(null)
    showToast('Check deleted')
  }

  const renderCard = (r) => {
    const chip = CASITA_STATUS_CHIP[r.status] || { tone: 'grey', label: r.status }
    const jurisdictionLabel = r.result?.jurisdiction_label || r.jurisdiction
    const chips = (
      <>
        {caseChip(chip.tone, chip.label)}
        {jurisdictionLabel && caseChip('grey', jurisdictionLabel)}
      </>
    )
    // The number when there is one, the status in words when there is not —
    // a card reading "Max — sq ft" would say nothing twice.
    const meta = [
      r.max_sqft != null ? `Max ${casitaNum(r.max_sqft)} sq ft` : chip.label,
      `checked ${timeAgo(r.checked_at || r.updated_at)}`,
    ].join(' · ')
    return (
      <CaseCard
        key={r.id}
        chips={chips}
        address={r.address || r.county_address || 'Untitled'}
        meta={meta}
        onOpen={()=>onOpenCheck(r.id)}
        deletable
        confirming={confirmDelete === r.id}
        onAskDelete={()=>setConfirmDelete(r.id)}
        onConfirmDelete={()=>deleteCheck(r.id)}
        onCancelDelete={()=>setConfirmDelete(null)}
      />
    )
  }

  const q = search.trim().toLowerCase()
  const filtered = q
    ? rows.filter(r => (r.address || '').toLowerCase().includes(q)
                    || (r.county_address || '').toLowerCase().includes(q)
                    || (r.apn || '').toLowerCase().includes(q))
    : rows

  // AGENT ONLY, and screen only — nothing here ever reaches CasitaReportPDF.
  // These are the six things an agent gets wrong reading a result out loud to
  // a client, so they belong beside the tool rather than inside the document
  // the client keeps.
  //
  // Same navy-gradient-and-gold treatment as the Strategist's HOW TO USE THIS
  // panel — every colour, size and the item row's markup are that panel's.
  // Two differences, both deliberate: it collapses rather than dismissing,
  // because there is no casita_help_seen column and this pass adds no database
  // change; and the item marker is a bullet rather than a number, because the
  // Strategist's four are ordered steps and these six are independent
  // cautions that no order applies to.
  const HELP_POINTS = [
    ['"One attached and one detached" means both.', 'Most cities allow two casitas on one lot, and the size limit applies to each. Whether two actually fit is a different question.'],
    ['The number is a ceiling, not a promise.', 'Lot coverage, height and 5-foot setbacks still apply, and the city applies them at permit time. A small lot with a big house may not hold the full size.'],
    ['Talk in ranges, not guarantees.', '"The city allows up to 1,000 sq ft here" is accurate. "You can build a 1,000 sq ft casita" is not.'],
    ['HOA first.', "If there's an HOA, pull the CC&Rs before the client gets excited. HOAs can say no even where the city says yes."],
    ['"Check with the city/county" is a real answer.', "Small towns and county islands set their own rules. Don't guess a number for them."],
    ["County records aren't the MLS.", 'Living area and lot size come from the Assessor and can differ from the listing. The report says where the numbers came from.'],
  ]

  const helpPanel = (
    <div style={{background:'linear-gradient(135deg, #1d2e42 0%, #2a4060 100%)',border:'1px solid #fdbf00',borderRadius:'12px',padding: helpOpen ? '20px 24px' : '14px 24px',marginBottom:'20px',color:'white',fontFamily:FONT_BODY}}>
      <button
        onClick={()=>setHelpOpen(o => !o)}
        aria-expanded={helpOpen}
        style={{width:'100%',display:'flex',alignItems:'center',gap:'12px',background:'transparent',border:'none',padding:0,cursor:'pointer',textAlign:'left'}}>
        <span style={{flex:1,minWidth:0,fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',fontFamily:FONT_HEAD}}>
          HOW TO READ THIS
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdbf00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
          {helpOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {helpOpen && (
        <div style={{display:'flex',flexDirection:'column',gap:'10px',fontSize:'13px',lineHeight:1.5,color:'#cbd5e1',marginTop:'12px'}}>
          {HELP_POINTS.map(([lead, rest], i) => (
            <div key={i}><span style={{color:'#fdbf00',fontWeight:700}}>•</span> <strong style={{color:'white'}}>{lead}</strong> {rest}</div>
          ))}
        </div>
      )}
    </div>
  )

  const listNode = (
    <div style={{ paddingTop: isMobile ? '8px' : '24px' }}>
      <ToolIntroBand
        title="Casita Check"
        description="What this lot allows under Arizona casita law and the city's own rules."
        stats={[
          (<><span style={{ color:'#fdbf00' }}>Maricopa County</span> parcel records</>),
          'Not a survey',
        ]}
      />

      {helpPanel}

      <div style={{ ...casitaCard, marginBottom:'14px' }}>
        {/* htmlFor ONLY on the fallback branch. When Places is live the real
            input lives inside the element's shadow DOM and carries no id at
            all, so an htmlFor there would point at nothing — a label that
            claims an association it does not have is worse for a screen reader
            than one that just sits above the field. */}
        <label htmlFor={placesReady ? undefined : 'casita-address'} style={{ ...SETUP_EYEBROW, display:'block', marginBottom:'8px' }}>
          CHECK A NEW ADDRESS
        </label>
        <div style={createRow(isMobile)}>
          <div style={{ flex:1, minWidth:0 }}>
            {placesReady
              ? <div ref={mountAddressEl} style={{ width:'100%' }} />
              : (
                <input
                  id="casita-address"
                  value={address}
                  onChange={e=>setAddress(e.target.value)}
                  placeholder="Start typing the property address"
                  style={{ ...S.input, fontSize:'13px', padding:'10px 12px', background:'#ffffff', border:`0.5px solid ${RAIL_BORDER}`, borderRadius:'8px', marginBottom:0 }}
                />
              )}
          </div>
          <button onClick={checkLot} disabled={!canCheck} style={marketPrimaryStyle(canCheck)}>
            {checking ? 'Checking…' : 'Check lot'}
          </button>
        </div>
        {/* Says WHY the button is off rather than leaving a dead control. The
            point is the thing the lookup runs on, so a typed address that was
            never picked from the list cannot be checked. */}
        {!hasPoint && (
          <div style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY, marginTop:'8px' }}>
            {placesReady
              ? 'Pick the address from the list — the lookup runs on the map point, not the text.'
              : placesFailed
                ? 'Address lookup is unavailable right now, so a lot cannot be checked. Try reloading the page.'
                : 'Loading address lookup… type here in the meantime, then pick the address once suggestions appear.'}
          </div>
        )}
      </div>

      {checking && !interstitial && (
        <div style={{ ...casitaCard, marginBottom:'14px', color:'#666', fontSize:'12px', fontFamily:FONT_BODY }}>
          Checking Maricopa County parcel records…
        </div>
      )}

      {/* A plain message and a retry. lastBodyRef holds the request that failed,
          so Retry sends that one again rather than rebuilding it from state. */}
      {error && (
        <div style={{ ...casitaCard, marginBottom:'14px', borderColor:'#C8102E' }}>
          <div style={{ fontSize:'12.5px', color:'#C8102E', fontFamily:FONT_BODY, marginBottom:'8px', lineHeight:1.6 }}>{error}</div>
          <button
            onClick={()=>{ if (lastBodyRef.current) runAndSave(lastBodyRef.current) }}
            disabled={checking || !lastBodyRef.current}
            style={marketSecondaryStyle(!checking && !!lastBodyRef.current)}>
            {checking ? 'Checking…' : 'Try again'}
          </button>
        </div>
      )}

      {/* The interstitial statuses only. Anything that settled on a parcel has
          already been saved and the agent is on its page by now. */}
      {!error && interstitial && (
        <div style={{ marginBottom:'18px' }}>
          <CasitaResultView
            data={interstitial}
            isMobile={isMobile}
            busy={checking}
            onUseApn={(apn)=>runAndSave({ apn })}
            onClear={clearInterstitial}
          />
        </div>
      )}

      {rows.length > 0 && (
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          onFocus={()=>setSearchFocused(true)}
          onBlur={()=>setSearchFocused(false)}
          placeholder="Search by address or parcel number"
          style={{
            width:'100%', boxSizing:'border-box', marginBottom:'16px',
            background:'#ffffff', borderRadius:'18px',
            border: searchFocused ? '0.5px solid #147efb' : '0.5px solid #e8e8e8',
            padding:'5px 12px', fontSize:'11px', fontFamily:FONT_BODY,
            color:'#1d2e42', outline:'none',
          }}
        />
      )}

      {rows.length === 0 ? (
        <div style={{ padding:'24px 0', color:'#999', fontSize:'13px', fontFamily:FONT_BODY }}>
          No saved checks yet. Check an address above and it is kept here.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:'24px 0', color:'#999', fontSize:'13px', fontFamily:FONT_BODY }}>
          No checks match that search.
        </div>
      ) : (
        <div style={strategyGrid(isMobile)}>
          {filtered.map(renderCard)}
        </div>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Casita Check"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={CASITA_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── CASITA CHECK: ONE SAVED CHECK ──────────────────────────────────────────
// /casita/{id}. Reads the row, rebuilds the response shape it was saved from,
// and renders it with the SAME component that drew it live — so a reopened
// check and a fresh one cannot look different.
//
// There is no address input here. A check is about one parcel; changing the
// address means a different check, which is what the list page is for.
function CasitaCheckView({ session, profile, checkId, showToast, isMobile, onBack, navigateTo, isAdmin, signOut }) {
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rechecking, setRechecking] = useState(false)

  // ── Client report ───────────────────────────────────────────────────────
  // Only reachable on `allowed` and `state_minimum`, which are the two
  // statuses that carry a rule. The preview hook and the overlay are the same
  // ones the Strategist uses — see REPORT DELIVERY (shared).
  const [reportLanguage, setReportLanguage] = useState('en')
  const [generating, setGenerating] = useState(false)
  const [reportPreview, setReportPreview, closeReportPreview] = useReportPreview()

  // MarketReportView's loader (App.js:9355): a missing or refused row sends
  // the agent back to the list with a toast rather than rendering an empty
  // page. An RLS refusal and a deleted row are indistinguishable here, and
  // both mean the same thing to the agent.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { data, error } = await supabase.from('casita_checks').select('*').eq('id', checkId).single()
      if (cancelled) return
      if (error || !data) { showToast('Could not open that check.', true); onBack(); return }
      setRow(data)
      setLoading(false)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkId])

  const data = casitaRowToResponse(row)
  const parcel = data?.parcel || null
  const rule = data?.result || null

  // Re-run the county and the rule against the SAME parcel. By APN, never by
  // address: the parcel is settled, and re-resolving an address could land on
  // a different lot and quietly overwrite this row with another property.
  async function recheck() {
    if (rechecking || !row?.apn) return
    setRechecking(true)
    try {
      const fresh = await runCasitaCheck({ apn: row.apn })
      if (!fresh.parcel) {
        showToast('The county no longer returns a parcel for this APN.', true)
        return
      }
      const { row: saved, error: saveErr, refused } = await upsertCasitaCheck({
        userId: session.user.id,
        data: fresh,
        // The agent's own address for this row is kept: the county's string
        // is already on the card below, and overwriting the label they
        // recognise would make their own list harder to read.
        address: row.address,
      })
      if (saveErr) { showToast(`Re-checked, but could not save: ${saveErr.message}`, true); return }
      if (refused || !saved) { showToast('Re-checked, but the save was refused.', true); return }
      setRow(saved)
      showToast('Re-checked against county records')
    } catch (e) {
      console.error('[casita recheck]', e)
      showToast(`Re-check failed: ${e.message}`, true)
    } finally {
      setRechecking(false)
    }
  }

  // The two statuses that carry a rule, named rather than inferred. reportBar
  // is only rendered inside CasitaResultView's rule branch, so the guard below
  // is already unreachable on any other status — it is written down so that a
  // future status that happens to fill `result` cannot quietly acquire a PDF.
  const CASITA_REPORTABLE = ['allowed', 'state_minimum']

  // NOTHING IS UPLOADED and no file row is written. The document is
  // reproducible from this row in about two seconds, and storing one would
  // create a copy of a rule that can change with nothing on the file to say
  // which version it holds.
  async function generateCasitaReport() {
    if (generating || !profile?.brokerage || !parcel || !rule) return
    if (!CASITA_REPORTABLE.includes(row?.status)) return
    setGenerating(true)
    try {
      let blob
      try {
        blob = await pdf(
          <CasitaReportPDF data={data} profile={profile} email={session.user.email} language={reportLanguage} />
        ).toBlob()
      } catch (pdfErr) {
        throw new Error(`Could not build PDF: ${pdfErr.message}`)
      }
      const dateStr = new Date().toISOString().slice(0, 10)
      const fileName = `${addressSlug(parcel.county_address)}_Casita-Check_${dateStr}.pdf`

      // Desktop only. iOS ignores <a download> and NAVIGATES to the blob: URL,
      // which would take the tab before the overlay could paint. On a phone
      // the overlay opens and its Share button is a fresh user gesture, which
      // is the only way navigator.share reliably fires after an await. The
      // Strategy report and Market Snapshot do the same.
      if (!isMobile) triggerBlobDownload(blob, fileName)

      // A SECOND object URL where the download fired: triggerBlobDownload
      // revokes its own the moment it runs, so reusing it would hand the
      // iframe a dead handle. useReportPreview revokes this one.
      setReportPreview({
        blob,
        blobUrl: URL.createObjectURL(blob),
        fileName,
        language: reportLanguage,
        addressLabel: parcel.county_address,
      })
    } catch (e) {
      console.error('[casita report]', e)
      showToast(`Report failed: ${e.message}`, true)
    } finally {
      setGenerating(false)
    }
  }

  // This document's share caption. Everything else is shareReportFile.
  async function shareCasitaReport() {
    return shareReportFile(
      reportPreview,
      `Casita Check — ${reportPreview?.addressLabel || 'HomePros'}`,
      showToast,
      "This browser can't share files. Use Download, then attach the file.",
    )
  }

  // The report bar, on the Strategist's pattern (App.js controlsNode): a tinted
  // card holding the language toggle, who signs it, and the one gold action.
  // The two segmented-control styles are copied rather than lifted — they are
  // ten lines of literals and each view's cursor reads its own busy flag, so
  // sharing them would mean threading that flag through for no gain.
  const CTRL_PAD_Y = '4px'
  const segWrap = { display:'flex', alignItems:'center', background:'#ffffff', border:'0.5px solid #d8d8d4', borderRadius:'16px', overflow:'hidden', flexShrink:0 }
  const segBtn = (active, divider = false) => ({
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:`${CTRL_PAD_Y} 11px`, fontSize:'11.5px', fontWeight:600, fontFamily:FONT_BODY,
    border:'none', whiteSpace:'nowrap',
    ...(divider ? { borderRight:'0.5px solid #e8e8e8' } : {}),
    cursor: generating ? 'not-allowed' : 'pointer',
    background: active ? '#ffffff' : 'transparent',
    color: active ? '#1d2e42' : '#888',
  })
  const canGenerate = !!profile?.brokerage && !generating

  const reportBar = (
    <div style={{ background:'#f4f4f2', border:'1px solid #d8d8d4', borderRadius:'12px', padding:'11px 14px',
                  display:'flex', flexDirection:'row', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
      <span style={{ fontSize:'12px', color:'#999', fontFamily:FONT_BODY, flexShrink:0 }}>Report:</span>
      <div style={segWrap}>
        <button onClick={()=>setReportLanguage('en')} disabled={generating} style={segBtn(reportLanguage === 'en', true)}>English</button>
        <button onClick={()=>setReportLanguage('es')} disabled={generating} style={segBtn(reportLanguage === 'es')}>Español</button>
      </div>
      {profile?.full_name && (
        <span style={{ fontSize:'10.5px', color:'#999', fontFamily:FONT_BODY, whiteSpace:'nowrap' }}>
          Signed by {profile.full_name}
        </span>
      )}
      {/* Says WHY rather than leaving a dead button. The brokerage is the
          wordmark at the top of the document and is named in its disclaimer,
          so there is no report to make without one — the same gate
          generateReport applies in the Strategist. */}
      {!profile?.brokerage && (
        <span style={{ fontSize:'12px', color:'#666', fontFamily:FONT_BODY }}>
          Set your brokerage in Profile to generate a report.
        </span>
      )}
      <div style={{ marginLeft:'auto', flexShrink:0 }}>
        <button
          onClick={generateCasitaReport}
          disabled={!canGenerate}
          style={{ ...S.btnPrimary, padding:`${CTRL_PAD_Y} 14px`, fontSize:'12px', flex:'none', whiteSpace:'nowrap',
                   background:'#fdbf00', color:'#1d2e42', opacity: canGenerate ? 1 : 0.5 }}>
          {generating ? 'Generating…' : 'Generate Report'}
        </button>
      </div>
    </div>
  )

  const bodyNode = row ? (
    <div style={{ paddingTop: isMobile ? '8px' : '24px' }}>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', marginBottom:'14px' }}>
        <button onClick={onBack}
          style={{ background:'none', border:'none', padding:0, color:'#147efb', fontSize:'11.5px', fontFamily:FONT_BODY, cursor:'pointer' }}>
          ← Back to checks
        </button>
        <div style={{ display:'flex', alignItems:'baseline', gap:'12px', flexWrap:'wrap' }}>
          {/* When the county was asked, not when the row was last written.
              A saved number is only as good as its date, and the agent is the
              one who decides whether it is old enough to redo. */}
          <span style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY }}>
            Checked {timeAgo(row.checked_at || row.updated_at)}
          </span>
          <button onClick={recheck} disabled={rechecking} style={marketSecondaryStyle(!rechecking)}>
            {rechecking ? 'Re-checking…' : 'Re-check'}
          </button>
        </div>
      </div>

      <CasitaResultView data={data} isMobile={isMobile} reportBar={reportBar} />
    </div>
  ) : null

  return (
    <>
      <CaseShell
        isMobile={isMobile}
        title={row?.address || 'Casita Check'}
        summary={null}
        setupOpen={false}
        onToggleSetup={null}
        setup={null}
        loading={loading}
        messages={[]}
        renderMessage={()=>null}
        emptyState={bodyNode}
        contentWidth={CASITA_CONTENT_WIDTH}
        controls={null}
        showComposer={false}
        composerValue=""
        onComposerChange={()=>{}}
        onSubmit={()=>{}}
        composerPlaceholder=""
        sendDisabled
        footer={null}
      />

      {/* The same overlay the Strategist opens. Returns null until a report
          exists, so it costs nothing on every other visit to this page. */}
      <ReportPreviewOverlay
        preview={reportPreview}
        isMobile={isMobile}
        onClose={closeReportPreview}
        onShare={shareCasitaReport}
        docLabel="Casita Check report"
      />
    </>
  )
}


function StrategyPage({ session, showToast, isMobile, onOpenCase, profile, navigateTo, isAdmin, signOut }) {
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newAddress, setNewAddress] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  // Client-side filter over rows already in memory. No query and no debounce —
  // the whole list is fetched on mount with no limit, so there is nothing to
  // wait for and every keystroke is a synchronous array filter.
  const [search, setSearch] = useState('')
  // Drives the search input's focus ring. The field sets outline:'none' to keep
  // the pill shape the browser's square outline would break, so without this
  // there is no focus affordance at all and a keyboard user tabbing through the
  // page has nothing telling them where they are.
  const [searchFocused, setSearchFocused] = useState(false)
  // Same job for the create field, which shares the search field's pill metrics
  // and so had the same missing focus affordance. Separate state rather than one
  // shared flag: both fields are on screen at once, and a shared one would light
  // up whichever field the agent is not in.
  const [createFocused, setCreateFocused] = useState(false)

  const fetchCases = useCallback(async () => {
    const userId = session.user.id

    // file_type rather than count on the files embed. The count was one integer
    // that blended agent uploads with generated report PDFs, so a card could not
    // tell "3 documents attached" from "1 CMA and two regenerations" — and in
    // production 125 of 255 file rows are reports, so the number was wrong on
    // most cards. Selecting the column instead returns one short string per file
    // and both facts fall out client-side. Still ONE query each; the messages
    // embed stays an aggregate because a bare count is all it was ever asked for.
    //
    // Query 1: cases I own
    const ownedPromise = supabase.from('property_cases')
      .select('*, property_case_files(file_type), property_case_messages(count)')
      .eq('agent_id', userId)

    // Query 2: cases shared with me
    const sharedPromise = supabase.from('property_cases')
      .select('*, property_case_files(file_type), property_case_messages(count)')
      .contains('shared_with', [userId])

    const [ownedRes, sharedRes] = await Promise.all([ownedPromise, sharedPromise])

    if (ownedRes.error) console.error('owned cases query error:', ownedRes.error)
    if (sharedRes.error) console.error('shared cases query error:', sharedRes.error)

    const owned = ownedRes.data || []
    const shared = sharedRes.data || []

    // Merge & dedupe by id (an admin who owns AND is in shared_with should only see one row)
    const seen = new Set()
    const merged = []
    for (const c of [...owned, ...shared]) {
      if (seen.has(c.id)) continue
      seen.add(c.id)
      merged.push(c)
    }

    // Find all distinct owner ids that are NOT the current user — need their names for the SHARED BY badge
    const otherOwnerIds = [...new Set(merged.map(c => c.agent_id).filter(id => id && id !== userId))]

    let ownersById = {}
    if (otherOwnerIds.length > 0) {
      const { data: owners, error: ownersErr } = await supabase.from('profiles')
        .select('id, full_name, email')
        .in('id', otherOwnerIds)
      if (ownersErr) console.error('owner names query error:', ownersErr)
      if (owners) ownersById = Object.fromEntries(owners.map(o => [o.id, o]))
    }

    // Attach owner profile to each case as `agent` (matches the field name we use in render)
    const decorated = merged.map(c => ({
      ...c,
      agent: c.agent_id === userId ? null : (ownersById[c.agent_id] || null)
    }))

    // Sort by updated_at desc to match previous behavior
    decorated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))

    setCases(decorated)
    setLoading(false)
  }, [session.user.id])

  useEffect(() => { fetchCases() }, [fetchCases])

  async function createCase() {
    // 2, not 5. The 5 was sized for an address; this field names the case now,
    // and "Smith" is a name. Two still stops an empty or single-keystroke
    // submission without the app deciding what counts as a name.
    if (newAddress.trim().length < 2) return
    setCreating(true)
    const { data, error } = await supabase.from('property_cases')
      .insert({ agent_id: session.user.id, subject_address: newAddress.trim() }).select().single()
    if (error) { showToast('Error creating case'); setCreating(false); return }
    setCreating(false)
    setNewAddress('')
    onOpenCase(data.id)
  }

  // .select() and a row count, the same guard AdminPage uses. A row refused by
  // RLS is NOT an error: PostgREST applies the policy as a filter, so a refused
  // delete matches zero rows and returns { error: null }. Without counting, a
  // refusal was indistinguishable from success — and this handler dropped the
  // row from local state either way, so it showed "Case deleted" and the card
  // vanished until the next load brought it back.
  async function deleteCase(caseId) {
    const { data: deleted, error } = await supabase
      .from('property_cases').delete().eq('id', caseId).select()
    if (error) { showToast('Delete failed: ' + error.message); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the case is still there. You may not have permission to delete it.')
      setConfirmDelete(null)
      return
    }
    setCases(prev => prev.filter(c => c.id !== caseId))
    setConfirmDelete(null)
    showToast('Case deleted')
  }

  // Soft-tint bg/text pairs, the same family as the neutral status chips, so a
  // confidence chip reads as one more chip rather than as a second visual
  // system. The saturated fills these replace put white-on-colour at the top of
  // every card, which made the machine's confidence grade louder than the
  // address — the one thing on the card an agent is actually scanning for.
  const CONF_TONES = {
    High:     { background: '#EAF3DE', color: '#27500A' },
    Moderate: { background: '#E4EDF7', color: '#123763' },
    Limited:  { background: '#FAEEDA', color: '#633806' },
    Low:      { background: '#FCEBEB', color: '#791F1F' },
  }
  // Also the fallback for a confidence value outside the four the report
  // function whitelists. The old fallback was a grey FILL with white text, which
  // looked deliberate; neutral makes an unexpected value read as "no grade".
  const NEUTRAL_TONE = { background: '#F4F4F2', color: '#666' }

  // Everything both renderers need, derived once. The card and the compact row
  // show the same facts in two shapes, so this lives in one place rather than
  // being copied into each — two copies would be two things to keep in step, and
  // the file_type rules below are exactly the kind of detail that drifts.
  const caseFacts = (c) => {
    // The files embed returns rows, not an aggregate. filter(Boolean) because
    // file_type is nullable — Deal Desk writes a null type and a legacy Strategy
    // row may carry one, and an undefined here would throw on startsWith below.
    const fileTypes = (c.property_case_files || []).map(f => f.file_type).filter(Boolean)
    // Only the three agent-upload slots count as "files" to an agent. A
    // regenerated report must not inflate this.
    const uploadCount = fileTypes.filter(t => ['cma','mls_sheet','tax_record'].includes(t)).length
    // Prefix, not an equality check against the two types the current code
    // writes. The live table also holds strategy_report_agent_current and
    // strategy_report_agent_initial from the old two-PDF system; that code path
    // is gone but those rows still mean a report exists, and a card claiming
    // otherwise would be wrong about history.
    const hasReport = fileTypes.some(t => t.startsWith('strategy_report_'))
    const msgCount = c.property_case_messages?.[0]?.count || 0
    const isSharedToMe = c.agent_id !== session.user.id
    const ownerName = c.agent?.full_name || c.agent?.email || 'Admin'
    const meta = [
      timeAgo(c.updated_at),
      uploadCount > 0 ? `${uploadCount} file${uploadCount!==1?'s':''}` : null,
      msgCount > 0 ? `${msgCount} message${msgCount!==1?'s':''}` : null,
    ].filter(Boolean).join(' · ')
    // Only the EXCEPTION gets a line. Twelve of fourteen cards said "Report
    // ready", which is the normal end state — a label every card carries is a
    // label nobody reads, and it cost a row of vertical space on each one. A
    // card with no stage line now means the complete, expected thing happened;
    // a card with one is asking for something.
    const stage = hasReport
      ? null
      : msgCount > 0
        ? { text: 'Analyzed · no report', color: '#666' }
        : { text: 'Setup not finished', color: '#999' }
    // EVERY card gets a status chip. The old block rendered nothing at all
    // without confidence or a share, so a grid of new cases had a ragged top
    // edge where some cards started with a chip row and others with the address.
    const statusChip = c.confidence
      ? <span style={{...CASE_CHIP, ...(CONF_TONES[c.confidence] || NEUTRAL_TONE)}}>{c.confidence}</span>
      : <span style={{...CASE_CHIP, ...NEUTRAL_TONE}}>{msgCount > 0 ? 'In progress' : 'Draft'}</span>
    // SHARED BY is additive, not an alternative — it answers a different
    // question (whose case is this) than the status chip does.
    const chips = (
      <>
        {statusChip}
        {isSharedToMe && (
          <span style={{...CASE_CHIP,background:'rgba(20,126,251,0.12)',color:'#147efb'}}>SHARED BY {ownerName.toUpperCase()}</span>
        )}
      </>
    )
    return { address: c.subject_address || 'Untitled Property', meta, stage, chips, isSharedToMe }
  }

  // One card definition, called from both the grouped path and the flat search
  // path. Extracted rather than duplicated so the two can never drift.
  const renderCard = (c) => {
    const f = caseFacts(c)
    return (
      <CaseCard
        key={c.id}
        chips={f.chips}
        address={f.address}
        meta={f.meta}
        stage={f.stage}
        onOpen={()=>onOpenCase(c.id)}
        deletable={!f.isSharedToMe}
        confirming={confirmDelete === c.id}
        onAskDelete={()=>setConfirmDelete(c.id)}
        onConfirmDelete={()=>deleteCase(c.id)}
        onCancelDelete={()=>setConfirmDelete(null)}
      />
    )
  }

  // Search runs off `cases`, which is already sorted updated_at desc, so the
  // filtered list keeps that order for free.
  const q = search.trim().toLowerCase()
  const searching = q.length > 0
  const filtered = searching
    ? cases.filter(c => (c.subject_address || '').toLowerCase().includes(q))
    : cases

  // Grouped once per render rather than filtering the list four times. `now` is
  // captured once so every card in a pass is bucketed against the same instant —
  // four separate Date.now() calls could straddle midnight and put two cases
  // updated a second apart into different groups.
  const now = new Date()
  const grouped = cases.reduce((acc, c) => {
    const g = recencyGroupOf(c.updated_at, now)
    ;(acc[g] = acc[g] || []).push(c)
    return acc
  }, {})

  // The input + button pair, shared by the create card and the empty state so
  // the two cannot drift. Only the placeholder differs — everything else about
  // creating a case is the same act in both places, including the state it
  // reads and the Enter-to-create shortcut.
  const createFields = (placeholder) => (
    <div style={{display:'flex', gap:'7px'}}>
      <input
        value={newAddress} onChange={e=>setNewAddress(e.target.value)}
        onKeyDown={e=>{ if (e.key==='Enter') createCase() }}
        onFocus={()=>setCreateFocused(true)}
        onBlur={()=>setCreateFocused(false)}
        placeholder={placeholder}
        // textAlign left explicitly: the empty state centres its card's text,
        // and an input inherits that — a centred caret in a text field reads as
        // broken.
        style={{
          flex:1, minWidth:0, boxSizing:'border-box', textAlign:'left',
          background:'#f4f4f2', borderRadius:'8px',
          // Blue on focus, which is what the design system reserves blue for.
          // Same 0.5px width in both states so the field cannot shift by half a
          // pixel as focus enters and leaves.
          border: createFocused ? '0.5px solid #147efb' : '0.5px solid #e8e8e8',
          padding:'8px 11px', fontSize:'11.5px', fontFamily:FONT_BODY,
          color:'#1d2e42', outline:'none',
        }}
      />
      {/* Gold, not the blue S.btnPrimary ships. The design system reserves blue
          for interactive and selected states and gives the screen's one primary
          action to gold. Overridden INLINE on purpose: S.btnPrimary is spread at
          35 call sites across this file, so repainting the shared style to fix
          one screen would repaint 34 others as a side effect.
          fontWeight, letterSpacing and textTransform are overridden too:
          S.btnPrimary sets 700 / 1px / uppercase, sized for white on saturated
          blue, and at that weight and tracking #412402 on #fdbf00 goes muddy and
          the letters close up. The disabled opacity behaviour is untouched. */}
      <button onClick={createCase} disabled={creating||newAddress.trim().length<2}
        style={{...S.btnPrimary,background:'#fdbf00',color:'#412402',fontWeight:500,letterSpacing:'normal',textTransform:'none',borderRadius:'8px',flex:'none',flexShrink:0,padding:'8px 16px',fontSize:'11.5px',opacity:(creating||newAddress.trim().length<2)?0.5:1}}>
        {creating ? 'Creating...' : 'Create Case'}
      </button>
    </div>
  )

  // The list is not a conversation, so it rides in emptyState — which the shell
  // renders whenever messages is empty, and messages is always empty here.
  const listNode = (
    <div>
      {/* OUTSIDE the ternary on purpose, so it renders on the first-case branch
          as well as the normal list. An agent with no cases is exactly the one
          who has not been told what the tool does.
          No badge: nothing live sits behind this tool to report the state of.
          "Live MLS data", never the feed's name — a marketing compliance rule,
          not a wording preference. Do not "correct" this to ARMLS. */}
      <ToolIntroBand
        title="The Strategist"
        description="Pricing and negotiation strategy, built on live comps. Name the property, pull the comps, then ask."
        stats={[
          (<><span style={{color:'#fdbf00'}}>Live MLS data</span></>),
          'Comps in seconds',
          'Client-ready reports',
        ]}
      />

      {cases.length === 0 ? (
        // One object on an otherwise empty page. It REPLACES the create card,
        // the search field and the old "No property analyses yet" line — with no
        // cases there is nothing to search, and a bare grey sentence under an
        // input is not an invitation to do anything.
        <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'22px 20px',textAlign:'center'}}>
          {/* StrategistIcon is already the trending-up glyph and takes size and
              colour, so this is the file's own icon rather than a new one. The
              #E4EDF7 tile is the same one the rail uses for Strategy. */}
          <div style={{width:'38px',height:'38px',borderRadius:'10px',background:'#E4EDF7',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
            <StrategistIcon size={19} color="#147efb" />
          </div>
          <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'5px'}}>
            Start your first case
          </div>
          <div style={{fontSize:'11.5px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.6,maxWidth:'340px',margin:'0 auto 14px'}}>
            Give it a name first — an address, or whatever you'll recognise it by. You'll add the property, pull comps and set up the analysis inside.
          </div>
          <div style={{maxWidth:'380px',margin:'0 auto'}}>
            {createFields('e.g. 1234 E Main St')}
          </div>
        </div>
      ) : (
      <>
      {/* A card, not a bare row. As a bare row it was a visual twin of the
          search field directly below it — two pills doing unrelated jobs — and
          the create action reads as a search box you cannot search with. The
          header line is what tells them apart before the placeholder is read.
          Always visible, like the Inspection Desk's. No Cancel, because with
          nothing to hide there is nothing to cancel back to, and no autoFocus,
          because unconditional focus-stealing on every load would scroll a long
          case list to the top and take the caret with it. */}
      <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 14px',marginBottom:'14px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'9px'}}>
          {/* A plain glyph. This file's icons are hand-written inline SVGs and
              it has no plus among them, so a "+" span is one character against a
              new component nothing else would use. */}
          <span style={{color:'#147efb',fontSize:'14px',lineHeight:1,fontFamily:FONT_BODY}}>+</span>
          <span style={{fontSize:'12.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>Start a new case</span>
        </div>
        {createFields('Name it — an address, or "Smith listing"')}
        <div style={{fontSize:'10.5px',color:'#999',fontFamily:FONT_BODY,marginTop:'8px'}}>
          You'll add the property, comps and setup inside the case.
        </div>
      </div>

      <input
        value={search}
        onChange={e=>setSearch(e.target.value)}
        onFocus={()=>setSearchFocused(true)}
        onBlur={()=>setSearchFocused(false)}
        placeholder="Search cases"
        style={{
          width:'100%', boxSizing:'border-box', marginBottom:'16px',
          background:'#ffffff', borderRadius:'18px',
          // Blue on focus, which is what the design system reserves blue for.
          // Same 0.5px width in both states so the field cannot shift by half
          // a pixel as focus enters and leaves.
          border: searchFocused ? '0.5px solid #147efb' : '0.5px solid #e8e8e8',
          padding:'5px 12px', fontSize:'11px', fontFamily:FONT_BODY,
          color:'#1d2e42', outline:'none',
        }}
      />

      {searching ? (
        // Results are a flat grid. Recency headings are a browsing aid, and
        // splitting six matches across four of them buries the thing the agent
        // just typed the address of.
        filtered.length === 0 ? (
          <div style={{padding:'24px 0',color:'#999',fontSize:'13px',fontFamily:FONT_BODY}}>
            No cases match that search.
          </div>
        ) : (
          <div style={strategyGrid(isMobile)}>
            {filtered.map(renderCard)}
          </div>
        )
      ) : (
        // Groups render in RECENCY_GROUPS order; an empty one is dropped rather
        // than shown as a heading over nothing.
        RECENCY_GROUPS.map(label => {
          const group = grouped[label]
          if (!group || group.length === 0) return null
          return (
            <div key={label} style={{marginBottom:'20px'}}>
              <div style={GROUP_EYEBROW}>{label}</div>
              <div style={strategyGrid(isMobile)}>
                {group.map(renderCard)}
              </div>
            </div>
          )
        })
      )}
      </>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="The Strategist"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={STRATEGY_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// ─── STRATEGY: PDF REPORT TEMPLATES ───────────────────────────────────────────

const PDF_COLORS = {
  navy: '#1d2e42',
  blue: '#147efb',
  blueLight: '#e6f2ff',
  yellow: '#fdbf00',
  gray: '#f7f7f7',
  grayBorder: '#dddddd',
  grayMid: '#888888',
  grayText: '#666666',
  red: '#c0392b',
  white: '#ffffff',
  navyFooterText: '#c8d4e0',
}

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 60,
    paddingLeft: 28,
    paddingRight: 28,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: PDF_COLORS.navy,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 4,
  },
  brokerageWordmark: {
    fontSize: 24,
    fontFamily: 'Helvetica-Bold',
    color: PDF_COLORS.navy,
    letterSpacing: 0.5,
  },
  variantTag: {
    fontSize: 9,
    color: PDF_COLORS.grayMid,
    letterSpacing: 1,
  },
  subline: {
    fontSize: 11,
    color: PDF_COLORS.grayText,
    marginBottom: 4,
  },
  yellowAccent: {
    height: 2,
    backgroundColor: PDF_COLORS.yellow,
    marginTop: 8,
    marginBottom: 4,
  },
  sectionHeader: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: PDF_COLORS.blue,
    letterSpacing: 0.3,
    marginTop: 16,
    marginBottom: 8,
    paddingBottom: 2,
    borderBottomWidth: 2,
    borderBottomColor: PDF_COLORS.blue,
    borderBottomStyle: 'solid',
  },
  table: {
    width: '100%',
    marginBottom: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: PDF_COLORS.navy,
  },
  tableHeaderRowBlue: {
    flexDirection: 'row',
    backgroundColor: PDF_COLORS.blue,
  },
  tableHeaderCell: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: PDF_COLORS.white,
    padding: 5,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableRowAlt: {
    flexDirection: 'row',
    backgroundColor: PDF_COLORS.gray,
  },
  tableCell: {
    fontSize: 10,
    padding: 5,
    color: PDF_COLORS.navy,
  },
  callout: {
    padding: 10,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
  },
  calloutBlue: {
    backgroundColor: PDF_COLORS.blueLight,
    borderLeftColor: PDF_COLORS.blue,
  },
  calloutGray: {
    backgroundColor: PDF_COLORS.gray,
    borderLeftColor: PDF_COLORS.grayMid,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: PDF_COLORS.navy,
    padding: 10,
  },
  footerContact: {
    fontSize: 8,
    color: PDF_COLORS.white,
    marginBottom: 4,
  },
  footerDisclaimer: {
    fontSize: 8,
    color: PDF_COLORS.navyFooterText,
    fontStyle: 'italic',
  },
  // Two-agent footer. flexDirection row + percentage widths + borderLeft
  // longhands, the same vocabulary the tables above use — react-pdf has no
  // border shorthand, so the three parts are spelled out.
  footerRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  footerCol: {
    width: '50%',
    paddingRight: 8,
  },
  footerColDivided: {
    borderLeftWidth: 0.5,
    borderLeftStyle: 'solid',
    borderLeftColor: PDF_COLORS.navyFooterText,
    paddingLeft: 8,
  },
  footerContactSub: {
    fontSize: 7,
    color: PDF_COLORS.navyFooterText,
  },
})

function pdfFormatCurrency(n) {
  if (n == null || isNaN(n)) return '—'
  return '$' + Math.round(n).toLocaleString()
}
function pdfFormatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function pdfFormatDateShort(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}
function pdfSafe(value, fallback = '—') {
  if (value == null) return fallback
  const s = String(value).trim()
  return s.length === 0 ? fallback : s
}

// The three-element opening every PDF in this file shares: the brokerage
// wordmark and a variant tag on one row, a subline under it, then the gold
// rule. It was written out inline in StrategyReportPDFClient and again in
// MarketReportPDF, identical apart from what goes in the subline — a third
// document was the point at which copying it again stopped being reasonable.
//
// Every style is the one the inline version used and the element order is
// unchanged, so both existing reports render exactly as they did. A fragment
// rather than a wrapping View, because a View would add a flex item to the
// page that was not there before.
//
// `subline` is a node, not a string: the market report composes its own out of
// three pieces and the strategy report out of two.
function PdfHeader({ brokerage, variantTag, subline }) {
  return (
    <>
      <View style={pdfStyles.headerRow}>
        <Text style={pdfStyles.brokerageWordmark}>{pdfSafe(brokerage, ' ')}</Text>
        <Text style={pdfStyles.variantTag}>{variantTag}</Text>
      </View>
      <Text style={pdfStyles.subline}>{subline}</Text>
      <View style={pdfStyles.yellowAccent} />
    </>
  )
}

function PdfFooter({ profile, email, disclaimerText, teammate }) {
  const contactBits = [profile?.full_name, email, profile?.phone].filter(s => s && String(s).trim())
  const contactLine = contactBits.join(' · ')
  // Two columns only when a teammate was chosen. The single-agent branch below
  // is the original markup, unchanged — one Text, one join, same styles — so a
  // report without team branding is byte-identical to every one before this.
  if (teammate) {
    const col = (name, mail, phone) => (
      [mail, phone].filter(v => v && String(v).trim()).join(' · ')
    )
    return (
      <View style={pdfStyles.footer} fixed>
        <View style={pdfStyles.footerRow}>
          <View style={pdfStyles.footerCol}>
            <Text style={pdfStyles.footerContact}>{pdfSafe(profile?.full_name, ' ')}</Text>
            <Text style={pdfStyles.footerContactSub}>{col(null, email, profile?.phone) || ' '}</Text>
          </View>
          <View style={[pdfStyles.footerCol, pdfStyles.footerColDivided]}>
            <Text style={pdfStyles.footerContact}>{pdfSafe(teammate.full_name, ' ')}</Text>
            <Text style={pdfStyles.footerContactSub}>{col(null, teammate.email, teammate.phone) || ' '}</Text>
          </View>
        </View>
        <Text style={pdfStyles.footerDisclaimer}>{disclaimerText}</Text>
      </View>
    )
  }
  return (
    <View style={pdfStyles.footer} fixed>
      <Text style={pdfStyles.footerContact}>{contactLine || ' '}</Text>
      <Text style={pdfStyles.footerDisclaimer}>{disclaimerText}</Text>
    </View>
  )
}

// The section VIEW WRAPS. It used to carry wrap={false}, which told react-pdf
// never to split it across a page — and when a section grew past the space left
// on the page, the engine had no legal break to take and laid its children on
// top of each other instead. Comp cards drew over the card above; table rows
// drew over the header. Latent since the PDF shipped and purely content-length
// triggered, which is why short reports looked fine.
//
// Unbreakability moved DOWN a level: individual comp cards, callouts and table
// rows carry wrap={false} so none of them splits mid-block, while the section
// itself flows across pages.
//
// minPresenceAhead on the header is what wrap={false} was really buying — orphan
// protection. The engine computes top + height + marginBottom + minPresenceAhead
// and breaks BEFORE the element if that overruns the page, so the header cannot
// strand alone at the bottom with its content overleaf. 72pt ≈ one inch, which
// is a table header plus a first row, or the top of a callout. Not enough to
// keep a whole section together, which is the point.
function PdfSection({ title, children }) {
  return (
    <View>
      <Text style={pdfStyles.sectionHeader} minPresenceAhead={72}>{title}</Text>
      {children}
    </View>
  )
}

// keepTogether is OPT-IN and MUST STAY THAT WAY. Defaulting it to true would
// put wrap={false} back on every comp table in the strategy and market
// reports, which is the exact change the PdfSection comment above describes
// undoing: when an unbreakable block is taller than the space left on the
// page, react-pdf has no legal break to take and lays the children on top of
// each other instead of moving them. A 30-row comp table is reliably taller
// than half a page; the casita report's tables are five and seven rows and
// cannot be.
//
// So: pass it only where the table is short enough that it can always fit on
// a fresh page, and never on a table whose length is driven by data.
function PdfTable({ headers, rows, blueHeader = false, keepTogether = false }) {
  if (!rows || rows.length === 0) return null
  return (
    <View style={pdfStyles.table} wrap={!keepTogether}>
      {/* Each row is individually unbreakable so a row never splits across a
          page with its cells half on each side. The TABLE is breakable unless
          keepTogether says otherwise, so a long one flows. minPresenceAhead
          keeps the header from being the last thing on a page with its first
          row overleaf. */}
      <View style={blueHeader ? pdfStyles.tableHeaderRowBlue : pdfStyles.tableHeaderRow} wrap={false} minPresenceAhead={36}>
        {headers.map((h, i) => (
          <Text key={i} style={[pdfStyles.tableHeaderCell, { flex: h.flex || 1, textAlign: h.align || 'left' }]}>{h.label}</Text>
        ))}
      </View>
      {rows.map((row, ri) => (
        <View key={ri} wrap={false} style={ri % 2 === 0 ? pdfStyles.tableRow : pdfStyles.tableRowAlt}>
          {row.map((cell, ci) => {
            const isObj = typeof cell === 'object' && cell !== null
            const text = isObj ? pdfSafe(cell.value) : pdfSafe(cell)
            const color = isObj && cell.color ? cell.color : PDF_COLORS.navy
            return (
              <Text key={ci} style={[pdfStyles.tableCell, { flex: headers[ci]?.flex || 1, textAlign: headers[ci]?.align || 'left', color }]}>
                {text}
              </Text>
            )
          })}
        </View>
      ))}
    </View>
  )
}

const AGENT_DISCLAIMER = 'Pricing perspective for agent use only. Not an appraisal, not for lending or legal use. Based on the documents and available data; results may change with additional information or physical inspection.'

function clientDisclaimer(brokerage, language = 'en') {
  if (language === 'es') {
    const nameEs = pdfSafe(brokerage, 'La correduría')
    // REVIEW REQUIRED: Spanish legal disclaimer must be reviewed by Josh/Danae before client use.
    return `Este es un análisis de mercado preparado únicamente con fines informativos. No es una tasación y no debe utilizarse para decisiones de préstamo, impuestos, legales o de seguro. Los valores inmobiliarios pueden fluctuar según las condiciones del mercado, el estado de la propiedad y otros factores. Para una valuación formal, consulte a un tasador autorizado. ${nameEs} es una correduría inmobiliaria con licencia en Arizona. No se hace ninguna representación respecto a la exactitud de los datos de terceros mencionados en este documento.`
  }
  const name = pdfSafe(brokerage, 'The brokerage')
  return `This is a market analysis prepared for informational purposes only. It is not an appraisal and should not be used for lending, tax, legal, or insurance decisions. Real estate values can fluctuate based on market conditions, property condition, and other factors. For a formal valuation, consult a licensed appraiser. ${name} is an Arizona-licensed real estate brokerage. No representation is made regarding the accuracy of any third-party data referenced herein.`
}

// The casita report's own disclaimer. Not a variant of clientDisclaimer above:
// that one is about a VALUATION and says "consult a licensed appraiser", which
// is the wrong warning entirely for a document about what a city will permit.
function casitaDisclaimer(brokerage, language = 'en') {
  if (language === 'es') {
    const nameEs = pdfSafe(brokerage, 'La correduría')
    // REVIEW REQUIRED: Spanish text must be reviewed by Josh/Danae before client use.
    return `Este reporte se entrega como cortesía y únicamente con fines informativos. Se basa en los registros públicos del Asesor del Condado de Maricopa y en las reglas de casitas (ADU) de la ciudad, verificadas en la fecha indicada. Los registros públicos cambian y pueden contener errores, y las reglas de la ciudad cambian sin previo aviso. Este reporte no es un levantamiento topográfico, ni un avalúo, ni una determinación de zonificación, ni asesoría legal, ni una garantía de que se pueda construir una casita, y nadie debe confiar en él como tal. El tamaño, la ubicación y la aprobación finales los decide la ciudad al momento del permiso. Las reglas de la HOA (CC&Rs) pueden prohibir una casita aunque la ciudad la permita. Verifique toda la información con la ciudad, el condado y la HOA antes de tomar cualquier decisión. ${nameEs} es una corredora de bienes raíces con licencia en Arizona.`
  }
  const name = pdfSafe(brokerage, 'The brokerage')
  return `This report is provided as a courtesy for general information only. It is based on Maricopa County Assessor public records and city casita (ADU) rules as verified on the date shown. Public records change and may contain errors, and city rules change without notice. This report is not a survey, an appraisal, a zoning determination, legal advice, or a guarantee that a casita can be built, and no one should rely on it as such. Final size, placement and approval are decided by the city at permit time. HOA rules (CC&Rs) can prohibit a casita even where the city allows one. Verify all information with the city, the county and the HOA before making any decision. ${name} is an Arizona-licensed real estate brokerage.`
}

function threatColor(level) {
  if (level === 'high') return PDF_COLORS.red
  if (level === 'moderate') return '#e67e22'
  return PDF_COLORS.grayMid
}

// ─── COMP ADDRESS DISPLAY ────────────────────────────────────────────────────
// ARMLS UnparsedAddress arrives as "7326 N KIRSTEN Avenue, Glendale, AZ 85305"
// — all-caps street name, spelled-out suffix, and the city/state/ZIP the report
// does not need, because every comp in it is in the subject's neighbourhood by
// construction. This turns that into "7326 N Kirsten Ave".
//
// PURE, and used ONLY by the join below, so nothing else in the app changes
// shape. The comp cards and the detail modal keep showing the raw MLS address:
// on screen an agent is cross-referencing against ARMLS, and there the verbatim
// string is the useful one. The PDF is the client's document.
const COMP_ADDR_SUFFIX = {
  avenue: 'Ave', ave: 'Ave',
  drive: 'Dr', dr: 'Dr',
  lane: 'Ln', ln: 'Ln',
  street: 'St', st: 'St',
  road: 'Rd', rd: 'Rd',
  boulevard: 'Blvd', blvd: 'Blvd',
  court: 'Ct', ct: 'Ct',
  place: 'Pl', pl: 'Pl',
  circle: 'Cir', cir: 'Cir',
  parkway: 'Pkwy', pkwy: 'Pkwy',
  way: 'Way',
}
const COMP_ADDR_DIRECTIONAL = new Set(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'])

function formatCompAddress(raw) {
  if (typeof raw !== 'string') return raw
  // Everything before the first comma. ARMLS puts the street line there and
  // the locality after it, so this needs no parsing beyond the split.
  const street = raw.split(',')[0].trim()
  if (!street) return raw
  const words = street.split(/\s+/)

  // THE SUFFIX IS THE LAST ONE THAT MATCHES, NOT EVERY MATCH. Applying the map
  // to any position would rewrite street NAMES that happen to contain one of
  // these words — "Circle Mountain Road" is a real New River street, and a
  // blanket map turns it into "Cir Mountain Rd". Scanning from the end finds
  // the actual street type and leaves the name alone, and it still works when
  // a unit or a trailing directional follows it.
  let suffixIdx = -1
  for (let i = words.length - 1; i >= 0; i--) {
    if (COMP_ADDR_SUFFIX[words[i].toLowerCase()]) { suffixIdx = i; break }
  }

  return words.map((word, i) => {
    const lower = word.toLowerCase()
    // Directionals first: "N" is a directional, never a name to title-case.
    if (COMP_ADDR_DIRECTIONAL.has(lower)) return lower.toUpperCase()
    if (i === suffixIdx) return COMP_ADDR_SUFFIX[lower]
    // 85th, 79th, 1st, 2nd, 3rd — the ordinal suffix stays lowercase, which is
    // how a street sign and an MLS sheet both write it.
    if (/^\d+(st|nd|rd|th)$/.test(lower)) return lower
    if (/^\d+$/.test(word)) return word
    // Title case that survives hyphens and apostrophes: O'BRIEN -> O'Brien,
    // VAN-BUREN -> Van-Buren.
    return lower.replace(/(^|[\s'’-])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase())
  }).join(' ')
}

// ─── COMP ADDRESS CHECK ──────────────────────────────────────────────────────
// Does the address the model wrote for a comp describe the same house as the
// payload row its listing_id points at? A listing_id copied from the wrong
// COMP INDEX row joins cleanly and prints another home's price under this
// one's name, so the id alone is not enough.
//
// Two parts must agree: the house number, and the first word of the street
// name after any directional. Suffix, city and the directional itself are
// ignored, because the model and ARMLS write those differently for the same
// house ("7326 Kirsten Ave" / "7326 N KIRSTEN Avenue, Glendale, AZ").
//
// UNITS: when BOTH sides carry a unit, it must match too — otherwise every
// unit in a condo building agrees with every other. When only one side has a
// unit, units are ignored: the model often drops it.
// PURE.
const COMP_UNIT_MARKERS = new Set(['#', 'UNIT', 'APT', 'STE', 'SUITE'])

function compAddressKey(raw) {
  if (typeof raw !== 'string') return null
  // Street line only — the locality after the first comma is not compared.
  // "#" is kept as its own token because it marks a unit. Other punctuation is
  // deleted, not spaced, so O'BRIEN and OBRIEN are one token.
  const tokens = raw.split(',')[0].toUpperCase()
    .replace(/#/g, ' # ')
    .replace(/[^A-Z0-9#\s]/g, '')
    .split(/\s+/).filter(Boolean)
  if (tokens.length < 2 || !/^\d+$/.test(tokens[0])) return null
  let nameIdx = 1
  if (COMP_ADDR_DIRECTIONAL.has(tokens[nameIdx].toLowerCase())) nameIdx++
  if (nameIdx >= tokens.length || COMP_UNIT_MARKERS.has(tokens[nameIdx])) return null

  // The unit: the token after a marker ("#1124", "Unit 1124", "Apt 3B"), or a
  // bare trailing number straight after the street suffix ("93RD Avenue 1124").
  let unit = null
  for (let j = nameIdx + 1; j < tokens.length - 1; j++) {
    if (COMP_UNIT_MARKERS.has(tokens[j])) { unit = tokens[j + 1]; break }
  }
  const last = tokens.length - 1
  if (!unit && last > nameIdx + 1 && /^\d+$/.test(tokens[last]) && COMP_ADDR_SUFFIX[tokens[last - 1].toLowerCase()]) {
    unit = tokens[last]
  }

  return { number: tokens[0], name: tokens[nameIdx], unit }
}

function addressesAgree(modelAddress, payloadAddress) {
  const a = compAddressKey(modelAddress)
  const b = compAddressKey(payloadAddress)
  if (!a || !b) return false
  if (a.number !== b.number || a.name !== b.name) return false
  if (a.unit && b.unit && a.unit !== b.unit) return false
  return true
}

// ─── COMP JOIN ───────────────────────────────────────────────────────────────
// Every comp number the client PDF prints comes from HERE, off the live payload,
// not from the extraction. The model still decides WHICH comps matter and WHY —
// that is analysis and it is what the model is for — but the price, the size,
// the close date and the days on market are copied from the pull.
//
// The reason is two lossy hops. A comp number used to travel: ARMLS → the chat
// transcript (written by claude-opus-4-7) → the extraction (re-read by
// claude-sonnet-4-6) → the PDF. Each hop is a model retyping a figure, and a
// retyped figure is a figure that can be wrong in a document a seller reads.
// listing_id is the join key because it is the only stable identifier a comp
// has — the extraction schema has no other, and addresses are free text on both
// sides.
//
// PURE, AND IT RETURNS A NEW OBJECT. cachedReportRef keeps the UNJOINED report
// so a language switch recomposes the model's own words rather than a report
// that already has payload numbers baked into it — recompose is told to leave
// numbers alone, and handing it joined ones would make that promise circular.
// Mutating `report` here would poison that cache.
//
// A comp with no match is DROPPED, not printed with whatever the model said.
// The whole point is that an unverifiable number does not reach the client, and
// a comp that is not in this pull cannot be verified. The caller reports the
// count.
//
// A matched id is not enough on its own: the model's address for the comp must
// also agree with the payload row's (addressesAgree). An id that points at a
// different house is dropped the same way an unknown id is. A comp the model
// gave no address for cannot be checked; it is KEPT, and listed separately in
// uncheckedRows so the caller can log it.
function joinCompsFromPayload(report, compResult) {
  if (!report || typeof report !== 'object') return { report, dropped: 0, droppedRows: [], uncheckedRows: [] }
  const groups = compResult?.groups
  if (!groups || typeof groups !== 'object') return { report, dropped: 0, droppedRows: [], uncheckedRows: [] }

  // One index across every bucket. COMP_BUCKET_ORDER rather than
  // Object.keys(groups), so a key the server adds later cannot silently become
  // a status_group the tag map has no entry for.
  const byId = new Map()
  for (const group of COMP_BUCKET_ORDER) {
    for (const c of (groups[group] || [])) {
      if (c && typeof c.listing_id === 'string' && c.listing_id) {
        byId.set(c.listing_id, { comp: c, group })
      }
    }
  }
  if (byId.size === 0) return { report, dropped: 0, droppedRows: [], uncheckedRows: [] }

  // What was dropped, for the console. Four fields only — which list, the id
  // the model returned, the address it wrote, and why — because that is exactly
  // what is needed to tell an unmatched id from a hallucinated one, and
  // anything more would be comp data in a log.
  const droppedRows = []
  // Kept but unchecked: matched on id, but the model wrote no address.
  const uncheckedRows = []

  // The payload row for this comp, or null when it must be dropped. Shared by
  // every list so the three drop reasons are decided in one place.
  const lookup = (list, item) => {
    const id = item && typeof item.listing_id === 'string' && item.listing_id ? item.listing_id : null
    if (!id) { droppedRows.push(describeDropped(list, item, 'no id')); return null }
    const hit = byId.get(id)
    if (!hit) { droppedRows.push(describeDropped(list, item, 'id not in pull')); return null }
    const modelAddress = typeof item.address === 'string' ? item.address.trim() : ''
    if (!modelAddress) { uncheckedRows.push(describeDropped(list, item, 'no address to check')); return hit }
    if (!addressesAgree(modelAddress, hit.comp.address)) { droppedRows.push(describeDropped(list, item, 'address mismatch')); return null }
    return hit
  }

  // close_price on a closed comp, list_price on everything else — the same
  // bucket-switched rule compPrice applies on screen, so the PDF and the comp
  // card can never disagree about what a comp "cost".
  const joinSale = (list) => (item) => {
    const hit = lookup(list, item)
    if (!hit) return null
    const { comp, group } = hit
    return {
      ...item,
      address: formatCompAddress(comp.address),
      price: group === 'closed' ? comp.close_price : comp.list_price,
      sqft: comp.living_area,
      close_date: comp.close_date,
      dom: comp.days_on_market,
      status_group: group,
    }
  }

  // Active competition is a LIST price by definition, so no bucket switch and
  // no close date. Same drop rule.
  const joinActive = (item) => {
    const hit = lookup('active', item)
    if (!hit) return null
    const { comp, group } = hit
    return {
      ...item,
      address: formatCompAddress(comp.address),
      list_price: comp.list_price,
      dom: comp.days_on_market,
      status_group: group,
    }
  }

  const joinList = (arr, fn) => (Array.isArray(arr) ? arr.map(fn).filter(Boolean) : arr)

  return {
    report: {
      ...report,
      best_comps: joinList(report.best_comps, joinSale('best')),
      weak_comps: joinList(report.weak_comps, joinSale('weak')),
      active_competition: joinList(report.active_competition, joinActive),
    },
    dropped: droppedRows.length,
    droppedRows,
    uncheckedRows,
  }
}

// One dropped (or unchecked) comp, as four strings. The ADDRESS IS THE MODEL'S,
// unformatted: the point of the log is to compare what the model wrote against
// what the pull holds, and running it through formatCompAddress first would
// hide the difference this exists to show. reason is "no id", "id not in pull",
// "address mismatch", or — for a kept comp — "no address to check".
function describeDropped(list, item, reason) {
  return {
    list,
    listing_id: (item && typeof item.listing_id === 'string' && item.listing_id) ? item.listing_id : 'none',
    address: (item && typeof item.address === 'string') ? item.address : '',
    reason,
  }
}

// How many distinct comps in the joined report matched a comp in the CLOSED
// bucket of the pull. Zero stops generateReport before the figure check, the
// PDF and the upload: a report with no closed sale behind it has nothing the
// pull can vouch for.
//
// The join's early returns (no groups, or no comp in the pull with a
// listing_id) hand back the SAME object they were given, unjoined. That is
// read here as zero, never as a pass, so an empty pull stops too. Only the
// join stamps status_group, so a pending or active match never counts.
//
// PURE. Takes the report as extracted and the join's output.
function countClosedMatches(extracted, joined, compResult) {
  if (!joined || typeof joined !== 'object' || joined === extracted) return 0
  const closedIds = new Set(
    (compResult?.groups?.closed || [])
      .map(c => c?.listing_id)
      .filter(id => typeof id === 'string' && id)
  )
  if (closedIds.size === 0) return 0
  const matched = new Set()
  for (const list of [joined.best_comps, joined.weak_comps, joined.active_competition]) {
    if (!Array.isArray(list)) continue
    for (const item of list) {
      if (item?.status_group === 'closed' && closedIds.has(item.listing_id)) matched.add(item.listing_id)
    }
  }
  return matched.size
}

// ─── PRE-PDF FIGURE CHECK ────────────────────────────────────────────────────
// Runs on the JOINED report, after joinCompsFromPayload and before pdf(), on
// both the extraction and the recompose path. Two checks, both reported in one
// confirm the agent can override:
//
//   1. Clean prices (listing reports). Every lane price and price-drop target
//      must be an exact multiple of $5,000 — the analysis is told to land on
//      one, and a price that does not is a model slip, not a strategy.
//   2. Dollar figures in the client-facing prose the PDF prints for this
//      report type. An amount of $100,000 or more must equal something the
//      report can stand behind: a comp's close, list, original list or net
//      (close less concession) price, a lane price, a price-drop target, the
//      subject's own prices, or a buyer-report figure field. Also passed:
//      - an approximate figure within $1,000 of any of those: "~", "about",
//        "roughly", "around", "nearly", "approximately", "closer to", "just
//        under", "net", and in a Spanish report "aproximadamente", "alrededor
//        de", "cerca de", "casi", "poco menos de", "justo por debajo de",
//        "unos", "unas", "neto", "neta";
//      - a figure exactly $100 below a lane price (the $X,900 bracket
//        counter-example);
//      - any exact multiple of $5,000 within $50,000 of a lane price, or, on a
//        buyer report, which has no lanes, of an offer figure;
//      - a range whose low end is under $100,000 ("$75,000–$120,000"), which
//        is a cost, not a home price.
//      Anything else is a number the model composed, in a document a client
//      reads. Each amount is listed once, under the first section it appears
//      in; a range is one line.
//
// PURE. Nothing here is logged or persisted — the result goes to the confirm
// and nowhere else.

// One dollar amount, with an optional range tail ("$450K–$475K",
// "$5,000 to $13,000"). A number carries a K/M suffix, or the word "million",
// "billion", or in Spanish "millón"/"millones" (also "millon"); "$1.2 million"
// and "$1.2 millones" read as "$1.2M". Amounts are read in U.S. format only:
// "$850.000" reads as $850, which is why generate-strategy-report tells
// Spanish output to keep U.S. format. The negative lookahead rejects
// "mid-$460s" and "$820,000s": a figure glued to letters is not an amount.
const REPORT_MONEY_NUM = '(\\d{1,3}(?:,\\d{3})+|\\d+(?:\\.\\d+)?)'
const REPORT_MONEY_SUFFIX = '(?:([KkMm])(?![A-Za-z0-9])|\\s+([Mm]illion|[Bb]illion|[Mm]ill[oó]n(?:es)?)(?![A-Za-z0-9])|(?![A-Za-z0-9]))'
const REPORT_MONEY_RE = new RegExp(
  '\\$\\s?' + REPORT_MONEY_NUM + REPORT_MONEY_SUFFIX +
  '(?:\\s*(?:–|—|-|to)\\s*\\$?\\s?' + REPORT_MONEY_NUM + REPORT_MONEY_SUFFIX + ')?',
  'g'
)
const REPORT_MONEY_MULT = { k: 1000, m: 1000000, b: 1000000000 }

// The multiplier key for one number: its K/M letter, or the first letter of
// the word ("million", "millón" and "millones" give m; "billion" gives b).
function reportMoneyScale(letter, word) {
  if (letter) return letter.toLowerCase()
  if (word) return word.charAt(0).toLowerCase()
  return null
}

// Every dollar amount in a string: [{ value, inThousands, index, length, range }].
// A range yields both ends, each carrying range: { low, high } so the confirm
// lists it once; a single figure has range: null. A suffix written on one end
// only ("$450–475K", "$450K–475", "$1.2 to $1.5 million") is applied to the
// bare end when that end is under 1,000.
function parseReportDollars(text) {
  if (typeof text !== 'string' || !text.includes('$')) return []
  const out = []
  REPORT_MONEY_RE.lastIndex = 0
  let m
  while ((m = REPORT_MONEY_RE.exec(text)) !== null) {
    const [whole, n1, l1, w1, n2, l2, w2] = m
    const raw1 = Number(n1.replace(/,/g, ''))
    const raw2 = n2 != null ? Number(n2.replace(/,/g, '')) : null
    let s1 = reportMoneyScale(l1, w1)
    let s2 = reportMoneyScale(l2, w2)
    if (raw2 != null) {
      if (!s1 && s2 && raw1 < 1000) s1 = s2
      if (!s2 && s1 && raw2 < 1000) s2 = s1
    }
    const v1 = Math.round(raw1 * (s1 ? REPORT_MONEY_MULT[s1] : 1))
    const v2 = raw2 != null ? Math.round(raw2 * (s2 ? REPORT_MONEY_MULT[s2] : 1)) : null
    const range = v2 != null ? { low: v1, high: v2 } : null
    out.push({ value: v1, inThousands: s1 === 'k', index: m.index, length: whole.length, range })
    if (v2 != null) {
      out.push({ value: v2, inThousands: s2 === 'k', index: m.index, length: whole.length, range })
    }
  }
  return out
}

// The client-facing prose the PDF prints, each with the English title of the
// section it appears under. Mirrors StrategyReportPDFClient's own conditions,
// so a field the PDF does not print for this report type is not checked.
// English only, as the confirm is.
function reportProseFields(report) {
  const L = LABELS.en
  const fields = []
  const add = (section, value) => { if (typeof value === 'string' && value.trim()) fields.push({ section, text: value }) }
  const addAll = (section, arr) => { if (Array.isArray(arr)) arr.forEach(v => add(section, v)) }
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

  const isBuyer = report.report_type === 'buyer'
  const ss = report.strategy_section && typeof report.strategy_section === 'object' ? report.strategy_section : null
  const iv = ss?.investor || null
  // The PDF's own test for an investor report.
  const isInvestorReport = isBuyer && !!iv && (ss.type === 'investor' || !ss.owner_occupant)

  // Comps print on both report types.
  for (const bc of (Array.isArray(report.best_comps) ? report.best_comps : [])) {
    add(L.bestComp, bc?.client_explanation?.why_it_anchors)
    add(L.bestComp, bc?.client_explanation?.whats_different)
  }
  for (const wc of (Array.isArray(report.weak_comps) ? report.weak_comps : [])) {
    add(L.notUsingComp, wc?.client_explanation)
  }

  // The pricing options and "What this means" print on listing reports only.
  if (!isBuyer) {
    const lps = report.list_price_strategy
    if (lps && lps.lanes && typeof lps.lanes === 'object') {
      for (const key of ['aggressive', 'market', 'aspirational']) {
        add(L.threePricing, lps.lanes[key]?.fits_seller)
        add(L.threePricing, lps.lanes[key]?.expected_dom)
      }
      // Only the recommended lane's reasoning is printed.
      if (lps.recommended_lane) add(L.threePricing, lps.lanes[lps.recommended_lane]?.reasoning)
    }
    addAll(L.whatThisMeans, report.client_strategy_notes)
  }

  if (isInvestorReport) {
    // The not-a-deal message replaces the whole section; the repair basis
    // prints only under the offer table, which needs repairs and an offer.
    const noEvidence = iv.arv_low == null && iv.arv_high == null
    if (noEvidence || iv.not_a_deal_reason) {
      add(L.afterRepairTitle, iv.not_a_deal_reason)
    } else {
      add(L.afterRepairTitle, iv.arv_basis)
      const anOffer = isNum(iv.offer_low) || isNum(iv.offer_high) || isNum(iv.margin_amount)
      if (isNum(iv.repair_estimate) && anOffer) add(L.afterRepairTitle, iv.repair_basis)
    }
  } else if (isBuyer && ss) {
    // Our Strategy is skipped when it has no table row, risk or note, and the
    // stretch note goes with it.
    const oo = ss.owner_occupant
    const hasRow = !!oo && [oo.fair_offer_low, oo.fair_offer_high, oo.strong_offer, oo.stretch_offer, oo.walk_away].some(v => v != null)
    const risks = oo && Array.isArray(oo.risks) ? oo.risks.filter(Boolean) : []
    const notes = Array.isArray(ss.buyer_education_notes) ? ss.buyer_education_notes.filter(Boolean) : []
    if (hasRow || risks.length > 0 || notes.length > 0) {
      add(L.ourStrategy, oo?.stretch_note)
      addAll(L.ourStrategy, risks)
      addAll(L.ourStrategy, notes)
    }
  }

  // Recommended Offer: one reasoning per printed offer, with the PDF's
  // fallbacks, so an unprinted reasoning field is not checked.
  const o = report.offer_recommendation
  if (isBuyer && o && typeof o === 'object') {
    if (isInvestorReport) {
      if (isNum(iv.offer_low) || isNum(iv.offer_high) || isNum(o.investor_number)) {
        add(L.recommendedOffer, o.investor_reasoning || o.reasoning)
      }
    } else if (o.offer_number != null) {
      add(L.recommendedOffer, o.offer_reasoning || o.reasoning)
    } else {
      const raw = [
        { n: o.owner_occupant_number, why: o.owner_occupant_reasoning },
        { n: o.first_time_buyer_number, why: o.first_time_buyer_reasoning },
      ].filter(l => l.n != null)
      const lanes = raw.filter((l, i) => raw.findIndex(x => x.n === l.n) === i)
      lanes.forEach(l => add(L.recommendedOffer, l.why || (lanes.length === 1 ? o.reasoning : null)))
    }
  }
  return fields
}

function formatWholeDollars(n) {
  return '$' + Math.round(n).toLocaleString('en-US')
}

// Is this figure written as approximate? "~$347,000", "about $850,000",
// "approximately $120,000", "closer to $850,000", "just under $880,000",
// "$347,000 net", "net of $12,000", and the Spanish equivalents:
// "aproximadamente", "alrededor de", "cerca de", "casi", "poco menos de",
// "justo por debajo de", "unos", "unas", "neto", "neta". An approximate word
// must sit directly before the figure (24 characters, room for "justo por
// debajo de "); "net"/"neto"/"neta" is looked for in the 12 characters either
// side. For a range the words apply to both ends, since the match starts at
// the first.
const FIGURE_APPROX_BEFORE_RE = new RegExp(
  '(?:~|\\b(?:about|roughly|around|nearly|approximately|closer to|just under' +
  '|aproximadamente|alrededor de|cerca de|casi|poco menos de|justo por debajo de|unos|unas))\\s*$',
  'i'
)
const FIGURE_NET_NEAR_RE = /\b(?:net|neto|neta)\b/i
const FIGURE_NET_AFTER_RE = /^\s*(?:net|neto|neta)\b/i
function figureIsApprox(text, index, length) {
  const before = text.slice(Math.max(0, index - 24), index)
  const near = text.slice(Math.max(0, index - 12), index)
  const after = text.slice(index + length, index + length + 12)
  return FIGURE_APPROX_BEFORE_RE.test(before) || FIGURE_NET_NEAR_RE.test(near) || FIGURE_NET_AFTER_RE.test(after)
}

// { nonClean: [{ amount, label }], figures: [{ amount, label, section }] }
// A figure's label is the amount as written for the confirm: "$880,000", or
// "$830,000–$860,000" for a range.
function checkReportFigures(report, compResult) {
  const nonClean = []
  const figures = []
  if (!report || typeof report !== 'object') return { nonClean, figures }
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v)
  const L = LABELS.en

  const lanes = report.list_price_strategy?.lanes
  const dropTargets = Array.isArray(report.price_drop_targets) ? report.price_drop_targets.filter(isNum) : []

  // 1. Clean $5K prices — listing reports.
  if (report.report_type === 'listing') {
    for (const key of ['aggressive', 'market', 'aspirational']) {
      const price = lanes?.[key]?.price
      if (isNum(price) && price % 5000 !== 0) {
        nonClean.push({ amount: price, label: L['lane' + key.charAt(0).toUpperCase() + key.slice(1)] + ' option' })
      }
    }
    for (const t of dropTargets) {
      if (t % 5000 !== 0) nonClean.push({ amount: t, label: 'Price-drop target' })
    }
  }

  // 2. Dollar figures in prose.
  const allowed = new Set()
  const allow = (v) => { if (isNum(v)) allowed.add(Math.round(v)) }
  for (const group of COMP_BUCKET_ORDER) {
    for (const c of (compResult?.groups?.[group] || [])) {
      if (!c) continue
      allow(c.close_price); allow(c.list_price); allow(c.original_list_price)
      // The comp's NET price, which the analysis is told to quote ("$359,000
      // closed / ~$347,000 net of $12,000 seller concession"). Same rule the
      // strategy-chat prompt states: "%" is a percentage of the close, anything
      // else a dollar amount.
      const conc = c.seller_concession_amount
      if (isNum(c.close_price) && isNum(conc) && conc > 0) {
        allow(c.seller_concession_type === '%' ? c.close_price * (1 - conc / 100) : c.close_price - conc)
      }
    }
  }
  // The subject's own prices — a listed home's current and original list
  // price are real figures the notes routinely quote.
  const subj = compResult?.subject
  if (subj) { allow(subj.close_price); allow(subj.list_price); allow(subj.original_list_price) }
  if (lanes && typeof lanes === 'object') {
    for (const key of ['aggressive', 'market', 'aspirational']) allow(lanes[key]?.price)
  }
  dropTargets.forEach(allow)
  const oo = report.strategy_section?.owner_occupant
  const iv = report.strategy_section?.investor
  const o = report.offer_recommendation
  if (oo) [oo.fair_offer_low, oo.fair_offer_high, oo.strong_offer, oo.stretch_offer, oo.walk_away].forEach(allow)
  if (iv) [iv.arv_low, iv.arv_high, iv.repair_estimate, iv.margin_amount, iv.offer_low, iv.offer_high].forEach(allow)
  if (o) [o.offer_number, o.owner_occupant_number, o.investor_number, o.first_time_buyer_number].forEach(allow)

  // "$469K" is written to the thousand, so it is compared to the thousand.
  const allowedThousands = new Set([...allowed].map(v => Math.round(v / 1000) * 1000))
  const allowedList = [...allowed]
  const lanePrices = ['aggressive', 'market', 'aspirational'].map(k => lanes?.[k]?.price).filter(isNum)
  // The round-number rule's anchors: lane prices on a listing report. A buyer
  // report has no lanes, so its offer figures stand in. The $100-below rule
  // stays lane-only.
  const roundAnchors = report.report_type === 'buyer'
    ? [oo?.fair_offer_low, oo?.fair_offer_high, oo?.strong_offer, oo?.stretch_offer, oo?.walk_away,
       iv?.offer_low, iv?.offer_high,
       o?.offer_number, o?.owner_occupant_number, o?.investor_number, o?.first_time_buyer_number].filter(isNum)
    : lanePrices

  // One line per amount, under the first section it appears in. A range is
  // one line, listed when either end fails; its label is the whole range.
  const seen = new Set()
  for (const field of reportProseFields(report)) {
    for (const hit of parseReportDollars(field.text)) {
      if (hit.value < 100000) continue
      // A range whose low end is under $100,000 ("$75,000–$120,000" for a
      // pool) is a cost, not a home price.
      if (hit.range && hit.range.low < 100000) continue
      if (allowed.has(hit.value)) continue
      if (hit.inThousands && allowedThousands.has(hit.value)) continue
      // Approximate figures ("~$347,000", "about $850,000", "$347,000 net")
      // pass within $1,000 of any allowed value.
      if (figureIsApprox(field.text, hit.index, hit.length) &&
          allowedList.some(v => Math.abs(v - hit.value) <= 1000)) continue
      // The bracket counter-example: "$474,900" written to show why the lane is
      // $475,000 — exactly $100 below a lane price.
      if (lanePrices.some(p => hit.value === p - 100)) continue
      // A clean $5K strategy number near a lane or, on a buyer report, an offer
      // ("back toward $455,000"): any exact multiple of $5,000 within $50,000.
      if (hit.value % 5000 === 0 && roundAnchors.some(p => Math.abs(p - hit.value) <= 50000)) continue
      const label = hit.range
        ? `${formatWholeDollars(hit.range.low)}–${formatWholeDollars(hit.range.high)}`
        : formatWholeDollars(hit.value)
      if (seen.has(label)) continue
      seen.add(label)
      figures.push({ amount: hit.value, label, section: field.section })
    }
  }
  return { nonClean, figures }
}

// ─── PULL FINGERPRINT ────────────────────────────────────────────────────────
// normalizePullFingerprint, compPullFingerprint and samePullFingerprint live in
// src/pullConsistency.js, with the rest of the pull-consistency rules, so Node
// can test them without this file's JSX.

// The Analyze button's prompt. sendMessage recognises the analysis by this
// prefix, so the button and the check share one string.
const STRATEGY_ANALYZE_PROMPT = 'Analyze these comps — which are anchors, which are weak, and what price range they support.'

// LIVE MLS ACCESS. isMlsLicensed and canUseLiveComps live in src/mlsLicense.js.
// Market Snapshot is paused for everyone except a HomePros admin, who keeps it
// for development and testing — the same rule armls-market-snapshot enforces
// on the server. Spelled out here so it never follows canUseLiveComps to
// HomePros Advantage.
function canUseMarketSnapshot(profile) {
  return profile?.role === 'admin' && profile?.brokerage === 'HomePros'
}

// The gold notice strip. Background and border are the sidebar announcement
// banner's exactly (WorkspaceRail: #fdf6e3, 1px #ecdca6, 3px #fdbf00 left
// accent), so a notice reads as the same kind of message. Module level so the
// Strategist report bar, the Strategist setup, Market Snapshot and My Wins all
// use the one style. Rendered only when a notice applies; two notices stack as
// two strips.
const reportNoticeStrip = {
  display:'flex', alignItems:'flex-start', gap:'8px', marginTop:'8px',
  background:'#fdf6e3', color:'#1d2e42',
  // Shorthand first, then the left overrides — same order as the banner.
  border:'1px solid #ecdca6', borderLeftWidth:'3px', borderLeftColor:'#fdbf00',
  borderRadius:'8px', padding:'7px 12px',
  fontSize:'12.5px', lineHeight:1.45, fontFamily:FONT_BODY,
}
const reportNoticeIcon = <span aria-hidden="true" style={{flexShrink:0,color:'#b0894a'}}>⚠</span>

// Market Snapshot's paused notice, shared by the list page and the report view.
// marginTop 0: it replaces content rather than following it.
const marketPausedNotice = (
  <div style={{...reportNoticeStrip, marginTop:0}}>
    {reportNoticeIcon}
    <span><span style={{ fontWeight: 500 }}>Market Snapshot is paused</span> while we confirm its MLS licensing.</span>
  </div>
)

// status_group → the label key in LABELS. pending, ucb and ccbs are all "under
// contract" to a client; the three-letter MLS distinctions are agent shorthand
// and mean nothing on a seller's kitchen table.
const COMP_STATUS_TAG_KEY = {
  closed: 'tagClosed',
  pending: 'tagPending',
  ucb: 'tagPending',
  ccbs: 'tagPending',
  active: 'tagActive',
  coming_soon: 'tagComingSoon',
}

const LABELS = {
  en: {
    preparedSeller: 'PREPARED FOR THE SELLER',
    preparedBuyer: 'PREPARED FOR THE BUYER',
    yourProperty: 'Your Property',
    property: 'Property',
    detail: 'Detail',
    address: 'Address',
    yearBuilt: 'Year Built',
    subdivision: 'Subdivision',
    size: 'Size',
    bedsBaths: 'Beds / Baths',
    lot: 'Lot',
    condition: 'Condition',
    poolHoa: 'Pool / HOA',
    yes: 'Yes',
    no: 'No',
    recentSales: 'Recent Sales in Your Neighborhood',
    soldPrice: 'Sold Price',
    timeToSell: 'Time to Sell',
    days: 'days',
    bestComp: 'Best Comparables',
    // Status tag per comp, from the joined status_group. The heading is no
    // longer "Sale" because the section is no longer only sales: a joined comp
    // can be pending, active or coming soon, and calling a live listing a sale
    // in front of a client is the kind of error the join exists to prevent.
    tagClosed: 'Sold',
    tagPending: 'Pending',
    tagActive: 'Active listing',
    tagComingSoon: 'Coming soon',
    whyItMatters: 'Why it matters:',
    whatsDifferent: "What's different:",
    notUsingComp: "Why We're Not Using This Comp",
    threePricing: 'Your Three Pricing Options',
    strategy: 'Strategy',
    listPrice: 'List Price',
    expectedTime: 'Expected Time on Market',
    bestWhen: 'Best When…',
    laneAggressive: 'Aggressive',
    laneMarket: 'Market',
    laneAspirational: 'Aspirational',
    ourRec: 'Our recommendation:',
    activeComp: 'Active Competition',
    daysOnMarket: 'Days on Market',
    closestListing: 'The closest comparable listing right now is',
    at: 'at',
    whatThisMeans: 'What This Means for You',
    ourStrategy: 'Our Strategy',
    recommendedOffer: 'Recommended Offer',
    // Buyer-side report labels.
    offerLevel: 'Offer Level',
    amount: 'Amount',
    whatItMeans: 'What it means',
    fairOfferRange: 'Fair offer range',
    fairOfferMeans: 'Supported by the comps',
    strongOffer: 'Strong offer',
    strongOfferMeans: 'Likely to win',
    stretchOffer: 'Stretch',
    walkAway: 'Walk-away',
    walkAwayMeans: 'Above this, step back',
    riskLow: 'Low appraisal risk',
    riskModerate: 'Moderate appraisal risk',
    riskHigh: 'High appraisal risk',
    whatToWatch: 'What to watch for',
    goodToKnow: 'Good to Know',
    asHome: 'As a home to live in',
    asInvestment: 'As an investment',
    // Investor branch. Its own section title — this is not the same product as
    // the owner-occupant "Our Strategy" and should not read as though it were.
    afterRepairTitle: 'After-Repair Value & Offer',
    afterRepairValue: 'After-repair value',
    projectedNote: 'This is the projected list price for the renovated home, not what the property is worth today.',
    ceilingReference: 'ceiling reference',
    offerToday: 'What that means as an offer today',
    low: 'Low',
    high: 'High',
    lessRepairs: 'Less estimated repairs',
    lessMargin: 'Less investor margin',
    offerRow: 'Offer',
    substituteNote: 'Substitute your own repair figure or margin and the offer moves by the same amount.',
    noData: 'No data available for this report.',
  },
  es: {
    preparedSeller: 'PREPARADO PARA EL VENDEDOR',
    preparedBuyer: 'PREPARADO PARA EL COMPRADOR',
    yourProperty: 'Su Propiedad',
    property: 'Propiedad',
    detail: 'Detalle',
    address: 'Dirección',
    yearBuilt: 'Año de construcción',
    subdivision: 'Subdivisión',
    size: 'Tamaño',
    bedsBaths: 'Recámaras / Baños',
    lot: 'Terreno',
    condition: 'Estado',
    poolHoa: 'Alberca / HOA',
    yes: 'Sí',
    no: 'No',
    recentSales: 'Ventas recientes en su vecindario',
    soldPrice: 'Precio de venta',
    timeToSell: 'Tiempo para vender',
    days: 'días',
    bestComp: 'Mejores comparables',
    tagClosed: 'Vendida',
    tagPending: 'Pendiente',
    tagActive: 'En venta',
    tagComingSoon: 'Próximamente',
    whyItMatters: 'Por qué importa:',
    whatsDifferent: 'Qué es diferente:',
    notUsingComp: 'Por qué no estamos usando este comparable',
    threePricing: 'Sus tres opciones de precio',
    strategy: 'Estrategia',
    listPrice: 'Precio de lista',
    expectedTime: 'Tiempo esperado en el mercado',
    bestWhen: 'Mejor cuando…',
    laneAggressive: 'Agresiva',
    laneMarket: 'De mercado',
    laneAspirational: 'Aspiracional',
    ourRec: 'Nuestra recomendación:',
    activeComp: 'Competencia activa',
    daysOnMarket: 'Días en el mercado',
    closestListing: 'El listado comparable más cercano en este momento es',
    at: 'en',
    whatThisMeans: 'Qué significa esto para usted',
    ourStrategy: 'Nuestra estrategia',
    recommendedOffer: 'Oferta recomendada',
    // Buyer-side report labels. "Qué tener en cuenta" for whatToWatch — it
    // carries the same caution as "What to watch for" without the alarm of
    // "advertencia," which would read as a warning notice rather than guidance.
    offerLevel: 'Nivel de oferta',
    amount: 'Monto',
    whatItMeans: 'Qué significa',
    fairOfferRange: 'Rango de oferta justa',
    fairOfferMeans: 'Respaldado por las ventas comparables',
    strongOffer: 'Oferta fuerte',
    strongOfferMeans: 'Probablemente gane',
    stretchOffer: 'Oferta máxima',
    walkAway: 'Límite para retirarse',
    walkAwayMeans: 'Por encima de esto, conviene retirarse',
    riskLow: 'Riesgo bajo de avalúo',
    riskModerate: 'Riesgo moderado de avalúo',
    riskHigh: 'Riesgo alto de avalúo',
    whatToWatch: 'Qué tener en cuenta',
    goodToKnow: 'Información útil',
    asHome: 'Como vivienda propia',
    asInvestment: 'Como inversión',
    // Investor branch. "Valor después de reparaciones" rather than the English
    // loan "ARV" — the section has to be readable by the client, and ARV is
    // trade shorthand in either language.
    afterRepairTitle: 'Valor después de reparaciones y oferta',
    afterRepairValue: 'Valor después de reparaciones',
    projectedNote: 'Este es el precio de lista proyectado para la casa ya renovada, no lo que vale la propiedad hoy.',
    ceilingReference: 'referencia de techo',
    offerToday: 'Qué significa como oferta hoy',
    low: 'Mínimo',
    high: 'Máximo',
    lessRepairs: 'Menos reparaciones estimadas',
    lessMargin: 'Menos margen del inversionista',
    offerRow: 'Oferta',
    substituteNote: 'Sustituya su propia cifra de reparaciones o su margen y la oferta cambia en la misma medida.',
    noData: 'No hay datos disponibles para este informe.',
  },
}

// Casita Check report labels. Same shape as LABELS above — a flat key→string
// map per language, read once at the top of the PDF — so the document itself
// carries no language branching.
//
// A label map, not a translation layer: nothing here translates a VALUE that
// came back from casita-check. The one exception is unitsStandard, and the
// note on it says why.
const CASITA_LABELS = {
  en: {
    variantTag: 'CASITA CHECK',
    theAnswer: 'The answer',
    sqftMax: 'sq ft max',
    sqftAtLeast: 'sq ft at least',
    atLeastPrefix: 'At least ',
    oneEach: 'Up to two casitas: one attached and one detached. The size limit applies to each.',
    withPermit: 'Up to {n} sq ft with an Administrative Use Permit',
    secondCasita: 'Second casita: up to {n} sq ft',
    whatAllows: 'What the rule allows',
    detail: 'Detail',
    casitasAllowed: 'Casitas allowed',
    maximumSize: 'Maximum size',
    // casita-check returns units as the literal "1 attached + 1 detached".
    // Printed verbatim that leaves one English row in an otherwise Spanish
    // document, so the known value is mapped here — the same thing LABELS
    // does for status_group with tagClosed/tagPending. Any OTHER value the
    // function ever returns passes through untouched; see the PDF.
    unitsStandard: '1 attached + 1 detached',
    height: 'Height',
    heightValue: 'Zoning district maximum',
    setback: 'Side and rear setback',
    setbackValue: '5 ft minimum',
    extraParking: 'Extra parking',
    extraParkingValue: 'Not required',
    ruleSource: 'Rule source',
    ruleVerified: 'Rule verified',
    // The three authority lines casita-check emits, and the one jurisdiction
    // label it rewrites. Held here in BOTH languages, with English as the
    // lookup key — see CASITA_AUTHORITY_KEY.
    authorityCity: 'CITY ORDINANCE APPLIES',
    authorityCounty: 'COUNTY ORDINANCE APPLIES',
    authorityState: 'STATE CASITA LAW APPLIES',
    jurisdictionUnincorporated: 'UNINCORPORATED MARICOPA COUNTY',
    checkBefore: 'Check before promising',
    fromCounty: 'From county records',
    lotSize: 'Lot size',
    livingArea: 'Living area',
    yearBuilt: 'Year built',
    city: 'City',
    subdivision: 'Subdivision',
    parcel: 'Parcel',
    lukeRow: 'Luke AFB',
    lukeInsideTerritory: 'Inside territory',
    lukeOutside: 'Outside',
    lukeNotChecked: 'Not checked',
    sqftUnit: 'sq ft',
    unincorporated: 'Unincorporated Maricopa County',
    noData: 'No data available for this report.',
  },
  es: {
    // REVIEW REQUIRED: Spanish text must be reviewed by Josh/Danae before client use.
    variantTag: 'REVISIÓN DE CASITA',
    theAnswer: 'La respuesta',
    sqftMax: 'pies cuadrados máximo',
    sqftAtLeast: 'pies cuadrados como mínimo',
    atLeastPrefix: 'Al menos ',
    oneEach: 'Hasta dos casitas: una adosada y una independiente. El límite de tamaño aplica a cada una.',
    withPermit: 'Hasta {n} pies cuadrados con un Permiso de Uso Administrativo',
    secondCasita: 'Segunda casita: hasta {n} pies cuadrados',
    whatAllows: 'Lo que permite la regla',
    detail: 'Detalle',
    casitasAllowed: 'Casitas permitidas',
    maximumSize: 'Tamaño máximo',
    unitsStandard: '1 adosada + 1 independiente',
    height: 'Altura',
    heightValue: 'El máximo del distrito de zonificación',
    setback: 'Retiro lateral y posterior',
    setbackValue: '5 pies mínimo',
    extraParking: 'Estacionamiento adicional',
    extraParkingValue: 'No se requiere',
    ruleSource: 'Fuente de la regla',
    ruleVerified: 'Regla verificada',
    authorityCity: 'APLICA LA ORDENANZA DE LA CIUDAD',
    authorityCounty: 'APLICA LA ORDENANZA DEL CONDADO',
    authorityState: 'APLICA LA LEY ESTATAL DE CASITAS',
    jurisdictionUnincorporated: 'ÁREA NO INCORPORADA DEL CONDADO DE MARICOPA',
    checkBefore: 'Verifique antes de prometer',
    fromCounty: 'De los registros del condado',
    lotSize: 'Tamaño del terreno',
    livingArea: 'Área habitable',
    yearBuilt: 'Año de construcción',
    city: 'Ciudad',
    subdivision: 'Subdivisión',
    parcel: 'Parcela',
    lukeRow: 'Zona de Luke AFB',
    lukeInsideTerritory: 'Dentro del territorio',
    lukeOutside: 'Fuera',
    lukeNotChecked: 'No verificado',
    sqftUnit: 'pies cuadrados',
    unincorporated: 'Área no incorporada del Condado de Maricopa',
    noData: 'No hay datos disponibles para este reporte.',
  },
}

// casita-check computes authority_label and jurisdiction_label server-side and
// returns them in ENGLISH ONLY — unlike basis/flags, which the function returns
// in both languages. So the translation happens here, keyed on the English
// value the response carries.
//
// A value that is not in this map PASSES THROUGH IN ENGLISH rather than being
// dropped or guessed at. The function can add an authority line at any time
// and a deploy of it does not wait on a deploy of this file; an untranslated
// line is a small seam, and a missing or invented one is a wrong statement
// about who governs the lot.
const CASITA_AUTHORITY_KEY = {
  'CITY ORDINANCE APPLIES': 'authorityCity',
  'COUNTY ORDINANCE APPLIES': 'authorityCounty',
  'STATE CASITA LAW APPLIES': 'authorityState',
}

// ---------- ZIP market report: labels ----------
const MARKET_LABELS = {
  en: {
    preparedFor: 'PREPARED FOR',
    reportTitle: 'Market Snapshot',
    forSellers: 'For sellers',
    forBuyers: 'For buyers',
    dataThrough: 'ARMLS data through',
    whatsHappening: "What's Happening in",
    byTheNumbers: 'By the Numbers',
    month: 'Month',
    forSale: 'For sale',
    newListings: 'New listings',
    underContract: 'Went under contract',
    sold: 'Sold',
    typicalPrice: 'Typical sold price in',
    typicalPriceNote: 'Half of the homes sold for more than this, half for less.',
    notEnoughSales: 'Not enough homes sold in',
    notEnoughSalesTail: 'to show a typical price.',
    chartTitle: 'Homes for sale vs. sold, last three months',
    legendActive: 'For sale',
    legendSold: 'Sold',
    generated: 'Generated',
    noData: 'No market data is available for this ZIP code.',
  },
  es: {
    // REVIEW REQUIRED: Spanish market-report labels must be reviewed by Josh/Danae before client use.
    preparedFor: 'PREPARADO PARA',
    reportTitle: 'Panorama del mercado',
    forSellers: 'Para vendedores',
    forBuyers: 'Para compradores',
    dataThrough: 'Datos de ARMLS hasta',
    whatsHappening: 'Qué está pasando en',
    byTheNumbers: 'En cifras',
    month: 'Mes',
    forSale: 'En venta',
    newListings: 'Listados nuevos',
    underContract: 'Entraron en contrato',
    sold: 'Vendidas',
    typicalPrice: 'Precio típico de venta en',
    typicalPriceNote: 'La mitad de las casas se vendieron por más de esta cifra y la mitad por menos.',
    notEnoughSales: 'No se vendieron suficientes casas en',
    notEnoughSalesTail: 'para mostrar un precio típico.',
    chartTitle: 'Casas en venta vs. vendidas, últimos tres meses',
    legendActive: 'En venta',
    legendSold: 'Vendidas',
    generated: 'Generado',
    noData: 'No hay datos de mercado disponibles para este código postal.',
  },
}

// ---------- ZIP market report: month label translation ----------
// The snapshot function emits two label formats: "Aug 2026" (months[], prior)
// and "August 2026" (last_month, through_month). Keyed on the first three
// letters so both resolve.
const MARKET_MONTHS_ES = {
  Jan: 'enero', Feb: 'febrero', Mar: 'marzo', Apr: 'abril',
  May: 'mayo', Jun: 'junio', Jul: 'julio', Aug: 'agosto',
  Sep: 'septiembre', Oct: 'octubre', Nov: 'noviembre', Dec: 'diciembre',
}
// Full date in the report's language: "Sep 2, 2026" / "2 de septiembre de 2026".
function marketDateLabel(iso, language = 'en') {
  if (language !== 'es') return pdfFormatDate(iso)
  const d = new Date(iso)
  if (isNaN(d.getTime())) return pdfFormatDate(iso)
  const es = Object.values(MARKET_MONTHS_ES)[d.getMonth()]
  return `${d.getDate()} de ${es} de ${d.getFullYear()}`
}
function marketMonthLabel(label, language = 'en') {
  if (!label) return ''
  if (language !== 'es') return label
  const [name, year] = String(label).split(' ')
  const es = MARKET_MONTHS_ES[String(name).slice(0, 3)]
  return es ? `${es} de ${year}` : label
}

// ---------- ZIP market report: number helpers ----------
// contract_ratio arrives as a 0–1 fraction (pended / active).
// sale_to_original_list_ratio arrives as a percentage (97.4). Different scales.
function marketMoney(n) {
  if (n == null || isNaN(n)) return '—'
  return pdfFormatCurrency(n)
}
function marketCents(ratioPct) {
  return Math.round(ratioPct)
}
function marketOutOfTen(fraction) {
  if (fraction == null || isNaN(fraction)) return 0
  return Math.max(0, Math.min(10, Math.round(fraction * 10)))
}
function marketDuration(days, language = 'en') {
  if (days == null || isNaN(days)) return '—'
  if (days < 21) {
    const w = Math.max(1, Math.round(days / 7))
    if (language === 'es') return w === 1 ? 'una semana' : `${w} semanas`
    return w === 1 ? 'a week' : `${w} weeks`
  }
  const m = Math.max(1, Math.round(days / 30))
  if (language === 'es') return m === 1 ? 'un mes' : `${m} meses`
  return m === 1 ? 'a month' : `${m} months`
}
function marketDirection(now, before) {
  if (now == null || before == null || before === 0) return 'same'
  const change = (now - before) / before
  if (change > 0.05) return 'more'
  if (change < -0.05) return 'fewer'
  return 'same'
}

// ---------- ZIP market report: sentence templates ----------
// Each returns { text, sub }. ctx is built in buildMarketSentences.
// Same keys in en and es. Sentences describe; they never advise.
// "prior" is three months back — named by its month, never by a season.
const MARKET_SENTENCES = {
  en: {
    inventory: (c) => ({
      text: c.audience === 'seller'
        ? { more: `There are more homes for sale than there were in ${c.priorLabel}.`,
            fewer: `There are fewer homes for sale than there were in ${c.priorLabel}.`,
            same: `The number of homes for sale is about the same as it was in ${c.priorLabel}.` }[c.direction]
        : { more: `There are more homes to choose from than there were in ${c.priorLabel}.`,
            fewer: `There are fewer homes to choose from than there were in ${c.priorLabel}.`,
            same: `The number of homes to choose from is about the same as it was in ${c.priorLabel}.` }[c.direction],
      sub: `${c.active} now · ${c.m1Active} in ${c.m1Label} · ${c.priorActive} in ${c.priorLabel}`,
    }),
    contractShare: (c) => ({
      text: `About ${c.outOfTen} out of every 10 homes for sale are already ${c.audience === 'seller' ? 'under contract' : 'spoken for'}, ${
        c.outOfTen > c.priorOutOfTen ? 'up from' : c.outOfTen < c.priorOutOfTen ? 'down from' : 'the same as'
      } about ${c.priorOutOfTen} in 10 in ${c.priorLabel}.`,
      sub: `${c.pended} of ${c.active}`,
    }),
    timeToSell: (c) => ({
      text: c.audience === 'seller'
        ? `Homes that sold in ${c.monthLabel} took about ${c.duration} to find a buyer.`
        : `Homes that sold in ${c.monthLabel} sat about ${c.duration} before going under contract.`,
      sub: `Average of ${c.avgDom} days across ${c.sold} sales`,
    }),
    shareOfAsking: (c) => ({
      text: c.audience === 'seller'
        ? `Sellers got about ${c.cents} cents on every dollar they first asked.`
        : `Buyers paid about ${c.cents} cents on every dollar sellers first asked.`,
      sub: `${c.ratioPct}% of the original asking price`,
    }),
    askingVsSold: (c) => ({
      text: `Homes for sale right now are asking around ${c.asking}. Half the ${c.monthLabel} sales went for more than ${c.median}, half for less.`,
      sub: `${c.sold} sales in ${c.monthLabel}`,
    }),
    notEnough: (c) => ({
      text: `Not enough homes sold in ${c.monthLabel} to show this (${c.sold} ${c.sold === 1 ? 'sale' : 'sales'}).`,
      sub: '',
    }),
  },
  es: {
    // REVIEW REQUIRED: Spanish sentences must be reviewed by Josh/Danae before client use.
    inventory: (c) => ({
      text: c.audience === 'seller'
        ? { more: `Hay más casas en venta que en ${c.priorLabel}.`,
            fewer: `Hay menos casas en venta que en ${c.priorLabel}.`,
            same: `La cantidad de casas en venta es casi la misma que en ${c.priorLabel}.` }[c.direction]
        : { more: `Hay más casas para elegir que en ${c.priorLabel}.`,
            fewer: `Hay menos casas para elegir que en ${c.priorLabel}.`,
            same: `La cantidad de casas para elegir es casi la misma que en ${c.priorLabel}.` }[c.direction],
      sub: `${c.active} ahora · ${c.m1Active} en ${c.m1Label} · ${c.priorActive} en ${c.priorLabel}`,
    }),
    contractShare: (c) => ({
      text: `Aproximadamente ${c.outOfTen} de cada 10 casas en venta ya están ${c.audience === 'seller' ? 'en contrato' : 'apartadas'}, ${
        c.outOfTen > c.priorOutOfTen ? 'más que' : c.outOfTen < c.priorOutOfTen ? 'menos que' : 'igual que'
      } las ${c.priorOutOfTen} de cada 10 en ${c.priorLabel}.`,
      sub: `${c.pended} de ${c.active}`,
    }),
    timeToSell: (c) => ({
      text: c.audience === 'seller'
        ? `Las casas que se vendieron en ${c.monthLabel} tardaron aproximadamente ${c.duration} en encontrar comprador.`
        : `Las casas que se vendieron en ${c.monthLabel} estuvieron aproximadamente ${c.duration} en el mercado antes de entrar en contrato.`,
      sub: `Promedio de ${c.avgDom} días en ${c.sold} ventas`,
    }),
    shareOfAsking: (c) => ({
      text: c.audience === 'seller'
        ? `Los vendedores recibieron aproximadamente ${c.cents} centavos por cada dólar que pidieron al inicio.`
        : `Los compradores pagaron aproximadamente ${c.cents} centavos por cada dólar que los vendedores pidieron al inicio.`,
      sub: `${c.ratioPct}% del precio original de lista`,
    }),
    askingVsSold: (c) => ({
      text: `Las casas en venta ahora piden alrededor de ${c.asking}. La mitad de las ventas de ${c.monthLabel} fueron por más de ${c.median} y la mitad por menos.`,
      sub: `${c.sold} ventas en ${c.monthLabel}`,
    }),
    notEnough: (c) => ({
      text: `No se vendieron suficientes casas en ${c.monthLabel} para mostrar este dato (${c.sold} ${c.sold === 1 ? 'venta' : 'ventas'}).`,
      sub: '',
    }),
  },
}

// Builds the five sentences from the snapshot payload. Returns [{ key, text, sub }].
function buildMarketSentences(data, audience = 'seller', language = 'en') {
  if (!data?.ok || !data.months?.length) return []
  const T = MARKET_SENTENCES[language] || MARKET_SENTENCES.en
  const m0 = data.months[0]
  const m1 = data.months[1] || m0
  const cur = data.current || {}
  const prior = data.prior || {}
  const last = data.last_month || {}
  const c = {
    audience,
    active: cur.active ?? m0.active ?? 0,
    pended: cur.pended ?? 0,
    m1Active: m1.active ?? 0,
    m1Label: marketMonthLabel(m1.label, language),
    priorActive: prior.active ?? 0,
    priorLabel: marketMonthLabel(prior.label, language),
    direction: marketDirection(cur.active ?? m0.active, prior.active),
    outOfTen: marketOutOfTen(cur.contract_ratio),
    priorOutOfTen: marketOutOfTen(prior.contract_ratio),
    monthLabel: marketMonthLabel(last.label || m0.label, language),
    sold: last.sold ?? m0.sold ?? 0,
    avgDom: last.avg_dom != null ? Math.round(last.avg_dom) : null,
    duration: marketDuration(last.avg_dom, language),
    cents: marketCents(last.sale_to_original_list_ratio ?? 0),
    ratioPct: last.sale_to_original_list_ratio != null ? Number(last.sale_to_original_list_ratio).toFixed(1) : '—',
    asking: marketMoney(last.median_asking_price_active),
    median: marketMoney(last.median_sold_price),
  }
  // Thin month: flagged by the function, or no closings, or no DOM to average.
  const thin = !!last.low_confidence || !(c.sold > 0) || c.avgDom == null
  return [
    { key: 'inventory', ...T.inventory(c) },
    { key: 'contractShare', ...T.contractShare(c) },
    { key: 'timeToSell', ...(thin ? T.notEnough(c) : T.timeToSell(c)) },
    { key: 'shareOfAsking', ...(thin ? T.notEnough(c) : T.shareOfAsking(c)) },
    { key: 'askingVsSold', ...(thin ? T.notEnough(c) : T.askingVsSold(c)) },
  ]
}

// ---------- ZIP market report: disclaimer ----------
function marketDisclaimer(brokerage, throughLabel, language = 'en') {
  if (language === 'es') {
    const nameEs = pdfSafe(brokerage, 'La correduría')
    // REVIEW REQUIRED: Spanish legal disclaimer must be reviewed by Josh/Danae before client use.
    return `Estadísticas de mercado únicamente con fines informativos. No es una valuación de ninguna propiedad en particular ni un avalúo. Las cifras reflejan datos de ARMLS hasta ${throughLabel} y pueden cambiar a medida que se actualicen los registros. ${nameEs} es una correduría inmobiliaria con licencia en Arizona.`
  }
  const name = pdfSafe(brokerage, 'The brokerage')
  return `Market statistics for informational purposes only. Not a valuation of any specific property and not an appraisal. Figures reflect ARMLS data through ${throughLabel} and may change as records are updated. ${name} is an Arizona-licensed real estate brokerage.`
}

// ---------- ZIP market report: PDF ----------
// Strategy's skin exactly: pdfStyles.page, header row, subline, gold rule,
// blue section headers, blue callout, navy PdfFooter. Only the pieces that
// have no Strategy analogue (bars, sentences, callout body) get styles here.
// Bars are plain Views with computed heights — no chart library, no new import.
const marketPdfStyles = StyleSheet.create({
  sentence: { marginBottom: 10 },
  sentenceText: { fontSize: 12, color: PDF_COLORS.navy, lineHeight: 1.4 },
  sentenceSub: { fontSize: 8, color: PDF_COLORS.grayMid, marginTop: 2 },
  chartWrap: { marginTop: 4, marginBottom: 12 },
  chartTitle: { fontSize: 9, color: PDF_COLORS.grayText, marginBottom: 6 },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 90,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'solid',
    borderBottomColor: PDF_COLORS.grayBorder,
  },
  chartGroup: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'center' },
  barCol: { alignItems: 'center', marginHorizontal: 3 },
  bar: { width: 22 },
  barActive: { backgroundColor: PDF_COLORS.navy },
  barSold: { backgroundColor: PDF_COLORS.yellow },
  barValue: { fontSize: 7, color: PDF_COLORS.grayText, marginBottom: 2 },
  chartLabels: { flexDirection: 'row', marginTop: 4 },
  chartLabel: { flex: 1, fontSize: 8, color: PDF_COLORS.grayText, textAlign: 'center' },
  legend: { flexDirection: 'row', justifyContent: 'center', marginTop: 6 },
  legendItem: { flexDirection: 'row', alignItems: 'center', marginHorizontal: 8 },
  legendSwatch: { width: 8, height: 8, marginRight: 4 },
  legendText: { fontSize: 8, color: PDF_COLORS.grayText },
  calloutTitle: { fontSize: 9, color: PDF_COLORS.grayText, letterSpacing: 0.5, marginBottom: 2 },
  calloutValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.navy },
  calloutNote: { fontSize: 9, color: PDF_COLORS.grayText, marginTop: 3 },
  generated: { fontSize: 8, color: PDF_COLORS.grayMid, marginTop: 10 },
})

function MarketReportPDF({ data, request, profile, email, language = 'en', teammate = null }) {
  const lang = language === 'es' ? 'es' : 'en'
  const L = MARKET_LABELS[lang]
  if (!data?.ok || !data.months?.length) {
    return (
      <Document>
        <Page size="LETTER" style={pdfStyles.page}>
          <Text>{L.noData}</Text>
        </Page>
      </Document>
    )
  }
  const audience = request?.audience === 'buyer' ? 'buyer' : 'seller'
  const audienceLabel = audience === 'buyer' ? L.forBuyers : L.forSellers
  const clientName = pdfSafe(request?.client_name, '').toUpperCase()
  const variantTag = clientName ? `${L.preparedFor} ${clientName}` : audienceLabel.toUpperCase()
  const through = marketMonthLabel(data.as_of?.through_month, lang)
  const place = [data.zip, data.city].filter(Boolean).join(' · ')
  const sentences = buildMarketSentences(data, audience, lang)
  const last = data.last_month || {}
  const lastLabel = marketMonthLabel(last.label, lang)
  const thin = !!last.low_confidence || !(last.sold > 0)
  // Chart reads oldest → newest left to right; payload is newest first.
  const months = [...data.months].reverse()
  const maxVal = Math.max(1, ...months.map(m => Math.max(m.active || 0, m.sold || 0)))
  const barH = (n) => Math.max(2, Math.round(70 * ((n || 0) / maxVal)))
  const newest = data.months[0]
  const disclaimer = marketDisclaimer(profile?.brokerage, through, lang)

  return (
    <Document>
      <Page size="LETTER" style={pdfStyles.page}>
        <PdfHeader
          brokerage={profile?.brokerage}
          variantTag={variantTag}
          subline={<>{place} · {audienceLabel} · {L.dataThrough} {through}</>}
        />

        <PdfSection title={`${L.whatsHappening} ${data.zip}`}>
          <View style={marketPdfStyles.chartWrap} wrap={false}>
            <Text style={marketPdfStyles.chartTitle}>{L.chartTitle}</Text>
            <View style={marketPdfStyles.chartRow}>
              {months.map((m, i) => (
                <View key={i} style={marketPdfStyles.chartGroup}>
                  <View style={marketPdfStyles.barCol}>
                    <Text style={marketPdfStyles.barValue}>{m.active ?? '—'}</Text>
                    <View style={[marketPdfStyles.bar, marketPdfStyles.barActive, { height: barH(m.active) }]} />
                  </View>
                  <View style={marketPdfStyles.barCol}>
                    <Text style={marketPdfStyles.barValue}>{m.sold ?? '—'}</Text>
                    <View style={[marketPdfStyles.bar, marketPdfStyles.barSold, { height: barH(m.sold) }]} />
                  </View>
                </View>
              ))}
            </View>
            <View style={marketPdfStyles.chartLabels}>
              {months.map((m, i) => (
                <Text key={i} style={marketPdfStyles.chartLabel}>{marketMonthLabel(m.label, lang)}</Text>
              ))}
            </View>
            <View style={marketPdfStyles.legend}>
              <View style={marketPdfStyles.legendItem}>
                <View style={[marketPdfStyles.legendSwatch, marketPdfStyles.barActive]} />
                <Text style={marketPdfStyles.legendText}>{L.legendActive}</Text>
              </View>
              <View style={marketPdfStyles.legendItem}>
                <View style={[marketPdfStyles.legendSwatch, marketPdfStyles.barSold]} />
                <Text style={marketPdfStyles.legendText}>{L.legendSold}</Text>
              </View>
            </View>
          </View>

          {sentences.map(s => (
            <View key={s.key} style={marketPdfStyles.sentence} wrap={false}>
              <Text style={marketPdfStyles.sentenceText}>{s.text}</Text>
              {s.sub ? <Text style={marketPdfStyles.sentenceSub}>{s.sub}</Text> : null}
            </View>
          ))}

          <View style={[pdfStyles.callout, pdfStyles.calloutBlue]} wrap={false}>
            {thin ? (
              <Text style={marketPdfStyles.calloutNote}>{L.notEnoughSales} {lastLabel} {L.notEnoughSalesTail}</Text>
            ) : (
              <>
                <Text style={marketPdfStyles.calloutTitle}>{`${L.typicalPrice} ${lastLabel}`.toUpperCase()}</Text>
                <Text style={marketPdfStyles.calloutValue}>{marketMoney(last.median_sold_price)}</Text>
                <Text style={marketPdfStyles.calloutNote}>{L.typicalPriceNote}</Text>
              </>
            )}
          </View>
        </PdfSection>

        <PdfSection title={L.byTheNumbers}>
          <PdfTable
            blueHeader
            headers={[
              { label: L.month, flex: 1.4 },
              { label: L.forSale, flex: 1, align: 'right' },
              { label: L.newListings, flex: 1, align: 'right' },
              { label: L.underContract, flex: 1.3, align: 'right' },
              { label: L.sold, flex: 1, align: 'right' },
            ]}
            rows={[[
              marketMonthLabel(newest.label, lang),
              newest.active,
              newest.new_listings,
              newest.pended,
              newest.sold,
            ]]}
          />
          <Text style={marketPdfStyles.generated}>{L.generated} {marketDateLabel(data.as_of?.generated_at || new Date().toISOString(), lang)}</Text>
        </PdfSection>

        <PdfFooter profile={profile} email={email} teammate={teammate} disclaimerText={disclaimer} />
      </Page>
    </Document>
  )
}

function StrategyReportPDFClient({ data, profile, email, language = 'en', teammate = null }) {
  const L = LABELS[language] || LABELS.en
  if (!data) {
    return (
      <Document>
        <Page size="LETTER" style={pdfStyles.page}>
          <Text>{L.noData}</Text>
        </Page>
      </Document>
    )
  }

  const isBuyer = data.report_type === 'buyer'
  const variantTag = isBuyer ? L.preparedBuyer : L.preparedSeller
  // An investor report is one that carries the investor lane and no
  // owner-occupant lane. Checking for the ABSENCE of owner_occupant rather than
  // trusting type alone is what keeps a pre-change "dual" extraction — where
  // client type Buyer emitted both lanes — rendering as owner-occupant, which is
  // what Buyer means now. Those reports must not suddenly become investor
  // documents because they happen to contain investor fields.
  const investorData = (data.strategy_section && data.strategy_section.investor) || null
  const isInvestorReport = isBuyer && !!investorData &&
    (data.strategy_section.type === 'investor' || !data.strategy_section.owner_occupant)
  const subject = data.subject || {}
  const bestComps = Array.isArray(data.best_comps) ? data.best_comps : []
  const weakComps = Array.isArray(data.weak_comps) ? data.weak_comps : []
  const competition = Array.isArray(data.active_competition) ? data.active_competition : []
  const clientStrategyNotes = Array.isArray(data.client_strategy_notes) ? data.client_strategy_notes : []

  // RECENT SALES — closed comps only, and "closed" now means the BUCKET the
  // live payload put the comp in, not the presence of a close_date the model
  // typed. A section headed "Recent Sales in Your Neighborhood" that lists a
  // pending or an active listing is telling a seller a house sold when it did
  // not, which is the single worst thing this document can do.
  //
  // Deduped on listing_id rather than on a normalised address string: the join
  // guarantees an id on every surviving comp, and two rows for one property is
  // the failure the address key was approximating.
  //
  // The data.comp_buckets.remodeled branch that stood here is gone. It was
  // dead — `remodeled` appears nowhere in the extraction schema, so the
  // condition was never true — and it read off unjoined model output, which is
  // exactly what this pass removes.
  const seenIds = new Set()
  const recentSales = []
  bestComps.forEach(bc => {
    if (bc.status_group !== 'closed') return
    if (seenIds.has(bc.listing_id)) return
    seenIds.add(bc.listing_id)
    recentSales.push({ address: bc.address, price: bc.price, sqft: bc.sqft, dom: bc.dom, close_date: bc.close_date })
  })

  return (
    <Document>
      <Page size="LETTER" style={pdfStyles.page}>
        <PdfHeader
          brokerage={profile?.brokerage}
          variantTag={variantTag}
          subline={<>{pdfSafe(subject.address || data.subject_address, 'Property')} · {pdfFormatDate(new Date().toISOString())}</>}
        />

        <PdfSection title={isBuyer ? L.property : L.yourProperty}>
          <PdfTable
            headers={[{ label: L.detail, flex: 1 }, { label: '', flex: 2 }]}
            rows={[
              [L.address, pdfSafe(subject.address || data.subject_address)],
              [L.yearBuilt, pdfSafe(subject.year_built)],
              subject.subdivision ? [L.subdivision, pdfSafe(subject.subdivision)] : null,
              [L.size, subject.gla_sqft ? subject.gla_sqft.toLocaleString() + ' sqft' : '—'],
              [L.bedsBaths, (subject.beds != null && subject.baths != null) ? `${subject.beds} / ${subject.baths}` : '—'],
              [L.lot, subject.lot_sqft ? subject.lot_sqft.toLocaleString() + ' sqft' : '—'],
              [L.condition, pdfSafe(subject.condition_tier)],
              [L.poolHoa, `${subject.pool ? L.yes : L.no} / ${subject.hoa ? L.yes : L.no}`],
            ].filter(Boolean)}
          />
        </PdfSection>

        {recentSales.length > 0 && (
          <PdfSection title={L.recentSales}>
            <PdfTable
              blueHeader
              headers={[
                { label: L.address, flex: 2 },
                { label: L.soldPrice, flex: 1, align: 'right' },
                { label: L.size, flex: 1 },
                { label: L.timeToSell, flex: 1 },
              ]}
              rows={recentSales.map(rs => [
                pdfSafe(rs.address),
                pdfFormatCurrency(rs.price),
                (rs.sqft && rs.sqft > 0) ? rs.sqft.toLocaleString() + ' sqft' : '—',
                (rs.dom != null && rs.dom > 0) ? (rs.dom + ' ' + L.days) : '—',
              ])}
            />
          </PdfSection>
        )}

        {bestComps.length > 0 && (
          <PdfSection title={L.bestComp}>
            {bestComps.map((bc, i) => (
              <View key={i} wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue]}>
                {/* The status tag rides INSIDE the existing bold address line
                    as a nested Text, so it sits on the same baseline with no
                    new element and no layout change. Its style is
                    pdfStyles.variantTag — the 9pt grayMid the page header
                    already uses for "PREPARED FOR THE SELLER" — reused rather
                    than a new colour, because this is the same kind of thing:
                    a quiet label qualifying the line it sits on.
                    Rendered only when the join produced a group, so a report
                    made before this change shows the line exactly as it was. */}
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>
                  {pdfSafe(bc.address)} — {pdfFormatCurrency(bc.price)}
                  {L[COMP_STATUS_TAG_KEY[bc.status_group]] && (
                    <Text style={pdfStyles.variantTag}>  {L[COMP_STATUS_TAG_KEY[bc.status_group]]}</Text>
                  )}
                </Text>
                {bc.client_explanation?.why_it_anchors && (
                  <Text style={{ fontSize: 10, marginBottom: 4 }}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>{L.whyItMatters} </Text>
                    {pdfSafe(bc.client_explanation.why_it_anchors)}
                  </Text>
                )}
                {bc.client_explanation?.whats_different && (
                  <Text style={{ fontSize: 10 }}>
                    <Text style={{ fontFamily: 'Helvetica-Bold' }}>{L.whatsDifferent} </Text>
                    {pdfSafe(bc.client_explanation.whats_different)}
                  </Text>
                )}
              </View>
            ))}
          </PdfSection>
        )}

        {weakComps.length > 0 && (
          <PdfSection title={L.notUsingComp}>
            {weakComps.map((wc, i) => (
              <View key={i} wrap={false} style={[pdfStyles.callout, pdfStyles.calloutGray]}>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 4 }}>
                  {pdfSafe(wc.address)} — {pdfFormatCurrency(wc.price)}
                </Text>
                <Text style={{ fontSize: 10 }}>{pdfSafe(wc.client_explanation)}</Text>
              </View>
            ))}
          </PdfSection>
        )}

        {!isBuyer && data.list_price_strategy && (
          <PdfSection title={L.threePricing}>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableHeaderRowBlue} wrap={false} minPresenceAhead={36}>
                <Text style={[pdfStyles.tableHeaderCell, { flex: 1 }]}>{L.strategy}</Text>
                <Text style={[pdfStyles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>{L.listPrice}</Text>
                <Text style={[pdfStyles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>{L.expectedTime}</Text>
                <Text style={[pdfStyles.tableHeaderCell, { flex: 2 }]}>{L.bestWhen}</Text>
              </View>
              {['aggressive', 'market', 'aspirational'].map((laneKey, i) => {
                const lane = data.list_price_strategy.lanes?.[laneKey]
                if (!lane) return null
                const isRecommended = data.list_price_strategy.recommended_lane === laneKey
                const rowStyle = isRecommended
                  ? { backgroundColor: PDF_COLORS.blueLight }
                  : (i % 2 === 1 ? { backgroundColor: PDF_COLORS.gray } : {})
                const txtColor = isRecommended ? PDF_COLORS.blue : PDF_COLORS.navy
                const txtFont = isRecommended ? 'Helvetica-Bold' : 'Helvetica'
                return (
                  <View key={laneKey} wrap={false} style={[pdfStyles.tableRow, rowStyle]}>
                    <Text style={[pdfStyles.tableCell, { flex: 1, color: txtColor, fontFamily: txtFont }]}>
                      {isRecommended ? '★ ' : ''}{L['lane' + laneKey.charAt(0).toUpperCase() + laneKey.slice(1)]}
                    </Text>
                    <Text style={[pdfStyles.tableCell, { flex: 1, textAlign: 'right', color: txtColor, fontFamily: txtFont }]}>
                      {pdfFormatCurrency(lane.price)}
                    </Text>
                    <Text style={[pdfStyles.tableCell, { flex: 1, textAlign: 'right', color: txtColor }]}>
                      {pdfSafe(lane.expected_dom)}
                    </Text>
                    <Text style={[pdfStyles.tableCell, { flex: 2, color: txtColor }]}>
                      {pdfSafe(lane.fits_seller)}
                    </Text>
                  </View>
                )
              })}
            </View>
            {data.list_price_strategy.recommended_lane && (() => {
              const recKey = data.list_price_strategy.recommended_lane
              const recLane = data.list_price_strategy.lanes?.[recKey]
              if (!recLane) return null
              return (
                <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue, { marginTop: 8 }]}>
                  <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: PDF_COLORS.blue }}>
                    {L.ourRec} {pdfFormatCurrency(recLane.price)}
                  </Text>
                  <Text style={{ fontSize: 10 }}>{pdfSafe(recLane.reasoning)}</Text>
                </View>
              )
            })()}
          </PdfSection>
        )}

        {/* !isBuyer, not just a length check. The extraction is instructed to
            null active_competition on buyer reports, but that is an instruction,
            not a guard — and the Investor branch is now explicitly told actives
            are never proof of value. An active listing reaching a buyer PDF
            would contradict the analysis that produced it, so the gate is
            structural rather than dependent on the model complying. */}
        {!isBuyer && competition.length > 0 && (
          <PdfSection title={L.activeComp}>
            <PdfTable
              headers={[
                { label: L.address, flex: 2 },
                { label: L.listPrice, flex: 1, align: 'right' },
                { label: L.daysOnMarket, flex: 1, align: 'right' },
              ]}
              rows={competition.map(c => [pdfSafe(c.address), pdfFormatCurrency(c.list_price), (c.dom != null && c.dom > 0) ? String(c.dom) : '—'])}
            />
            {(() => {
              // NAMED AS "the closest comparable listing", so it has to be one
              // the analysis actually treated as competition. Two changes:
              //
              // Only threat_level 'high' now qualifies. The old
              // `|| competition[0]` fallback printed the first row whatever it
              // was, so a table with nothing marked high still produced the
              // sentence — about a listing the analysis never called a threat.
              //
              // And a listing the analysis put in weak_comps is excluded
              // outright. A comp can legitimately appear in both arrays, and
              // naming the seller's closest competitor as the very property the
              // report has just explained it is NOT using is a contradiction on
              // one page. Matched on listing_id, which the join guarantees.
              //
              // No qualifying listing means no line. The table above already
              // shows the competition; this sentence is the claim about it, and
              // an unfounded claim is worse than silence.
              const weakIds = new Set(weakComps.map(w => w.listing_id).filter(Boolean))
              const topThreat = competition.find(c =>
                c.threat_level === 'high' && !(c.listing_id && weakIds.has(c.listing_id)))
              if (!topThreat) return null
              return (
                <Text style={{ fontSize: 10, marginTop: 6, fontStyle: 'italic', color: PDF_COLORS.grayText }}>
                  {L.closestListing} {pdfSafe(topThreat.address)} {L.at} {pdfFormatCurrency(topThreat.list_price)}.
                </Text>
              )
            })()}
          </PdfSection>
        )}

        {!isBuyer && clientStrategyNotes.length > 0 && (
          <PdfSection title={L.whatThisMeans}>
            {clientStrategyNotes.map((note, i) => (
              <Text key={i} style={{ fontSize: 10, marginBottom: 6, lineHeight: 1.5 }}>{pdfSafe(note)}</Text>
            ))}
          </PdfSection>
        )}

        {/* Both of these used to be JSON.stringify of the whole object — the
            reader got curly braces and quoted keys. The typeof === 'string'
            branch they sat behind was unreachable: the extraction schema types
            both as objects, so the stringify was the only path, on every buyer
            report since the feature shipped.

            Every field is optional and rows with no value are dropped, so a
            report generated before the schema was tightened renders as a
            shorter table rather than an empty section.

            INVESTOR REPORTS TAKE THE SEPARATE SECTION BELOW, under its own
            heading. The two are different products and must not read as the
            same one. An extraction that carries BOTH lanes is a pre-change
            "dual" report from when client type Buyer emitted both; those render
            as owner-occupant only, which is what Buyer means now. */}
        {/* INVESTOR — its own section, its own heading. Two parts only: the
            after-repair range, and the subtraction that turns it into an offer.
            No holding costs, no financing, no resale breakdown — the analysis is
            instructed not to produce them and there is nowhere here to put them.

            The margin column is DERIVED as arv - repairs - offer rather than
            printed from margin_amount, so the subtraction on the page always
            balances. A document whose entire purpose is showing its own
            arithmetic cannot afford a column that does not add up; if the
            extraction's own figures disagree, the derived value is the one that
            keeps the page honest about the relationship between the numbers. */}
        {isInvestorReport && (() => {
          const iv = investorData
          const lowV = iv.arv_low, highV = iv.arv_high
          const ends = []
          if (lowV != null) ends.push(lowV)
          if (highV != null && highV !== lowV) ends.push(highV)
          const singleComp = iv.arv_comp_count === 1 || ends.length === 1
          const noEvidence = ends.length === 0

          // Nothing to show at all — say why rather than printing an empty box.
          if (noEvidence || iv.not_a_deal_reason) {
            const msg = iv.not_a_deal_reason || null
            if (!msg) return null
            return (
              <PdfSection title={L.afterRepairTitle}>
                <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutGray]}>
                  <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{pdfSafe(msg)}</Text>
                </View>
              </PdfSection>
            )
          }

          const repair = iv.repair_estimate
          const offerAt = (arv, offerField) => {
            if (offerField != null) return offerField
            if (arv != null && repair != null && iv.margin_amount != null) return arv - repair - iv.margin_amount
            return null
          }
          const cols = ends.map((arv, i) => {
            const offer = offerAt(arv, i === 0 ? iv.offer_low : iv.offer_high)
            const margin = (arv != null && repair != null && offer != null)
              ? arv - repair - offer
              : iv.margin_amount
            return { arv, offer, margin }
          })

          const showTable = repair != null && cols.some(c => c.offer != null)
          const twoCol = cols.length === 2
          const headers = [{ label: '', flex: 3 }]
          if (twoCol) headers.push({ label: L.low, flex: 2, align: 'right' }, { label: L.high, flex: 2, align: 'right' })
          else headers.push({ label: '', flex: 2, align: 'right' })
          const money = v => v == null ? '—' : pdfFormatCurrency(v)
          const less = v => v == null ? '—' : `– ${pdfFormatCurrency(v)}`
          const rows = showTable ? [
            [L.afterRepairValue, ...cols.map(c => money(c.arv))],
            [L.lessRepairs, ...cols.map(() => less(repair))],
            [L.lessMargin, ...cols.map(c => less(c.margin))],
            [L.offerRow, ...cols.map(c => money(c.offer))],
          ] : []

          return (
            <PdfSection title={L.afterRepairTitle}>
              <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue]}>
                <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.navy, marginBottom: 4 }}>
                  {twoCol
                    ? `${pdfFormatCurrency(lowV)} – ${pdfFormatCurrency(highV)}`
                    : `${pdfFormatCurrency(ends[0])}${singleComp ? `  — ${L.ceilingReference}` : ''}`}
                </Text>
                {iv.arv_basis && (
                  <Text style={{ fontSize: 10, lineHeight: 1.5, marginBottom: 4 }}>{pdfSafe(iv.arv_basis)}</Text>
                )}
                <Text style={{ fontSize: 10, lineHeight: 1.5, fontStyle: 'italic' }}>{L.projectedNote}</Text>
              </View>

              {showTable && (
                <>
                  <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', marginTop: 10, marginBottom: 4, color: PDF_COLORS.navy }}>{L.offerToday}</Text>
                  <PdfTable headers={headers} rows={rows} />
                  {iv.repair_basis && (
                    <Text style={{ fontSize: 9.5, color: '#555', marginTop: 6, lineHeight: 1.5 }}>{pdfSafe(iv.repair_basis)}</Text>
                  )}
                  <Text style={{ fontSize: 9.5, color: '#555', marginTop: 3, lineHeight: 1.5 }}>{L.substituteNote}</Text>
                </>
              )}
            </PdfSection>
          )
        })()}

        {isBuyer && data.strategy_section && !isInvestorReport && (() => {
          const oo = data.strategy_section.owner_occupant
          const notes = Array.isArray(data.strategy_section.buyer_education_notes)
            ? data.strategy_section.buyer_education_notes.filter(Boolean) : []
          const risks = oo && Array.isArray(oo.risks) ? oo.risks.filter(Boolean) : []

          const riskLabel = { low: L.riskLow, moderate: L.riskModerate, high: L.riskHigh }
          const rangeText = oo && (oo.fair_offer_low != null || oo.fair_offer_high != null)
            ? (oo.fair_offer_low != null && oo.fair_offer_high != null
                ? `${pdfFormatCurrency(oo.fair_offer_low)} – ${pdfFormatCurrency(oo.fair_offer_high)}`
                : pdfFormatCurrency(oo.fair_offer_low != null ? oo.fair_offer_low : oo.fair_offer_high))
            : null

          const rows = []
          if (rangeText) rows.push([L.fairOfferRange, rangeText, L.fairOfferMeans])
          if (oo && oo.strong_offer != null) rows.push([L.strongOffer, pdfFormatCurrency(oo.strong_offer), L.strongOfferMeans])
          if (oo && oo.stretch_offer != null) rows.push([L.stretchOffer, pdfFormatCurrency(oo.stretch_offer), oo.stretch_appraisal_risk ? pdfSafe(riskLabel[oo.stretch_appraisal_risk], '') : ''])
          if (oo && oo.walk_away != null) rows.push([L.walkAway, pdfFormatCurrency(oo.walk_away), L.walkAwayMeans])

          if (rows.length === 0 && risks.length === 0 && notes.length === 0) return null

          return (
            <PdfSection title={L.ourStrategy}>
              {rows.length > 0 && (
                <PdfTable
                  headers={[
                    { label: L.offerLevel, flex: 2 },
                    { label: L.amount, flex: 2, align: 'right' },
                    { label: L.whatItMeans, flex: 3 },
                  ]}
                  rows={rows}
                />
              )}
              {oo && oo.stretch_note && (
                <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue, { marginTop: 8 }]}>
                  <Text style={{ fontSize: 10 }}>{pdfSafe(oo.stretch_note)}</Text>
                </View>
              )}
              {risks.length > 0 && (
                <>
                  <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', marginTop: 10, marginBottom: 4, color: PDF_COLORS.navy }}>{L.whatToWatch}</Text>
                  {risks.map((r, i) => (
                    <Text key={i} style={{ fontSize: 10, marginBottom: 5, lineHeight: 1.5 }}>• {pdfSafe(r)}</Text>
                  ))}
                </>
              )}
              {notes.length > 0 && (
                <>
                  <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', marginTop: 10, marginBottom: 4, color: PDF_COLORS.navy }}>{L.goodToKnow}</Text>
                  {notes.map((n, i) => (
                    <Text key={i} style={{ fontSize: 10, marginBottom: 5, lineHeight: 1.5 }}>• {pdfSafe(n)}</Text>
                  ))}
                </>
              )}
            </PdfSection>
          )
        })()}

        {isBuyer && data.offer_recommendation && (() => {
          const o = data.offer_recommendation

          // INVESTOR: one callout carrying the offer BAND, matching the
          // subtraction above rather than restating a single point from it.
          if (isInvestorReport) {
            const lo = investorData && investorData.offer_low
            const hi = investorData && investorData.offer_high
            const band = (lo != null && hi != null && hi !== lo)
              ? `${pdfFormatCurrency(lo)} – ${pdfFormatCurrency(hi)}`
              : (lo != null || hi != null)
                ? pdfFormatCurrency(lo != null ? lo : hi)
                : (o.investor_number != null ? pdfFormatCurrency(o.investor_number) : null)
            if (!band) return null
            const why = o.investor_reasoning || o.reasoning
            return (
              <PdfSection title={L.recommendedOffer}>
                <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue]}>
                  <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.navy, marginBottom: 4 }}>{band}</Text>
                  {why && <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{pdfSafe(why)}</Text>}
                </View>
              </PdfSection>
            )
          }

          // Owner-occupant side. offer_number is the only field a current
          // extraction populates; the per-lane fields are read only as a
          // fallback for reports made before the schema collapsed to one number.
          //
          // investor_number is deliberately NOT a lane here: client type Buyer
          // no longer produces an investor lane, and a pre-change "dual" report
          // must not hand a home buyer an investment offer they never asked for.
          //
          // DEDUPED ON THE NUMBER, not on presence. A first-time-buyer report
          // arrived with owner_occupant_number and first_time_buyer_number set
          // to the SAME value and two different paragraphs of reasoning, and
          // printed $350,000 twice — once labelled, once not. Two lanes holding
          // genuinely different numbers is still a real state for an old dual
          // report, so identical values collapse and distinct ones both survive.
          const rawLanes = o.offer_number != null
            ? [{ n: o.offer_number, why: o.offer_reasoning || o.reasoning, label: null }]
            : [
                { n: o.owner_occupant_number, why: o.owner_occupant_reasoning, label: L.asHome },
                { n: o.first_time_buyer_number, why: o.first_time_buyer_reasoning, label: null },
              ].filter(l => l.n != null)

          const lanes = rawLanes.filter((l, i) => rawLanes.findIndex(x => x.n === l.n) === i)

          if (lanes.length === 0) return null
          // Keyed on distinct lanes surviving the dedupe, not on array length —
          // the phantom second lane was the only reason a label appeared at all.
          const showLabels = lanes.length > 1

          return (
            <PdfSection title={L.recommendedOffer}>
              {lanes.map((l, i) => (
                <View key={i} wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue, i > 0 ? { marginTop: 8 } : null]}>
                  {showLabels && l.label && (
                    <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.blue, marginBottom: 3 }}>{l.label}</Text>
                  )}
                  <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.navy, marginBottom: 4 }}>
                    {pdfFormatCurrency(l.n)}
                  </Text>
                  {/* Per-lane reasoning, falling back to the shared `reasoning`
                      field that older extractions used. */}
                  {(l.why || (lanes.length === 1 && o.reasoning)) && (
                    <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{pdfSafe(l.why || o.reasoning)}</Text>
                  )}
                </View>
              ))}
            </PdfSection>
          )
        })()}

        <PdfFooter profile={profile} email={email} teammate={teammate} disclaimerText={clientDisclaimer(profile?.brokerage, language)} />
      </Page>
    </Document>
  )
}

// ─── CASITA CHECK: PDF REPORT ───────────────────────────────────────────────
// The client-facing casita report. Unlike the Strategy and Market reports there
// is NO model anywhere in this pipeline: casita-check already returns the
// parcel, the computed size, the `basis` sentence explaining how that size was
// reached, the flags and the source — so every word below is either a label
// from CASITA_LABELS or a value out of that response.
//
// NO ARITHMETIC. Not one number here is derived, rounded or recomputed. The
// function rounds down and says why in `basis`; a second opinion formed in the
// browser would be a second answer to the same question, and this document
// goes to a client.
function CasitaReportPDF({ data, profile, email, language = 'en', teammate = null }) {
  const lang = language === 'es' ? 'es' : 'en'
  const L = CASITA_LABELS[lang]
  const parcel = data?.parcel || null
  const rule = data?.result || null

  // Only `allowed` and `state_minimum` carry a rule, and the button that makes
  // this document renders on nothing else — so this is a guard, not a state an
  // agent can reach.
  if (!parcel || !rule) {
    return (
      <Document>
        <Page size="LETTER" style={pdfStyles.page}>
          <Text>{L.noData}</Text>
        </Page>
      </Document>
    )
  }

  const isMinimum = data.status === 'state_minimum'
  // Spanish when casita-check supplied it, English otherwise. The fallback is
  // not decoration: a deployed function older than the flags_es/basis_es
  // change returns neither field, and a Spanish report with a blank basis and
  // no flags would be worse than an English one. The ON-SCREEN page stays
  // English regardless — only this document is bilingual.
  const basisText = (lang === 'es' && typeof rule.basis_es === 'string' && rule.basis_es.trim())
    ? rule.basis_es
    : rule.basis
  const flagSource = (lang === 'es' && Array.isArray(rule.flags_es)) ? rule.flags_es : rule.flags
  const flags = Array.isArray(flagSource) ? flagSource.filter(Boolean) : []
  // The one known value that is mapped rather than printed — see the note on
  // unitsStandard in CASITA_LABELS. Anything else prints exactly as it arrived.
  const unitsText = rule.units === '1 attached + 1 detached' ? L.unitsStandard : pdfSafe(rule.units)
  const sqft = (n) => (n != null ? `${casitaNum(n)} ${L.sqftUnit}` : '—')
  // Same precedence as the screen. The APZ names are the map service's own
  // strings and print as they arrived — they are the zone's legal name, not
  // prose to translate.
  const luke = data?.luke || null
  const lukeValue = luke
    ? (luke.apz || (luke.territory ? L.lukeInsideTerritory : (luke.checked ? L.lukeOutside : L.lukeNotChecked)))
    : L.lukeNotChecked

  // WHO SAYS SO, the same line the navy card carries on screen. Both halves
  // are translated through CASITA_AUTHORITY_KEY / the label table, and both
  // fall back to the English the function sent. The fallbacks down to
  // parcel.jurisdiction cover a row saved before those fields existed.
  const authorityKey = CASITA_AUTHORITY_KEY[String(rule.authority_label || '').trim().toUpperCase()]
  const authorityText = authorityKey ? L[authorityKey] : pdfSafe(rule.authority_label, '')
  const jurisdictionRaw = rule.jurisdiction_label || parcel.jurisdiction || ''
  const jurisdictionText =
    String(jurisdictionRaw).trim().toUpperCase() === CASITA_LABELS.en.jurisdictionUnincorporated
      ? L.jurisdictionUnincorporated
      : pdfSafe(jurisdictionRaw, '')
  const authorityLine = [jurisdictionText, authorityText].filter(Boolean).join(' · ')

  return (
    <Document>
      <Page size="LETTER" style={pdfStyles.page}>
        {/* marketDateLabel rather than pdfFormatDate: it is the only date
            helper in this file that speaks Spanish, and a Spanish report with
            an English date is the kind of seam a client notices. */}
        <PdfHeader
          brokerage={profile?.brokerage}
          variantTag={L.variantTag}
          subline={<>{pdfSafe(parcel.county_address, 'Property')} · {marketDateLabel(new Date().toISOString(), lang)}</>}
        />

        <PdfSection title={L.theAnswer}>
          <View wrap={false} style={[pdfStyles.callout, pdfStyles.calloutBlue]}>
            {/* INSIDE the callout and above the number, which is where the
                screen's navy card puts it. Gray rather than the screen's gold:
                gold on this pale blue is unreadable in print. Letter-spaced
                caps at 8pt matches variantTag, the document's other eyebrow. */}
            {authorityLine !== '' && (
              <Text style={{ fontSize: 8, letterSpacing: 1, color: PDF_COLORS.grayMid, marginBottom: 5 }}>
                {authorityLine}
              </Text>
            )}
            {/* The unit rides INSIDE the number's Text as a nested Text, so it
                sits on the same baseline without a second element — the same
                thing the comp status tag does in the strategy report. */}
            <Text style={{ fontSize: 22, fontFamily: 'Helvetica-Bold', color: PDF_COLORS.navy, marginBottom: 4 }}>
              {casitaNum(rule.max_sqft)}
              <Text style={{ fontSize: 11, fontFamily: 'Helvetica', color: PDF_COLORS.grayText }}>  {isMinimum ? L.sqftAtLeast : L.sqftMax}</Text>
            </Text>
            <Text style={{ fontSize: 10, marginBottom: 4 }}>{L.oneEach}</Text>
            {basisText && (
              <Text style={{ fontSize: 10, lineHeight: 1.5 }}>{pdfSafe(basisText)}</Text>
            )}
          </View>
          {rule.max_sqft_with_permit != null && (
            <Text style={{ fontSize: 10, marginBottom: 4 }}>{L.withPermit.replace('{n}', casitaNum(rule.max_sqft_with_permit))}</Text>
          )}
          {rule.second_casita_sqft != null && (
            <Text style={{ fontSize: 10, marginBottom: 4 }}>{L.secondCasita.replace('{n}', casitaNum(rule.second_casita_sqft))}</Text>
          )}
        </PdfSection>

        <PdfSection title={L.whatAllows}>
          <PdfTable
            keepTogether
            headers={[{ label: L.detail, flex: 1 }, { label: '', flex: 2 }]}
            rows={[
              [L.casitasAllowed, unitsText],
              [L.maximumSize, `${isMinimum ? L.atLeastPrefix : ''}${sqft(rule.max_sqft)}`],
              [L.height, L.heightValue],
              [L.setback, L.setbackValue],
              [L.extraParking, L.extraParkingValue],
            ]}
          />
          {/* OUT OF THE TABLE. A municipal-code URL runs to 150 characters and
              in a flex:2 cell it wrapped to four lines, stretching one striped
              row to four times the height of its neighbours and pushing the
              table over a page break. On its own full-width line it wraps
              once, and the table above keeps even rows.
              The URL still prints in full rather than as a link: a client
              reading this on paper needs something they can type. */}
          {(rule.source_url || rule.verified_on) && (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginTop: 6 }} wrap={false}>
              {rule.source_url && (
                <Text style={{ flex: 1, fontSize: 8, color: PDF_COLORS.grayMid, lineHeight: 1.4 }}>
                  {L.ruleSource}: {pdfSafe(rule.source_url)}
                </Text>
              )}
              {rule.verified_on && (
                <Text style={{ fontSize: 8, color: PDF_COLORS.grayMid, marginLeft: 10 }}>
                  {L.ruleVerified} {pdfSafe(rule.verified_on)}
                </Text>
              )}
            </View>
          )}
        </PdfSection>

        {flags.length > 0 && (
          <PdfSection title={L.checkBefore}>
            {flags.map((f, i) => (
              <Text key={i} style={{ fontSize: 10, marginBottom: 5, lineHeight: 1.5 }}>• {pdfSafe(f)}</Text>
            ))}
          </PdfSection>
        )}

        <PdfSection title={L.fromCounty}>
          {/* keepTogether: this is the table that broke, with the lot size
              alone at the foot of page 1 and the other six rows overleaf.
              Seven fixed rows — it can always fit on a fresh page. */}
          <PdfTable
            keepTogether
            headers={[{ label: L.detail, flex: 1 }, { label: '', flex: 2 }]}
            rows={[
              [L.lotSize, sqft(parcel.lot_sqft)],
              [L.livingArea, sqft(parcel.living_sqft)],
              [L.yearBuilt, parcel.year_built != null ? String(parcel.year_built) : '—'],
              // The county's own wording for unincorporated land reads as a
              // missing value on a client document, so it is named.
              [L.city, parcel.jurisdiction === 'NO CITY/TOWN' ? L.unincorporated : pdfSafe(parcel.jurisdiction)],
              [L.subdivision, pdfSafe(parcel.subdivision)],
              [L.parcel, pdfSafe(parcel.apn)],
              [L.lukeRow, lukeValue],
            ]}
          />
        </PdfSection>

        <PdfFooter profile={profile} email={email} teammate={teammate} disclaimerText={casitaDisclaimer(profile?.brokerage, lang)} />
      </Page>
    </Document>
  )
}

// ─── STRATEGY: CASE VIEW ────────────────────────────────────────────────────
// Strategy's transcript carries comp tables and bucket grids that are cramped at
// the shell's default reading width.
const STRATEGY_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

// Status is carried by which bucket a comp sits in, not by a field on the comp —
// comp.status is StandardStatus while the query filters on MlsStatus, and the two
// enums differ. The bucket key is the reliable source.
const COMP_STATUS_CHIP = {
  closed:      { label: 'Closed',      bg: '#FCEBEB', fg: '#791F1F' },
  active:      { label: 'Active',      bg: '#EAF3DE', fg: '#27500A' },
  pending:     { label: 'Pending',     bg: '#FAEEDA', fg: '#633806' },
  ucb:         { label: 'UCB',         bg: '#FAEEDA', fg: '#633806' },
  ccbs:        { label: 'CCBS',        bg: '#FAEEDA', fg: '#633806' },
  coming_soon: { label: 'Coming soon', bg: '#FAEEDA', fg: '#633806' },
}

// Same house outline as the Single Family context card, so the placeholder reads
// as part of the same icon set.
function CompPhotoFallback() {
  return (
    <div style={{width:'100%',height:'130px',background:'#f4f4f2',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#c4c4c0" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/>
      </svg>
    </div>
  )
}

// The three derived values the card and the detail modal both show. Hoisted out
// of CompCard so the modal reads the same numbers rather than a second
// implementation of the same arithmetic — a card saying 0.42 mi beside a modal
// saying 0.4 mi is the kind of drift that costs trust in the whole panel.
function compPrice(comp, bucket) {
  return bucket === 'closed' ? comp.close_price : comp.list_price
}
function compPpsf(price, livingArea) {
  return (price && livingArea) ? Math.round(price / livingArea) : null
}
// Haversine, miles, 2dp — moved verbatim from CompCard.
function compDistanceMiles(comp, subj) {
  if (!(typeof comp.latitude === 'number' && typeof comp.longitude === 'number'
     && typeof subj?.latitude === 'number' && typeof subj?.longitude === 'number')) return null
  const R = 3958.8, tr = d => d * Math.PI / 180
  const dLat = tr(comp.latitude - subj.latitude), dLng = tr(comp.longitude - subj.longitude)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(tr(subj.latitude)) * Math.cos(tr(comp.latitude)) * Math.sin(dLng / 2) ** 2
  return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2)
}
function compDropLabel(comp, price) {
  return (comp.original_list_price && price && comp.original_list_price > price)
    ? `was $${comp.original_list_price.toLocaleString()}` : null
}

// A component rather than inline JSX so each card can own the failed-image state
// a dead MediaURL needs. Every derived value below is unchanged from the
// pre-photo card: bucket-switched price, recency, haversine distance, and the
// original-list-price drop.
// `pick` is the appraiser-report checkbox for this card, or null when it does
// not render (appraiser mode off, not an admin, mobile, or a coming-soon
// listing): { checked, disabled, onChange }, built by StrategyCaseView's
// appraiserSelection.
function CompCard({ comp, bucket, subject, onOpen, pick = null }) {
  const [imgFailed, setImgFailed] = useState(false)
  const subj = subject || {}

  const dist = compDistanceMiles(comp, subj)
  const price = compPrice(comp, bucket)
  const ppsf = compPpsf(price, comp.living_area)
  const recency = bucket==='closed'
    ? (comp.close_date ? `closed ${comp.close_date}` : '')
    : (comp.days_on_market!=null ? `${comp.days_on_market} days on market` : '')
  const drop = compDropLabel(comp, price)

  const chip = COMP_STATUS_CHIP[bucket] || COMP_STATUS_CHIP.pending
  const dom = comp.days_on_market
  const lot = Number(comp.lot_size_sqft)
  const showLot = Number.isFinite(lot) && lot > 0

  const chipBase = { position:'absolute', top:'8px', fontSize:'10px', padding:'3px 8px', borderRadius:'12px', fontFamily:FONT_BODY, fontWeight:600, whiteSpace:'nowrap' }
  const muted = { fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }

  const footerBits = [
    ppsf ? `$${ppsf}/sqft` : null,
    recency || null,
    dist ? `${dist} mi` : null,
    drop || null,
  ].filter(Boolean).join(' · ')

  // The whole card is the open target. role/tabIndex/onKeyDown rather than a
  // <button>, because a button cannot legally contain the card's own <img> and
  // heading structure — this is the same treatment ContentCard uses.
  return (
    <div
      onClick={onOpen ? () => onOpen(comp, bucket) : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(comp, bucket) } }) : undefined}
      // A comp picked for the appraiser packet trades the hairline for a 1px
      // blue border, so the picks read at a glance in the panel and the grid.
      style={{background:'white',border:pick?.checked ? '1px solid #147efb' : '0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden',cursor:onOpen?'pointer':undefined}}>
      <div style={{position:'relative'}}>
        {comp.photo_url && !imgFailed ? (
          <img
            src={comp.photo_url}
            alt={comp.address || 'Comparable property'}
            onError={()=>setImgFailed(true)}
            style={{width:'100%',height:'130px',objectFit:'cover',display:'block'}}
          />
        ) : (
          <CompPhotoFallback />
        )}
        <div style={{...chipBase,left:'8px',background:chip.bg,color:chip.fg}}>{chip.label}</div>
        {dom != null && (
          <div style={{...chipBase,right:'8px',background:'#2C2C2A',color:'#F1EFE8'}}>{dom} DOM</div>
        )}
      </div>

      <div style={{padding:'10px 12px 12px'}}>
        <div style={{fontSize:'13px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {comp.address || 'Unknown address'}
        </div>
        <div style={{...muted,marginTop:'3px'}}>
          {comp.living_area} sqft · {comp.beds}bd/{comp.baths_total}ba · {comp.stories_total} story · {comp.year_built}{comp.pool ? ' · pool' : ''}
        </div>
        {showLot && (
          <div style={muted}>{lot.toLocaleString()} sf lot</div>
        )}
        <div style={{fontSize:'17px',fontWeight:700,color:'#147efb',fontFamily:FONT_HEAD,marginTop:'6px'}}>
          {price ? `$${price.toLocaleString()}` : '—'}
        </div>
        {footerBits && (
          <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,marginTop:'3px'}}>{footerBits}</div>
        )}
        {/* APPRAISER REPORT. Only in appraiser mode. Below the details, behind
            the card's own hairline, never over the photo or its chips. Click AND
            keydown stop here: the whole card is an open target, and a checkbox
            click or a Space on a focused box would otherwise bubble up and open
            the detail modal. Blue while it can be used, #999 at a limit. */}
        {pick && (
          <label
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            title={pick.disabled ? 'Limit reached' : undefined}
            style={{display:'flex',alignItems:'center',gap:'6px',marginTop:'8px',paddingTop:'8px',borderTop:'0.5px solid #e8e8e8',fontSize:'11px',color:pick.disabled ? '#999' : '#147efb',fontFamily:FONT_BODY,cursor:pick.disabled?'not-allowed':'pointer'}}>
            <input type="checkbox" checked={pick.checked} disabled={pick.disabled} onChange={pick.onChange} style={{accentColor:'#147efb',margin:0}} />
            Include in appraiser report
          </label>
        )}
      </div>
    </div>
  )
}

// ─── COMP DETAIL MODAL ───────────────────────────────────────────────────────
// PUBLIC DATA ONLY. This component must never reference private_remarks,
// showing instructions, lockbox, owner or occupant identity, or agent contact
// details. normalizeRecord does carry private_remarks — it is in the payload
// this reads from — so the boundary is enforced here by simply never naming it.
// Grep this component for `private_remarks`, `list_agent`, `list_office` before
// changing anything: none of them belong in an agent-facing comp panel.
//
// Reachable only from a comp card, and comp cards exist only while compResult
// is in memory. There is deliberately no route and no URL param: comp data is
// not retained past the session per ARMLS licensing (see the retention note in
// armls-comp-pull), so a deep link would be a promise this app cannot keep.
const COMP_CHIP_TEAL    = { bg:'#DDEFED', fg:'#0B3F3A' }
const COMP_CHIP_BLUE    = { bg:'#E4EDF7', fg:'#123763' }
const COMP_CHIP_NEUTRAL = { bg:'#F4F4F2', fg:'#666666' }

function compDetailChip(tone, text, key) {
  return (
    <span key={key} style={{background:tone.bg,color:tone.fg,fontSize:'10px',fontWeight:500,padding:'3px 8px',borderRadius:'12px',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>{text}</span>
  )
}

// ARMLS returns the fee as it was entered — 70, or 51.5. It is printed exactly
// as given and the frequency verbatim, because a quarterly $90 and a monthly
// $70 are not comparable without knowing which is which, and normalising them
// would be us inventing a number the MLS never published.
function compHoaLabel(fee, frequency) {
  if (typeof fee !== 'number') return null
  const amount = `$${fee.toFixed(2)}`
  const freq = typeof frequency === 'string' && frequency.trim() ? `/${frequency.trim().toLowerCase()}` : ''
  return `HOA ${amount}${freq}`
}

function CompDetailModal({ comp, subject, bucket, isMobile, onClose, pick = null }) {
  const photos = Array.isArray(comp.photos) ? comp.photos : []
  // Open on the listing's own preferred photo when it has one — that is the
  // shot the agent chose to lead with.
  const initialIndex = Math.max(0, photos.findIndex(p => p?.preferred))
  const [activePhoto, setActivePhoto] = useState(initialIndex)
  const [remarksOpen, setRemarksOpen] = useState(false)
  // Per-index, not a single flag. One dead MediaURL in a set of 87 must take
  // out its own frame and nothing else — a boolean here meant the first broken
  // photo replaced the entire carousel with the fallback and hid the other 86.
  // Lazy initialiser: `useState(new Set())` would allocate a throwaway Set on
  // every render.
  const [failedPhotos, setFailedPhotos] = useState(() => new Set())

  const len = photos.length
  const clamped = len > 0 ? Math.min(activePhoto, len - 1) : 0
  const heroBroken = len === 0 || failedPhotos.has(clamped)

  // Keeps the active thumbnail on screen as the arrows walk past the visible
  // slice. Not behavior:'smooth' — a held arrow key would queue one animation
  // per keypress and the strip would keep gliding after the key came up.
  const stripRef = useRef(null)
  useEffect(() => {
    const el = stripRef.current?.children?.[clamped]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [clamped])

  // Body scroll lock, lifted from FeaturedListings. Restores the previous value
  // rather than clearing it, so it cannot clobber another component's lock.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  // Arrows move the carousel, Escape closes. One listener, removed on unmount.
  // Escape is new to this codebase — no other overlay in App.js handles it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
      if (len <= 1) return
      if (e.key === 'ArrowRight') { e.preventDefault(); setActivePhoto(i => (i + 1) % len) }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setActivePhoto(i => (i - 1 + len) % len) }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [len, onClose])

  const nextPhoto = () => { if (len > 1) setActivePhoto(i => (i + 1) % len) }
  const prevPhoto = () => { if (len > 1) setActivePhoto(i => (i - 1 + len) % len) }

  const chip = COMP_STATUS_CHIP[bucket] || COMP_STATUS_CHIP.pending
  const price = compPrice(comp, bucket)
  const ppsf = compPpsf(price, comp.living_area)
  const dist = compDistanceMiles(comp, subject)

  const eyebrow = { fontSize:'10px',fontWeight:500,letterSpacing:'1.5px',textTransform:'uppercase',color:'#999',fontFamily:FONT_HEAD }
  const label   = { ...eyebrow, marginBottom:'3px' }
  const value   = { fontSize:'13px',fontWeight:400,color:'#1d2e42',fontFamily:FONT_BODY }
  const hair    = '0.5px solid #e8e8e8'

  // ── 2. price sub-line ──
  const priceBits = [
    ppsf ? `$${ppsf}/sqft` : null,
    comp.days_on_market != null ? `${comp.days_on_market} DOM` : null,
    dist ? `${dist} mi from subject` : null,
  ].filter(Boolean).join(' · ')

  // ── 3. price history ──
  // Omitted entirely when the list never moved and nothing closed, which is the
  // common case on a fresh active listing — an unchanged history is not history.
  const olp = comp.original_list_price, lp = comp.list_price, cp = comp.close_price
  const showHistory = typeof olp === 'number' && (olp !== lp || typeof cp === 'number')
  const pct = (from, to) => (typeof from === 'number' && typeof to === 'number' && from > 0)
    ? `${to < from ? '−' : '+'}${Math.abs(((to - from) / from) * 100).toFixed(1)}%` : null
  // Red carries meaning: the price came DOWN. A listing that went UP, or held,
  // is not a warning and must not borrow the danger colour — it reads as muted
  // secondary text like the rest of the line.
  const pctColor = (from, to) => (typeof from === 'number' && typeof to === 'number' && to < from) ? '#C8102E' : '#666'

  // ── 4. facts ──
  const facts = [
    ['Beds', comp.beds], ['Baths', comp.baths_total],
    ['Living area', comp.living_area ? `${comp.living_area.toLocaleString()} sf` : null],
    ['Lot', Number(comp.lot_size_sqft) > 0 ? `${Number(comp.lot_size_sqft).toLocaleString()} sf` : null],
    ['Year built', comp.year_built], ['Stories', comp.stories_total],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '')

  // ── 5/6. chips ──
  const hoaLabel = comp.hoa_yn === true ? compHoaLabel(comp.hoa_fee, comp.hoa_frequency) : null
  const chips = []
  if (comp.pool === true) chips.push(compDetailChip(COMP_CHIP_TEAL, 'Private pool', 'pool'))
  if (hoaLabel) chips.push(compDetailChip(COMP_CHIP_BLUE, hoaLabel, 'hoafee'))
  if (comp.hoa_yn === true && comp.hoa_name) chips.push(compDetailChip(COMP_CHIP_NEUTRAL, comp.hoa_name, 'hoaname'))
  if (typeof comp.garage_spaces === 'number' && comp.garage_spaces > 0) chips.push(compDetailChip(COMP_CHIP_NEUTRAL, `${comp.garage_spaces} car garage`, 'garage'))
  if (comp.subdivision) chips.push(compDetailChip(COMP_CHIP_NEUTRAL, comp.subdivision, 'subdiv'))

  // ── 8. features ──
  const features = [
    ['Flooring', comp.flooring], ['Cooling', comp.cooling], ['Heating', comp.heating],
    ['Roof', comp.roof], ['Construction', comp.construction], ['Parking', comp.parking_features],
  ].filter(([, arr]) => Array.isArray(arr) && arr.length > 0)

  // ── 9. financing ── sold loan type only makes sense once something closed.
  const terms = Array.isArray(comp.listing_terms) ? comp.listing_terms : []
  const soldWith = (bucket === 'closed' && Array.isArray(comp.buyer_financing)) ? comp.buyer_financing : []

  // ── 10. schools ──
  const schoolNames = [comp.school_elementary, comp.school_middle, comp.school_high].filter(Boolean)
  const showSchools = !!comp.school_district || schoolNames.length > 0

  const remarks = typeof comp.public_remarks === 'string' ? comp.public_remarks.trim() : ''

  return (
    <div
      style={isMobile ? {...S.overlay, alignItems:'flex-start'} : S.overlay}
      onClick={e => e.target === e.currentTarget && onClose()}>
      {/* Same mobile treatment the report preview arrived at, for the same
          reason: a centred card leaves too little width for the content. The
          .chromeless-vh class carries the 100vh→100dvh fallback pair a JS style
          object cannot express, and boxSizing:'border-box' is what stops a
          100dvh panel plus padding rendering taller than the viewport and
          pushing its own header off the top. */}
      <div
        onClick={e => e.stopPropagation()}
        className={isMobile ? 'chromeless-vh' : undefined}
        style={isMobile
          ? {...S.detailModal, width:'100vw', maxWidth:'100vw', maxHeight:'100%',
             boxSizing:'border-box', borderRadius:0}
          : {...S.detailModal, width:'min(720px, 95vw)', maxWidth:'95vw', maxHeight:'90vh'}}>

        {/* ── PHOTO AREA ── */}
        <div style={{position:'relative',flexShrink:0,background:'#f4f4f2'}}>
          <div style={{width:'100%',height:'260px',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center'}}>
            {!heroBroken ? (
              // Only the active photo and its immediate neighbours are real
              // <img> elements. A comp can carry 87 photos and mounting all of
              // them would fire 87 CDN requests the moment the panel opens.
              photos.map((p, i) => (
                Math.abs(i - clamped) <= 1 ? (
                  <img
                    key={p.url}
                    src={p.url}
                    alt={i === clamped ? (comp.address || 'Comparable property') : ''}
                    onError={() => setFailedPhotos(prev => { const next = new Set(prev); next.add(i); return next })}
                    style={{width:'100%',height:'260px',objectFit:'cover',display:i===clamped?'block':'none'}}
                  />
                ) : null
              ))
            ) : (
              <CompPhotoFallback />
            )}
          </div>

          {len > 1 && (
            <>
              <button onClick={prevPhoto} aria-label="Previous photo"
                style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',width:'32px',height:'32px',borderRadius:'50%',background:'rgba(0,0,0,0.45)',border:'none',color:'#fff',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>‹</button>
              <button onClick={nextPhoto} aria-label="Next photo"
                style={{position:'absolute',right:'10px',top:'50%',transform:'translateY(-50%)',width:'32px',height:'32px',borderRadius:'50%',background:'rgba(0,0,0,0.45)',border:'none',color:'#fff',fontSize:'16px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>›</button>
            </>
          )}

          <div style={{position:'absolute',top:'10px',right:'10px',display:'flex',alignItems:'center',gap:'8px'}}>
            {len > 0 && (
              <span style={{background:'rgba(0,0,0,0.45)',color:'#fff',fontSize:'11px',fontWeight:400,padding:'4px 9px',borderRadius:'12px',fontFamily:FONT_BODY}}>
                {clamped + 1} of {len}
              </span>
            )}
            <button style={S.closeBtn} onClick={onClose} aria-label="Close">×</button>
          </div>
        </div>

        {/* ── THUMBNAIL STRIP ──
            Every photo gets a thumbnail. loading="lazy" means the offscreen
            ones cost nothing until they are scrolled to, so a cap bought no
            performance and only hid photos the arrows could still reach. */}
        {len > 1 && (
          <div ref={stripRef} style={{flexShrink:0,display:'flex',gap:'6px',padding:'8px 10px',overflowX:'auto',borderBottom:hair}}>
            {photos.map((p, i) => (
              <button key={p.url} onClick={() => setActivePhoto(i)} aria-label={`Photo ${i + 1}`}
                style={{flexShrink:0,width:'52px',height:'38px',padding:0,borderRadius:'4px',overflow:'hidden',cursor:'pointer',background:'#f4f4f2',
                        border:i===clamped?'1.5px solid #147efb':'0.5px solid #e8e8e8'}}>
                {/* A dead URL leaves a plain neutral tile rather than the
                    browser's broken-image glyph, which reads as breakage. */}
                {failedPhotos.has(i) ? null : (
                  <img src={p.url} alt="" loading="lazy"
                    onError={() => setFailedPhotos(prev => { const next = new Set(prev); next.add(i); return next })}
                    style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                )}
              </button>
            ))}
          </div>
        )}

        <div style={{...S.detailBody, padding:isMobile?'16px':'20px 22px 22px'}}>

          {/* ── 1. HEADER ── */}
          <div style={{display:'flex',alignItems:'flex-start',gap:'12px',justifyContent:'space-between'}}>
            <div style={{minWidth:0}}>
              <div style={{fontSize:'15px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>{comp.address || 'Unknown address'}</div>
              {/* normalizeRecord carries city and postal_code but no state
                  field, so the locality line is City ZIP rather than
                  City, ST ZIP. The full address above already contains the
                  state — inventing a `state` key here would be worse. */}
              <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,marginTop:'2px'}}>
                {[[comp.city, comp.postal_code].filter(Boolean).join(' '), comp.listing_id ? `MLS ${comp.listing_id}` : null].filter(Boolean).join(' · ')}
              </div>
              {/* Same checkbox as the card, same limits — both read one pick. */}
              {pick && (
                <label
                  title={pick.disabled ? 'Limit reached' : undefined}
                  style={{display:'flex',alignItems:'center',gap:'6px',marginTop:'8px',fontSize:'12px',color:pick.disabled ? '#999' : '#147efb',fontFamily:FONT_BODY,cursor:pick.disabled?'not-allowed':'pointer'}}>
                  <input type="checkbox" checked={pick.checked} disabled={pick.disabled} onChange={pick.onChange} style={{accentColor:'#147efb',margin:0}} />
                  Include in appraiser report
                </label>
              )}
            </div>
            <span style={{flexShrink:0,background:chip.bg,color:chip.fg,fontSize:'10px',fontWeight:500,padding:'3px 8px',borderRadius:'12px',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>{chip.label}</span>
          </div>

          {/* ── 2. PRICE ── */}
          {price && (
            <div style={{display:'flex',alignItems:'baseline',gap:'10px',flexWrap:'wrap',marginTop:'14px'}}>
              <span style={{fontSize:'22px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>${price.toLocaleString()}</span>
              {priceBits && <span style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>{priceBits}</span>}
            </div>
          )}

          {/* ── 3. PRICE HISTORY ── */}
          {showHistory && (
            <div style={{background:'#f4f4f2',borderRadius:'8px',padding:'11px 13px',marginTop:'14px'}}>
              <div style={label}>Price history</div>
              <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.6}}>
                Original list ${olp.toLocaleString()}
                {typeof lp === 'number' && lp !== olp && (
                  <> {'→'} list ${lp.toLocaleString()} <span style={{color:pctColor(olp, lp)}}>{pct(olp, lp)}</span></>
                )}
                {typeof cp === 'number' && (
                  <> {'→'} sold ${cp.toLocaleString()} <span style={{color:pctColor(typeof lp === 'number' ? lp : olp, cp)}}>{pct(typeof lp === 'number' ? lp : olp, cp)}</span></>
                )}
              </div>
            </div>
          )}

          {/* ── 4. FACTS ── */}
          {facts.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(90px, 1fr))',gap:'12px',marginTop:'16px'}}>
              {facts.map(([k, v]) => (
                <div key={k}>
                  <div style={label}>{k}</div>
                  <div style={value}>{v}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── 5/6. CHIPS ── */}
          {chips.length > 0 && (
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginTop:'16px'}}>{chips}</div>
          )}

          {/* ── 7. PUBLIC REMARKS ── public only, never private_remarks. */}
          {remarks && (
            <div style={{marginTop:'18px',paddingTop:'14px',borderTop:hair}}>
              <div style={label}>Public remarks</div>
              <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.7,
                           ...(remarksOpen ? {} : {display:'-webkit-box',WebkitLineClamp:4,WebkitBoxOrient:'vertical',overflow:'hidden'})}}>
                {remarks}
              </div>
              {remarks.length > 220 && (
                <button onClick={() => setRemarksOpen(o => !o)}
                  style={{background:'none',border:'none',padding:'6px 0 0',fontSize:'12px',fontWeight:500,color:'#147efb',fontFamily:FONT_BODY,cursor:'pointer'}}>
                  {remarksOpen ? 'Read less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          {/* ── 8/9/10. FEATURES · FINANCING · SCHOOLS ── */}
          {(features.length > 0 || terms.length > 0 || soldWith.length > 0 || showSchools) && (
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'20px',marginTop:'18px',paddingTop:'14px',borderTop:hair}}>
              <div>
                {features.length > 0 && (
                  <>
                    <div style={label}>Features</div>
                    {features.map(([k, arr]) => (
                      <div key={k} style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.7}}>
                        <span style={{color:'#1d2e42'}}>{k}:</span> {arr.join(' · ')}
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div>
                {(terms.length > 0 || soldWith.length > 0) && (
                  <div style={{marginBottom:showSchools?'14px':0}}>
                    <div style={label}>Financing accepted</div>
                    {terms.length > 0 && (
                      <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.7}}>{terms.join(' · ')}</div>
                    )}
                    {soldWith.length > 0 && (
                      <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.7}}>
                        <span style={{color:'#1d2e42'}}>Sold with:</span> {soldWith.join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                {showSchools && (
                  <div>
                    <div style={label}>Schools</div>
                    {comp.school_district && (
                      <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.7}}>{comp.school_district}</div>
                    )}
                    {schoolNames.length > 0 && (
                      <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,lineHeight:1.7}}>{schoolNames.join(' · ')}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── 11. LOCK BANNER ── states the boundary this component enforces. */}
          <div style={{display:'flex',alignItems:'flex-start',gap:'9px',background:'#f4f4f2',borderRadius:'8px',padding:'11px 13px',marginTop:'18px'}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d2e42" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0,marginTop:'1px'}}>
              <rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>
            </svg>
            <div style={{fontSize:'11.5px',color:'#666',fontFamily:FONT_BODY,lineHeight:1.5}}>
              Public data only — private remarks, showing instructions, lockbox, and owner information are never shown.
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── COMP BUCKETS ────────────────────────────────────────────────────────────
// The bucket sections, lifted out of compsPanelNode so the panel and the
// full-width grid render the same thing at different densities rather than two
// copies that drift. The only differences between the two are cap and
// minTrack; everything else is identical by construction.
//
// Order and labels are module constants now. The labels map was rebuilt inside
// the map callback, so it was reallocated once per bucket per render.
// COMP_BUCKET_ORDER is imported from src/pullConsistency.js.
const COMP_BUCKET_LABELS = {
  closed: 'CLOSED', pending: 'PENDING', active: 'ACTIVE',
  ucb: 'UCB', ccbs: 'CCBS', coming_soon: 'COMING SOON',
}

// ─── APPRAISER REPORT: comp selection limits ─────────────────────────────────
// The agent, not the analysis, picks which comps go to the appraiser. Closed
// sales are the evidence (up to six); pending, under-contract and active
// listings are supporting context (up to three across all four buckets).
// Coming-soon listings never qualify and get no checkbox.
const APPRAISER_CLOSED_MAX = 6
const APPRAISER_OTHER_MAX = 3
const APPRAISER_OTHER_BUCKETS = ['pending', 'ucb', 'ccbs', 'active']
// Fewest closed sales before the report can be started.
const APPRAISER_CLOSED_MIN = 3
// 'closed' | 'other' | null — which limit a bucket counts against.
function appraiserGroupOf(bucket) {
  if (bucket === 'closed') return 'closed'
  return APPRAISER_OTHER_BUCKETS.includes(bucket) ? 'other' : null
}
// Stored value → label. The values match the CHECK on
// property_cases.contract_loan_type (supabase/schema/2026-10-03_appraiser_report.sql).
const APPRAISER_LOAN_TYPES = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'fha', label: 'FHA' },
  { value: 'va', label: 'VA' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
]

// cap: how many cards show before "+N more". null means show everything and
// render no control at all — the grid exists precisely to remove that limit,
// and a "Show less" in there would collapse a section the agent opened the
// grid to read.
// `selection` is StrategyCaseView's appraiserSelection, or null. Threaded here
// once so the panel and the "See all" grid both get the checkbox.
function CompBuckets({ groups, subject, isMobile, expandedGroups, setExpandedGroups, detailComp, setDetailComp, cap, minTrack, selection = null }) {
  return (
    <>
      {subject && (
        <div style={{fontSize:'13px',color:'#666',fontFamily:FONT_BODY,margin:'10px 0 18px'}}>
          Subject: {subject.address} · {subject.living_area} sqft · {subject.beds}bd/{subject.baths_total}ba · {subject.stories_total} story · built {subject.year_built}
        </div>
      )}
      {COMP_BUCKET_ORDER.map(bucket => {
        const list = groups?.[bucket] || []
        if (list.length === 0) return null
        const isExpanded = !!expandedGroups[bucket]
        const capped = typeof cap === 'number'
        const visible = (!capped || isExpanded) ? list : list.slice(0, cap)
        return (
          <div key={bucket} style={{marginBottom:'24px'}}>
            <div style={{...S.label,marginBottom:'10px'}}>{COMP_BUCKET_LABELS[bucket]} · {list.length}</div>
            <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill, minmax(${minTrack}, 1fr))`,gap:'12px'}}>
              {visible.map(comp => (
                <CompCard key={comp.listing_id} comp={comp} bucket={bucket} subject={subject}
                  onOpen={(c, b) => setDetailComp({ comp: c, bucket: b })}
                  pick={selection ? selection.pickFor(comp, bucket) : null} />
              ))}
            </div>
            {capped && list.length > cap && (
              <button
                onClick={() => setExpandedGroups(prev => ({...prev, [bucket]: !isExpanded}))}
                style={{fontSize:'12px',fontFamily:FONT_BODY,color:'#147efb',background:'none',border:'none',cursor:'pointer',padding:'8px 0',display:'block',margin:'0 auto'}}
              >
                {isExpanded ? 'Show less' : `+ ${list.length - cap} more`}
              </button>
            )}
          </div>
        )
      })}

      {/* Sibling of the comp grid. Overlay is position:fixed, so its
          place in the tree is immaterial — it sits here to stay next
          to the cards that open it. */}
      {detailComp && (
        <CompDetailModal
          comp={detailComp.comp}
          bucket={detailComp.bucket}
          subject={subject}
          isMobile={isMobile}
          onClose={() => setDetailComp(null)}
          pick={selection ? selection.pickFor(detailComp.comp, detailComp.bucket) : null}
        />
      )}
    </>
  )
}

// sendMessage prepends [Property type: …], [Client type: …] and
// [Property condition: …] — newline-joined, then a blank line, then the agent's
// text — and the row stores the prefixed string because the edge function and
// system prompt read the directives from the message body. They are plumbing the
// agent never typed, so they come off at display time only. Nothing about what is
// sent or stored changes.
//
// Anchored at the start and requiring a colon inside the brackets, so a bracket
// used mid-sentence — "is [comp 3: 825 W Vineyard] an anchor?" — is left alone.
const DIRECTIVE_PREFIX = /^(?:\[[^\]\n]*:[^\]\n]*\]\s*)+/

function stripDirectives(text) {
  if (typeof text !== 'string') return text
  const stripped = text.replace(DIRECTIVE_PREFIX, '').trim()
  // A message that was nothing but directives still has to render something.
  return stripped || text
}

// The reverse of stripDirectives: pull the directives back out as a map keyed by
// their lowercased label — 'property type', 'client type', 'property condition'.
function parseDirectives(text) {
  const out = {}
  if (typeof text !== 'string') return out
  const head = text.match(DIRECTIVE_PREFIX)
  if (!head) return out
  const re = /\[([^\]\n:]+):\s*([^\]\n]+)\]/g
  let g
  while ((g = re.exec(head[0])) !== null) out[g[1].trim().toLowerCase()] = g[2].trim()
  return out
}

// Fallback for cases last touched before property_cases grew its three context
// columns. Those rows have nothing to hydrate from, so the directives stored on
// each user message are their only durable record and the summary strip reads
// the newest one back rather than showing nothing.
//
// Display only. These are never written into the state sendMessage reads — that
// state hydrates from the columns, and feeding it parsed history instead would
// start prepending directives to old cases that have never sent any.
function contextFromHistory(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m?.role !== 'user') continue
    const parsed = parseDirectives(m.content)
    if (Object.keys(parsed).length > 0) return parsed
  }
  return {}
}

// The seven subject columns, turned back into what the setup inputs hold. One
// definition, because two call sites read them — the case-load hydration and
// the rollback after a refused write — and two copies of this mapping would be
// two things to keep in step.
//
// Empty string rather than null for the five specs: they feed controlled text
// inputs, and a null value there makes React switch the field to uncontrolled.
//
// `place` is non-null ONLY when both coordinates are present. That pairing is
// what the whole feature turns on: SUBJECT DETAILS renders on selectedPlace,
// and pullComps takes the form path on selectedPlace, so a place without
// coordinates would put the block on screen and then post a subject the edge
// function rejects for want of a latitude.
function subjectFieldsFromRow(row) {
  const s = (v) => (v != null ? String(v) : '')
  return {
    query: row?.subject_query || '',
    place: (row?.subject_lat != null && row?.subject_lng != null)
      ? { address: row.subject_query || '', lat: row.subject_lat, lng: row.subject_lng }
      : null,
    sqft: s(row?.subject_sqft),
    yearBuilt: s(row?.subject_year_built),
    stories: s(row?.subject_stories),
    beds: s(row?.subject_beds),
    bathsTotal: s(row?.subject_baths_total),
  }
}

// Every spec column, nulled. The coordinates and the five specs describe ONE
// property, so they are written and cleared together — a square footage left
// behind after the address changed belongs to a building that is no longer in
// the field, and it would be re-hydrated on the next load as though it did.
const SUBJECT_SPECS_NULL = {
  subject_sqft: null, subject_year_built: null, subject_stories: null,
  subject_beds: null, subject_baths_total: null,
}

// Shown when an update matches zero rows. property_cases has exactly one UPDATE
// policy, "Agents update own cases", and it is owner-only — while SELECT also
// admits shared_with and admins. So a shared agent can open a case, read it,
// and pull comps, but cannot write its subject. An RLS refusal is not an error
// (see the note in deleteAdminCase), so without a row count this said nothing
// at all and looked like a save.
//
// NOTHING IS ROLLED BACK ON A REFUSAL, and the wording promises exactly that.
// A shared agent can still pull comps and run the analysis — armls-comp-pull
// only requires a signed-in caller — so clearing their selection would take a
// working session away to enforce a rule about what SURVIVES the session. The
// value stays in memory, the sentence says what will happen to it, and the
// agent decides. A real driver error is different and does roll back: there
// the write may have half-happened and local state cannot be trusted.
const SUBJECT_REFUSED_MSG = "Only the case owner can save the subject property. It will work for this session but won't be kept after a refresh."

// At most one refusal toast per case view. Nothing rolls back on a refusal, and
// saveSpec deliberately does not touch caseData either, so every later blur
// retries the same doomed write — a shared agent filling in five boxes would be
// told the same sentence five times. The ref is passed in rather than read from
// a closure so this can live at module scope: the Places effect calls it too,
// and a component-scope function would become a dependency of that effect and
// rebuild the Autocomplete.
function noteSubjectRefused(seenRef, toast) {
  if (seenRef.current) return
  seenRef.current = true
  toast(SUBJECT_REFUSED_MSG)
}

// The same refusal, for the appraiser report's contract terms. Its own message
// and its own once-per-view ref: a shared agent told about the subject property
// has not been told anything about contract terms, and vice versa.
const CONTRACT_REFUSED_MSG = "Only the case owner can save contract terms. They will work for this session but won't be kept after a refresh."

function noteContractRefused(seenRef, toast) {
  if (seenRef.current) return
  seenRef.current = true
  toast(CONTRACT_REFUSED_MSG)
}

function StrategyCaseView({ session, caseId, showToast, isMobile, onBack, profile, navigateTo, isAdmin, signOut }) {
  const shellRef = useRef(null)
  // Setup starts open on an untouched case and closed once there is a
  // transcript, decided once when messages first land.
  const [setupOpen, setSetupOpen] = useState(true)
  const setupInitRef = useRef(false)
  const [caseData, setCaseData] = useState(null)
  const [address, setAddress] = useState('')
  const [files, setFiles] = useState([])
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  // Whether the composer holds the Analyze prompt, put there by the button
  // rather than typed. Session-only and deliberately not persisted: it
  // describes what is in the box right now, and the box does not survive a
  // reload either. Step 3's marker and the prime hint both read it.
  const [primed, setPrimed] = useState(false)
  // The pre-PDF figure check's confirm. Non-null while it is open; the ref
  // holds the resolver generateReport is awaiting (true = Generate anyway).
  const [figureCheck, setFigureCheck] = useState(null)
  const figureCheckResolveRef = useRef(null)
  // Set when generateReport stops because no closed sale in the report matched
  // the pull. Only shows a modal; nothing awaits it.
  const [noClosedMatch, setNoClosedMatch] = useState(false)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const [propertyType, setPropertyType] = useState(null)
  const [clientType, setClientType] = useState(null)
  const [condition, setCondition] = useState(null)
  const [helpSeen, setHelpSeen] = useState(true)
  // The help panel's "Good to know" fold. Session-only, never persisted, and
  // reset whenever the panel is opened again (help link or setup toggle).
  const [goodToKnowOpen, setGoodToKnowOpen] = useState(false)
  useEffect(() => { setGoodToKnowOpen(false) }, [helpSeen, setupOpen])
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [agentsList, setAgentsList] = useState([])
  const [sharedWith, setSharedWith] = useState([])
  const [shareNote, setShareNote] = useState('')
  const [sharing, setSharing] = useState(false)
  const [selectedAgentId, setSelectedAgentId] = useState('')
  const [agentSearch, setAgentSearch] = useState('')
  const addressInputRef = useRef(null)
  const [placesReady, setPlacesReady] = useState(false)
  const googleRef = useRef(null)
  // The formatted address from the most recent Places selection, or null when
  // the field no longer holds one. A ref because the blur handler needs a value
  // that cannot be stale: clicking a suggestion blurs the input on mousedown,
  // BEFORE place_changed runs, so the handler's closure still carries the
  // half-typed fragment — observed directly, not assumed. Refs are read at call
  // time, so this survives that ordering where selectedPlace and mlsNumber
  // cannot. Cleared alongside selectedPlace on any manual edit, so it never
  // outlives the text it describes.
  const lastPlaceQueryRef = useRef(null)
  // Which context groups already held a value when this case was read. A ref,
  // not state: it is a snapshot of how the case arrived and must not move when
  // the agent then fills one in. That is the whole point — a group that was
  // empty on load stays editable for the rest of the session, so an agent
  // repairing a case that reached `locked` with no context can undo a misclick
  // instead of being trapped by it. It re-locks on the next load, when the
  // group does have a value.
  const contextAtLoadRef = useRef(null)
  const [selectedPlace, setSelectedPlace] = useState(null)
  const [specSqft, setSpecSqft] = useState('')
  const [specYearBuilt, setSpecYearBuilt] = useState('')
  const [specStories, setSpecStories] = useState('')
  const [specBeds, setSpecBeds] = useState('')
  const [specBathsTotal, setSpecBathsTotal] = useState('')
  // showToast, held where the Places effect can reach it. That effect cannot
  // list showToast as a dependency: Dashboard declares it as a plain function,
  // so it is a new identity on every parent render, and depending on it would
  // rebuild the Autocomplete each time — the same trap the note on the
  // subject_query write below describes. A ref is read at call time and needs
  // no dependency. Assigned in an effect rather than during render so the
  // render itself stays pure.
  const showToastRef = useRef(showToast)
  useEffect(() => { showToastRef.current = showToast })
  // Whether the refusal toast has already been shown on this case. A plain ref
  // is enough: Dashboard mounts this view as key={activeCaseId}, so switching
  // cases unmounts and remounts it and every ref starts over. Nothing has to
  // reset it by hand.
  const subjectRefusedRef = useRef(false)
  // Same, for the contract-terms refusal. Separate so each notice shows once.
  const contractRefusedRef = useRef(false)

  useEffect(() => {
    supabase.from('property_cases').select('*').eq('id', caseId).single()
      .then(({ data, error }) => {
        if (error || !data) { showToast('Case not found.'); onBack(); return }
        setCaseData(data); setAddress(data.subject_address || '')
        // The setup field takes an address OR an MLS number, so it persists to
        // its own column rather than subject_address, which is the case name.
        // The ref is cleared with it: this effect re-runs on caseId, and a
        // selection left over from the previous case would otherwise be written
        // onto this one by the next blur.
        //
        // SELECTED PLACE AND THE FIVE SPECS HYDRATE TOO. Until they did, an
        // address case came back with its text and nothing else: no
        // selectedPlace, so SUBJECT DETAILS did not render and pullComps fell
        // through to the MLS branch, which posted the address string as a
        // ListingId and got a 404 out of ARMLS.
        //
        // The ref is deliberately NOT hydrated. It means "a Places selection
        // happened in this session", which is false after a reload, and it
        // outranks selectedPlace in saveSubjectQuery — so seeding it here would
        // hand that fallback chain a value no user action put there. With it
        // null, a blur that changes nothing rewrites the same address it read.
        const subj = subjectFieldsFromRow(data)
        setMlsNumber(subj.query)
        lastPlaceQueryRef.current = null
        setSelectedPlace(subj.place)
        setSpecSqft(subj.sqft)
        setSpecYearBuilt(subj.yearBuilt)
        setSpecStories(subj.stories)
        setSpecBeds(subj.beds)
        setSpecBathsTotal(subj.bathsTotal)
        setSharedWith(Array.isArray(data.shared_with) ? data.shared_with : [])
        // select('*') already returns the three context columns. Null on any
        // case last touched before they existed — contextFromHistory covers
        // those for display, and sendMessage prepends nothing, as it did before.
        setPropertyType(data.property_type || null)
        setClientType(data.client_type || null)
        setCondition(data.property_condition || null)
        // Snapshot before the agent can touch anything. Guarded so a refetch
        // cannot overwrite it with values the agent just chose.
        if (!contextAtLoadRef.current) {
          contextAtLoadRef.current = {
            pt: !!data.property_type,
            ct: !!data.client_type,
            pc: !!data.property_condition,
          }
        }
      })
    supabase.from('property_case_files').select('*').eq('case_id', caseId).order('uploaded_at')
      .then(({ data }) => { if (data) setFiles(data) })
    supabase.from('property_case_messages').select('*').eq('case_id', caseId).order('created_at')
      .then(({ data, error }) => {
        if (error) showToast('Could not load messages')
        setMessages(data || [])
        if (!setupInitRef.current) { setupInitRef.current = true; setSetupOpen((data || []).length === 0) }
      })
  }, [caseId])

  useEffect(() => {
    supabase.from('profiles').select('strategy_help_seen').eq('id', session.user.id).single()
      .then(({ data }) => { if (data) setHelpSeen(!!data.strategy_help_seen) })
  }, [session.user.id])

  useEffect(() => {
    if (profile?.role !== 'admin') return
    supabase.from('profiles').select('id, full_name, email, role, brokerage').in('role', ['agent','admin']).order('full_name')
      .then(({ data, error }) => { if (!error && data) setAgentsList(data) })
  }, [profile?.role])

  // Google Places, split across two effects the way the public link form does
  // it. This was one []-deps effect that bailed on `!addressInputRef.current`
  // and never retried. CaseShell mounts the setup panel conditionally, and the
  // messages fetch closes it on any case that already has a transcript — which
  // lands while the Maps bundle is still downloading. The input was gone by the
  // time the loader resolved, so the binding was skipped and, with empty deps,
  // never attempted again. Autocomplete was dead on every case with messages.
  //
  // The link form builds a detached PlaceAutocompleteElement once and appends
  // it wherever it is needed. Legacy Autocomplete cannot work that way: it
  // takes its input node in the constructor and cannot be moved. So the load
  // and the binding split differently here — effect one loads the API and
  // touches no DOM, effect two builds an instance against whatever node is
  // mounted right now. Reopening setup renders a new node and gets a new
  // instance, which is the only correct move for this API rather than waste.
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_PLACES_KEY
    if (!key) { console.warn('[Places] REACT_APP_GOOGLE_PLACES_KEY not set'); return }
    let cancelled = false
    const loader = new Loader({ apiKey: key, version: 'weekly', libraries: ['places'] })
    loader.load()
      .then((google) => {
        if (cancelled) return
        googleRef.current = google
        setPlacesReady(true)
      })
      .catch((err) => { console.error('[Places] loader failed:', err) })
    // A cancelled flag rather than the old listener teardown: StrictMode
    // double-invokes effects, and nothing here is attached to the DOM to undo.
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!placesReady || !setupOpen) return
    const google = googleRef.current
    const node = addressInputRef.current
    if (!google || !node) return

    // Every Autocomplete appends its own .pac-container dropdown to <body> and
    // does not remove it. Since a new instance is built each time setup
    // reopens, note which container this one added and take it down with it.
    const before = new Set(document.querySelectorAll('.pac-container'))
    const autocomplete = new google.maps.places.Autocomplete(node, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'geometry'],
    })
    // Bias toward Arizona without hard-excluding edge cases
    autocomplete.setBounds(new google.maps.LatLngBounds(
      new google.maps.LatLng(31.3, -114.8),
      new google.maps.LatLng(37.0, -109.0)
    ))
    const listener = autocomplete.addListener('place_changed', async () => {
      const place = autocomplete.getPlace()
      if (!place || !place.geometry || !place.geometry.location) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const formatted = place.formatted_address || ''
      setSelectedPlace({ address: formatted, lat, lng })
      setMlsNumber(formatted)
      // Set BEFORE the await, synchronously, so a blur arriving in either order
      // reads the formatted address rather than the closure's stale fragment.
      lastPlaceQueryRef.current = formatted
      console.log('[Places] selected:', formatted, lat, lng)

      // Persisted here as well as on blur, because the widget can keep focus on
      // the input after a selection and a blur is not guaranteed before the
      // agent navigates away. `formatted` is a local const, so unlike the blur
      // path this can never carry stale text. Written inline rather than through
      // saveSubjectQuery because that function's identity changes every render,
      // and listing it as a dependency would rebuild the Autocomplete on every
      // keystroke.
      //
      // AWAITED, and that is not cosmetic. A Supabase query builder is a lazy
      // thenable — the fetch happens inside then(), so the previous
      // fire-and-forget form issued no request at all and this write silently
      // never happened. The error is checked as well as caught: PostgREST
      // failures come back on the result object, they do not throw.
      //
      // THE COORDINATES GO IN THIS SAME PATCH, never a second one. Both writers
      // of subject_query — this and the blur — now carry the coordinates with
      // it, so the text and the point it describes can never be split across
      // two requests that land in either order. Agent-picked Google values
      // only; see supabase/schema/2026-09-25_add_property_cases_subject_fields.sql.
      //
      // The specs are nulled because a NEW place invalidates them: sqft, year
      // and stories were typed for the address that was in the field a moment
      // ago. The input's onChange has already cleared them locally, so this is
      // the durable half of the same clear.
      try {
        const { data: saved, error: saveErr } = await supabase.from('property_cases')
          .update({ subject_query: formatted, subject_lat: lat, subject_lng: lng, ...SUBJECT_SPECS_NULL })
          .eq('id', caseId).select()
        if (saveErr) {
          console.error('[Places] subject save failed:', saveErr.message)
        } else if (!saved || saved.length === 0) {
          // Refused by RLS, which is zero rows and no error. The selection is
          // KEPT — see the note on SUBJECT_REFUSED_MSG. caseData deliberately
          // is not updated: nothing landed, and saveSpec compares against it to
          // decide whether a write is needed.
          noteSubjectRefused(subjectRefusedRef, showToastRef.current)
        } else {
          setCaseData(cd => (cd
            ? { ...cd, subject_query: formatted, subject_lat: lat, subject_lng: lng, ...SUBJECT_SPECS_NULL }
            : cd))
        }
      } catch (err) {
        console.error('[Places] subject save threw:', err)
      }
    })
    const added = Array.from(document.querySelectorAll('.pac-container')).filter(el => !before.has(el))

    return () => {
      google.maps.event.removeListener(listener)
      google.maps.event.clearInstanceListeners(autocomplete)
      added.forEach(el => el.remove())
    }
  }, [placesReady, setupOpen, caseId])

  async function dismissHelp() {
    setHelpSeen(true)
    await supabase.from('profiles').update({ strategy_help_seen: true }).eq('id', session.user.id)
  }

  // The shell owns the transcript element now, so the distance-from-bottom read
  // comes off the event target instead of a local ref. The gate itself is
  // unchanged: a reader who has scrolled up is never yanked back down.
  const handleChatScroll = useCallback((e) => {
    const el = e.currentTarget
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    setShouldAutoScroll(distanceFromBottom < 100)
  }, [])

  const lastAssistantIdRef = useRef(null)
  const lastUserIdRef = useRef(null)

  useEffect(() => {
    if (!shouldAutoScroll) return

    const newestAssistant = [...messages].reverse().find(m =>
      m.role === 'assistant' && typeof m.id === 'string' && m.id.startsWith('temp-assistant-')
    )
    const newestUser = [...messages].reverse().find(m =>
      m.role === 'user' && typeof m.id === 'string' && m.id.startsWith('temp-user-')
    )

    // Case 1: a brand-new assistant message just appeared → pin user's most-recent message top to viewport top, once
    // The shell's pin targets whatever streamingMessageRef points at, and
    // Strategy attaches that to the temp-user row — same anchor as before.
    if (newestAssistant && newestAssistant.id !== lastAssistantIdRef.current) {
      lastAssistantIdRef.current = newestAssistant.id
      shellRef.current?.pinAssistantToTop()
      return
    }

    // Case 2: a brand-new user message just appeared (and no new assistant yet) → scroll to bottom
    if (newestUser && newestUser.id !== lastUserIdRef.current && !newestAssistant) {
      lastUserIdRef.current = newestUser.id
      shellRef.current?.scrollToBottom()
      return
    }

    // Case 3: token delta on the current streaming message, DB refresh, or any other change → do nothing
  }, [messages, shouldAutoScroll])

  // Team branding. Empty unless the agent has an armls_agent_id AND a team AND
  // at least one OTHER member — any of those missing and the whole feature is
  // absent, not disabled. `teammates` excludes the signing agent.
  const [teammates, setTeammates] = useState([])
  const [selectedMateId, setSelectedMateId] = useState(null)
  // Derived here, not beside the avatar row 700 lines below: generateReport
  // reads it, and a const declared after its reader is a temporal-dead-zone
  // trap waiting for the first caller that runs during render.
  const selectedMate = teammates.find(t => t.armls_agent_id === selectedMateId) || null

  const [generatingCurrent, setGeneratingCurrent] = useState(false)
  const [reportLanguage, setReportLanguage] = useState('en')
  // In-memory, per-view cache of the last generated report + the language it's in.
  // Used to recompose across languages without re-analyzing the chat. { language, report } | null
  const cachedReportRef = useRef(null)
  // The last generated report, held for the on-screen preview. State rather than
  // a ref because the overlay has to repaint when it lands.
  // { blob, blobUrl, fileName, language, addressLabel } | null
  //
  // useReportPreview carries the close handler and the unmount revoke that used
  // to live here as a function and an effect — the same two behaviours, one
  // copy, now that Casita Check opens the same overlay.
  const [reportPreview, setReportPreview, closeReportPreview] = useReportPreview()
  const [mlsNumber, setMlsNumber] = useState('')
  const [pullingComps, setPullingComps] = useState(false)
  const [compResult, setCompResult] = useState(null)
  // The tier_overrides the live pull was requested with (null = defaults). Part
  // of the pull fingerprint: the widen settings beyond tier and radius are not
  // echoed back in compResult._meta.
  const [compPullOverrides, setCompPullOverrides] = useState(null)
  // Whether the widen controls are showing, or folded behind a link. Set from
  // each pull rather than remembered: a thin pool opens them because that is
  // the pull that needs them, and a healthy one folds them away. An agent who
  // opens them by hand keeps them open until the next pull re-decides.
  const [widenOpen, setWidenOpen] = useState(false)
  // LIVE MLS ACCESS. Not licensed = the profile's brokerage holds no ARMLS
  // license of its own (canUseLiveComps), OR armls-comp-pull refused with brokerage_not_licensed
  // (a stale profile in the browser — the server is the authority). Either way
  // the pull controls go and the notices below take their place.
  const [mlsBlocked, setMlsBlocked] = useState(false)
  const mlsLicensed = canUseLiveComps(profile) && !mlsBlocked
  // The comp whose detail panel is open, or null. Local state on purpose — no
  // route, no URL param. Comps live only in compResult for the session, so a
  // reload leaves no cards and the panel is correctly unreachable.
  const [detailComp, setDetailComp] = useState(null)
  // Whether the comps have taken over the case area. Internal to this view —
  // like detailComp it is session-only, with no route and no URL param, because
  // the comps behind it are not retained past the session either.
  const [compsGridOpen, setCompsGridOpen] = useState(false)
  const [widenToggles, setWidenToggles] = useState({ dropStory: false, biggerSqft: false, radius15: false, closings6: false, widenYear: false, radius2: false })
  // PULL SETTINGS FOLLOW THE ANALYSIS. The widen settings are session state, so
  // a reload used to drop them and the next pull ran at the tight defaults —
  // a different pool from the one the analysis was written on. When the case
  // opens, the pills are set from the latest analysis's pull fingerprint,
  // before any pull. Once per case view (Dashboard remounts it per case), and
  // never over a pull already on screen. A case whose analyses carry no
  // fingerprint keeps the defaults, as before.
  const pullSettingsRestoredRef = useRef(false)
  useEffect(() => {
    if (pullSettingsRestoredRef.current || compResult) return
    const latest = latestPullAnalysis(messages)
    if (!latest) return
    pullSettingsRestoredRef.current = true
    const restored = widenTogglesFromFingerprint(latest.fingerprint)
    if (restored) setWidenToggles(restored)
  }, [messages, compResult])
  // Which widen pill the cursor is over, or null. One key rather than six
  // booleans: only one pill can be hovered at a time.
  const [hoverPill, setHoverPill] = useState(null)
  const [expandedGroups, setExpandedGroups] = useState({})
  // APPRAISER REPORT — admin-only and desktop-only until the PDF ships.
  // The comps the agent has ticked, by listing_id. Session-only like
  // compResult itself: it names MLS listings, so it is never persisted, and
  // Dashboard remounts this view per case, so a case switch starts empty. A
  // re-pull prunes it rather than clearing it (see pullComps).
  const [appraiserPicks, setAppraiserPicks] = useState(() => new Set())
  // Appraiser mode: the checkboxes and the step box exist only while it is on.
  // Session-only. A re-pull or widen leaves it on (the picks are pruned, not
  // cleared); Cancel turns it off and clears the picks; a case switch remounts.
  const [appraiserMode, setAppraiserMode] = useState(false)
  // The contract-terms form while it is open: four strings, as typed. null when
  // closed. Errors are per field, shown only after a Save attempt.
  const [contractForm, setContractForm] = useState(null)
  const [contractErrors, setContractErrors] = useState({})
  const [contractSaving, setContractSaving] = useState(false)
  // The last terms submitted this session, saved or not. A refused write (a
  // shared agent; the UPDATE policy is owner-only) keeps them here, so the
  // form reopens on what the agent typed rather than on the empty row.
  const [contractSession, setContractSession] = useState(null)

  // addressSlug and triggerBlobDownload are module scope now — see REPORT
  // DELIVERY (shared). Both were pure and byte-identical to the copies
  // Casita Check would otherwise have needed.

  // reportType is the discriminator the extraction produced. It decides which
  // half of the PDF renders, and until now it was computed, used, and thrown
  // away — leaving no way to ask afterwards which reports were buyer reports.
  // That question cost a full PDF scan to answer once; the column makes it a
  // query. Nullable, so the 81 rows written before this stay NULL.
  //
  // LANGUAGE. A new report replaces earlier rows of the same type AND the same
  // language only, so English and Spanish versions are kept side by side. The
  // language of an existing row is read from its file name (_EN / _ES — see
  // strategyReportFileName); there is no language column. A row from before
  // the tag has no known language and is left in place rather than guessed at.
  async function uploadReportBlob(blob, fileName, fileType, reportType = null, language = 'en') {
    const langTag = reportLanguageTag(language)
    const sameLanguage = (f) => reportLanguageOfFileName(f?.file_name) === langTag
    const folderPath = `${session.user.id}/${caseId}`
    const fullPath = `${folderPath}/${fileName}`
    // Same ordering rule as uploadFile, and it matters more here: the report
    // name carries only the date and the language, so regenerating in the same
    // language on the same day produces the identical path. Previously the old
    // object and row were deleted up front, so a failed generation destroyed
    // the previous report and left nothing.
    const { data: existingRows } = await supabase.from('property_case_files')
      .select('id, file_name, storage_path').eq('case_id', caseId).eq('file_type', fileType)

    const { error: upErr } = await supabase.storage.from('property-docs')
      .upload(fullPath, blob, { upsert: true, contentType: 'application/pdf' })
    if (upErr) throw new Error(`Storage upload failed: ${upErr.message}`)

    // storage_path is the record of where the file lives; file_url stays null.
    // No long-lived link is created or stored (schema 2026-10-10).
    const { data: row, error: insertErr } = await supabase.from('property_case_files')
      .insert({ case_id: caseId, file_type: fileType, storage_path: fullPath, file_name: fileName, report_type: reportType }).select().single()
    if (insertErr) {
      // Rolled back unless an existing row already points at this path — see
      // the note in uploadFile. Same-day regeneration hits that case every time.
      const stillReferenced = (existingRows || []).some(r => (r.storage_path || `${folderPath}/${r.file_name}`) === fullPath)
      if (!stillReferenced) {
        const { error: rmErr } = await supabase.storage.from('property-docs').remove([fullPath])
        if (rmErr) console.error('[strategy report] rollback remove failed:', rmErr.message)
      }
      throw new Error(`DB insert failed: ${insertErr.message}`)
    }

    setFiles(prev => [...prev.filter(f => !(f.case_id === caseId && f.file_type === fileType && sameLanguage(f))), row])

    // Row first, then object, and never the path just written.
    const superseded = (existingRows || []).filter(r => r.id !== row.id && sameLanguage(r))
    if (superseded.length > 0) {
      // .select() and a row count: an RLS refusal returns zero rows with no
      // error. Storage is removed only for the rows actually deleted.
      const { data: deletedRows, error: delErr } = await supabase.from('property_case_files')
        .delete().in('id', superseded.map(r => r.id)).select('id')
      if (delErr) {
        console.error('[strategy report] superseded row(s) not removed:', delErr.message)
      } else {
        const deletedIds = new Set((deletedRows || []).map(r => r.id))
        if (deletedIds.size < superseded.length) {
          console.error('[strategy report] superseded row(s) refused:', superseded.length - deletedIds.size, 'of', superseded.length)
        }
        const stalePaths = superseded
          .filter(r => deletedIds.has(r.id))
          .map(r => r.storage_path || `${folderPath}/${r.file_name}`)
          .filter(p => p !== fullPath)
        if (stalePaths.length > 0) {
          const { error: rmErr } = await supabase.storage.from('property-docs').remove(stalePaths)
          if (rmErr) console.error('[strategy report] superseded object(s) not removed:', rmErr.message)
        }
      }
    }

    // No link is created: the agent shares the PDF file itself from the
    // preview overlay. Returning the whole row so a caller can reach
    // file_name, id and storage_path.
    return row
  }

  // Strategy's share caption, and nothing else. The File construction, the
  // gesture-preserving ordering and the canShare feature test are all
  // shareReportFile in REPORT DELIVERY (shared).
  async function shareReport() {
    return shareReportFile(
      reportPreview,
      `Strategy report — ${reportPreview?.addressLabel || 'HomePros'}`,
      showToast,
      "This browser can't share files. Use Download, then attach the file.",
    )
  }

  // Resolve the signing agent's team, once. Three queries, the same shape
  // backfill-cards uses (team_members has no FK to profiles — armls_agent_id is
  // the only link), and deliberately silent: this decorates a report, so a
  // failure here must never stop one being generated. Every early return leaves
  // `teammates` empty, which is what hides the feature.
  const myArmlsId = profile?.armls_agent_id
  const myBrokerage = profile?.brokerage
  useEffect(() => {
    if (!myArmlsId || !myBrokerage) return
    let cancelled = false
    ;(async () => {
      try {
        const { data: mine, error: mineErr } = await supabase
          .from('team_members').select('team_id').eq('armls_agent_id', myArmlsId)
        if (mineErr || cancelled) return
        // [0] like backfill-cards: team_members.armls_agent_id is uniquely
        // indexed, so an agent is on at most one team.
        const teamId = mine?.[0]?.team_id
        if (!teamId) return

        const { data: mates, error: matesErr } = await supabase
          .from('team_members').select('armls_agent_id').eq('team_id', teamId)
        if (matesErr || cancelled) return
        const mateIds = (mates || [])
          .map(m => m.armls_agent_id)
          .filter(id => id && id !== myArmlsId)
        if (mateIds.length === 0) return

        // Same brokerage only: a report carries the signed-in agent's
        // brokerage name, so a teammate from another brokerage is never
        // offered for its footer.
        const { data: profs, error: profErr } = await supabase
          .from('profiles')
          .select('full_name, phone, email, armls_agent_id, avatar_url')
          .in('armls_agent_id', mateIds)
          .eq('brokerage', myBrokerage)
        if (profErr || cancelled) return
        // A teammate with no name cannot be rendered as initials or printed in
        // a footer, so they are dropped rather than shown as a blank circle.
        setTeammates((profs || []).filter(p => p.full_name && p.full_name.trim()))
      } catch (_e) {
        // Silent by design. See the note above.
      }
    })()
    return () => { cancelled = true }
  }, [myArmlsId, myBrokerage])

  async function generateReport(version, language = 'en') {
    if (!profile?.brokerage) {
      showToast('Please complete your brokerage setup first.')
      return
    }
    // Defense in depth. The button is already disabled without a payload, so
    // this is unreachable through the UI — it exists so a future caller, or a
    // bypassed button state, cannot produce a report whose comp numbers have
    // nothing behind them.
    if (!compResult) return
    // Same stop as the disabled button, for any other caller: the pull on
    // screen is not the one the latest analysis was written on.
    if (pullDiffersFromAnalysis(messages, compResult, compPullOverrides)) return
    if (generatingCurrent) return
    setGeneratingCurrent(true)
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token
      // The only place an expired session is unambiguously identifiable.
      //
      // getSession() does refresh an expired token (auth-js 2.99 __loadSession),
      // so this fires only when that refresh FAILED — a revoked refresh token,
      // rotation consumed by another tab, a network fault at that instant. In
      // that case it resolves with a null session, `token` is undefined, and
      // without this guard `Bearer undefined` goes out.
      //
      // Catching it here rather than on the response is deliberate. The edge
      // function never returns 401: an unusable auth header fails RLS, throws
      // "Case not found or access denied", and exits through one terminal catch
      // as a 500 — identical to a genuine server fault. Status cannot tell them
      // apart, so the check has to happen before the request.
      //
      // The early return is inside the try, so the finally below still clears
      // the generating flag.
      if (!token) {
        showToast('Your session has expired. Please refresh the page and sign in again.', true)
        return
      }
      // Recompose (reuse the last report) ONLY when switching to a different language on the same case.
      // Otherwise send no base_report so the edge function runs a fresh analysis.
      const cached = cachedReportRef.current
      const shouldRecompose = !!(cached && cached.report && cached.language !== language)
      // comp_index — the join key, and NOTHING else about a comp. Three string
      // fields per entry: the id the join runs on, the address the extraction
      // matches the conversation against, and the bucket. No price, no size,
      // no date, no remarks, and never private_remarks — the model does not
      // need them to pick an id, and this array is the one place comp data
      // would otherwise leave the browser on the report path.
      //
      // Only on the EXTRACTION path. A recompose is a translation of a report
      // that has already been joined once; sending the index again would invite
      // the model to re-derive ids it is instructed to copy verbatim.
      //
      // Built from compResult.groups, which is in memory only. Nothing here is
      // persisted, by this function or by the edge function that receives it.
      const compIndex = shouldRecompose ? null : COMP_BUCKET_ORDER.flatMap(group =>
        (compResult.groups?.[group] || [])
          .filter(c => c && c.listing_id)
          .map(c => ({ listing_id: c.listing_id, address: c.address || '', group }))
      )
      const requestBody = shouldRecompose
        ? { case_id: caseId, version, language, base_report: cached.report }
        : { case_id: caseId, version, language, ...(compIndex && compIndex.length ? { comp_index: compIndex } : {}) }
      const postReport = (body) => fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-strategy-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
      })
      // Read the body before throwing. The function reports what actually went
      // wrong in an `error` field — "Extraction returned invalid JSON", "Could
      // not load conversation history" — and throwing the status alone discarded
      // all of it, so every distinct failure reached the agent as "500". This is
      // the same thing the success:false branch below already does; only this
      // path was dropping it.
      //
      // res.json() is guarded because a failure upstream of the function — a
      // gateway timeout, a proxy error page — is not JSON, and letting the parse
      // throw here would replace a real error with a parser message.
      const readError = async (r) => {
        try { return await r.json() } catch (_) { return null }
      }
      let res = await postReport(requestBody)
      if (!res.ok) {
        let errBody = await readError(res)
        // ONE automatic retry, as a fresh request, when the writer's output was
        // unusable (cut off at its token cap, or not valid JSON). The flag
        // raises the cap for the retry. A second failure falls through to the
        // existing error below, unchanged — the agent never has to press
        // anything for the first retry.
        if (errBody?.code === 'extraction_retryable') {
          console.warn('[generate-strategy-report] writer output unusable (' + (errBody.reason || 'unknown') + '), retrying once')
          res = await postReport({ ...requestBody, extraction_retry: true })
          errBody = res.ok ? null : await readError(res)
        }
        if (!res.ok) throw new Error(errBody?.error || `Server error (${res.status})`)
      }
      const responseData = await res.json()
      if (!responseData.success || !responseData.extracted) {
        throw new Error(responseData.error || 'Unknown extraction error')
      }
      const extracted = responseData.extracted
      // Cache the most recent report + the language it was generated in (fresh OR recomposed),
      // so a subsequent language switch can recompose instead of re-analyzing.
      //
      // THE UNJOINED REPORT IS WHAT GETS CACHED, deliberately — the join runs
      // below and returns a new object, so this holds the model's own output.
      // Recompose is instructed to leave every number exactly as given; feeding
      // it payload numbers would make that instruction meaningless, and the
      // Spanish pass would then be translating figures this function is about
      // to overwrite anyway.
      cachedReportRef.current = { language, report: extracted }

      // THE JOIN. Runs on the extraction path and the recompose path alike —
      // a translated report carries the same listing_ids (the recompose prompt
      // preserves them), so it gets the same live numbers rather than whatever
      // survived a second model hop.
      const { report: joined, dropped, droppedRows, uncheckedRows } = joinCompsFromPayload(extracted, compResult)
      // NOT ITS OWN TOAST. There is one toast slot, and the success message
      // fires a few awaits later — it replaced this one before an agent could
      // read it, so the notice was effectively invisible. The two are merged at
      // the success call below instead.
      //
      // The console line is the diagnosis: an id that is present but unmatched
      // means the extraction picked the wrong COMP INDEX row, and "none" means
      // it ignored the instruction to carry one. Those are different bugs and
      // the toast cannot tell them apart. Each row carries its reason: "no id",
      // "id not in pull", or "address mismatch" (an id that points at a
      // different house than the one the model named).
      if (dropped > 0) {
        console.warn('[strategy report] comps dropped:', droppedRows)
      }
      if (uncheckedRows.length > 0) {
        console.warn('[strategy report] comps kept — no address to check:', uncheckedRows)
      }

      // NO CLOSED SALE MATCHED. Before the figure check, the PDF and the
      // upload, on the extraction AND the recompose path, since both run
      // through here. The return is inside the try, so the finally below clears
      // the generating flag. Nothing is built or saved.
      if (countClosedMatches(extracted, joined, compResult) === 0) {
        setNoClosedMatch(true)
        return
      }

      // PRE-PDF FIGURE CHECK, on the joined report, so comp prices are the
      // payload's. This function serves the extraction AND the recompose
      // (language switch) path, so both are checked here. Cancel returns
      // before pdf() — no PDF, no upload — and the finally below clears the
      // generating flag, which stays set while the confirm is open so the
      // button cannot fire a second report underneath it.
      const figureIssues = checkReportFigures(joined, compResult)
      if (figureIssues.nonClean.length > 0 || figureIssues.figures.length > 0) {
        const proceed = await new Promise(resolve => {
          figureCheckResolveRef.current = resolve
          setFigureCheck({ ...figureIssues, reportType: joined.report_type })
        })
        if (!proceed) return
      }

      const subjectAddr = responseData.subject_address || extracted.subject?.address || address
      const userEmail = session.user.email

      let clientBlob
      try {
        clientBlob = await pdf(<StrategyReportPDFClient data={joined} profile={profile} email={userEmail} language={language} teammate={selectedMate} />).toBlob()
      } catch (pdfErr) {
        throw new Error(`Could not build PDF: ${pdfErr.message}`)
      }

      // The Arizona date and the language are in the name: an English and a
      // Spanish report of the same case are kept side by side, and an evening
      // report is dated the day it was made in Arizona, not the next UTC day.
      // `language` is this call's argument — the language the request asked
      // for, the PDF was rendered in, and cachedReportRef just recorded.
      const slug = addressSlug(subjectAddr)
      const clientName = strategyReportFileName(slug, version, language)
      const clientType = `strategy_report_client_${version}`

      await uploadReportBlob(clientBlob, clientName, clientType, extracted.report_type || null, language)

      // Desktop only, as Casita Check does. iOS ignores <a download> and
      // NAVIGATES to the blob: URL, which would take the tab before the overlay
      // could paint. On a phone the overlay opens instead, and its Share button
      // is a fresh tap — the Generate tap was spent several awaits ago — so
      // navigator.share hands over the PDF file itself. No link is created.
      if (!isMobile) triggerBlobDownload(clientBlob, clientName)

      // A SECOND object URL, deliberately. triggerBlobDownload revokes its own
      // the moment it fires, so reusing that one would hand the iframe a dead
      // handle. This one lives until the overlay closes or the view unmounts.
      const previewUrl = URL.createObjectURL(clientBlob)
      setReportPreview({
        blob: clientBlob,
        blobUrl: previewUrl,
        fileName: clientName,
        // The generated language, not the toggle. They diverge the moment the
        // agent flips the switch without regenerating.
        language: cachedReportRef.current?.language || language,
        addressLabel: subjectAddr,
      })

      // ONE toast. At 8s rather than the 2.5s default: the merged sentence is
      // twice the length and ends with something the agent may want to act on
      // (re-pull, or check which comp went missing), and 2.5s is not enough to
      // read that and decide.
      if (dropped > 0) {
        showToast(`Report generated and saved. ${dropped} comp${dropped === 1 ? '' : 's'} from the analysis couldn't be matched to this pull and ${dropped === 1 ? 'was' : 'were'} left out.`, false, 8000)
      } else {
        showToast('Strategy report generated and saved to case files.')
      }
    } catch (e) {
      console.error('[generate-strategy-report]', e)
      showToast(`Report generation failed: ${e.message}`, true)
    } finally {
      setGeneratingCurrent(false)
    }
  }

  // Leaving the case with the confirm open is a Cancel: the awaiting
  // generateReport returns before pdf() instead of waiting forever.
  useEffect(() => () => {
    const resolve = figureCheckResolveRef.current
    figureCheckResolveRef.current = null
    if (resolve) resolve(false)
  }, [])

  // Closes the figure-check confirm and hands the answer to the generateReport
  // awaiting it. Safe to call twice: the resolver is taken once.
  function answerFigureCheck(proceed) {
    const resolve = figureCheckResolveRef.current
    figureCheckResolveRef.current = null
    setFigureCheck(null)
    if (resolve) resolve(proceed)
  }

  async function pullComps(tierOverrides = null) {
    // The button is disabled too; this covers every other caller.
    if (!mlsLicensed) return
    const mls = mlsNumber.trim()
    const specsValid = Number(specSqft) > 0 && Number(specStories) > 0 && Number(specYearBuilt) > 0
    const usingForm = !!selectedPlace && specsValid
    if (!usingForm && !mls) { showToast('Enter an MLS number, or pick an address and fill Sqft, Year, and Stories.'); return }
    if (pullingComps) return
    setPullingComps(true)
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token
      // Same guard, same reason as generateReport. getSession() refreshes an
      // expired token on its own, so this fires only when that refresh failed —
      // and without it `Bearer undefined` goes out, fails RLS, and comes back as
      // a status code that looks like a server fault. Inside the try, so the
      // finally below still clears pullingComps.
      if (!token) {
        showToast('Your session has expired. Please refresh the page and sign in again.', true)
        return
      }
      const subtypeMap = {
        'Single Family': ['Single Family Residence'],
        'Condo / Townhouse': ['Townhouse', 'Patio Home', 'Apartment', 'Gemini/Twin Home', 'Loft Style'],
        'Manufactured / Mobile': ['Mfg/Mobile Housing', 'Modular/Pre-Fab'],
      }
      let body
      if (usingForm) {
        body = { case_id: caseId, subject: { form: {
          address: selectedPlace.address,
          latitude: selectedPlace.lat,
          longitude: selectedPlace.lng,
          living_area: Number(specSqft),
          year_built: Number(specYearBuilt),
          stories_total: Number(specStories),
          beds: specBeds ? Number(specBeds) : null,
          baths_total: specBathsTotal ? Number(specBathsTotal) : null,
          subtype: subtypeMap[propertyType] || null,
        } } }
      } else {
        body = { case_id: caseId, subject: { listing_id: mls } }
      }
      if (tierOverrides) body.tier_overrides = tierOverrides
      // THE SALE WINDOW IS ANCHORED TO THE ANALYSIS. armls-comp-pull counts the
      // closed-sale window back from as_of_date, so a pull made hours or a few
      // days after the analysis reaches back exactly as far as the analysis's
      // pull did. The date is the Arizona date of the analysis message's own
      // timestamp — the app's record of when it ran, not anything from ARMLS.
      // The server ignores a date that is in the future or more than 7 days
      // old and uses today's Arizona date instead.
      const asOf = latestPullAnalysis(messages)?.createdAt
      const asOfDate = asOf ? arizonaDateISO(asOf) : null
      if (asOfDate) body.as_of_date = asOfDate
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/armls-comp-pull`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
      })
      // Read the body before throwing — armls-comp-pull reports the real reason
      // in `error`, and the status alone discarded it. Guarded parse because an
      // upstream gateway failure is not JSON.
      if (!res.ok) {
        let serverMsg = ''
        let serverCode = ''
        try { const errBody = await res.json(); serverMsg = errBody?.error || ''; serverCode = errBody?.code || '' } catch (_) { serverMsg = ''; serverCode = '' }
        // Refused by the brokerage gate: switch to the locked notices instead
        // of an error toast. Inside the try, so the finally still clears
        // pullingComps.
        if (res.status === 403 && serverCode === 'brokerage_not_licensed') {
          setMlsBlocked(true)
          return
        }
        throw new Error(serverMsg || `Server error (${res.status})`)
      }
      const responseData = await res.json()
      if (!responseData.ok) {
        showToast(responseData.message || responseData.error || 'Comp pull failed.')
        return
      }
      setCompResult(responseData)
      setCompPullOverrides(tierOverrides || null)
      // A re-pull or widen keeps every ticked comp that is still in a bucket
      // that can be ticked, and drops only the ones that left. The same Set is
      // returned when nothing was dropped, so an unchanged pick costs no render.
      const stillPickable = new Set(
        COMP_BUCKET_ORDER.filter(b => appraiserGroupOf(b))
          .flatMap(b => (responseData.groups?.[b] || []).map(c => c?.listing_id))
          .filter(Boolean)
      )
      setAppraiserPicks(prev => {
        if (prev.size === 0) return prev
        const next = new Set([...prev].filter(id => stillPickable.has(id)))
        return next.size === prev.size ? prev : next
      })
      // Decided per pull, at the one point a pull result arrives. Under five
      // closed comps is a thin pool and widening is the likely next move, so
      // the controls open themselves; at five or more they stay behind a link.
      // A pull that ran with widen settings also shows them, so settings
      // restored from the analysis are visible rather than folded away.
      setWidenOpen((responseData.groups?.closed || []).length < 5 || !!tierOverrides)
      showToast(`Pulled ${responseData._meta?.total_comps ?? 0} comps.`)
    } catch (e) {
      console.error('[armls-comp-pull]', e)
      showToast(`Comp pull failed: ${e.message}`, true)
    } finally {
      setPullingComps(false)
    }
  }

  async function shareWithAgent() {
    if (!selectedAgentId || sharing) return
    setSharing(true)
    try {
      const newShared = [...sharedWith, selectedAgentId]
      // .select() AND A ROW COUNT. A write refused by RLS is NOT an error:
      // PostgREST applies the policy as a filter, so a refused update returns
      // { error: null } with zero rows. Without this the UI added the agent to
      // sharedWith, toasted success, AND fired the invitation email — telling
      // someone they have access to a case the database never shared. The
      // throw lands in the catch below, which toasts and leaves state
      // untouched, so nothing is sent.
      const { data: updatedRows, error } = await supabase
        .from('property_cases').update({ shared_with: newShared }).eq('id', caseId).select()
      if (error) throw error
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error('the case could not be updated. Nothing was shared and no email was sent.')
      }
      const agent = agentsList.find(a => a.id === selectedAgentId)
      setSharedWith(newShared)
      const noteToSend = shareNote
      setSelectedAgentId('')
      setAgentSearch('')
      setShareNote('')
      showToast(`Case shared with ${agent?.full_name || agent?.email || 'agent'}`)

      // Fire the email send — non-blocking from the user's perspective
      supabase.functions.invoke('share-case', {
        body: {
          case_id: caseId,
          agent_id: agent?.id,
          share_note: noteToSend || ''
        }
      }).then(({ data, error: fnErr }) => {
        if (fnErr || !data?.ok) {
          console.error('share-case email failed:', fnErr || data?.error)
          showToast(`Share saved, but email did not send: ${fnErr?.message || data?.error || 'unknown error'}`)
        } else {
          console.log('share-case email sent:', data?.email_id)
        }
      }).catch((e) => {
        console.error('share-case invoke threw:', e)
        showToast(`Share saved, but email did not send: ${e.message}`)
      })

    } catch (e) {
      showToast(`Share failed: ${e.message}`)
    } finally {
      setSharing(false)
    }
  }

  async function unshareAgent(agentId) {
    const agent = agentsList.find(a => a.id === agentId)
    const label = agent?.full_name || agent?.email || 'this agent'
    if (!window.confirm(`Remove access for ${label}?`)) return
    const newShared = sharedWith.filter(id => id !== agentId)
    // .select() AND A ROW COUNT, the same guard shareWithAgent carries. A
    // write refused by RLS is not an error: PostgREST applies the policy as a
    // filter, so a refused update returns { error: null } with zero rows.
    // Without this the row stays shared and the UI says "Removed" — the worse
    // direction of the two, because the agent believes access is gone.
    //
    // An early return rather than a throw: unlike shareWithAgent this function
    // has no try/catch to land in, and its existing error path is the line
    // above. Both exits happen before setSharedWith, so local state never
    // drifts from the table.
    const { data: updatedRows, error } = await supabase
      .from('property_cases').update({ shared_with: newShared }).eq('id', caseId).select()
    if (error) { showToast(`Could not remove: ${error.message}`); return }
    if (!updatedRows || updatedRows.length === 0) {
      showToast(`Could not remove ${label} — the change was refused and access is unchanged.`, true)
      return
    }
    setSharedWith(newShared)
    showToast(`Removed ${label}`)
  }

  async function saveAddress() {
    await supabase.from('property_cases').update({ subject_address: address }).eq('id', caseId)
  }

  // The setup field, which takes an address OR an MLS number. Its own column,
  // because subject_address is the case name and this is neither always an
  // address nor always the name. Agent-typed, so the ARMLS retention rule
  // permits storing it — nothing ARMLS returned is persisted here.
  // The ref first, then state. Clicking a suggestion blurs the input on
  // mousedown, BEFORE place_changed runs — observed, not assumed — so at that
  // moment this function's closure still holds the half-typed fragment in both
  // mlsNumber and selectedPlace. Reading the ref sidesteps that entirely: refs
  // are read at call time, and place_changed sets it synchronously. Either
  // ordering now writes the formatted address, so the two writers agree and it
  // no longer matters which PATCH lands last.
  //
  // selectedPlace stays the source for everything that is not a Places
  // selection, and mlsNumber behind it for typed input. Both the ref and
  // selectedPlace are cleared by onChange on any manual edit, so neither can
  // outlive the text it describes or override a genuine later edit.
  //
  // Takes no argument on purpose. It is only ever used as onBlur, which passes
  // an event; an earlier explicit-value parameter had no caller and meant every
  // blur silently took the fallback path.
  //
  // THE COORDINATES RIDE IN THE SAME PATCH AS THE TEXT, and are nulled whenever
  // there is no live place. That one rule is also what stops a stale point
  // outliving the address it belongs to: replacing an address with an MLS
  // number clears selectedPlace in onChange, and the blur that follows writes
  // the new text with both coordinates null. Without it the row would keep the
  // old point, the next load would rebuild a place out of it, and pullComps
  // would quietly pull comps around the previous property while the field read
  // an MLS number.
  //
  // The five specs are nulled on the same branch, for the same reason: they
  // were typed for the place that has just gone.
  //
  // The `subjectLocked` guard is read at call time, not during render — this
  // is only ever an onBlur/onKeyDown handler — so referencing a const declared
  // further down is safe here in a way it would not be in the render body. Once
  // the field is read-only there is nothing a blur could have changed, and
  // without the guard every click into it would fire a pointless PATCH, and a
  // shared agent would get the refusal toast for merely looking at the field.
  async function saveSubjectQuery() {
    if (subjectLocked) return
    const v = lastPlaceQueryRef.current != null
      ? lastPlaceQueryRef.current
      : (selectedPlace ? selectedPlace.address : mlsNumber)
    const patch = selectedPlace
      ? { subject_query: v, subject_lat: selectedPlace.lat, subject_lng: selectedPlace.lng }
      : { subject_query: v, subject_lat: null, subject_lng: null, ...SUBJECT_SPECS_NULL }
    const { data: saved, error: saveErr } = await supabase.from('property_cases')
      .update(patch).eq('id', caseId).select()
    if (saveErr) { console.error('[Places] subject_query blur save failed:', saveErr.message); return }
    // Refused, not failed. Local state is left exactly as it is — see the note
    // on SUBJECT_REFUSED_MSG — so the session keeps working.
    if (!saved || saved.length === 0) { noteSubjectRefused(subjectRefusedRef, showToast); return }
    setCaseData(cd => (cd ? { ...cd, ...patch } : cd))
  }

  // ONE writer for the five agent-typed spec columns, shaped like BINSR's
  // saveCreditAmount: parse to a number or null, skip a write that changes
  // nothing, and put the field back from caseData when it does not land.
  //
  // GUARDED ON selectedPlace, and that guard is the compliance boundary rather
  // than a convenience. These inputs render only inside the SUBJECT DETAILS
  // block, which itself renders only on selectedPlace, so an MLS-path case has
  // no way to reach them — the guard makes that structural, so a future caller
  // cannot write an ARMLS-derived figure into a column reserved for what the
  // agent typed. See supabase/schema/2026-09-25_add_property_cases_subject_fields.sql.
  //
  // `specsLocked`, not subjectLocked: the address can be frozen while these
  // five are still being filled in or corrected. It is in the guard for the
  // same reason subjectLocked is in saveSubjectQuery's — once these boxes are
  // read-only a blur cannot carry a change worth writing.
  //
  // subject_specs_saved_at comes back from the row, never from the browser: a
  // BEFORE UPDATE trigger stamps it with the server's now() whenever one of the
  // five changes. specsLocked compares it with the assistant rows' created_at,
  // which is also server time, so both sides of that test share one clock. See
  // supabase/schema/2026-10-07_subject_specs_saved_at.sql.
  async function saveSpec(column, raw, setter) {
    if (!selectedPlace || !caseData || specsLocked) return
    const fromRow = () => (caseData[column] != null ? String(caseData[column]) : '')
    const trimmed = String(raw ?? '').trim()
    const typed = trimmed === '' ? null : Number(trimmed)
    // ANYTHING THAT IS NOT A POSITIVE NUMBER IS STORED AS NOTHING — all five,
    // not just the three required ones. A zero square footage, a negative year
    // or a NaN is not a figure the comp match can use: specsValid in pullComps
    // tests > 0 and would refuse the pull, so persisting one would leave a
    // column that reads as filled in and behaves as missing. Zero beds is the
    // same thing said about an optional field.
    const parsed = (typed === null || !Number.isFinite(typed) || typed <= 0) ? null : typed
    // The box follows the column. Something was typed and none of it is being
    // saved, so leaving it on screen would claim a value the row does not have
    // — and on the three required fields it would hide the gold border that
    // says they are still outstanding.
    if (parsed === null && trimmed !== '') setter('')
    if ((caseData[column] ?? null) === parsed) return
    const { data: saved, error } = await supabase.from('property_cases')
      .update({ [column]: parsed }).eq('id', caseId).select()
    // A driver error rolls the box back: the write may have half-happened and
    // what is on screen cannot be trusted. A REFUSAL does not — the value is
    // good for this session, and the toast says so.
    if (error) { showToast('Could not save that: ' + error.message); setter(fromRow()); return }
    if (!saved || saved.length === 0) { noteSubjectRefused(subjectRefusedRef, showToast); return }
    setCaseData(cd => (cd ? { ...cd, [column]: parsed, subject_specs_saved_at: saved[0].subject_specs_saved_at ?? null } : cd))
  }

  // ── APPRAISER REPORT: contract terms ──────────────────────────────────────
  // The form opens on this session's last submission if there is one, else on
  // the row. Every value is a string while it is in the form.
  function openContractForm() {
    const src = contractSession || caseData || {}
    const str = (v) => (v != null ? String(v) : '')
    // A stored percentage means the agent entered one: reopen in % mode on it.
    // Otherwise $ mode on the dollar figure.
    const hasPct = src.contract_seller_concession_pct != null
    setContractForm({
      price: str(src.contract_price),
      date: src.contract_date ? String(src.contract_date).slice(0, 10) : '',
      concessionMode: hasPct ? 'pct' : 'usd',
      concession: hasPct ? '' : str(src.contract_seller_concession),
      concessionPct: hasPct ? str(Number(src.contract_seller_concession_pct)) : '',
      loanType: src.contract_loan_type || '',
    })
    setContractErrors({})
  }

  // The contract price as a number, or null when the box does not hold a whole
  // dollar amount above zero. Shared by the validator and the % helper line.
  function contractPriceValue(f) {
    const price = f.price.trim()
    return (/^\d+$/.test(price) && Number(price) > 0) ? Number(price) : null
  }
  // The % box as a number, or null: 0 to 20 with at most two decimals.
  function contractPctValue(f) {
    const pct = f.concessionPct.trim()
    if (!/^\d+(\.\d{1,2})?$/.test(pct)) return null
    const n = Number(pct)
    return n <= 20 ? n : null
  }
  // The dollar figure a percentage comes to, rounded to the nearest dollar, or
  // null while either side is missing. This is what gets saved as
  // contract_seller_concession in % mode.
  function contractPctDollars(f) {
    const price = contractPriceValue(f)
    const pct = contractPctValue(f)
    return (price != null && pct != null) ? Math.round(price * pct / 100) : null
  }

  // Whole dollars only: digits, nothing else. A type="number" box hands back
  // "1.5" or "1e5" as typed, and neither is a contract figure.
  function contractFormErrors(f) {
    const errs = {}
    if (contractPriceValue(f) == null) errs.price = 'Enter the contract price in whole dollars.'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date)) errs.date = 'Enter the contract date.'
    if (f.concessionMode === 'pct') {
      if (contractPctValue(f) == null) errs.concession = 'Enter a percentage from 0 to 20.'
    } else if (!/^\d+$/.test(f.concession.trim())) {
      errs.concession = 'Enter the concession in whole dollars, or 0 for none.'
    }
    if (!APPRAISER_LOAN_TYPES.some(t => t.value === f.loanType)) errs.loanType = "Choose the buyer's loan type."
    return errs
  }

  // ONE writer for the four contract columns, shaped like saveSpec: .select()
  // and a row count, because an RLS refusal is zero rows with no error. Agent-
  // typed terms, storable under the same rule as the subject columns — see
  // supabase/schema/2026-10-03_appraiser_report.sql.
  //
  // A driver error keeps the form open on what was typed. A REFUSAL keeps the
  // values for this session and shows the one-per-view owner notice, the same
  // treatment saveSpec gives a shared agent.
  async function saveContractTerms() {
    if (!contractForm || contractSaving) return
    const errs = contractFormErrors(contractForm)
    setContractErrors(errs)
    if (Object.keys(errs).length > 0) return
    // The dollar figure is ALWAYS written — computed from the percentage in %
    // mode — so a reader of contract_seller_concession never has to know which
    // way the agent typed it. The percentage is written only in % mode and is
    // NULL in $ mode, which is also what tells the form how to reopen.
    const isPct = contractForm.concessionMode === 'pct'
    const patch = {
      contract_price: Number(contractForm.price.trim()),
      contract_date: contractForm.date,
      contract_seller_concession: isPct ? contractPctDollars(contractForm) : Number(contractForm.concession.trim()),
      contract_seller_concession_pct: isPct ? contractPctValue(contractForm) : null,
      contract_loan_type: contractForm.loanType,
    }
    setContractSaving(true)
    try {
      const { data: saved, error } = await supabase.from('property_cases')
        .update(patch).eq('id', caseId).select()
      if (error) { showToast('Could not save the contract terms: ' + error.message, true); return }
      setContractSession(patch)
      setContractForm(null)
      if (!saved || saved.length === 0) { noteContractRefused(contractRefusedRef, showToast); return }
      setCaseData(cd => (cd ? { ...cd, ...patch } : cd))
      showToast('Contract terms saved.')
    } finally {
      setContractSaving(false)
    }
  }

  function sanitizeFilename(name) {
    const lastDot = name.lastIndexOf('.')
    const base = lastDot > 0 ? name.slice(0, lastDot) : name
    const ext = lastDot > 0 ? name.slice(lastDot) : ''
    const cleanBase = base.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase()
    const cleanExt = ext.trim().toLowerCase().replace(/[^a-zA-Z0-9.]/g, '')
    return `${cleanBase || 'file'}${cleanExt}`
  }

  async function uploadFile(file, fileType) {
    setUploading(fileType)
    try {
      const safeName = sanitizeFilename(file.name)
      const folderPath = `${session.user.id}/${caseId}`
      const fullPath = `${folderPath}/${safeName}`

      // NOTHING IS REMOVED UNTIL THE REPLACEMENT IS COMMITTED. This used to
      // delete the previous object and its row FIRST, so a failed upload or a
      // failed insert left the agent with nothing at all: old file gone, old row
      // gone, new one absent or orphaned. Read now, act at the end.
      const { data: existingRows } = await supabase.from('property_case_files')
        .select('id, file_name, storage_path').eq('case_id', caseId).eq('file_type', fileType)

      // upsert:true, so a same-named replacement overwrites in place. The old
      // pre-clear of fullPath was only there to sweep a stale object off the
      // target path and is not needed for the write to land.
      const { error: upErr } = await supabase.storage.from('property-docs').upload(fullPath, file, { upsert: true })
      if (upErr) { showToast('Upload failed: ' + upErr.message); setUploading(null); return }

      // storage_path is the record of where the file lives; file_url stays
      // null and no link is created (schema 2026-10-10).
      const { data: row, error: insertErr } = await supabase.from('property_case_files')
        .insert({ case_id: caseId, file_type: fileType, storage_path: fullPath, file_name: safeName }).select().single()
      if (insertErr) {
        // Roll the object back so it is not left orphaned — but NOT when an
        // existing row already points at this exact path. Strategy does not
        // timestamp its filenames, so re-uploading the same name overwrites in
        // place; removing it there would leave that row referencing nothing,
        // which reads downstream as a silently missing document. Leaving it
        // means the row resolves to the new bytes: wrong content, but not a
        // dangling reference, and the agent still has a file.
        const stillReferenced = (existingRows || []).some(r => (r.storage_path || `${folderPath}/${r.file_name}`) === fullPath)
        if (!stillReferenced) {
          const { error: rmErr } = await supabase.storage.from('property-docs').remove([fullPath])
          if (rmErr) console.error('[strategy upload] rollback remove failed:', rmErr.message)
        }
        showToast('Upload failed: ' + insertErr.message)
        setUploading(null)
        return
      }

      setFiles(prev => [...prev.filter(f => !(f.case_id === caseId && f.file_type === fileType)), row])

      // Only now is the superseded copy removed. Row FIRST, then object: if the
      // row delete fails the object stays and the pair is still consistent,
      // whereas removing the object first would leave a row pointing at nothing.
      // fullPath is excluded from the object removal — on a same-named
      // replacement it is the file that was just written.
      const superseded = (existingRows || []).filter(r => r.id !== row.id)
      if (superseded.length > 0) {
        // .select() and a row count: an RLS refusal returns zero rows with no
        // error. Storage is removed only for the rows actually deleted.
        const { data: deletedRows, error: delErr } = await supabase.from('property_case_files')
          .delete().in('id', superseded.map(r => r.id)).select('id')
        if (delErr) {
          console.error('[strategy upload] superseded row(s) not removed:', delErr.message)
        } else {
          const deletedIds = new Set((deletedRows || []).map(r => r.id))
          if (deletedIds.size < superseded.length) {
            console.error('[strategy upload] superseded row(s) refused:', superseded.length - deletedIds.size, 'of', superseded.length)
          }
          const stalePaths = superseded
            .filter(r => deletedIds.has(r.id))
            .map(r => r.storage_path || `${folderPath}/${r.file_name}`)
            .filter(p => p !== fullPath)
          if (stalePaths.length > 0) {
            const { error: rmErr } = await supabase.storage.from('property-docs').remove(stalePaths)
            if (rmErr) console.error('[strategy upload] superseded object(s) not removed:', rmErr.message)
          }
        }
      }

      setUploading(null)
      showToast('File uploaded')
    } catch (e) {
      showToast('Upload failed: ' + (e.message || String(e)))
      setUploading(null)
    }
  }

  // ROW FIRST, with .select() and a row count. A delete refused by RLS is not
  // an error — PostgREST applies the policy as a filter and returns
  // { error: null } with zero rows — so the count is the only real signal.
  // This used to remove the storage object first and ignore the row result:
  // the row survived a refusal, came back on the next load pointing at a file
  // that was gone, and strategy-chat silently skipped that document.
  //
  // Storage is touched only after exactly one row was deleted. On a refusal
  // nothing changes: the object stays, the tile stays, and the agent is told.
  async function removeFile(fileId, fileName) {
    const { data: deletedRows, error: delErr } = await supabase.from('property_case_files')
      .delete().eq('id', fileId).select('id, storage_path')
    if (delErr || !deletedRows || deletedRows.length !== 1) {
      if (delErr) console.error('[strategy remove] row not removed:', delErr.message)
      showToast('Could not remove the file. It is still on this case.', true)
      return
    }
    // The stored path first: it is exact even when a shared user uploaded the
    // file under their own folder. The rebuilt path is for rows without one.
    const path = deletedRows[0].storage_path || `${session.user.id}/${caseId}/${sanitizeFilename(fileName)}`
    const { error: rmErr } = await supabase.storage.from('property-docs').remove([path])
    if (rmErr) console.error('[strategy remove] object not removed:', rmErr.message)
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  function renderInline(text) {
    // Split on **bold** and *italic*, process in order
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>
      return part
    })
  }

  function renderMarkdown(text) {
    if (!text) return null
    const lines = text.split('\n')
    const elements = []
    let i = 0
    while (i < lines.length) {
      const line = lines[i]
      const trimmed = line.trim()

      // Skip empty lines
      if (trimmed === '') { i++; continue }

      // Horizontal rule
      if (/^-{3,}$/.test(trimmed)) {
        elements.push(<hr key={i} style={{border:'none',borderTop:'1px solid #e8e8e8',margin:'16px 0'}} />)
        i++; continue
      }

      // Headings
      if (trimmed.startsWith('### ')) {
        elements.push(<div key={i} style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',margin:'12px 0 6px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(4))}</div>)
        i++; continue
      }
      if (trimmed.startsWith('## ')) {
        elements.push(<div key={i} style={{fontSize:'18px',fontWeight:700,color:'#1d2e42',margin:'14px 0 8px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(3))}</div>)
        i++; continue
      }
      if (trimmed.startsWith('# ')) {
        elements.push(<div key={i} style={{fontSize:'24px',fontWeight:700,color:'#1d2e42',margin:'16px 0 10px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(2))}</div>)
        i++; continue
      }

      // Table
      if (trimmed.includes('|') && trimmed.startsWith('|')) {
        const tableRows = []
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          const row = lines[i].trim()
          if (!/^[\s|:-]+$/.test(row)) { // skip separator rows like |---|---|
            const cells = row.split('|').filter(c => c.trim() !== '').map(c => c.trim())
            tableRows.push(cells)
          }
          i++
        }
        if (tableRows.length > 0) {
          const headerRow = tableRows[0]
          const bodyRows = tableRows.slice(1)
          elements.push(
            <div key={'tbl'+i} style={{overflowX:'auto',WebkitOverflowScrolling:'touch',marginTop:'8px',marginBottom:'12px',maxWidth:'100%'}}>
              <table style={{borderCollapse:'collapse',minWidth:'100%',fontSize:'13px',fontFamily:FONT_BODY}}>
                <thead>
                  <tr>{headerRow.map((cell,ci) => <th key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px',background:'#f5f5f5',fontWeight:700,textAlign:'left',whiteSpace:'nowrap'}}>{renderInline(cell)}</th>)}</tr>
                </thead>
                <tbody>
                  {bodyRows.map((row,ri) => <tr key={ri}>{row.map((cell,ci) => <td key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px'}}>{renderInline(cell)}</td>)}</tr>)}
                </tbody>
              </table>
            </div>
          )
        }
        continue
      }

      // Unordered list — see collectListItems: blank lines between items must
      // not split the run.
      if (trimmed.startsWith('- ')) {
        const items = collectListItems(lines, i, l => l.startsWith('- '), l => l.slice(2))
        i = items.next
        elements.push(<ul key={'ul'+i} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderInline(item)}</li>)}</ul>)
        continue
      }

      // Ordered list — a split run restarts numbering at 1 on every fragment.
      if (/^\d+\.\s/.test(trimmed)) {
        const items = collectListItems(lines, i, l => /^\d+\.\s/.test(l), l => l.replace(/^\d+\.\s/, ''))
        i = items.next
        elements.push(<ol key={'ol'+i} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderInline(item)}</li>)}</ol>)
        continue
      }

      // Paragraph — collect consecutive non-special lines
      let para = trimmed
      i++
      while (i < lines.length && lines[i].trim() !== '' && !lines[i].trim().startsWith('#') && !lines[i].trim().startsWith('- ') && !/^\d+\.\s/.test(lines[i].trim()) && !lines[i].trim().startsWith('|') && !/^-{3,}$/.test(lines[i].trim())) {
        para += ' ' + lines[i].trim()
        i++
      }
      elements.push(<p key={'p'+i} style={{margin:'8px 0',lineHeight:1.65}}>{renderInline(para)}</p>)
    }
    return elements
  }

  // textArg is optional and is the shape PipelineView and AdaCaseView already
  // use. A caller that has the text in hand passes it directly rather than
  // writing it to state and hoping a later call reads it back — which is what
  // retry() used to do, through a setTimeout that captured this function from
  // the previous render and therefore read the previous render's `input`.
  //
  // Deal Desk's sendMessage is deliberately left alone: nothing there
  // primes-then-sends, so it has no caller that needs this.
  async function sendMessage(textArg) {
    const userMsg = (typeof textArg === 'string' ? textArg : input).trim()
    if (!userMsg || sending) return
    // Only when the BOX was the source. A retry passes its own text and must
    // not clear a composer the agent may have typed into since the failure —
    // and must not un-prime one that is still holding the Analyze prompt.
    if (typeof textArg !== 'string') {
      setInput('')
      // The box no longer holds the Analyze prompt, so step 3 stops reading
      // "primed" and starts reading "analyzed" once the reply lands.
      setPrimed(false)
    }
    setError(null)
    const tempUserId = 'temp-user-' + Date.now()
    const tempAssistantId = 'temp-assistant-' + Date.now()
    const tempUserMsg = { id: tempUserId, role: 'user', content: userMsg, created_at: new Date().toISOString() }
    const tempAssistantMsg = { id: tempAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg])
    // New chat input changes the analysis basis — drop the cached report so the next
    // report re-analyzes instead of recomposing a now-stale one.
    cachedReportRef.current = null
    setSending(true)

    try {
      let edgeFnMsg = userMsg
      const prefixLines = []
      if (propertyType) prefixLines.push(`[Property type: ${propertyType}]`)
      if (clientType) prefixLines.push(`[Client type: ${clientType}]`)
      if (condition) prefixLines.push(`[Property condition: ${condition}]`)
      if (prefixLines.length > 0) edgeFnMsg = prefixLines.join('\n') + '\n\n' + userMsg

      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token

      // The ANALYSIS carries a fingerprint of the pull it ran on, so a later
      // re-pull can be told apart from the comps the analysis saw. Recognised
      // by the Analyze prompt's prefix rather than `primed`, which a retry does
      // not carry. Counts and settings only — see compPullFingerprint.
      const pullFingerprint = userMsg.startsWith(STRATEGY_ANALYZE_PROMPT) && compResult
        ? compPullFingerprint(compResult, compPullOverrides)
        : null

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/strategy-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        // comp_session carries the comps for the life of this session only.
        // property_cases.comp_pull_result used to be the transport as well as
        // the store — strategy-chat read the comps back out of the column. The
        // column now keeps counts only, per ARMLS retention, so without this the
        // model would be told the data was not retained while the agent was
        // still looking at it on screen.
        //
        // Sent from state on every turn while a pull is live, and never written
        // anywhere: it exists server-side for the duration of one request. That
        // is stricter than the column was, not looser.
        //
        // Three keys explicitly, not compResult wholesale — the response also
        // carries `ok` and an `_audit` block of identifiers the server issued,
        // and there is no reason to send those back. Spread conditionally so a
        // case with no pull sends a byte-identical body to before.
        body: JSON.stringify({
          case_id: caseId,
          user_message: edgeFnMsg,
          ...(compResult ? {
            comp_session: {
              subject: compResult.subject,
              groups: compResult.groups,
              _meta: compResult._meta,
            },
          } : {}),
          // strategy-chat writes this on the assistant row it inserts, as
          // sources_used.pull_fingerprint. Absent on every other turn.
          ...(pullFingerprint ? { pull_fingerprint: pullFingerprint } : {}),
        }),
      })

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulatedText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (!payload) continue
          try {
            const evt = JSON.parse(payload)
            if (evt.text) {
              accumulatedText += evt.text
              const updatedText = accumulatedText
              setMessages(prev => prev.map(m =>
                m.id === tempAssistantId ? { ...m, content: updatedText } : m
              ))
            }
            if (evt.error) throw new Error(evt.error)
            if (evt.done) {
              // Stream complete — refresh from DB
              const { data: fresh } = await supabase.from('property_case_messages').select('*').eq('case_id', caseId).order('created_at')
              if (fresh) setMessages(fresh)
            }
          } catch (parseErr) {
            if (String(parseErr).includes('Error')) throw parseErr
          }
        }
      }
    } catch (e) {
      setError({ message: e.message, retryMsg: userMsg })
      setMessages(prev => prev.filter(m => m.id !== tempAssistantId))
    }
    setSending(false)
  }

  function retry() {
    if (!error) return
    // Straight in, not through the box. sendMessage clears the composer before
    // the request, so by the time a retry is possible `input` is empty — the
    // old setInput-then-timeout wrote the message somewhere the timeout's
    // stale closure could never see it, and every retry silently did nothing.
    sendMessage(error.retryMsg)
    setError(null)
  }

  const FILE_ZONES = [
    { type: 'cma', label: 'Comps / CMA', sublabel: 'ARMLS comp report', nudge: 'Unlocks Mode A — sharper analysis, real comp data, fewer guesses.', accept: '.pdf', badge: 'RECOMMENDED' },
    { type: 'mls_sheet', label: 'MLS Sheet', sublabel: 'Subject property listing', nudge: null, accept: '.pdf', badge: null },
    { type: 'tax_record', label: 'Tax Record', sublabel: 'Maricopa County assessor', nudge: null, accept: '.pdf', badge: null },
  ]

  const baseDropStyle = { padding:'14px', borderRadius:'10px', cursor:'pointer', minHeight:'64px', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s' }
  const emptyDropStyle = { ...baseDropStyle, border:'2px dashed #ccc', background:'#fafafa' }
  const uploadedDropStyle = { ...baseDropStyle, border:'2px solid #2d7a2d', background:'#f0f9f0', justifyContent:'flex-start' }

  // ── Context-pill helpers, hoisted to component scope so they can be rendered
  //    in both the Subject Property card and any other consumer in this view.
  const locked = messages.length > 0

  // The subject's own lock, and it is NOT bare `locked`. Keyed on what the ROW
  // holds, the way wasSetAtLoad keys the pills on how the case arrived: a case
  // that reached `locked` without a real subject stays editable, because
  // freezing an empty address field would leave it with no way to pull comps
  // and therefore no way to ever produce a report. That is the same dead end
  // the pills' `frozen` rule exists to avoid.
  //
  // A real subject is one of two things: persisted Google coordinates, or a
  // subject_query that is all digits, which is an MLS number. Anything else —
  // null, blank, or half-typed text from before these columns existed — is not
  // a subject the analysis can be said to have run against.
  //
  // caseData, not local state, and the three writers (place_changed,
  // saveSubjectQuery, saveSpec) all update caseData on success. So a legacy
  // case locks the moment the agent re-picks its address, with no reload.
  const subjectLocked = locked && (
    (caseData?.subject_lat != null && caseData?.subject_lng != null) ||
    /^\d+$/.test((caseData?.subject_query || '').trim())
  )
  // The specs lock only once an analysis has RUN ON THEM: all three REQUIRED
  // ones are on the row, and an assistant reply was saved after the last time
  // any of the five changed. Saving the third one does not lock them — an
  // agent who mistypes the square footage can correct it until the next
  // analysis lands. Bare `locked` (any message at all) is not enough, because
  // on an older case the chat already has messages from before these figures
  // were typed, and the boxes froze the moment the third one saved.
  //
  // subject_specs_saved_at is stamped by a trigger with the server's now()
  // whenever one of the five changes, and set back to null when all five are
  // cleared. Null means nothing to compare against, so nothing is locked.
  // Picking an address nulls the five (a new place invalidates the old
  // figures), so the boxes reopen for the agent to refill.
  //
  // Server rows only. The optimistic user message and the streaming assistant
  // placeholder carry a created_at from the browser clock and a temp- id; the
  // stream's done event replaces them with the rows from the table, and that
  // is when the lock can close.
  //
  // > 0, the SAME test pullComps applies to the live boxes as `specsValid`:
  //
  //   specsValid  = Number(specSqft) > 0 && Number(specStories) > 0 && Number(specYearBuilt) > 0
  //   specsSet    = Number(caseData?.subject_sqft) > 0 && ... (below)
  //
  // Same rule, different source — one reads what is on screen, the other what
  // is on the row. They have to agree, because the lock is supposed to mean
  // "an analysis ran on these, and a pull can run on them". A `!= null` test would
  // freeze the boxes on a stored 0, which pullComps would then refuse, leaving
  // an agent looking at three filled, uneditable fields and a Pull comps
  // button that will not fire. Number(null) is 0 and Number(undefined) is NaN,
  // and neither is > 0, so a missing column still reads as unset.
  const specsSet =
    Number(caseData?.subject_sqft) > 0 &&
    Number(caseData?.subject_year_built) > 0 &&
    Number(caseData?.subject_stories) > 0
  const specsSavedAt = caseData?.subject_specs_saved_at ? Date.parse(caseData.subject_specs_saved_at) : NaN
  const analyzedSinceSpecs = Number.isFinite(specsSavedAt) && messages.some(m =>
    m.role === 'assistant' && !m.streaming && m.created_at &&
    !String(m.id).startsWith('temp-') &&
    Date.parse(m.created_at) > specsSavedAt
  )
  const specsLocked = subjectLocked && specsSet && analyzedSinceSpecs

  // Readiness. Component scope rather than inside the comps block, because it
  // now gates the composer as well as the Analyze button. Strategy analyses
  // comps for a specific property and client — it is not a market-stats tool,
  // so a question asked without all three has no basis to answer from.
  const missing = []
  if (!propertyType) missing.push('property type')
  if (!clientType) missing.push('client type')
  if (!condition) missing.push('condition')
  const ready = missing.length === 0

  // Gates the "Locked" notice, which is only true when nothing is editable.
  // On a repaired case — one that reached `locked` with a group still empty —
  // some pills are clickable, and a notice saying they cannot be changed sits
  // directly above pills that can. The clickable pills say what the agent needs
  // to know, so the notice simply does not render there.
  const allContextSetAtLoad = contextAtLoadRef.current
    ? contextAtLoadRef.current.pt && contextAtLoadRef.current.ct && contextAtLoadRef.current.pc
    : ready
  const PROP_TYPE_OPTS = [
    { key: 'Single Family', tip: 'Detached single-family residence on its own lot' },
    { key: 'Condo / Townhouse', tip: 'Attached or shared-wall homes — townhouses, patio homes, apartments/condos, twins, lofts' },
    { key: 'Manufactured / Mobile', tip: 'Manufactured, mobile, and modular/pre-fab housing' },
    { key: 'Land', tip: 'Vacant lot or parcel — broad comp pull; analysis focuses on zoning, utilities, access, and lot size' },
  ]
  const CLIENT_OPTS = [
    { key: 'Seller', tip: 'Helping a seller decide whether to accept an offer or how to negotiate' },
    { key: 'Buyer', tip: 'Representing a buyer who will live in or use the property' },
    { key: 'First-time buyer', tip: 'Buyer with no prior home purchase experience — needs more education and FHA/down-payment context' },
    { key: 'Investor', tip: 'Buyer underwriting for profit (flip, BRRRR, or rental). Pure deal math.' },
    { key: 'Listing agent', tip: 'Pricing your own listing — where should we list this?' },
  ]
  const COND_OPTS = [
    { key: 'Distressed', tip: 'Needs full remodel, has major systems issues (roof, HVAC, plumbing), pool problems, or REO/as-is' },
    { key: 'Original', tip: 'Livable but dated — original kitchen and baths, no significant updates done' },
    { key: 'Partially updated', tip: 'Some remodel work done (e.g., kitchen but not baths), or work was done 5+ years ago' },
    { key: 'Fully remodeled', tip: 'Comprehensive remodel completed within the last 12 months — modern kitchen, baths, flooring, paint' },
  ]
  // The three context selections are durable now. One writer, so the icon cards
  // and the condition bar cannot drift: optimistic set first to keep the click
  // instant, then the write, then roll back to the previous value on failure.
  async function persistContext(column, setter, prev, next) {
    setter(next)
    const { error: err } = await supabase.from('property_cases')
      .update({ [column]: next }).eq('id', caseId)
    if (err) {
      showToast('Could not save that selection')
      setter(prev)
      return
    }
    setCaseData(cd => (cd ? { ...cd, [column]: next } : cd))
  }

  const choosePropertyType = v => persistContext('property_type', setPropertyType, propertyType, v)
  const chooseClientType = v => persistContext('client_type', setClientType, clientType, v)
  const chooseCondition = v => persistContext('property_condition', setCondition, condition, v)

  // dealdeskPill, not a local copy. showIcon is gone — the caller either passes
  // an icon or does not, which is the same decision expressed once.
  //
  // ONE guard, not two. The old version returned early on `disabled` and then
  // again on `locked`, and the second was doing real work: disabled is
  // `locked && !sel`, so without it a locked agent could still DESELECT the
  // option they had chosen. Locked means locked, including deselect, so the
  // guard now covers both cases by itself.
  //
  // `frozen` is derived once and used by BOTH the click guard and the disabled
  // flag, because the two have to agree and previously only happened to. It
  // keys on how the case ARRIVED, not on the current value: a group that was
  // empty on load stays editable even after the agent fills it. Otherwise the
  // readiness gate was unsatisfiable — it demanded three values while `locked`
  // disabled the pills that set them, and freezing on first click would have
  // turned a misclick into a second dead end. A group that loaded with a value
  // is locked exactly as before.
  const wasSetAtLoad = (group, value) =>
    contextAtLoadRef.current ? !!contextAtLoadRef.current[group] : value != null


  // Unchanged from the pre-shell version, including the anchor: Strategy pins
  // the question to the top, not the answer.
  // 0.5px rule after every assistant turn except the last, so one answer is
  // separable from the next now that the bubble is gone. isLast is computed from
  // `messages`, which every tool already has — renderMessage's (m, streamingRef)
  // signature is shared by four callers and stays as it is.
  const renderMessage = (m, streamingRef) => (
    <div key={m.id} ref={typeof m.id === 'string' && m.id.startsWith('temp-user-') ? streamingRef : null}>
      <div style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
        {m.role === 'user' ? (
          <div style={{maxWidth:'80%',background:'#f4f4f2',color:'#1d2e42',borderRadius:'14px',padding:'12px 16px',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY}}>
            {stripDirectives(m.content)}
          </div>
        ) : (
          // Full column, no panel. The answer is the document, not an aside.
          <div style={{width:'100%',fontSize:'14px',lineHeight:1.65,color:'#1d2e42',fontFamily:FONT_BODY}}>
            {m.streaming && !m.content ? (
              <ThinkingRow label="Analyzing documents…" />
            ) : renderMarkdown(m.content)}
            {m.streaming && <span style={{animation:'streamCursor 1s infinite',fontWeight:700}}>▋</span>}
          </div>
        )}
      </div>
      {m.role === 'assistant' && messages[messages.length - 1]?.id !== m.id && (
        <div style={{height:'0.5px',background:'#e8e8e8',marginTop:'18px'}} />
      )}
    </div>
  )

  // Built here rather than at module scope: the first action's disabled state
  // depends on compResult, which is component state. Everything else follows
  // Pipeline's rule — a price or an address the tool cannot guess PRIMES the
  // composer instead of firing.
  const emptyState = (
    <div style={{paddingTop:'32px'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:'38px',height:'38px',borderRadius:'10px',background:'#E4EDF7',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
          <StrategistIcon size={19} color="#123763" />
        </div>
        <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'5px'}}>
          What do you need to know about this property?
        </div>
        <div style={{fontSize:'11.5px',color:'#666',fontFamily:FONT_BODY}}>
          Pull comps first, or ask anything about pricing and strategy.
        </div>
      </div>
    </div>
  )

  // Only the parts that exist, comp count pushed right. State hydrates from the
  // three columns on mount, so it wins; contextFromHistory stays as the fallback
  // for cases last touched before those columns existed, which have nothing to
  // hydrate from. Renders above the transcript whether setup is open or closed —
  // with the directives hidden from the bubbles, this is now the only place the
  // agent can see the context an answer was built on.
  const histCtx = contextFromHistory(messages)
  const summaryBits = [
    propertyType || histCtx['property type'],
    clientType || histCtx['client type'],
    condition || histCtx['property condition'],
  ].filter(Boolean)
  const compCount = compResult?._meta?.total_comps ?? caseData?.comp_pull_result?._meta?.total_comps

  // ── STEP MARKERS ──────────────────────────────────────────────────────────
  // Five numbered markers on the five things an agent actually does. Agents
  // were missing Analyze because it lives inside the comps panel and nothing on
  // the page pointed at it; exactly one gold badge is what fixes that.
  //
  // NOT `locked`. locked is messages.length > 0 and turns true the moment the
  // agent types — a marker keyed to it would go grey on the question rather
  // than on the answer. Step 3 is done when the reply exists.
  const analyzed = messages.some(m => m.role === 'assistant')

  // Do the comps on screen differ from the ones the latest analysis ran on?
  // Only when that analysis recorded a fingerprint — older analyses have none,
  // and for them nothing changes. When they differ, Generate Report is
  // stopped: a report built on a different pool can name sales its tables
  // cannot show (12539 W Tuckey Ln, Oct 10).
  const compsDifferFromAnalysis = pullDiffersFromAnalysis(messages, compResult, compPullOverrides)
  // PREFIX, matching caseFacts in the case list rather than restating the rule
  // a second way. The live table still holds strategy_report_agent_current and
  // _initial rows from the old two-PDF system; those still mean a report
  // exists, and a marker claiming otherwise would be wrong about history.
  const hasReport = files.some(f => (f.file_type || '').startsWith('strategy_report_'))
  // Five steps, five signals — no two share one any more. Analyze fills the box
  // (3), sending gets the reply (4), the report is its own act (5).
  //
  // Step 3 is primed OR analyzed. An agent who typed their own question and
  // sent it never pressed Analyze, but they did not miss a step — and once a
  // reply exists, how the box got filled stops mattering.
  //
  // TWO entries do NOT survive a reload, and both are correct.
  //
  // Step 1 reads `compResult`, the in-memory comp payload, so it resets on a
  // reload, a sign-out, or a different computer. That is by design: comp data
  // is session-only per ARMLS licensing, so a case whose payload is gone
  // genuinely cannot produce a report and the marker has to say so. `compCount`
  // still falls back to the persisted comp_pull_result counts, but that is the
  // summary strip's number — deliberately not this marker's.
  //
  // `primed` resets too, for its own reason: it describes the contents of the
  // composer, which is also gone. After a reload `analyzed` carries step 3 on
  // any case that got a reply.
  //
  // The other entries hydrate from persisted state — the three context values
  // from their columns, messages and files from the case-load effect.
  const stepDone = [!!compResult, ready, primed || analyzed, analyzed, hasReport]
  // WALKED IN ORDER, first incomplete wins — deliberately not a count of what
  // is finished. Analyze is disabled until all three pills are set, so an agent
  // who pulled comps without setting them has step 2 as their next action even
  // though step 1 is done. Order expresses that; arithmetic would not.
  const nextStepIdx = stepDone.findIndex(d => !d)
  const stepState = (i) =>
    nextStepIdx === -1 ? 'done' : i < nextStepIdx ? 'done' : i === nextStepIdx ? 'next' : 'later'

  // Setup folds when the first reply lands. The case-load effect already closes
  // it for a case that arrives with a transcript; this is the IN-SESSION half —
  // the agent sends, the answer comes back, and the setup they are done with
  // gets out of the way.
  //
  // Keyed on `analyzed`, NOT on `locked`. locked is messages.length > 0 and
  // turns true the moment the question is sent, folding setup while the agent
  // is still waiting; the design folds it when the ANSWER arrives.
  //
  // Fires once on the false → true edge and never again while it stays true, so
  // an agent who reopens setup afterwards is not fought. Placed here rather than
  // beside the other effects because `analyzed` is declared above and a
  // dependency array is evaluated during render — referencing it earlier would
  // be a temporal dead zone.
  useEffect(() => {
    if (analyzed) setSetupOpen(false)
  }, [analyzed])
  // Always renders now, because it also carries the help toggle. `summary` is the
  // only node the shell puts in the title row, so the link rides along here
  // rather than adding a prop to CaseShell.
  const summaryNode = (
    <div style={{display:'flex',alignItems:'center',gap:'10px',fontSize:'12px',color:'#555',fontFamily:FONT_BODY,minWidth:0}}>
      {summaryBits.length > 0 && (
        <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{summaryBits.join(' · ')}</span>
      )}
      {compCount != null && (
        <span style={{flexShrink:0,color:'#999'}}>{compCount} comps</span>
      )}
      {/* setHelpSeen is the same flag the ✕ persists via dismissHelp, so
          toggling here leaves the "don't show again" behaviour intact.
          The help panel lives inside setup, so with setup closed the link
          opens setup AND shows the panel; with setup open it toggles as
          before. Closing help never closes setup. */}
      <button
        onClick={()=>{ if (!setupOpen) { setSetupOpen(true); setHelpSeen(false) } else setHelpSeen(v => !v) }}
        style={{marginLeft:'auto',flexShrink:0,background:'none',border:'none',padding:0,cursor:'pointer',color:'#147efb',fontSize:'12px',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>
        How to use this
      </button>
    </div>
  )

  // Both segmented controls come from the same two helpers so they cannot drift
  // in padding, font, or height. They read as quiet outlined pills — the gold
  // Generate report is the only filled control in the row.
  //
  // CTRL_PAD_Y is shared with Generate report rather than a fixed pixel height:
  // both then derive their box from the same vertical padding plus their own
  // line box, so they stay level without one hard-coding the other's size.
  const CTRL_PAD_Y = '4px'
  // White fill so the toggles read against the tinted card. The border was
  // already 0.5px #d8d8d4 — unchanged.
  const segWrap = { display:'flex', alignItems:'center', background:'#ffffff', border:'0.5px solid #d8d8d4', borderRadius:'16px', overflow:'hidden', flexShrink:0 }

  // First letter of the first two words. 'Ana Leslie Ruiz' -> 'AL', 'Ana' -> 'A'.
  const initialsOf = (name) => String(name || '').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase()
  const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || ''
  // `divider` draws the hairline between the two halves. It is load-bearing
  // now: both segments are white, so the only other thing separating them is
  // the text colour.
  const segBtn = (active, divider = false) => ({
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:`${CTRL_PAD_Y} 11px`, fontSize:'11.5px', fontWeight:600, fontFamily:FONT_BODY,
    border:'none', whiteSpace:'nowrap',
    ...(divider ? { borderRight:'0.5px solid #e8e8e8' } : {}),
    cursor:generatingCurrent?'not-allowed':'pointer',
    background: active ? '#ffffff' : 'transparent',
    color: active ? '#1d2e42' : '#888',
  })

  // reportNoticeStrip and reportNoticeIcon are module level now (next to
  // STRATEGY_ANALYZE_PROMPT), shared with Market Snapshot and My Wins.
  const controlsNode = (
    <div style={{padding:'12px 20px 10px',display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
      {/* The error box lived at the end of the old transcript. The shell renders
          messages and nothing after them, so it sits above the composer now —
          still the last thing above the input, still carrying RETRY. */}
      {error && (
        <div style={{...S.errorBox,width:'100%',maxWidth:STRATEGY_CONTENT_WIDTH,marginBottom:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span>{error.message}</span>
          <button onClick={retry} style={{background:'none',border:'none',color:'#147efb',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,fontSize:'12px',letterSpacing:'0.5px'}}>RETRY</button>
        </div>
      )}
      {/* A card, so the comp tables scrolling behind stop bleeding into the
          buttons. boxSizing border-box because the padding would otherwise push
          the row 28px past STRATEGY_CONTENT_WIDTH and break alignment with the
          transcript above it. A column: the one control row, then any notice
          strips under it. Vertical padding is small so that with no notice
          the card is one row tall. */}
      <div style={{width:'100%',maxWidth:STRATEGY_CONTENT_WIDTH,boxSizing:'border-box',
        background:'#f4f4f2',border:'1px solid #d8d8d4',borderRadius:'12px',padding:'6px 14px',
        display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',flexDirection:'row',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
          {/* Step 4's marker used to sit here. It moved to the composer's send
              button, because sending is the step — this bar is step 5, and it
              now carries only that one. */}
          <span style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY,flexShrink:0}}>Report:</span>
          <div style={segWrap}>
            <button
              onClick={() => setReportLanguage('en')}
              disabled={generatingCurrent}
              style={segBtn(reportLanguage==='en', true)}>
              English
            </button>
            <button
              onClick={() => setReportLanguage('es')}
              disabled={generatingCurrent}
              style={segBtn(reportLanguage==='es')}>
              Español
            </button>
          </div>
          {/* TEAM BRANDING — absent entirely unless the agent has teammates. The
              agent's own avatar is always lit and inert; a teammate toggles, and
              picking a second replaces the first rather than adding to it. */}
          {teammates.length > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                {[{ ...profile, __self: true }, ...teammates].map(person => {
                  const isSelf = !!person.__self
                  const on = isSelf || person.armls_agent_id === selectedMateId
                  const common = {
                    width:'27px', height:'27px', borderRadius:'50%', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxSizing:'border-box', overflow:'hidden', padding:0,
                    fontSize:'10px', fontWeight:700, fontFamily:FONT_HEAD, letterSpacing:'0.3px',
                    cursor: isSelf ? 'default' : 'pointer', transition:'all .15s',
                    // #ffffff, not #f4f4f2: that tint is now the card behind it,
                    // so an unselected avatar would vanish into its own background.
                    border: on ? '1.5px solid #147efb' : '1.5px dashed #d8d8d4',
                    background: on ? '#147efb' : '#ffffff',
                    color: on ? '#ffffff' : '#999',
                  }
                  return (
                    <button
                      key={isSelf ? '__self' : person.armls_agent_id}
                      type="button"
                      title={person.full_name || ''}
                      aria-pressed={on}
                      disabled={isSelf}
                      onClick={()=>{ if (isSelf) return; setSelectedMateId(prev => prev === person.armls_agent_id ? null : person.armls_agent_id) }}
                      style={common}>
                      {person.avatar_url
                        ? <img src={person.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                        : initialsOf(person.full_name)}
                    </button>
                  )
                })}
              </div>
              <div style={{fontSize:'10.5px',color:'#999',fontFamily:FONT_BODY,whiteSpace:'nowrap'}}>
                Signed by {[firstNameOf(profile?.full_name), selectedMate && firstNameOf(selectedMate.full_name)].filter(Boolean).join(' and ')}
              </div>
            </div>
          )}
          {/* marginLeft auto pushes this to the right end of the row. */}
          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'14px',flexShrink:0}}>
            <button
              onClick={() => generateReport('current', reportLanguage)}
              disabled={generatingCurrent || !profile?.brokerage || !compResult || compsDifferFromAnalysis}
              style={{...S.btnPrimary,padding:`${CTRL_PAD_Y} 14px`,fontSize:'12px',flex:'none',whiteSpace:'nowrap',background:'#fdbf00',color:'#1d2e42',opacity:(generatingCurrent||!profile?.brokerage||!compResult||compsDifferFromAnalysis)?0.5:1}}>
              {/* STEP 5, inside the button and before its text. onGold because
                  this button IS #fdbf00 — without the inversion the badge is gold
                  on gold and vanishes exactly when it turns 'next'. */}
              <span style={{marginRight:'7px',display:'inline-flex'}}><StepMarker n={5} state={stepState(4)} onGold /></span>
              {generatingCurrent ? 'Generating…' : 'Generate Report'}
            </button>
            {profile?.role === 'admin' && (
              <button
                onClick={() => setShareModalOpen(true)}
                style={{background:'none',border:'none',padding:0,color:'#147efb',fontSize:'12px',fontFamily:FONT_BODY,cursor:'pointer',flexShrink:0,whiteSpace:'nowrap'}}>
                Share with Agent{sharedWith.length > 0 ? ' (' + sharedWith.length + ')' : ''}
              </button>
            )}
          </div>
        </div>
        {/* Why the report cannot be generated right now. Below the row, not in
            it, so the row stays one line and the buttons hold their place. */}
        {!compResult && (mlsLicensed ? (
          <div style={reportNoticeStrip}>
            {reportNoticeIcon}
            <span><span style={{ fontWeight: 500 }}>Comps aren't loaded.</span> Pull comps again to generate the report.</span>
          </div>
        ) : (
          <div style={reportNoticeStrip}>
            {reportNoticeIcon}
            <span><span style={{ fontWeight: 500 }}>Live MLS comps aren't available for your brokerage yet.</span> Reports from an uploaded CMA are coming soon.</span>
          </div>
        ))}
        {/* A stop, not advice: Generate is disabled while this shows. Same
            strip as the notice above, which it can never appear alongside
            (this one needs compResult, that one needs it absent). */}
        {compsDifferFromAnalysis && (
          <div style={reportNoticeStrip}>
            {reportNoticeIcon}
            <span>These comps differ from the ones the analysis used. Run "Analyze these comps" again to generate a report.</span>
          </div>
        )}
      </div>
    </div>
  )

  const setupNode = (
    <div style={{maxWidth:SETUP_CONTENT_WIDTH,display:'flex',flexDirection:'column',gap:'18px'}}>

        {/* HELP — toggled from the link in the title row, not a standing bar */}
        {!helpSeen && (
          <div style={{background:'linear-gradient(135deg, #1d2e42 0%, #2a4060 100%)',border:'1px solid #fdbf00',borderRadius:'12px',padding:'20px 24px',marginBottom:'20px',position:'relative',color:'white',fontFamily:FONT_BODY}}>
            <button onClick={dismissHelp} title="Don't show this again" style={{position:'absolute',top:'12px',right:'12px',background:'transparent',border:'none',color:'rgba(255,255,255,0.6)',fontSize:'18px',cursor:'pointer',padding:'4px 8px',borderRadius:'4px'}}>✕</button>
            <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',marginBottom:'8px',fontFamily:FONT_HEAD}}>HOW TO USE THIS · RECOMMENDED</div>
            <div style={{fontSize:'15px',fontWeight:700,color:'white',marginBottom:'12px',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>This is a strategy tool. The more context you give it, the sharper the analysis.</div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px',fontSize:'13px',lineHeight:1.5,color:'#cbd5e1'}}>
              <div><span style={{color:'#fdbf00',fontWeight:700}}>1.</span> <strong style={{color:'white'}}>Start with the property.</strong> The best way is the MLS number. Enter it and the subject's details come in from live MLS data. No MLS number? Enter the address, then type the square footage, year built, and number of stories. Those three drive the comp match. Beds and baths are optional.</div>
              <div><span style={{color:'#fdbf00',fontWeight:700}}>2.</span> <strong style={{color:'white'}}>Set the context.</strong> Choose the property type, your client, and the condition. They shape the analysis.</div>
              <div><span style={{color:'#fdbf00',fontWeight:700}}>3.</span> <strong style={{color:'white'}}>Pull comps.</strong> You get closed, pending, and active homes near the subject, matched on size, age, and stories. Too few? Use "Widen the search."</div>
              <div><span style={{color:'#fdbf00',fontWeight:700}}>4.</span> <strong style={{color:'white'}}>Analyze, then ask.</strong> Click "Analyze these comps" and send it. Then be direct: "What should I list this at?" or "What's a strong offer for my buyer?" Push back when you disagree. It gets sharper with conversation.</div>
              <div><span style={{color:'#fdbf00',fontWeight:700}}>5.</span> <strong style={{color:'white'}}>Generate the report.</strong> The report prints the sales the analysis used, with the numbers taken straight from live MLS data.</div>
            </div>
            {/* Folded by default. The chevron is the setup toggle's, in the heading's gold. */}
            <button
              type="button"
              onClick={() => setGoodToKnowOpen(o => !o)}
              aria-expanded={goodToKnowOpen}
              aria-controls="strategy-help-good-to-know"
              style={{display:'inline-flex',alignItems:'center',gap:'5px',background:'transparent',border:'none',padding:0,cursor:'pointer',fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',marginTop:'16px',marginBottom:goodToKnowOpen ? '8px' : 0,fontFamily:FONT_HEAD}}>
              Good to know
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdbf00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {goodToKnowOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
            {goodToKnowOpen && (
            <div id="strategy-help-good-to-know" style={{display:'flex',flexDirection:'column',gap:'10px',fontSize:'13px',lineHeight:1.5,color:'#cbd5e1'}}>
              <div><strong style={{color:'white'}}>Comps aren't saved.</strong> MLS data clears when you reload, sign out, or switch computers. Pull comps again when you come back. If the comps changed, run "Analyze these comps" again before generating.</div>
              <div><strong style={{color:'white'}}>Documents add depth.</strong> Upload a tax record, an MLS sheet, or your own CMA and the analysis reads them. A report still needs a live comp pull.</div>
              <div><strong style={{color:'white'}}>What locks.</strong> After an analysis, the address, context, and subject details lock, so the analysis and the report always describe the same property. You can correct the subject details until the next analysis runs. A different property needs a new case.</div>
              <div><strong style={{color:'white'}}>The mode line.</strong> Each analysis opens by naming its source. Mode A+ means live MLS comps. Mode A means your uploaded CMA. Mode B means no comp data yet, so treat it as a preliminary read.</div>
            </div>
            )}
            <div style={{marginTop:'14px',paddingTop:'12px',borderTop:'1px solid rgba(253,191,0,0.2)',fontSize:'11px',fontStyle:'italic',color:'rgba(255,255,255,0.7)',fontFamily:FONT_BODY}}>
              Reminder: this is pricing perspective for agent use only. Not an appraisal. Always verify with your own due diligence before advising clients.
            </div>
          </div>
        )}
        {/* SUBJECT — the navy bar became a card so the panel reads as one
            vocabulary. The Pull comps gate is unchanged: an address pull needs
            sqft, stories and year before it can run.
            STEP 1's marker wraps the call site rather than going inside
            SetupCard: that component is shared with BINSR and Deal Desk and has
            no slot for an adornment, so adding one would change three tools to
            decorate one. alignItems flex-start keeps the badge level with the
            top of the card as it grows. */}
        <div style={{display:'flex',alignItems:'flex-start',gap:'11px'}}>
        <StepMarker n={1} state={stepState(0)} />
        <div style={{flex:1,minWidth:0}}>
        <SetupCard
          titleIsControl
          blue
          icon={<MapPinIcon />}
          title={(
            <input
              ref={addressInputRef}
              value={mlsNumber}
              // A manual edit invalidates a previously selected place: the
              // structured address no longer describes the text, and pullComps
              // posts selectedPlace rather than the field. Compared instead of
              // cleared outright because the widget writes the formatted
              // address into the input itself on selection — matching on the
              // value keeps that write from clearing what it just set, whatever
              // order the native input event and place_changed arrive in.
              onChange={e => {
                const v = e.target.value
                setMlsNumber(v)
                setSelectedPlace(prev => (prev && prev.address === v ? prev : null))
                // Read BEFORE the ref is cleared below. The ref is the reliable
                // witness here: place_changed sets it synchronously, so it is
                // already the formatted address when the widget's own write to
                // the input fires this handler — whereas the closure's
                // selectedPlace may still be the pre-selection value in that
                // same batch, which is the ordering the note above describes.
                const keepsPlace = lastPlaceQueryRef.current === v
                // Same rule for the ref, and it has to be here: the ref outranks
                // selectedPlace in saveSubjectQuery, so leaving it set after an
                // edit would persist a replaced address over what was just typed
                // — an MLS number entered after an address would never save.
                if (lastPlaceQueryRef.current !== v) lastPlaceQueryRef.current = null
                // The specs belong to the place. When the place goes they go
                // with it, or the boxes keep showing a square footage measured
                // at the previous address. Local only — the blur that follows
                // nulls the same five columns in one patch.
                if (!keepsPlace) {
                  setSpecSqft('')
                  setSpecYearBuilt('')
                  setSpecStories('')
                  setSpecBeds('')
                  setSpecBathsTotal('')
                }
              }}
              // Blur was the only save trigger, so typing a value and pressing
              // Enter — or refreshing — without clicking away lost it silently.
              // Calls saveSubjectQuery rather than repeating its value
              // selection, so the ref-first rule holds on both paths.
              //
              // No preventDefault, and none needed: there is no <form> anywhere
              // in this tree, so Enter submits nothing. Google's widget attaches
              // its own keydown listener directly to the input, which fires
              // before this one reaches React's root delegate — so when its
              // dropdown is open Enter selects the suggestion first, and if
              // place_changed has set lastPlaceQueryRef by then this writes the
              // formatted address. If the widget's details lookup is still in
              // flight this writes the typed text, and place_changed's own
              // awaited write corrects the row immediately after, exactly as it
              // does for a mouse selection.
              onKeyDown={e => { if (e.key === 'Enter') saveSubjectQuery() }}
              onBlur={saveSubjectQuery}
              placeholder="Address or MLS #"
              // Same `locked` the three context pills use — the subject the
              // analysis ran against is as much a part of that context as the
              // client type, and a transcript that argued about one property
              // must not end up labelled with another.
              //
              // readOnly, not disabled. The value stays selectable, copyable
              // and reachable by a screen reader, which is the input version of
              // the rule the pills already follow: the chosen one stays lit so
              // a locked case still SAYS what it was run against.
              //
              // The background falls back to S.input's own #f4f4f2 — the grey
              // this file already uses for a value you can read and not change
              // (Deal Desk's FORM SET card). No new colour.
              readOnly={subjectLocked}
              style={{...S.input, width:'100%', boxSizing:'border-box', fontSize:'13px', padding:'5px 8px', border:`0.5px solid ${RAIL_BORDER}`, borderRadius:'6px', background: subjectLocked ? '#f4f4f2' : '#ffffff'}}
            />
          )}
          // The card's existing subtitle slot, carrying the same sentence the
          // context note below uses rather than a second element in a second
          // voice.
          subtitle={subjectLocked ? 'Locked — the analysis was run against this property.' : 'Subject property'}
          action={(
            <button
              onClick={() => pullComps(tierOverridesFromToggles(widenToggles))}
              disabled={!mlsLicensed || pullingComps || (selectedPlace ? !(Number(specSqft) > 0 && Number(specStories) > 0 && Number(specYearBuilt) > 0) : !mlsNumber.trim())}
              style={{background:'#fdbf00',color:'#1d2e42',border:'none',borderRadius:'8px',padding:'7px 14px',fontSize:'12px',fontWeight:700,letterSpacing:'0.5px',fontFamily:FONT_HEAD,cursor:mlsLicensed?'pointer':'not-allowed',flexShrink:0,whiteSpace:'nowrap',
                      opacity: (!mlsLicensed || pullingComps || (selectedPlace ? !(Number(specSqft) > 0 && Number(specStories) > 0 && Number(specYearBuilt) > 0) : !mlsNumber.trim())) ? 0.5 : 1}}>
              {pullingComps ? 'Pulling…' : 'Pull comps'}
            </button>
          )}
        />
        {/* LIVE MLS LOCKED. Under the address row, beside the Pull comps
            button it explains, inside step 1's column. */}
        {!mlsLicensed && (
          <div style={reportNoticeStrip}>
            {reportNoticeIcon}
            <span><span style={{ fontWeight: 500 }}>Live MLS comps aren't available for your brokerage yet.</span> Its MLS license is in process. Upload your own CMA under Documents to analyze it here.</span>
          </div>
        )}
        </div>
        </div>

            {/* SELECTIONS then DOCUMENTS, stacked. They were a two-column row
                with documents pinned to 300px; pills wrap on their own, so the
                narrow column bought nothing and cost the documents their
                width. Both are full width now. */}
            <div style={{display:'flex',flexDirection:'column',gap:'20px'}}>
              {/* STEP 2 wraps the WHOLE selections column, not just the pills.
                  SUBJECT DETAILS is a sibling of the pill container, and
                  wrapping only the pills left it hanging at the original left
                  edge while they sat 30px in. It belongs to step 2 anyway —
                  sqft, year and stories are context the analysis needs, exactly
                  like the three pills.
                  The column div IS the flex row rather than nesting a new one
                  inside it: same result, one less element. */}
              <div style={{minWidth:0,display:'flex',alignItems:'flex-start',gap:'11px'}}>
              <StepMarker n={2} state={stepState(1)} />
              <div style={{flex:1,minWidth:0}}>
                {/* One block, not three forms. No gap — PillSelector carries
                    its own 13px below each row, and a second gap on top of it
                    would space these further apart than the design allows.
                    Labels are shortened to fit the 104px eyebrow column
                    without wrapping. Every guard below is unchanged: the lock
                    behaviour is not part of this change. */}
                <div style={{padding:0,display:'flex',flexDirection:'column'}}>
                  <PillSelector
                    label="PROPERTY"
                    // Gone the moment a pill is picked. Not a validation state
                    // and not persisted — it says what is needed, never what
                    // was missed.
                    chip={!propertyType ? 'Required' : undefined}
                    options={PROP_TYPE_OPTS.map(o => ({ value: o.key, label: o.key }))}
                    selected={propertyType}
                    // frozen gates the whole GROUP, including the selected pill —
                    // what stops a locked case being cleared by a second click.
                    onSelect={v => { if (locked && wasSetAtLoad('pt', propertyType)) return; choosePropertyType(v) }}
                    // Dimming is narrower than the click guard on purpose: the
                    // chosen pill stays lit so a locked case still SAYS what it
                    // was run against.
                    disabledFor={v => locked && wasSetAtLoad('pt', propertyType) && propertyType !== v}
                  />
                  <PillSelector
                    label="CLIENT"
                    chip={!clientType ? 'Required' : undefined}
                    options={CLIENT_OPTS.map(o => ({ value: o.key, label: o.key }))}
                    selected={clientType}
                    onSelect={v => { if (locked && wasSetAtLoad('ct', clientType)) return; chooseClientType(v) }}
                    disabledFor={v => locked && wasSetAtLoad('ct', clientType) && clientType !== v}
                  />
                  <PillSelector
                    label="CONDITION"
                    chip={!condition ? 'Required' : undefined}
                    options={COND_OPTS.map(o => ({ value: o.key, label: o.key }))}
                    selected={condition}
                    onSelect={v => { if (locked && wasSetAtLoad('pc', condition)) return; chooseCondition(v) }}
                    disabledFor={v => locked && wasSetAtLoad('pc', condition) && condition !== v}
                  />
                  {/* Replaces the text summary that used to stand in for the
                      pills once locked. The lit pill already says what was
                      chosen, so the summary was restating it; this says why it
                      can no longer be changed.
                      Gated on allContextSetAtLoad, not just `locked`: on a case
                      being repaired, some of these pills are still clickable,
                      and a notice claiming otherwise contradicts what is on
                      screen. Nothing is shown there instead. */}
                  {locked && allContextSetAtLoad && (
                    <div style={{fontSize:'11.5px',color:'#999',fontStyle:'italic',fontFamily:FONT_BODY}}>
                      Locked — the analysis was run against this context.
                    </div>
                  )}
                </div>
              {selectedPlace && (
                <div style={{marginTop:'16px', paddingTop:'14px', borderTop:'1px solid #f0f0f0'}}>
                  <div style={{fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#999',marginBottom:'4px',fontFamily:FONT_HEAD}}>SUBJECT DETAILS</div>
                  <div style={{fontSize:'10px',color:'#999',marginBottom:'10px',fontStyle:'italic'}}>{specsLocked ? 'Locked — the analysis was run against these details.' : specsSet ? 'You can correct these until the next analysis runs.' : 'Required for an address pull — Sqft, Year, and Stories drive the comp match.'}</div>
                  <div style={{display:'grid', gridTemplateColumns:isMobile?'1fr 1fr 1fr':'repeat(5, 1fr)', gap:'8px'}}>
                    <div>
                      <label style={{fontSize:'10px',fontWeight:700,color:'#666',display:'block',marginBottom:'3px',fontFamily:FONT_HEAD}}>SQFT *</label>
                      <input type="number" value={specSqft} onChange={e=>setSpecSqft(e.target.value)} onBlur={e=>saveSpec('subject_sqft', e.target.value, setSpecSqft)} placeholder="Enter sqft" readOnly={specsLocked}
                        style={{...S.input, fontSize:'12px', padding:'8px 10px', borderColor: (specsLocked || specSqft) ? '#e8e8e8' : '#fdbf00'}} />
                    </div>
                    <div>
                      <label style={{fontSize:'10px',fontWeight:700,color:'#666',display:'block',marginBottom:'3px',fontFamily:FONT_HEAD}}>YEAR BUILT *</label>
                      <input type="number" value={specYearBuilt} onChange={e=>setSpecYearBuilt(e.target.value)} onBlur={e=>saveSpec('subject_year_built', e.target.value, setSpecYearBuilt)} placeholder="Enter year" readOnly={specsLocked}
                        style={{...S.input, fontSize:'12px', padding:'8px 10px', borderColor: (specsLocked || specYearBuilt) ? '#e8e8e8' : '#fdbf00'}} />
                    </div>
                    <div>
                      <label style={{fontSize:'10px',fontWeight:700,color:'#666',display:'block',marginBottom:'3px',fontFamily:FONT_HEAD}}>STORIES *</label>
                      <input type="number" value={specStories} onChange={e=>setSpecStories(e.target.value)} onBlur={e=>saveSpec('subject_stories', e.target.value, setSpecStories)} placeholder="e.g. 1" readOnly={specsLocked}
                        style={{...S.input, fontSize:'12px', padding:'8px 10px', borderColor: (specsLocked || specStories) ? '#e8e8e8' : '#fdbf00'}} />
                    </div>
                    <div>
                      <label style={{fontSize:'10px',fontWeight:700,color:'#666',display:'block',marginBottom:'3px',fontFamily:FONT_HEAD}}>BEDS</label>
                      <input type="number" value={specBeds} onChange={e=>setSpecBeds(e.target.value)} onBlur={e=>saveSpec('subject_beds', e.target.value, setSpecBeds)} placeholder="Enter beds" readOnly={specsLocked}
                        style={{...S.input, fontSize:'12px', padding:'8px 10px'}} />
                    </div>
                    <div>
                      <label style={{fontSize:'10px',fontWeight:700,color:'#666',display:'block',marginBottom:'3px',fontFamily:FONT_HEAD}}>BATHS</label>
                      <input type="number" value={specBathsTotal} onChange={e=>setSpecBathsTotal(e.target.value)} onBlur={e=>saveSpec('subject_baths_total', e.target.value, setSpecBathsTotal)} placeholder="Enter baths" readOnly={specsLocked}
                        style={{...S.input, fontSize:'12px', padding:'8px 10px'}} />
                    </div>
                  </div>
                </div>
              )}
              </div>
              </div>

              {/* DOCUMENTS — three fixed slots, not a growing list. An empty
                  slot still renders and is still a click target. One bordered
                  list rather than three stacked cards: three slots that never
                  grow read as a list, and three separate cards made each look
                  like its own decision. */}
              <div style={{display:'flex',alignItems:'flex-start',gap:'11px'}}>
              {/* NO NUMBER. Documents are optional and skipping them is not a
                  missed step, so numbering them would put a gold badge on
                  something an agent is free to ignore. The spacer is the same
                  19px the markers occupy, so the left edge of every zone in
                  this panel still lines up. */}
              <span style={{width:'19px',flexShrink:0,display:'inline-flex',alignItems:'center',justifyContent:'center',color:'#ccc',fontSize:'11px',fontFamily:FONT_BODY,lineHeight:'19px'}}>—</span>
              <div style={{flex:1,minWidth:0}}>
              <div style={{minWidth:0,display:'flex',flexDirection:'column',gap:'8px'}}>
                {/* Label and note on one line. The note was a 9px italic line
                    of its own, which gave a parenthetical its own row.
                    It used to read "auto-fills once address lookup is live",
                    which promised a feature that does not exist. */}
                <div style={{display:'flex',alignItems:'baseline',gap:'8px',flexWrap:'wrap'}}>
                  <div style={SETUP_EYEBROW}>DOCUMENTS</div>
                  <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>optional</div>
                </div>
                {/* Three tiles across one row. uploadFile, removeFile, the
                    picker and every file_type string are untouched — those
                    strings are a whitelist in strategy-chat, and one changed
                    character stops an upload reaching the model with no error.
                    Only the markup around them changed. */}
                <DocTileRow isMobile={isMobile}>
                {FILE_ZONES.map(zone => {
                  const uploadedFile = files.find(f => f.file_type === zone.type)
                  const picker = (
                    <input type="file" accept={zone.accept} style={{display:'none'}}
                      onChange={e=>{
                        const f = e.target.files[0]
                        if (f) uploadFile(f, zone.type)
                        e.target.value = ''
                      }}
                    />
                  )
                  // The zone's sublabel and nudge are the only place an agent is
                  // told WHICH document to fetch — "Maricopa County assessor" is
                  // the whole instruction for a tax record. The tile has no room
                  // for them, so they stay reachable on hover exactly as before.
                  return uploadedFile ? (
                    <DocTile
                      key={zone.type}
                      mode="filled"
                      label={zone.label}
                      sub={uploadedFile.file_name}
                      title={zone.nudge || zone.sublabel}
                      onRemove={()=>removeFile(uploadedFile.id, uploadedFile.file_name)}
                    />
                  ) : (
                    <DocTile
                      key={zone.type}
                      mode="empty"
                      label={zone.label}
                      sub={uploading===zone.type ? 'Uploading…' : 'Not attached'}
                      chip={zone.badge ? 'Recommended' : undefined}
                      title={zone.nudge || zone.sublabel}
                      input={picker}
                    />
                  )
                })}
                </DocTileRow>
              </div>
              </div>
              </div>
            </div>
    </div>
  )

  // ── APPRAISER REPORT: selection ─────────────────────────────────────────
  // Admin-only and desktop-only until the PDF ships, using the same admin test
  // the report bar's "Share with Agent" link uses. appraiserEnabled gates the
  // "Prepare appraiser packet" button and the form; appraiserSelection (null
  // outside appraiser mode) gates the card and modal checkboxes.
  const appraiserEnabled = profile?.role === 'admin' && !isMobile && !!compResult
  // Counted off the live payload, so a pick whose listing left the pull can
  // never count against a limit.
  const appraiserPickedIn = (buckets) => buckets.reduce((n, b) =>
    n + (compResult?.groups?.[b] || []).filter(c => c?.listing_id && appraiserPicks.has(c.listing_id)).length, 0)
  const appraiserClosedPicked = appraiserEnabled ? appraiserPickedIn(['closed']) : 0
  const appraiserOtherPicked = appraiserEnabled ? appraiserPickedIn(APPRAISER_OTHER_BUCKETS) : 0
  const appraiserSelection = (appraiserEnabled && appraiserMode) ? {
    // The checkbox for one comp, or null when that comp cannot be picked. At a
    // group's limit the UNchecked boxes in that group are disabled; a checked
    // one always stays clickable so it can be taken back out.
    pickFor: (comp, bucket) => {
      const group = appraiserGroupOf(bucket)
      const id = comp?.listing_id
      if (!group || !id) return null
      const checked = appraiserPicks.has(id)
      const atLimit = group === 'closed'
        ? appraiserClosedPicked >= APPRAISER_CLOSED_MAX
        : appraiserOtherPicked >= APPRAISER_OTHER_MAX
      return {
        checked,
        disabled: !checked && atLimit,
        onChange: () => setAppraiserPicks(prev => {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        }),
      }
    },
  } : null

  // The appraiser-mode step box. ONE node, rendered in two places: the panel's
  // summary block (where "Prepare appraiser packet" was) and the top of the
  // "See all" grid, which hides that block — so the step and the counts are on
  // screen wherever the agent is ticking. Outlined blue, never a blue fill.
  const appraiserStepNode = appraiserSelection && (
    <div style={{background:'#ffffff',border:'1px solid #147efb',borderRadius:'8px',padding:'12px 14px',fontFamily:FONT_BODY,lineHeight:1.5}}>
      <div style={{fontSize:'12px',fontWeight:500,color:'#147efb'}}>Appraiser packet · Step 1 of 2</div>
      <div style={{fontSize:'12px',color:'#1d2e42',marginTop:'4px'}}>
        {appraiserClosedPicked < APPRAISER_CLOSED_MIN
          ? `Select ${APPRAISER_CLOSED_MIN} to ${APPRAISER_CLOSED_MAX} closed sales. ${appraiserClosedPicked} of ${APPRAISER_CLOSED_MIN} selected.`
          : `${appraiserClosedPicked} closed sales selected.`}
      </div>
      <div style={{fontSize:'11px',color:'#666',marginTop:'2px'}}>
        Optional: up to {APPRAISER_OTHER_MAX} pending or active listings ({appraiserOtherPicked} selected).
      </div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',marginTop:'10px'}}>
        {/* Cancel leaves the mode AND drops the picks, so the next packet
            starts clean. The form, if open, is unaffected. */}
        <button
          onClick={() => { setAppraiserMode(false); setAppraiserPicks(new Set()) }}
          style={{background:'none',border:'none',padding:0,fontSize:'12px',fontFamily:FONT_BODY,color:'#666',cursor:'pointer'}}>
          Cancel
        </button>
        {appraiserClosedPicked >= APPRAISER_CLOSED_MIN && (
          // The "See all N" button's outlined blue, exactly.
          <button
            onClick={openContractForm}
            onMouseEnter={()=>setHoverPill('appraiserNext')}
            onMouseLeave={()=>setHoverPill(null)}
            style={{display:'inline-flex',alignItems:'center',gap:'6px',
              border:'0.5px solid #147efb',
              background:hoverPill === 'appraiserNext' ? '#f4f4f2' : '#fff',
              color:'#147efb',fontSize:'12px',fontWeight:500,fontFamily:'inherit',
              padding:'4px 11px',borderRadius:'18px',cursor:'pointer',transition:'background .15s'}}>
            Next: contract terms
          </button>
        )}
      </div>
    </div>
  )

  // Lifted out of setupNode. The comps are a RESULT, not setup — while they
  // lived inside it, collapsing the setup panel also hid the comps that panel
  // had just produced, and they sat above the transcript in the same scroller.
  // They are the shell's panel node now and are independent of setupOpen.
  //
  // The JSX below is unchanged from where it sat inside setupNode: same markup,
  // same styles, same handlers. missing/ready are component scope, so they still
  // resolve from here exactly as they did there.
  const compsPanelNode = compResult && (() => {
    // Bucket counts for the meta line. Same source and the same drop-at-zero
    // rule the sections below use, so the two can never disagree about which
    // buckets exist. Names are lowercase prose except the acronyms, which are
    // not words and read wrong in lower case.
    const META_BUCKETS = ['closed','pending','active','ucb','ccbs','coming_soon']
    const META_NAMES = {closed:'closed',pending:'pending',active:'active',ucb:'UCB',ccbs:'CCBS',coming_soon:'coming soon'}
    const breakdown = META_BUCKETS
      .map(b => ({ b, n: (compResult.groups?.[b] || []).length }))
      .filter(x => x.n > 0)
      .map(x => `${x.n} ${META_NAMES[x.b]}`)
      .join(' · ')
    // The closed count decides whether the widen controls open themselves, but
    // that decision is made in pullComps where the response is in hand — a copy
    // here would have no consumer, since a setter cannot run during render.

    // Escalation order, cheapest loss of comp quality first. Not the state
    // object's key order, which was arbitrary.
    const WIDEN_PILLS = [
      { key: 'radius15',   label: '1.5 mi' },
      { key: 'closings6',  label: '6 months' },
      { key: 'widenYear',  label: '±15 years' },
      { key: 'biggerSqft', label: 'Bigger sqft' },
      { key: 'dropStory',  label: 'Any story' },
      { key: 'radius2',    label: '2 mi' },
    ]

    // flex:1, not height:100%. A percentage height resolves only against a
    // parent with a definite height, and this panel's parent gets its height
    // from flex layout rather than from a declaration — so on mobile the
    // percentage had nothing to resolve against and collapsed to zero. flex:1
    // fills the parent however that parent's height was arrived at.
    return (
              <div style={{display:'flex',flexDirection:'column',minHeight:0,flex:1}}>

                {/* FIXED TOP. The action, what was found, and how to widen stay
                    put while the cards scroll under them — those are the three
                    things an agent reaches for repeatedly.
                    //
                    Hidden in the grid: there an agent is comparing, not
                    adjusting, and the row is worth more as cards. display
                    rather than unmounting, because the widen pills hold state
                    and a trip through the grid must not reset a tier the agent
                    already escalated to. */}
                <div style={{flexShrink:0,padding:'14px 16px',borderBottom:'0.5px solid #e8e8e8',display:compsGridOpen?'none':undefined}}>
                  {/* Same onClick, same disabled gate, same labels. Full width
                      and gold now: it is the one primary action in this panel.
                      Analyze, widen and the appraiser packet all need a pull,
                      so none of them shows to a profile not licensed for live
                      MLS data. */}
                  {mlsLicensed && (<>
                  <button
                    // FILLS THE BOX. It does not send, and the setTimeout that
                    // used to try never worked: sendMessage is redeclared every
                    // render and reads `input` from its own closure, so the
                    // callback held the pre-click instance, saw an empty input,
                    // and returned on its own guard. Waiting longer could not
                    // help — the captured function is frozen at that render.
                    // Worse when the agent had already typed: the stale value
                    // was non-empty, so it sent THEIR text and cleared the box
                    // this button had just filled.
                    //
                    // Anything already typed is kept and moved AFTER the prompt,
                    // so the agent's context reads as an addition to the
                    // question rather than a preamble to it. Trailing space so
                    // the caret lands ready to keep typing.
                    onClick={() => {
                      const typed = input.trim()
                      setInput(STRATEGY_ANALYZE_PROMPT + (typed ? ' ' + typed : '') + ' ')
                      setPrimed(true)
                    }}
                    disabled={!ready || sending}
                    style={{...S.btnPrimary,width:'100%',flex:'none',background:'#fdbf00',color:'#412402',borderRadius:'8px',padding:'10px',fontSize:'12.5px',fontWeight:500,letterSpacing:'normal',textTransform:'none',opacity:(!ready||sending)?0.4:1,cursor:(!ready||sending)?'not-allowed':'pointer'}}>
                    {sending ? 'Analyzing…' : 'Analyze these comps'}
                  </button>
                  {!ready && (
                    <div style={{fontSize:'12px',color:'#c0392b',fontFamily:FONT_BODY,marginTop:'8px'}}>
                      Set {missing.join(', ')} above before analyzing — the analysis needs the full context.
                    </div>
                  )}
                  </>)}

                  {/* Replaces the gold uppercase COMPARABLES badge. Same numbers,
                      read as a sentence instead of shouted as a label.
                      The total is NOT repeated here — the shell's panel header
                      carries it, and printing "15 found" directly under a header
                      reading "Comps · 15" says the same thing twice. */}
                  <div style={{fontSize:'11px',color:'#666',fontFamily:FONT_BODY,marginTop:'10px',lineHeight:1.5}}>
                    <div>Tier {compResult._meta?.tier ?? 1} · {compResult._meta?.radius_miles ?? 1} mi radius</div>
                    {breakdown && <div>{breakdown}</div>}
                  </div>

                  {/* Six pills and two links are a lot of control for a panel
                      whose main action is the gold button above. Folded behind a
                      link unless the pull came back thin — under five closed
                      comps, which is the case where widening is the next move
                      rather than a setting to browse. */}
                  {mlsLicensed && (!widenOpen ? (
                  <button
                    onClick={() => setWidenOpen(true)}
                    style={{background:'none',border:'none',padding:0,marginTop:'10px',fontSize:'12px',fontFamily:FONT_BODY,color:'#147efb',cursor:'pointer'}}>
                    Widen the search
                  </button>
                  ) : (
                  <div style={{marginTop:'12px'}}>
                    <div style={{fontSize:'10px',letterSpacing:'1px',color:'#666',fontFamily:FONT_HEAD,marginBottom:'8px'}}>THIN POOL? WIDEN THE SEARCH</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
                      {WIDEN_PILLS.map(t => {
                        const on = !!widenToggles[t.key]
                        return (
                          <button key={t.key}
                            onClick={() => setWidenToggles(prev => {
                              const next = { ...prev, [t.key]: !prev[t.key] }
                              // One radius, one lit pill. Both could be on
                              // before, while the override mapping silently kept
                              // only the larger — so the row said 1.5 and the
                              // pull used 2. Turning either OFF is unaffected
                              // and falls back to the tight default.
                              if (t.key === 'radius15' && next.radius15) next.radius2 = false
                              if (t.key === 'radius2' && next.radius2) next.radius15 = false
                              return next
                            })}
                            disabled={pullingComps}
                            onMouseEnter={()=>setHoverPill(t.key)}
                            onMouseLeave={()=>setHoverPill(null)}
                            style={{padding:'5px 12px',fontSize:'12px',fontFamily:FONT_BODY,borderRadius:'18px',
                              cursor: pullingComps ? 'default' : 'pointer',
                              border: on
                                ? '0.5px solid #147efb'
                                : `0.5px solid ${hoverPill === t.key && !pullingComps ? '#d8d8d4' : '#e8e8e8'}`,
                              background: on ? '#f4f4f2' : '#ffffff',
                              color: on ? '#1d2e42' : '#666',
                              transition:'all .15s'}}>
                            {t.label}
                          </button>
                        )
                      })}
                    </div>
                    {/* Text links, not buttons. Analyze above is the one gold
                        action here; two more filled controls would compete with
                        it. Handlers and the override mapping are unchanged. */}
                    <div style={{display:'flex',alignItems:'center',gap:'14px',marginTop:'12px'}}>
                      <button
                        onClick={() => pullComps(tierOverridesFromToggles(widenToggles))}
                        disabled={pullingComps}
                        style={{background:'none',border:'none',padding:0,fontSize:'12px',fontFamily:FONT_BODY,color:'#147efb',cursor:pullingComps?'default':'pointer',opacity:pullingComps?0.5:1}}>
                        {pullingComps ? 'Pulling…' : 'Re-pull with these'}
                      </button>
                      <button
                        onClick={() => { setWidenToggles({dropStory:false,biggerSqft:false,radius15:false,closings6:false,widenYear:false,radius2:false}); pullComps(null) }}
                        disabled={pullingComps}
                        style={{background:'none',border:'none',padding:0,fontSize:'12px',fontFamily:FONT_BODY,color:'#999',cursor:pullingComps?'default':'pointer',opacity:pullingComps?0.5:1}}>
                        Reset to tight
                      </button>
                    </div>
                  </div>
                  ))}

                  {/* APPRAISER PACKET. Under the widen control, in the summary
                      block — not the panel header, which already carries the
                      count, "See all" and the chevron. Off: one full-width
                      button with Analyze's box (8px radius, 12.5px/500), but
                      OUTLINED blue — gold stays for Pull comps, Analyze and
                      Generate, and blue is never a fill. Padding is 9px, not
                      Analyze's 10px, because this has a 1px border and Analyze
                      has none, so the two come out the same height. On: the
                      step box in the same spot. */}
                  {mlsLicensed && appraiserEnabled && (appraiserMode ? (
                    <div style={{marginTop:'10px'}}>{appraiserStepNode}</div>
                  ) : (
                    <button
                      onClick={() => setAppraiserMode(true)}
                      style={{...S.btnPrimary,width:'100%',flex:'none',marginTop:'10px',background:'#ffffff',color:'#147efb',border:'1px solid #147efb',borderRadius:'8px',padding:'9px',fontSize:'12.5px',fontWeight:500,letterSpacing:'normal',textTransform:'none',cursor:'pointer'}}>
                      Prepare appraiser packet
                    </button>
                  ))}
                </div>

                {/* SCROLLING BODY. The bucket sections and cards are unchanged. */}
                <div style={{flex:1,minHeight:0,overflowY:'auto',padding:'14px 16px'}}>
                {/* The grid hides the summary block, so the step box rides at
                    the top of the grid instead while appraiser mode is on. */}
                {compsGridOpen && appraiserStepNode && (
                  <div style={{marginBottom:'14px'}}>{appraiserStepNode}</div>
                )}
                {/* cap 4 / 280px track is the panel's density, unchanged. The
                    grid renders the SAME component with cap null and a tighter
                    track — see the panelWide branch below. */}
                <CompBuckets
                  groups={compResult.groups}
                  subject={compResult.subject}
                  isMobile={isMobile}
                  expandedGroups={expandedGroups}
                  setExpandedGroups={setExpandedGroups}
                  detailComp={detailComp}
                  setDetailComp={setDetailComp}
                  cap={compsGridOpen ? null : 4}
                  minTrack={compsGridOpen ? '215px' : '280px'}
                  selection={appraiserSelection}
                />
                </div>

              </div>
    )
  })()

  // What the panel holds before a pull. The column used to appear out of
  // nowhere the moment comps landed, which moved the transcript sideways mid
  // -case and left the setup sitting at 720px in a wide undivided nothing.
  // Reserving the space costs 400px that was empty anyway and makes the pull
  // fill a place the agent has already seen.
  //
  // Reads no compResult, deliberately — it renders only when there is none, so
  // anything derived from a pull would be reading null. That is also why it is
  // a plain node rather than the IIFE above: there is nothing to compute.
  const compsEmptyNode = (
    <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'30px 26px',textAlign:'center'}}>
      <div style={{width:'38px',height:'38px',borderRadius:'11px',background:'#f4f4f2',color:'#999',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        {/* The same four-square glyph the "See all" button carries, so the
            empty panel and the control that fills it are visibly one thing.
            currentColor, so the tile owns the colour. */}
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
        </svg>
      </div>
      <div style={{fontSize:'13px',color:'#1d2e42',fontFamily:FONT_BODY,marginTop:'12px'}}>
        {mlsLicensed ? 'No comps yet' : "Live MLS comps aren't available yet"}
      </div>
      {/* maxWidth in ch rather than px: the measure should follow the text, and
          26ch breaks this sentence in the same place at any panel width. */}
      <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,lineHeight:1.6,marginTop:'5px',maxWidth:'26ch'}}>
        {mlsLicensed
          ? 'Pull comps and they appear here, beside the conversation.'
          : "Your brokerage's MLS license is in process."}
      </div>
    </div>
  )

  // The shell's footer slot takes ONE node, and two independent things want to
  // sit under the composer: the readiness warning and the prime hint. Both can
  // be true at once — setup incomplete and the box just primed — so they are
  // composed here rather than fought over with a ternary.
  //
  // Warning first. It says the send will not go through; the hint says what to
  // type before sending. Reading them the other way round would offer advice
  // about an action that is currently blocked.
  //
  // Both reuse the composer's own padding and max width, with the same
  // marginTop:-12px that tucks a line under the box rather than leaving it
  // floating. The hint is 11.5px #999 — an aside, not an error.
  const primeHint = primed && !analyzed ? (
    <div key="hint" style={{padding:isMobile?'0 20px 20px':'0 20px 28px',display:'flex',justifyContent:'center'}}>
      <div style={{width:'100%',maxWidth:STRATEGY_CONTENT_WIDTH,marginTop:'-12px',fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>
        Add anything the analysis should know, or send as-is.
      </div>
    </div>
  ) : null
  const readyWarning = !ready ? (
    <div key="warn" style={{padding:isMobile?'0 20px 20px':'0 20px 28px',display:'flex',justifyContent:'center'}}>
      <div style={{width:'100%',maxWidth:STRATEGY_CONTENT_WIDTH,marginTop:'-12px',fontSize:'12px',color:'#c0392b',fontFamily:FONT_BODY}}>
        Set {missing.join(', ')} in setup before asking — the analysis needs the full context.
      </div>
    </div>
  ) : null
  // null when neither, so the shell's `{footer && …}` guard skips the wrapper
  // entirely and the composer keeps its own bottom padding.
  const footerNode = (readyWarning || primeHint)
    ? <>{readyWarning}{primeHint}</>
    : null

  return (
    <>
      <CaseShell
        ref={shellRef}
        isMobile={isMobile}
        // `title` is rendered as a node, so passing an input makes the case name
        // edit-in-place here.
        //
        // This is the CASE NAME, not the subject address — the subject goes in
        // the setup field, which takes an address or an MLS number and persists
        // to subject_query. It still writes subject_address because that column
        // is already what every case list, the rail and the dashboard read as a
        // case's label; renaming it would mean a migration and six read sites to
        // buy nothing. An address remains a perfectly good case name, so
        // existing cases need no backfill.
        title={(
          <input
            value={address}
            onChange={e=>setAddress(e.target.value)}
            onBlur={saveAddress}
            placeholder="Name this case..."
            style={{width:'100%',boxSizing:'border-box',fontSize:'16px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',background:'transparent',border:'none',padding:0,outline:'none'}}
          />
        )}
        summary={summaryNode}
        setupOpen={setupOpen}
        onToggleSetup={()=>setSetupOpen(o=>!o)}
        // A labelled outline pill rather than the bare chevron, so the control
        // says what it opens. CaseShell's existing toggleLabel variant (Deal
        // Desk's), neutral — no toggleLabelAccent. Short on phones, where the
        // long label would clip the summary and its "How to use this" link.
        toggleLabel={setupOpen ? 'Hide setup' : (isMobile ? 'Setup' : 'Address, details and documents')}
        setup={setupNode}
        // The panel exists from the moment the case opens, so the column is
        // reserved rather than conjured. Before a pull it holds the empty state
        // above; after one, the comps.
        //
        // NOT on mobile, where the two columns become a segmented control
        // instead of a row. An empty panel there would put a "Comps" tab beside
        // Conversation that opens onto nothing, and a phone has no dead space
        // the reservation could be fixing. Below the breakpoint the old
        // behaviour is unchanged: no comps, no panel, no tab.
        panel={isMobile && !compResult ? null : (compResult ? compsPanelNode : compsEmptyNode)}
        // Strategy supplies the words; the shell decides how much width to give
        // them. The count rides in the label so it survives collapse — a strip
        // reading "Comps · 15" says whether reopening is worth it. Bare "Comps"
        // before a pull: "Comps · 0" reads as a pull that found nothing, which
        // is a different and much worse thing than not having pulled yet.
        panelLabel={compResult ? `Comps · ${compResult._meta?.total_comps ?? 0}` : 'Comps'}
        // STEP 3 lives here because Analyze does — inside the comps panel, which
        // is exactly why agents were missing it.
        panelStep={<StepMarker n={3} state={stepState(2)} />}
        panelWide={compsGridOpen}
        // Nothing to collapse toward when the panel is empty — the chevron would
        // offer to fold away a column holding one sentence, and the strip it
        // folds to would say "Comps" over nothing.
        panelCollapsible={!!compResult}
        // Desktop only. Below the breakpoint the panel is already the full
        // width of the case area, so there is nothing for "See all" to widen.
        // The count rides in the label: "See all 16" says what the button is
        // worth, "See all" makes the agent guess.
        // Also absent before a pull: there is nothing to see all of. Both are
        // dropped rather than one, because the shell's guard requires both and
        // leaving a live handler behind a missing label is a trap for whoever
        // reads this next.
        panelAction={(isMobile || !compResult) ? undefined : (compsGridOpen ? 'Back to conversation' : `See all ${compResult._meta?.total_comps ?? 0}`)}
        onPanelAction={(isMobile || !compResult) ? undefined : (()=>setCompsGridOpen(v=>!v))}
        loading={false}
        messages={messages}
        renderMessage={renderMessage}
        emptyState={emptyState}
        onTranscriptScroll={handleChatScroll}
        contentWidth={STRATEGY_CONTENT_WIDTH}
        // The report bar appears only once a reply exists — the SAME signal as
        // step 4, so the bar can never be on screen while its own marker still
        // reads "later". A falsy controls renders nothing and costs no height.
        controls={analyzed ? controlsNode : null}
        // STEP 4 rides the send button, because sending is the step.
        composerBadge={<StepMarker n={4} state={stepState(3)} />}
        composerValue={input}
        // Emptying the box un-primes it. Priming means "the box holds the
        // Analyze prompt"; an empty box holds nothing. Editing the text does
        // NOT clear it — adding context is what the hint asks for, and a
        // marker that went dark on the first keystroke would punish it.
        onComposerChange={e=>{ const v = e.target.value; setInput(v); if (!v.trim()) setPrimed(false) }}
        onSubmit={()=>sendMessage()}
        composerPlaceholder="Ask about pricing, comps, strategy..."
        // Send is gated on readiness, the input is not: the agent can draft the
        // question while they set context. composerDisabled stays as it was.
        sendDisabled={sending || !input.trim() || !ready}
        composerMultiline
        composerDisabled={sending}
        footer={footerNode}
      />

      {/* The overlay is ReportPreviewOverlay in REPORT DELIVERY (shared) — the
          same component Casita Check opens. It returns null when preview is
          null, so the `&&` gate that used to sit here lives inside it. */}
      <ReportPreviewOverlay
        preview={reportPreview}
        isMobile={isMobile}
        onClose={closeReportPreview}
        onShare={shareReport}
        docLabel="Strategy report"
      />

      {/* PRE-PDF FIGURE CHECK. The same markup as BINSR's copy-confirm modal
          (copyModal in BinsrCaseView): S.overlay, the white card, the title,
          and Cancel / primary in a bordered footer. No backdrop-click close —
          an answer is required, and generateReport is awaiting it. */}
      {figureCheck && (
        <div style={S.overlay}>
          <div style={{
            background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
            padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
          }}>
            <div style={{fontSize:'18px', fontWeight:700, color:'#1d2e42', fontFamily:FONT_HEAD, letterSpacing:'0.5px', marginBottom:'10px'}}>
              Before you generate, check these numbers
            </div>
            <div style={{fontSize:'14px', color:'#333', lineHeight:1.65}}>
              {figureCheck.nonClean.length > 0 && (
                <div>Price drops and pricing options should land on a clean $5,000 number (like $445,000 or $450,000). These don't:</div>
              )}
              {figureCheck.nonClean.map((n, i) => (
                <div key={'c' + i}>• {n.label}: {formatWholeDollars(n.amount)}</div>
              ))}
              {figureCheck.figures.length > 0 && (
                <div style={{marginTop: figureCheck.nonClean.length > 0 ? '10px' : 0}}>These amounts in the report don't match a comp or an offer number:</div>
              )}
              {/* One line per amount, at most three, then a count. */}
              {figureCheck.figures.slice(0, 3).map((f, i) => (
                <div key={'f' + i}>• {f.label}, in {f.section}</div>
              ))}
              {figureCheck.figures.length > 3 && (
                <div>and {figureCheck.figures.length - 3} more</div>
              )}
              <div style={{marginTop:'10px'}}>
                Check them before sending. If one looks wrong, run "Analyze these comps" again.
              </div>
            </div>

            <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8', display:'flex', gap:'10px'}}>
              <button
                onClick={()=>answerFigureCheck(false)}
                style={{...S.btnOutline, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:'pointer'}}>
                Cancel
              </button>
              <button
                onClick={()=>answerFigureCheck(true)}
                style={{...S.btnPrimary, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:'pointer'}}>
                Generate anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NO CLOSED SALE MATCHED. The figure check's markup above, with one
          Close button. generateReport has already returned; this only says why. */}
      {noClosedMatch && (
        <div style={S.overlay}>
          <div style={{
            background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
            padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
          }}>
            <div style={{fontSize:'18px', fontWeight:700, color:'#1d2e42', fontFamily:FONT_HEAD, letterSpacing:'0.5px', marginBottom:'10px'}}>
              This report can't be generated yet
            </div>
            <div style={{fontSize:'14px', color:'#333', lineHeight:1.65}}>
              None of the analyzed sales are in this pull. Run "Analyze these comps" again.
            </div>

            <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8', display:'flex', gap:'10px'}}>
              <button
                onClick={()=>setNoClosedMatch(false)}
                style={{...S.btnPrimary, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:'pointer'}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* APPRAISER REPORT: CONTRACT TERMS. The figure check's layout above —
          S.overlay, the white card, the title, Cancel / primary in a bordered
          footer — with the S.fieldGroup / S.label / S.input fields the rest of
          the file uses. No backdrop-click close: a stray click must not throw
          away four typed fields. appraiserEnabled is checked again so a view
          that turns mobile, or loses its comps, cannot leave the form open. */}
      {appraiserEnabled && contractForm && (
        <div style={S.overlay}>
          <div style={{
            background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
            padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
            boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
          }}>
            <div style={{fontSize:'18px', fontWeight:700, color:'#1d2e42', fontFamily:FONT_HEAD, letterSpacing:'0.5px', marginBottom:'18px'}}>
              Contract terms for the appraiser
            </div>
            {[
              { key: 'price', label: 'CONTRACT PRICE ($)', input: (
                <input type="number" min="1" step="1" inputMode="numeric" style={S.input}
                  value={contractForm.price} onChange={e=>setContractForm(f => ({ ...f, price: e.target.value }))} placeholder="e.g. 450000" />
              ) },
              { key: 'date', label: 'CONTRACT DATE', input: (
                <input type="date" style={S.input}
                  value={contractForm.date} onChange={e=>setContractForm(f => ({ ...f, date: e.target.value }))} />
              ) },
              { key: 'concession', label: 'SELLER CONCESSIONS TO BUYER', input: (
                <>
                  {/* $ / % toggle: the report bar's English/Español segmented
                      control (segWrap / segBtn). Its cursor follows
                      generatingCurrent, which means nothing here, so only the
                      cursor is overridden. Each mode keeps its own value, so
                      flipping back and forth loses nothing. */}
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    {contractForm.concessionMode === 'pct' ? (
                      <input type="number" min="0" max="20" step="0.01" inputMode="decimal" style={S.input}
                        value={contractForm.concessionPct} onChange={e=>setContractForm(f => ({ ...f, concessionPct: e.target.value }))} placeholder="e.g. 2" />
                    ) : (
                      <input type="number" min="0" step="1" inputMode="numeric" style={S.input}
                        value={contractForm.concession} onChange={e=>setContractForm(f => ({ ...f, concession: e.target.value }))} placeholder="0 if none" />
                    )}
                    <div style={segWrap}>
                      <button type="button" onClick={()=>setContractForm(f => ({ ...f, concessionMode: 'usd' }))}
                        style={{...segBtn(contractForm.concessionMode !== 'pct', true), cursor:'pointer'}}>$</button>
                      <button type="button" onClick={()=>setContractForm(f => ({ ...f, concessionMode: 'pct' }))}
                        style={{...segBtn(contractForm.concessionMode === 'pct'), cursor:'pointer'}}>%</button>
                    </div>
                  </div>
                  {/* Live, recomputed on every keystroke in either box: both
                      values are read from contractForm on each render. */}
                  {contractForm.concessionMode === 'pct' && (() => {
                    const price = contractPriceValue(contractForm)
                    if (price == null) {
                      return <div style={{fontSize:'12px', color:'#666', fontFamily:FONT_BODY, marginTop:'6px'}}>Enter the contract price to calculate.</div>
                    }
                    const dollars = contractPctDollars(contractForm)
                    if (dollars == null) return null
                    return (
                      <div style={{fontSize:'12px', color:'#666', fontFamily:FONT_BODY, marginTop:'6px'}}>
                        = ${dollars.toLocaleString('en-US')} at a ${price.toLocaleString('en-US')} contract price
                      </div>
                    )
                  })()}
                </>
              ) },
              { key: 'loanType', label: 'BUYER LOAN TYPE', input: (
                <select style={S.input}
                  value={contractForm.loanType} onChange={e=>setContractForm(f => ({ ...f, loanType: e.target.value }))}>
                  <option value="">Choose…</option>
                  {APPRAISER_LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              ) },
            ].map(field => (
              <div key={field.key} style={S.fieldGroup}>
                <label style={S.label}>{field.label}</label>
                {field.input}
                {contractErrors[field.key] && (
                  <div style={{fontSize:'13px', color:'#c0392b', fontFamily:FONT_BODY, marginTop:'6px'}}>{contractErrors[field.key]}</div>
                )}
              </div>
            ))}

            <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8', display:'flex', gap:'10px'}}>
              <button
                onClick={()=>{ setContractForm(null); setContractErrors({}) }}
                disabled={contractSaving}
                style={{...S.btnOutline, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:'pointer'}}>
                Cancel
              </button>
              <button
                onClick={saveContractTerms}
                disabled={contractSaving}
                style={{...S.btnPrimary, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:contractSaving?'not-allowed':'pointer', opacity:contractSaving?0.6:1}}>
                {contractSaving ? 'Saving…' : 'Save terms'}
              </button>
            </div>
          </div>
        </div>
      )}

      {shareModalOpen && (
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setShareModalOpen(false)}>
          <div style={{...S.modal,maxWidth:'520px',maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
            <div style={{...S.modalHeader,flexShrink:0}}>
              <div>
                <div style={S.modalTitle}>SHARE CASE WITH AGENT</div>
                {address && <div style={{fontSize:'12px',color:'#999',marginTop:'6px',fontFamily:FONT_BODY}}>{address}</div>}
              </div>
              <button style={S.closeBtn} onClick={()=>setShareModalOpen(false)}>×</button>
            </div>

            <div style={{flex:1,overflowY:'auto',paddingRight:'4px'}}>
            {sharedWith.length > 0 && (
              <div style={{marginBottom:'20px'}}>
                <div style={{...S.label,marginBottom:'10px'}}>CURRENTLY SHARED WITH</div>
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  {sharedWith.map(id => {
                    const a = agentsList.find(x => x.id === id)
                    return (
                      <div key={id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',padding:'10px 12px',background:'#f5f5f5',border:'1px solid #e8e8e8',borderRadius:'6px'}}>
                        <div style={{minWidth:0,flex:1}}>
                          <div style={{fontSize:'13px',fontWeight:700,color:'#222',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a?.full_name || '(agent)'}</div>
                          <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a?.email || id}</div>
                        </div>
                        <button onClick={()=>unshareAgent(id)} style={{background:'transparent',color:'#C8102E',border:'1px solid rgba(200,16,46,0.3)',borderRadius:'6px',padding:'6px 10px',fontSize:'11px',fontWeight:600,cursor:'pointer',fontFamily:FONT_BODY,flexShrink:0}}>Remove</button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div style={S.fieldGroup}>
              <label style={S.label}>SELECT AN AGENT</label>
              <input
                style={{...S.input,marginBottom:'8px'}}
                placeholder="Search by name or email..."
                value={agentSearch}
                onChange={e=>setAgentSearch(e.target.value)}
              />
              <div style={{maxHeight:'200px',overflowY:'auto',border:'1px solid #e8e8e8',borderRadius:'6px',background:'white'}}>
                {(() => {
                  // A case that ran a live MLS pull shares only within the
                  // owner's brokerage: the MLS license covers that brokerage,
                  // and can_read_case() refuses anyone outside it, so offering
                  // them would share nothing. share-case enforces the same rule.
                  // Unknown owner brokerage fails closed. No pull: unchanged.
                  const hasMls = !!(caseData?.comp_pull_result || compResult)
                  const ownerBrokerage = caseData?.agent_id === session.user.id
                    ? profile?.brokerage
                    : agentsList.find(a => a.id === caseData?.agent_id)?.brokerage
                  const eligible = hasMls
                    ? agentsList.filter(a => !!ownerBrokerage && a.brokerage === ownerBrokerage)
                    : agentsList
                  if (hasMls && eligible.length === 0) {
                    return <div style={{padding:'14px',fontSize:'13px',color:'#999',fontFamily:FONT_BODY,textAlign:'center'}}>No other agents in this brokerage to share with.</div>
                  }
                  const available = eligible.filter(a => !sharedWith.includes(a.id))
                  const q = agentSearch.trim().toLowerCase()
                  const filtered = q
                    ? available.filter(a => (a.full_name||'').toLowerCase().includes(q) || (a.email||'').toLowerCase().includes(q))
                    : available
                  if (filtered.length === 0) {
                    return <div style={{padding:'14px',fontSize:'13px',color:'#999',fontFamily:FONT_BODY,textAlign:'center'}}>{available.length === 0 ? 'All agents already have access.' : 'No agents match your search.'}</div>
                  }
                  return filtered.map(a => {
                    const isSel = selectedAgentId === a.id
                    return (
                      <div
                        key={a.id}
                        onClick={()=>setSelectedAgentId(a.id)}
                        style={{padding:'10px 12px',cursor:'pointer',borderBottom:'1px solid #f0f0f0',background:isSel?'rgba(20,126,251,0.08)':'transparent'}}>
                        <div style={{fontSize:'13px',fontWeight:700,color:'#222',fontFamily:FONT_BODY}}>
                          {a.full_name || '(no name)'}
                          {a.role === 'admin' && <span style={{marginLeft:'8px',fontSize:'10px',color:'#999',fontWeight:600,letterSpacing:'1px'}}>(ADMIN)</span>}
                        </div>
                        <div style={{fontSize:'11px',color:'#999',fontFamily:FONT_BODY}}>{a.email}</div>
                      </div>
                    )
                  })
                })()}
              </div>
            </div>

            <div style={S.fieldGroup}>
              <label style={S.label}>NOTE (OPTIONAL)</label>
              <textarea
                style={{...S.textarea,minHeight:'80px'}}
                placeholder="Add a personal note (optional)"
                value={shareNote}
                onChange={e=>setShareNote(e.target.value)}
              />
            </div>
            </div>

            <div style={{...S.modalFooter,flexShrink:0}}>
              <button onClick={()=>setShareModalOpen(false)} style={S.btnOutline}>Cancel</button>
              <button
                onClick={shareWithAgent}
                disabled={!selectedAgentId || sharing}
                style={{...S.btnPrimary,opacity:(!selectedAgentId||sharing)?0.5:1,cursor:(!selectedAgentId||sharing)?'not-allowed':'pointer'}}>
                {sharing ? 'Sharing...' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── BINSR: CASES LIST ──────────────────────────────────────────────────────
function BinsrPage({ session, showToast, isMobile, onOpenCase, initialAddress = '', profile, navigateTo, isAdmin, signOut }) {
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newAddress, setNewAddress] = useState('')
  const [newRepresentation, setNewRepresentation] = useState('buyer')
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Arriving from the Assistant composer: open the create form with the address
  // already filled so the agent only has to pick buyer/seller. Default '' means
  // every existing call site behaves exactly as before.
  useEffect(() => {
    if (!initialAddress) return
    setShowNewForm(true)
    setNewAddress(initialAddress)
  }, [initialAddress])

  const fetchCases = useCallback(async () => {
    const { data } = await supabase.from('binsr_cases')
      .select('*, binsr_case_files(count), binsr_case_messages(count)')
      .eq('user_id', session.user.id).order('updated_at', { ascending: false })
    if (data) setCases(data)
    setLoading(false)
  }, [session.user.id])

  useEffect(() => { fetchCases() }, [fetchCases])

  async function createCase() {
    if (newAddress.trim().length < 5) return
    setCreating(true)
    const { data, error } = await supabase.from('binsr_cases')
      .insert({ user_id: session.user.id, property_address: newAddress.trim(), representation: newRepresentation }).select().single()
    if (error) { showToast('Error creating case'); setCreating(false); return }
    setCreating(false)
    setShowNewForm(false)
    setNewAddress('')
    setNewRepresentation('buyer')
    onOpenCase(data.id)
  }

  // Same guard as AdminPage and the Strategist list: an RLS refusal is not an
  // error, it is zero rows, so the row count is the only thing that separates a
  // refused delete from a real one.
  async function deleteCase(caseId) {
    const { data: deleted, error } = await supabase
      .from('binsr_cases').delete().eq('id', caseId).select()
    if (error) { showToast('Delete failed: ' + error.message); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the case is still there. You may not have permission to delete it.')
      setConfirmDelete(null)
      return
    }
    setCases(prev => prev.filter(c => c.id !== caseId))
    setConfirmDelete(null)
    showToast('Case deleted')
  }

  // Was a hand-rolled button that reimplemented the pill's styling and dropped
  // its tooltip and disabled handling. Now the shared pill in blue, so the two
  // Buyer/Seller selectors in this app cannot drift apart again. No deselect:
  // a case must have a representation, so clicking the lit pill re-sets it
  // rather than clearing it — which is what the old button did too.
  //
  // TILE_ICONS is keyed 'Buyer'/'Seller'; the stored values are lowercase.
  const repChip = (value, label) => dealdeskPill(
    newRepresentation === value,
    label,
    ()=>setNewRepresentation(value),
    pillIcon(TILE_ICONS[label] || ''),
    undefined,
    isMobile,
    false,
    'blue',
  )

  // The create form is always visible here: representation has to be chosen
  // before a row can exist, so there is nothing to hide behind a button.
  const listNode = (
    <div>
      {/* No badge: there is no live connection behind this tool the way Follow
          Up Boss sits behind Pipeline, and a decorative pill would be stating
          something that is not a fact about the system. */}
      <ToolIntroBand
        title="Inspection Desk"
        description="Draft a BINSR response from the inspection report. Buyer side or seller side — it reads the report and writes the notice."
        stats={[
          (<><span style={{color:'#fdbf00'}}>A.R.S. §32-1121</span> contractor rules</>),
          'FHA · VA aware',
          'You approve every word',
        ]}
      />

      {/* White card, matching the Strategist list. The grey gradient panel this
          replaces made the least-used action the heaviest object under the
          band; the header line is what names it now that the panel does not. */}
      <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 14px',marginBottom:'14px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'9px'}}>
          <span style={{color:'#147efb',fontSize:'14px',lineHeight:1,fontFamily:FONT_BODY}}>+</span>
          <span style={{fontSize:'12.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>Start a new case</span>
        </div>
        <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',marginBottom:'6px',fontFamily:FONT_HEAD}}>I REPRESENT</div>
        <div style={{display:'flex',gap:'6px',marginBottom:'12px'}}>
          {repChip('buyer','Buyer')}
          {repChip('seller','Seller')}
        </div>
        <div style={createRow(isMobile)}>
          <input
            value={newAddress} onChange={e=>setNewAddress(e.target.value)}
            onKeyDown={e=>{ if (e.key==='Enter') createCase() }}
            placeholder="e.g. 1234 E Main St, Scottsdale, AZ 85251"
            style={{...S.input, flex:1, minWidth:0, marginBottom:0, background:'#ffffff'}}
          />
          {/* Gold, not S.btnPrimary's blue: blue is reserved for interactive and
              selected states, and the screen's one primary action is gold.
              Overridden INLINE — S.btnPrimary is shared across the file, so
              repainting it here would repaint every other screen. The disabled
              behaviour and its 5-character threshold are untouched. */}
          <button onClick={createCase} disabled={creating||newAddress.trim().length<5} style={{...S.btnPrimary,background:'#fdbf00',color:'#412402',fontWeight:500,letterSpacing:'normal',textTransform:'none',flex:'none',flexShrink:0,padding:'9px 18px',fontSize:'12px',opacity:(creating||newAddress.trim().length<5)?0.5:1}}>
            {creating ? 'Creating...' : 'Create Case'}
          </button>
        </div>
      </div>

      {cases.length === 0 ? (
        <div style={{padding:'24px 0',color:'#999',fontSize:'14px',fontFamily:FONT_BODY}}>
          No BINSR cases yet.
        </div>
      ) : (
        <div style={caseGrid(isMobile)}>
          {cases.map(c => {
            const fileCount = c.binsr_case_files?.[0]?.count || 0
            const msgCount = c.binsr_case_messages?.[0]?.count || 0
            // Representation moved out of the meta line and into a chip.
            const isSeller = c.representation === 'seller'
            const meta = [
              timeAgo(c.updated_at),
              fileCount > 0 ? `${fileCount} file${fileCount!==1?'s':''}` : null,
              msgCount > 0 ? `${msgCount} message${msgCount!==1?'s':''}` : null,
            ].filter(Boolean).join(' · ')
            return (
              <CaseCard
                key={c.id}
                chips={caseChip(isSeller ? 'gold' : 'blue', isSeller ? 'SELLER' : 'BUYER')}
                address={c.property_address || 'Untitled Property'}
                meta={meta}
                onOpen={()=>onOpenCase(c.id)}
                deletable
                confirming={confirmDelete === c.id}
                onAskDelete={()=>setConfirmDelete(c.id)}
                onConfirmDelete={()=>deleteCase(c.id)}
                onCancelDelete={()=>setConfirmDelete(null)}
              />
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Inspection desk"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={BINSR_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// BINSR's transcript carries drafted BINSR language and inspection tables that
// are cramped at the shell's default reading width. Shared with the case list.
const BINSR_CONTENT_WIDTH = SHARED_CONTENT_WIDTH


// Finds a "SECTION N" heading however the model happened to write it. What this
// replaced was the bare literal 'SECTION 1', and nothing in the prompt required
// the model to print that string — so a reply that wrote "## Section One", or
// dropped the caption, matched nothing, the Export button vanished, and a
// perfectly good draft was unreachable. The prompt now asks for the headings;
// this is the other half, so a heading the model varies slightly still lands.
//
// Deliberately loose: any markdown prefix, either case, digit or word, any dash
// or none, and the caption is not checked at all. Anchored to a line start, so
// "see section 2 below" inside a sentence is not mistaken for a heading.
//
// fromIndex lets the SECTION 2 search begin after the SECTION 1 heading, the way
// indexOf(s2, afterHeader) did. Set via lastIndex rather than by slicing the
// string, so the ^ anchor is still evaluated against real line starts.
function findSectionHeading(content, n, fromIndex = 0) {
  if (!content) return null
  const word = { 1: 'ONE', 2: 'TWO', 3: 'THREE' }[n]
  const num = word ? `(?:${n}|${word})` : String(n)
  const re = new RegExp(`^[ \\t]*[#*_>]*[ \\t]*SECTION[ \\t]+${num}\\b[^\\n]*$`, 'gim')
  re.lastIndex = fromIndex
  const match = re.exec(content)
  if (!match) return null
  const nl = content.indexOf('\n', match.index)
  return { start: match.index, lineEnd: nl === -1 ? content.length : nl }
}

// Module scope rather than inside the component so the memo in BinsrCaseView can
// call it without carrying it as a dependency.
//
// The slice runs from the end of the SECTION 1 heading line to the start of the
// SECTION 2 heading line, or to the end of the content when there is no SECTION
// 2. Because a match begins at its own line start, the "##" that the old
// lastIndexOf('\n') had to walk back over is now excluded for free.
function extractBinsrLanguage(content) {
  if (!content) return null
  const h1 = findSectionHeading(content, 1)
  if (!h1) return null
  const h2 = findSectionHeading(content, 2, h1.lineEnd)
  const end = h2 ? h2.start : content.length
  // Empty when the SECTION 1 heading is the last line with nothing beneath it.
  // Null is right there — there is no draft to copy — and the button runs this
  // same test now, so it no longer offers an export that would fail on click.
  return content.slice(h1.lineEnd, end).trim() || null
}

// ─── BINSR: CASE VIEW ───────────────────────────────────────────────────────
function BinsrCaseView({ session, caseId, showToast, isMobile, onBack, profile, navigateTo, isAdmin, signOut }) {
  const shellRef = useRef(null)
  // Setup starts open on an untouched case and closed once there is a
  // transcript, decided once when messages first land.
  const [setupOpen, setSetupOpen] = useState(true)
  const setupInitRef = useRef(false)
  const [caseData, setCaseData] = useState(null)
  const [address, setAddress] = useState('')
  const [files, setFiles] = useState([])
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const [loanType, setLoanType] = useState(null)
  const [mode, setMode] = useState('drafting')
  const [representation, setRepresentation] = useState('buyer')
  const [creditAmount, setCreditAmount] = useState('')
  // The message awaiting a copy confirmation, or null.
  const [copyTarget, setCopyTarget] = useState(null)
  const [copying, setCopying] = useState(false)

  useEffect(() => {
    supabase.from('binsr_cases').select('*').eq('id', caseId).single()
      .then(({ data, error }) => {
        if (error || !data) { showToast('Case not found.'); onBack(); return }
        setCaseData(data)
        setAddress(data.property_address || '')
        setCreditAmount(data.credit_amount != null ? String(data.credit_amount) : '')
        setMode(data.mode || 'drafting')
        setRepresentation(data.representation || 'buyer')
      })
    supabase.from('binsr_case_files').select('*').eq('case_id', caseId).order('uploaded_at')
      .then(({ data }) => { if (data) setFiles(data) })
    supabase.from('binsr_case_messages').select('*').eq('case_id', caseId).order('created_at')
      .then(({ data, error }) => {
        if (error) showToast('Could not load messages')
        setMessages(data || [])
        if (!setupInitRef.current) { setupInitRef.current = true; setSetupOpen((data || []).length === 0) }
      })
  }, [caseId])

  // The shell owns the transcript element now, so the distance-from-bottom read
  // comes off the event target instead of a local ref. The gate is unchanged.
  const handleChatScroll = useCallback((e) => {
    const el = e.currentTarget
    if (!el) return
    setShouldAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 100)
  }, [])

  const lastAssistantIdRef = useRef(null)
  const lastUserIdRef = useRef(null)

  useEffect(() => {
    if (!shouldAutoScroll) return

    const newestAssistant = [...messages].reverse().find(m =>
      m.role === 'assistant' && typeof m.id === 'string' && m.id.startsWith('temp-assistant-')
    )
    const newestUser = [...messages].reverse().find(m =>
      m.role === 'user' && typeof m.id === 'string' && m.id.startsWith('temp-user-')
    )

    // Case 1: a brand-new assistant message just appeared → pin user's most-recent message top to viewport top, once
    // The shell's pin targets whatever streamingMessageRef points at, and BINSR
    // attaches that to the temp-user row — same anchor as before.
    if (newestAssistant && newestAssistant.id !== lastAssistantIdRef.current) {
      lastAssistantIdRef.current = newestAssistant.id
      shellRef.current?.pinAssistantToTop()
      return
    }

    // Case 2: a brand-new user message just appeared (and no new assistant yet) → scroll to bottom
    if (newestUser && newestUser.id !== lastUserIdRef.current && !newestAssistant) {
      lastUserIdRef.current = newestUser.id
      shellRef.current?.scrollToBottom()
      return
    }

    // Case 3: DB refresh or any other change → do nothing
  }, [messages, shouldAutoScroll])

  async function saveAddress() {
    await supabase.from('binsr_cases').update({ property_address: address }).eq('id', caseId)
  }

  async function saveCreditAmount() {
    if (!caseData) return
    const trimmed = (creditAmount || '').trim()
    // Parse to number; empty → null in DB
    const parsed = trimmed === '' ? null : Number(trimmed.replace(/[^0-9.]/g, ''))
    if (parsed != null && (Number.isNaN(parsed) || parsed < 0)) {
      showToast('Credit amount must be a non-negative number')
      // Restore last good value from caseData
      setCreditAmount(caseData.credit_amount != null ? String(caseData.credit_amount) : '')
      return
    }
    // Skip the DB write if the value didn't actually change
    if ((caseData.credit_amount ?? null) === parsed) return
    const { error } = await supabase.from('binsr_cases')
      .update({ credit_amount: parsed }).eq('id', caseId)
    if (error) { showToast('Failed to save credit amount'); return }
    setCaseData(prev => ({ ...prev, credit_amount: parsed }))
  }

  async function switchMode(newMode) {
    if (!caseData) return
    if (newMode !== 'drafting' && newMode !== 'walkthrough') return
    if (caseData.mode === newMode) return
    setMode(newMode)
    const { error } = await supabase.from('binsr_cases')
      .update({ mode: newMode }).eq('id', caseId)
    if (error) {
      showToast('Failed to switch mode')
      setMode(caseData.mode || 'drafting')
      return
    }
    setCaseData(prev => ({ ...prev, mode: newMode }))
  }

  function sanitizeFilename(name) {
    const lastDot = name.lastIndexOf('.')
    const base = lastDot > 0 ? name.slice(0, lastDot) : name
    const ext = lastDot > 0 ? name.slice(lastDot) : ''
    const cleanBase = base.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase()
    const cleanExt = ext.trim().toLowerCase().replace(/[^a-zA-Z0-9.]/g, '')
    return `${cleanBase || 'file'}${cleanExt}`
  }

  async function uploadFile(file, fileType) {
    setUploading(fileType)
    try {
      const safeBase = sanitizeFilename(file.name)
      const prefixedName = `${Date.now()}-${safeBase}`
      const folderPath = `${session.user.id}/${caseId}`
      const fullPath = `${folderPath}/${prefixedName}`

      const { error: upErr } = await supabase.storage.from('binsr-docs').upload(fullPath, file, { upsert: false })
      if (upErr) { showToast('Upload failed: ' + upErr.message); setUploading(null); return }

      // storage_path is the record of where the file lives; file_url stays
      // null and no link is created (schema 2026-10-10).
      const { data: row, error: insertErr } = await supabase.from('binsr_case_files')
        .insert({ case_id: caseId, file_type: fileType, storage_path: fullPath, file_name: prefixedName }).select().single()
      if (insertErr) {
        // The object is already in the bucket; without its row nothing will ever
        // reference it, so take it back out rather than leaving it orphaned.
        // Every one of these used to survive as a permanent orphan.
        //
        // No collision check needed here, unlike Strategy: the name is
        // timestamp-prefixed, so fullPath cannot be a path an existing row
        // already points at.
        const { error: rmErr } = await supabase.storage.from('binsr-docs').remove([fullPath])
        if (rmErr) console.error('[binsr upload] rollback remove failed:', rmErr.message)
        showToast('Upload failed: ' + insertErr.message)
        setUploading(null)
        return
      }

      setFiles(prev => [...prev, row])
      setUploading(null)
      showToast('File uploaded')
    } catch (e) {
      showToast('Upload failed: ' + (e.message || String(e)))
      setUploading(null)
    }
  }

  // LEGACY-AWARE ON PURPOSE — do not "simplify" this to f.file_type === 'quote'.
  // Uploads now write exactly 'report' or 'quote', but rows written while the
  // type dropdown existed carry contractor_quote, roofer_quote, termite_report,
  // inbound_binsr and a dozen others. The suffix test keeps those in the right
  // column without a migration, and without destroying what the agent actually
  // classified them as — which is still the only record of that.
  //
  // Reports are the else-branch rather than a second list, deliberately: a row
  // with a null, a typo, or a value invented later renders under REPORTS
  // instead of vanishing from the panel while still being sent to the model.
  const isQuoteFile = (f) => f.file_type === 'quote' || /_quote$/.test(f.file_type || '')
  const byUploaded = (a, b) => new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0)
  const reportFiles = files.filter(f => !isQuoteFile(f)).sort(byUploaded)
  const quoteFiles = files.filter(isQuoteFile).sort(byUploaded)
  const displayFileName = (raw) => (raw || '').replace(/^\d+-/, '')

  async function removeFile(fileId, fileName) {
    // The row's stored path first; the rebuilt one for rows without it.
    const stored = files.find(f => f.id === fileId)?.storage_path
    const path = stored || `${session.user.id}/${caseId}/${sanitizeFilename(fileName)}`
    await supabase.storage.from('binsr-docs').remove([path])
    await supabase.from('binsr_case_files').delete().eq('id', fileId)
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/)
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>
      return part
    })
  }

  function renderMarkdown(text) {
    if (!text) return null
    const lines = text.split('\n')
    const elements = []
    let idx = 0
    // Items drawn so far. Nothing resets it, mirroring stripMarkdown's
    // singleNumberedList: the whole message is one numbered sequence, and each
    // <ol> continues via its start attribute. The two must stay identical — the
    // screen and the clipboard showing different item numbers is the failure
    // this pair exists to prevent.
    let orderedCount = 0
    while (idx < lines.length) {
      const trimmed = lines[idx].trim()
      if (trimmed === '') { idx++; continue }
      if (/^-{3,}$/.test(trimmed)) { elements.push(<hr key={idx} style={{border:'none',borderTop:'1px solid #e8e8e8',margin:'16px 0'}} />); idx++; continue }
      if (trimmed.startsWith('### ')) { elements.push(<div key={idx} style={{fontSize:'15px',fontWeight:700,color:'#1d2e42',margin:'12px 0 6px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(4))}</div>); idx++; continue }
      if (trimmed.startsWith('## ')) { elements.push(<div key={idx} style={{fontSize:'18px',fontWeight:700,color:'#1d2e42',margin:'14px 0 8px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(3))}</div>); idx++; continue }
      if (trimmed.startsWith('# ')) { elements.push(<div key={idx} style={{fontSize:'24px',fontWeight:700,color:'#1d2e42',margin:'16px 0 10px',fontFamily:FONT_HEAD}}>{renderInline(trimmed.slice(2))}</div>); idx++; continue }
      if (trimmed.startsWith('|') && trimmed.includes('|')) {
        const tableRows = []
        while (idx < lines.length && lines[idx].trim().startsWith('|')) {
          const row = lines[idx].trim()
          if (!/^[\s|:-]+$/.test(row)) { tableRows.push(row.split('|').filter(c => c.trim() !== '').map(c => c.trim())) }
          idx++
        }
        if (tableRows.length > 0) {
          elements.push(<div key={'tbl'+idx} style={{overflowX:'auto',WebkitOverflowScrolling:'touch',marginTop:'8px',marginBottom:'12px',maxWidth:'100%'}}><table style={{borderCollapse:'collapse',minWidth:'100%',fontSize:'13px',fontFamily:FONT_BODY}}><thead><tr>{tableRows[0].map((cell,ci) => <th key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px',background:'#f5f5f5',fontWeight:700,textAlign:'left',whiteSpace:'nowrap'}}>{renderInline(cell)}</th>)}</tr></thead><tbody>{tableRows.slice(1).map((row,ri) => <tr key={ri}>{row.map((cell,ci) => <td key={ci} style={{border:'1px solid #e8e8e8',padding:'6px 10px'}}>{renderInline(cell)}</td>)}</tr>)}</tbody></table></div>)
        }
        continue
      }
      if (trimmed.startsWith('- ')) {
        // See collectListItems: blank lines between items must not split the run.
        const items = collectListItems(lines, idx, l => l.startsWith('- '), l => l.slice(2))
        idx = items.next
        elements.push(<ul key={'ul'+idx} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderInline(item)}</li>)}</ul>)
        continue
      }
      if (/^\d+\.\s/.test(trimmed)) {
        // The run continues across whatever separates the items, via start, so
        // ROOFING 1-3 is followed by ELECTRICAL 4-6 rather than a second item 1.
        // BINSR's drafted language is one Export click from a form.
        const items = collectListItems(lines, idx, l => /^\d+\.\s/.test(l), l => l.replace(/^\d+\.\s/, ''))
        idx = items.next
        const startAt = orderedCount + 1
        orderedCount += items.values.length
        elements.push(<ol start={startAt} key={'ol'+idx} style={{margin:'8px 0',paddingLeft:'20px'}}>{items.values.map((item,ii) => <li key={ii} style={{marginBottom:items.loose?'10px':'4px',lineHeight:1.6}}>{renderInline(item)}</li>)}</ol>)
        continue
      }
      let para = trimmed; idx++
      while (idx < lines.length && lines[idx].trim() !== '' && !lines[idx].trim().startsWith('#') && !lines[idx].trim().startsWith('- ') && !/^\d+\.\s/.test(lines[idx].trim()) && !lines[idx].trim().startsWith('|') && !/^-{3,}$/.test(lines[idx].trim())) { para += ' ' + lines[idx].trim(); idx++ }
      elements.push(<p key={'p'+idx} style={{margin:'8px 0',lineHeight:1.65}}>{renderInline(para)}</p>)
    }
    return elements
  }


  // The ids of the assistant messages that actually contain a draft. One source
  // of truth with the copy: the button appears only when extractBinsrLanguage
  // would return something, so a visible button can never fail on click.
  //
  // Memoised rather than called inline in the JSX. renderMessage runs for every
  // message on every render, and this view re-renders on every keystroke in the
  // composer — so an inline call would rescan every reply in the transcript per
  // character typed. `messages` changes only a few times per turn.
  const exportableIds = useMemo(() => {
    const ids = new Set()
    for (const m of messages) {
      if (m.role === 'assistant' && !m.streaming && extractBinsrLanguage(m.content)) ids.add(m.id)
    }
    return ids
  }, [messages])

  async function copyBinsrLanguage(content) {
    const raw = extractBinsrLanguage(content)
    if (!raw) { showToast('Could not find BINSR language section'); return }
    const text = stripMarkdown(raw, { singleNumberedList: true })
    try {
      await navigator.clipboard.writeText(text)
      showToast('Copied to clipboard')
    } catch { showToast('Copy failed — try selecting manually') }
  }

  // Wraps copyBinsrLanguage rather than replacing it: extraction, stripping, the
  // success toast and the clipboard catch all stay exactly as they were, and the
  // only new behaviour is the pause before the write. Cancel never reaches it.
  //
  // No attestation row. BINSR has no session gate, so a copy row would carry a
  // borrowed name and a disclaimer version pointing at nothing — a record whose
  // main property is looking like evidence. If a gate is added, log then.
  async function confirmCopy() {
    if (!copyTarget || copying) return
    setCopying(true)
    await copyBinsrLanguage(copyTarget.content)
    setCopying(false)
    setCopyTarget(null)
  }

  // textArg is optional, the same shape PipelineView and AdaCaseView use. A
  // caller holding the text passes it directly instead of writing it to state
  // and calling back later — which is what retry() did, through a setTimeout
  // that captured this function from the previous render along with that
  // render's `input`.
  async function sendMessage(textArg) {
    const userMsg = (typeof textArg === 'string' ? textArg : input).trim()
    if (!userMsg || sending) return
    // Only when the box was the source: a retry supplies its own text and must
    // not clear a composer the agent may have typed into since the failure.
    if (typeof textArg !== 'string') setInput('')
    setError(null)
    const tempAssistantId = 'temp-assistant-' + Date.now()
    const tempUserMsg = { id: 'temp-user-' + Date.now(), role: 'user', content: userMsg, created_at: new Date().toISOString() }
    const tempAssistantMsg = { id: tempAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg])
    setSending(true)

    try {
      const context = { property_address: caseData?.property_address || address }
      context.output_format = 'Itemized'
      if (loanType) context.loan_type = loanType
      if (caseData?.credit_amount != null) context.credit_amount = caseData.credit_amount
      context.mode = mode || 'drafting'
      context.representation = representation || 'buyer'

      const fullHistory = [...messages.filter(m => !m.id?.startsWith('temp-')), { role: 'user', content: userMsg }]
        .map(m => ({ role: m.role, content: m.content }))

      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/binsr-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ messages: fullHistory, case_id: caseId, context }),
      })

      if (!res.ok) {
        const errText = await res.text()
        throw new Error(errText || `Request failed: ${res.status}`)
      }

      const data = await res.json()
      const assistantContent = data?.content || data?.response || data?.text || (typeof data === 'string' ? data : JSON.stringify(data))

      // Null unless a document failed to load. Pre-composed by the edge
      // function so it is stored verbatim rather than recomposed here from
      // data.documents — one writer for the sentence, one shape in the row.
      const docFlag = data?.document_flag || null

      // Optimistic only, and only when there is something to say: the refetch
      // below replaces this within a beat, but it puts the warning on screen the
      // moment the draft lands rather than after two more round trips. Skipped
      // entirely when clean, so a clean turn touches no state it did not before.
      if (docFlag) {
        setMessages(prev => prev.map(m => m.id === tempAssistantId ? { ...m, document_flag: docFlag } : m))
      }

      // BOTH ROWS ARE WRITTEN HERE, AFTER THE REPLY EXISTS, user first.
      //
      // The user row used to be inserted before the fetch. Nothing rolled it
      // back, so every failure past that point — a dropped connection, a 500
      // from binsr-chat, a bad parse — left a question in the table with no
      // answer, and that orphan then rode in fullHistory on every later turn.
      // Retry made it visible by inserting a second identical row; the
      // duplicate was real and survived a reload. Now a failure writes nothing
      // and a retry writes the pair once.
      //
      // Safe to write this late because the edge function never reads the
      // table: binsr-chat destructures `messages` from the request body, and
      // its only Supabase call is a storage download. fullHistory appends
      // userMsg itself, so the model's context never depended on this row.
      //
      // Sequential and in this order so created_at puts the question before
      // the answer — the refetch below sorts on it.
      await supabase.from('binsr_case_messages').insert({ case_id: caseId, role: 'user', content: userMsg })
      await supabase.from('binsr_case_messages').insert({ case_id: caseId, role: 'assistant', content: assistantContent, document_flag: docFlag })
      await supabase.from('binsr_cases').update({ updated_at: new Date().toISOString() }).eq('id', caseId)

      // Refresh from DB
      const { data: fresh } = await supabase.from('binsr_case_messages').select('*').eq('case_id', caseId).order('created_at')
      if (fresh) setMessages(fresh)
    } catch (e) {
      setError({ message: e.message, retryMsg: userMsg })
      setMessages(prev => prev.filter(m => m.id !== tempAssistantId))
    }
    setSending(false)
  }

  function retry() {
    if (!error) return
    // Straight in, not through the box. sendMessage clears the composer before
    // the request, so by the time a retry is possible `input` is empty — the
    // old setInput-then-timeout wrote the message somewhere the timeout's
    // stale closure could never see it, and every retry silently did nothing.
    sendMessage(error.retryMsg)
    setError(null)
  }

  const LOAN_OPTS = [
    { key: 'Conventional', tip: 'Standard conventional financing — standard repair requirements' },
    { key: 'FHA', tip: 'FHA loan — stricter health/safety requirements (chipping paint, handrails, etc.)' },
    { key: 'VA', tip: 'VA loan — MPR requirements (pest inspection, utilities, roof, etc.)' },
    { key: 'Cash', tip: 'Cash purchase — no lender requirements, purely negotiation-based' },
  ]
  // Mode glyphs are new — the segmented control used emoji (📋 🚶), which do not
  // take currentColor and would sit oddly beside line icons. Keyed by the stored
  // VALUE ('drafting'/'walkthrough'), not the display label.
  const BINSR_MODE_OPTS = [
    { key: 'drafting', label: 'BINSR Drafting' },
    { key: 'walkthrough', label: 'Walk-through' },
  ]


  const disclaimerNode = (
    <div style={{background:'#f4f4f2',borderTop:'0.5px solid #e8e8e8',color:'#666',padding:'11px 16px',display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_BODY}}>
      <div style={{fontSize:'13px',color:'#791F1F',flexShrink:0}}>⚠</div>
      <div style={{fontSize:'10.5px',lineHeight:1.4}}><span style={{fontWeight:700,color:'#791F1F',letterSpacing:'0.3px',fontFamily:FONT_HEAD}}>NOT LEGAL ADVICE&nbsp;&nbsp;</span>This tool helps you draft better BINSRs. Under Arizona law, you are responsible for every word in your final notice. When in doubt, contact your broker.</div>
    </div>
  )

  // Mode tabs for a buyer case; a seller case has no modes, so it gets the
  // static banner instead. Same handlers, same switchMode persistence.
  // A seller case has no modes, and the title row already reads "Seller · N
  // files" — the static banner that used to sit here said the same thing twice
  // and occupied a whole control slot to do it. CaseShell guards on {controls &&},
  // so null renders nothing rather than an empty band.
  //
  // The buyer side is the compact segmented pill Strategy's controls card and
  // the Events title row use, not the icon tiles it briefly became: two modes is
  // a switch, and a tile row reads as a menu of options.
  const modeSegWrap = { display:'flex', alignItems:'center', border:'0.5px solid #d8d8d4', borderRadius:'16px', overflow:'hidden', flexShrink:0, background:'#ffffff' }
  const modeSegBtn = (active, divider = false) => ({
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:'4px 11px', fontSize:'11.5px', fontWeight:600, fontFamily:FONT_BODY,
    border:'none', whiteSpace:'nowrap', cursor:'pointer',
    ...(divider ? { borderRight:'0.5px solid #e8e8e8' } : {}),
    background: active ? '#f4f4f2' : 'transparent',
    color: active ? '#1d2e42' : '#888',
  })
  const controlsNode = representation === 'seller' ? null : (
    <div style={{padding:'0 20px 10px',display:'flex',justifyContent:'center'}}>
      <div style={modeSegWrap}>
        {BINSR_MODE_OPTS.map((o, i) => (
          <button
            key={o.key}
            onClick={()=>switchMode(o.key)}
            style={modeSegBtn(mode === o.key, i === 0)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )

  // The Export to BINSR button and the temp-user anchor are unchanged; the
  // document_flag banner below them is the only addition.
  //
  // document_flag is stored as `key: payload`, matching citation_flag. The
  // payload is the display sentence, so the banner shows it verbatim — parsing
  // counts and filenames back out of a delimited string would break the first
  // time a filename contained the delimiter. Split on the FIRST separator only.
  const documentFlagText = (flag) => {
    const i = flag.indexOf(': ')
    return i === -1 ? flag : flag.slice(i + 2)
  }

  const renderMessage = (m, streamingRef) => (
    <div key={m.id} ref={typeof m.id === 'string' && m.id.startsWith('temp-user-') ? streamingRef : null}>
      <div style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
        {m.role === 'user' ? (
          <div style={{maxWidth:'80%',background:'#f4f4f2',color:'#1d2e42',borderRadius:'14px',padding:'12px 16px',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY}}>
            {m.content}
          </div>
        ) : (
          <div style={{width:'100%',fontSize:'14px',lineHeight:1.65,color:'#1d2e42',fontFamily:FONT_BODY}}>
            {m.streaming && !m.content ? (
              <ThinkingRow label="Drafting BINSR language…" />
            ) : renderMarkdown(m.content)}
            {m.streaming && <span style={{animation:'streamCursor 1s infinite',fontWeight:700}}>▋</span>}
          </div>
        )}
      </div>
      {/* Rendered from the row, not from the turn's response, so it survives a
          reload — a warning that vanishes on refresh is not a record, and the
          agent may well come back to this draft tomorrow. Sits ABOVE Export to
          BINSR on purpose: the export action must not be reachable without
          passing the reason the draft may be incomplete. */}
      {m.document_flag && (
        <div style={{display:'flex',justifyContent:'flex-start',marginTop:'8px'}}>
          <div style={{maxWidth:'80%',background:'#fdf2f2',border:'1px solid #C8102E',borderRadius:'8px',padding:'10px 14px',fontSize:'12.5px',lineHeight:1.5,fontFamily:FONT_BODY,color:'#8a1020'}}>
            <div style={{fontWeight:700,marginBottom:'3px',fontFamily:FONT_HEAD,letterSpacing:'0.3px'}}>DOCUMENTS DID NOT LOAD</div>
            <div>{documentFlagText(m.document_flag)}</div>
            <div style={{marginTop:'6px'}}>This draft was written without them. Re-upload and send again before relying on it.</div>
          </div>
        </div>
      )}
      {exportableIds.has(m.id) && (
        <div style={{display:'flex',justifyContent:'flex-start',marginTop:'6px'}}>
          <button onClick={()=>setCopyTarget(m)} style={{background:'#147efb',color:'white',border:'none',borderRadius:'6px',padding:'6px 14px',fontSize:'11px',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>
            Export to BINSR
          </button>
        </div>
      )}
      {m.role === 'assistant' && messages[messages.length - 1]?.id !== m.id && (
        <div style={{height:'0.5px',background:'#e8e8e8',marginTop:'18px'}} />
      )}
    </div>
  )

  // GATED ON caseData, which is null until the row resolves. `representation`
  // initialises to 'buyer' and is only corrected inside that same .then(), so
  // rendering before it lands would ask a seller case what the BUYER wants and
  // then swap the wording — a beat of nothing is better than the wrong question.
  // Both the heading and the subtitle below switch on representation, so this
  // guard is still doing work now that the starter cards are gone. There is no
  // separate loading flag in this view; caseData being non-null IS the resolved
  // signal, and it is set on the same line that sets representation.
  const emptyState = !caseData ? null : (
    <div style={{paddingTop:'32px'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:'38px',height:'38px',borderRadius:'10px',background:'#FAEEDA',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
          <InspectionIcon size={19} color="#633806" />
        </div>
        <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'5px'}}>
          {representation === 'seller'
            ? 'The report is attached. What does the seller want?'
            : 'The report is attached. What does the buyer want?'}
        </div>
        <div style={{fontSize:'11.5px',color:'#666',fontFamily:FONT_BODY}}>
          {representation === 'seller'
            ? 'Describe the response in plain language and it drafts the notice.'
            : 'Describe the items in plain language and it drafts the notice.'}
        </div>
      </div>
    </div>
  )

  // Representation is fixed at creation, so it always shows. Mode only means
  // something on a buyer case. File count is omitted at zero.
  const summaryBits = [
    representation === 'seller' ? 'Seller' : 'Buyer',
    representation === 'seller' ? null : (mode === 'walkthrough' ? 'Walk-through' : 'Drafting'),
    files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean)
  const summaryNode = (
    <div style={{display:'flex',alignItems:'center',gap:'10px',fontSize:'12px',color:'#555',fontFamily:FONT_BODY,minWidth:0}}>
      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{summaryBits.join(' · ')}</span>
    </div>
  )

  // The hidden input is the same for both groups; only the type it writes
  // differs. One click from the link straight to the file picker — the type
  // dropdown it replaces asked the agent to classify fifteen ways and then
  // used the answer for exactly one thing: which column to draw the row in.
  const fileInput = (fileType) => (
    <input
      type="file"
      accept=".pdf"
      disabled={!!uploading}
      style={{display:'none'}}
      onChange={async e=>{
        const f = e.target.files[0]
        e.target.value = ''
        if (!f) return
        await uploadFile(f, fileType)
      }}
    />
  )

  // docRow and docAddRow stood here. Both are DocTile now — the attached row
  // became mode "filled", the two add rows became mode "add" — and neither had
  // any other caller.

  // Three zones in the order Strategy uses: subject, selections, documents.
  // Full width — the 560px cap and the 300px document column are gone, so the
  // three tools measure the same.
  const setupNode = (
    <div style={{width:'100%',maxWidth:SETUP_CONTENT_WIDTH,display:'flex',flexDirection:'column',gap:'18px'}}>

      {/* A row, not a card. This was a full-width blue SetupCard with an icon
          tile, a gradient, a border and a full-width input — for five
          characters — and it did not line up with the two rows beneath it.
          The shape below is PillSelector's exactly: same 104px label column,
          same 16px gap, same 13px below, so the three zones share one left
          edge. The field keeps a border of its own because unlike a pill it
          is a place to type, and that has to look different from a place to
          click. */}
      <div>
        <div style={{display:'flex',alignItems:'baseline',gap:'16px',marginBottom:'13px'}}>
          <div style={{...SETUP_EYEBROW,width:'104px',flexShrink:0}}>CREDIT</div>
          <span style={{display:'inline-flex',alignItems:'center',gap:'5px',border:'0.5px solid #147efb',borderRadius:'8px',padding:'5px 11px',background:'#F7FAFE'}}>
            <span style={{fontSize:'13px',color:'#147efb',fontFamily:FONT_BODY}}>$</span>
            <input
              type="text"
              inputMode="decimal"
              value={creditAmount}
              onChange={e=>setCreditAmount(e.target.value)}
              onBlur={saveCreditAmount}
              placeholder="0.00"
              style={{width:'84px',fontSize:'13px',color:'#1d2e42',fontFamily:FONT_BODY,border:'none',outline:'none',background:'transparent',padding:0}}
            />
          </span>
        </div>
        {/* Was the card's subtitle. Same representation switch, moved to the
            hint position PillSelector uses so both hints share a left edge. */}
        <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginLeft:'120px'}}>
          {representation === 'seller' ? 'Optional — total credit offered to buyer' : 'Optional — total credit ask from buyer'}
        </div>
      </div>

      {/* Nothing in this view locks, so no disabledFor: editable for the life
          of the case. The not-set note is the hint prop rather than a div of
          its own, so it aligns under the pills. */}
      <PillSelector
        label="LOAN TYPE"
        options={LOAN_OPTS.map(o => ({ value: o.key, label: o.key }))}
        selected={loanType}
        onSelect={setLoanType}
        hint={!loanType ? 'Not set — the draft will not name a loan program.' : undefined}
      />

      <div style={{minWidth:0,display:'flex',flexDirection:'column',gap:'8px'}}>
        <div style={SETUP_EYEBROW}>DOCUMENTS</div>
        {/* Tiles, the same DocTile the Strategist uses. Open-ended rather than
            fixed slots, so attached files come first and the row ends with one
            Add tile per kind the tool accepts. They wrap as files accumulate.
            reportFiles, quoteFiles, isQuoteFile, displayFileName, fileInput and
            removeFile are all unchanged — only the markup around them. */}
        <DocTileRow isMobile={isMobile}>
          {reportFiles.map(f => (
            <DocTile key={f.id} mode="filled" label={displayFileName(f.file_name)} sub="Inspection report"
              onRemove={()=>removeFile(f.id, f.file_name)} />
          ))}
          {quoteFiles.map(f => (
            <DocTile key={f.id} mode="filled" label={displayFileName(f.file_name)} sub="Contractor quote"
              onRemove={()=>removeFile(f.id, f.file_name)} />
          ))}
          <DocTile mode="add" label="Add report"
            sub={uploading === 'report' ? 'Uploading…' : 'Another inspection'}
            input={fileInput('report')} />
          <DocTile mode="add" label="Add quote"
            sub={uploading === 'quote' ? 'Uploading…' : 'Contractor estimate'}
            input={fileInput('quote')} />
        </DocTileRow>
      </div>
    </div>
  )

  // Same shell as Deal Desk's copy modal — same card, divider and button pair.
  // Only the wording differs, because the warning has to be true of what BINSR
  // actually produces: notice language for the form, not contract language.
  // "Verify against your executed document" would be the wrong instruction here
  // — there is no contract in a BINSR case, only inspection reports and quotes.
  const copyModal = copyTarget && (
    <div style={S.overlay}>
      <div style={{
        background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
        padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
      }}>
        <div style={{fontSize:'18px', fontWeight:700, color:'#1d2e42', fontFamily:FONT_HEAD, letterSpacing:'0.5px', marginBottom:'10px'}}>
          Read it before it goes on the form.
        </div>
        <div style={{fontSize:'14px', color:'#333', lineHeight:1.65}}>
          This is notice language for the BINSR. It does not amend your contract — credits and price changes belong in a separate addendum. You are responsible for every word in your final notice.
        </div>

        <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8', display:'flex', gap:'10px'}}>
          <button
            onClick={()=>setCopyTarget(null)}
            disabled={copying}
            style={{...S.btnOutline, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:copying?'not-allowed':'pointer'}}>
            Cancel
          </button>
          <button
            onClick={confirmCopy}
            disabled={copying}
            style={{...S.btnPrimary, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', opacity:copying?0.5:1, cursor:copying?'not-allowed':'pointer'}}>
            {copying ? 'Copying…' : 'COPY'}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
    {copyModal}
    <CaseShell
      ref={shellRef}
      isMobile={isMobile}
      title={(
        <input
          value={address}
          onChange={e=>setAddress(e.target.value)}
          onBlur={saveAddress}
          placeholder="Enter subject property address..."
          style={{width:'100%',boxSizing:'border-box',fontSize:'16px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',background:'transparent',border:'none',padding:0,outline:'none'}}
        />
      )}
      summary={summaryNode}
      setupOpen={setupOpen}
      onToggleSetup={()=>setSetupOpen(o=>!o)}
      setup={setupNode}
      loading={false}
      messages={messages}
      renderMessage={renderMessage}
      emptyState={emptyState}
      onTranscriptScroll={handleChatScroll}
      contentWidth={BINSR_CONTENT_WIDTH}
      controls={(
        <>
          {error && (
            <div style={{padding:'0 20px 8px',display:'flex',justifyContent:'center'}}>
              <div style={{...S.errorBox,width:'100%',maxWidth:BINSR_CONTENT_WIDTH,marginBottom:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>{error.message}</span>
                <button onClick={retry} style={{background:'none',border:'none',color:'#147efb',fontWeight:700,cursor:'pointer',fontFamily:FONT_HEAD,fontSize:'12px',letterSpacing:'0.5px'}}>RETRY</button>
              </div>
            </div>
          )}
          {controlsNode}
        </>
      )}
      composerValue={input}
      onComposerChange={e=>setInput(e.target.value)}
      onSubmit={()=>sendMessage()}
      composerPlaceholder={representation === 'seller' ? "Describe how the seller wants to respond..." : "Describe the inspection findings or paste items..."}
      sendDisabled={sending || !input.trim()}
      composerMultiline
      composerDisabled={sending}
      footer={disclaimerNode}
    />
    </>
  )
}

// ─── DEAL DESK ──────────────────────────────────────────────────────────────
// Two axes the case row carries and the header must keep showing. form_set is
// the one that cannot be inferred later: an agent who picks the wrong one gets
// correct-looking language for the wrong contract family, which is the failure
// Section 2 of the v3.2 prompt exists to prevent. Neither has a default.
const DEALDESK_FORM_SETS = [
  { value: 'HomePros', label: 'HomePros · AAR forms', short: 'HomePros · AAR' },
  { value: 'HomePros Advantage', label: 'HomePros Advantage · MLS Choice', short: 'HomePros Advantage · MLS Choice' },
]
const DEALDESK_CONTENT_WIDTH = SHARED_CONTENT_WIDTH

// v3.7 sorts every message into three requests — drafting, reading, judgment —
// and decides that itself from the message. Only the drafting one has subtypes
// the agent picks, and these are those two. Per turn, not per case — an agent
// may polish a clause in the morning and amend the executed contract the same
// afternoon on the same deal.
const DEALDESK_MODE_DOCUMENTS = 'documents'
const DEALDESK_MODE_CLEANUP = 'cleanup'

// Same pill vocabulary BinsrPage uses for representation, with one change:
// nothing is selected until the agent selects it, so the unselected state is
// the starting state rather than an alternative to the default. Module-level
// with an optional icon so the creation screen and the composer row share one
// definition rather than two that drift.
// The first ancestor that clips overflow, or null if nothing does.
//
// parentElement, NOT offsetParent. offsetParent only visits POSITIONED ancestors,
// and the container that actually clips these tooltips — CaseShell's transcript
// pane — sets no position at all, so an offsetParent walk from a pill resolves
// straight to BODY and finds nothing. Verified in a browser before relying on it.
function findClipAncestor(el) {
  let node = el?.parentElement
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node)
    if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') return node
    node = node.parentElement
  }
  return null
}

// How far to slide a centred tooltip so it sits inside its clipping container,
// and where the arrow has to go to keep pointing at the pill.
//
// Pure, so the geometry can be tested without a DOM. Both rects are viewport
// coordinates from getBoundingClientRect.
function computeTipShift(bubbleRect, clipRect, pad = 8) {
  let shift = 0
  if (bubbleRect.left < clipRect.left + pad) {
    shift = (clipRect.left + pad) - bubbleRect.left
  } else if (bubbleRect.right > clipRect.right - pad) {
    shift = (clipRect.right - pad) - bubbleRect.right
  }
  // The bubble moves by `shift`, so the arrow moves the other way to stay over
  // the pill. Clamped inside the bubble's own edges — an arrow that slides off
  // its bubble points at nothing and looks like a rendering fault.
  const half = bubbleRect.width / 2
  const arrowOffset = Math.max(-half + 10, Math.min(half - 10, -shift))
  return { shift, arrowOffset }
}

// The pill itself. A component rather than inline markup because the optional
// tooltip needs state, and per-pill state means hovering one closes the last.
// With no tip it renders exactly the button dealdeskPill always rendered —
// same element, same styles, no wrapper — so Deal Desk is untouched.
function DealDeskPillButton({ selected, label, onClick, icon, tip, isMobile, disabled, tone }) {
  const [tipOpen, setTipOpen] = useState(false)
  // Blue tone only. The default tone has no hover and must not gain one — its
  // rendered style string is supposed to stay byte-identical.
  const [hover, setHover] = useState(false)
  const bubbleRef = useRef(null)
  const arrowRef = useRef(null)

  // Placed after render and before paint, so the shifted position is the first
  // one drawn — no flash of a clipped tooltip.
  //
  // The cap comes first and is a real fix, not a nicety: in a container narrower
  // than the bubble there is no offset that fits, so the bubble has to get
  // taller instead of sliding somewhere it still overflows.
  useLayoutEffect(() => {
    if (!tipOpen) return
    const bubble = bubbleRef.current
    const arrow = arrowRef.current
    if (!bubble || !arrow) return

    const clipEl = findClipAncestor(bubble)
    const clipRect = clipEl
      ? clipEl.getBoundingClientRect()
      : { left: 0, right: window.innerWidth, width: window.innerWidth }

    const PAD = 8
    const cap = Math.min(240, Math.max(0, clipRect.width - PAD * 2))
    bubble.style.maxWidth = `${cap}px`
    bubble.style.transform = 'translateX(-50%)'

    const { shift, arrowOffset } = computeTipShift(bubble.getBoundingClientRect(), clipRect, PAD)
    bubble.style.transform = `translateX(calc(-50% + ${shift}px))`
    arrow.style.left = `calc(50% + ${arrowOffset}px)`
  }, [tipOpen, tip])
  const button = (
    <button
      disabled={disabled}
      onClick={()=>{
        if (disabled) return
        // Mobile has no hover, so the first tap reveals the tip and the second
        // selects. Carried over from BINSR's renderChip verbatim — without it,
        // a phone user picks FHA having never seen what FHA changes.
        if (isMobile && tip && !tipOpen) { setTipOpen(true); return }
        setTipOpen(false)
        onClick()
      }}
      onMouseEnter={()=>setHover(true)}
      onMouseLeave={()=>setHover(false)}
      style={{display:'inline-flex',alignItems:'center',gap:'6px',
        // Two tones on one engine. The default branch is the original string,
        // character for character, because the Deal Desk and BINSR mode rows
        // must not move when a different caller opts into blue.
        ...(tone === 'blue'
          ? {
              // Unselected was transparent on #ececec, which reads as a LABEL
              // rather than a control — and on these rows nothing is selected by
              // default, so an agent sees three rows of what look like captions.
              // White on a real border is the same treatment as every other
              // input on these pages. The border darkens on hover, which is the
              // only thing that says "this responds to you" before the click.
              border: selected
                ? '1.5px solid #147efb'
                : `0.5px solid ${hover && !disabled ? '#d8d8d4' : '#e8e8e8'}`,
              borderRadius: '11px',
              background: selected ? 'rgba(20,126,251,0.08)' : '#ffffff',
              // One colour, which the icon inherits through currentColor.
              color: selected ? '#147efb' : '#666',
            }
          : {
              border: `0.5px solid ${selected ? '#147efb' : '#e8e8e8'}`,
              borderRadius: '18px',
              background: selected ? '#f4f4f2' : 'transparent',
              color: selected ? '#1d2e42' : '#666',
            }),
        padding:'5px 12px',fontSize:'12px',
        fontFamily:FONT_BODY,fontWeight:600,whiteSpace:'nowrap',
        cursor:disabled ? 'default' : 'pointer',
        // Spread rather than `opacity: disabled ? 0.4 : 1`. The ternary emits
        // opacity:1 on every enabled pill, which is a no-op visually but changes
        // the rendered style string — and Deal Desk's and BINSR's pills are
        // supposed to come out byte-identical to before this prop existed.
        ...(disabled ? { opacity: 0.4 } : {}),
        transition:'all .15s'}}>
      {icon}
      {label}
    </button>
  )
  if (!tip) return button
  return (
    <div style={{position:'relative',display:'inline-block'}}
      onMouseEnter={()=>!isMobile && setTipOpen(true)}
      onMouseLeave={()=>setTipOpen(false)}>
      {button}
      {tipOpen && (
        <div ref={bubbleRef} style={{position:'absolute',bottom:'calc(100% + 8px)',left:'50%',transform:'translateX(-50%)',
          background:'#1d2e42',color:'white',fontSize:'12px',padding:'8px 12px',borderRadius:6,
          maxWidth:240,width:'max-content',whiteSpace:'normal',zIndex:50,fontFamily:FONT_BODY,lineHeight:1.4,
          boxShadow:'0 4px 12px rgba(0,0,0,0.2)',pointerEvents:'none',
        }}>
          {tip}
          {/* Positioned independently of the bubble now. It tracks the pill,
              the bubble tracks the container, and the layout effect keeps the
              two in step. */}
          <div ref={arrowRef} style={{position:'absolute',top:'100%',left:'50%',transform:'translateX(-50%)',
            width:0,height:0,borderLeft:'6px solid transparent',borderRight:'6px solid transparent',borderTop:'6px solid #1d2e42'
          }} />
        </div>
      )}
    </div>
  )
}

// tip and isMobile are optional and only BINSR passes them. Every existing
// call site passes four arguments and is unaffected.
function dealdeskPill(selected, label, onClick, icon, tip, isMobile, disabled, tone) {
  return (
    <DealDeskPillButton
      key={label}
      selected={selected}
      label={label}
      onClick={onClick}
      icon={icon}
      tip={tip}
      isMobile={isMobile}
      disabled={disabled}
      tone={tone}
    />
  )
}

// Rail-style inline SVG, currentColor so each pill's selected/unselected colour
// carries into its icon.
function DraftFromDocsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
  )
}

function CleanUpTermsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="19" x2="17" y2="7"/>
      <path d="M15 5l4 4"/>
      <path d="M18 14l.7 2.3 2.3.7-2.3.7L18 20l-.7-2.3-2.3-.7 2.3-.7z"/>
      <path d="M6 3l.6 1.9L8.5 5.5 6.6 6.1 6 8l-.6-1.9L3.5 5.5l1.9-.6z"/>
    </svg>
  )
}

// ─── COPY TO CLIPBOARD ──────────────────────────────────────────────────────
// The model puts the draft first and its commentary below a horizontal rule.
// Only the draft belongs on a form, so the copy stops at that rule.
//
// Detection matches renderCaseMarkdown's rule exactly — a trimmed line of three
// or more hyphens — because the intent is "copy above the line the agent can
// see." stripMarkdown's HR rule is looser and would also fire on `* * *`, which
// the renderer does not draw, so a copy could silently stop somewhere the agent
// sees no separator at all. Table dividers begin with `|` and match neither.
//
// No separator means the whole response is the draft. That is the safe
// direction: copying commentary the agent then deletes costs them a moment,
// while guessing a boundary that is not there could cut the draft in half.
function dealdeskDraftPortion(content) {
  if (!content) return ''
  const lines = content.split('\n')
  const cut = lines.findIndex(l => /^-{3,}$/.test(l.trim()))
  return (cut === -1 ? lines : lines.slice(0, cut)).join('\n').trim()
}

function dealdeskCopyText(content) {
  return stripMarkdown(dealdeskDraftPortion(content))
}

function dealdeskFormSetShort(value) {
  return DEALDESK_FORM_SETS.find(f => f.value === value)?.short || value || 'No form set'
}

// ─── SESSION ATTESTATION ────────────────────────────────────────────────────
// The value logged with every attestation. Declared by the prompt itself at
// supabase/functions/dealdesk-chat/index.ts:33.
const DEALDESK_DISCLAIMER_VERSION = 'DD-3.7'

// SOLE SOURCE — this text is no longer a duplicate of anything. v3.5 dropped
// Section 0 entirely, along with every other numbered section; the prompt now
// contains no attestation language at all. What follows is the only copy that
// exists, so it is not at risk of drifting out of step with the prompt — but it
// is also no longer backstopped by one. If it is deleted or garbled, nothing
// anywhere re-presents the gate.
//
// Through v3.3 this duplicated the Section 0 blockquote at index.ts lines
// 36-50; that range no longer exists. Do not go looking for it, and do not
// re-derive this text from the prompt.
//
// DEALDESK_DISCLAIMER_VERSION above still tracks the prompt, because the
// attestation is logged against whatever version was live when it was signed.
// Bump it on every prompt revision even when this text is untouched.
//
// Two deliberate departures from the wording as it last appeared in Section 0,
// neither of them content:
//   - the markdown "> " blockquote markers are dropped, being syntax
//   - the trailing "______________________" is dropped, because in a chat that
//     underscore run IS the input; here there is a real field below it
const DEALDESK_ATTESTATION = `**Before you use HomePros DealDesk A.I., read this.**

This tool drafts contract language. It does not practice law, it does not approve documents, and it does not supervise you.

**You are responsible for every single word that goes into a contract.** Not this tool. Not the AI. You. If language drafted here ends up in an executed document, it is your work product and your license behind it.

By typing your name below you acknowledge:

1. All output is a draft for your review. You will read every word before it leaves your hands.
2. You will verify every form reference, section number, line number, and deadline against the actual document governing your transaction. Form revisions change; this tool's reference copies may be older than yours.
3. You are solely responsible for compliance with Arizona law, A.A.C. Title 4 Ch. 28, ADRE rules, and HomePros policies and procedures.
4. This tool does not give legal advice and does not create an attorney-client relationship.
5. **When in doubt, you call your broker.** Using AI is not an excuse for ignorance. "The AI wrote it" is not a defense at ADRE, in arbitration, or in court.`

// Blocks the whole /dealdesk route until the agent types their name. Not
// dismissible by design: no close control, and S.overlay carries no onClick, so
// there is nothing to click through. Session-scoped state lives in Dashboard —
// the case view remounts on every case switch, so holding it here would
// re-prompt per case instead of per session.
function DealDeskAttestationGate({ session, caseId, isMobile, onAttested }) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const trimmed = name.trim()
  const words = trimmed.split(/\s+/).filter(Boolean)
  // Two words and eight characters, per spec. Rejects initials, "ok", "yes",
  // and any single word. It cannot reject "asdf asdf" — no client-side rule
  // can, and Section 0 puts the plausibility test on the model, not the field.
  const valid = words.length >= 2 && trimmed.length >= 8

  async function submit() {
    if (!valid || saving) return
    setSaving(true)
    setError('')
    // attested_at comes from the column default rather than the client clock —
    // this is an evidence row, and the server's time is the defensible one.
    const { error: insErr } = await supabase.from('dealdesk_attestations').insert({
      user_id: session.user.id,
      case_id: caseId || null,
      typed_name: trimmed,
      disclaimer_version: DEALDESK_DISCLAIMER_VERSION,
      kind: 'session',
    })
    if (insErr) {
      // Deliberately stays blocked. An attestation that was not written is the
      // same as no attestation, so a failed insert must not open the door.
      setError(insErr.message || 'Could not record your attestation. Please try again.')
      setSaving(false)
      return
    }
    setSaving(false)
    // The name goes back up rather than being discarded. A copy attestation has
    // no name of its own — asking for one on every copy would get clicked
    // through, which makes the record worse — so it reuses the one signed here,
    // under the same disclaimer version. typed_name is NOT NULL, so there has to
    // be a real value, and inventing a sentinel would poison the only column
    // that exists to hold evidence.
    onAttested(trimmed)
  }

  return (
    <div style={S.overlay}>
      <div style={{
        background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
        padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
      }}>
        <div style={{fontSize:'14px', color:'#333', lineHeight:1.65}}>
          {renderCaseMarkdown(DEALDESK_ATTESTATION)}
        </div>

        <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8'}}>
          <div style={{fontSize:'14px', fontWeight:700, color:'#1d2e42', marginBottom:'10px'}}>
            Type your full legal name to continue:
          </div>
          <input
            value={name}
            onChange={e=>setName(e.target.value)}
            onKeyDown={e=>{ if (e.key === 'Enter') submit() }}
            placeholder="First and last name"
            autoFocus={!isMobile}
            disabled={saving}
            style={{...S.input, marginBottom:'12px'}}
          />
          {error && (
            <div style={{
              padding:'10px 14px', marginBottom:'12px',
              background:'rgba(255,60,60,0.08)', border:'1px solid rgba(255,60,60,0.2)',
              color:'#cc3333', fontSize:'13px', borderRadius:'8px',
            }}>{error}</div>
          )}
          <button
            onClick={submit}
            disabled={!valid || saving}
            style={{...S.btnPrimary, width:'100%', flex:'none', padding:'13px', fontSize:'13px', borderRadius:'10px',
                    opacity:(!valid||saving) ? 0.5 : 1, cursor:(!valid||saving) ? 'not-allowed' : 'pointer'}}>
            {saving ? 'Recording…' : 'I understand — continue'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DealDeskPage({ session, showToast, isMobile, onOpenCase, profile, navigateTo, isAdmin, signOut, attested }) {
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  // Both null on purpose. See DEALDESK_FORM_SETS.
  const [newFormSet, setNewFormSet] = useState(null)
  const [newRepresentation, setNewRepresentation] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const fetchCases = useCallback(async () => {
    const { data, error } = await supabase.from('dealdesk_cases')
      .select('*, dealdesk_case_files(count), dealdesk_case_messages(count)')
      .eq('user_id', session.user.id).order('updated_at', { ascending: false })
    if (error) showToast('Could not load cases')
    if (data) setCases(data)
    setLoading(false)
  }, [session.user.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCases() }, [fetchCases])

  const canCreate = newTitle.trim().length >= 3 && !!newFormSet && !!newRepresentation

  async function createCase() {
    // The overlay covers the viewport, but there is no focus trap, so the
    // fields behind it are still tab-reachable. Guarding the action itself is
    // what actually holds.
    if (!attested) return
    if (!canCreate || creating) return
    setCreating(true)
    const { data, error } = await supabase.from('dealdesk_cases')
      .insert({
        user_id: session.user.id,
        title: newTitle.trim(),
        form_set: newFormSet,
        representation: newRepresentation,
      }).select().single()
    if (error) { showToast('Error creating case'); setCreating(false); return }
    setCreating(false)
    setNewTitle('')
    setNewFormSet(null)
    setNewRepresentation(null)
    onOpenCase(data.id)
  }

  // This one already caught driver errors, but not refusals — which is the case
  // that actually happens under RLS. .select() plus a row count closes it, same
  // guard as AdminPage and the other two lists.
  async function deleteCase(caseId) {
    const { data: deleted, error } = await supabase
      .from('dealdesk_cases').delete().eq('id', caseId).select()
    if (error) { showToast('Delete failed: ' + error.message); setConfirmDelete(null); return }
    if (!deleted || deleted.length === 0) {
      showToast('Delete was refused — the case is still there. You may not have permission to delete it.')
      setConfirmDelete(null)
      return
    }
    setCases(prev => prev.filter(c => c.id !== caseId))
    setConfirmDelete(null)
    showToast('Case deleted')
  }

  const listNode = (
    <div>
      {/* No badge, same reason as Inspection Desk — nothing live sits behind
          this tool to report the state of. */}
      <ToolIntroBand
        title="Deal Desk"
        description="Contract questions answered against the forms in play. Upload what you're working from, then ask."
        stats={[
          (<><span style={{color:'#fdbf00'}}>AAR forms 2025</span></>),
          'MLS Choice · Jointly',
          'You are responsible for every term',
        ]}
      />

      {/* White card, matching the Strategist and Inspection Desk lists. The
          grey gradient panel this replaces made the least-used action the
          heaviest object under the band; the header line names it now that the
          panel does not. */}
      <div style={{background:'#ffffff',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'13px 14px',marginBottom:'14px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'7px',marginBottom:'9px'}}>
          <span style={{color:'#147efb',fontSize:'14px',lineHeight:1,fontFamily:FONT_BODY}}>+</span>
          <span style={{fontSize:'12.5px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY}}>Start a new case</span>
        </div>
        {/* Both groups on one line, so the card is two rows tall rather than
            three. Column on a phone rather than relying on flexWrap: Form Set's
            labels are long enough that wrapping leaves the second group on a
            line of its own anyway, and the explicit column keeps the gap even
            when it does. */}
        <div style={{display:'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '28px', flexWrap:'wrap', marginBottom:'12px'}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',marginBottom:'6px',fontFamily:FONT_HEAD}}>FORM SET</div>
            <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
              {DEALDESK_FORM_SETS.map(f => dealdeskPill(newFormSet === f.value, f.label, ()=>setNewFormSet(f.value), pillIcon(TILE_ICONS[f.value] || ''), undefined, isMobile, false, 'blue'))}
            </div>
          </div>

          <div style={{minWidth:0}}>
            <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',marginBottom:'6px',fontFamily:FONT_HEAD}}>I REPRESENT</div>
            <div style={{display:'flex',gap:'6px'}}>
              {/* Argument-for-argument the same call BinsrPage's repChip makes, so
                  the two Buyer/Seller rows in this app cannot drift apart again. */}
              {dealdeskPill(newRepresentation === 'buyer', 'Buyer', ()=>setNewRepresentation('buyer'), pillIcon(TILE_ICONS['Buyer'] || ''), undefined, isMobile, false, 'blue')}
              {dealdeskPill(newRepresentation === 'seller', 'Seller', ()=>setNewRepresentation('seller'), pillIcon(TILE_ICONS['Seller'] || ''), undefined, isMobile, false, 'blue')}
            </div>
          </div>
        </div>

        <div style={createRow(isMobile)}>
          <input
            value={newTitle} onChange={e=>setNewTitle(e.target.value)}
            onKeyDown={e=>{ if (e.key==='Enter') createCase() }}
            placeholder="e.g. 1234 E Main St — inspection period extension"
            // This is the name in the case list and on the review card, not a
            // place to paste the question. Nothing derived the title before, so
            // a pasted paragraph became the heading of every list it appeared
            // in. canCreate still only requires 3 characters.
            maxLength={60}
            style={{...S.input, flex:1, minWidth:0, marginBottom:0, background:'#ffffff'}}
          />
          {/* Gold, not S.btnPrimary's blue: blue is reserved for interactive and
              selected states, and the screen's one primary action is gold.
              Overridden INLINE — S.btnPrimary is shared across the file, so
              repainting it here would repaint every other screen. canCreate,
              which also requires a form set and a representation, is untouched. */}
          <button onClick={createCase} disabled={!canCreate||creating} style={{...S.btnPrimary,background:'#fdbf00',color:'#412402',fontWeight:500,letterSpacing:'normal',textTransform:'none',flex:'none',flexShrink:0,padding:'9px 18px',fontSize:'12px',opacity:(!canCreate||creating)?0.5:1}}>
            {creating ? 'Creating...' : 'Create Case'}
          </button>
        </div>
        {!canCreate && !creating && (
          <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY,marginTop:'8px'}}>
            Pick a form set and who you represent, then name the case.
          </div>
        )}
      </div>

      {cases.length === 0 ? (
        <div style={{padding:'24px 0',color:'#999',fontSize:'14px',fontFamily:FONT_BODY}}>
          No Deal Desk cases yet.
        </div>
      ) : (
        <div style={caseGrid(isMobile)}>
          {cases.map(c => {
            const fileCount = c.dealdesk_case_files?.[0]?.count || 0
            const msgCount = c.dealdesk_case_messages?.[0]?.count || 0
            // Form set and representation both moved out of the meta line and
            // into chips — same dealdeskFormSetShort text as before.
            const isSeller = c.representation === 'seller'
            const formSet = dealdeskFormSetShort(c.form_set)
            const meta = [
              timeAgo(c.updated_at),
              fileCount > 0 ? `${fileCount} file${fileCount!==1?'s':''}` : null,
              msgCount > 0 ? `${msgCount} message${msgCount!==1?'s':''}` : null,
            ].filter(Boolean).join(' · ')
            return (
              <CaseCard
                key={c.id}
                chips={<>
                  {formSet && caseChip('grey', formSet)}
                  {caseChip(isSeller ? 'gold' : 'blue', isSeller ? 'SELLER' : 'BUYER')}
                </>}
                address={c.title || 'Untitled case'}
                meta={meta}
                onOpen={()=>onOpenCase(c.id)}
                deletable
                confirming={confirmDelete === c.id}
                onAskDelete={()=>setConfirmDelete(c.id)}
                onConfirmDelete={()=>deleteCase(c.id)}
                onCancelDelete={()=>setConfirmDelete(null)}
              />
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Deal Desk"
      summary={null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={DEALDESK_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

// citation_flag is stored as `key: payload`. Three keys, and they mean
// different things to the agent:
//   unsourced_reference   nothing was attached and you did not name it
//   not_in_documents      the documents WERE read, and it is not in them
//   verification_failed   the check could not be made at all
// One sentence for all three used to say "nothing was attached", which is
// flatly untrue of the last two — a banner that misdescribes why is worse than
// no banner, because the agent acts on the reason.
//
// Module scope, not inside the component: it is a pure function of a string,
// and both call sites — the transcript banner and the copy modal — live in
// DealDeskCaseView.
function citationBannerText(flag) {
  if (typeof flag !== 'string') return null
  if (flag.startsWith('not_in_documents:')) {
    return 'This section was not found in the documents on this case. Check it against the contract before you send.'
  }
  if (flag.startsWith('verification_failed:')) {
    return "We couldn't verify this section against the documents. Check it yourself before you send."
  }
  return 'Nothing was attached to this case and you did not name it, so it could not be sourced.'
}

// ─── DEAL DESK: CASE VIEW ───────────────────────────────────────────────────
function DealDeskCaseView({ session, caseId, showToast, isMobile, onBack, profile, navigateTo, isAdmin, signOut, attested, attestedName }) {
  const shellRef = useRef(null)
  // null = not yet known, and the shell treats nullish as closed. Defaulting to
  // true instead asserts "this case has no documents" before the files query has
  // answered, so a case that does have them paints the panel open and then
  // closes it a frame later. One paint, in the right state, is the whole point.
  const [setupOpen, setSetupOpen] = useState(null)
  const setupInitRef = useRef(false)
  const [caseData, setCaseData] = useState(null)
  const [title, setTitle] = useState('')
  const [files, setFiles] = useState([])
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  // The two axes the edge function emits before any text. Held for the current
  // turn so the banners render above the answer rather than after it.
  const [turnTier, setTurnTier] = useState(null)
  const [turnReason, setTurnReason] = useState(null)
  const [turnRetrieval, setTurnRetrieval] = useState(null)
  // Per turn, but sticky across turns of this case: cleaning up three clauses in
  // a row should not cost three clicks. The view remounts on every case switch
  // via key={activeCaseId}, so this resets when the agent leaves the case.
  const [mode, setMode] = useState(DEALDESK_MODE_DOCUMENTS)
  // Set only by a send that was refused, never by the mere absence of files — a
  // case starts empty, and a standing banner would read as an error on a case
  // the agent just created. The upload card already owns that moment.
  const [blockedNotice, setBlockedNotice] = useState(false)
  // The message awaiting a copy confirmation, or null. Holds the row itself so
  // the modal can read citation_flag off it without a second lookup.
  const [copyTarget, setCopyTarget] = useState(null)
  const [copying, setCopying] = useState(false)

  useEffect(() => {
    let cancelled = false
    supabase.from('dealdesk_cases').select('*').eq('id', caseId).single()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data) { showToast('Case not found.'); onBack(); return }
        setCaseData(data)
        setTitle(data.title || '')
      })
    // Setup collapses on documents, not on messages. Uploading a contract
    // mid-case is the normal path here, so collapsing once a transcript exists
    // would hide the upload control on every return visit and read as "you can't
    // add documents anymore". A case with no contract is unusable, so zero files
    // keeps the panel open no matter how long the transcript is.
    supabase.from('dealdesk_case_files').select('*').eq('case_id', caseId).order('created_at')
      .then(({ data }) => {
        if (cancelled) return
        if (data) setFiles(data)
        // A failed read leaves the panel open, which is the harmless direction.
        if (!setupInitRef.current) { setupInitRef.current = true; setSetupOpen((data || []).length === 0) }
      })
    supabase.from('dealdesk_case_messages').select('*').eq('case_id', caseId).order('created_at')
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) showToast('Could not load messages')
        setMessages(data || [])
      })
    return () => { cancelled = true }
  }, [caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChatScroll = useCallback((e) => {
    const el = e.currentTarget
    if (!el) return
    setShouldAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 100)
  }, [])

  const scrollToBottom = () => { if (shouldAutoScroll) shellRef.current?.scrollToBottom() }
  const pinAssistantToTop = () => { if (shouldAutoScroll) shellRef.current?.pinAssistantToTop() }

  async function saveTitle() {
    const trimmed = title.trim()
    if (!caseData || trimmed === (caseData.title || '')) return
    const { error } = await supabase.from('dealdesk_cases').update({ title: trimmed }).eq('id', caseId)
    if (error) { showToast('Could not save title'); setTitle(caseData.title || ''); return }
    setCaseData(prev => ({ ...prev, title: trimmed }))
  }

  function sanitizeFilename(name) {
    const lastDot = name.lastIndexOf('.')
    const base = lastDot > 0 ? name.slice(0, lastDot) : name
    const ext = lastDot > 0 ? name.slice(lastDot) : ''
    const cleanBase = base.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase()
    const cleanExt = ext.trim().toLowerCase().replace(/[^a-zA-Z0-9.]/g, '')
    return `${cleanBase || 'file'}${cleanExt}`
  }

  // Unlike binsr_case_files, the path is written to the row rather than rebuilt
  // at send time. The edge function reads storage_path directly and never takes
  // a path from the client.
  async function uploadFile(file) {
    setUploading(true)
    try {
      const prefixedName = `${Date.now()}-${sanitizeFilename(file.name)}`
      const fullPath = `${session.user.id}/${caseId}/${prefixedName}`

      const { error: upErr } = await supabase.storage.from('dealdesk-docs').upload(fullPath, file, { upsert: false })
      if (upErr) { showToast('Upload failed: ' + upErr.message); setUploading(false); return }

      const { data: row, error: insertErr } = await supabase.from('dealdesk_case_files')
        .insert({ case_id: caseId, file_name: prefixedName, storage_path: fullPath, file_type: file.type || null })
        .select().single()
      if (insertErr) {
        // The object is already in the bucket; without its row nothing will ever
        // reference it, so take it back out rather than leaving it orphaned.
        await supabase.storage.from('dealdesk-docs').remove([fullPath])
        showToast('Upload failed: ' + insertErr.message)
        setUploading(false)
        return
      }

      setFiles(prev => [...prev, row])
      // The refusal was about this exact gap, and it has just been closed.
      setBlockedNotice(false)
      setUploading(false)
      showToast('File uploaded')
    } catch (e) {
      showToast('Upload failed: ' + (e.message || String(e)))
      setUploading(false)
    }
  }

  async function removeFile(row) {
    if (row.storage_path) await supabase.storage.from('dealdesk-docs').remove([row.storage_path])
    const { error } = await supabase.from('dealdesk_case_files').delete().eq('id', row.id)
    if (error) { showToast('Could not remove file'); return }
    setFiles(prev => prev.filter(f => f.id !== row.id))
  }

  // The edge function persists both the question and the answer, so this sends
  // only case_id and the message — no history, no file list.
  async function sendMessage() {
    // Same reasoning as createCase: the composer is behind the overlay but
    // still tab-reachable without a focus trap. No attestation, no request.
    if (!attested) return
    const userMsg = input.trim()
    if (!userMsg || sending) return

    // The enforcement. Three prompt revisions each produced partial compliance,
    // because a model asked to draft will draft — so this turn never reaches a
    // model at all. Deterministic, no tokens spent, and it cannot drift.
    // The composer keeps its text: switching mode and pressing send again must
    // not cost the agent their typing.
    if (mode === DEALDESK_MODE_DOCUMENTS && files.length === 0) {
      setBlockedNotice(true)
      return
    }
    setBlockedNotice(false)

    setInput('')
    setTurnTier(null)
    setTurnReason(null)
    setTurnRetrieval(null)

    const tempAssistantId = 'temp-assistant-' + Date.now()
    const tempUserMsg = { id: 'temp-user-' + Date.now(), role: 'user', content: userMsg, created_at: new Date().toISOString() }
    const tempAssistantMsg = { id: tempAssistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true }
    setMessages(prev => [...prev, tempUserMsg, tempAssistantMsg])
    setSending(true)
    scrollToBottom()

    let pinnedOnFirstToken = false

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const token = currentSession?.access_token

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/dealdesk-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ case_id: caseId, user_message: userMsg, mode }),
      })

      if (!res.ok || !res.body) {
        throw new Error(`Stream failed: ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let accumulatedText = ''
      let streamError = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (!payload) continue
          // Parsing and handling are separated deliberately: a malformed frame
          // is skipped, but an error carried INSIDE a well-formed frame must
          // still surface. ADA conflates the two by string-matching the thrown
          // value, which swallows real parse failures.
          let evt
          try {
            evt = JSON.parse(payload)
          } catch {
            console.error('dealdesk: unparseable SSE frame', payload)
            continue
          }
          if (evt.tier) setTurnTier(evt.tier)
          // Sent on the same frames as tier. Only 'financing-terms' changes the
          // banner; every other reason is for the supervision record.
          if (evt.tier_reason) setTurnReason(evt.tier_reason)
          if (evt.retrieval_status) setTurnRetrieval(evt.retrieval_status)
          if (evt.text) {
            accumulatedText += evt.text
            const updatedText = accumulatedText
            setMessages(prev => prev.map(m =>
              m.id === tempAssistantId ? { ...m, content: updatedText } : m
            ))
            if (!pinnedOnFirstToken) { pinnedOnFirstToken = true; pinAssistantToTop() }
          }
          if (evt.error) { streamError = new Error(evt.error) }
          if (evt.done) {
            const { data: fresh } = await supabase.from('dealdesk_case_messages').select('*').eq('case_id', caseId).order('created_at')
            if (fresh) setMessages(fresh)
          }
        }
      }

      if (streamError) throw streamError
    } catch (e) {
      setMessages(prev => prev.map(m =>
        m.id === tempAssistantId ? { ...m, content: e.message, error: true, streaming: false } : m
      ))
    }
    setSending(false)
  }

  // Fires on Copy, never on open. Cancel writes nothing and logs nothing — the
  // attestation is a record that the agent took the text, so a modal they backed
  // out of must leave no trace.
  async function confirmCopy() {
    if (!copyTarget || copying) return
    setCopying(true)
    const text = dealdeskCopyText(copyTarget.content)
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      showToast('Copy failed — try selecting manually')
      setCopying(false)
      setCopyTarget(null)
      return
    }
    // Logged after the clipboard write succeeds, so the record says the text was
    // actually taken. A failed write with a row behind it would overstate what
    // happened. A failed log after a successful copy is the lesser error and is
    // reported rather than silently swallowed.
    const { error: logErr } = await supabase.from('dealdesk_attestations').insert({
      user_id: session.user.id,
      case_id: caseId,
      typed_name: attestedName || pdfSafe(profile?.full_name, 'Unknown'),
      disclaimer_version: DEALDESK_DISCLAIMER_VERSION,
      kind: 'copy',
      output_ref: String(copyTarget.id),
    })
    if (logErr) {
      console.error('dealdesk: copy attestation insert failed:', logErr.message)
      showToast('Copied — but the record failed to save')
    } else {
      showToast('Copied to clipboard')
    }
    setCopying(false)
    setCopyTarget(null)
  }

  const renderMessage = (m, streamingRef) => (
    <div key={m.id} ref={typeof m.id === 'string' && m.id.startsWith('temp-assistant-') ? streamingRef : null}>
      <div style={{display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
      {m.role === 'user' ? (
        <div style={{maxWidth:'80%',background:'#f4f4f2',color:'#1d2e42',borderRadius:'14px',padding:'12px 16px',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>
          {m.content}
        </div>
      ) : (
        // No pre-wrap here: renderCaseMarkdown emits block elements, and
        // preserved newlines on top of them double every gap. An error is a
        // plain string, so it stays raw.
        <div style={{width:'100%',color:m.error?'#C8102E':'#1d2e42',fontSize:'14px',lineHeight:1.65,fontFamily:FONT_BODY,overflowWrap:'anywhere'}}>
          {/* Rendered from the row rather than from the turn's SSE frames, so it
              survives a reload — the record is the point. Sits above the draft
              because it changes how the draft should be read. */}
          {m.citation_flag && (
            <div style={{background:'#fffdf5',border:'1px solid #fdbf00',borderRadius:'10px',padding:'9px 13px',marginBottom:'10px',fontSize:'12.5px',lineHeight:1.5,color:'#7a5c00'}}>
              A section reference was removed — the draft shows <strong>[SECTION — VERIFY]</strong> where it appeared. {citationBannerText(m.citation_flag)}
            </div>
          )}
          {m.error ? m.content : renderCaseMarkdown(m.content)}
          {m.streaming && !m.content && (
            <ThinkingRow label="Checking the forms…" />
          )}
          {/* Shown on every settled assistant message. No attempt to detect
              whether it contains a draft: a heuristic misfires both ways, and a
              button on a clarifying question is ignorable, while a missing one
              on a real draft sends the agent to hand-select and lose the
              renumbering. Suppressed only where it cannot mean anything. */}
          {!m.error && !m.streaming && m.content && (
            <div style={{marginTop:'10px'}}>
              {/* Text link, matching every other action in the product. The
                  copied confirmation lives in the modal this opens, not here. */}
              <button
                onClick={()=>setCopyTarget(m)}
                style={{background:'none',border:'none',padding:0,color:'#147efb',fontSize:'12px',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>
                Copy to clipboard
              </button>
            </div>
          )}
        </div>
      )}
      </div>
      {m.role === 'assistant' && messages[messages.length - 1]?.id !== m.id && (
        <div style={{height:'0.5px',background:'#e8e8e8',marginTop:'18px'}} />
      )}
    </div>
  )

  const emptyState = (
    <div style={{paddingTop:'40px'}}>
      <div style={{fontSize:isMobile?'24px':'30px',fontWeight:900,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'1px',lineHeight:1.1,marginBottom:'8px'}}>
        Deal Desk
      </div>
      <div style={{fontSize:'15px',color:'#555',fontFamily:FONT_BODY}}>
        Contract questions against {dealdeskFormSetShort(caseData?.form_set)}. Upload the forms in play, then ask.
      </div>
    </div>
  )

  // Form set drift is invisible if the only place it appears is the screen the
  // agent saw once at creation, so it sits in the header beside the title and
  // stays there whether or not setup is open.
  const summaryNode = (
    <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'12px',color:'#555',fontFamily:FONT_BODY,minWidth:0}}>
      {/* minWidth:0 + ellipsis so the pill shrinks like BINSR's summary span
          rather than holding the row open at its own text width. */}
      <span style={{display:'inline-flex',alignItems:'center',minWidth:0,padding:'2px 9px',borderRadius:'12px',background:'#f4f4f2',border:`0.5px solid ${RAIL_BORDER}`,color:'#1d2e42',fontWeight:700,fontSize:'11px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
        {dealdeskFormSetShort(caseData?.form_set)}
      </span>
      <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
        {[
          caseData?.representation === 'seller' ? 'Seller' : 'Buyer',
          // Stated at zero rather than omitted: a missing count reads as "not
          // worth mentioning", and the gap is the thing worth mentioning.
          files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : 'No documents',
        ].filter(Boolean).join(' · ')}
      </span>
    </div>
  )

  // The hidden input is the same in both branches; only its trigger changes, so
  // it lives here rather than being written twice.
  const fileInput = (
    <input
      type="file"
      accept=".pdf"
      disabled={uploading}
      style={{display:'none'}}
      onChange={async e=>{
        const f = e.target.files[0]
        e.target.value = ''
        if (!f) return
        await uploadFile(f)
      }}
    />
  )

  // Mirrors goldBtn (AssistantWorkspace, ~5162) — same fill, radius and type
  // scale. Not imported because that one is local to its component.


  // 520px so the cards read as a panel rather than a band stretched across the
  // 900px transcript column. Centered to sit under that column, not beside it.
  const setupNode = (
    <div style={{width:'100%',maxWidth:SETUP_CONTENT_WIDTH,display:'flex',flexDirection:'column',gap:'18px'}}>
      {/* FORM SET — information, not a control. It is fixed when the case is
          created and there is no path to change it here, so a blue card that
          looks like the editable ones was overstating what an agent can do. */}
      <div style={{minWidth:0,display:'flex',flexDirection:'column',gap:'8px'}}>
        <div style={SETUP_EYEBROW}>FORM SET</div>
        <div style={{display:'flex',alignItems:'center',gap:'11px',background:'#f4f4f2',border:'0.5px solid #e8e8e8',borderRadius:'10px',padding:'12px 14px'}}>
          <span style={{flexShrink:0,display:'flex'}}>
            <DealDeskIcon size={17} color="#147efb" />
          </span>
          <div style={{minWidth:0}}>
            <div style={{fontSize:'13.5px',color:'#1d2e42',fontFamily:FONT_BODY}}>
              {DEALDESK_FORM_SETS.find(f => f.value === caseData?.form_set)?.label || caseData?.form_set || 'Form set not set'}
            </div>
            <div style={{fontSize:'11.5px',color:'#999',fontFamily:FONT_BODY}}>Fixed at creation</div>
          </div>
        </div>
      </div>

      {/* SIDE — READ-ONLY. representation is written once, when the case is
          created, and nothing in this view writes it back: the only reference
          here is the read in summaryNode, and the only dealdesk_cases update
          is the title. So the pills state what the case was created as; the
          other side is disabled rather than being a control that silently
          discards the click.
          The read keeps its existing shape — anything that is not 'seller'
          reads as Buyer, including null. That is unchanged behaviour, not a
          decision made here. */}
      <PillSelector
        label="SIDE"
        options={[{ value:'buyer', label:'Buyer' }, { value:'seller', label:'Seller' }]}
        selected={caseData?.representation === 'seller' ? 'seller' : 'buyer'}
        onSelect={()=>{}}
        allowDeselect={false}
        disabledFor={v => v !== (caseData?.representation === 'seller' ? 'seller' : 'buyer')}
        hint="Fixed at creation"
      />

      <div style={{minWidth:0,display:'flex',flexDirection:'column',gap:'8px'}}>
        <div style={SETUP_EYEBROW}>DOCUMENTS</div>
        {/* Tiles, the same DocTile the other two tools use. Presentation only:
            fileInput stays a VALUE rather than a function — Deal Desk has one
            kind, so there is nothing to parameterise — removeFile still takes
            the whole row and still reads storage_path, and file_type still
            holds the MIME type rather than a semantic kind. Nothing about how
            a file is stored changed. */}
        <DocTileRow isMobile={isMobile}>
          {files.length === 0 ? (
            // ONE tile, and nothing beside it. There is nothing to add another
            // to yet, and an Add tile next to an empty slot would offer the
            // same action twice. Gold is the chip, not a fill: Deal Desk has no
            // primary setup action the way Strategy has Pull comps.
            <DocTile
              mode="empty"
              label="Your contract"
              sub={uploading ? 'Uploading…' : 'Answers stay general without it'}
              chip="Recommended"
              input={fileInput}
            />
          ) : (
            <>
              {files.map(f => (
                // removeFile takes the whole file object here, unlike Strategy
                // and BINSR which take (id, name). Left as it is.
                <DocTile
                  key={f.id}
                  mode="filled"
                  label={(f.file_name || '').replace(/^\d+-/, '')}
                  sub="Read on every turn"
                  onRemove={()=>removeFile(f)}
                />
              ))}
              <DocTile
                mode="add"
                label="Add another form"
                sub={uploading ? 'Uploading…' : 'Addendum, counter, disclosure'}
                input={fileInput}
              />
            </>
          )}
        </DocTileRow>
      </div>
    </div>
  )

  // Tier drives the banner; retrieval status is a separate axis and only says
  // something when the library did not answer. `empty` is a real answer — no law
  // matched — so it is not surfaced as a fault.
  const TIER_STYLES = {
    Elevated: { bg:'#fff8e6', border:'#fdbf00', color:'#7a5c00', label:'ELEVATED', text:'Higher-risk question. Read the answer against the form itself before you act on it.' },
    Stop: { bg:'#fdecea', border:'#c0392b', color:'#8e2318', label:'STOP', text:'This needs your broker or counsel. Do not act on this answer alone.' },
  }
  // The one Elevated case that does not point at the broker. Concession and
  // credit caps are set by the loan program, move over time, and are not in the
  // knowledge base — so the only correct answer to "how much" comes from the
  // lender, and this banner has to say so rather than sending them to a broker
  // who cannot know either.
  const TIER_STYLE_FINANCING = {
    bg:'#fff8e6', border:'#fdbf00', color:'#7a5c00', label:'ELEVATED — CHECK WITH THE LENDER',
    text:'Concession and credit limits depend on the loan program and change over time. This tool does not know the cap and will not state one. Confirm the limit with the lender before a number goes in the contract.',
  }
  const tierStyle = (turnTier === 'Elevated' && turnReason === 'financing-terms')
    ? TIER_STYLE_FINANCING
    : (turnTier ? TIER_STYLES[turnTier] : null)

  const controlsNode = (tierStyle || turnRetrieval === 'unavailable' || blockedNotice) ? (
    <div style={{padding:'0 20px 10px',display:'flex',justifyContent:'center',flexDirection:'column',alignItems:'center',gap:'6px'}}>
      {tierStyle && (
        <div style={{width:'100%',maxWidth:DEALDESK_CONTENT_WIDTH,background:tierStyle.bg,border:`1px solid ${tierStyle.border}`,borderRadius:'10px',padding:'9px 14px',display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_BODY}}>
          <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1px',color:tierStyle.color,fontFamily:FONT_HEAD,flexShrink:0}}>{tierStyle.label}</span>
          <span style={{fontSize:'12px',lineHeight:1.4,color:tierStyle.color}}>{tierStyle.text}</span>
        </div>
      )}
      {turnRetrieval === 'unavailable' && (
        <div style={{width:'100%',maxWidth:DEALDESK_CONTENT_WIDTH,background:'#f4f4f2',border:`0.5px solid ${RAIL_BORDER}`,borderRadius:'10px',padding:'9px 14px',fontSize:'12px',lineHeight:1.4,color:'#555',fontFamily:FONT_BODY}}>
          The law library did not respond for this question, so nothing below cites statute.
        </div>
      )}
      {/* Same gold treatment as the upload card in the setup panel — the two say
          the same thing about the same gap, so they should look alike. */}
      {blockedNotice && (
        <div style={{width:'100%',maxWidth:DEALDESK_CONTENT_WIDTH,background:'#fffdf5',border:'1px solid #fdbf00',borderRadius:'10px',padding:'10px 14px',fontSize:'12.5px',lineHeight:1.5,color:'#7a5c00',fontFamily:FONT_BODY}}>
          No contract attached to this case. Upload it to amend a term — or switch to <strong>Clean up my terms</strong> and give me the language you want tightened.
        </div>
      )}
    </div>
  ) : null

  // Sits in CaseShell's footer slot, which is the area directly below the
  // composer. No new prop needed — footer takes arbitrary JSX and the disclaimer
  // follows it. Switching mode clears a refusal from the previous attempt: the
  // agent has just done the thing the notice asked for.
  const modeRow = (
    <div style={{padding:'0 20px 12px',display:'flex',justifyContent:'center'}}>
      <div style={{width:'100%',maxWidth:DEALDESK_CONTENT_WIDTH,display:'flex',gap:'6px',flexWrap:'wrap'}}>
        {dealdeskPill(
          mode === DEALDESK_MODE_DOCUMENTS,
          'Draft from documents',
          ()=>{ setMode(DEALDESK_MODE_DOCUMENTS); setBlockedNotice(false) },
          <DraftFromDocsIcon />
        )}
        {dealdeskPill(
          mode === DEALDESK_MODE_CLEANUP,
          'Clean up my terms',
          ()=>{ setMode(DEALDESK_MODE_CLEANUP); setBlockedNotice(false) },
          <CleanUpTermsIcon />
        )}
      </div>
    </div>
  )

  const disclaimerNode = (
    <div style={{background:'#f4f4f2',borderTop:'0.5px solid #e8e8e8',color:'#666',padding:'11px 16px',display:'flex',alignItems:'center',gap:'10px',fontFamily:FONT_BODY}}>
      <div style={{fontSize:'13px',color:'#791F1F',flexShrink:0}}>⚠</div>
      <div style={{fontSize:'10.5px',lineHeight:1.4}}><span style={{fontWeight:700,color:'#791F1F',letterSpacing:'0.3px',fontFamily:FONT_HEAD}}>NOT LEGAL ADVICE&nbsp;&nbsp;</span>This tool helps you read your own contracts. You are responsible for every term you agree to on your client's behalf. When in doubt, contact your broker.</div>
    </div>
  )

  // Same shell as the attestation gate. Fires on every copy rather than once per
  // case: the point is the pause before text goes on a form, and a confirmation
  // that only appears the first time is one an agent stops seeing.
  //
  // Cancel is an explicit button because S.overlay carries no onClick and
  // nothing in this codebase handles Escape — there is no click-outside to
  // fall back on.
  const copyModal = copyTarget && (
    <div style={S.overlay}>
      <div style={{
        background:'white', borderRadius:'16px', width:'min(560px, 92vw)', maxWidth:'92vw',
        padding:isMobile?'24px 20px':'32px', maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 8px 40px rgba(0,0,0,0.15)', fontFamily:FONT_BODY,
      }}>
        <div style={{fontSize:'18px', fontWeight:700, color:'#1d2e42', fontFamily:FONT_HEAD, letterSpacing:'0.5px', marginBottom:'10px'}}>
          Read it before it goes on a form.
        </div>
        <div style={{fontSize:'14px', color:'#333', lineHeight:1.65}}>
          This is a draft. Verify every number, date, and section reference against your executed document. You are responsible for every word.
        </div>

        {/* The one thing worth saying at this exact moment: we already know a
            reference could not be sourced. Asking them to verify while withholding
            that would be perverse. */}
        {copyTarget.citation_flag && (
          <div style={{marginTop:'14px', background:'#fffdf5', border:'1px solid #fdbf00', borderRadius:'10px', padding:'10px 13px', fontSize:'12.5px', lineHeight:1.5, color:'#7a5c00'}}>
            This draft had a section reference removed — it shows <strong>[SECTION — VERIFY]</strong> where it appeared. {citationBannerText(copyTarget.citation_flag)}
          </div>
        )}

        <div style={{marginTop:'20px', paddingTop:'18px', borderTop:'1px solid #e8e8e8', display:'flex', gap:'10px'}}>
          <button
            onClick={()=>setCopyTarget(null)}
            disabled={copying}
            style={{...S.btnOutline, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', cursor:copying?'not-allowed':'pointer'}}>
            Cancel
          </button>
          <button
            onClick={confirmCopy}
            disabled={copying}
            style={{...S.btnPrimary, flex:1, padding:'13px', fontSize:'13px', borderRadius:'10px', opacity:copying?0.5:1, cursor:copying?'not-allowed':'pointer'}}>
            {copying ? 'Copying…' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
    {copyModal}
    <CaseShell
      ref={shellRef}
      isMobile={isMobile}
      title={(
        <input
          value={title}
          onChange={e=>setTitle(e.target.value)}
          onBlur={saveTitle}
          placeholder="Name this case..."
          // Same cap as the create field, so renaming cannot reintroduce what
          // creating can no longer produce.
          maxLength={60}
          style={{width:'100%',boxSizing:'border-box',fontSize:'16px',fontWeight:700,color:'#1d2e42',fontFamily:FONT_HEAD,letterSpacing:'0.5px',background:'transparent',border:'none',padding:0,outline:'none'}}
        />
      )}
      summary={summaryNode}
      setupOpen={setupOpen}
      // Marking the ref here makes a deliberate click authoritative. Without it,
      // toggling during the window before the files query answers would be
      // overwritten by the auto-init a moment later.
      onToggleSetup={()=>{ setupInitRef.current = true; setSetupOpen(o => !o) }}
      // Gold while the case has no documents, for the same reason the upload
      // card is: that is the state where opening the panel is the point. Once a
      // contract is attached it drops to the quiet grey pill.
      toggleLabel={setupOpen ? 'Hide' : 'Documents'}
      toggleLabelAccent={files.length === 0}
      setup={setupNode}
      // false, matching BINSR and Strategy. The shell's loading branch hides
      // setup along with the transcript, so gating it on the messages query
      // meant a slow or rejected fetch left the upload control unreachable.
      loading={false}
      messages={messages}
      renderMessage={renderMessage}
      emptyState={emptyState}
      onTranscriptScroll={handleChatScroll}
      contentWidth={DEALDESK_CONTENT_WIDTH}
      controls={controlsNode}
      composerValue={input}
      onComposerChange={e=>setInput(e.target.value)}
      onSubmit={()=>sendMessage()}
      composerPlaceholder="Ask about a term, a deadline, or a form..."
      sendDisabled={sending || !input.trim()}
      composerMultiline
      composerDisabled={sending}
      footer={<>{modeRow}{disclaimerNode}</>}
    />
    </>
  )
}

const MYWINS_CONTENT_WIDTH = '1320px'

// 2026-10-10: buyer-side sales get no card (the listing photos belong to the
// listing brokerage), so My Wins shows them as text rows.
const isBuyerSideWin = (w) => w.hp_side === 'buyer' || w.hp_side === 'co_buyer'
const MYWINS_STATUS_LABEL = { sold: 'Sold', pending: 'Pending', active: 'Just listed' }

function MyWins({ session, profile, showToast, isMobile, navigateTo, isAdmin, signOut }) {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [shown, setShown] = useState(10);
  const [modal, setModal] = useState(null);
  // The signed-in user's own MLS office, and whether they have none.
  const [myOffice, setMyOffice] = useState(null);
  const [noOffice, setNoOffice] = useState(false);
  const PAGE = 10;
  // The rail hides this row for a profile not licensed for live MLS data; this
  // covers opening the route directly. No query runs, one notice shows.
  const mlsLicensed = isMlsLicensed(profile)

  useEffect(() => {
    if (!mlsLicensed) { setLoading(false); return }
    (async () => {
      // 2026-10-26: the user's own brokerage office only (HomePros ajaa01,
      // HomePros Advantage hmpr001), from the database's one brokerage ->
      // office mapping. The read policies enforce the same; this filter
      // keeps the page honest on its own. No office: the notice, no query.
      const { data: office, error: officeErr } = await supabase.rpc('current_armls_office')
      if (officeErr || !office) { setNoOffice(true); setLoading(false); return }
      setMyOffice(office)
      const { data, error } = await supabase
        .from('listing_events')
        .select('id,listing_id,event_type,hp_office_id,hp_agent_name,address,card_square_url,card_story_url,detected_at,event_date,hp_agent_id,from_status,to_status,hp_side')
        .eq('hp_office_id', office)
        // Listing-side wins need a card, as before. Buyer-side sales have no
        // card (2026-10-10: the listing photos belong to the listing
        // brokerage) and are kept regardless, rendered as text rows below.
        .or('card_square_url.not.is.null,hp_side.in.(buyer,co_buyer)')
        .order('detected_at', { ascending: false });
      if (!error && data) {
        // DISPLAY-ONLY. Nothing is deleted; the rows stay in listing_events and
        // every other consumer still sees them. This narrows what the feed
        // paints, and it runs BEFORE state so the tab filter, the paging slice
        // and all three counts downstream operate on the cleaned set.

        // 1) DEDUPE: keep only the most-recent event per listing (by event_date).
        //    A listing that went active->pending->active should show once (its current status).
        const latestByListing = new Map()
        for (const ev of (data || [])) {
          const prev = latestByListing.get(ev.listing_id)
          // Compare by event_date; fall back to id if event_date missing/equal.
          const evTime = ev.event_date ? new Date(ev.event_date).getTime() : 0
          const prevTime = prev && prev.event_date ? new Date(prev.event_date).getTime() : -1
          if (!prev || evTime > prevTime || (evTime === prevTime && ev.id > prev.id)) {
            latestByListing.set(ev.listing_id, ev)
          }
        }
        let deduped = Array.from(latestByListing.values())

        // 2) HIDE DEPARTED AGENTS: only show events whose agent still exists in profiles.
        //    hp_agent_id is an ARMLS agent id, so it matches profiles.armls_agent_id
        //    rather than a primary key — the same field openWin reads below.
        const { data: activeAgents } = await supabase
          .from('profiles')
          .select('armls_agent_id')
          .not('armls_agent_id', 'is', null)
        const activeIds = new Set((activeAgents || []).map(a => a.armls_agent_id))
        deduped = deduped.filter(ev => ev.hp_agent_id && activeIds.has(ev.hp_agent_id))

        // Preserve the previous ordering (newest first) for display.
        deduped.sort((a, b) => {
          const at = a.event_date ? new Date(a.event_date).getTime() : 0
          const bt = b.event_date ? new Date(b.event_date).getTime() : 0
          return bt - at || b.id - a.id
        })

        setWins(deduped);
      }
      setLoading(false);
    })();
  }, [mlsLicensed]);

  async function openWin(w) {
    // Default to the deal-doer's card (current behavior).
    let view = w
    const myId = profile?.armls_agent_id
    if (myId && myOffice && w.listing_id) {
      // Do I have my own personalized version of this card? (My office only.)
      const { data: mine, error } = await supabase
        .from('agent_cards')
        .select('card_square_url, card_story_url')
        .eq('hp_office_id', myOffice)
        .eq('listing_id', w.listing_id)
        .eq('event_type', w.event_type)
        .eq('armls_agent_id', myId)
        .maybeSingle()
      if (!error && mine && (mine.card_square_url || mine.card_story_url)) {
        // Show MY version — fall back field-by-field to the deal-doer's if a url is missing.
        view = {
          ...w,
          card_square_url: mine.card_square_url || w.card_square_url,
          card_story_url: mine.card_story_url || w.card_story_url,
        }
      }
    }
    setModal(view)
  }

  // 'all' stays brokerage-wide — that feed is the point of the page and is not
  // being scoped. 'mine' is the one user-scoped view, and it is a SCOPE rather
  // than a status: it spans sold, pending and active alike. hp_agent_id is an
  // ARMLS id, so it compares against profiles.armls_agent_id, the same field
  // openWin reads. Both sides are lowercased because ARMLS ids arrive in mixed
  // case from the feed but are stored lowercase in profiles.
  const myArmlsId = (profile?.armls_agent_id || '').trim().toLowerCase()
  const filtered =
    filter === 'all' ? wins
    // No armls id on the profile (an admin without one, or an agent missing it)
    // means there is nothing to match. Return empty rather than comparing '' to
    // '', which would otherwise match every row with a blank hp_agent_id.
    : filter === 'mine' ? (myArmlsId ? wins.filter(w => (w.hp_agent_id || '').trim().toLowerCase() === myArmlsId) : [])
    : wins.filter(w => w.event_type === filter);
  const visible = filtered.slice(0, shown);

  // Same outlined pill Content and Events use. The bespoke solid-blue chips
  // carried #d9dde3 and #4a5361, neither of which appears anywhere else.
  const chip = (f, label) => {
    const sel = filter === f
    return (
      <button
        onClick={() => { setFilter(f); setShown(PAGE); }}
        style={{
          border:`0.5px solid ${sel ? '#147efb' : '#e8e8e8'}`,borderRadius:'18px',padding:'5px 12px',
          fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,whiteSpace:'nowrap',flexShrink:0,
          background:sel ? '#f4f4f2' : 'transparent',color:sel ? '#1d2e42' : '#666',
          cursor:'pointer',transition:'all .15s',
        }}
      >{label}</button>
    )
  };

  const summaryNode = (
    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', minWidth:0, overflowX:'auto' }}>
      {chip('all','All Wins')}{chip('mine','Just Mine')}{chip('sold','Sold')}{chip('pending','Pending')}{chip('active','Just Listed')}
    </div>
  )

  const listNode = (!mlsLicensed || noOffice) ? (
    <div style={{...reportNoticeStrip, marginTop:0}}>
      {reportNoticeIcon}
      <span><span style={{ fontWeight: 500 }}>My Wins isn't available for your brokerage yet.</span> Its MLS license is in process.</span>
    </div>
  ) : (
    <div>
      <div style={{ color:'#999', fontSize:'12.5px', fontFamily:FONT_BODY, maxWidth:580, marginBottom:20 }}>
        Every {profile?.brokerage} listing, pending, and sale — auto-designed the moment it happens. Pick a format and post.
      </div>

      {filtered.length === 0 ? (
        <div style={{ color:'#999', fontSize:'12.5px', fontFamily:FONT_BODY, padding:'40px 0' }}>
          {filter === 'mine'
            ? 'No wins under your name yet — your cards appear here as your deals close.'
            : 'No wins yet — cards appear here automatically as deals close.'}
        </div>
      ) : (
        <>
          {/* auto-fill rather than a fixed column count, so a card never
              collapses to an unreadable width between breakpoints. */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(155px, 1fr))', gap:12 }}>
            {visible.map(w => isBuyerSideWin(w) ? (
              // Buyer-side sale: a full-width text row. No image, no download,
              // and never card_square_url, so deleting the old buyer-side card
              // images cannot leave a broken image here.
              <div key={w.id} style={{ gridColumn:'1 / -1', background:'white', border:'0.5px solid #e8e8e8', borderRadius:'12px', padding:'12px 14px', display:'flex', flexDirection:'column', gap:4 }}>
                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                  <div style={{ color:'#1d2e42', fontSize:'13px', fontWeight:600, fontFamily:FONT_BODY, minWidth:0, overflowWrap:'anywhere' }}>{w.address}</div>
                  <div style={{ color:'#666', fontSize:'12px', fontFamily:FONT_BODY, whiteSpace:'nowrap' }}>
                    {[MYWINS_STATUS_LABEL[w.event_type] || w.event_type, linksDate(w.event_date)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ color:'#999', fontSize:'12.5px', fontFamily:FONT_BODY }}>
                  Buyer-side sale. Post your own photo; listing photos belong to the listing brokerage.
                </div>
              </div>
            ) : (
              <div key={w.id} style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:'12px', overflow:'hidden', display:'flex', flexDirection:'column' }}>
                <div onClick={() => openWin(w)} style={{ width:'100%', aspectRatio:'1/1', background:'#1d2e42', cursor:'pointer', overflow:'hidden' }}>
                  <img src={w.card_square_url} loading="lazy" alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                </div>
                {/* One action, not two. Downloading happens in the lightbox,
                    which is where both formats are actually visible. */}
                <div style={{ padding:8 }}>
                  <button onClick={() => openWin(w)}
                    style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
                      background:'#E4EDF7', color:'#123763', border:'none', borderRadius:'6px',
                      padding:'6px 8px', fontSize:'11px', fontWeight:600, fontFamily:FONT_BODY, cursor:'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#123763" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
                      <path d="M12 3v12"/><polyline points="7 10 12 15 17 10"/><path d="M4 19h16"/>
                    </svg>
                    Get card
                  </button>
                </div>
              </div>
            ))}
          </div>

          {shown < filtered.length && (
            <div style={{ display:'flex', justifyContent:'center', marginTop:24 }}>
              <button onClick={() => setShown(s => s + PAGE)}
                style={{ background:'transparent', color:'#666', border:'0.5px solid #e8e8e8', borderRadius:'8px',
                  padding:'8px 20px', fontSize:'12px', fontWeight:600, fontFamily:FONT_BODY, cursor:'pointer' }}>
                Load more ({filtered.length - shown} remaining)
              </button>
            </div>
          )}
          <div style={{ color:'#999', fontSize:'11.5px', fontFamily:FONT_BODY, textAlign:'center', marginTop:12 }}>
            Showing {Math.min(shown, filtered.length)} of {filtered.length} wins
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      <CaseShell
        isMobile={isMobile}
        title="My wins"
        summary={mlsLicensed && !noOffice ? summaryNode : null}
        setupOpen={false}
        onToggleSetup={null}
        setup={null}
        loading={loading}
        messages={[]}
        renderMessage={()=>null}
        emptyState={listNode}
        contentWidth={MYWINS_CONTENT_WIDTH}
        controls={null}
        showComposer={false}
        composerValue=""
        onComposerChange={()=>{}}
        onSubmit={()=>{}}
        composerPlaceholder=""
        sendDisabled
        footer={null}
      />

      {modal && (
        <div onClick={(e)=>{ if(e.target===e.currentTarget) setModal(null); }} style={{ position:'fixed', inset:0, background:'rgba(15,22,33,.82)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:30 }}>
          <div onClick={()=>setModal(null)} style={{ position:'fixed', top:22, right:26, color:'#fff', fontSize:34, cursor:'pointer', lineHeight:1 }}>&times;</div>
          <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:24, alignItems:'center', maxHeight:'90vh', overflowY:'auto', justifyContent:'center' }}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
              <img src={modal.card_square_url} alt="" style={{ maxWidth: isMobile ? '82vw' : undefined, maxHeight: isMobile ? '58vh' : '78vh', borderRadius:12, background:'#1d2e42' }} />
              <button onClick={()=>window.open(modal.card_square_url,'_blank')} style={{ background:'#147efb', color:'#fff', border:'none', fontWeight:600, fontSize:'12px', padding:'9px 20px', borderRadius:8, cursor:'pointer', fontFamily:FONT_BODY }}>Download square</button>
            </div>
            {/* Only when a story card exists. Rendering it unconditionally gave a
                broken image and a Download Story that opened a blank tab. `modal`
                is the post-override view, so this reads the resolved url. */}
            {modal.card_story_url && (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12 }}>
                <img src={modal.card_story_url} alt="" style={{ maxWidth: isMobile ? '82vw' : undefined, maxHeight: isMobile ? '58vh' : '78vh', borderRadius:12, background:'#1d2e42' }} />
                <button onClick={()=>window.open(modal.card_story_url,'_blank')} style={{ background:'#147efb', color:'#fff', border:'none', fontWeight:600, fontSize:'12px', padding:'9px 20px', borderRadius:8, cursor:'pointer', fontFamily:FONT_BODY }}>Download story</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── MARKETPLACE ────────────────────────────────────────────────────────────
// /marketplace and /marketplace/{mls}. Live listings from the consented offices
// (public.marketplace_offices), read by get-marketplace-listings with the
// caller's OWN brokerage key. Nothing about a listing is kept in the database
// or the browser: the function returns it and the page shows it. The only
// write is the agent's own post log (MLS number, the price advertised, the
// date), made by the function when they click I posted it.

// ONE caller into the edge function, same auth as runCasitaCheck. Two refusals
// are answers rather than failures and come back flagged for the caller to
// render: the brokerage gate (403 brokerage_not_licensed) and a listing that
// is gone or cannot be posted (404). Everything else throws.
async function callMarketplace(body) {
  const { data: { session: currentSession } } = await supabase.auth.getSession()
  const token = currentSession?.access_token
  if (!token) throw new Error('Your session has expired. Please refresh the page and sign in again.')

  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-marketplace-listings`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  })
  // The photos mode answers with a ZIP, not JSON.
  if (res.ok && (res.headers.get('Content-Type') || '').includes('application/zip')) {
    return { blob: await res.blob() }
  }
  // Guarded parse: an upstream gateway failure is not JSON.
  let data = null
  try { data = await res.json() } catch (_) { data = null }
  if (res.status === 403 && data?.code === 'brokerage_not_licensed') {
    return { notLicensed: data.error || 'Marketplace is not available for your brokerage yet.' }
  }
  if (res.status === 404) return { notFound: data?.error || "This listing is no longer active, or it can't be posted." }
  if (!res.ok || !data?.ok) throw new Error(data?.error || `Server error (${res.status})`)
  return { data }
}

// Facebook Marketplace's three property types (get-marketplace-listings homeType).
const MARKETPLACE_HOME_TYPE_ES = { House: 'Casa', Townhouse: 'Casa adosada', Apartment: 'Departamento' }

// My Wins' outlined filter pill, shared by the list's filters and the detail
// view's language toggle.
const marketplaceChipStyle = (sel) => ({
  border:`0.5px solid ${sel ? '#147efb' : '#e8e8e8'}`,borderRadius:'18px',padding:'5px 12px',
  fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,whiteSpace:'nowrap',flexShrink:0,
  background:sel ? '#f4f4f2' : 'transparent',color:sel ? '#1d2e42' : '#666',
  cursor:'pointer',transition:'all .15s',
})
// A text link, the "← Back to checks" style.
const marketplaceLinkStyle = { background:'none', border:'none', padding:0, color:'#147efb', fontSize:'12px', fontFamily:FONT_BODY, cursor:'pointer' }

function marketplaceMoney(n) {
  return typeof n === 'number' && Number.isFinite(n) ? '$' + n.toLocaleString('en-US') : '—'
}
function marketplaceDate(s) {
  const d = s ? new Date(s) : null
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : ''
}
function marketplaceShortDate(s) {
  const d = s ? new Date(s) : null
  return d && !isNaN(d.getTime()) ? `${d.getMonth() + 1}/${d.getDate()}` : ''
}

// The middle description block, from the listing facts only. TEMPORARY until
// the AI step replaces it; a missing fact drops its phrase rather than
// printing "null".
function marketplaceFactsSentence(l, lang) {
  const sqft = typeof l.sqft === 'number' ? l.sqft.toLocaleString('en-US') : null
  const garage = typeof l.garage_spaces === 'number' && l.garage_spaces > 0 ? l.garage_spaces : null
  if (lang === 'es') {
    const type = MARKETPLACE_HOME_TYPE_ES[l.home_type] || 'Casa'
    return `${type} de ${l.beds ?? '—'} recámaras y ${l.baths ?? '—'} baños en ${l.city || ''}`
      + (sqft ? `, con ${sqft} pies cuadrados` : '')
      + (l.year_built ? `, construida en ${l.year_built}` : '')
      + (l.pool === true ? ', alberca privada' : '')
      + (garage ? `, cochera para ${garage} autos` : '')
      + '.'
  }
  return `${l.beds ?? '—'}-bedroom, ${l.baths ?? '—'}-bath ${(l.home_type || 'House').toLowerCase()} in ${l.city || ''}`
    + (sqft ? ` with ${sqft} sq ft` : '')
    + (l.year_built ? `, built in ${l.year_built}` : '')
    + (l.pool === true ? ', a private pool' : '')
    + (garage ? `, a ${garage}-car garage` : '')
    + '.'
}

// Outside listings (another brokerage's, posted with consent): the whole ad is
// this fixed text, never AI text. EMPLOYING is the agent's brokerage name as
// licensed (both are licensed DBAs, confirmed by Josh Oct 29 2026).
const MARKETPLACE_EMPLOYING = { 'HomePros': 'HomePros', 'HomePros Advantage': 'HomePros Advantage' }
function marketplaceJoinFacts(list, lang) {
  if (list.length <= 1) return list[0] || ''
  if (list.length === 2) return `${list[0]} ${lang === 'es' ? 'y' : 'and'} ${list[1]}`
  return `${list.slice(0, -1).join(', ')}${lang === 'es' ? ' y ' : ', and '}${list[list.length - 1]}`
}
function marketplaceOutsideDescription(l, agent, lang) {
  const employing = MARKETPLACE_EMPLOYING[agent?.brokerage] || ''
  const broker = agent?.license_type === 'broker'
  const facts = marketplaceJoinFacts(l.facts?.[lang] || [], lang)
  const sqft = typeof l.sqft === 'number' ? l.sqft.toLocaleString('en-US') : null
  const date = new Date().toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', { timeZone:'America/Phoenix', month:'long', day:'numeric', year:'numeric' })
  const phone = (agent?.phone || '').trim()
  const beds = l.beds ?? '—'
  const baths = l.baths ?? '—'
  const where = `${l.address || ''}, ${l.city || ''}, AZ ${l.zip || ''}`
  if (lang === 'es') {
    return [
      employing,
      `Descubra esta casa de ${beds} recámaras y ${baths} baños en ${l.city || ''}${facts ? `, con ${facts}` : ''}.`,
      '',
      `Dirección: ${where}`,
      ...(sqft ? [`Tamaño: Aproximadamente ${sqft} pies cuadrados.`] : []),
      `Comuníquese con ${agent?.name || ''}, Agente de bienes raíces con licencia en Arizona (${broker ? 'Broker' : 'Salesperson'}), para información de la propiedad o para hablar sobre servicios de representación de compradores.`,
      phone,
      employing,
      '',
      `Información y disponibilidad de la propiedad verificadas el ${date}.`,
      '',
      `Corretaje del listado: ${l.office || ''}. Esta propiedad no está listada por ${employing}. Es el listado de otro licenciatario y no se presenta como parte de mi historial de transacciones.`,
    ].join('\n')
  }
  return [
    employing,
    `Discover this ${beds}-bedroom, ${baths}-bathroom home in ${l.city || ''}${facts ? `, featuring ${facts}` : ''}.`,
    '',
    `Address: ${where}`,
    ...(sqft ? [`Size: Approximately ${sqft} sq. ft.`] : []),
    `Contact ${agent?.name || ''}, Arizona Licensed Real Estate ${broker ? 'Broker' : 'Salesperson'}, for property information or to discuss buyer-representation services.`,
    phone,
    employing,
    '',
    `Property information and availability verified on ${date}.`,
    '',
    `Listing brokerage: ${l.office || ''}. This property is not listed by ${employing}. It is another licensee's listing and is not presented as part of my transaction history.`,
  ].join('\n')
}

function marketplaceNotice(content) {
  return (
    <div style={{...reportNoticeStrip, marginTop:0}}>
      {reportNoticeIcon}
      <span style={{ flex:1, minWidth:0 }}>{content}</span>
    </div>
  )
}

function MarketplacePage({ showToast, isMobile, onOpenListing }) {
  const [result, setResult] = useState(null)          // { listings, posts, attention }
  const [officeNames, setOfficeNames] = useState({})  // office_key -> brokerage name
  const [loading, setLoading] = useState(true)
  const [notLicensed, setNotLicensed] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  // Collapsed on arrival, local only — the Casita help panel's rule.
  const [helpOpen, setHelpOpen] = useState(false)
  const [clearing, setClearing] = useState(null)

  // Only the LATEST load may write state. Each load takes a number, and an
  // answer whose number is no longer the newest is dropped, so an older answer
  // never overwrites a newer one. Nothing else bumps the number: a load is only
  // ever superseded by another load, and that newer load is the one that ends
  // `loading`.
  const loadSeq = useRef(0)
  // Set once this list instance has started its load. Survives StrictMode's
  // dev-only cleanup-and-rerun of the effect (same instance, refs kept), so
  // that rerun does not send a second request. Two overlapping requests on one
  // MLS key made Spark stall one of them ~60 s, and the page waited on it.
  const startedRef = useRef(false)

  // The brokerage of each consented office comes from marketplace_offices,
  // the same table the function filters on, so no office key lives here.
  const load = useCallback(async () => {
    const seq = ++loadSeq.current
    const isCurrent = () => seq === loadSeq.current
    try {
      const [res, offices] = await Promise.all([
        callMarketplace({ mode: 'list' }),
        supabase.from('marketplace_offices').select('office_key, name'),
      ])
      if (!isCurrent()) return
      if (offices.error) console.error('marketplace offices query error:', offices.error)
      setOfficeNames(Object.fromEntries((offices.data || []).map(o => [o.office_key, o.name])))
      if (res.notLicensed) { setNotLicensed(res.notLicensed); setError(null); return }
      setNotLicensed(null)
      setResult(res.data)
      setError(null)
    } catch (e) {
      if (!isCurrent()) return
      console.error('[marketplace list]', e)
      setError(e.message)
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [])
  // Exactly one load each time the list is shown. Going to a listing unmounts
  // this page and coming back mounts a new instance, which loads again.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    load()
  }, [load])

  // "I updated it" on a post that left the MLS: the post log row goes to
  // removed, and the list reloads so the strip goes with it.
  async function markUpdated(id) {
    if (clearing) return
    setClearing(id)
    try {
      const res = await callMarketplace({ mode: 'mark_removed', listing_id: id })
      if (res.notLicensed) { setNotLicensed(res.notLicensed); return }
      showToast('Marked as updated')
      await load()
    } catch (e) {
      console.error('[marketplace mark_removed]', e)
      showToast(`Could not save: ${e.message}`, true)
    } finally {
      setClearing(null)
    }
  }

  const listings = result?.listings || []
  const posts = result?.posts || []
  const attention = result?.attention || []
  const postById = new Map(posts.map(p => [String(p.listing_id), p]))
  const priceChanged = new Set(attention.filter(a => a.type === 'price').map(a => a.listing_id))
  const groupOf = (l) => {
    const name = officeNames[l.office_key]
    return name === 'HomePros' ? 'hp' : name === 'HomePros Advantage' ? 'adv' : 'other'
  }
  const count = (g) => listings.filter(l => groupOf(l) === g).length
  const filtered =
    filter === 'all' ? listings
    : filter === 'notposted' ? listings.filter(l => !postById.has(l.mls))
    : listings.filter(l => groupOf(l) === filter)

  const chip = (f, label) => (
    <button key={f} onClick={() => setFilter(f)} style={marketplaceChipStyle(filter === f)}>{label}</button>
  )
  const summaryNode = result ? (
    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', minWidth:0, overflowX:'auto' }}>
      {chip('all', `All · ${listings.length}`)}
      {chip('hp', `HomePros · ${count('hp')}`)}
      {chip('adv', `HomePros Advantage · ${count('adv')}`)}
      {chip('other', `Other brokerages · ${count('other')}`)}
      {chip('notposted', 'Not posted by me')}
    </div>
  ) : null

  const HOW_TO_POST = [
    'Open a listing and check the fill sheet.',
    'In Chrome, open Facebook Marketplace > Create new listing > Property for sale.',
    'Tell Claude in Chrome: "Fill this Marketplace listing from my Hub fill sheet for MLS <number>. Do not click Publish."',
    'Review, click Publish on Facebook, then click I posted it here.',
    'When the Hub flags a price change or a pending sale, update or mark the post on Facebook.',
  ]

  // Casita's HOW TO READ THIS panel, numbered here because these are steps.
  const helpPanel = (
    <div style={{background:'linear-gradient(135deg, #1d2e42 0%, #2a4060 100%)',border:'1px solid #fdbf00',borderRadius:'12px',padding: helpOpen ? '20px 24px' : '14px 24px',marginBottom:'20px',color:'white',fontFamily:FONT_BODY}}>
      <button
        onClick={()=>setHelpOpen(o => !o)}
        aria-expanded={helpOpen}
        style={{width:'100%',display:'flex',alignItems:'center',gap:'12px',background:'transparent',border:'none',padding:0,cursor:'pointer',textAlign:'left'}}>
        <span style={{flex:1,minWidth:0,fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#fdbf00',fontFamily:FONT_HEAD}}>
          HOW TO POST
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fdbf00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}} aria-hidden="true">
          {helpOpen ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {helpOpen && (
        <div style={{display:'flex',flexDirection:'column',gap:'10px',fontSize:'13px',lineHeight:1.5,color:'#cbd5e1',marginTop:'12px'}}>
          {HOW_TO_POST.map((text, i) => (
            <div key={i}><span style={{color:'#fdbf00',fontWeight:700}}>{i + 1}.</span> {text}</div>
          ))}
        </div>
      )}
    </div>
  )

  const attentionNode = attention.length > 0 && (
    <div style={{ marginBottom:'20px' }}>
      <div style={{ ...SETUP_EYEBROW, marginBottom:'8px' }}>NEEDS ATTENTION</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {attention.map(a => (
          <div key={`${a.type}-${a.listing_id}`}>
            {a.type === 'price'
              ? marketplaceNotice(<>
                  Price changed on {a.address}, {a.city}. You posted it at {marketplaceMoney(a.advertised_price)} on {marketplaceDate(a.posted_at)}, now {marketplaceMoney(a.price)}.{' '}
                  <button onClick={()=>onOpenListing(a.listing_id)} style={marketplaceLinkStyle}>Update post</button>
                </>)
              : marketplaceNotice(<>
                  MLS {a.listing_id} is no longer active{a.status ? ` (${a.status})` : ''}. Mark it sold or pending on Marketplace, or remove it.{' '}
                  <button onClick={()=>markUpdated(a.listing_id)} disabled={!!clearing} style={{ ...marketplaceLinkStyle, opacity: clearing ? 0.5 : 1 }}>
                    {clearing === a.listing_id ? 'Saving…' : 'I updated it'}
                  </button>
                </>)}
          </div>
        ))}
      </div>
    </div>
  )

  const card = (l) => {
    const post = postById.get(l.mls)
    const changed = priceChanged.has(l.mls)
    const facts = [
      l.beds != null && `${l.beds} bd`,
      l.baths != null && `${l.baths} ba`,
      l.sqft != null && `${l.sqft.toLocaleString('en-US')} sq ft`,
      l.office && `Listed by ${l.office}`,
    ].filter(Boolean).join(' · ')
    return (
      <div key={l.mls} style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:'12px', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div onClick={() => onOpenListing(l.mls)} style={{ width:'100%', aspectRatio:'4/3', background:'#e8e8e8', cursor:'pointer', overflow:'hidden' }}>
          {l.photos?.[0] && <img src={l.photos[0]} loading="lazy" alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />}
        </div>
        <div style={{ padding:'10px 10px 0', display:'flex', flexDirection:'column', gap:'4px', flex:1, minWidth:0 }}>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
            {changed ? caseChip('gold', 'Price changed')
              : post ? caseChip('green', `Posted ${marketplaceShortDate(post.posted_at)}`)
              : caseChip('grey', 'Not posted')}
          </div>
          <div style={{ fontFamily:FONT_HEAD, fontSize:'18px', fontWeight:700, color:'#1d2e42' }}>{marketplaceMoney(l.price)}</div>
          <div style={{ fontWeight:500, fontSize:'13.5px', color:'#1d2e42', fontFamily:FONT_BODY, overflowWrap:'anywhere' }}>
            {[l.address, l.city].filter(Boolean).join(', ')}
          </div>
          <div style={{ fontSize:'11.5px', color:'#888', fontFamily:FONT_BODY }}>{facts}</div>
        </div>
        <div style={{ padding:8 }}>
          <button onClick={() => onOpenListing(l.mls)}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
              background:'#E4EDF7', color:'#123763', border:'none', borderRadius:'6px',
              padding:'6px 8px', fontSize:'11px', fontWeight:600, fontFamily:FONT_BODY, cursor:'pointer' }}>
            {changed ? 'Update post' : 'Post to Marketplace'}
          </button>
        </div>
      </div>
    )
  }

  const listNode = notLicensed ? marketplaceNotice(notLicensed) : (
    <div style={{ paddingTop: isMobile ? '8px' : '24px' }}>
      <ToolIntroBand
        title="Marketplace"
        description="Live listings ready to post on Facebook Marketplace. Pick one, let Claude fill the form, and you click Publish."
        stats={[
          `${listings.length} listings live from the MLS`,
          `${posts.length} posted by you`,
          `${attention.length} need attention`,
        ]}
      />

      {helpPanel}

      {error && (
        <div style={{ ...casitaCard, marginBottom:'14px', borderColor:'#C8102E' }}>
          <div style={{ fontSize:'12.5px', color:'#C8102E', fontFamily:FONT_BODY, marginBottom:'8px', lineHeight:1.6 }}>{error}</div>
          <button onClick={()=>{ setLoading(true); load() }} style={marketSecondaryStyle(true)}>Try again</button>
        </div>
      )}

      {attentionNode}

      {result && (filtered.length === 0 ? (
        <div style={{ color:'#999', fontSize:'12.5px', fontFamily:FONT_BODY, padding:'40px 0' }}>
          {listings.length === 0 ? 'No active listings from the consented offices right now.' : 'No listings match this filter.'}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(155px, 1fr))', gap:12 }}>
          {filtered.map(card)}
        </div>
      ))}

      <div style={{ color:'#999', fontSize:'12px', fontFamily:FONT_BODY, marginTop:'20px' }}>
        Listings whose sellers opted out of internet or address display never appear here.
      </div>
    </div>
  )

  return (
    <CaseShell
      isMobile={isMobile}
      title="Marketplace"
      summary={summaryNode}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={listNode}
      contentWidth={MYWINS_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

const MARKETPLACE_MAX_PHOTOS = 10

// Each agent's saved Facebook groups (marketplace_groups, their own row under
// RLS) and the broker's never-post list (marketplace_never_groups). Read and
// written with the page's own client: no edge function, no listing data.
const MARKETPLACE_MAX_GROUPS = 20

function MarketplaceGroupsCard({ userId, isMobile }) {
  const [groups, setGroups] = useState([])
  const [never, setNever] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [notices, setNotices] = useState([])

  // Database reads, not MLS calls, so StrictMode's second run may simply read
  // again; the cleanup keeps the first run from writing after it.
  useEffect(() => {
    let active = true
    ;(async () => {
      const [mine, blocked] = await Promise.all([
        supabase.from('marketplace_groups').select('groups').eq('agent_id', userId).maybeSingle(),
        supabase.from('marketplace_never_groups').select('name').order('name'),
      ])
      if (!active) return
      if (mine.error) console.error('marketplace groups query error:', mine.error)
      if (blocked.error) console.error('marketplace never-groups query error:', blocked.error)
      setGroups(mine.data?.groups || [])
      setNever((blocked.data || []).map(r => r.name))
      setLoaded(true)
    })()
    return () => { active = false }
  }, [userId])

  function startEdit() {
    setDraft(groups.join('\n'))
    setNotices([])
    setEditing(true)
  }

  // Trim, drop blanks and repeats, skip never-post names (said so, not
  // silently), keep the first 20. .select() and a row count: a write refused
  // by RLS returns no error and no row.
  async function save() {
    if (saving) return
    const neverByLower = new Map(never.map(n => [n.toLowerCase(), n]))
    const skipped = []
    const kept = []
    for (const line of draft.split('\n').map(s => s.trim()).filter(Boolean)) {
      if (kept.includes(line) || skipped.includes(line)) continue
      if (neverByLower.has(line.toLowerCase())) { skipped.push(line); continue }
      kept.push(line)
    }
    const next = kept.slice(0, MARKETPLACE_MAX_GROUPS)
    setSaving(true)
    try {
      const { data, error } = await supabase.from('marketplace_groups')
        .upsert({ agent_id: userId, groups: next, updated_at: new Date().toISOString() }, { onConflict: 'agent_id' })
        .select('groups')
      if (error) { setNotices([`Could not save your groups: ${error.message}`]); return }
      if (!data || data.length === 0) { setNotices(['The save was refused. Your groups were not changed.']); return }
      setGroups(data[0].groups || [])
      setNotices(skipped.map(name => `${name} is on your broker's never-post list and will be skipped.`))
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  const noticeNodes = notices.length > 0 && (
    <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'10px' }}>
      {notices.map((n, i) => <div key={i}>{marketplaceNotice(n)}</div>)}
    </div>
  )
  const muted = { fontSize:'12px', color:'#999', fontFamily:FONT_BODY, lineHeight:1.55 }

  if (editing) {
    return (
      <div style={casitaCard}>
        <label htmlFor="marketplace-groups-input" style={{ ...SETUP_EYEBROW, display:'block', marginBottom:'10px' }}>EDIT YOUR FACEBOOK GROUPS</label>
        {noticeNodes}
        <textarea
          id="marketplace-groups-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={8}
          style={{ ...S.input, fontSize:'13px', padding:'10px 12px', background:'#ffffff', border:`0.5px solid ${RAIL_BORDER}`, borderRadius:'8px', resize:'vertical', lineHeight:1.55 }}
        />
        <div style={{ ...muted, margin:'6px 0 12px' }}>One group per line, exactly as Facebook shows it. Up to 20. The first 20 lines are used.</div>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <button onClick={save} disabled={saving} style={marketPrimaryStyle(!saving)}>{saving ? 'Saving…' : 'Save groups'}</button>
          <button onClick={() => { setEditing(false); setNotices([]) }} disabled={saving} style={marketSecondaryStyle(!saving)}>Cancel</button>
        </div>
      </div>
    )
  }

  if (groups.length === 0) {
    return (
      <div style={casitaCard}>
        {noticeNodes}
        <div style={{ ...SETUP_EYEBROW, marginBottom:'8px' }}>YOUR FACEBOOK GROUPS · NONE SAVED</div>
        <div style={{ ...muted, fontSize:'12.5px', color:'#666', marginBottom:'10px' }}>
          Save the buy-and-sell and real estate groups you belong to, and Claude ticks them on every post.
        </div>
        <button onClick={startEdit} style={marketSecondaryStyle(true)}>Add groups</button>
      </div>
    )
  }

  return (
    <div style={casitaCard}>
      {noticeNodes}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', marginBottom:'6px' }}>
        <div style={SETUP_EYEBROW}>YOUR FACEBOOK GROUPS · {groups.length} OF {MARKETPLACE_MAX_GROUPS}</div>
        <button onClick={startEdit} style={marketSecondaryStyle(true)}>Edit groups</button>
      </div>
      <div style={{ ...muted, marginBottom:'10px' }}>
        Claude ticks these on the "List in more places" step. Names must match Facebook exactly.
      </div>
      {/* Plain text, numbered down each column. */}
      <ol id="marketplace-groups" style={{ columns: isMobile ? 1 : 2, columnGap:'28px', listStyleType:'decimal', paddingLeft:'22px', margin:0,
        fontSize:'13px', color:'#1d2e42', fontFamily:FONT_BODY, lineHeight:1.7 }}>
        {groups.map(g => <li key={g} style={{ breakInside:'avoid' }}>{g}</li>)}
      </ol>
      {never.length > 0 && (
        <div style={{ borderTop:'0.5px solid #e8e8e8', marginTop:'14px', paddingTop:'12px' }}>
          <div style={{ ...SETUP_EYEBROW, marginBottom:'8px' }}>NEVER POST · SET BY YOUR BROKER</div>
          <div id="marketplace-never-groups" style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
            {never.map(n => caseChip('grey', n))}
          </div>
        </div>
      )}
    </div>
  )
}

function MarketplaceListingView({ session, listingId, showToast, isMobile, onBack }) {
  const [listing, setListing] = useState(null)
  const [agent, setAgent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notLicensed, setNotLicensed] = useState(null)
  const [notFound, setNotFound] = useState(null)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState([])        // photo indexes, ascending
  const [downloading, setDownloading] = useState(false)
  const [photoError, setPhotoError] = useState(null)
  const [lang, setLang] = useState('en')
  const [middle, setMiddle] = useState({ en: '', es: '' })
  const [posting, setPosting] = useState(false)
  // The middle paragraph: the plain facts sentence until Claude's answer
  // arrives (aiText), and kept as it is if the answer is refused or fails.
  const [describing, setDescribing] = useState(false)
  const [describeFailed, setDescribeFailed] = useState(false)
  const [aiText, setAiText] = useState(false)

  // Same superseded-response guard as the list's load: only the latest
  // describe call may write the paragraph. Only a newer describe call bumps
  // the number, and that call is the one that ends `describing`.
  const describeSeq = useRef(0)
  const runDescribe = useCallback(async (mls) => {
    const seq = ++describeSeq.current
    const isCurrent = () => seq === describeSeq.current
    setDescribing(true)
    setDescribeFailed(false)
    try {
      const res = await callMarketplace({ mode: 'describe', listing_id: mls })
      if (!isCurrent()) return
      if (!res.data?.en || !res.data?.es) { setDescribeFailed(true); return }
      setMiddle({ en: res.data.en, es: res.data.es })
      setAiText(true)
    } catch (e) {
      // 422 (facts check) lands here too. No error strip: the paragraph on
      // screen stays, and the muted line under it says what happened.
      if (!isCurrent()) return
      console.warn('[marketplace describe]', e.message)
      setDescribeFailed(true)
    } finally {
      if (isCurrent()) setDescribing(false)
    }
  }, [])

  // One listing load per instance (the view is keyed on the MLS number), with
  // the list's guard: a ref that survives StrictMode's dev-only effect rerun so
  // no second request goes out, and no cleanup that could drop the only
  // answer. A cancelled flag here used to do exactly that: StrictMode's
  // cleanup cancelled the first request and the rerun sent a second one, which
  // was the one the page then waited on.
  const startedFor = useRef(null)
  useEffect(() => {
    if (startedFor.current === listingId) return
    startedFor.current = listingId
    ;(async () => {
      try {
        const res = await callMarketplace({ mode: 'listing', listing_id: listingId })
        if (res.notLicensed) { setNotLicensed(res.notLicensed); return }
        if (res.notFound) { setNotFound(res.notFound); return }
        const l = res.data.listing
        setListing(l)
        setAgent(res.data.agent || null)
        setError(null); setNotFound(null); setNotLicensed(null)
        // The first ten photos start selected, Marketplace's own limit.
        setSelected((l.photos || []).slice(0, MARKETPLACE_MAX_PHOTOS).map((_, i) => i))
        setMiddle({ en: marketplaceFactsSentence(l, 'en'), es: marketplaceFactsSentence(l, 'es') })
        // Once, in the background: the page is usable on the plain sentence
        // while Claude writes. Never for an outside listing (fixed format).
        if (l.outside !== true) runDescribe(l.mls)
      } catch (e) {
        console.error('[marketplace listing]', e)
        setError(e.message)
      } finally {
        // Always: this is the only listing load this view makes.
        setLoading(false)
      }
    })()
  }, [listingId, runDescribe])

  async function copy(text, label) {
    const ok = await copyToClipboard(text)
    showToast(ok ? `${label} copied` : 'Copy failed. Select the text and copy it by hand.', !ok)
  }

  function togglePhoto(i) {
    if (selected.includes(i)) { setSelected(selected.filter(x => x !== i)); return }
    if (selected.length >= MARKETPLACE_MAX_PHOTOS) { showToast(`Marketplace takes up to ${MARKETPLACE_MAX_PHOTOS} photos.`, true); return }
    setSelected([...selected, i].sort((a, b) => a - b))
  }

  // The function fetches the selected photos (this listing's own public ones
  // only) and hands them back as one ZIP, so the browser never fetches the
  // MLS photo host itself. Selection order is the file order.
  async function downloadSelected() {
    if (downloading || selected.length === 0) return
    setDownloading(true)
    setPhotoError(null)
    try {
      const res = await callMarketplace({ mode: 'photos', listing_id: listing.mls, urls: selected.map(i => listing.photos[i]) })
      if (res.notLicensed) { setPhotoError(res.notLicensed); return }
      if (res.notFound) { setPhotoError(res.notFound); return }
      if (!res.blob) throw new Error('The photos could not be prepared. Try again.')
      // Object URL created, clicked and revoked inside triggerBlobDownload.
      triggerBlobDownload(res.blob, `MLS-${listing.mls}-photos.zip`)
    } catch (e) {
      console.error('[marketplace photos]', e)
      setPhotoError(e.message)
    } finally {
      setDownloading(false)
    }
  }

  // ADRE: the agent's contact goes on every ad, so no phone means no post.
  const phone = (agent?.phone || '').trim()
  const canPost = !!listing && !!phone && !posting

  async function logPost() {
    if (!canPost) return
    setPosting(true)
    try {
      const res = await callMarketplace({ mode: 'log_post', listing_id: listing.mls })
      if (res.notLicensed) { setNotLicensed(res.notLicensed); return }
      if (res.notFound) { setNotFound(res.notFound); return }
      showToast('Saved. The Hub will tell you if the price changes or it goes pending.')
      onBack()
    } catch (e) {
      console.error('[marketplace log_post]', e)
      showToast(`Could not save: ${e.message}`, true)
    } finally {
      setPosting(false)
    }
  }

  const backLink = <button onClick={onBack} style={marketplaceLinkStyle}>← Back to Marketplace</button>

  let bodyNode = null
  if (notLicensed || notFound || error) {
    bodyNode = (
      <div style={{ paddingTop: isMobile ? '8px' : '24px', display:'flex', flexDirection:'column', gap:'12px' }}>
        {marketplaceNotice(notLicensed || notFound || error)}
        <div>{backLink}</div>
      </div>
    )
  } else if (listing) {
    const l = listing
    const photos = l.photos || []
    const title = [l.beds != null && `${l.beds} bd`, l.baths != null && `${l.baths} ba`, `${(l.home_type || 'House').toLowerCase()} in ${l.city || ''}`]
      .filter(Boolean).join(' ')
    const fill = [
      ['For sale or rent', 'For Sale'],
      ['Title', title],
      ['Price', typeof l.price === 'number' ? String(Math.round(l.price)) : ''],
      ['Property type', l.home_type || ''],
      ['Bedrooms', l.beds != null ? String(l.beds) : ''],
      ['Bathrooms', l.baths != null ? String(l.baths) : ''],
      ['Square feet', l.sqft != null ? String(Math.round(l.sqft)) : ''],
      ['Address', `${l.address || ''}, ${l.city || ''}, AZ ${l.zip || ''}`],
      ['MLS number', l.mls],
    ]
    const es = lang === 'es'
    // One builder per language, so Copy description and the two ready-to-paste
    // blocks can never differ: fixed top lines, the middle paragraph as it
    // stands (agent edits, or the AI version once it arrives), fixed closing.
    const topFor = (lg) => [
      `${lg === 'es' ? 'Listado por' : 'Listed by'} ${l.office || ''}`,
      `${lg === 'es' ? 'Presentado por' : 'Presented by'} ${agent?.name || ''}, ${agent?.brokerage || ''}${phone ? ` · ${phone}` : ''}`,
    ].join('\n')
    const bottomFor = (lg) => lg === 'es'
      ? 'Envíeme un mensaje para ver todas las fotos y agendar una visita.'
      : 'Message me for all photos and a showing.'
    const outside = l.outside === true
    const fullDescription = (lg) => outside
      ? marketplaceOutsideDescription(l, agent, lg)
      : [topFor(lg), middle[lg].trim(), bottomFor(lg)].join('\n\n')
    const top = topFor(lang)
    const bottom = bottomFor(lang)
    const instruction = `Post MLS ${l.mls} to Facebook Marketplace from my Hub fill sheet. Fill the address last. Do not click Publish.`
    const fixedBlock = { background:'#f4f4f2', borderRadius:'8px', padding:'10px 12px', fontSize:'13px', lineHeight:1.55, color:'#1d2e42', fontFamily:FONT_BODY, whiteSpace:'pre-wrap' }
    const smallBtn = { ...marketSecondaryStyle(true), padding:'5px 11px', flexShrink:0 }

    bodyNode = (
      <div style={{ paddingTop: isMobile ? '8px' : '24px', display:'flex', flexDirection:'column', gap:'14px' }}>
        <div>{backLink}</div>

        {/* PHOTOS */}
        <div style={casitaCard}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', marginBottom:'10px' }}>
            <div style={SETUP_EYEBROW}>PHOTOS · {selected.length} OF {photos.length} SELECTED</div>
            <button onClick={downloadSelected} disabled={downloading || selected.length === 0}
              style={marketSecondaryStyle(!downloading && selected.length > 0)}>
              {downloading ? 'Preparing photos…' : 'Download selected photos'}
            </button>
          </div>
          {photoError && <div style={{ marginBottom:'10px' }}>{marketplaceNotice(photoError)}</div>}
          {photos.length === 0 ? (
            <div style={{ fontSize:'12.5px', color:'#999', fontFamily:FONT_BODY }}>This listing has no public photos.</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px, 1fr))', gap:'8px' }}>
              {photos.map((url, i) => {
                const on = selected.includes(i)
                return (
                  <button key={url} onClick={() => togglePhoto(i)} aria-pressed={on}
                    style={{ padding:0, border:'none', borderRadius:'8px', overflow:'hidden', cursor:'pointer', aspectRatio:'4/3',
                      background:'#e8e8e8', outline: on ? '2px solid #147efb' : 'none', outlineOffset:'-2px', opacity: on ? 1 : 0.55 }}>
                    <img src={url} loading="lazy" alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* FILL SHEET. The ids on this card and the two ready-to-paste
            blocks are for finding them on the page (Claude in Chrome). */}
        <div id="marketplace-fill-sheet" style={casitaCard}>
          <div style={{ ...SETUP_EYEBROW, marginBottom:'10px' }}>FILL SHEET · PROPERTY FOR SALE</div>
          <div style={{ display:'flex', flexDirection:'column' }}>
            {fill.map(([label, value]) => (
              <div key={label} style={{ padding:'7px 0', borderTop:'0.5px solid #f0f0ee' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                  <div style={{ width:'110px', flexShrink:0, fontSize:'12px', color:'#999', fontFamily:FONT_BODY }}>{label}</div>
                  <div style={{ flex:1, minWidth:0, fontSize:'13px', color:'#1d2e42', fontFamily:FONT_BODY, overflowWrap:'anywhere' }}>{value}</div>
                  <button onClick={() => copy(value, label)} disabled={!value} style={{ ...smallBtn, opacity: value ? 1 : 0.5 }}>Copy</button>
                </div>
                {label === 'Address' && (
                  <div style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY, lineHeight:1.5, marginTop:'4px', paddingLeft:'122px' }}>
                    Facebook only accepts its own address suggestions. Pick the one with the same number, street and ZIP; the street direction can differ from the MLS.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* READY TO PASTE: the exact text Copy description produces, per
            language, as plain selectable text rather than a field. */}
        {[['en', 'READY TO PASTE · ENGLISH'], ['es', 'READY TO PASTE · ESPAÑOL']].map(([lg, eyebrow]) => (
          <div key={lg} style={casitaCard}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', marginBottom:'10px' }}>
              <div style={SETUP_EYEBROW}>{eyebrow}</div>
              <button onClick={() => copy(fullDescription(lg), lg === 'es' ? 'Spanish description' : 'English description')} style={smallBtn}>Copy</button>
            </div>
            <div id={`marketplace-desc-${lg}`} style={fixedBlock}>{fullDescription(lg)}</div>
          </div>
        ))}

        {/* FACEBOOK GROUPS: the agent's own list and the broker's never-post list. */}
        <MarketplaceGroupsCard userId={session.user.id} isMobile={isMobile} />

        {/* DESCRIPTION */}
        <div style={casitaCard}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap', marginBottom:'10px' }}>
            <div style={SETUP_EYEBROW}>{outside ? 'OUTSIDE LISTING · FIXED FORMAT' : 'DESCRIPTION'}</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setLang('en')} style={marketplaceChipStyle(!es)}>English</button>
              <button onClick={() => setLang('es')} style={marketplaceChipStyle(es)}>Español</button>
            </div>
          </div>
          {/* Outside listing: the whole ad is fixed text, nothing to edit or rewrite. */}
          {outside ? (
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              <div style={fixedBlock}>{fullDescription(lang)}</div>
              <div>
                <button onClick={() => copy(fullDescription(lang), 'Description')} style={marketplaceLinkStyle}>
                  Copy description
                </button>
              </div>
            </div>
          ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            <div>
              <div style={{ ...SETUP_EYEBROW, marginBottom:'6px' }}>FIXED · AI NEVER CHANGES THIS</div>
              <div style={fixedBlock}>{top}</div>
            </div>
            <div>
              <div style={{ ...SETUP_EYEBROW, marginBottom:'6px' }}>
                {aiText ? 'WRITTEN BY AI FROM THE LISTING FACTS · EDITABLE' : 'FROM THE LISTING FACTS · EDITABLE'}
              </div>
              {/* Edits are kept per language; Copy description uses them. */}
              <textarea
                value={middle[lang]}
                onChange={e => { const v = e.target.value; setMiddle(m => ({ ...m, [lang]: v })) }}
                rows={4}
                style={{ ...S.input, fontSize:'13px', padding:'10px 12px', background:'#ffffff', border:`0.5px solid ${RAIL_BORDER}`, borderRadius:'8px', resize:'vertical', lineHeight:1.55 }}
              />
              {(describing || describeFailed) && (
                <div style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY, marginTop:'4px' }}>
                  {describing ? 'Writing the description…'
                    : aiText ? 'AI rewrite unavailable. The paragraph above is unchanged.'
                    : 'Plain description shown. AI description unavailable.'}
                </div>
              )}
            </div>
            <div>
              <div style={{ ...SETUP_EYEBROW, marginBottom:'6px' }}>FIXED</div>
              <div style={fixedBlock}>{bottom}</div>
            </div>
            <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
              <button onClick={() => copy(fullDescription(lang), 'Description')} style={marketplaceLinkStyle}>
                Copy description
              </button>
              {/* Replaces BOTH languages, including any edits. */}
              <button onClick={() => runDescribe(l.mls)} disabled={describing}
                style={{ ...marketplaceLinkStyle, opacity: describing ? 0.5 : 1, cursor: describing ? 'default' : 'pointer' }}>
                Rewrite paragraph
              </button>
            </div>
          </div>
          )}
        </div>

        {/* CLAUDE IN CHROME */}
        <div style={casitaCard}>
          <div style={{ ...SETUP_EYEBROW, marginBottom:'8px' }}>FILL IT WITH CLAUDE IN CHROME</div>
          <div style={{ fontSize:'12.5px', color:'#666', fontFamily:FONT_BODY, lineHeight:1.55, marginBottom:'8px' }}>
            In Chrome, open Facebook Marketplace &gt; Create new listing &gt; Property for sale, then give Claude this instruction:
          </div>
          <div style={{ ...fixedBlock, marginBottom:'10px' }}>{instruction}</div>
          <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
            <button onClick={() => copy(instruction, 'Instruction')} style={marketSecondaryStyle(true)}>Copy instruction</button>
            <span style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY }}>No Chrome extension? Use the Copy buttons above, about 2 minutes.</span>
          </div>
        </div>

        {/* I POSTED IT */}
        {!phone && marketplaceNotice('Add your phone number in your profile before posting. ADRE requires your contact on every ad.')}
        <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', paddingBottom:'12px' }}>
          <button onClick={logPost} disabled={!canPost} style={marketPrimaryStyle(canPost)}>
            {posting ? 'Saving…' : 'I posted it'}
          </button>
          <span style={{ fontSize:'11.5px', color:'#999', fontFamily:FONT_BODY }}>Saves the date and today's MLS price to your post log.</span>
        </div>
      </div>
    )
  }

  return (
    <CaseShell
      isMobile={isMobile}
      title={listing ? [listing.address, listing.city].filter(Boolean).join(', ') : 'Marketplace'}
      summary={listing ? caseChip('blue', 'Active · checked just now') : null}
      setupOpen={false}
      onToggleSetup={null}
      setup={null}
      loading={loading}
      messages={[]}
      renderMessage={()=>null}
      emptyState={bodyNode}
      contentWidth={SHARED_CONTENT_WIDTH}
      controls={null}
      showComposer={false}
      composerValue=""
      onComposerChange={()=>{}}
      onSubmit={()=>{}}
      composerPlaceholder=""
      sendDisabled
      footer={null}
    />
  )
}

